import { useState, useEffect, useRef, useCallback, useMemo, memo, Component, createContext, useContext, Fragment } from "react";
import Papa from "papaparse";

// ─── ERROR BOUNDARY ───────────────────────────────────────
// Catches render crashes, reports them, and shows a recovery UI
// instead of a white screen. In production, wire reportError to
// Sentry, Datadog, or your /api/errors endpoint.
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) {
    console.error(`[${BRAND.name} Error Boundary]`, error, info.componentStack);

  }

  render() {
    if (this.state.hasError) {

      const quips = [
        "Looks like we over-proofed this pour.",
        "The barrel sprung a leak. We're patching it up.",
        "Someone left the rickhouse door open.",
        "This batch didn't quite make it past quality control.",
        "Even the best distillers have an off-run.",
        "The angel's share took a little more than expected.",
        "Proof too high. Things got a bit... volatile.",
      ];

      const quip = quips[Math.floor(Math.random() * quips.length)];

      return (
        <div style={{ minHeight: "100vh", background: "#1A1409", display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
          <div style={{ background: "#2A1F14", borderRadius: 16, padding: 40, maxWidth: 440, textAlign: "center", border: "1px solid #5A4F3E" }}>
            <div style={{ fontSize: "4.0rem", marginBottom: 12 }}>{"🥃"}</div>
            <h2 style={{ color: "#F5E6D0", margin: "0 0 8px", fontSize: "1.375rem", fontFamily: FONT_DISPLAY }}>Well, That Wasn't Smooth</h2>
            <p style={{ color: "#D4A053", fontSize: "0.9375rem", fontStyle: "italic", margin: "0 0 16px", lineHeight: 1.5 }}>
              "{quip}"
            </p>
            <p style={{ color: "#B5A898", fontSize: "0.8125rem", lineHeight: 1.6, margin: "0 0 24px" }}>
              Your collection, check-ins, and tasting notes are all safe in the barrel. Nothing lost {"\u2014"} just a hiccup on our end.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              <button onClick={() => { this.setState({ hasError: false, error: null }); }} style={{ background: GRAD_PRIMARY, color: "#1A1409", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer" }}>
                Try Another Pour
              </button>
              <button onClick={() => window.location.reload()} style={{ background: "transparent", color: "#D4A053", border: "1px solid #5A4F3E", borderRadius: 8, padding: "10px 24px", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer" }}>
                Start Fresh
              </button>
            </div>
            {this.state.error && (
              <details style={{ marginTop: 20, textAlign: "left" }}>
                <summary style={{ color: "#A69880", fontSize: "0.6875rem", cursor: "pointer", userSelect: "none" }}>Technical details (for the distillery nerds)</summary>
                <pre style={{ color: "#A69880", fontSize: "0.625rem", marginTop: 8, background: "#1A1409", borderRadius: 8, padding: 12, maxHeight: 100, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );

    }

    return this.props.children;

  }

}

// ─── DATA SEED ────────────────────────────────────────────
// Production merge constants (pre-built Sets — avoids .map() on every loadUserData)

const SEED_BRAND_IDS = new Set(["d1","d2","d3","d4","d5","d6","d7","d8","d9","d10","d11","d12","d13","d14","d15","d16","d17","d18","d19","d20","d21","d22","d23","d24","d25","d26","d27","d28","d29","d30","d31","d32","d33","d34","d35","d36","d37","d38","d39","d40","d41","d42","d43","d44","d45"]);

const SEED_BOURBON_IDS = new Set(["b1","b2","b3","b16","b17","b18","b19","b20","b21","b22","b23","b24","b25","b26","b27","b4","b5","b28","b29","b30","b31","b32","b6","b33","b34","b35","b36","b37","b7","b8","b38","b39","b40","b41","b9","b42","b43","b44","b45","b10","b11","b15","b46","b47","b48","b49","b50","b51","b52","b53","b12","b13","b142","b143","b54","b55","b56","b57","b58","b59","b60","b14","b61","b62","b63","b64","b65","b66","b67","b68","b69","b70","b71","b72","b73","b74","b75","b76","b77","b78","b79","b80","b81","b82","b83","b84","b85","b86","b87","b88","b89","b90","b91","b92","b93","b94","b95","b96","b97","b98","b99","b100","b101","b102","b103","b104","b105","b106","b107","b108","b109","b110","b111","b112","b113","b114","b115","b116","b117","b118","b119","b120","b121","b122","b123","b124","b125","b126","b127","b128","b129","b130","b131","b132","b133","b134","b135","b136","b137","b138","b139","b140","b141"]);

const SEED_BRAND_NAMES = {"d1":"Buffalo Trace","d2":"Wild Turkey","d3":"Woodford Reserve","d4":"Maker's Mark","d5":"Four Roses","d6":"Heaven Hill","d7":"Jim Beam","d8":"Angel's Envy","d9":"Town Branch (Alltech)","d10":"Michter's Fort Nelson","d11":"Old Forester","d12":"Barton 1792","d13":"Bulleit Distilling Co.","d14":"Castle & Key","d15":"Wilderness Trail","d16":"Lux Row Distillers","d17":"Rabbit Hole","d18":"New Riff","d19":"Bardstown Bourbon Company","d20":"Starlight Distillery","d21":"Jack Daniel's","d22":"George Dickel","d23":"Nelson's Green Brier","d24":"MGP of Indiana","d25":"West Fork Whiskey Co.","d26":"Garrison Brothers","d27":"Balcones","d28":"Still Austin","d29":"Stranahan's","d30":"Breckenridge Distillery","d31":"Catoctin Creek","d32":"Virginia Distillery Company","d33":"Hudson Whiskey (Tuthilltown)","d34":"Kings County Distillery","d35":"Westland","d36":"Wyoming Whiskey","d37":"Pinhook","d38":"Smoke Wagon","d39":"Barrell Craft Spirits","d40":"Willet Distillery","d41":"Old Elk","d42":"Penelope Bourbon","d43":"Blue Run Spirits","d44":"Kentucky Owl","d45":"Stellum"};

const BRANDS_SEED = [

  // ── KENTUCKY LEGENDS ──
  { id: "d1", name: "Buffalo Trace", location: "Frankfort, KY", est: 1773, lat: 38.2107, lng: -84.8733, description: "One of America's oldest continuously operating distilleries, home to legendary expressions like Pappy Van Winkle, Eagle Rare, W.L. Weller, E.H. Taylor, and the eponymous Buffalo Trace. A National Historic Landmark sprawling across 130 acres.", image: "letter_B", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-113" },
  { id: "d2", name: "Wild Turkey", location: "Lawrenceburg, KY", est: 1869, lat: 38.0406, lng: -84.8897, description: "Perched atop a cliff overlooking the Kentucky River, Wild Turkey has been crafting bold, high-proof bourbon under master distiller Jimmy Russell since 1954. His son Eddie continues the legacy alongside him.", kbt: true, image: "eagle", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-67" },
  { id: "d3", name: "Woodford Reserve", location: "Versailles, KY", est: 1812, lat: 38.0717, lng: -84.7450, description: "A National Historic Landmark distillery nestled in the heart of horse country, known for its copper pot-still distillation and rich, complex flavor profiles. The official bourbon of the Kentucky Derby.", kbt: true, image: "horse", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-52" },
  { id: "d4", name: "Maker's Mark", location: "Loretto, KY", est: 1953, lat: 37.6336, lng: -85.4064, description: "Famous for its hand-dipped red wax seal and wheated bourbon recipe, Maker's Mark was a pioneer in the premium bourbon movement. The picturesque distillery campus is itself a National Historic Landmark.", kbt: true, image: "letter_M", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-44" },
  { id: "d5", name: "Four Roses", location: "Lawrenceburg, KY", est: 1888, lat: 38.0489, lng: -84.8961, description: "Unique among bourbon distilleries for using ten distinct recipes across two mash bills and five proprietary yeast strains, creating extraordinary complexity. The Spanish Mission-style distillery is an iconic landmark.", kbt: true, image: "letter_R", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-8" },
  { id: "d6", name: "Heaven Hill", location: "Bardstown, KY", est: 1935, lat: 37.8092, lng: -85.4669, description: "The largest independent, family-owned distiller in America, now in its third generation of Shapira family ownership. Home to Elijah Craig, Evan Williams, Larceny, Henry McKenna, and Parker's Heritage.", kbt: true, image: "mountain", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-31" },
  { id: "d7", name: "Jim Beam", location: "Clermont, KY", est: 1795, lat: 37.8031, lng: -85.6478, description: "The world's best-selling bourbon brand, distilled by the Beam family for over eight generations. The Clermont campus houses the American Outpost visitor center and is home to Knob Creek, Basil Hayden's, Baker's, and Booker's.", kbt: true, image: "glass", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-230" },
  { id: "d8", name: "Angel's Envy", location: "Louisville, KY", est: 2010, lat: 38.2542, lng: -85.7594, description: "Founded by the late Lincoln Henderson, a master distiller who spent decades at Brown-Forman. The Louisville Main Street distillery pioneered port wine barrel finishing and has become a must-visit destination.", kbt: true, ubt: true, image: "letter_A", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-20022" },
  { id: "d9", name: "Town Branch (Alltech)", location: "Lexington, KY", est: 2012, lat: 38.0496, lng: -84.4953, description: "Part of the Alltech Lexington Brewing & Distilling Company, Town Branch produces bourbon in the heart of downtown Lexington. Named after the Town Branch creek that runs beneath the city's streets.", kbt: true, image: "river", brandType: "distiller", isSourced: false, facilityId: "KY-I-15017" },
  { id: "d10", name: "Michter's Fort Nelson", location: "Louisville, KY", est: 1753, lat: 38.2573, lng: -85.7603, description: "Claiming heritage as America's first whiskey company, Michter's restored the historic Fort Nelson building on Louisville's Whiskey Row. Known for small-batch production with a focus on quality over quantity.", kbt: true, ubt: true, image: "castle", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-20003" },
  { id: "d11", name: "Old Forester", location: "Louisville, KY", est: 1870, lat: 38.2563, lng: -85.7567, description: "America's first bottled bourbon, founded by George Garvin Brown. The beautifully restored Whiskey Row distillery offers a window into bourbon's past. A Brown-Forman flagship with an unbroken distilling history even through Prohibition.", kbt: true, ubt: true, image: "scroll", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-354" },
  { id: "d12", name: "Barton 1792", location: "Bardstown, KY", est: 1879, lat: 37.8128, lng: -85.4761, description: "One of the oldest fully operating distilleries in Bardstown, featuring 29 barrel aging warehouses and a stunning limestone spring water source. Produces the 1792 line and Very Old Barton under the Sazerac umbrella.", kbt: true, image: "warehouse", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-12" },
  { id: "d13", name: "Bulleit Distilling Co.", location: "Shelbyville, KY", est: 2017, lat: 38.2120, lng: -85.2237, description: "A state-of-the-art distillery built by Diageo to meet soaring demand for Bulleit Bourbon. The modern facility combines cutting-edge technology with traditional bourbon-making craft on over 300 acres.", kbt: true, image: "shield", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-16" },
  { id: "d14", name: "Castle & Key", location: "Frankfort, KY", est: 2014, lat: 38.1992, lng: -84.8656, description: "Meticulously restored from the ruins of the Old Taylor Distillery, a 19th-century bourbon palace designed by architect Edmund H. Taylor Jr. The stunning grounds feature sunken gardens and a Roman-inspired springhouse.", kbt: true, image: "key", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-18" },
  { id: "d15", name: "Wilderness Trail", location: "Danville, KY", est: 2012, lat: 37.6456, lng: -84.7722, description: "A science-driven craft distillery founded by Dr. Pat Heist and Shane Baker, fermentation scientists who literally wrote the textbook on distilling microbiology. Sweet mash process produces distinctively clean, fruity bourbon.", kbt: true, image: "lab", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-20005" },
  { id: "d16", name: "Lux Row Distillers", location: "Bardstown, KY", est: 2018, lat: 37.8225, lng: -85.4617, description: "A premium distillery built by Luxco, makers of Ezra Brooks, Rebel, Blood Oath, and David Nicholson. The stunning 18,000-sq-ft visitor center anchors Bardstown's growing bourbon tourism corridor.", kbt: true, image: "letter_L", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-20044" },
  { id: "d17", name: "Rabbit Hole", location: "Louisville, KY", est: 2012, lat: 38.2348, lng: -85.7428, description: "Founded by Kaveh Zamanian with a focus on transparency and innovation. The architecturally striking Louisville distillery features a towering glass atrium and uses unique grain recipes including honey malted barley.", kbt: true, ubt: true, image: "letter_R", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-20041" },
  { id: "d18", name: "New Riff", location: "Newport, KY", est: 2014, lat: 39.0914, lng: -84.4936, description: "A fiercely independent Northern Kentucky distillery founded by Ken Lewis. Known for its commitment to bottled-in-bond standards and full transparency with age statements, mash bills, and barrel details on every bottle.", kbt: true, image: "letter_N", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-20016" },
  { id: "d19", name: "Bardstown Bourbon Company", location: "Bardstown, KY", est: 2016, lat: 37.8081, lng: -85.4528, description: "A next-generation bourbon distillery and tourism destination with a 100-room luxury hotel, restaurant, and custom distilling services for partner brands. Produces its own Discovery and Fusion series alongside contract distilling.", kbt: true, image: "star", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-20037" },
  { id: "d20", name: "Starlight Distillery", location: "Starlight, IN", est: 2001, lat: 38.3422, lng: -85.9467, description: "A family-owned farm distillery in Southern Indiana's rolling hills, operating from a property the Huber family has farmed since 1843. Known for Carl T. Huber bourbon and unique wine barrel-finished expressions.", image: "sun", brandType: "distiller", isSourced: false, facilityId: "DSP-IN-31" },

  // ── TENNESSEE ──
  { id: "d21", name: "Jack Daniel's", location: "Lynchburg, TN", est: 1866, lat: 35.2832, lng: -86.3744, description: "The iconic Lynchburg distillery produces Tennessee whiskey using the Lincoln County Process — charcoal mellowing through sugar maple charcoal before barrel aging. The Cave Spring Hollow water source has been used since the 1800s.", image: "letter_J", brandType: "distiller", isSourced: false, facilityId: "DSP-TN-1" },
  { id: "d22", name: "George Dickel", location: "Tullahoma, TN", est: 1870, lat: 35.3622, lng: -86.2094, description: "Nestled in Cascade Hollow, George Dickel uses a chill-charcoal mellowing process and spells whisky without the 'e' in the Scottish tradition. The distillery sits beside a pristine limestone spring in the Tennessee hills.", image: "spring", brandType: "distiller", isSourced: false, facilityId: "DSP-TN-2" },
  { id: "d23", name: "Nelson's Green Brier", location: "Nashville, TN", est: 2014, lat: 36.1732, lng: -86.7919, description: "Revived by brothers Andy and Charlie Nelson, descendants of the original Green Brier distillery that was Tennessee's largest before Prohibition. Now producing Belle Meade Bourbon from their Marathon Village location.", image: "bell", brandType: "distiller", isSourced: false, facilityId: "DSP-TN-5" },

  // ── INDIANA ──
  { id: "d24", name: "MGP of Indiana", location: "Lawrenceburg, IN", est: 1847, lat: 39.0909, lng: -84.8499, description: "One of the most important distilleries in American whiskey, MGP (formerly Seagram's/LDI) produces bourbon and rye for dozens of brands. Their 95/5 rye and 21% rye bourbon mash bills are industry benchmarks.", image: "copper", brandType: "distiller", isSourced: false, facilityId: "DSP-IN-7" },
  { id: "d25", name: "West Fork Whiskey Co.", location: "Indianapolis, IN", est: 2015, lat: 39.7684, lng: -86.1581, description: "An Indianapolis craft distillery focused on Indiana-grown grains. Their estate bourbon uses corn, wheat, and malted barley grown within 30 miles of the distillery, creating a true grain-to-glass experience.", image: "mashbill", brandType: "distiller", isSourced: false, facilityId: "DSP-IN-21028" },

  // ── TEXAS ──
  { id: "d26", name: "Garrison Brothers", location: "Hye, TX", est: 2006, lat: 30.2524, lng: -98.3463, description: "The first legal bourbon distillery in Texas, aging barrels in the intense Hill Country heat which accelerates maturation dramatically. Their estate-grown white corn and on-site spring water create distinctly Texan bourbon.", image: "letter_G", brandType: "distiller", isSourced: false, facilityId: "DSP-TX-15013" },
  { id: "d27", name: "Balcones", location: "Waco, TX", est: 2008, lat: 31.5493, lng: -97.1467, description: "A Texas craft whiskey pioneer operating from a converted industrial building under a bridge in Waco. Known for blue corn bourbon, their Texas-style whiskeys embrace bold, adventurous flavors shaped by the extreme climate.", image: "flame", brandType: "distiller", isSourced: false, facilityId: "DSP-TX-20062" },
  { id: "d28", name: "Still Austin", location: "Austin, TX", est: 2017, lat: 30.2427, lng: -97.7681, description: "A grain-to-glass distillery in the heart of South Austin, using Texas-grown grains exclusively. Master Blender Nancy Fraley creates approachable yet complex bourbon from an all-local supply chain.", image: "still", brandType: "distiller", isSourced: false, facilityId: "DSP-TX-20165" },

  // ── COLORADO ──
  { id: "d29", name: "Stranahan's", location: "Denver, CO", est: 2004, lat: 39.7392, lng: -104.9903, description: "Colorado's first legal distillery since Prohibition, founded after a barn fire brought together a firefighter and a brewer. Their American single malt set the standard, and they've since expanded into bourbon-style expressions.", image: "letter_S", brandType: "distiller", isSourced: false, facilityId: "DSP-CO-15022" },
  { id: "d30", name: "Breckenridge Distillery", location: "Breckenridge, CO", est: 2008, lat: 39.4817, lng: -106.0384, description: "One of the world's highest-altitude distilleries at 9,600 feet. The thin mountain air and dramatic temperature swings create unique aging conditions. Their bourbon blend uses high-rye distillate finished in Colorado.", image: "mountain", brandType: "distiller", isSourced: false, facilityId: "BR-CO-21575" },

  // ── VIRGINIA / EAST COAST ──
  { id: "d31", name: "Catoctin Creek", location: "Purcellville, VA", est: 2009, lat: 39.1368, lng: -77.7147, description: "Virginia's first legal rye whiskey distillery since Prohibition, and an organic craft pioneer. Founders Becky and Scott Harris produce bourbon and rye from Virginia-grown grains using traditional copper pot stills.", image: "river", brandType: "distiller", isSourced: false, facilityId: "DSP-VA-15012" },
  { id: "d32", name: "Virginia Distillery Company", location: "Lovingston, VA", est: 2015, lat: 37.7818, lng: -78.8986, description: "Set in the foothills of the Blue Ridge Mountains, this distillery combines American and Scottish whisky-making traditions. Known for their Courage & Conviction single malt and Virginia Highland series.", image: "letter_V", brandType: "distiller", isSourced: false, facilityId: "DSP-VA-20002" },

  // ── NEW YORK ──
  { id: "d33", name: "Hudson Whiskey (Tuthilltown)", location: "Gardiner, NY", est: 2003, lat: 41.6862, lng: -74.1616, description: "New York's first whiskey distillery since Prohibition, Tuthilltown Spirits pioneered the craft whiskey movement from a converted granary in the Hudson Valley. Famed for small barrels and Baby Bourbon.", image: "letter_H", brandType: "distiller", isSourced: false, facilityId: "DSP-NY-15001" },
  { id: "d34", name: "Kings County Distillery", location: "Brooklyn, NY", est: 2010, lat: 40.6892, lng: -73.9708, description: "Operating from the Brooklyn Navy Yard's Paymaster Building, Kings County is New York City's oldest operating whiskey distillery. Their bourbon and peated bourbon are made from New York organic corn.", image: "crown", brandType: "distiller", isSourced: false, facilityId: "BR-NY-21213" },

  // ── PACIFIC NORTHWEST / WEST ──
  { id: "d35", name: "Westland", location: "Seattle, WA", est: 2010, lat: 47.5675, lng: -122.3425, description: "A Seattle distillery dedicated to American single malt whiskey, using locally grown barley and Pacific Northwest peat. Their Garryana series, aged in native Garry oak, has earned international acclaim.", image: "pine", brandType: "distiller", isSourced: false, facilityId: "DSP-WA-21164" },
  { id: "d36", name: "Wyoming Whiskey", location: "Kirby, WY", est: 2009, lat: 43.8147, lng: -107.7467, description: "Founded by a ranching family in the Big Horn Basin, Wyoming Whiskey uses estate-grown grain irrigated by limestone-filtered mountain water. Former Maker's Mark master distiller Steve Nally designed the recipe.", image: "letter_W", brandType: "distiller", isSourced: false, facilityId: "DSP-WY-15004" },

  // ── CRAFT / EMERGING ──
  { id: "d37", name: "Pinhook", location: "Frankfort, KY", est: 2010, lat: 38.2009, lng: -84.8731, description: "Pinhook is a bourbon brand inspired by thoroughbred horse racing — each annual release is a unique vintage, much like a racehorse. They source and blend from Castle & Key distillery, offering a new character each year.", image: "horse", brandType: "ndp", isSourced: true, facilityId: "DSP-KY-18" },
  { id: "d38", name: "Smoke Wagon", location: "Las Vegas, NV", est: 2017, lat: 36.1699, lng: -115.1398, description: "A Las Vegas-based bourbon brand blending sourced MGP and other distillates to create bold, desert-inspired expressions. Their Uncut Unfiltered release has become a cult favorite among barrel-proof enthusiasts.", image: "flame", brandType: "ndp", isSourced: true, facilityId: "DSP-NV-15006" },
  { id: "d39", name: "Barrell Craft Spirits", location: "Louisville, KY", est: 2013, lat: 38.2527, lng: -85.7585, description: "A blending house that sources exceptional barrels from distilleries across America and beyond, creating award-winning limited batches. Founded by Joe Beatrice, their releases consistently rank among the best in whiskey.", image: "lab", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-20046" },
  { id: "d40", name: "Willet Distillery", location: "Bardstown, KY", est: 1936, lat: 37.8156, lng: -85.4867, description: "A storied family distillery revived by the Kulsveen family. Famous for their pot-still-shaped bottles and family estate releases. Now distilling their own bourbon again after years of sourcing exceptional aged stock.", kbt: true, image: "still", brandType: "distiller", isSourced: false, facilityId: "DSP-KY-78" },
  { id: "d41", name: "Old Elk", location: "Fort Collins, CO", est: 2013, lat: 40.5853, lng: -105.0844, description: "Co-founded by Curt Richardson and master distiller Greg Metze, formerly of MGP. Known for an unusually high malted barley content in their mash bill, which adds a distinctive smoothness and sweetness.", image: "letter_E", brandType: "distiller", isSourced: false, facilityId: "DSP-CO-20042" },
  { id: "d42", name: "Penelope Bourbon", location: "Louisville, KY", est: 2018, lat: 38.2527, lng: -85.7585, description: "Named after the founders' daughter, Penelope blends four different bourbon mash bills for complexity. Rapidly growing from a sourced brand into a serious player with unique four-grain and rosé cask-finished expressions.", image: "letter_P", brandType: "blender", isSourced: true, facilityId: "DSP-NJ-21058" },
  { id: "d43", name: "Blue Run Spirits", location: "Georgetown, KY", est: 2020, lat: 38.2098, lng: -84.5588, description: "Founded by a team of Nike and tech-industry veterans who bring design thinking to bourbon. Known for stunning bottle design and sourcing exceptional single barrels, with master distiller Jim Rutledge consulting.", image: "diamond", brandType: "ndp", isSourced: true, facilityId: "DSP-KY-20037" },
  { id: "d44", name: "Kentucky Owl", location: "Bardstown, KY", est: 2014, lat: 37.8092, lng: -85.4669, description: "A revived heritage brand with roots dating to the 1800s, resurrected by master blender Dixon Dedman. Their Confiscated and Wiseman lines honor the distillery's pre-Prohibition history. Now building a new distillery.", image: "letter_K", brandType: "blender", isSourced: true, facilityId: "DSP-LA-20001" },
  { id: "d45", name: "Stellum", location: "Louisville, KY", est: 2021, lat: 38.2527, lng: -85.7585, description: "Barrell Craft Spirits' sister brand focused on accessible, high-quality blended bourbon and rye. Named for star constellations, each release blends distillates from Kentucky, Indiana, and Tennessee.", image: "compass", brandType: "ndp", isSourced: true, facilityId: "DSP-KY-20046" },
];

const BOURBONS_SEED = [

  // ── BUFFALO TRACE (d1) ──
  { id: "b1", name: "Buffalo Trace", brandId: "d1", proof: 90, age: "NAS", mashBill: "#1 (low rye)", msrp: 28, type: "Bourbon, Straight", notes: "Vanilla, caramel, toffee, with hints of anise and dark fruit. Smooth and approachable." },
  { id: "b2", name: "Eagle Rare 10 Year", brandId: "brand-eagle-rare", proof: 90, age: "10 years", mashBill: "#1 (low rye)", msrp: 35, type: "Bourbon, Straight", notes: "Rich toffee, orange peel, toasted oak. Complex and rewarding." },
  { id: "b3", name: "Blanton's Single Barrel", brandId: "brand-blanton-s", proof: 93, age: "NAS (~6-8 yrs)", mashBill: "#2 (high rye)", msrp: 65, type: "Bourbon, Single Barrel", notes: "Citrus, vanilla, caramel, with a spicy rye finish. Velvety mouthfeel." },
  { id: "b16", name: "W.L. Weller Special Reserve", brandId: "brand-weller", proof: 90, age: "NAS", mashBill: "Wheated", msrp: 25, type: "Bourbon, Straight", notes: "Soft caramel, honey, vanilla, and wheat sweetness. Gentle and approachable wheated bourbon." },
  { id: "b17", name: "W.L. Weller Antique 107", brandId: "brand-weller", proof: 107, age: "NAS", mashBill: "Wheated", msrp: 30, type: "Bourbon, Straight", notes: "Bold wheat sweetness, cinnamon, dried fruit, and honey. Excellent backbone for cocktails or neat." },
  { id: "b18", name: "W.L. Weller 12 Year", brandId: "brand-weller", proof: 90, age: "12 years", mashBill: "Wheated", msrp: 35, type: "Bourbon, Straight", notes: "Refined wheat sweetness, dark cherry, almond, vanilla. Called 'poor man's Pappy' for its lineage." },
  { id: "b19", name: "W.L. Weller Full Proof", brandId: "brand-weller", proof: 114, age: "NAS", mashBill: "Wheated", msrp: 50, type: "Bourbon, Straight", notes: "Rich caramel, baking spice, dark fruit, and leather at full proof. Viscous and complex." },
  { id: "b20", name: "E.H. Taylor Small Batch", brandId: "brand-colonel-e-h-taylor", proof: 100, age: "NAS", mashBill: "#1 (low rye)", msrp: 42, type: "Bourbon, Bottled in Bond", notes: "Caramel corn, dried fruit, vanilla, oak. Bottled in Bond at 100 proof. Balanced and classic." },
  { id: "b21", name: "E.H. Taylor Single Barrel", brandId: "brand-colonel-e-h-taylor", proof: 100, age: "NAS", mashBill: "#1 (low rye)", msrp: 65, type: "Bourbon, Bottled in Bond", notes: "Toasted oak, butterscotch, dark chocolate, baking spice. Each barrel varies beautifully." },
  { id: "b22", name: "Pappy Van Winkle 15 Year", brandId: "d1", proof: 107, age: "15 years", mashBill: "Wheated", msrp: 120, type: "Bourbon, Straight", notes: "Complex caramel, cherries, vanilla, toasted oak, maple. The holy grail of wheated bourbon.", limitedRelease: true },
  { id: "b23", name: "Pappy Van Winkle 20 Year", brandId: "d1", proof: 90.4, age: "20 years", mashBill: "Wheated", msrp: 200, type: "Bourbon, Straight", notes: "Deeply complex with aged leather, dark fruit, vanilla cream, toasted pecan, and ancient oak. A masterpiece.", limitedRelease: true },
  { id: "b24", name: "Stagg Jr. (Stagg)", brandId: "brand-stagg", proof: 131, age: "NAS (~8-9 yrs)", mashBill: "#1 (low rye)", msrp: 55, type: "Bourbon, Barrel Proof", notes: "Massive dark fruit, brown sugar, cinnamon, charred oak, and a finish that lasts for days. Uncut power." },
  { id: "b25", name: "George T. Stagg", brandId: "brand-george-t-stagg", proof: 138, age: "15+ years", mashBill: "#1 (low rye)", msrp: 100, type: "Bourbon, Barrel Proof", notes: "The ultimate barrel proof bourbon. Dark chocolate, espresso, black cherry, tobacco, and infinite oak. Legendary.", limitedRelease: true },
  { id: "b26", name: "Blanton's Gold", brandId: "brand-blanton-s", proof: 103, age: "NAS (~8 yrs)", mashBill: "#2 (high rye)", msrp: 80, type: "Bourbon, Single Barrel", notes: "Amped-up Blanton's with more caramel, spice, and citrus at higher proof. Richer and bolder." },
  { id: "b27", name: "Sazerac Rye", brandId: "cola-brand-sazeracrye-1771051555224", proof: 90, age: "NAS (6 yrs)", mashBill: "Rye", msrp: 28, type: "Rye, Straight", notes: "Peppery rye spice, clove, anise, gentle sweetness. Excellent value rye whiskey." },

  // ── WILD TURKEY (d2) ──
  { id: "b4", name: "Wild Turkey 101", brandId: "d2", proof: 101, age: "NAS (6-8 yrs)", mashBill: "75/13/12", msrp: 25, type: "Bourbon, Straight", notes: "Bold vanilla, caramel, and baking spice. A bartender's go-to with incredible value." },
  { id: "b5", name: "Russell's Reserve 10 Year", brandId: "d2", proof: 90, age: "10 years", mashBill: "75/13/12", msrp: 38, type: "Bourbon, Straight", notes: "Rich oak, butterscotch, dried fruit. Sophisticated sibling to Wild Turkey 101." },
  { id: "b28", name: "Wild Turkey Rare Breed", brandId: "d2", proof: 116.8, age: "NAS (6-12 yrs)", mashBill: "75/13/12", msrp: 45, type: "Bourbon, Barrel Proof", notes: "Barrel proof blend of 6, 8, and 12-year bourbons. Rich caramel, deep spice, tobacco, toasted oak. Phenomenal." },
  { id: "b29", name: "Russell's Reserve Single Barrel", brandId: "d2", proof: 110, age: "NAS (~8-10 yrs)", mashBill: "75/13/12", msrp: 60, type: "Bourbon, Single Barrel", notes: "Non-chill filtered at 110 proof. Intense vanilla, dark cherry, leather, and baking spice. Eddie Russell's gem." },
  { id: "b30", name: "Russell's Reserve 13 Year", brandId: "d2", proof: 114.8, age: "13 years", mashBill: "75/13/12", msrp: 70, type: "Bourbon, Barrel Proof", notes: "Deeply aged Wild Turkey at barrel proof. Dark caramel, toasted pecan, pipe tobacco, dried cherry. Magnificent." },
  { id: "b31", name: "Wild Turkey Longbranch", brandId: "d2", proof: 86, age: "NAS (8 yrs)", mashBill: "75/13/12", msrp: 40, type: "Bourbon, Straight", notes: "Refined with Texas mesquite and oak charcoal. Smoky sweetness, light citrus, creamy vanilla. Matthew McConaughey collaboration.", discontinued: true },
  { id: "b32", name: "Kentucky Spirit", brandId: "d2", proof: 101, age: "NAS (~8-10 yrs)", mashBill: "75/13/12", msrp: 55, type: "Bourbon, Single Barrel", notes: "Wild Turkey's original single barrel offering. Rich toffee, dark fruit, and signature Turkey spice." },

  // ── WOODFORD RESERVE (d3) ──
  { id: "b6", name: "Woodford Reserve Distiller's Select", brandId: "d3", proof: 90.4, age: "NAS", mashBill: "72/18/10", msrp: 38, type: "Bourbon, Straight", notes: "Dark chocolate, espresso, dried cherry, tobacco. Beautifully balanced." },
  { id: "b33", name: "Woodford Reserve Double Oaked", brandId: "d3", proof: 90.4, age: "NAS", mashBill: "72/18/10", msrp: 55, type: "Bourbon, Straight", notes: "Extra matured in a second heavily toasted barrel. Dark caramel, apple butter, chocolate, and rich vanilla. Dessert in a glass." },
  { id: "b34", name: "Woodford Reserve Batch Proof", brandId: "d3", proof: 123.6, age: "NAS", mashBill: "72/18/10", msrp: 130, type: "Bourbon, Barrel Proof", notes: "Annual limited release at barrel proof. Dark fruit, deep char, cocoa, espresso. The full Woodford experience uncut." },
  { id: "b35", name: "Woodford Reserve Rye", brandId: "d3", proof: 90.4, age: "NAS", mashBill: "53/33/14 (rye)", msrp: 38, type: "Rye, Straight", notes: "Peppery and herbaceous with clove, mint, and dark fruit. Rich and balanced rye." },
  { id: "b36", name: "Woodford Reserve Wheat", brandId: "d3", proof: 90.4, age: "NAS", mashBill: "52/20/28 (wheat)", msrp: 38, type: "Whiskey, Corn", notes: "Soft and sweet with fruit-forward notes, honey, floral elements, and gentle spice. Unique grain expression." },
  { id: "b37", name: "Woodford Reserve Malt", brandId: "d3", proof: 90.4, age: "NAS", mashBill: "51/47/2 (malt)", msrp: 38, type: "Whiskey, American Single Malt", notes: "Rich malty sweetness, dark chocolate, dried fruit, and coffee. A completely different Woodford experience." },

  // ── MAKER'S MARK (d4) ──
  { id: "b7", name: "Maker's Mark", brandId: "d4", proof: 90, age: "NAS (~6-7 yrs)", mashBill: "70/16/14 (wheat)", msrp: 28, type: "Bourbon, Straight", notes: "Soft wheat sweetness, caramel, vanilla, with a gentle oak finish." },
  { id: "b8", name: "Maker's Mark 46", brandId: "d4", proof: 94, age: "NAS", mashBill: "70/16/14 (wheat)", msrp: 40, type: "Bourbon, Straight", notes: "Rich caramel, baking spice, oak. More complex and spicy than standard Maker's." },
  { id: "b38", name: "Maker's Mark Cask Strength", brandId: "d4", proof: 110, age: "NAS (~7 yrs)", mashBill: "70/16/14 (wheat)", msrp: 40, type: "Bourbon, Barrel Proof", notes: "All the Maker's character amplified. Rich caramel, vanilla, red fruit, and a warming wheat finish. Excellent value for cask strength." },
  { id: "b39", name: "Maker's Mark Private Select", brandId: "d4", proof: 110, age: "NAS", mashBill: "70/16/14 (wheat)", msrp: 70, type: "Bourbon, Single Barrel", notes: "Finished with custom stave profiles. Each pick is unique — varying from fruit-forward to spice-driven. A choose-your-own-adventure bourbon." },
  { id: "b40", name: "Maker's Mark Wood Finishing Series", brandId: "d4", proof: 110.6, age: "NAS", mashBill: "70/16/14 (wheat)", msrp: 60, type: "Bourbon, Limited Edition", notes: "Annual limited release exploring different wood stave finishes. Complex, innovative expressions that push Maker's boundaries." },
  { id: "b41", name: "Maker's Mark FAE-01", brandId: "d4", proof: 110.6, age: "NAS", mashBill: "70/16/14 (wheat)", msrp: 60, type: "Bourbon, Limited Edition", notes: "French oak stave finished. Wine-like tannins, dark berry, vanilla custard, and toasted caramel with a silky mouthfeel.", discontinued: true, limitedRelease: true },

  // ── FOUR ROSES (d5) ──
  { id: "b9", name: "Four Roses Single Barrel", brandId: "d5", proof: 100, age: "NAS (~7-9 yrs)", mashBill: "OBSV recipe", msrp: 45, type: "Bourbon, Single Barrel", notes: "Ripe plum, cocoa, maple syrup, spice. Exceptional depth and complexity." },
  { id: "b42", name: "Four Roses Small Batch", brandId: "d5", proof: 90, age: "NAS", mashBill: "4-recipe blend", msrp: 32, type: "Bourbon, Small Batch", notes: "Mellow fruit, honey, and floral notes with a soft spice finish. Approachable and elegant." },
  { id: "b43", name: "Four Roses Small Batch Select", brandId: "d5", proof: 104, age: "NAS (~6-7 yrs)", mashBill: "6-recipe blend", msrp: 55, type: "Bourbon, Small Batch", notes: "Six of the ten recipes blended at higher proof. Richer body, dark fruit, baking spice, cocoa. Outstanding." },
  { id: "b44", name: "Four Roses Yellow Label", brandId: "d5", proof: 80, age: "NAS (~5 yrs)", mashBill: "10-recipe blend", msrp: 22, type: "Bourbon, Straight", notes: "Light and crisp with floral notes, pear, and honey. A gentle introduction to Four Roses." },
  { id: "b45", name: "Four Roses Limited Edition Small Batch", brandId: "d5", proof: 112, age: "12-23 years", mashBill: "Varies annually", msrp: 140, type: "Bourbon, Limited Edition", notes: "Annual release blending hand-selected aged barrels. Each year's blend tells a different story. Highly allocated and collectible.", limitedRelease: true },

  // ── HEAVEN HILL (d6) ──
  { id: "b10", name: "Elijah Craig Small Batch", brandId: "d6", proof: 94, age: "NAS (~8-12 yrs)", mashBill: "78/10/12", msrp: 32, type: "Bourbon, Small Batch", notes: "Char, vanilla, butterscotch, nutmeg. Outstanding value for the age." },
  { id: "b11", name: "Evan Williams Single Barrel", brandId: "d6", proof: 86.6, age: "~7 years", mashBill: "78/10/12", msrp: 28, type: "Bourbon, Single Barrel", notes: "Honey, caramel, light oak, mint. Approachable and vintage-dated." },
  { id: "b15", name: "Elijah Craig Barrel Proof", batch: "C924", brandId: "d6", proof: 131.4, age: "12 years", mashBill: "78/10/12", msrp: 65, type: "Bourbon, Barrel Proof", notes: "Intensely rich dark fruit, caramel, char, cinnamon. A powerhouse sipper." },
  { id: "b46", name: "Evan Williams Black Label", brandId: "d6", proof: 86, age: "NAS (~4-5 yrs)", mashBill: "78/10/12", msrp: 15, type: "Bourbon, Straight", notes: "Caramel, vanilla, light oak. The best-selling bourbon in America by volume. Unbeatable at the price." },
  { id: "b47", name: "Evan Williams Bottled-in-Bond", brandId: "d6", proof: 100, age: "NAS (~4-6 yrs)", mashBill: "78/10/12", msrp: 18, type: "Bourbon, Bottled in Bond", notes: "More structured than Black Label with richer caramel, spice, and oak. One of the best values in all bourbon." },
  { id: "b48", name: "Henry McKenna 10 Year Single Barrel", brandId: "d6", proof: 100, age: "10 years", mashBill: "78/10/12", msrp: 40, type: "Bourbon, Bottled in Bond", notes: "10-year single barrel at BiB proof. Vanilla, caramel, nuts, and balanced oak. Won Best in Show at the San Francisco World Spirits Competition." },
  { id: "b49", name: "Larceny Small Batch", brandId: "d6", proof: 92, age: "NAS (~6-8 yrs)", mashBill: "68/20/12 (wheat)", msrp: 25, type: "Bourbon, Straight", notes: "Buttery sweetness, honey, bread pudding, and gentle wheat warmth. Heaven Hill's wheated offering." },
  { id: "b50", name: "Larceny Barrel Proof", brandId: "d6", proof: 124.4, age: "NAS (~6-8 yrs)", mashBill: "68/20/12 (wheat)", msrp: 50, type: "Bourbon, Barrel Proof", notes: "All the Larceny sweetness at cask strength. Brown sugar, butterscotch, peanut brittle, and a long, warm finish. Released three times annually." },
  { id: "b51", name: "Elijah Craig Toasted Barrel", brandId: "d6", proof: 94, age: "NAS", mashBill: "78/10/12", msrp: 50, type: "Bourbon, Finished", notes: "Finished in a second toasted barrel. Rich vanilla, caramel apple, almond, and extra sweetness from the toasting." },
  { id: "b52", name: "Parker's Heritage Collection", brandId: "d6", proof: 120, age: "Varies", mashBill: "Varies", msrp: 120, type: "Bourbon, Limited Edition", notes: "Annual limited release honoring the late master distiller Parker Beam. Each edition is unique. Proceeds support ALS research.", limitedRelease: true },
  { id: "b53", name: "Old Fitzgerald Bottled-in-Bond", brandId: "d6", proof: 100, age: "Varies (9-17 yrs)", mashBill: "68/20/12 (wheat)", msrp: 90, type: "Bourbon, Bottled in Bond", notes: "Decanter-style bottle with rotating age statements. Dark caramel, dried fruit, vanilla, and refined wheat sweetness. A collector's treasure." },

  // ── JIM BEAM (d7) ──
  { id: "b12", name: "Knob Creek 9 Year", brandId: "d7", proof: 100, age: "9 years", mashBill: "77/13/10", msrp: 35, type: "Bourbon, Straight", notes: "Deep maple, toasted nuts, rich oak, dark caramel. Full-bodied and warming." },
  { id: "b13", name: "Booker's Bourbon", batch: "2024-01 (Springfield Batch)", brandId: "d7", proof: 124.2, age: "6 yrs 5 mos", mashBill: "77/13/10", msrp: 90, type: "Bourbon, Barrel Proof", notes: "Intense vanilla, brown sugar, oak, tobacco, smoke. Uncut and unfiltered power." },
  { id: "b142", name: "Booker's Bourbon", batch: "2024-02 (The Lumberyard Batch)", brandId: "d7", proof: 125.4, age: "6 yrs 7 mos", mashBill: "77/13/10", msrp: 90, type: "Bourbon, Barrel Proof", notes: "Robust oak, charred wood, caramel corn, and warm cinnamon. Slightly more tannic than 2024-01." },
  { id: "b143", name: "Booker's Bourbon", batch: "2025-01 (The Storyteller Batch)", brandId: "d7", proof: 126.8, age: "7 yrs 1 mo", mashBill: "77/13/10", msrp: 100, type: "Bourbon, Barrel Proof", notes: "Rich dark fruit, molasses, vanilla cream, and a long peppery finish. Extra age brings depth and refinement." },
  { id: "b54", name: "Jim Beam White Label", brandId: "d7", proof: 80, age: "4 years", mashBill: "77/13/10", msrp: 18, type: "Bourbon, Straight", notes: "Light vanilla, corn sweetness, and mild oak. The world's best-selling bourbon for a reason." },
  { id: "b55", name: "Jim Beam Black Extra Aged", brandId: "d7", proof: 86, age: "NAS (~8 yrs)", mashBill: "77/13/10", msrp: 24, type: "Bourbon, Straight", notes: "Richer than White Label with more caramel, oak, and a smoother finish. Excellent value for the age." },
  { id: "b56", name: "Basil Hayden's", brandId: "d7", proof: 80, age: "NAS (~8 yrs)", mashBill: "63/27/10 (high rye)", msrp: 40, type: "Bourbon, Straight", notes: "Light-bodied with prominent rye spice, tea, pepper, and citrus. The most approachable of the Small Batch Collection." },
  { id: "b57", name: "Baker's 7 Year Single Barrel", brandId: "d7", proof: 107, age: "7 years", mashBill: "77/13/10", msrp: 60, type: "Bourbon, Single Barrel", notes: "Rich fruit, toasted grain, vanilla, and oak. Each barrel hand-selected. A tribute to Baker Beam." },
  { id: "b58", name: "Knob Creek Single Barrel", brandId: "d7", proof: 120, age: "9 years", mashBill: "77/13/10", msrp: 50, type: "Bourbon, Single Barrel", notes: "All the Knob Creek character at higher proof. Deep maple, toasted nuts, heavy oak, and dark caramel. Intense and rewarding." },
  { id: "b59", name: "Knob Creek 12 Year", brandId: "d7", proof: 100, age: "12 years", mashBill: "77/13/10", msrp: 60, type: "Bourbon, Straight", notes: "Extra aging adds layers of dark fruit, leather, tobacco, and complex oak. More refined than the 9-year." },
  { id: "b60", name: "Little Book", brandId: "d7", proof: 118, age: "Varies", mashBill: "Varies", msrp: 125, type: "Whiskey, Tennessee", notes: "Freddie Noe's annual experimental blend. Each chapter tells a unique story through innovative blending of different whiskey styles." },

  // ── ANGEL'S ENVY (d8) ──
  { id: "b14", name: "Angel's Envy Bourbon", brandId: "d8", proof: 86.6, age: "4-6 years", mashBill: "72/18/10", msrp: 50, type: "Bourbon, Finished", notes: "Port wine sweetness, ripe cherry, vanilla, maple. Silky and unique." },
  { id: "b61", name: "Angel's Envy Cask Strength", brandId: "d8", proof: 120, age: "NAS (5-7 yrs)", mashBill: "72/18/10", msrp: 230, type: "Bourbon, Barrel Proof", notes: "Port-finished at cask strength. Intense dark fruit, chocolate, port wine, and rich oak. Annual limited release. Worth the hunt.", limitedRelease: true },
  { id: "b62", name: "Angel's Envy Rye", brandId: "d8", proof: 100, age: "NAS", mashBill: "95/5 rye", msrp: 90, type: "Rye, Straight", notes: "Caribbean rum cask-finished rye. Tropical fruit, baking spice, maple, and vanilla. Completely unique rye experience." },

  // ── TOWN BRANCH (d9) ──
  { id: "b63", name: "Town Branch Bourbon", brandId: "d9", proof: 80, age: "NAS (3-4 yrs)", mashBill: "72/15/13", msrp: 35, type: "Bourbon, Straight", notes: "Caramel, vanilla, light citrus, and honey. Clean and easy-drinking Lexington bourbon." },

  // ── MICHTER'S (d10) ──
  { id: "b64", name: "Michter's US*1 Small Batch", brandId: "d10", proof: 91.4, age: "NAS", mashBill: "Undisclosed", msrp: 45, type: "Bourbon, Small Batch", notes: "Caramel, brown butter, dried fruit, and subtle spice. Exceptionally well-balanced and approachable." },
  { id: "b65", name: "Michter's US*1 Single Barrel", brandId: "d10", proof: 94.4, age: "NAS", mashBill: "Undisclosed", msrp: 55, type: "Bourbon, Single Barrel", notes: "Richer and spicier than the Small Batch. Toasted oak, dark caramel, coffee, and stone fruit. Every barrel is hand-selected." },
  { id: "b66", name: "Michter's Toasted Barrel Finish", brandId: "d10", proof: 91.4, age: "NAS", mashBill: "Undisclosed", msrp: 90, type: "Bourbon, Finished", notes: "Transferred to a custom toasted barrel. Extra vanilla, brown sugar, honey, and toasted marshmallow. Dessert-like." },
  { id: "b67", name: "Michter's 10 Year Single Barrel", brandId: "d10", proof: 94.4, age: "10 years", mashBill: "Undisclosed", msrp: 150, type: "Bourbon, Single Barrel", notes: "Deeply complex with dark chocolate, aged leather, dried cherry, tobacco, and long oak finish. Magnificent when found." },
  { id: "b68", name: "Michter's 20 Year", brandId: "d10", proof: 114.2, age: "20 years", mashBill: "Undisclosed", msrp: 700, type: "Bourbon, Limited Edition", notes: "Only released when barrels meet the highest quality threshold. Ancient oak, dark fruit, leather, and staggering complexity. Unicorn status.", limitedRelease: true },

  // ── OLD FORESTER (d11) ──
  { id: "b69", name: "Old Forester 86 Proof", brandId: "d11", proof: 86, age: "NAS (~4 yrs)", mashBill: "72/18/10", msrp: 22, type: "Bourbon, Straight", notes: "Orange peel, vanilla, banana, and light oak. America's first bottled bourbon remains a solid daily pour." },
  { id: "b70", name: "Old Forester 1897 Bottled-in-Bond", brandId: "d11", proof: 100, age: "NAS (~4-5 yrs)", mashBill: "72/18/10", msrp: 30, type: "Bourbon, Bottled in Bond", notes: "Structured and complex with caramel, dark fruit, oak, and baking spice at BiB proof. Excellent value." },
  { id: "b71", name: "Old Forester 1910 Old Fine Whisky", brandId: "d11", proof: 93, age: "NAS", mashBill: "72/18/10", msrp: 55, type: "Bourbon, Finished", notes: "Double-barreled in a lightly charred second barrel. Intense chocolate, caramel, dark cherry, and rich vanilla. Exceptional." },
  { id: "b72", name: "Old Forester 1920 Prohibition Style", brandId: "d11", proof: 115, age: "NAS", mashBill: "72/18/10", msrp: 60, type: "Bourbon, Straight", notes: "Dark caramel, chocolate truffle, banana foster, and cherry. Bold and intense. One of the best values in premium bourbon." },
  { id: "b73", name: "Old Forester Single Barrel", brandId: "d11", proof: 90, age: "NAS (~5 yrs)", mashBill: "72/18/10", msrp: 45, type: "Bourbon, Single Barrel", notes: "Each barrel hand-selected. Bright fruit, caramel, vanilla, and moderate oak. Great for discovering barrel-to-barrel variation." },
  { id: "b74", name: "Old Forester Birthday Bourbon", brandId: "d11", proof: 96, age: "11-12 years", mashBill: "72/18/10", msrp: 150, type: "Bourbon, Limited Edition", notes: "Annual September release celebrating George Garvin Brown's birthday. Deeply aged and complex. One of bourbon's most coveted annual releases.", limitedRelease: true },

  // ── BARTON 1792 (d12) ──
  { id: "b75", name: "1792 Small Batch", brandId: "d12", proof: 93.7, age: "NAS (~6-8 yrs)", mashBill: "74/18/8 (high rye)", msrp: 30, type: "Bourbon, Small Batch", notes: "Caramel, vanilla, spice, and fresh orange. Well-balanced and spicy. Great value for the quality." },
  { id: "b76", name: "1792 Bottled-in-Bond", brandId: "d12", proof: 100, age: "NAS (~8 yrs)", mashBill: "74/18/8 (high rye)", msrp: 35, type: "Bourbon, Bottled in Bond", notes: "More structured than Small Batch with additional oak, caramel, and rye spice. Robust and rewarding." },
  { id: "b77", name: "1792 Full Proof", brandId: "d12", proof: 125, age: "NAS (~8 yrs)", mashBill: "74/18/8 (high rye)", msrp: 45, type: "Bourbon, Barrel Proof", notes: "Entered and exited the barrel at the same proof. Intense caramel, dark fruit, pepper, and char. Powerfully delicious." },
  { id: "b78", name: "1792 Single Barrel", brandId: "d12", proof: 98.6, age: "NAS (~8 yrs)", mashBill: "74/18/8 (high rye)", msrp: 38, type: "Bourbon, Single Barrel", notes: "Each barrel unique. Nutty, with spice, caramel, and tobacco. Great for exploring barrel variation." },
  { id: "b79", name: "Very Old Barton 100 Proof", brandId: "d12", proof: 100, age: "NAS (~4-6 yrs)", mashBill: "74/18/8", msrp: 15, type: "Bourbon, Straight", notes: "Classic Kentucky bourbon at 100 proof. Corn sweetness, vanilla, light spice. An incredible bottom-shelf gem." },

  // ── BULLEIT (d13) ──
  { id: "b80", name: "Bulleit Bourbon", brandId: "d13", proof: 90, age: "NAS (~6-8 yrs)", mashBill: "68/28/4 (high rye)", msrp: 28, type: "Bourbon, Straight", notes: "High rye spiciness, dried apricot, vanilla, light maple. Clean and versatile for cocktails or neat." },
  { id: "b81", name: "Bulleit Barrel Strength", brandId: "d13", proof: 119, age: "NAS", mashBill: "68/28/4 (high rye)", msrp: 60, type: "Bourbon, Barrel Proof", notes: "All the Bulleit character amplified with rich toffee, dried fruit, cinnamon, and heavy rye spice. Non-chill filtered." },
  { id: "b82", name: "Bulleit 10 Year", brandId: "d13", proof: 91.2, age: "10 years", mashBill: "68/28/4 (high rye)", msrp: 40, type: "Bourbon, Straight", notes: "Smoother than the original with more developed oak, vanilla, and dried fruit. Balanced with moderate rye kick." },

  // ── CASTLE & KEY (d14) ──
  { id: "b83", name: "Castle & Key Small Batch Bourbon", brandId: "d14", proof: 100, age: "4 years", mashBill: "73/10/17", msrp: 50, type: "Bourbon, Straight", notes: "Bright citrus, honey, fresh grain, and light oak. Young but well-crafted with lovely fruit-forward character." },

  // ── WILDERNESS TRAIL (d15) ──
  { id: "b84", name: "Wilderness Trail Single Barrel Bourbon", brandId: "d15", proof: 100, age: "NAS (~6 yrs)", mashBill: "64/24/12", msrp: 55, type: "Bourbon, Bottled in Bond", notes: "Sweet mash bourbon with bright fruit, caramel, vanilla, and clean grain sweetness. Distinctively fresh and vibrant." },
  { id: "b85", name: "Wilderness Trail Wheated Bourbon", brandId: "d15", proof: 100, age: "NAS (~6 yrs)", mashBill: "64/24/12 (wheat)", msrp: 55, type: "Bourbon, Bottled in Bond", notes: "Sweet mash wheated bourbon. Honey, apple, cream, and gentle wheat spice. Smooth and elegant." },

  // ── LUX ROW (d16) ──
  { id: "b86", name: "Ezra Brooks 99", brandId: "d16", proof: 99, age: "NAS (~4 yrs)", mashBill: "78/10/12", msrp: 20, type: "Bourbon, Straight", notes: "Caramel, vanilla, smooth grain, light spice. An accessible everyday bourbon at an incredible price." },
  { id: "b87", name: "Rebel 100", brandId: "d16", proof: 100, age: "NAS", mashBill: "68/20/12 (wheat)", msrp: 22, type: "Bourbon, Straight", notes: "Wheated bourbon with honey, vanilla, toffee, and soft wheat sweetness. Solid value wheater." },
  { id: "b88", name: "Blood Oath Pact", brandId: "d16", proof: 99.2, age: "Varies", mashBill: "Varies", msrp: 100, type: "Bourbon, Blended", notes: "Annual limited release blending three unique bourbon recipes. Each pact is a numbered, one-time-only creation. Collectible and complex." },
  { id: "b89", name: "David Nicholson Reserve", brandId: "d16", proof: 100, age: "NAS", mashBill: "Wheated", msrp: 35, type: "Bourbon, Straight", notes: "Sweet caramel, vanilla, butterscotch, and warm wheat. Based on the 1843 recipe allegedly sold to the founders of Old Forester." },
  { id: "b90", name: "Rebel Single Barrel", brandId: "d16", proof: 100, age: "10 years", mashBill: "68/20/12 (wheat)", msrp: 60, type: "Bourbon, Single Barrel", notes: "Single barrel wheated bourbon aged 10 years. Rich butter, dried fruit, dark caramel, and toasted wheat. Underrated." },

  // ── RABBIT HOLE (d17) ──
  { id: "b91", name: "Rabbit Hole Cavehill", brandId: "d17", proof: 95, age: "NAS (~4 yrs)", mashBill: "70/10/10/10 (honey malt)", msrp: 50, type: "Bourbon, Straight", notes: "Unique honey malted barley in the mash. Light honey, apple, vanilla, and toasted grain. Smooth and approachable." },
  { id: "b92", name: "Rabbit Hole Heigold", brandId: "d17", proof: 95, age: "NAS (~4 yrs)", mashBill: "70/25/5 (high rye)", msrp: 55, type: "Bourbon, Straight", notes: "High malted rye mash bill. Peppery spice, citrus, toffee, and dark fruit. Bold and distinctive." },
  { id: "b93", name: "Rabbit Hole Dareringer", brandId: "d17", proof: 93, age: "NAS", mashBill: "68/18/14", msrp: 75, type: "Bourbon, Finished", notes: "PX sherry cask-finished. Dark fruit, chocolate, fig, and sherry sweetness. Dessert bourbon with Spanish flair." },

  // ── NEW RIFF (d18) ──
  { id: "b94", name: "New Riff Bottled-in-Bond Bourbon", brandId: "d18", proof: 100, age: "4 years", mashBill: "65/30/5 (non-GMO)", msrp: 42, type: "Bourbon, Bottled in Bond", notes: "High rye, sour mash bourbon. Bright cherry, caramel, rye spice, and grain. Full transparency on every bottle." },
  { id: "b95", name: "New Riff Single Barrel Bourbon", brandId: "d18", proof: 105.6, age: "4+ years", mashBill: "65/30/5 (non-GMO)", msrp: 52, type: "Bourbon, Single Barrel", notes: "Barrel proof single barrel with no chill filtration. Varies per barrel with consistent quality. Spicy, fruity, and honest." },

  // ── BARDSTOWN BOURBON CO. (d19) ──
  { id: "b96", name: "Bardstown Bourbon Discovery Series", brandId: "d19", proof: 115, age: "Varies", mashBill: "Multi-source blend", msrp: 130, type: "Bourbon, Blended", notes: "Each numbered release blends bourbons from multiple distilleries and ages. Complex, layered, and always surprising. A blender's showcase." },
  { id: "b97", name: "Bardstown Bourbon Fusion Series", brandId: "d19", proof: 98.9, age: "Varies", mashBill: "Multi-source blend", msrp: 60, type: "Bourbon, Blended", notes: "Blends their own young distillate with aged sourced bourbon. Each release shifts as their own stock matures. Fascinating evolution." },

  // ── STARLIGHT (d20) ──
  { id: "b98", name: "Starlight Carl T. Huber Bourbon", brandId: "d20", proof: 92, age: "4 years", mashBill: "51/39/10", msrp: 40, type: "Bourbon, Straight", notes: "Farm distillery bourbon with corn sweetness, caramel, apple, and light spice. Charming Indiana bourbon." },

  // ── JACK DANIEL'S (d21) ──
  { id: "b99", name: "Jack Daniel's Single Barrel Select", brandId: "d21", proof: 94, age: "NAS (~5-7 yrs)", mashBill: "80/8/12", msrp: 50, type: "Whiskey, Tennessee", notes: "Rich caramel, dark fruit, toasted oak, and bold vanilla. Each barrel hand-selected from the highest warehouse floors." },
  { id: "b100", name: "Jack Daniel's Single Barrel Barrel Proof", brandId: "d21", proof: 132, age: "NAS (~5-7 yrs)", mashBill: "80/8/12", msrp: 65, type: "Whiskey, Tennessee", notes: "Uncut and unfiltered single barrels. Intense caramel, banana, dark chocolate, and charred oak. A different animal from standard Jack." },
  { id: "b101", name: "Gentleman Jack", brandId: "d21", proof: 80, age: "NAS (~4 yrs)", mashBill: "80/8/12", msrp: 30, type: "Whiskey, Tennessee", notes: "Double charcoal mellowed for extra smoothness. Light vanilla, soft caramel, and clean finish. An approachable step up." },

  // ── GEORGE DICKEL (d22) ──
  { id: "b102", name: "George Dickel Bottled-in-Bond (13 Year)", brandId: "d22", proof: 100, age: "13 years", mashBill: "84/8/8", msrp: 40, type: "Whiskey, Tennessee", notes: "A steal at the price. Rich caramel, toffee, leather, and old oak. Proof that Tennessee whisky can rival any bourbon." },
  { id: "b103", name: "George Dickel Barrel Select", brandId: "d22", proof: 86, age: "NAS (~10 yrs)", mashBill: "84/8/8", msrp: 30, type: "Whiskey, Tennessee", notes: "Hand-selected single barrels with caramel, butterscotch, and vanilla. Smooth and underrated." },

  // ── NELSON'S GREEN BRIER (d23) ──
  { id: "b104", name: "Belle Meade Reserve", brandId: "d23", proof: 108.3, age: "NAS", mashBill: "Sourced", msrp: 60, type: "Bourbon, Straight", notes: "High proof sourced bourbon. Rich butterscotch, dark fruit, rye spice, and toasted oak. Outstanding Nashville bourbon." },

  // ── MGP (d24) ──
  { id: "b105", name: "Remus Repeal Reserve", brandId: "d24", proof: 100, age: "Varies (7-15 yrs)", mashBill: "Multi-mash blend", msrp: 95, type: "Bourbon, Blended", notes: "MGP's own premium brand blending multiple mash bills and ages. Rich, complex, with dark fruit, chocolate, and heavy oak. Showcases MGP's best." },
  { id: "b106", name: "George Remus Bourbon", brandId: "d24", proof: 94, age: "NAS", mashBill: "75/21/4", msrp: 32, type: "Bourbon, Straight", notes: "Sweet corn, vanilla, caramel, and moderate rye spice. An excellent representation of MGP distillate at an accessible price." },
  { id: "b107", name: "Rossville Union Master Crafted", brandId: "d24", proof: 94, age: "NAS", mashBill: "51/45/4 rye + 95/5 rye", msrp: 40, type: "Rye, Straight", notes: "A blend of two rye mash bills. Rich spice, dried fruit, caramel, and herbal notes. Shows MGP rye at its best." },

  // ── WEST FORK (d25) ──
  { id: "b108", name: "West Fork Old Hamer Bourbon", brandId: "d25", proof: 90, age: "NAS (~3 yrs)", mashBill: "70/21/9", msrp: 40, type: "Bourbon, Straight", notes: "Indiana-grown grain bourbon. Sweet corn, caramel, vanilla, light floral note. Fresh and craft-forward." },

  // ── GARRISON BROTHERS (d26) ──
  { id: "b109", name: "Garrison Brothers Small Batch", brandId: "d26", proof: 94, age: "NAS (~3 yrs)", mashBill: "74/15/11 (wheat)", msrp: 80, type: "Bourbon, Straight", notes: "Texas heat-aged wheated bourbon. Caramel, dried fruit, rich oak, and honey. Concentrated and bold from intense aging." },
  { id: "b110", name: "Garrison Brothers Balmorhea", brandId: "d26", proof: 115, age: "NAS (~5+ yrs)", mashBill: "74/15/11 (wheat)", msrp: 150, type: "Bourbon, Barrel Proof", notes: "Named for the spring-fed pool. Rich dark fruit, caramel, leather, and a long, complex finish. Texas bourbon at its peak." },

  // ── BALCONES (d27) ──
  { id: "b111", name: "Balcones Texas Pot Still Bourbon", brandId: "d27", proof: 92, age: "NAS (~2 yrs)", mashBill: "Roasted blue corn", msrp: 35, type: "Bourbon, Straight", notes: "Blue corn sweetness, roasted grain, caramel, and citrus. Distinctly Texan with big grain-forward character." },
  { id: "b112", name: "Balcones True Blue 100", brandId: "d27", proof: 100, age: "NAS", mashBill: "100% roasted blue corn", msrp: 40, type: "Whiskey, Corn", notes: "100% Hopi blue corn. Intensely sweet roasted corn, chocolate, fruit cake, and exotic spice. Unlike anything else." },

  // ── STILL AUSTIN (d28) ──
  { id: "b113", name: "Still Austin The Musician Bourbon", brandId: "d28", proof: 98, age: "NAS (~2-3 yrs)", mashBill: "70/25/5 (Texas grain)", msrp: 45, type: "Bourbon, Straight", notes: "All Texas-grown grain. Bright and fruity with caramel, vanilla, and a gentle warmth. Austin's grain-to-glass pioneer." },

  // ── STRANAHAN'S (d29) ──
  { id: "b114", name: "Stranahan's Original", brandId: "d29", proof: 94, age: "NAS (2-5 yrs)", mashBill: "100% malted barley", msrp: 60, type: "Whiskey, American Single Malt", notes: "Rich malt, dark fruit, honey, and chocolate. Colorado's original single malt that started a movement." },

  // ── BRECKENRIDGE (d30) ──
  { id: "b115", name: "Breckenridge Bourbon", brandId: "d30", proof: 86, age: "NAS (~3 yrs)", mashBill: "56/38/6 (high rye)", msrp: 40, type: "Bourbon, Blended", notes: "High-altitude aged blend. Butterscotch, vanilla, dried apricot, and a clean finish. Rocky Mountain bourbon." },

  // ── CATOCTIN CREEK (d31) ──
  { id: "b116", name: "Catoctin Creek Roundstone Rye", brandId: "d31", proof: 80, age: "NAS (~2 yrs)", mashBill: "100% rye", msrp: 42, type: "Rye, Straight", notes: "Organic 100% rye. Fresh rye bread, pepper, honey, and bright citrus. Clean Virginia craft spirit." },

  // ── VIRGINIA DISTILLERY (d32) ──
  { id: "b117", name: "Virginia Highland Whisky (Port Cask)", brandId: "d32", proof: 92, age: "NAS", mashBill: "Malt blend", msrp: 55, type: "Whiskey, American Single Malt", notes: "American and Scotch malt whisky finished in port casks. Berry fruit, chocolate, and malt sweetness. Blue Ridge elegance." },

  // ── HUDSON (d33) ──
  { id: "b118", name: "Hudson Baby Bourbon", brandId: "d33", proof: 92, age: "NAS (~3 yrs)", mashBill: "100% NY corn", msrp: 45, type: "Bourbon, Straight", notes: "100% New York corn aged in small barrels. Sweet corn, vanilla, caramel, and a hint of grassiness. A craft pioneer." },

  // ── KINGS COUNTY (d34) ──
  { id: "b119", name: "Kings County Bourbon", brandId: "d34", proof: 90, age: "NAS (~2 yrs)", mashBill: "80/20 (organic)", msrp: 40, type: "Bourbon, Straight", notes: "New York organic corn bourbon. Sweet, malty, with chocolate and caramel. Small-barrel Brooklyn character." },

  // ── WESTLAND (d35) ──
  { id: "b120", name: "Westland American Single Malt", brandId: "d35", proof: 92, age: "NAS (~3-5 yrs)", mashBill: "100% malted barley", msrp: 55, type: "Whiskey, American Single Malt", notes: "Pacific NW barley. Biscuit, honey, stone fruit, and subtle peat. The standard-bearer for American single malt." },

  // ── WYOMING (d36) ──
  { id: "b121", name: "Wyoming Whiskey Small Batch", brandId: "d36", proof: 88, age: "5 years", mashBill: "68/20/12", msrp: 40, type: "Bourbon, Straight", notes: "Estate-grown grain bourbon. Vanilla, honey, caramel, and a clean wheat sweetness. Big Sky Country in a bottle." },

  // ── PINHOOK (d37) ──
  { id: "b122", name: "Pinhook Bourbon War Vertical", brandId: "d37", proof: 104, age: "NAS (~5-6 yrs)", mashBill: "75/15/10", msrp: 55, type: "Bourbon, Straight", notes: "Annual vintage release. Cherry, vanilla, baking spice, and oak. Each year a unique horse-inspired expression." },

  // ── SMOKE WAGON (d38) ──
  { id: "b123", name: "Smoke Wagon Uncut Unfiltered", brandId: "d38", proof: 115, age: "NAS (~8 yrs)", mashBill: "75/21/4 (MGP)", msrp: 55, type: "Bourbon, Barrel Proof", notes: "MGP-sourced barrel proof. Rich dark fruit, chocolate, caramel, and heavy rye spice. A cult-following bourbon with legitimate quality." },
  { id: "b124", name: "Smoke Wagon Small Batch", brandId: "d38", proof: 100, age: "NAS", mashBill: "Blended", msrp: 40, type: "Bourbon, Blended", notes: "Approachable blend of MGP bourbons. Caramel, vanilla, light spice, and clean finish. Excellent everyday sipper." },

  // ── BARRELL CRAFT SPIRITS (d39) ──
  { id: "b125", name: "Barrell Bourbon", batch: "Batch 036", brandId: "d39", proof: 117, age: "Varies (5-15 yrs)", mashBill: "Multi-source blend", msrp: 90, type: "Bourbon, Barrel Proof", notes: "Each numbered batch a unique blend at cask strength. Consistently world-class with dark fruit, spice, and complexity. Award-winning." },
  { id: "b126", name: "Barrell Vantage", brandId: "d39", proof: 114.4, age: "Varies", mashBill: "Multi-source blend", msrp: 80, type: "Bourbon, Finished", notes: "Finished in three different cask types: rum, port, and toasted French oak. Tropical fruit, wine notes, caramel, and layers of complexity." },
  { id: "b127", name: "Barrell Seagrass", brandId: "d39", proof: 130, age: "Varies", mashBill: "Multi-source rye", msrp: 85, type: "Rye, Straight", notes: "Rye finished in Martinique rum, apricot brandy, and Madeira barrels. Sea salt, tropical fruit, and herbal complexity. Groundbreaking." },
  { id: "b128", name: "Barrell Gold Label", brandId: "d39", proof: 112, age: "16-18 years", mashBill: "Multi-source blend", msrp: 300, type: "Bourbon, Blended", notes: "The pinnacle of Barrell's blending art. Exceptionally aged bourbon blended with cask-finished components. Transcendent complexity.", limitedRelease: true },

  // ── WILLETT (d40) ──
  { id: "b129", name: "Willett Family Estate 4 Year", brandId: "d40", proof: 110, age: "4 years", mashBill: "72/13/15", msrp: 50, type: "Bourbon, Small Batch", notes: "Estate-distilled bourbon in the iconic pot-still bottle. Fruit-forward, spicy, caramel, and grain sweetness. The new generation." },
  { id: "b130", name: "Old Bardstown Bottled-in-Bond", brandId: "d40", proof: 100, age: "NAS (~4 yrs)", mashBill: "72/13/15", msrp: 20, type: "Bourbon, Bottled in Bond", notes: "Willett's value-priced offering. Clean vanilla, caramel, and mild spice at BiB proof. A hidden gem." },
  { id: "b131", name: "Noah's Mill", brandId: "d40", proof: 114.3, age: "NAS (~4-20 yrs)", mashBill: "Blend", msrp: 55, type: "Bourbon, Small Batch", notes: "Bold blend of young and well-aged bourbons. Dark fruit, heavy oak, caramel, and spice. Big and robust." },
  { id: "b132", name: "Rowan's Creek", brandId: "d40", proof: 100.1, age: "NAS (~12 yrs)", mashBill: "Blend", msrp: 45, type: "Bourbon, Small Batch", notes: "Balanced blend with honey, citrus, toasted almond, and moderate oak. Smooth and complex. Underappreciated." },

  // ── OLD ELK (d41) ──
  { id: "b133", name: "Old Elk Bourbon", brandId: "d41", proof: 88, age: "NAS (~5 yrs)", mashBill: "51/34/15 (high malt)", msrp: 50, type: "Bourbon, Straight", notes: "Unusually high malted barley. Smooth honey, malt, vanilla, and light spice. Greg Metze's distinctive vision." },
  { id: "b134", name: "Old Elk Wheated Bourbon", brandId: "d41", proof: 92, age: "NAS (~5 yrs)", mashBill: "51/34/15 (wheat)", msrp: 60, type: "Bourbon, Straight", notes: "Wheat-forward with caramel, honey, red fruit, and gentle spice. Soft and inviting." },

  // ── PENELOPE (d42) ──
  { id: "b135", name: "Penelope Bourbon", brandId: "d42", proof: 80, age: "NAS", mashBill: "Four-grain blend", msrp: 33, type: "Bourbon, Blended", notes: "Four mash bills blended together. Light caramel, vanilla, fruit, and accessible sweetness. Approachable and mixable." },
  { id: "b136", name: "Penelope Barrel Strength", brandId: "d42", proof: 118, age: "NAS", mashBill: "Four-grain blend", msrp: 60, type: "Bourbon, Barrel Proof", notes: "Same four-grain blend at full power. Richer caramel, dark fruit, baking spice, and a warm, lingering finish." },

  // ── BLUE RUN (d43) ──
  { id: "b137", name: "Blue Run Kentucky Straight Bourbon", brandId: "d43", proof: 95, age: "NAS", mashBill: "Varies", msrp: 65, type: "Bourbon, Straight", notes: "Jim Rutledge-selected bourbon. Bright fruit, caramel, vanilla, and moderate spice. Beautiful bottle, quality to match." },

  // ── KENTUCKY OWL (d44) ──
  { id: "b138", name: "Kentucky Owl Confiscated", brandId: "d44", proof: 96.4, age: "NAS", mashBill: "Blend", msrp: 50, type: "Bourbon, Blended", notes: "Named for the bourbon seized during Prohibition. Honey, vanilla, light fruit, and accessible. A great introduction to the brand." },
  { id: "b139", name: "Kentucky Owl The Wiseman", brandId: "d44", proof: 90.8, age: "NAS", mashBill: "Blend", msrp: 55, type: "Bourbon, Blended", notes: "Toasted oak, caramel, dried cherry, and baking spice. Smooth and well-balanced. Dixon Dedman's vision of approachable quality." },

  // ── STELLUM (d45) ──
  { id: "b140", name: "Stellum Bourbon", brandId: "d45", proof: 114.98, age: "NAS", mashBill: "Multi-state blend", msrp: 55, type: "Bourbon, Barrel Proof", notes: "Blends KY, IN, and TN bourbons at cask strength. Rich caramel, dark fruit, spice, and oak. Outstanding value for barrel proof." },
  { id: "b141", name: "Stellum Equinox Bourbon", brandId: "d45", proof: 109, age: "NAS", mashBill: "Multi-state blend", msrp: 75, type: "Bourbon, Finished", notes: "Core blend finished in sherry, Madeira, and toasted barrels. Dark fruit, chocolate, wine notes, and toasted grain. Complex and layered." },
];

const TASTING_WHEEL = {
  Sweet:  { icon: "🍯", color: "#D4A053", glow: "#D4A053", notes: ["Vanilla", "Caramel", "Honey", "Butterscotch", "Maple", "Brown Sugar", "Toffee", "Molasses"] },
  Fruit:  { icon: "🍒", color: "#C45B4A", glow: "#E06B58", notes: ["Cherry", "Apple", "Orange Peel", "Dried Fruit", "Plum", "Banana", "Apricot", "Fig"] },
  Spice:  { icon: "🌶️", color: "#B8602A", glow: "#D07030", notes: ["Cinnamon", "Nutmeg", "Black Pepper", "Clove", "Allspice", "Ginger", "Anise", "Rye Spice"] },
  Wood:   { icon: "🪵", color: "#8B6F47", glow: "#A08050", notes: ["Oak", "Cedar", "Char", "Smoke", "Sawdust", "Pine", "Sandalwood", "Toasted Wood"] },
  Grain:  { icon: "🌾", color: "#C4A644", glow: "#D4B654", notes: ["Corn", "Wheat", "Biscuit", "Toast", "Bread", "Cereal", "Malt", "Cracker"] },
  Rich:   { icon: "🫘", color: "#7B5230", glow: "#8B6240", notes: ["Chocolate", "Coffee", "Tobacco", "Leather", "Dark Cocoa", "Espresso", "Toffee", "Caramelized Sugar"] },
  Floral:   { icon: "🌸", color: "#9B6B8A", glow: "#B07B9A", notes: ["Rose", "Lavender", "Jasmine", "Honeysuckle", "Chamomile", "Wildflower", "Mint", "Eucalyptus"] },
  Nutty:  { icon: "🥜", color: "#A07850", glow: "#B08860", notes: ["Almond", "Pecan", "Walnut", "Peanut", "Hazelnut", "Cashew", "Roasted Nuts", "Marzipan"] },
};

const TW_CATS = Object.keys(TASTING_WHEEL);

// Reverse lookup: note ←’ dimension category (Toffee←’Rich per scope decision)

const NOTE_TO_DIMENSION = {};
TW_CATS.forEach(cat => { NOTE_TO_DIMENSION[cat] = cat; TASTING_WHEEL[cat].notes.forEach(n => { if (!NOTE_TO_DIMENSION[n]) NOTE_TO_DIMENSION[n] = cat; }); });

NOTE_TO_DIMENSION["Toffee"] = "Rich";

const PILL_TO_DIMENSION = Object.freeze({
  "Baking Spice": "Spice", "Citrus": "Fruit", "Toasted Nuts": "Nutty", "Dark Fruit": "Fruit",
  "Dark Chocolate": "Rich", "Oaky": "Wood", "Smoky": "Wood", "Spicy": "Spice",
  "Sweet": "Sweet", "Peppery": "Spice", "Dried Fruit": "Fruit",
});
const resolveFlavorDimension = (term) => {
  if (!term) return null;
  const trimmed = term.trim();
  if (NOTE_TO_DIMENSION[trimmed]) return NOTE_TO_DIMENSION[trimmed];
  if (PILL_TO_DIMENSION[trimmed]) return PILL_TO_DIMENSION[trimmed];
  const titleCase = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  if (NOTE_TO_DIMENSION[titleCase]) return NOTE_TO_DIMENSION[titleCase];
  if (PILL_TO_DIMENSION[titleCase]) return PILL_TO_DIMENSION[titleCase];
  return null;
};

const TW_SEGS = 8, TW_ANGPER = 360 / TW_SEGS, TW_CX = 200, TW_CY = 200;

const twAllNotes = (cat) => [cat, ...TASTING_WHEEL[cat].notes];

function twSegPath(i, ir, or) {

  const s = (i * TW_ANGPER - 90) * Math.PI / 180, e = ((i + 1) * TW_ANGPER - 90) * Math.PI / 180;

  const ox1 = TW_CX + or * Math.cos(s), oy1 = TW_CY + or * Math.sin(s), ox2 = TW_CX + or * Math.cos(e), oy2 = TW_CY + or * Math.sin(e);

  const ix1 = TW_CX + ir * Math.cos(e), iy1 = TW_CY + ir * Math.sin(e), ix2 = TW_CX + ir * Math.cos(s), iy2 = TW_CY + ir * Math.sin(s);
  return `M ${ix2} ${iy2} L ${ox1} ${oy1} A ${or} ${or} 0 0 1 ${ox2} ${oy2} L ${ix1} ${iy1} A ${ir} ${ir} 0 0 0 ${ix2} ${iy2} Z`;

}

function twRimArc(i, r) {

  const g = 1.5, s = (i * TW_ANGPER - 90 + g) * Math.PI / 180, e = ((i + 1) * TW_ANGPER - 90 - g) * Math.PI / 180;
  return `M ${TW_CX + r * Math.cos(s)} ${TW_CY + r * Math.sin(s)} A ${r} ${r} 0 0 1 ${TW_CX + r * Math.cos(e)} ${TW_CY + r * Math.sin(e)}`;

}

function twLabelPos(i, r) { const a = ((i + 0.5) * TW_ANGPER - 90) * Math.PI / 180; return { x: TW_CX + r * Math.cos(a), y: TW_CY + r * Math.sin(a) }; }

function twSpoke(i) { const a = (i * TW_ANGPER - 90) * Math.PI / 180; return { x1: TW_CX + 40 * Math.cos(a), y1: TW_CY + 40 * Math.sin(a), x2: TW_CX + 170 * Math.cos(a), y2: TW_CY + 170 * Math.sin(a) }; }

// \u2500\u2500\u2500 RECOMMENDATION ENGINE CONSTANTS \u2500\u2500\u2500

const PROOF_BRACKETS = [
  { key: "low", label: "Easy-sipping", min: 80, max: 95 },
  { key: "medium", label: "Mid-proof", min: 96, max: 109 },
  { key: "high", label: "High-proof", min: 110, max: 125 },
  { key: "cask", label: "Cask strength", min: 126, max: 200 },
];

const BOURBON_SUBTYPES = new Set([
  "Bourbon, Straight", "Bourbon, Barrel Proof", "Bourbon, Single Barrel",
  "Bourbon, Bottled in Bond", "Bourbon, Small Batch", "Bourbon, Finished",
  "Bourbon, Limited Edition", "Bourbon, Blended",
  "Rye, Straight", "Whiskey, Tennessee",
]);

const NOTE_TO_WHEEL_CATEGORY = Object.freeze(
  Object.entries(TASTING_WHEEL).reduce((acc, [cat, data]) => {
    [cat, ...data.notes].forEach(n => { acc[n.toLowerCase()] = cat; });

    return acc;
  }, {})
);

const RECO_COLD_START_THRESHOLD = 5;

const normalizeQuotes = (s) => s.replace(/[\u2018\u2019\u201A\u201B]/g, "'").replace(/[\u201C\u201D\u201E\u201F]/g, '"');
const normalizeSearch = (s) => s.replace(/['\u2018\u2019\u201A\u201B`\u0060"\u201C\u201D\u201E\u201F]/g, "");

// ── NAME NORMALIZATION (Sprint 6 Step 8 — ported from Edge Function) ──
const NORM_CLASS_NAMES = new Set([
  "kentucky straight bourbon whiskey","kentucky straight bourbon whisky",
  "kentucky straight rye whiskey","kentucky straight rye whisky",
  "kentucky straight malt whiskey","kentucky straight whiskey","kentucky straight whisky",
  "kentucky bourbon whiskey","kentucky bourbon whisky",
  "kentucky straight bourbon","kentucky straight rye","kentucky straight",
  "straight bourbon whiskey","straight bourbon whisky",
  "straight rye whiskey","straight rye whisky",
  "straight corn whiskey","straight corn whisky",
  "straight wheat whiskey","straight wheat whisky",
  "straight malt whiskey","straight whiskey","straight whisky",
  "straight bourbon","straight rye","straight wheat",
  "blended bourbon whiskey","blended bourbon whisky",
  "blended straight bourbon whiskey","blended straight bourbon whisky",
  "blended rye whiskey","blended whiskey","blended whisky",
  "american single malt whiskey","american single malt whisky",
  "single malt whiskey","single malt whisky",
  "bourbon whiskey","bourbon whisky","malt whiskey","malt whisky",
  "corn whiskey","corn whisky","rye whiskey","rye whisky",
  "light whiskey","light whisky","whiskey","whisky",
  "bourbon","rye","corn","malt","wheat","bonded",
  "straight","blended","kentucky","single","american","light",
  "indiana","tennessee","texas","michigan","ohio","iowa",
  "missouri","virginia","georgia","oat",
  "a","an","the","of","and","or","by","in","on","at","to","for","from",
  "b","d","e","f","h","k","m","o","s","v",
]);
const NORM_SUFFIXES = [
  "KENTUCKY STRAIGHT BOURBON WHISKEY","KENTUCKY STRAIGHT BOURBON WHISKY",
  "KENTUCKY STRAIGHT RYE WHISKEY","KENTUCKY STRAIGHT RYE WHISKY",
  "KENTUCKY STRAIGHT MALT WHISKEY","KENTUCKY STRAIGHT MALT WHISKY",
  "KENTUCKY STRAIGHT CORN WHISKEY","KENTUCKY STRAIGHT CORN WHISKY",
  "KENTUCKY STRAIGHT WHISKEY","KENTUCKY STRAIGHT WHISKY",
  "KENTUCKY BOURBON WHISKEY","KENTUCKY BOURBON WHISKY",
  "KENTUCKY STRAIGHT BOURBON","KENTUCKY STRAIGHT RYE","KENTUCKY STRAIGHT",
  "STRAIGHT BOURBON WHISKEY","STRAIGHT BOURBON WHISKY",
  "STRAIGHT RYE WHISKEY","STRAIGHT RYE WHISKY",
  "STRAIGHT MALT WHISKEY","STRAIGHT MALT WHISKY",
  "STRAIGHT CORN WHISKEY","STRAIGHT CORN WHISKY",
  "STRAIGHT WHEAT WHISKEY","STRAIGHT WHEAT WHISKY",
  "STRAIGHT WHISKEY","STRAIGHT WHISKY",
  "STRAIGHT BOURBON","STRAIGHT RYE","STRAIGHT WHEAT",
  "BLENDED BOURBON WHISKEY","BLENDED BOURBON WHISKY",
  "BLENDED RYE WHISKEY","BLENDED RYE WHISKY",
  "BLENDED WHISKEY","BLENDED WHISKY",
  "AMERICAN SINGLE MALT WHISKEY","AMERICAN SINGLE MALT WHISKY",
  "SINGLE MALT WHISKEY","SINGLE MALT WHISKY",
  "BOURBON WHISKEY","BOURBON WHISKY",
  "MALT WHISKEY","MALT WHISKY",
  "CORN WHISKEY","CORN WHISKY",
  "RYE WHISKEY","RYE WHISKY",
  "LIGHT WHISKEY","LIGHT WHISKY",
  "WHISKEY","WHISKY",
];
const NORM_LOWERCASE = new Set([
  "a","an","and","at","by","for","from","in","of","on","or","the","to","with",
]);
const NORM_PRESERVE_UPPER = new Set([
  "BIB","CS","SIB","SMB","ECBP","XO","NAS","VS","VSOP",
  "II","III","IV","VI","VII","VIII","IX","XI","XII","XIII","XIV","XV","XX","XXV",
  "JR","SR",
]);
const NORM_SPECIAL = {
  "JR": "Jr.", "JR.": "Jr.", "SR": "Sr.", "SR.": "Sr.",
  "SIB": "SiB", "SMB": "SmB", "BIB": "BiB",
};

function titleCaseWord(word, isFirst) {
  const upper = word.toUpperCase();
  if (word.includes("&") && !/^\d/.test(word)) {
    return word.split("&").map(p => {
      if (p.length <= 2 && /^[A-Za-z]+$/.test(p)) return p.toUpperCase();
      if (p.length > 0) return p[0].toUpperCase() + p.slice(1).toLowerCase();
      return p;
    }).join("&");
  }
  if (word.includes("/") && word.length > 1) {
    return word.split("/").map(p => {
      if (p.length <= 4 && /^[A-Za-z]+$/.test(p) && p === p.toUpperCase()) return p;
      if (/^\d/.test(p)) return p;
      if (p.length > 0) return p[0].toUpperCase() + p.slice(1).toLowerCase();
      return p;
    }).join("/");
  }
  if (word.includes("-") && word.length > 1) {
    return word.split("-").map((part, idx) => {
      if (/[A-Za-z]/.test(part) && /[0-9]/.test(part)) return part.toUpperCase();
      if (part.length <= 4 && /^[A-Za-z]+$/.test(part) && part === part.toUpperCase()) return part;
      if (NORM_PRESERVE_UPPER.has(part.toUpperCase())) {
        return NORM_SPECIAL[part.toUpperCase()] || part.toUpperCase();
      }
      if (idx > 0 && NORM_LOWERCASE.has(part.toLowerCase())) return part.toLowerCase();
      if (part.length > 0) return part[0].toUpperCase() + part.slice(1).toLowerCase();
      return part;
    }).join("-");
  }
  if (word.includes(".") && !word.endsWith(".") && !/^\d/.test(word)) {
    return word.split(".").map(p => {
      if (p.length > 0) return p[0].toUpperCase() + p.slice(1).toLowerCase();
      return p;
    }).join(".");
  }
  if (word.startsWith("(")) {
    let inner = word.slice(1);
    let suffix = "";
    if (inner.endsWith(")")) { suffix = ")"; inner = inner.slice(0, -1); }
    if (NORM_PRESERVE_UPPER.has(inner.toUpperCase())) {
      inner = inner.toUpperCase();
    } else if (inner.length > 0) {
      inner = inner[0].toUpperCase() + inner.slice(1).toLowerCase();
    }
    return "(" + inner + suffix;
  }
  if (NORM_PRESERVE_UPPER.has(upper)) return NORM_SPECIAL[upper] || upper;
  if (/^[A-Za-z]\.[A-Za-z]/.test(word)) return upper;
  if (word.endsWith(".") && word.length <= 5) return word[0].toUpperCase() + word.slice(1).toLowerCase();
  if (/^\d/.test(word)) return word;
  if (!isFirst && NORM_LOWERCASE.has(word.toLowerCase())) return word.toLowerCase();
  if (word.length > 0) return word[0].toUpperCase() + word.slice(1).toLowerCase();
  return word;
}

function normalizeColaName(rawName, brandName) {
  if (!rawName || !rawName.trim()) return rawName;
  let result = rawName.trim().replace(/\s+/g, " ");
  result = result
    .replace(/\u2018/g, "'").replace(/\u2019/g, "'")
    .replace(/\u201C/g, '"').replace(/\u201D/g, '"')
    .replace(/`/g, "'");
  result = result.replace(/'([A-Z])(\w)/g, (_, c, rest) => `'${c}${rest}`);
  const upperCheck = result.toUpperCase();
  if (!upperCheck.includes("BIB &") && !upperCheck.includes("BIB&")) {
    result = result.replace(/BOTTLED\s*-\s*IN\s*-\s*BOND/gi, "Bottled in Bond");
    result = result.replace(/BOTTLED\s+IN\s+BOND/gi, "Bottled in Bond");
    result = result.replace(/\bBIB\b/gi, "BiB");
  }
  result = result.trim().replace(/\s+/g, " ");
  let skipStripping = false;
  if (NORM_CLASS_NAMES.has(result.toLowerCase().trim())) {
    if (brandName?.trim()) result = brandName.trim();
    skipStripping = true;
  }
  if (result.toUpperCase().trim() === "BOTTLED IN BOND") skipStripping = true;
  if (!skipStripping) {
    let upperResult = result.toUpperCase();
    for (const suffix of NORM_SUFFIXES) {
      if (upperResult.length > suffix.length + 1 && upperResult.endsWith(" " + suffix)) {
        const remainderUpper = upperResult.slice(0, upperResult.length - suffix.length - 1).trim();
        if (remainderUpper.length >= 2 && !NORM_CLASS_NAMES.has(remainderUpper.toLowerCase())) {
          result = result.slice(0, result.length - suffix.length - 1).trim();
          upperResult = result.toUpperCase();
        } else if (NORM_CLASS_NAMES.has(remainderUpper.toLowerCase()) && brandName?.trim()) {
          result = brandName.trim();
          upperResult = result.toUpperCase();
        }
        break;
      }
    }
    if (/\s+BOURBON$/i.test(result)) {
      const remainder = result.replace(/\s+BOURBON$/i, "").trim();
      const words = remainder.split(/\s+/);
      if ((words.length >= 2 || remainder.length >= 8) &&
          !NORM_CLASS_NAMES.has(remainder.toLowerCase())) {
        result = remainder;
      }
    }
    result = result.replace(/[\s,&\-]+$/, "").trim();
    if (NORM_CLASS_NAMES.has(result.toLowerCase().trim())) {
      if (brandName?.trim()) result = brandName.trim();
    }
    result = result
      .replace(/\b(\d+)-years?\b/gi, "$1 Year")
      .replace(/\b(\d+)-year\b/gi, "$1 Year")
      .replace(/\baged\s+(\d+)\s+years?\b/gi, "$1 Year")
      .replace(/\b(\d+)\s+years?\s+old\b/gi, "$1 Year")
      .replace(/\b(\d+)\s+years\b/gi, "$1 Year")
      .replace(/\b(\d+)\s*yrs?\b/gi, "$1 Year");
    result = result.trim().replace(/\s+/g, " ");
    if (!result || result.trim().length < 1) {
      result = brandName?.trim() || rawName.trim();
    }
  }
  const words = result.split(" ");
  result = words.map((w, i) => titleCaseWord(w, i === 0)).join(" ").trim();
  return result || rawName.trim();
}
// ── END NAME NORMALIZATION ──

const STARTER_BOURBONS = Object.freeze([
  { name: "Buffalo Trace", profile: "Balanced \u00B7 Caramel-forward", proof: "90°", price: "~$28", tag: "Gateway Classic", mashBill: "Traditional" },
  { name: "Wild Turkey 101", profile: "Bold \u00B7 Spicy \u00B7 High-rye", proof: "101°", price: "~$25", tag: "Bartender\u2019s Pick", mashBill: "High-Rye" },
  { name: "Maker\u2019s Mark", profile: "Smooth \u00B7 Sweet \u00B7 Wheated", proof: "90°", price: "~$30", tag: "Most Recognized", mashBill: "Wheated" },
  { name: "Bulleit Bourbon", profile: "Dry \u00B7 Peppery \u00B7 High-rye", proof: "90°", price: "~$30", tag: "Cocktail King", mashBill: "High-Rye" },
  { name: "Woodford Reserve", profile: "Complex \u00B7 Balanced \u00B7 Oaky", proof: "90.4°", price: "~$38", tag: "Premium Staple", mashBill: "Traditional" },
  { name: "Evan Williams Black", profile: "Sweet \u00B7 Simple \u00B7 Value", proof: "86°", price: "~$16", tag: "Best Value", mashBill: "Traditional" },
  { name: "Four Roses Small Batch", profile: "Fruity \u00B7 Floral \u00B7 Balanced", proof: "90°", price: "~$34", tag: "Unique Blend", mashBill: "Multi-Recipe" },
  { name: "Basil Hayden\u2019s", profile: "Light \u00B7 Approachable \u00B7 Gentle", proof: "80°", price: "~$38", tag: "Easy Sipper", mashBill: "High-Rye" },
]);

const US_STATES = Object.freeze(["AK","AL","AR","AZ","CA","CO","CT","DC","DE","FL","GA","HI","IA","ID","IL","IN","KS","KY","LA","MA","MD","ME","MI","MN","MO","MS","MT","NC","ND","NE","NH","NJ","NM","NV","NY","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VA","VT","WA","WI","WV","WY"]);

// ─── ICONS ────────────────────────────────────────────────

const BarrelIcon = () => (
  <svg width="28" height="28" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    {/* Top & bottom heads */}
    <ellipse cx="16" cy="5" rx="9" ry="3.5" />
    <ellipse cx="16" cy="27" rx="9" ry="3.5" />
    {/* Stave sides — curved outward at the bilge */}
    <path d="M7 5c-1.8 5-1.8 17 0 22" />
    <path d="M25 5c1.8 5 1.8 17 0 22" />
    {/* Hoops — three bands like a real cooperage */}
    <ellipse cx="16" cy="10" rx="10.2" ry="3" strokeWidth="1.2" opacity=".45" />
    <ellipse cx="16" cy="16" rx="11" ry="3.2" strokeWidth="1.2" opacity=".35" />
    <ellipse cx="16" cy="22" rx="10.2" ry="3" strokeWidth="1.2" opacity=".45" />
    {/* Bung hole hint */}
    <circle cx="16" cy="16" r="1.2" strokeWidth="1" opacity=".5" />
  </svg>
);

// ─── DISTILLERY ICON SYSTEM ──────────────────────────────

const DISTILLERY_ICONS = {

  // ── BOURBON & WHISKEY ──
  barrel: { category: "Bourbon", label: "Barrel", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round"><ellipse cx="20" cy="8" rx="12" ry="5"/><ellipse cx="20" cy="32" rx="12" ry="5"/><path d="M8 8v24M32 8v24"/><path d="M9 16c4-2 18-2 22 0M9 24c4-2 18-2 22 0" strokeDasharray="3 2" opacity=".5"/></svg> },
  bottle: { category: "Bourbon", label: "Bottle", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="16" y="2" width="8" height="6" rx="1"/><path d="M17 8l-3 6v20a4 4 0 004 4h4a4 4 0 004-4V14l-3-6"/><line x1="14" y1="22" x2="26" y2="22" opacity=".4"/></svg> },
  glass: { category: "Bourbon", label: "Rocks Glass", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M8 8l3 26a3 3 0 003 3h12a3 3 0 003-3l3-26z"/><path d="M8 8h24"/><path d="M11 18h18" opacity=".4" strokeDasharray="3 2"/></svg> },
  glencairn: { category: "Bourbon", label: "Glencairn", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6c0 0-2 10 0 16s6 8 8 10v4h0"/><path d="M28 6c0 0 2 10 0 16s-6 8-8 10v4h0"/><line x1="14" y1="36" x2="26" y2="36"/><path d="M12 6h16"/><path d="M11 14c3 2 15 2 18 0" opacity=".35"/></svg> },
  still: { category: "Bourbon", label: "Pot Still", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="16" cy="28" rx="10" ry="8"/><path d="M16 20c0-4 2-7 6-10h8"/><circle cx="30" cy="10" r="3"/><path d="M33 10h4" opacity=".4"/></svg> },
  mashbill: { category: "Bourbon", label: "Grain", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round"><path d="M20 38V14"/><path d="M20 14c-3-4-8-6-8-12"/><path d="M20 14c3-4 8-6 8-12"/><path d="M20 20c-4-2-7-4-9-2"/><path d="M20 20c4-2 7-4 9-2"/><path d="M20 26c-3-1-6-3-8-1"/><path d="M20 26c3-1 6-3 8-1"/></svg> },
  flame: { category: "Bourbon", label: "Flame", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 4c0 8-10 12-10 22a10 10 0 0020 0c0-10-10-14-10-22z"/><path d="M20 18c0 4-4 6-4 10a4 4 0 008 0c0-4-4-6-4-10z" opacity=".4"/></svg> },
  warehouse: { category: "Bourbon", label: "Rickhouse", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 18L20 6l16 12"/><rect x="6" y="18" width="28" height="18" rx="1"/><line x1="6" y1="24" x2="34" y2="24" opacity=".4"/><line x1="6" y1="30" x2="34" y2="30" opacity=".4"/><rect x="16" y="28" width="8" height="8"/></svg> },
  copper: { category: "Bourbon", label: "Copper Pot", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10 36h20"/><path d="M12 36V18a8 8 0 0116 0v18"/><path d="M20 18c0-4 2-8 8-12"/><ellipse cx="20" cy="28" rx="8" ry="3" opacity=".3"/></svg> },
  oak: { category: "Bourbon", label: "Oak Tree", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 38V22"/><circle cx="20" cy="14" r="10"/><circle cx="14" cy="10" r="5" opacity=".3"/><circle cx="26" cy="10" r="5" opacity=".3"/><circle cx="20" cy="6" r="4" opacity=".3"/></svg> },

  // ── NATURE & GEOGRAPHY ──
  mountain: { category: "Nature", label: "Mountain", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 34L16 10l6 10 4-6L38 34z"/><path d="M16 10l3 5" opacity=".4"/></svg> },
  river: { category: "Nature", label: "River", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round"><path d="M8 6c8 4 4 10 12 14s4 10 12 14"/><path d="M4 10c8 4 4 10 12 14s4 10 12 14" opacity=".35"/></svg> },
  pine: { category: "Nature", label: "Pine Tree", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 4l-8 12h4l-6 10h4l-6 10h24l-6-10h4l-6-10h4z"/><rect x="18" y="36" width="4" height="2"/></svg> },
  horse: { category: "Nature", label: "Horse", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10 34v-8l4-6 8-2 6 2 2 6v8"/><path d="M22 18c2-4 6-8 8-10l2 2-4 6"/><path d="M14 20c-2-4-4-6-6-6"/><circle cx="26" cy="12" r="1.5" fill="#D4A053"/></svg> },
  eagle: { category: "Nature", label: "Eagle", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 16c-8 0-16 4-16 10l8-2c0 4 4 8 8 10 4-2 8-6 8-10l8 2c0-6-8-10-16-10z"/><path d="M20 6v10"/><path d="M16 6h8l-4 4z"/></svg> },
  star: { category: "Nature", label: "Star", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinejoin="round"><path d="M20 4l4.9 10 11.1 1.6-8 7.8 1.9 11L20 28.9 10.1 34.4l1.9-11-8-7.8L15.1 14z"/></svg> },
  sun: { category: "Nature", label: "Sun", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round"><circle cx="20" cy="20" r="8"/>{[0,45,90,135,180,225,270,315].map(a=><line key={a} x1="20" y1="20" x2={20+15*Math.cos(a*Math.PI/180)} y2={20+15*Math.sin(a*Math.PI/180)} opacity=".5" transform={`rotate(0)`} style={{transformOrigin:'20px 20px'}}/>)}<line x1="20" y1="2" x2="20" y2="8"/><line x1="20" y1="32" x2="20" y2="38"/><line x1="2" y1="20" x2="8" y2="20"/><line x1="32" y1="20" x2="38" y2="20"/><line x1="7.3" y1="7.3" x2="11.5" y2="11.5"/><line x1="28.5" y1="28.5" x2="32.7" y2="32.7"/><line x1="7.3" y1="32.7" x2="11.5" y2="28.5"/><line x1="28.5" y1="11.5" x2="32.7" y2="7.3"/></svg> },
  spring: { category: "Nature", label: "Spring", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round"><path d="M20 8c0 0-12 6-12 18a12 12 0 0024 0c0-12-12-18-12-18z"/><path d="M16 24c2-2 6-2 8 0" opacity=".5"/><path d="M14 28c3-2 9-2 12 0" opacity=".35"/></svg> },

  // ── HERITAGE & CRAFT ──
  shield: { category: "Heritage", label: "Shield", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 4L6 10v10c0 10 14 16 14 16s14-6 14-16V10z"/><path d="M20 14v10M15 19h10" opacity=".4"/></svg> },
  crown: { category: "Heritage", label: "Crown", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinejoin="round"><path d="M6 30l4-16 6 8 4-12 4 12 6-8 4 16z"/><rect x="6" y="30" width="28" height="4" rx="1"/><circle cx="20" cy="10" r="1.5" fill="#D4A053"/></svg> },
  key: { category: "Heritage", label: "Key", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="28" cy="12" r="7"/><circle cx="28" cy="12" r="3" opacity=".4"/><path d="M22 16L8 30"/><path d="M8 30l4-1"/><path d="M8 30l1-4"/><path d="M14 24l3-1"/></svg> },
  scroll: { category: "Heritage", label: "Scroll", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10 8a4 4 0 014-4h16v28a4 4 0 01-4 4H10"/><path d="M10 8v28a4 4 0 004 4"/><path d="M10 8a4 4 0 00-4 4v2a4 4 0 004 4"/><line x1="18" y1="12" x2="26" y2="12" opacity=".4"/><line x1="18" y1="18" x2="26" y2="18" opacity=".4"/><line x1="18" y1="24" x2="24" y2="24" opacity=".4"/></svg> },
  castle: { category: "Heritage", label: "Castle", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="16" width="24" height="20" rx="1"/><path d="M8 16V8h4v4h4V8h8v4h4V8h4v8"/><rect x="16" y="28" width="8" height="8"/><rect x="18" y="20" width="4" height="4" rx="2" opacity=".4"/></svg> },
  compass: { category: "Heritage", label: "Compass", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round"><circle cx="20" cy="20" r="15"/><circle cx="20" cy="20" r="2" fill="#D4A053"/><path d="M20 7v4M20 29v4M7 20h4M29 20h4" opacity=".5"/><polygon points="20,8 23,18 20,16 17,18" fill="#D4A053" opacity=".6" stroke="none"/><polygon points="20,32 17,22 20,24 23,22" opacity=".3" stroke="none" fill="#D4A053"/></svg> },
  bell: { category: "Heritage", label: "Bell", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M16 32a4 4 0 008 0"/><path d="M10 28h20l-2-6c0-6-3-10-8-12-5 2-8 6-8 12z"/><circle cx="20" cy="8" r="2"/></svg> },
  lab: { category: "Heritage", label: "Flask", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h8M16 4v12L8 32a3 3 0 003 3h18a3 3 0 003-3L24 16V4"/><path d="M12 26h16" opacity=".4"/></svg> },
  ribbon: { category: "Heritage", label: "Ribbon", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="20" cy="16" r="10"/><path d="M14 24l-4 12 6-3 4 5"/><path d="M26 24l4 12-6-3-4 5"/><circle cx="20" cy="16" r="4" opacity=".35"/></svg> },
  diamond: { category: "Heritage", label: "Diamond", render: (s) => <svg width={s} height={s} viewBox="0 0 40 40" fill="none" stroke="#D4A053" strokeWidth="1.6" strokeLinejoin="round"><path d="M10 14h20l-10 22z"/><path d="M10 14l4-8h12l4 8"/><path d="M10 14h20" opacity=".4"/><path d="M14 6l6 8 6-8" opacity=".4"/></svg> },
};

// ── STYLIZED LETTER MONOGRAMS ──

const LETTER_ICONS = {};
"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").forEach(letter => {
  LETTER_ICONS[`letter_${letter}`] = {
    category: "Letters",
    label: letter,
    render: (s) => (
      <svg width={s} height={s} viewBox="0 0 40 40">
        <rect x="2" y="2" width="36" height="36" rx="8" fill="none" stroke="#D4A053" strokeWidth="1.4" opacity=".5"/>
        <text x="20" y="28" textAnchor="middle" fontFamily="'Playfair Display', Georgia, serif" fontSize="26" fontWeight="700" fill="#D4A053">{letter}</text>
      </svg>
    ),
  };

});

const ALL_DISTILLERY_ICONS = { ...DISTILLERY_ICONS, ...LETTER_ICONS };

// Renderer: takes an icon key and size, renders SVG or falls back to emoji

const DistilleryIconRender = memo(({ iconKey, size = 36 }) => {
  if (!iconKey) return <span style={{ fontSize: size * 0.7 }}>🏭</span>;

  const iconDef = ALL_DISTILLERY_ICONS[iconKey];
  if (iconDef) return iconDef.render(size);

  // Fallback: treat as emoji string (legacy data)

  return <span style={{ fontSize: size * 0.7, lineHeight: 1 }}>{iconKey}</span>;

});

// ─── HOISTED CONSTANT STYLES (never re-created) ────────

const FONT_DISPLAY = "'Playfair Display', Georgia, serif";

const GRAD_PRIMARY = "linear-gradient(135deg, #D4A053, #8B5E34)";

const formInputStyle = Object.freeze({ width: "100%", padding: "10px 12px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, color: "#F5E6D0", fontSize: "0.875rem", boxSizing: "border-box" });

const formLabelStyle = Object.freeze({ color: "#B5A898", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 1.5, display: "block", marginBottom: 6 });

const formGroupStyle = Object.freeze({ marginBottom: 16 });

const modalOverlayStyle = Object.freeze({ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 });

const searchIconStyle = Object.freeze({ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#A69880", fontSize: "0.875rem", pointerEvents: "none" });

const sectionLabelSmStyle = Object.freeze({ color: "#B5A898", fontSize: "0.625rem", textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 4 });

const sectionLabelMdStyle = Object.freeze({ display: "block", color: "#B5A898", fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 });

const selectInputStyle = Object.freeze({ width: "100%", padding: "6px 10px", background: "#1A1510", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem", boxSizing: "border-box" });

const searchInputIconStyle = Object.freeze({ width: "100%", padding: "10px 12px 10px 36px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 10, color: "#F5E6D0", fontSize: "0.875rem", boxSizing: "border-box" });

const closeButtonStyle = Object.freeze({ position: "absolute", top: 8, right: 0, background: "none", border: "none", color: "#B5A898", cursor: "pointer", fontSize: "1.25rem", padding: "8px 12px", zIndex: 10, minWidth: 44, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center" });

// ─── EXTRACTED STATIC STYLES ────────────────
const flexOneStyle = Object.freeze({ flex: 1 });
const mutedTextStyle = Object.freeze({ color: "#B5A898" });
const posRelativeStyle = Object.freeze({ position: "relative" });
const mutedText075Mt2Style = Object.freeze({ color: "#B5A898", fontSize: "0.75rem", marginTop: 2 });
const mb20Style = Object.freeze({ marginBottom: 20 });
const mutedText06875Style = Object.freeze({ color: "#B5A898", fontSize: "0.6875rem" });
const mutedText075Style = Object.freeze({ color: "#B5A898", fontSize: "0.75rem" });
const mb14Style = Object.freeze({ marginBottom: 14 });
const lightTextStyle = Object.freeze({ color: "#F5E6D0" });
const flexGap8Style = Object.freeze({ display: "flex", gap: 8 });
const lightText08125BoldStyle = Object.freeze({ color: "#F5E6D0", fontSize: "0.8125rem", fontWeight: 600 });
const goldText06875Style = Object.freeze({ color: "#D4A053", fontSize: "0.6875rem" });
const flexMinW0Style = Object.freeze({ flex: 1, minWidth: 0 });
const mb24Style = Object.freeze({ marginBottom: 24 });
const font0875Style = Object.freeze({ fontSize: "0.875rem" });
const font08125Style = Object.freeze({ fontSize: "0.8125rem" });
const flexGap10Style = Object.freeze({ display: "flex", gap: 10 });
const warmTextStyle = Object.freeze({ color: "#C4B5A0" });
const flexCenterGap10Style = Object.freeze({ display: "flex", alignItems: "center", gap: 10 });
const flexBetweenCenterMb16Style = Object.freeze({ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 });
const goldTextStyle = Object.freeze({ color: "#D4A053" });
const mutedText06875Mt2Style = Object.freeze({ color: "#B5A898", fontSize: "0.6875rem", marginTop: 2 });
const sFonMar1Style = Object.freeze({ fontSize: "1.75rem", marginBottom: 10 });
const sFonMar2Style = Object.freeze({ fontSize: "3.0rem", marginBottom: 12 });
const lightText0875BoldStyle = Object.freeze({ color: "#F5E6D0", fontSize: "0.875rem", fontWeight: 600 });
const goldText05625Mt2Style = Object.freeze({ fontSize: "0.5625rem", color: "#D4A053", marginTop: 2 });
const font1125Style = Object.freeze({ fontSize: "1.125rem" });
const mutedText15Style = Object.freeze({ background: "none", border: "none", color: "#B5A898", fontSize: "1.5rem", cursor: "pointer" });
const mb16Style = Object.freeze({ marginBottom: 16 });
const mutedText0625Mb4UpperSpacedStyle = Object.freeze({ fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 });
const flexGap6WrapStyle = Object.freeze({ display: "flex", gap: 6, flexWrap: "wrap" });
const sFonCol3Style = Object.freeze({ fontSize: "0.6875rem", color: "#B5A898" });
const flexCenterGap8Style = Object.freeze({ display: "flex", alignItems: "center", gap: 8 });
const sFonCol4Style = Object.freeze({ fontSize: "0.75rem", color: "#B5A898" });
const font075Style = Object.freeze({ fontSize: "0.75rem" });
const sColFonTex5Style = Object.freeze({ color: "#B5A898", fontSize: "0.625rem", textTransform: "uppercase", letterSpacing: 1.5, display: "block", marginBottom: 4 });
const lightText08125Style = Object.freeze({ width: "100%", padding: "8px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 8, color: "#F5E6D0", fontSize: "0.8125rem", boxSizing: "border-box" });
const goldText08125BoldStyle = Object.freeze({ color: "#D4A053", fontSize: "0.8125rem", fontWeight: 600 });
const dimText075Style = Object.freeze({ color: "#A69880", fontSize: "0.75rem" });
const goldText075BoldStyle = Object.freeze({ color: "#D4A053", fontSize: "0.75rem", fontWeight: 600 });
const mb10Style = Object.freeze({ marginBottom: 10 });
const flexBetweenCenterStyle = Object.freeze({ display: "flex", justifyContent: "space-between", alignItems: "center" });
const flexBetweenCenterMb10Style = Object.freeze({ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 });
const grid2ColGap8Style = Object.freeze({ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 });
const sMarFon6Style = Object.freeze({ margin: 0, fontSize: "0.8125rem" });
const textCenterStyle = Object.freeze({ textAlign: "center" });
const posRelMb10Style = Object.freeze({ position: "relative", marginBottom: 10 });
const cardPanelStyle = Object.freeze({ background: "#1E1A14", borderRadius: 14, padding: 20, marginBottom: 20, border: "1px solid #3D3428" });
const faintText05625Mt2Style = Object.freeze({ fontSize: "0.5625rem", color: "#5A5040", marginTop: 2 });
const mutedLinkStyle = Object.freeze({ color: "#B5A898", textDecoration: "underline" });
const dimText05625UpperSpacedStyle = Object.freeze({ color: "#A69880", fontSize: "0.5625rem", textTransform: "uppercase", letterSpacing: 1 });
const font10Style = Object.freeze({ fontSize: "1.0rem" });
const flexBetweenCenterMb6Style = Object.freeze({ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 });
const sPadBacBor7Style = Object.freeze({ padding: "6px 14px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 6, color: "#B5A898", fontSize: "0.75rem", cursor: "pointer" });
const mutedText08125Style = Object.freeze({ color: "#B5A898", fontSize: "0.8125rem" });
const flexCenterGap6WrapStyle = Object.freeze({ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" });
const flexBetweenStartStyle = Object.freeze({ display: "flex", justifyContent: "space-between", alignItems: "flex-start" });
const textRightShrinkStyle = Object.freeze({ textAlign: "right", flexShrink: 0 });
const mutedText10Style = Object.freeze({ background: "none", border: "none", color: "#B5A898", cursor: "pointer", fontSize: "1.0rem", padding: 0 });
const sMar8Style = Object.freeze({ marginTop: 28 });
const mutedText0625UpperSpacedStyle = Object.freeze({ color: "#B5A898", fontSize: "0.625rem", textTransform: "uppercase", letterSpacing: 1 });
const dimText06875Style = Object.freeze({ color: "#A69880", fontSize: "0.6875rem" });
const mb8Style = Object.freeze({ marginBottom: 8 });
const mutedText0875Style = Object.freeze({ color: "#B5A898", fontSize: "0.875rem", margin: 0 });
const flexCenterCenterStyle = Object.freeze({ width: 36, height: 36, borderRadius: "50%", background: "#3D3428", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.125rem", flexShrink: 0 });
const mutedText06875Mt3Style = Object.freeze({ color: "#B5A898", fontSize: "0.6875rem", marginTop: 3 });
const posRelMb12Style = Object.freeze({ position: "relative", marginBottom: 12 });
const mutedText0625Mt4Style = Object.freeze({ fontSize: "0.625rem", color: "#B5A898", marginTop: 4 });
const dividerLineStyle = Object.freeze({ flex: 1, height: 1, background: "#3D3428" });
// ─── BROWSE CARD STYLES ────────────
const browseCardStyle = Object.freeze({ background: "#1E1A14", borderRadius: 12, padding: 16, marginBottom: 10, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" });
const browseCardLeftStyle = Object.freeze({ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 });
const browseCardImgStyle = Object.freeze({ width: 36, height: 36, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#150F0A", borderRadius: 8, border: "1px solid rgba(76,175,80,0.3)" });
const browseCardNameStyle = Object.freeze({ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "1.0rem", color: "#F5E6D0" });
const browseCardRatingWrapStyle = Object.freeze({ textAlign: "right", flexShrink: 0, marginLeft: 12 });
const browseCardRatingRowStyle = Object.freeze({ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3 });
const browseCardRatingNumStyle = Object.freeze({ color: "#D4A053", fontWeight: 700, fontSize: "1.25rem", fontFamily: "'Playfair Display', Georgia, serif" });
const browseCardRatingCountStyle = Object.freeze({ color: "#A69880", fontSize: "0.625rem" });
const browseCardOfficialBadgeStyle = Object.freeze({ padding: "2px 8px", borderRadius: 20, fontSize: "0.5625rem", fontWeight: 600, background: "rgba(76,175,80,0.12)", color: "#4CAF50", border: "1px solid rgba(76,175,80,0.3)" });
const browseCardPendingBadgeStyle = Object.freeze({ padding: "2px 8px", borderRadius: 20, fontSize: "0.5625rem", fontWeight: 600, background: "rgba(255,193,7,0.12)", color: "#FFC107", border: "1px solid rgba(255,193,7,0.3)" });
const browseCardCuratedBadgeStyle = Object.freeze({ padding: "2px 7px", borderRadius: 20, fontSize: "0.5rem", fontWeight: 700, background: "rgba(212,160,83,0.12)", color: "#D4A053", border: "1px solid rgba(212,160,83,0.25)" });
const browseCardProgramBadgeStyle = Object.freeze({ marginLeft: 8, padding: "1px 8px", borderRadius: 10, fontSize: "0.5625rem", fontWeight: 600, color: "#D4A053", background: "rgba(212,160,83,0.1)", border: "1px solid rgba(212,160,83,0.2)" });
const browseCardPickBaseBadgeStyle = Object.freeze({ marginLeft: 8, padding: "1px 8px", borderRadius: 10, fontSize: "0.5625rem", fontWeight: 600, color: "#6495ED", background: "rgba(100,149,237,0.15)", border: "1px solid rgba(100,149,237,0.25)" });
const browseCardBrandStyle = Object.freeze({ color: "#D4A053", fontSize: "0.75rem", marginTop: 3 });
const browseCardYouRatingStyle = Object.freeze({ color: "#A69880", fontSize: "0.625rem", marginTop: 1 });
const grainMapSectionStyle = Object.freeze({ marginBottom: 20 });
const grainMapHeaderStyle = Object.freeze({ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", cursor: "pointer", padding: "0 0 12px", minHeight: 44 });
const grainInsightChipStyle = Object.freeze({ display: "inline-block", padding: "4px 10px", borderRadius: 12, background: "rgba(196, 164, 105, 0.1)", fontSize: "0.75rem", color: "#D4CFC7", margin: "4px 4px 4px 0" });
const grainLaneLabelStyle = Object.freeze({ fontSize: 10, fill: "#F5E6D0", fontFamily: "inherit" });
const grainLegendStyle = Object.freeze({ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 8, fontSize: "0.625rem", color: "#B5A898", alignItems: "center" });
const grainTooltipStyle = Object.freeze({ background: "#1E1A14", borderRadius: 10, padding: 14, marginBottom: 12, border: "1px solid #5A4F3E" });
const grainInfoBtnBaseStyle = Object.freeze({ width: 20, height: 20, borderRadius: "50%", fontSize: "0.6875rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0, transition: "all 0.2s" });
const grainSectionTitleStyle = Object.freeze({ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "1.125rem", color: "#F5E6D0", margin: 0 });
const grainBadgePillStyle = Object.freeze({ fontSize: "0.75rem", color: "#D4A053", fontWeight: 700, padding: "2px 8px", background: "rgba(212,160,83,0.15)", borderRadius: 10 });
const grainChevronStyle = Object.freeze({ fontSize: "0.625rem", color: "#B5A898" });
const grainTooltipTitleStyle = Object.freeze({ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "0.875rem", color: "#D4A053", fontWeight: 700 });
const grainTooltipBodyStyle = Object.freeze({ color: "#D4CFC7", fontSize: "0.75rem", lineHeight: 1.5, margin: 0 });
const grainStapleRowStyle = Object.freeze({ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 });
const grainStapleMashRowStyle = Object.freeze({ display: "flex", alignItems: "center", gap: 8, marginTop: 8 });
const grainStapleBarContainerStyle = Object.freeze({ flex: 1, height: 14, borderRadius: 3, overflow: "hidden", display: "flex" });
const grainStaplePctStyle = Object.freeze({ fontSize: "0.6875rem", color: "#A69880", flexShrink: 0 });
const grainStapleDescStyle = Object.freeze({ fontSize: "0.75rem", color: "#D4CFC7" });
const grainChartWrapStyle = Object.freeze({ borderRadius: 12, padding: "8px 0" });
const grainSvgStyle = Object.freeze({ display: "block" });
const grainInsightsWrapStyle = Object.freeze({ marginTop: 10 });

const scanButtonStyle = Object.freeze({ display: "flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, borderRadius: 8, border: "1px solid rgba(212, 165, 116, 0.3)", background: "rgba(212, 165, 116, 0.1)", cursor: "pointer", flexShrink: 0 });
const SCAN_NO_MATCH_MESSAGES = Object.freeze([
  { message: "Not in our barrel yet", submessage: "Try searching by name instead" },
  { message: "That's a new one to us", submessage: "Give the search bar a shot" },
  { message: "Bottle not recognized", submessage: "Try a quick name search" },
]);
const scanTransitionOverlayStyle = Object.freeze({ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(26, 20, 15, 0.85)", zIndex: 9999, animation: "fadeIn 0.3s ease-out" });
const scanTransitionCardStyle = Object.freeze({ display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 40px", borderRadius: 16, background: "rgba(45, 35, 25, 0.95)", border: "1px solid rgba(212, 165, 116, 0.2)", boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)", maxWidth: 320 });
const supportFabStyle = Object.freeze({ position: "fixed", bottom: "80px", right: "16px", width: "40px", height: "40px", borderRadius: "50%", background: "rgba(45,35,25,0.85)", border: "1px solid rgba(196,164,105,0.3)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 900, transition: "opacity 0.2s" });
const skeletonCardStyle = Object.freeze({ height: "72px", borderRadius: "8px", marginBottom: "8px", background: "linear-gradient(90deg, rgba(45,35,25,0.3) 25%, rgba(45,35,25,0.15) 50%, rgba(45,35,25,0.3) 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite" });
const SkeletonCard = () => <div style={skeletonCardStyle} />;

const TASTING_SUGGESTIONS = Object.freeze({
  nose: Object.freeze(["Baking Spice", "Brown Sugar", "Butterscotch", "Caramel", "Cherry", "Cinnamon", "Citrus", "Dark Fruit", "Honey", "Leather", "Oak", "Tobacco", "Toasted Nuts", "Vanilla"]),
  palate: Object.freeze(["Caramel", "Vanilla", "Oak", "Cinnamon", "Cherry", "Brown Sugar", "Toffee", "Black Pepper", "Dark Chocolate", "Honey", "Dried Fruit", "Maple", "Mint", "Nutmeg"]),
  finish: Object.freeze(["Dry", "Long", "Medium", "Oaky", "Peppery", "Short", "Smooth", "Smoky", "Spicy", "Sweet", "Tannic", "Warm"]),
});
const tastingPillStyle = Object.freeze({ display: "inline-block", padding: "6px 12px", borderRadius: 16, fontSize: "0.8125rem", cursor: "pointer", border: "1px solid #3A3028", background: "#2A2118", color: "#C4A469", marginRight: 6, marginBottom: 6, transition: "all 0.15s ease", WebkitTapHighlightColor: "transparent", userSelect: "none" });
const tastingPillActiveStyle = Object.freeze({ ...tastingPillStyle, background: "#C4A469", color: "#1A1510", border: "1px solid #C4A469" });
const tastingPillRowStyle = Object.freeze({ display: "flex", flexWrap: "wrap", marginBottom: 6 });
const TastingSuggestionPills = memo(({ field, value, onChange }) => {
  const suggestions = TASTING_SUGGESTIONS[field];
  if (!suggestions) return null;
  const currentItems = (value || "").split(",").map(s => s.trim().toLowerCase()).filter(Boolean);
  const activeSet = new Set(currentItems);
  const handleToggle = (pill) => {
    const pillLower = pill.toLowerCase();
    const items = (value || "").split(",").map(s => s.trim()).filter(Boolean);
    if (activeSet.has(pillLower)) {
      onChange(items.filter(s => s.toLowerCase() !== pillLower).join(", "));
    } else {
      const newValue = [...items, pill].join(", ");
      if (newValue.length <= 200) onChange(newValue);
    }
  };
  return (
    <div style={tastingPillRowStyle}>
      {suggestions.map(pill => (
        <span key={pill} className="tap-feedback" style={activeSet.has(pill.toLowerCase()) ? tastingPillActiveStyle : tastingPillStyle} onClick={() => handleToggle(pill)}>{pill}</span>
      ))}
    </div>
  );
});

// ─── ADMIN CONTEXT (Phase 1 — provider only, consumers in Phase 2) ───
const AdminContext = createContext(null);
const useAdmin = () => useContext(AdminContext);

// ─── BRAND DASHBOARD (read-only brand owner/manager/analyst view) ───
const brandDashboardKpiTileStyle = Object.freeze({ background: "#1E1A14", borderRadius: 12, padding: "14px 12px", flex: 1, textAlign: "center", minWidth: 80 });
const brandDashboardTableHeaderStyle = Object.freeze({ padding: "10px 8px", textAlign: "left", fontSize: "0.75rem", color: "#A69880", fontWeight: 600, cursor: "pointer", userSelect: "none", borderBottom: "1px solid #3D3428", whiteSpace: "nowrap" });
const brandDashboardTableCellStyle = Object.freeze({ padding: "10px 8px", fontSize: "0.8125rem", color: "#F5E6D0", borderBottom: "1px solid rgba(61,52,40,0.4)", whiteSpace: "nowrap" });
const brandDashboardTierColors = Object.freeze({ curated: "#D4A053", core: "#B0C4DE", visible: "#98D8A8", archive: "#A69880" });
const brandDashboardTypeColors = Object.freeze({ program: "#D4A053", program_release: "#C4A469", pick_base: "#7EB8DA", pick: "#7EB8DA", reapproval: "#A69880", standard: "#F5E6D0" });

const BrandDashboard = memo(function BrandDashboard({
  brandRoles, brands, bourbons, checkins, communityCheckins, upcCodes, fullCatalogLoaded,
  navigateTo, setSelectedBourbon, onLoadArchive, currentUser, setBourbons, setUpcCodes,
}) {
  const [dashTab, setDashTab] = useState("home");
  const [selectedBrandId, setSelectedBrandId] = useState(() => brandRoles.length > 0 ? brandRoles[0].business_id : null);
  const [catalogSort, setCatalogSort] = useState({ key: "popularity", dir: "desc" });
  const [catalogTierFilter, setCatalogTierFilter] = useState("all");
  const [catalogTypeFilter, setCatalogTypeFilter] = useState("all");
  const [archiveRequested, setArchiveRequested] = useState(false);
  const [retailData, setRetailData] = useState(null);
  const [retailLoading, setRetailLoading] = useState(false);
  const [storePicksData, setStorePicksData] = useState(null);
  const [storePicksLoading, setStorePicksLoading] = useState(false);
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [retailSort, setRetailSort] = useState("name");
  // Enrichment state (BS-05/06/07)
  const [enrichExprId, setEnrichExprId] = useState(null);
  const [enrichForm, setEnrichForm] = useState({});
  const [enrichSaving, setEnrichSaving] = useState(false);
  const [enrichError, setEnrichError] = useState("");
  const [enrichToast, setEnrichToast] = useState("");
  const [upcInput, setUpcInput] = useState("");
  const [upcError, setUpcError] = useState("");
  const [upcCollision, setUpcCollision] = useState(null);
  const [nameCorrection, setNameCorrection] = useState("");
  const [showNameCorrect, setShowNameCorrect] = useState(false);
  const [nameSubmitted, setNameSubmitted] = useState(false);
  // BS-08/09/10 state
  const [showAddExpr, setShowAddExpr] = useState(false);
  const [addExprForm, setAddExprForm] = useState({ name: "", proof: "", age: "", finish: "", batch: "", description: "", upc: "", type: "standard", mashCorn: "", mashRye: "", mashWheat: "", mashBarley: "", mashOther: "" });
  const [addExprSaving, setAddExprSaving] = useState(false);
  const [addExprError, setAddExprError] = useState("");
  const [addExprDedup, setAddExprDedup] = useState(null);
  const [addReleaseParent, setAddReleaseParent] = useState(null);
  const [addReleaseForm, setAddReleaseForm] = useState({ batch: "", proof: "", age: "", description: "", upc: "" });
  const [addReleaseSaving, setAddReleaseSaving] = useState(false);
  const [addReleaseError, setAddReleaseError] = useState("");
  // BS-11/12 state
  const [bulkUploadRows, setBulkUploadRows] = useState(null);
  const [bulkUploadFile, setBulkUploadFile] = useState("");
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [collisionDeclare, setCollisionDeclare] = useState(null);

  const selectedBrand = useMemo(() => brands.find(b => b.id === selectedBrandId), [brands, selectedBrandId]);

  const brandExpressions = useMemo(() => bourbons.filter(b => b.brandId === selectedBrandId), [bourbons, selectedBrandId]);

  const upcSet = useMemo(() => new Set((upcCodes || []).map(u => u.expressionId || u.expression_id)), [upcCodes]);

  // Map: UPC → count of expressions sharing it (for GTIN collision detection)
  const sharedUpcMap = useMemo(() => {
    const byUpc = {};
    for (const u of (upcCodes || [])) { const upc = u.upc; if (!byUpc[upc]) byUpc[upc] = new Set(); byUpc[upc].add(u.expressionId || u.expression_id); }
    const shared = new Set();
    for (const [, ids] of Object.entries(byUpc)) { if (ids.size > 1) { for (const id of ids) shared.add(id); } }
    return shared;
  }, [upcCodes]);

  const allCheckins = useMemo(() => {
    const seen = new Set();
    const merged = [];
    for (const c of checkins) { if (!seen.has(c.id)) { seen.add(c.id); merged.push(c); } }
    for (const c of (communityCheckins || [])) { if (!seen.has(c.id)) { seen.add(c.id); merged.push(c); } }
    return merged;
  }, [checkins, communityCheckins]);

  const brandCheckins = useMemo(() => {
    const exprIds = new Set(brandExpressions.map(b => b.id));
    return allCheckins.filter(c => exprIds.has(c.bourbonId));
  }, [brandExpressions, allCheckins]);

  // KPIs
  const kpis = useMemo(() => {
    const tierCounts = { curated: 0, core: 0, visible: 0, archive: 0 };
    for (const b of brandExpressions) tierCounts[b.tier] = (tierCounts[b.tier] || 0) + 1;
    const totalExprs = brandExpressions.length;
    const totalCheckins = brandCheckins.length;
    const uniqueRaters = new Set(brandCheckins.map(c => c.userId)).size;
    const ratings = brandCheckins.filter(c => c.rating > 0).map(c => c.rating);
    const avgRating = ratings.length > 0 ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(2) : "—";

    // Popularity rank: find brand's best expression rank among all visible+ expressions
    const visiblePlus = bourbons.filter(b => b.tier !== "archive" && !b.parentExpressionId && !b.mergedIntoId);
    const sorted = [...visiblePlus].sort((a, b) => (b.popularityScore || 0) - (a.popularityScore || 0));
    const bestBrandExpr = brandExpressions
      .filter(b => b.tier !== "archive")
      .sort((a, b) => (b.popularityScore || 0) - (a.popularityScore || 0))[0];
    const popRank = bestBrandExpr ? sorted.findIndex(b => b.id === bestBrandExpr.id) + 1 : null;

    return { tierCounts, totalExprs, totalCheckins, uniqueRaters, avgRating, popRank };
  }, [brandExpressions, brandCheckins, bourbons]);

  // Top 5 by check-in count
  const topByCheckins = useMemo(() => {
    const countMap = new Map();
    const ratingMap = new Map();
    for (const c of brandCheckins) {
      countMap.set(c.bourbonId, (countMap.get(c.bourbonId) || 0) + 1);
      if (c.rating > 0) {
        if (!ratingMap.has(c.bourbonId)) ratingMap.set(c.bourbonId, []);
        ratingMap.get(c.bourbonId).push(c.rating);
      }
    }
    return [...countMap.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([id, count]) => {
        const expr = brandExpressions.find(b => b.id === id);
        const rats = ratingMap.get(id) || [];
        const avg = rats.length > 0 ? (rats.reduce((a, b) => a + b, 0) / rats.length).toFixed(2) : "—";
        return { id, name: expr?.name || id, count, avgRating: avg };
      });
  }, [brandCheckins, brandExpressions]);

  // Top 5 by avg rating (min 3 ratings)
  const topByRating = useMemo(() => {
    const ratingMap = new Map();
    for (const c of brandCheckins) {
      if (c.rating > 0) {
        if (!ratingMap.has(c.bourbonId)) ratingMap.set(c.bourbonId, []);
        ratingMap.get(c.bourbonId).push(c.rating);
      }
    }
    return [...ratingMap.entries()]
      .filter(([, rats]) => rats.length >= 3)
      .map(([id, rats]) => {
        const expr = brandExpressions.find(b => b.id === id);
        const avg = rats.reduce((a, b) => a + b, 0) / rats.length;
        return { id, name: expr?.name || id, avgRating: avg.toFixed(2), count: rats.length };
      })
      .sort((a, b) => parseFloat(b.avgRating) - parseFloat(a.avgRating))
      .slice(0, 5);
  }, [brandCheckins, brandExpressions]);

  // Catalog table with sorting and filtering
  const catalogRows = useMemo(() => {
    let rows = brandExpressions;
    if (catalogTierFilter !== "all") rows = rows.filter(b => b.tier === catalogTierFilter);
    if (catalogTypeFilter !== "all") rows = rows.filter(b => (b.expressionType || "standard") === catalogTypeFilter);
    const dir = catalogSort.dir === "asc" ? 1 : -1;
    const key = catalogSort.key;
    rows = [...rows].sort((a, b) => {
      let va, vb;
      if (key === "name") { va = (a.name || "").toLowerCase(); vb = (b.name || "").toLowerCase(); return va < vb ? -dir : va > vb ? dir : 0; }
      if (key === "tier") { const rank = { curated: 4, core: 3, visible: 2, archive: 1 }; return (rank[a.tier] - rank[b.tier]) * dir; }
      if (key === "type") { va = a.expressionType || "standard"; vb = b.expressionType || "standard"; return va < vb ? -dir : va > vb ? dir : 0; }
      if (key === "proof") { return ((a.proof || 0) - (b.proof || 0)) * dir; }
      if (key === "age") { return ((a.age || 0) - (b.age || 0)) * dir; }
      if (key === "popularity") { return ((a.popularityScore || 0) - (b.popularityScore || 0)) * dir; }
      if (key === "checkins") {
        const ca = brandCheckins.filter(c => c.bourbonId === a.id).length;
        const cb = brandCheckins.filter(c => c.bourbonId === b.id).length;
        return (ca - cb) * dir;
      }
      if (key === "rating") {
        const ra = brandCheckins.filter(c => c.bourbonId === a.id && c.rating > 0);
        const rb = brandCheckins.filter(c => c.bourbonId === b.id && c.rating > 0);
        const avgA = ra.length > 0 ? ra.reduce((s, c) => s + c.rating, 0) / ra.length : 0;
        const avgB = rb.length > 0 ? rb.reduce((s, c) => s + c.rating, 0) / rb.length : 0;
        return (avgA - avgB) * dir;
      }
      return 0;
    });
    return rows;
  }, [brandExpressions, catalogSort, catalogTierFilter, catalogTypeFilter, brandCheckins]);

  const toggleSort = (key) => {
    setCatalogSort(prev => prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });
  };

  const sortArrow = (key) => catalogSort.key === key ? (catalogSort.dir === "asc" ? " ↑" : " ↓") : "";

  // Request archive load when catalog tab opened
  useEffect(() => {
    if (dashTab === "catalog" && !fullCatalogLoaded && !archiveRequested) {
      setArchiveRequested(true);
      if (onLoadArchive) onLoadArchive();
    }
  }, [dashTab, fullCatalogLoaded, archiveRequested, onLoadArchive]);

  // Fetch retail data on first Retail tab visit
  useEffect(() => {
    if (dashTab !== "retail" || retailData || retailLoading || !selectedBrandId) return;
    setRetailLoading(true);
    const sb = getSupabase();
    if (!sb) { setRetailLoading(false); return; }
    Promise.all([
      sb.from("plcb_catalog").select("plcb_item, description, msrp, upc, match_confidence, matched_bourbon_id").eq("matched_brand_id", selectedBrandId),
      sb.from("iowa_catalog").select("item_number, item_description, state_bottle_retail, bottles_sold_12mo, match_confidence, matched_bourbon_id").eq("matched_brand_id", selectedBrandId),
      sb.from("virginia_catalog").select("product_code, product_name, retail_price, match_confidence, matched_bourbon_id").eq("matched_brand_id", selectedBrandId),
      sb.from("nc_catalog").select("nc_code, product_name, retail_price, match_confidence, matched_bourbon_id").eq("matched_brand_id", selectedBrandId),
    ]).then(([pa, ia, va, nc]) => {
      setRetailData({
        pa: (pa.data || []).map(r => ({ ...r, state: "PA" })),
        ia: (ia.data || []).map(r => ({ ...r, state: "IA", msrp: r.state_bottle_retail })),
        va: (va.data || []).map(r => ({ ...r, state: "VA", msrp: r.retail_price })),
        nc: (nc.data || []).map(r => ({ ...r, state: "NC", msrp: r.retail_price })),
      });
      setRetailLoading(false);
    }).catch(() => setRetailLoading(false));
  }, [dashTab, retailData, retailLoading, selectedBrandId]);

  // Fetch store picks on first Community tab visit (if brand has pick_base expressions)
  const brandPickBaseIds = useMemo(() => brandExpressions.filter(b => b.expressionType === "pick_base").map(b => b.id), [brandExpressions]);

  useEffect(() => {
    if (dashTab !== "community" || storePicksData || storePicksLoading || brandPickBaseIds.length === 0) return;
    setStorePicksLoading(true);
    const sb = getSupabase();
    if (!sb) { setStorePicksLoading(false); return; }
    sb.from("store_picks").select("id, bourbon_id, store_name, proof, pick_date").in("bourbon_id", brandPickBaseIds)
      .then(({ data }) => { setStorePicksData(data || []); setStorePicksLoading(false); })
      .catch(() => setStorePicksLoading(false));
  }, [dashTab, storePicksData, storePicksLoading, brandPickBaseIds]);

  // Retail tab computed data
  const retailRows = useMemo(() => {
    if (!retailData) return [];
    const allEntries = [...retailData.pa, ...retailData.ia, ...retailData.va, ...retailData.nc];
    const byExpr = new Map();
    for (const r of allEntries) {
      if (!r.matched_bourbon_id) continue;
      if (!byExpr.has(r.matched_bourbon_id)) byExpr.set(r.matched_bourbon_id, { states: new Set(), prices: [], confidence: 0 });
      const group = byExpr.get(r.matched_bourbon_id);
      group.states.add(r.state);
      if (r.msrp && parseFloat(r.msrp) > 0) group.prices.push(parseFloat(r.msrp));
      group.confidence = Math.max(group.confidence, r.match_confidence || 0);
    }
    let rows = [...byExpr.entries()].map(([id, g]) => {
      const expr = brandExpressions.find(b => b.id === id);
      return { id, name: expr?.name || id, stateCount: g.states.size, states: g.states, bestPrice: g.prices.length > 0 ? Math.min(...g.prices) : null, confidence: g.confidence };
    });
    if (retailSort === "states") rows.sort((a, b) => b.stateCount - a.stateCount);
    else rows.sort((a, b) => a.name.localeCompare(b.name));
    return rows;
  }, [retailData, brandExpressions, retailSort]);

  const retailStateCounts = useMemo(() => {
    if (!retailData) return { PA: 0, IA: 0, VA: 0, NC: 0 };
    return {
      PA: new Set(retailData.pa.filter(r => r.matched_bourbon_id).map(r => r.matched_bourbon_id)).size,
      IA: new Set(retailData.ia.filter(r => r.matched_bourbon_id).map(r => r.matched_bourbon_id)).size,
      VA: new Set(retailData.va.filter(r => r.matched_bourbon_id).map(r => r.matched_bourbon_id)).size,
      NC: new Set(retailData.nc.filter(r => r.matched_bourbon_id).map(r => r.matched_bourbon_id)).size,
    };
  }, [retailData]);

  const retailUnmatched = useMemo(() => {
    const matchedIds = new Set(retailRows.map(r => r.id));
    return brandExpressions.filter(b => b.tier !== "archive" && !matchedIds.has(b.id));
  }, [retailRows, brandExpressions]);

  // Community tab computations
  const ratingDistribution = useMemo(() => {
    const buckets = Array.from({ length: 11 }, (_, i) => ({ label: (i * 0.5).toFixed(1), count: 0 }));
    for (const c of brandCheckins) {
      if (c.rating > 0) {
        const idx = Math.min(Math.round(c.rating * 2), 10);
        buckets[idx].count++;
      }
    }
    return buckets;
  }, [brandCheckins]);

  const topTastingNotes = useMemo(() => {
    const freq = new Map();
    for (const c of brandCheckins) {
      for (const note of (c.tastingNotes || [])) {
        freq.set(note, (freq.get(note) || 0) + 1);
      }
    }
    return [...freq.entries()].sort((a, b) => b[1] - a[1]).slice(0, 20).map(([note, count]) => ({ note, count }));
  }, [brandCheckins]);

  const activityTimeline = useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleString("default", { month: "short" }), count: 0 });
    }
    for (const c of brandCheckins) {
      if (!c.date) continue;
      const ym = c.date.slice(0, 7);
      const bucket = months.find(m => m.key === ym);
      if (bucket) bucket.count++;
    }
    return months;
  }, [brandCheckins]);

  const storePicksSummary = useMemo(() => {
    if (!storePicksData || storePicksData.length === 0) return null;
    const storeCount = new Map();
    for (const p of storePicksData) {
      if (p.store_name) storeCount.set(p.store_name, (storeCount.get(p.store_name) || 0) + 1);
    }
    const topStores = [...storeCount.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name, count]) => ({ name, count }));
    return { total: storePicksData.length, topStores };
  }, [storePicksData]);

  const userBrandRole = useMemo(() => brandRoles.find(r => r.business_id === selectedBrandId)?.role, [brandRoles, selectedBrandId]);
  const canEnrich = userBrandRole === "owner" || userBrandRole === "manager";
  const isOwner = userBrandRole === "owner";

  const ENRICHABLE_TYPES = useMemo(() => new Set(["standard", "program", "program_release", "pick_base"]), []);

  const openEnrichPanel = (expr) => {
    if (enrichExprId === expr.id) { setEnrichExprId(null); return; }
    setEnrichExprId(expr.id);
    setEnrichForm({
      proof: expr.proof || "", age: expr.age || "",
      mashCorn: expr.mashCorn ?? "", mashWheat: expr.mashWheat ?? "",
      mashRye: expr.mashRye ?? "", mashBarley: expr.mashBarley ?? "",
      mashOther: expr.mashOther ?? "",
      finish: expr.finish || "", batch: expr.batch || "",
      description: expr.colaDescription || "",
    });
    setEnrichError(""); setEnrichToast("");
    setUpcInput(""); setUpcError(""); setUpcCollision(null);
    setNameCorrection(""); setShowNameCorrect(false); setNameSubmitted(false);
  };

  const validateUpcCheckDigit = (upc) => {
    if (upc.length !== 12) return false;
    let sum = 0;
    for (let i = 0; i < 11; i++) sum += parseInt(upc[i]) * (i % 2 === 0 ? 3 : 1);
    const check = (10 - (sum % 10)) % 10;
    return check === parseInt(upc[11]);
  };

  const handleEnrichSave = async (expr) => {
    setEnrichSaving(true); setEnrichError("");
    const sb = getSupabase();
    if (!sb) { setEnrichSaving(false); setEnrichError("Database unavailable"); return; }
    const updates = {};
    const f = enrichForm;
    if (f.proof !== "" && parseFloat(f.proof) !== expr.proof) updates.proof = parseFloat(f.proof);
    if (f.age !== "" && parseFloat(f.age) !== expr.age) updates.age = parseFloat(f.age);
    if (f.finish !== (expr.finish || "")) updates.finish = f.finish;
    if (f.batch !== (expr.batch || "")) updates.batch = f.batch;
    if (f.description !== (expr.colaDescription || "")) updates.cola_description = f.description;
    const mc = parseInt(f.mashCorn) || 0, mw = parseInt(f.mashWheat) || 0, mr = parseInt(f.mashRye) || 0, mb = parseInt(f.mashBarley) || 0, mo = parseInt(f.mashOther) || 0;
    const mashTotal = mc + mw + mr + mb + mo;
    if (mashTotal > 0 && mashTotal <= 100) {
      if (mc !== (expr.mashCorn || 0) || mw !== (expr.mashWheat || 0) || mr !== (expr.mashRye || 0) || mb !== (expr.mashBarley || 0) || mo !== (expr.mashOther || 0)) {
        updates.mash_corn = mc; updates.mash_wheat = mw; updates.mash_rye = mr; updates.mash_barley = mb; updates.mash_other = mo;
        updates.mashbill_source = "published";
      }
    } else if (mashTotal > 100) { setEnrichSaving(false); setEnrichError("Mash bill total exceeds 100%"); return; }
    if (Object.keys(updates).length === 0) { setEnrichSaving(false); setEnrichError("No changes to save"); return; }
    updates.brand_enriched_at = new Date().toISOString();
    const { error } = await sb.from("bourbons").update(updates).eq("id", expr.id);
    if (error) { setEnrichSaving(false); setEnrichError(error.message); return; }
    await sb.from("audit_log").insert({
      actor: currentUser?.uid, action: "brand_enrich", target_type: "expression", target_id: expr.id,
      detail: JSON.stringify({ fields: Object.keys(updates), source: "brand" }), category: "expression",
    });
    // Update local state
    setBourbons(prev => prev.map(b => {
      if (b.id !== expr.id) return b;
      const upd = { ...b, brandEnrichedAt: updates.brand_enriched_at };
      if (updates.proof !== undefined) upd.proof = updates.proof;
      if (updates.age !== undefined) upd.age = updates.age;
      if (updates.finish !== undefined) upd.finish = updates.finish;
      if (updates.batch !== undefined) upd.batch = updates.batch;
      if (updates.cola_description !== undefined) upd.colaDescription = updates.cola_description;
      if (updates.mash_corn !== undefined) { upd.mashCorn = updates.mash_corn; upd.mashWheat = updates.mash_wheat; upd.mashRye = updates.mash_rye; upd.mashBarley = updates.mash_barley; upd.mashOther = updates.mash_other; upd.mashbillSource = "published"; }
      return upd;
    }));
    setEnrichSaving(false); setEnrichToast("Expression updated");
    setTimeout(() => setEnrichToast(""), 3000);
  };

  const handleUpcSubmit = async (expr, force = false) => {
    const cleaned = upcInput.replace(/[\s-]/g, "");
    if (!/^\d{12}$/.test(cleaned)) { setUpcError("UPC must be exactly 12 digits"); return; }
    if (!validateUpcCheckDigit(cleaned)) { setUpcError("Invalid check digit"); return; }
    setUpcError("");
    const sb = getSupabase();
    if (!sb) { setUpcError("Database unavailable"); return; }
    // Check collision
    if (!force) {
      const { data: existing } = await sb.from("upc_codes").select("expression_id").eq("upc", cleaned);
      if (existing && existing.length > 0) {
        if (existing.some(e => e.expression_id === expr.id)) { setUpcError("This UPC is already recorded for this expression"); return; }
        const linked = existing[0].expression_id;
        const linkedExpr = bourbons.find(b => b.id === linked);
        setUpcCollision({ upc: cleaned, linkedName: linkedExpr?.name || linked, linkedId: linked });
        return;
      }
    }
    setUpcCollision(null);
    const { error } = await sb.from("upc_codes").insert({ expression_id: expr.id, upc: cleaned, source: "brand", verified: true });
    if (error) { setUpcError(error.message); return; }
    await sb.from("audit_log").insert({
      actor: currentUser?.uid, action: "brand_upc_add", target_type: "upc", target_id: expr.id,
      detail: JSON.stringify({ upc: cleaned, source: "brand" }), category: "upc",
    });
    setUpcCodes(prev => [...prev, { expressionId: expr.id, upc: cleaned, source: "brand", verified: true }]);
    setUpcInput(""); setEnrichToast("UPC added");
    setTimeout(() => setEnrichToast(""), 3000);
  };

  const handleNameConfirm = async (expr) => {
    const sb = getSupabase();
    if (!sb) return;
    const { error } = await sb.from("bourbons").update({ name_locked: true, brand_confirmed: true }).eq("id", expr.id);
    if (error) { setEnrichError(error.message); return; }
    await sb.from("audit_log").insert({
      actor: currentUser?.uid, action: "brand_name_confirm", target_type: "expression", target_id: expr.id,
      detail: JSON.stringify({ name: expr.name, source: "brand" }), category: "expression",
    });
    setBourbons(prev => prev.map(b => b.id === expr.id ? { ...b, nameLocked: true, brandConfirmed: true } : b));
    setEnrichToast("Name confirmed");
    setTimeout(() => setEnrichToast(""), 3000);
  };

  const handleNameCorrectionSubmit = async (expr) => {
    const brandName = selectedBrand?.name || "";
    const normalized = normalizeColaName(nameCorrection, brandName);
    if (normalized === expr.name) { setEnrichError("Name already matches after normalization"); return; }
    const sb = getSupabase();
    if (!sb) return;
    const { error } = await sb.from("expression_edits").insert({
      bourbon_id: expr.id, user_id: currentUser?.uid,
      suggested_changes: JSON.stringify({ name: nameCorrection, normalized_name: normalized }),
      reason: "Brand name correction request", status: "pending", source: "brand",
    });
    if (error) { setEnrichError(error.message); return; }
    await sb.from("audit_log").insert({
      actor: currentUser?.uid, action: "brand_name_correction", target_type: "expression", target_id: expr.id,
      detail: JSON.stringify({ current: expr.name, suggested: normalized, source: "brand" }), category: "expression",
    });
    setNameSubmitted(true); setShowNameCorrect(false);
    setEnrichToast("Name correction submitted for review");
    setTimeout(() => setEnrichToast(""), 3000);
  };

  // BS-08: Dedup check for new expression
  const checkExprDedup = (name) => {
    if (!name.trim()) { setAddExprDedup(null); return; }
    const brandName = selectedBrand?.name || "";
    const normalized = normalizeColaName(name, brandName);
    const match = brandExpressions.find(e => normalizeColaName(e.name || "", brandName) === normalized);
    setAddExprDedup(match || null);
  };

  // BS-08: Create new expression
  const handleAddExpression = async (skipDedup = false) => {
    const f = addExprForm;
    if (!f.name.trim()) { setAddExprError("Name is required"); return; }
    const brandName = selectedBrand?.name || "";
    const normalizedName = normalizeColaName(f.name, brandName);
    // Dedup check
    if (!skipDedup) {
      const match = brandExpressions.find(e => normalizeColaName(e.name || "", brandName) === normalizedName);
      if (match) { setAddExprDedup(match); return; }
    }
    setAddExprSaving(true); setAddExprError("");
    const sb = getSupabase();
    if (!sb) { setAddExprSaving(false); setAddExprError("Database unavailable"); return; }
    const expressionId = `brand-${selectedBrandId}-${Date.now()}`;
    const mc = parseInt(f.mashCorn) || 0, mw = parseInt(f.mashWheat) || 0, mr = parseInt(f.mashRye) || 0, mb = parseInt(f.mashBarley) || 0, mo = parseInt(f.mashOther) || 0;
    const mashTotal = mc + mw + mr + mb + mo;
    if (mashTotal > 100) { setAddExprSaving(false); setAddExprError("Mash bill total exceeds 100%"); return; }
    const row = {
      id: expressionId, name: normalizedName, brand_id: selectedBrandId, source: "brand",
      tier: "visible", expression_type: f.type, submission_status: "approved",
      name_locked: true, brand_confirmed: true, brand_enriched_at: new Date().toISOString(),
      added_by: currentUser?.uid,
      ...(f.proof && { proof: parseFloat(f.proof) }),
      ...(f.age && { age: parseInt(f.age) }),
      ...(f.finish && { finish: f.finish }),
      ...(f.batch && { batch: f.batch }),
      ...(f.description && { cola_description: f.description }),
      ...(mashTotal > 0 && { mash_corn: mc, mash_wheat: mw, mash_rye: mr, mash_barley: mb, mash_other: mo, mashbill_source: "published" }),
    };
    const { error } = await sb.from("bourbons").insert(row).select().single();
    if (error) { setAddExprSaving(false); setAddExprError(error.message); return; }
    // UPC insert
    if (f.upc) {
      const cleaned = f.upc.replace(/[\s-]/g, "");
      if (/^\d{12}$/.test(cleaned) && validateUpcCheckDigit(cleaned)) {
        await sb.from("upc_codes").insert({ expression_id: expressionId, upc: cleaned, source: "brand", verified: true });
        setUpcCodes(prev => [...prev, { expressionId, upc: cleaned, source: "brand", verified: true }]);
      }
    }
    await sb.from("audit_log").insert({
      actor: currentUser?.uid, action: "brand_create", target_type: "expression", target_id: expressionId,
      detail: JSON.stringify({ name: normalizedName, type: f.type, source: "brand" }), category: "expression",
    });
    // Add to local state
    const localExpr = {
      id: expressionId, name: normalizedName, brandId: selectedBrandId, source: "brand",
      tier: "visible", expressionType: f.type, submissionStatus: "approved",
      nameLocked: true, brandConfirmed: true, brandEnrichedAt: row.brand_enriched_at,
      addedBy: currentUser?.uid, proof: f.proof ? parseFloat(f.proof) : null,
      age: f.age ? parseInt(f.age) : null, finish: f.finish || null, batch: f.batch || null,
      colaDescription: f.description || null, popularityScore: 0,
      ...(mashTotal > 0 && { mashCorn: mc, mashWheat: mw, mashRye: mr, mashBarley: mb, mashOther: mo, mashbillSource: "published" }),
    };
    setBourbons(prev => [...prev, localExpr]);
    setAddExprSaving(false); setShowAddExpr(false);
    setAddExprForm({ name: "", proof: "", age: "", finish: "", batch: "", description: "", upc: "", type: "standard", mashCorn: "", mashRye: "", mashWheat: "", mashBarley: "", mashOther: "" });
    setAddExprDedup(null);
    setEnrichToast("Expression created");
    setTimeout(() => setEnrichToast(""), 3000);
  };

  // BS-09: Create program release
  const handleAddRelease = async () => {
    const parent = addReleaseParent;
    const f = addReleaseForm;
    if (!f.batch.trim()) { setAddReleaseError("Batch identifier is required"); return; }
    if (!f.proof) { setAddReleaseError("Proof is required for releases"); return; }
    setAddReleaseSaving(true); setAddReleaseError("");
    const sb = getSupabase();
    if (!sb) { setAddReleaseSaving(false); setAddReleaseError("Database unavailable"); return; }
    const expressionId = `brand-${selectedBrandId}-${Date.now()}`;
    const brandName = selectedBrand?.name || "";
    const releaseName = normalizeColaName(`${parent.name} ${f.batch}`, brandName);
    const row = {
      id: expressionId, name: releaseName, brand_id: selectedBrandId, source: "brand",
      tier: "visible", expression_type: "program_release", parent_expression_id: parent.id,
      submission_status: "approved", name_locked: true, brand_confirmed: true,
      brand_enriched_at: new Date().toISOString(), added_by: currentUser?.uid,
      proof: parseFloat(f.proof), batch: f.batch,
      ...(f.age && { age: parseInt(f.age) }),
      ...(f.description && { cola_description: f.description }),
    };
    const { error } = await sb.from("bourbons").insert(row).select().single();
    if (error) { setAddReleaseSaving(false); setAddReleaseError(error.message); return; }
    // UPC
    if (f.upc) {
      const cleaned = f.upc.replace(/[\s-]/g, "");
      if (/^\d{12}$/.test(cleaned) && validateUpcCheckDigit(cleaned)) {
        await sb.from("upc_codes").insert({ expression_id: expressionId, upc: cleaned, source: "brand", verified: true });
        setUpcCodes(prev => [...prev, { expressionId, upc: cleaned, source: "brand", verified: true }]);
      }
    }
    // Update parent proof range
    const localRelease = { id: expressionId, name: releaseName, brandId: selectedBrandId, tier: "visible", expressionType: "program_release", parentExpressionId: parent.id, submissionStatus: "approved", proof: parseFloat(f.proof), age: f.age ? parseInt(f.age) : null, batch: f.batch, colaDescription: f.description || null, nameLocked: true, brandConfirmed: true, brandEnrichedAt: row.brand_enriched_at, addedBy: currentUser?.uid, popularityScore: 0, source: "brand" };
    const siblings = brandExpressions.filter(e => e.parentExpressionId === parent.id);
    const allProofs = [...siblings.map(s => s.proof), parseFloat(f.proof)].filter(Boolean);
    if (allProofs.length > 0) {
      const newMin = Math.min(...allProofs), newMax = Math.max(...allProofs);
      await sb.from("bourbons").update({ proof_min: newMin, proof_max: newMax }).eq("id", parent.id);
      setBourbons(prev => prev.map(b => b.id === parent.id ? { ...b, proofMin: newMin, proofMax: newMax } : b));
    }
    await sb.from("audit_log").insert({
      actor: currentUser?.uid, action: "brand_create_release", target_type: "expression", target_id: expressionId,
      detail: JSON.stringify({ name: releaseName, parent: parent.id, batch: f.batch, source: "brand" }), category: "expression",
    });
    setBourbons(prev => [...prev, localRelease]);
    setAddReleaseSaving(false); setAddReleaseParent(null);
    setAddReleaseForm({ batch: "", proof: "", age: "", description: "", upc: "" });
    setEnrichToast("Release added");
    setTimeout(() => setEnrichToast(""), 3000);
  };

  // BS-10: Set expression type (owner only)
  const handleSetExprType = async (expr, newType) => {
    const childCount = brandExpressions.filter(e => e.parentExpressionId === expr.id).length;
    if (childCount > 0) { setEnrichError(`This expression has ${childCount} child release${childCount !== 1 ? "s" : ""} and cannot change type.`); setTimeout(() => setEnrichError(""), 4000); return; }
    const label = newType === "program" ? "a batch program parent" : "a pick base for community Store Pick tracking";
    if (!window.confirm(`This will designate "${expr.name}" as ${label}. Continue?`)) return;
    const sb = getSupabase();
    if (!sb) return;
    const { error } = await sb.from("bourbons").update({ expression_type: newType }).eq("id", expr.id);
    if (error) { setEnrichError(error.message); setTimeout(() => setEnrichError(""), 4000); return; }
    await sb.from("audit_log").insert({
      actor: currentUser?.uid, action: "brand_set_type", target_type: "expression", target_id: expr.id,
      detail: JSON.stringify({ from: expr.expressionType || "standard", to: newType, source: "brand" }), category: "expression",
    });
    setBourbons(prev => prev.map(b => b.id === expr.id ? { ...b, expressionType: newType } : b));
    setEnrichToast(`Type set to ${newType}`);
    setTimeout(() => setEnrichToast(""), 3000);
  };

  const handleExprClick = (expr) => {
    setSelectedBourbon(expr);
    navigateTo("bourbon-detail");
  };

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.375rem", color: "#F5E6D0", fontWeight: 700 }}>Brand Dashboard</div>
      </div>

      {/* Brand switcher (multiple brands) */}
      {brandRoles.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <select
            value={selectedBrandId || ""}
            onChange={e => { setSelectedBrandId(e.target.value); setDashTab("home"); }}
            aria-label="Select brand"
            style={{ width: "100%", padding: "10px 12px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 8, color: "#F5E6D0", fontSize: "0.875rem" }}
          >
            {brandRoles.map(r => {
              const brand = brands.find(b => b.id === r.business_id);
              return <option key={r.id || r.business_id} value={r.business_id}>{brand?.name || r.business_id} ({r.role})</option>;
            })}
          </select>
        </div>
      )}

      {/* Brand info header */}
      {selectedBrand && (
        <div style={{ background: "#1E1A14", borderRadius: 12, padding: 16, marginBottom: 16, borderLeft: "3px solid #5B9A8B" }}>
          <div style={{ fontSize: "1.125rem", color: "#F5E6D0", fontWeight: 700 }}>{selectedBrand.name}</div>
          <div style={{ fontSize: "0.8125rem", color: "#A69880", marginTop: 4 }}>
            {[selectedBrand.city, selectedBrand.state].filter(Boolean).join(", ") || "Location not set"}
            {selectedBrand.yearEstablished ? ` · Est. ${selectedBrand.yearEstablished}` : ""}
          </div>
          <div style={{ fontSize: "0.75rem", color: "#5B9A8B", marginTop: 4, fontWeight: 600 }}>
            {brandRoles.find(r => r.business_id === selectedBrandId)?.role?.toUpperCase() || "MEMBER"}
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "1px solid #3D3428" }}>
        {[{ key: "home", label: "Overview" }, { key: "catalog", label: "Expressions" }, { key: "upcs", label: "UPCs" }, { key: "retail", label: "Retail" }, { key: "community", label: "Community" }].map(t => (
          <button key={t.key} onClick={() => setDashTab(t.key)}
            style={{ flex: 1, padding: "12px 0", background: "none", border: "none", borderBottom: dashTab === t.key ? "2px solid #5B9A8B" : "2px solid transparent", color: dashTab === t.key ? "#F5E6D0" : "#A69880", fontSize: "0.875rem", fontWeight: dashTab === t.key ? 700 : 400, cursor: "pointer", transition: "all 0.15s" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── HOME TAB ─── */}
      {dashTab === "home" && (
        <div>
          {/* KPI tiles */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={brandDashboardKpiTileStyle}>
              <div style={{ fontSize: "1.375rem", color: "#F5E6D0", fontWeight: 700 }}>{kpis.totalExprs}</div>
              <div style={{ fontSize: "0.6875rem", color: "#A69880", marginTop: 2 }}>Expressions</div>
              <div style={{ fontSize: "0.625rem", color: "#5B9A8B", marginTop: 4 }}>
                {Object.entries(kpis.tierCounts).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(" · ")}
              </div>
            </div>
            <div style={brandDashboardKpiTileStyle}>
              <div style={{ fontSize: "1.375rem", color: "#F5E6D0", fontWeight: 700 }}>{kpis.totalCheckins}</div>
              <div style={{ fontSize: "0.6875rem", color: "#A69880", marginTop: 2 }}>Check-ins</div>
            </div>
            <div style={brandDashboardKpiTileStyle}>
              <div style={{ fontSize: "1.375rem", color: "#F5E6D0", fontWeight: 700 }}>{kpis.uniqueRaters}</div>
              <div style={{ fontSize: "0.6875rem", color: "#A69880", marginTop: 2 }}>Unique Raters</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            <div style={brandDashboardKpiTileStyle}>
              <div style={{ fontSize: "1.375rem", color: "#D4A053", fontWeight: 700 }}>{kpis.avgRating}</div>
              <div style={{ fontSize: "0.6875rem", color: "#A69880", marginTop: 2 }}>Avg Rating</div>
            </div>
            <div style={brandDashboardKpiTileStyle}>
              <div style={{ fontSize: "1.375rem", color: "#F5E6D0", fontWeight: 700 }}>{kpis.popRank ? `#${kpis.popRank}` : "—"}</div>
              <div style={{ fontSize: "0.6875rem", color: "#A69880", marginTop: 2 }}>Popularity Rank</div>
            </div>
          </div>

          {/* Top by check-ins */}
          {topByCheckins.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: "0.8125rem", color: "#A69880", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Top by Check-ins</div>
              {topByCheckins.map((row, i) => (
                <div key={row.id} onClick={() => { const expr = brandExpressions.find(b => b.id === row.id); if (expr) handleExprClick(expr); }}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: i % 2 === 0 ? "#1E1A14" : "transparent", borderRadius: 8, cursor: "pointer" }}>
                  <span style={{ color: "#5B9A8B", fontWeight: 700, fontSize: "0.875rem", minWidth: 20 }}>{i + 1}</span>
                  <span style={{ flex: 1, fontSize: "0.8125rem", color: "#F5E6D0" }}>{row.name}</span>
                  <span style={{ fontSize: "0.75rem", color: "#A69880", minWidth: 50, textAlign: "right" }}>{row.count} pours</span>
                  <span style={{ fontSize: "0.75rem", color: "#D4A053", minWidth: 35, textAlign: "right" }}>{row.avgRating}</span>
                </div>
              ))}
            </div>
          )}

          {/* Top by rating */}
          {topByRating.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: "0.8125rem", color: "#A69880", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Top Rated (3+ reviews)</div>
              {topByRating.map((row, i) => (
                <div key={row.id} onClick={() => { const expr = brandExpressions.find(b => b.id === row.id); if (expr) handleExprClick(expr); }}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: i % 2 === 0 ? "#1E1A14" : "transparent", borderRadius: 8, cursor: "pointer" }}>
                  <span style={{ color: "#D4A053", fontWeight: 700, fontSize: "0.875rem", minWidth: 20 }}>{i + 1}</span>
                  <span style={{ flex: 1, fontSize: "0.8125rem", color: "#F5E6D0" }}>{row.name}</span>
                  <span style={{ fontSize: "0.75rem", color: "#D4A053", fontWeight: 600, minWidth: 35, textAlign: "right" }}>{row.avgRating}</span>
                  <span style={{ fontSize: "0.75rem", color: "#A69880", minWidth: 55, textAlign: "right" }}>{row.count} ratings</span>
                </div>
              ))}
            </div>
          )}

          {topByCheckins.length === 0 && topByRating.length === 0 && (
            <div style={{ textAlign: "center", padding: 32, color: "#A69880", fontSize: "0.875rem" }}>
              No check-in data yet for this brand.
            </div>
          )}
        </div>
      )}

      {/* ─── CATALOG TAB ─── */}
      {dashTab === "catalog" && (
        <div>
          {/* Filter row */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <select value={catalogTierFilter} onChange={e => setCatalogTierFilter(e.target.value)} aria-label="Filter by tier"
              style={{ flex: 1, padding: "8px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 8, color: "#F5E6D0", fontSize: "0.8125rem" }}>
              <option value="all">All Tiers</option>
              <option value="curated">Curated</option>
              <option value="core">Core</option>
              <option value="visible">Visible</option>
              <option value="archive">Archive</option>
            </select>
            <select value={catalogTypeFilter} onChange={e => setCatalogTypeFilter(e.target.value)} aria-label="Filter by type"
              style={{ flex: 1, padding: "8px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 8, color: "#F5E6D0", fontSize: "0.8125rem" }}>
              <option value="all">All Types</option>
              <option value="standard">Standard</option>
              <option value="program">Program</option>
              <option value="program_release">Release</option>
              <option value="pick_base">Pick Base</option>
            </select>
          </div>

          {/* Add Expression button */}
          {canEnrich && (
            <div style={{ marginBottom: 12 }}>
              <button onClick={() => { setShowAddExpr(p => !p); setAddExprError(""); setAddExprDedup(null); }}
                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #5B9A8B", background: showAddExpr ? "rgba(91,154,139,0.15)" : "transparent", color: "#5B9A8B", fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer" }}>
                {showAddExpr ? "Cancel" : "+ Add Expression"}
              </button>
            </div>
          )}

          {/* BS-08: Add Expression form */}
          {showAddExpr && canEnrich && (
            <div style={{ background: "#1A1510", border: "1px solid #3D3428", borderTop: "2px solid #5B9A8B", borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: "0.875rem", color: "#F5E6D0", fontWeight: 600, marginBottom: 12 }}>New Expression</div>
              {/* Name with live preview */}
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Name *</label>
                <input type="text" value={addExprForm.name}
                  onChange={e => { setAddExprForm(p => ({ ...p, name: e.target.value })); setAddExprDedup(null); }}
                  onBlur={() => checkExprDedup(addExprForm.name)}
                  placeholder="Expression name" aria-label="Expression name"
                  style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                {addExprForm.name.trim() && (
                  <div style={{ fontSize: "0.6875rem", color: "#A69880", marginTop: 3 }}>
                    Will display as: <span style={{ color: "#D4A053" }}>{normalizeColaName(addExprForm.name, selectedBrand?.name || "")}</span>
                  </div>
                )}
              </div>
              {/* Dedup warning */}
              {addExprDedup && (
                <div style={{ background: "rgba(212,160,83,0.08)", border: "1px solid rgba(212,160,83,0.3)", borderRadius: 6, padding: 12, marginBottom: 12 }}>
                  <div style={{ color: "#D4A053", fontSize: "0.8125rem", marginBottom: 8 }}>A similar expression already exists: "{addExprDedup.name}"</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => { setShowAddExpr(false); setAddExprDedup(null); openEnrichPanel(addExprDedup); }}
                      style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #D4A053", background: "transparent", color: "#D4A053", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>Enrich Existing</button>
                    <button onClick={() => { setAddExprDedup(null); handleAddExpression(true); }}
                      style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #3D3428", background: "transparent", color: "#A69880", fontSize: "0.75rem", cursor: "pointer" }}>This is Different</button>
                  </div>
                </div>
              )}
              {/* Type */}
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Type</label>
                <select value={addExprForm.type} onChange={e => setAddExprForm(p => ({ ...p, type: e.target.value }))} aria-label="Expression type"
                  style={{ padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }}>
                  <option value="standard">Standard</option>
                  <option value="pick_base">Pick Base</option>
                </select>
              </div>
              {/* Fields grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px", marginBottom: 10 }}>
                <div>
                  <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Proof</label>
                  <input type="number" step="0.1" value={addExprForm.proof} onChange={e => setAddExprForm(p => ({ ...p, proof: e.target.value }))}
                    aria-label="Proof" style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Age (years)</label>
                  <input type="number" step="0.5" value={addExprForm.age} onChange={e => setAddExprForm(p => ({ ...p, age: e.target.value }))}
                    aria-label="Age" style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Finish</label>
                  <input type="text" value={addExprForm.finish} onChange={e => setAddExprForm(p => ({ ...p, finish: e.target.value }))}
                    placeholder="e.g., Port Barrel" aria-label="Finish"
                    style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Batch</label>
                  <input type="text" value={addExprForm.batch} onChange={e => setAddExprForm(p => ({ ...p, batch: e.target.value }))}
                    aria-label="Batch" style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                </div>
              </div>
              {/* Description */}
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Description</label>
                <textarea value={addExprForm.description} onChange={e => { if (e.target.value.length <= 500) setAddExprForm(p => ({ ...p, description: e.target.value })); }}
                  aria-label="Description" rows={2} style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem", resize: "vertical", fontFamily: "inherit" }} />
              </div>
              {/* Mash bill */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: "0.6875rem", color: "#A69880", marginBottom: 4, fontWeight: 600 }}>Mash Bill (%)</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[["mashCorn", "Corn"], ["mashRye", "Rye"], ["mashWheat", "Wheat"], ["mashBarley", "Barley"], ["mashOther", "Other"]].map(([k, l]) => (
                    <div key={k} style={{ flex: 1 }}>
                      <label style={{ display: "block", fontSize: "0.5625rem", color: "#A69880", marginBottom: 2, textAlign: "center" }}>{l}</label>
                      <input type="number" min="0" max="100" value={addExprForm[k]} onChange={e => setAddExprForm(p => ({ ...p, [k]: e.target.value }))}
                        aria-label={`Mash ${l} percent`} style={{ width: "100%", padding: "4px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 4, color: "#F5E6D0", fontSize: "0.75rem", textAlign: "center" }} />
                    </div>
                  ))}
                </div>
                {(() => { const t = (parseInt(addExprForm.mashCorn) || 0) + (parseInt(addExprForm.mashRye) || 0) + (parseInt(addExprForm.mashWheat) || 0) + (parseInt(addExprForm.mashBarley) || 0) + (parseInt(addExprForm.mashOther) || 0); return t > 0 ? <div style={{ fontSize: "0.625rem", color: t > 100 ? "#CD5C5C" : t === 100 ? "#5B9A8B" : "#D4A053", marginTop: 3 }}>Total: {t}%{t > 100 ? " (exceeds 100)" : t < 100 ? " (incomplete)" : ""}</div> : null; })()}
              </div>
              {/* UPC */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>UPC (optional, 12 digits)</label>
                <input type="text" value={addExprForm.upc} onChange={e => setAddExprForm(p => ({ ...p, upc: e.target.value.replace(/[^\d-\s]/g, "") }))}
                  placeholder="012345678905" aria-label="UPC code" maxLength={15}
                  style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                {(() => { const c = (addExprForm.upc || "").replace(/[\s-]/g, ""); return c.length === 12 ? <div style={{ fontSize: "0.625rem", color: validateUpcCheckDigit(c) ? "#5B9A8B" : "#CD5C5C", marginTop: 2 }}>{validateUpcCheckDigit(c) ? "Valid check digit" : "Invalid check digit"}</div> : null; })()}
              </div>
              {/* Error + actions */}
              {addExprError && <div style={{ fontSize: "0.75rem", color: "#CD5C5C", marginBottom: 8 }}>{addExprError}</div>}
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => handleAddExpression()} disabled={addExprSaving}
                  style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: addExprSaving ? "#3D3428" : "#5B9A8B", color: "#FFF", fontSize: "0.8125rem", fontWeight: 600, cursor: addExprSaving ? "default" : "pointer" }}>
                  {addExprSaving ? "Creating..." : "Create Expression"}
                </button>
                <button onClick={() => setShowAddExpr(false)}
                  style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #3D3428", background: "transparent", color: "#A69880", fontSize: "0.8125rem", cursor: "pointer" }}>Cancel</button>
              </div>
            </div>
          )}

          {!fullCatalogLoaded && archiveRequested && (
            <div style={{ textAlign: "center", padding: 12, color: "#A69880", fontSize: "0.8125rem", marginBottom: 12 }}>Loading archive expressions...</div>
          )}

          <div style={{ fontSize: "0.75rem", color: "#A69880", marginBottom: 10 }}>{catalogRows.length} expression{catalogRows.length !== 1 ? "s" : ""}</div>

          {/* Toast */}
          {enrichToast && (
            <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", background: "#2D4A3E", color: "#98D8A8", padding: "10px 20px", borderRadius: 8, fontSize: "0.875rem", fontWeight: 600, zIndex: 1200, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>{enrichToast}</div>
          )}

          {/* Table */}
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: canEnrich ? 760 : 700 }}>
              <thead>
                <tr>
                  {[
                    { key: "name", label: "Name" },
                    { key: "tier", label: "Tier" },
                    { key: "type", label: "Type" },
                    { key: "proof", label: "Proof" },
                    { key: "age", label: "Age" },
                    { key: "upc", label: "UPC" },
                    { key: "popularity", label: "Pop." },
                    { key: "checkins", label: "Check-ins" },
                    { key: "rating", label: "Avg" },
                    ...(canEnrich ? [{ key: "_enrich", label: "" }] : []),
                  ].map(col => (
                    <th key={col.key} onClick={() => col.key !== "upc" && col.key !== "_enrich" && toggleSort(col.key)}
                      style={{ ...brandDashboardTableHeaderStyle, cursor: (col.key === "upc" || col.key === "_enrich") ? "default" : "pointer" }}>
                      {col.label}{col.key !== "upc" && col.key !== "_enrich" && sortArrow(col.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {catalogRows.map(expr => {
                  const exprCheckins = brandCheckins.filter(c => c.bourbonId === expr.id);
                  const exprRatings = exprCheckins.filter(c => c.rating > 0).map(c => c.rating);
                  const avgR = exprRatings.length > 0 ? (exprRatings.reduce((a, b) => a + b, 0) / exprRatings.length).toFixed(2) : "—";
                  const hasUpc = upcSet.has(expr.id);
                  const missingData = !expr.proof || !expr.age || !hasUpc;
                  const isOpen = enrichExprId === expr.id;
                  const enrichable = canEnrich && ENRICHABLE_TYPES.has(expr.expressionType || "standard");
                  const colCount = canEnrich ? 10 : 9;
                  return (
                    <Fragment key={expr.id}>
                      <tr onClick={() => !isOpen && handleExprClick(expr)}
                        style={{ cursor: "pointer", borderLeft: missingData ? "3px solid rgba(212,160,83,0.4)" : "3px solid transparent" }}
                        onMouseEnter={e => { e.currentTarget.style.background = "rgba(91,154,139,0.08)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                        <td style={{ ...brandDashboardTableCellStyle, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{expr.name}</td>
                        <td style={brandDashboardTableCellStyle}>
                          <span style={{ fontSize: "0.6875rem", padding: "2px 6px", borderRadius: 4, background: `${brandDashboardTierColors[expr.tier] || "#A69880"}22`, color: brandDashboardTierColors[expr.tier] || "#A69880" }}>{expr.tier}</span>
                        </td>
                        <td style={brandDashboardTableCellStyle}>
                          <span style={{ fontSize: "0.6875rem", padding: "2px 6px", borderRadius: 4, background: `${brandDashboardTypeColors[expr.expressionType || "standard"] || "#F5E6D0"}15`, color: brandDashboardTypeColors[expr.expressionType || "standard"] || "#F5E6D0" }}>{expr.expressionType || "standard"}</span>
                        </td>
                        <td style={brandDashboardTableCellStyle}>{expr.proof || "—"}</td>
                        <td style={brandDashboardTableCellStyle}>{expr.age ? `${expr.age}yr` : "—"}</td>
                        <td style={{ ...brandDashboardTableCellStyle, textAlign: "center" }}>{hasUpc ? <span style={{ color: "#5B9A8B" }}>Y{sharedUpcMap.has(expr.id) && <span title="Shared barcode" style={{ marginLeft: 3, fontSize: "0.5625rem", color: "#D4A053" }}>S</span>}</span> : <span style={{ color: "#A69880" }}>N</span>}</td>
                        <td style={brandDashboardTableCellStyle}>{expr.popularityScore || 0}</td>
                        <td style={brandDashboardTableCellStyle}>{exprCheckins.length}</td>
                        <td style={{ ...brandDashboardTableCellStyle, color: avgR !== "—" ? "#D4A053" : "#A69880" }}>{avgR}</td>
                        {canEnrich && (
                          <td style={{ ...brandDashboardTableCellStyle, whiteSpace: "nowrap" }}>
                            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                              {enrichable && (
                                <button onClick={e => { e.stopPropagation(); setAddReleaseParent(null); openEnrichPanel(expr); }}
                                  style={{ padding: "4px 8px", borderRadius: 6, border: isOpen ? "1px solid #5B9A8B" : "1px solid #3D3428", background: isOpen ? "rgba(91,154,139,0.15)" : "transparent", color: isOpen ? "#5B9A8B" : "#A69880", fontSize: "0.6875rem", cursor: "pointer" }}>
                                  {isOpen ? "Close" : "Enrich"}
                                </button>
                              )}
                              {(expr.expressionType || "standard") === "program" && (
                                <button onClick={e => { e.stopPropagation(); setEnrichExprId(null); setAddReleaseParent(addReleaseParent?.id === expr.id ? null : expr); setAddReleaseForm({ batch: "", proof: "", age: "", description: "", upc: "" }); setAddReleaseError(""); }}
                                  style={{ padding: "4px 8px", borderRadius: 6, border: addReleaseParent?.id === expr.id ? "1px solid #D4A053" : "1px solid #3D3428", background: addReleaseParent?.id === expr.id ? "rgba(212,160,83,0.12)" : "transparent", color: addReleaseParent?.id === expr.id ? "#D4A053" : "#A69880", fontSize: "0.6875rem", cursor: "pointer" }}>
                                  {addReleaseParent?.id === expr.id ? "Cancel" : "+ Release"}
                                </button>
                              )}
                              {isOwner && (expr.expressionType || "standard") === "standard" && (
                                <select value="" onChange={e => { if (e.target.value) handleSetExprType(expr, e.target.value); }} onClick={e => e.stopPropagation()} aria-label="Set type"
                                  style={{ padding: "3px 4px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 4, color: "#A69880", fontSize: "0.5625rem", cursor: "pointer" }}>
                                  <option value="">Type...</option>
                                  <option value="program">Set Program</option>
                                  <option value="pick_base">Set Pick Base</option>
                                </select>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                      {/* ─── ENRICHMENT PANEL ─── */}
                      {isOpen && enrichable && (
                        <tr><td colSpan={colCount} style={{ padding: 0, border: "none" }}>
                          <div style={{ background: "#1A1510", border: "1px solid #3D3428", borderTop: "2px solid #5B9A8B", borderRadius: "0 0 8px 8px", padding: 16, margin: "0 4px 8px" }}>

                            {/* BS-07: Name section */}
                            <div style={{ marginBottom: 16, paddingBottom: 14, borderBottom: "1px solid #2A2218" }}>
                              <div style={{ fontSize: "0.75rem", color: "#A69880", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Name</div>
                              <div style={{ fontSize: "1rem", color: "#F5E6D0", fontWeight: 600, marginBottom: 4 }}>{expr.name}</div>
                              {expr.colaProductName && expr.colaProductName !== expr.name && (
                                <div style={{ fontSize: "0.75rem", color: "#A69880", marginBottom: 8 }}>COLA filing: {expr.colaProductName}</div>
                              )}
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                {expr.brandConfirmed ? (
                                  <span style={{ padding: "5px 12px", borderRadius: 6, background: "rgba(91,154,139,0.15)", color: "#5B9A8B", fontSize: "0.75rem", fontWeight: 600 }}>Brand Confirmed</span>
                                ) : (
                                  <button onClick={() => handleNameConfirm(expr)}
                                    style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #5B9A8B", background: "transparent", color: "#5B9A8B", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>
                                    Confirm Name
                                  </button>
                                )}
                                {!nameSubmitted ? (
                                  showNameCorrect ? (
                                    <div style={{ flex: 1, minWidth: 200 }}>
                                      <input type="text" value={nameCorrection} onChange={e => setNameCorrection(e.target.value)}
                                        placeholder="Preferred name" aria-label="Preferred name"
                                        style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem", marginBottom: 4 }} />
                                      {nameCorrection && (
                                        <div style={{ fontSize: "0.6875rem", color: "#A69880", marginBottom: 6 }}>
                                          Will display as: <span style={{ color: "#D4A053" }}>{normalizeColaName(nameCorrection, selectedBrand?.name || "")}</span>
                                        </div>
                                      )}
                                      <div style={{ display: "flex", gap: 6 }}>
                                        <button onClick={() => handleNameCorrectionSubmit(expr)}
                                          disabled={!nameCorrection.trim()}
                                          style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: "#5B9A8B", color: "#FFF", fontSize: "0.6875rem", fontWeight: 600, cursor: nameCorrection.trim() ? "pointer" : "default", opacity: nameCorrection.trim() ? 1 : 0.5 }}>Submit</button>
                                        <button onClick={() => { setShowNameCorrect(false); setNameCorrection(""); }}
                                          style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #3D3428", background: "transparent", color: "#A69880", fontSize: "0.6875rem", cursor: "pointer" }}>Cancel</button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button onClick={() => setShowNameCorrect(true)}
                                      style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #3D3428", background: "transparent", color: "#A69880", fontSize: "0.75rem", cursor: "pointer" }}>
                                      Request Correction
                                    </button>
                                  )
                                ) : (
                                  <span style={{ fontSize: "0.75rem", color: "#5B9A8B" }}>Correction submitted for review</span>
                                )}
                              </div>
                            </div>

                            {/* BS-05: Metadata fields */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px", marginBottom: 14 }}>
                              <div>
                                <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Proof</label>
                                <input type="number" step="0.1" value={enrichForm.proof} onChange={e => setEnrichForm(p => ({ ...p, proof: e.target.value }))}
                                  aria-label="Proof" style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                              </div>
                              <div>
                                <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Age (years)</label>
                                <input type="number" step="0.5" value={enrichForm.age} onChange={e => setEnrichForm(p => ({ ...p, age: e.target.value }))}
                                  aria-label="Age" style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                              </div>
                              <div>
                                <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Finish</label>
                                <input type="text" value={enrichForm.finish} onChange={e => setEnrichForm(p => ({ ...p, finish: e.target.value }))}
                                  placeholder="e.g., Toasted Barrel" aria-label="Finish"
                                  style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                              </div>
                              <div>
                                <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Batch</label>
                                <input type="text" value={enrichForm.batch} onChange={e => setEnrichForm(p => ({ ...p, batch: e.target.value }))}
                                  placeholder="e.g., 2024-01" aria-label="Batch"
                                  style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                              </div>
                            </div>
                            <div style={{ marginBottom: 14 }}>
                              <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Description (max 500 chars)</label>
                              <textarea value={enrichForm.description} onChange={e => { if (e.target.value.length <= 500) setEnrichForm(p => ({ ...p, description: e.target.value })); }}
                                aria-label="Description" rows={3}
                                style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem", resize: "vertical", fontFamily: "inherit" }} />
                              <div style={{ fontSize: "0.625rem", color: "#A69880", textAlign: "right" }}>{(enrichForm.description || "").length}/500</div>
                            </div>

                            {/* Mash bill */}
                            <div style={{ marginBottom: 14 }}>
                              <div style={{ fontSize: "0.6875rem", color: "#A69880", marginBottom: 6, fontWeight: 600 }}>Mash Bill (%)</div>
                              <div style={{ display: "flex", gap: 8 }}>
                                {[["mashCorn", "Corn"], ["mashRye", "Rye"], ["mashWheat", "Wheat"], ["mashBarley", "Barley"], ["mashOther", "Other"]].map(([k, l]) => (
                                  <div key={k} style={{ flex: 1 }}>
                                    <label style={{ display: "block", fontSize: "0.5625rem", color: "#A69880", marginBottom: 2, textAlign: "center" }}>{l}</label>
                                    <input type="number" min="0" max="100" value={enrichForm[k]} onChange={e => setEnrichForm(p => ({ ...p, [k]: e.target.value }))}
                                      aria-label={`Mash ${l} percent`}
                                      style={{ width: "100%", padding: "4px 4px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 4, color: "#F5E6D0", fontSize: "0.75rem", textAlign: "center" }} />
                                  </div>
                                ))}
                              </div>
                              {(() => {
                                const t = (parseInt(enrichForm.mashCorn) || 0) + (parseInt(enrichForm.mashRye) || 0) + (parseInt(enrichForm.mashWheat) || 0) + (parseInt(enrichForm.mashBarley) || 0) + (parseInt(enrichForm.mashOther) || 0);
                                return t > 0 ? <div style={{ fontSize: "0.625rem", color: t > 100 ? "#CD5C5C" : t === 100 ? "#5B9A8B" : "#D4A053", marginTop: 4 }}>Total: {t}%{t > 100 ? " (exceeds 100)" : t < 100 ? " (incomplete)" : ""}</div> : null;
                              })()}
                            </div>

                            {/* BS-06: UPC section */}
                            <div style={{ marginBottom: 14, paddingTop: 12, borderTop: "1px solid #2A2218" }}>
                              <div style={{ fontSize: "0.6875rem", color: "#A69880", fontWeight: 600, marginBottom: 6 }}>UPC</div>
                              {hasUpc ? (
                                <div style={{ fontSize: "0.8125rem", color: "#5B9A8B" }}>
                                  Has UPC <span style={{ color: "#A69880", fontSize: "0.75rem" }}>(to report incorrect UPC, use the correction flow)</span>
                                </div>
                              ) : (
                                <div>
                                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                    <input type="text" value={upcInput} onChange={e => { setUpcInput(e.target.value.replace(/[^\d-\s]/g, "")); setUpcError(""); setUpcCollision(null); }}
                                      placeholder="12-digit UPC" aria-label="UPC code" maxLength={15}
                                      style={{ flex: 1, padding: "6px 10px", background: "#1E1A14", border: `1px solid ${upcError ? "#CD5C5C" : "#3D3428"}`, borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                                    <button onClick={() => handleUpcSubmit(expr)}
                                      disabled={!upcInput.trim()}
                                      style={{ padding: "6px 12px", borderRadius: 6, border: "none", background: "#5B9A8B", color: "#FFF", fontSize: "0.75rem", fontWeight: 600, cursor: upcInput.trim() ? "pointer" : "default", opacity: upcInput.trim() ? 1 : 0.5 }}>
                                      Add UPC
                                    </button>
                                  </div>
                                  {upcInput.replace(/[\s-]/g, "").length === 12 && !upcError && (
                                    <div style={{ fontSize: "0.625rem", color: validateUpcCheckDigit(upcInput.replace(/[\s-]/g, "")) ? "#5B9A8B" : "#CD5C5C", marginTop: 3 }}>
                                      {validateUpcCheckDigit(upcInput.replace(/[\s-]/g, "")) ? "Valid check digit" : "Invalid check digit"}
                                    </div>
                                  )}
                                  {upcError && <div style={{ fontSize: "0.6875rem", color: "#CD5C5C", marginTop: 4 }}>{upcError}</div>}
                                  {upcCollision && (
                                    <div style={{ background: "rgba(212,160,83,0.08)", border: "1px solid rgba(212,160,83,0.3)", borderRadius: 6, padding: 10, marginTop: 8, fontSize: "0.75rem" }}>
                                      <div style={{ color: "#D4A053", marginBottom: 6 }}>This UPC is already linked to "{upcCollision.linkedName}". Submit anyway?</div>
                                      <div style={{ display: "flex", gap: 6 }}>
                                        <button onClick={() => handleUpcSubmit(expr, true)}
                                          style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: "#D4A053", color: "#1A1510", fontSize: "0.6875rem", fontWeight: 600, cursor: "pointer" }}>Confirm</button>
                                        <button onClick={() => setUpcCollision(null)}
                                          style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #3D3428", background: "transparent", color: "#A69880", fontSize: "0.6875rem", cursor: "pointer" }}>Cancel</button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Save button + errors */}
                            {enrichError && <div style={{ fontSize: "0.75rem", color: "#CD5C5C", marginBottom: 8 }}>{enrichError}</div>}
                            <div style={{ display: "flex", gap: 8 }}>
                              <button onClick={() => handleEnrichSave(expr)} disabled={enrichSaving}
                                style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: enrichSaving ? "#3D3428" : "#5B9A8B", color: "#FFF", fontSize: "0.8125rem", fontWeight: 600, cursor: enrichSaving ? "default" : "pointer" }}>
                                {enrichSaving ? "Saving..." : "Save Changes"}
                              </button>
                              <button onClick={() => setEnrichExprId(null)}
                                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #3D3428", background: "transparent", color: "#A69880", fontSize: "0.8125rem", cursor: "pointer" }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        </td></tr>
                      )}
                      {/* ─── ADD RELEASE PANEL (BS-09) ─── */}
                      {addReleaseParent?.id === expr.id && (expr.expressionType || "standard") === "program" && (
                        <tr><td colSpan={colCount} style={{ padding: 0, border: "none" }}>
                          <div style={{ background: "#1A1510", border: "1px solid #3D3428", borderTop: "2px solid #D4A053", borderRadius: "0 0 8px 8px", padding: 16, margin: "0 4px 8px" }}>
                            <div style={{ fontSize: "0.875rem", color: "#F5E6D0", fontWeight: 600, marginBottom: 4 }}>New Release under {expr.name}</div>
                            {addReleaseForm.batch && (
                              <div style={{ fontSize: "0.6875rem", color: "#A69880", marginBottom: 10 }}>
                                Will display as: <span style={{ color: "#D4A053" }}>{normalizeColaName(`${expr.name} ${addReleaseForm.batch}`, selectedBrand?.name || "")}</span>
                              </div>
                            )}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px", marginBottom: 10 }}>
                              <div>
                                <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Batch ID *</label>
                                <input type="text" value={addReleaseForm.batch} onChange={e => setAddReleaseForm(p => ({ ...p, batch: e.target.value }))}
                                  placeholder="e.g., C926, 2024-01" aria-label="Batch identifier"
                                  style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                              </div>
                              <div>
                                <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Proof *</label>
                                <input type="number" step="0.1" value={addReleaseForm.proof} onChange={e => setAddReleaseForm(p => ({ ...p, proof: e.target.value }))}
                                  aria-label="Release proof"
                                  style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                              </div>
                              <div>
                                <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Age (years)</label>
                                <input type="number" step="0.5" value={addReleaseForm.age} onChange={e => setAddReleaseForm(p => ({ ...p, age: e.target.value }))}
                                  aria-label="Release age"
                                  style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                              </div>
                              <div>
                                <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>UPC (optional)</label>
                                <input type="text" value={addReleaseForm.upc} onChange={e => setAddReleaseForm(p => ({ ...p, upc: e.target.value.replace(/[^\d-\s]/g, "") }))}
                                  placeholder="12-digit UPC" aria-label="Release UPC" maxLength={15}
                                  style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem" }} />
                              </div>
                            </div>
                            <div style={{ marginBottom: 12 }}>
                              <label style={{ display: "block", fontSize: "0.6875rem", color: "#A69880", marginBottom: 3 }}>Description</label>
                              <textarea value={addReleaseForm.description} onChange={e => { if (e.target.value.length <= 500) setAddReleaseForm(p => ({ ...p, description: e.target.value })); }}
                                aria-label="Release description" rows={2}
                                style={{ width: "100%", padding: "6px 10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem", resize: "vertical", fontFamily: "inherit" }} />
                            </div>
                            {addReleaseError && <div style={{ fontSize: "0.75rem", color: "#CD5C5C", marginBottom: 8 }}>{addReleaseError}</div>}
                            <div style={{ display: "flex", gap: 8 }}>
                              <button onClick={handleAddRelease} disabled={addReleaseSaving}
                                style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: addReleaseSaving ? "#3D3428" : "#D4A053", color: "#1A1510", fontSize: "0.8125rem", fontWeight: 600, cursor: addReleaseSaving ? "default" : "pointer" }}>
                                {addReleaseSaving ? "Adding..." : "Add Release"}
                              </button>
                              <button onClick={() => setAddReleaseParent(null)}
                                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #3D3428", background: "transparent", color: "#A69880", fontSize: "0.8125rem", cursor: "pointer" }}>Cancel</button>
                            </div>
                          </div>
                        </td></tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {catalogRows.length === 0 && (
            <div style={{ textAlign: "center", padding: 32, color: "#A69880", fontSize: "0.875rem" }}>
              No expressions match the current filters.
            </div>
          )}
        </div>
      )}

      {/* ─── UPCS TAB (BS-11/BS-12) ─── */}
      {dashTab === "upcs" && (() => {
        const brandUpcEntries = (upcCodes || []).filter(u => {
          const eid = u.expressionId || u.expression_id;
          return brandExpressions.some(e => e.id === eid);
        });
        const nonArchive = brandExpressions.filter(e => e.tier !== "archive");
        const withUpc = nonArchive.filter(e => upcSet.has(e.id));
        const coveragePct = nonArchive.length > 0 ? Math.round((withUpc.length / nonArchive.length) * 100) : 0;
        // Shared barcodes: UPCs linked to more than one expression
        const upcToExprs = {};
        for (const u of brandUpcEntries) {
          const upc = u.upc;
          if (!upcToExprs[upc]) upcToExprs[upc] = [];
          upcToExprs[upc].push(u.expressionId || u.expression_id);
        }
        const sharedBarcodes = Object.entries(upcToExprs).filter(([, ids]) => ids.length > 1);
        // Also include UPCs shared with expressions OUTSIDE this brand
        const allUpcToExprs = {};
        for (const u of (upcCodes || [])) {
          const upc = u.upc;
          if (!allUpcToExprs[upc]) allUpcToExprs[upc] = [];
          allUpcToExprs[upc].push(u.expressionId || u.expression_id);
        }
        const crossBrandShared = Object.entries(allUpcToExprs).filter(([upc, ids]) =>
          ids.length > 1 && ids.some(id => brandExpressions.some(e => e.id === id))
        ).filter(([upc]) => !sharedBarcodes.some(([su]) => su === upc));

        return (
          <div>
            {/* Coverage summary */}
            <div style={{ background: "#1E1A14", borderRadius: 12, padding: 16, marginBottom: 20 }}>
              <div style={{ fontSize: "0.9375rem", color: "#F5E6D0", fontWeight: 600, marginBottom: 10 }}>UPC Coverage</div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ fontSize: "1.75rem", color: "#5B9A8B", fontWeight: 700 }}>{coveragePct}%</div>
                <div style={{ fontSize: "0.8125rem", color: "#A69880" }}>
                  {withUpc.length} of {nonArchive.length} non-archive expressions have UPC{withUpc.length !== 1 ? "s" : ""}
                </div>
              </div>
              <div style={{ marginTop: 10, height: 6, background: "#2A2218", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${coveragePct}%`, height: "100%", background: "#5B9A8B", borderRadius: 3, transition: "width 0.3s" }} />
              </div>
              <div style={{ marginTop: 8, fontSize: "0.75rem", color: "#A69880" }}>
                {brandUpcEntries.length} total UPC record{brandUpcEntries.length !== 1 ? "s" : ""} · {sharedBarcodes.length + crossBrandShared.length} shared barcode{sharedBarcodes.length + crossBrandShared.length !== 1 ? "s" : ""}
              </div>
            </div>

            {/* Bulk UPC Upload (owner/manager only) */}
            {canEnrich && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: "0.8125rem", color: "#A69880", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Bulk UPC Upload</div>

                {!bulkUploadRows && !bulkUploadResult && (
                  <div style={{ background: "#1E1A14", borderRadius: 12, padding: 20 }}>
                    <div style={{ fontSize: "0.8125rem", color: "#A69880", marginBottom: 12 }}>
                      Upload a CSV with columns: <span style={{ color: "#F5E6D0", fontFamily: "monospace" }}>name</span> and <span style={{ color: "#F5E6D0", fontFamily: "monospace" }}>upc</span>. Optional: <span style={{ color: "#F5E6D0", fontFamily: "monospace" }}>proof</span>, <span style={{ color: "#F5E6D0", fontFamily: "monospace" }}>batch</span>.
                    </div>
                    <label style={{ display: "inline-block", padding: "10px 20px", borderRadius: 8, background: "rgba(91,154,139,0.15)", color: "#5B9A8B", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", border: "1px solid #5B9A8B" }}>
                      Choose CSV File
                      <input type="file" accept=".csv" aria-label="Upload UPC CSV file" style={{ display: "none" }} onChange={e => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setBulkUploadFile(file.name);
                        Papa.parse(file, {
                          header: true,
                          skipEmptyLines: true,
                          complete: (results) => {
                            const rows = results.data.map(r => {
                              const rawUpc = (r.upc || r.UPC || r.barcode || r.BARCODE || "").toString().replace(/[\s-]/g, "");
                              const upc = rawUpc.length === 11 ? "0" + rawUpc : rawUpc;
                              const name = (r.name || r.NAME || r.expression || r.EXPRESSION || "").trim();
                              const proof = r.proof || r.PROOF || "";
                              const batch = r.batch || r.BATCH || "";
                              // Match to brand expression
                              const brandName = selectedBrand?.name || "";
                              const normalized = name ? normalizeColaName(name, brandName) : "";
                              const match = normalized ? brandExpressions.find(ex => {
                                const en = normalizeColaName(ex.name || "", brandName);
                                return en === normalized || en.includes(normalized) || normalized.includes(en);
                              }) : null;
                              // Validate UPC
                              const digitOnly = /^\d{12}$/.test(upc);
                              const validCheck = digitOnly ? validateUpcCheckDigit(upc) : false;
                              // Check collision
                              const existingLink = (upcCodes || []).find(u => u.upc === upc);
                              const collision = existingLink ? (existingLink.expressionId || existingLink.expression_id) : null;
                              const alreadyLinked = match && collision === match.id;
                              return { name, upc, proof, batch, match, normalized, digitOnly, validCheck, collision, alreadyLinked, skip: false };
                            }).filter(r => r.upc || r.name);
                            setBulkUploadRows(rows);
                          },
                          error: () => { setBulkUploadRows([]); },
                        });
                        e.target.value = "";
                      }} />
                    </label>
                  </div>
                )}

                {/* Preview table */}
                {bulkUploadRows && !bulkUploadResult && (
                  <div>
                    <div style={{ fontSize: "0.875rem", color: "#F5E6D0", marginBottom: 10 }}>
                      {bulkUploadFile}: {bulkUploadRows.length} row{bulkUploadRows.length !== 1 ? "s" : ""} parsed
                    </div>
                    <div style={{ overflowX: "auto", marginBottom: 16 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem" }}>
                        <thead>
                          <tr>
                            <th style={brandDashboardTableHeaderStyle}>CSV Name</th>
                            <th style={brandDashboardTableHeaderStyle}>UPC</th>
                            <th style={brandDashboardTableHeaderStyle}>Matched Expression</th>
                            <th style={brandDashboardTableHeaderStyle}>Status</th>
                            <th style={{ ...brandDashboardTableHeaderStyle, textAlign: "center" }}>Skip</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkUploadRows.map((row, i) => {
                            let status = "", statusColor = "#A69880";
                            if (!row.digitOnly) { status = "Invalid UPC"; statusColor = "#CD5C5C"; }
                            else if (!row.validCheck) { status = "Bad check digit"; statusColor = "#CD5C5C"; }
                            else if (row.alreadyLinked) { status = "Already linked"; statusColor = "#5B9A8B"; }
                            else if (row.collision) {
                              const colExpr = bourbons.find(b => b.id === row.collision);
                              status = `Collision: ${colExpr?.name || row.collision}`; statusColor = "#D4A053";
                            }
                            else if (!row.match) { status = "No match"; statusColor = "#CD5C5C"; }
                            else { status = "Ready"; statusColor = "#5B9A8B"; }
                            const canInsert = row.digitOnly && row.validCheck && row.match && !row.alreadyLinked;
                            return (
                              <tr key={i} style={{ opacity: row.skip ? 0.4 : 1 }}>
                                <td style={{ ...brandDashboardTableCellStyle, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" }}>{row.name || "—"}</td>
                                <td style={{ ...brandDashboardTableCellStyle, fontFamily: "monospace", fontSize: "0.75rem" }}>{row.upc || "—"}</td>
                                <td style={{ ...brandDashboardTableCellStyle, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" }}>{row.match?.name || "—"}</td>
                                <td style={{ ...brandDashboardTableCellStyle, color: statusColor, fontSize: "0.75rem" }}>{status}</td>
                                <td style={{ ...brandDashboardTableCellStyle, textAlign: "center" }}>
                                  {canInsert && (
                                    <input type="checkbox" checked={row.skip} aria-label={`Skip ${row.name}`}
                                      onChange={() => setBulkUploadRows(prev => prev.map((r, j) => j === i ? { ...r, skip: !r.skip } : r))} />
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {(() => {
                      const insertable = bulkUploadRows.filter(r => r.digitOnly && r.validCheck && r.match && !r.alreadyLinked && !r.skip);
                      const invalid = bulkUploadRows.filter(r => !r.digitOnly || !r.validCheck);
                      const unmatched = bulkUploadRows.filter(r => r.digitOnly && r.validCheck && !r.match);
                      const collisions = bulkUploadRows.filter(r => r.digitOnly && r.validCheck && r.match && !r.alreadyLinked && r.collision && !r.skip);
                      return (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                          <span style={{ fontSize: "0.75rem", padding: "4px 10px", borderRadius: 12, background: "rgba(91,154,139,0.12)", color: "#5B9A8B" }}>{insertable.length} ready</span>
                          {invalid.length > 0 && <span style={{ fontSize: "0.75rem", padding: "4px 10px", borderRadius: 12, background: "rgba(205,92,92,0.12)", color: "#CD5C5C" }}>{invalid.length} invalid</span>}
                          {unmatched.length > 0 && <span style={{ fontSize: "0.75rem", padding: "4px 10px", borderRadius: 12, background: "rgba(205,92,92,0.12)", color: "#CD5C5C" }}>{unmatched.length} unmatched</span>}
                          {collisions.length > 0 && <span style={{ fontSize: "0.75rem", padding: "4px 10px", borderRadius: 12, background: "rgba(212,160,83,0.12)", color: "#D4A053" }}>{collisions.length} collision{collisions.length !== 1 ? "s" : ""}</span>}
                        </div>
                      );
                    })()}
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={async () => {
                        const insertable = bulkUploadRows.filter(r => r.digitOnly && r.validCheck && r.match && !r.alreadyLinked && !r.skip);
                        if (insertable.length === 0) return;
                        setBulkUploading(true);
                        const sb = getSupabase();
                        if (!sb) { setBulkUploading(false); return; }
                        let inserted = 0, errors = 0;
                        // Batch in chunks of 50
                        for (let i = 0; i < insertable.length; i += 50) {
                          const chunk = insertable.slice(i, i + 50);
                          const rows = chunk.map(r => ({ expression_id: r.match.id, upc: r.upc, source: "brand", verified: true }));
                          const { error } = await sb.from("upc_codes").insert(rows);
                          if (error) errors += chunk.length; else inserted += chunk.length;
                        }
                        // Audit log
                        await sb.from("audit_log").insert({
                          actor: currentUser?.uid, action: "brand_bulk_upc", target_type: "brand", target_id: selectedBrandId,
                          detail: JSON.stringify({ file: bulkUploadFile, total: bulkUploadRows.length, inserted, errors, source: "brand" }), category: "upc",
                        });
                        // Update local upcCodes
                        const newEntries = insertable.filter((_, idx) => idx < inserted).map(r => ({ expressionId: r.match.id, upc: r.upc, source: "brand", verified: true }));
                        if (newEntries.length > 0) setUpcCodes(prev => [...prev, ...newEntries]);
                        setBulkUploading(false);
                        setBulkUploadResult({ inserted, errors, total: insertable.length });
                      }} disabled={bulkUploading || bulkUploadRows.filter(r => r.digitOnly && r.validCheck && r.match && !r.alreadyLinked && !r.skip).length === 0}
                        style={{ padding: "10px 20px", borderRadius: 8, background: bulkUploading ? "#3D3428" : "#5B9A8B", color: "#FFF", border: "none", fontSize: "0.875rem", fontWeight: 600, cursor: bulkUploading ? "wait" : "pointer", opacity: bulkUploadRows.filter(r => r.digitOnly && r.validCheck && r.match && !r.alreadyLinked && !r.skip).length === 0 ? 0.4 : 1 }}>
                        {bulkUploading ? "Uploading..." : "Insert Ready UPCs"}
                      </button>
                      <button onClick={() => { setBulkUploadRows(null); setBulkUploadFile(""); }}
                        style={{ padding: "10px 20px", borderRadius: 8, background: "transparent", color: "#A69880", border: "1px solid #3D3428", fontSize: "0.875rem", cursor: "pointer" }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Upload result */}
                {bulkUploadResult && (
                  <div style={{ background: "#1E1A14", borderRadius: 12, padding: 20 }}>
                    <div style={{ fontSize: "0.9375rem", color: "#5B9A8B", fontWeight: 600, marginBottom: 8 }}>Upload Complete</div>
                    <div style={{ fontSize: "0.8125rem", color: "#F5E6D0", marginBottom: 4 }}>{bulkUploadResult.inserted} UPC{bulkUploadResult.inserted !== 1 ? "s" : ""} inserted successfully</div>
                    {bulkUploadResult.errors > 0 && <div style={{ fontSize: "0.8125rem", color: "#CD5C5C" }}>{bulkUploadResult.errors} failed</div>}
                    <button onClick={() => { setBulkUploadResult(null); setBulkUploadRows(null); setBulkUploadFile(""); }}
                      style={{ marginTop: 12, padding: "8px 16px", borderRadius: 8, background: "rgba(91,154,139,0.15)", color: "#5B9A8B", border: "1px solid #5B9A8B", fontSize: "0.8125rem", cursor: "pointer" }}>
                      Upload Another
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Shared Barcodes (BS-12) */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: "0.8125rem", color: "#A69880", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Shared Barcodes</div>

              {sharedBarcodes.length === 0 && crossBrandShared.length === 0 && (
                <div style={{ background: "#1E1A14", borderRadius: 12, padding: 20, textAlign: "center", color: "#A69880", fontSize: "0.8125rem" }}>
                  No shared barcodes detected for this brand.
                </div>
              )}

              {(sharedBarcodes.length > 0 || crossBrandShared.length > 0) && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[...sharedBarcodes, ...crossBrandShared].map(([upc, exprIds]) => {
                    const linkedExprs = [...new Set(exprIds)].map(id => bourbons.find(b => b.id === id)).filter(Boolean);
                    const defaultEntry = (upcCodes || []).find(u => u.upc === upc && u.isDefault);
                    return (
                      <div key={upc} style={{ background: "#1E1A14", borderRadius: 10, padding: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <span style={{ fontFamily: "monospace", fontSize: "0.8125rem", color: "#D4A053" }}>{upc}</span>
                          <span style={{ fontSize: "0.6875rem", color: "#A69880" }}>{linkedExprs.length} expression{linkedExprs.length !== 1 ? "s" : ""}</span>
                        </div>
                        {linkedExprs.map(ex => {
                          const isBrandExpr = brandExpressions.some(e => e.id === ex.id);
                          const isDefault = defaultEntry && (defaultEntry.expressionId || defaultEntry.expression_id) === ex.id;
                          return (
                            <div key={ex.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 8px", borderRadius: 6, background: isBrandExpr ? "rgba(91,154,139,0.06)" : "rgba(166,152,128,0.06)", marginBottom: 4 }}>
                              <div>
                                <span style={{ fontSize: "0.8125rem", color: isBrandExpr ? "#F5E6D0" : "#A69880" }}>{ex.name}</span>
                                {!isBrandExpr && <span style={{ fontSize: "0.625rem", color: "#A69880", marginLeft: 6 }}>(other brand)</span>}
                                {isDefault && <span style={{ fontSize: "0.625rem", color: "#5B9A8B", marginLeft: 6 }}>default</span>}
                              </div>
                              {canEnrich && isBrandExpr && !isDefault && (
                                <button onClick={async () => {
                                  const sb = getSupabase();
                                  if (!sb) return;
                                  // Clear existing defaults for this UPC
                                  await sb.from("upc_codes").update({ is_default: false }).eq("upc", upc);
                                  // Set this one as default
                                  await sb.from("upc_codes").update({ is_default: true }).eq("upc", upc).eq("expression_id", ex.id);
                                  await sb.from("audit_log").insert({
                                    actor: currentUser?.uid, action: "brand_gtin_default", target_type: "expression", target_id: ex.id,
                                    detail: JSON.stringify({ upc, expressionId: ex.id, source: "brand" }), category: "upc",
                                  });
                                  setUpcCodes(prev => prev.map(u => u.upc === upc ? { ...u, isDefault: (u.expressionId || u.expression_id) === ex.id } : u));
                                  setEnrichToast("Default set for " + upc);
                                  setTimeout(() => setEnrichToast(""), 3000);
                                }} style={{ padding: "3px 8px", borderRadius: 4, background: "rgba(91,154,139,0.12)", color: "#5B9A8B", border: "none", fontSize: "0.625rem", cursor: "pointer" }}>
                                  Set Default
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Link Shared Barcode (owner/manager) */}
              {canEnrich && (
                <div style={{ marginTop: 16 }}>
                  {!collisionDeclare && (
                    <button onClick={() => setCollisionDeclare({ upc: "", selectedIds: [], step: "upc" })}
                      style={{ padding: "10px 16px", borderRadius: 8, background: "rgba(212,160,83,0.12)", color: "#D4A053", border: "1px solid rgba(212,160,83,0.3)", fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer" }}>
                      + Link Shared Barcode
                    </button>
                  )}

                  {collisionDeclare && collisionDeclare.step === "upc" && (
                    <div style={{ background: "#1E1A14", borderRadius: 12, padding: 16, marginTop: 8 }}>
                      <div style={{ fontSize: "0.8125rem", color: "#F5E6D0", fontWeight: 600, marginBottom: 10 }}>Declare Shared Barcode</div>
                      <div style={{ fontSize: "0.75rem", color: "#A69880", marginBottom: 10 }}>Enter a UPC that is shared across multiple expressions (e.g., repackaging, label changes).</div>
                      <input type="text" aria-label="Shared barcode UPC" placeholder="12-digit UPC" maxLength={12}
                        value={collisionDeclare.upc} onChange={e => setCollisionDeclare(p => ({ ...p, upc: e.target.value.replace(/\D/g, "") }))}
                        style={{ width: "100%", padding: "10px 12px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, color: "#F5E6D0", fontSize: "0.875rem", fontFamily: "monospace", boxSizing: "border-box", marginBottom: 10 }} />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => {
                          const upc = collisionDeclare.upc;
                          if (!/^\d{12}$/.test(upc)) return;
                          if (!validateUpcCheckDigit(upc)) { setEnrichError("Invalid check digit"); setTimeout(() => setEnrichError(""), 3000); return; }
                          setCollisionDeclare(p => ({ ...p, step: "select" }));
                        }} disabled={collisionDeclare.upc.length !== 12}
                          style={{ padding: "8px 16px", borderRadius: 8, background: collisionDeclare.upc.length === 12 ? "#5B9A8B" : "#3D3428", color: "#FFF", border: "none", fontSize: "0.8125rem", fontWeight: 600, cursor: collisionDeclare.upc.length === 12 ? "pointer" : "default" }}>
                          Next
                        </button>
                        <button onClick={() => setCollisionDeclare(null)}
                          style={{ padding: "8px 16px", borderRadius: 8, background: "transparent", color: "#A69880", border: "1px solid #3D3428", fontSize: "0.8125rem", cursor: "pointer" }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {collisionDeclare && collisionDeclare.step === "select" && (
                    <div style={{ background: "#1E1A14", borderRadius: 12, padding: 16, marginTop: 8 }}>
                      <div style={{ fontSize: "0.8125rem", color: "#F5E6D0", fontWeight: 600, marginBottom: 4 }}>Select Expressions for UPC {collisionDeclare.upc}</div>
                      <div style={{ fontSize: "0.75rem", color: "#A69880", marginBottom: 12 }}>Choose 2+ expressions that share this barcode. The first selected will be the default.</div>
                      <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: 12 }}>
                        {brandExpressions.filter(e => e.tier !== "archive").map(ex => {
                          const isSelected = collisionDeclare.selectedIds.includes(ex.id);
                          return (
                            <div key={ex.id} onClick={() => setCollisionDeclare(p => ({
                              ...p, selectedIds: isSelected ? p.selectedIds.filter(x => x !== ex.id) : [...p.selectedIds, ex.id],
                            }))} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 6, cursor: "pointer", background: isSelected ? "rgba(91,154,139,0.1)" : "transparent", borderBottom: "1px solid rgba(61,52,40,0.3)" }}>
                              <div style={{ width: 18, height: 18, borderRadius: 4, border: isSelected ? "2px solid #5B9A8B" : "2px solid #3D3428", background: isSelected ? "#5B9A8B" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                {isSelected && <span style={{ color: "#FFF", fontSize: "0.625rem", fontWeight: 700 }}>✓</span>}
                              </div>
                              <div>
                                <div style={{ fontSize: "0.8125rem", color: "#F5E6D0" }}>{ex.name}</div>
                                <div style={{ fontSize: "0.6875rem", color: "#A69880" }}>{ex.proof ? `${ex.proof}°` : ""} {ex.expressionType || "standard"}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={async () => {
                          const ids = collisionDeclare.selectedIds;
                          if (ids.length < 2) return;
                          const upc = collisionDeclare.upc;
                          const sb = getSupabase();
                          if (!sb) return;
                          const newEntries = [];
                          for (let i = 0; i < ids.length; i++) {
                            const existing = (upcCodes || []).find(u => u.upc === upc && (u.expressionId || u.expression_id) === ids[i]);
                            if (!existing) {
                              const { error } = await sb.from("upc_codes").insert({ expression_id: ids[i], upc, source: "brand", verified: true, is_default: i === 0 });
                              if (!error) newEntries.push({ expressionId: ids[i], upc, source: "brand", verified: true, isDefault: i === 0 });
                            } else if (i === 0 && !existing.isDefault) {
                              await sb.from("upc_codes").update({ is_default: true }).eq("upc", upc).eq("expression_id", ids[i]);
                            }
                          }
                          await sb.from("audit_log").insert({
                            actor: currentUser?.uid, action: "brand_gtin_collision", target_type: "brand", target_id: selectedBrandId,
                            detail: JSON.stringify({ upc, expressionIds: ids, defaultId: ids[0], source: "brand" }), category: "upc",
                          });
                          if (newEntries.length > 0) setUpcCodes(prev => [...prev, ...newEntries]);
                          setCollisionDeclare(null);
                          setEnrichToast("Shared barcode linked");
                          setTimeout(() => setEnrichToast(""), 3000);
                        }} disabled={collisionDeclare.selectedIds.length < 2}
                          style={{ padding: "8px 16px", borderRadius: 8, background: collisionDeclare.selectedIds.length >= 2 ? "#5B9A8B" : "#3D3428", color: "#FFF", border: "none", fontSize: "0.8125rem", fontWeight: 600, cursor: collisionDeclare.selectedIds.length >= 2 ? "pointer" : "default" }}>
                          Link {collisionDeclare.selectedIds.length} Expression{collisionDeclare.selectedIds.length !== 1 ? "s" : ""}
                        </button>
                        <button onClick={() => setCollisionDeclare(p => ({ ...p, step: "upc" }))}
                          style={{ padding: "8px 16px", borderRadius: 8, background: "transparent", color: "#A69880", border: "1px solid #3D3428", fontSize: "0.8125rem", cursor: "pointer" }}>
                          Back
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Expression UPC list */}
            <div>
              <div style={{ fontSize: "0.8125rem", color: "#A69880", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Expression UPC Records</div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={brandDashboardTableHeaderStyle}>Expression</th>
                      <th style={brandDashboardTableHeaderStyle}>UPC</th>
                      <th style={brandDashboardTableHeaderStyle}>Source</th>
                      <th style={brandDashboardTableHeaderStyle}>Verified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {brandUpcEntries.length === 0 && (
                      <tr><td colSpan={4} style={{ ...brandDashboardTableCellStyle, textAlign: "center", color: "#A69880" }}>No UPC records for this brand.</td></tr>
                    )}
                    {brandUpcEntries.map((u, i) => {
                      const expr = bourbons.find(b => b.id === (u.expressionId || u.expression_id));
                      return (
                        <tr key={i}>
                          <td style={{ ...brandDashboardTableCellStyle, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{expr?.name || (u.expressionId || u.expression_id)}</td>
                          <td style={{ ...brandDashboardTableCellStyle, fontFamily: "monospace", fontSize: "0.75rem" }}>{u.upc}</td>
                          <td style={{ ...brandDashboardTableCellStyle, fontSize: "0.75rem" }}>{u.source || "—"}</td>
                          <td style={{ ...brandDashboardTableCellStyle, color: u.verified ? "#5B9A8B" : "#A69880" }}>{u.verified ? "Yes" : "No"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ─── RETAIL TAB ─── */}
      {dashTab === "retail" && (
        <div>
          {retailLoading && (
            <div style={{ textAlign: "center", padding: 40, color: "#A69880", fontSize: "0.875rem" }}>Loading retail data...</div>
          )}
          {retailData && !retailLoading && (
            <div>
              {/* Summary */}
              <div style={{ background: "#1E1A14", borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: "0.9375rem", color: "#F5E6D0", fontWeight: 600, marginBottom: 10 }}>
                  {retailRows.length} of {brandExpressions.filter(b => b.tier !== "archive").length} expressions found in at least 1 state catalog
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[["PA", retailStateCounts.PA], ["IA", retailStateCounts.IA], ["VA", retailStateCounts.VA], ["NC", retailStateCounts.NC]].map(([st, cnt]) => (
                    <span key={st} style={{ padding: "4px 10px", borderRadius: 12, background: cnt > 0 ? "rgba(91,154,139,0.15)" : "rgba(166,152,128,0.1)", color: cnt > 0 ? "#5B9A8B" : "#A69880", fontSize: "0.75rem", fontWeight: 600 }}>
                      {st}: {cnt}
                    </span>
                  ))}
                </div>
              </div>

              {/* Sort toggle */}
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                {[["name", "By Name"], ["states", "By Coverage"]].map(([k, l]) => (
                  <button key={k} onClick={() => setRetailSort(k)}
                    style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid " + (retailSort === k ? "#5B9A8B" : "#3D3428"), background: retailSort === k ? "rgba(91,154,139,0.12)" : "transparent", color: retailSort === k ? "#5B9A8B" : "#A69880", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>
                    {l}
                  </button>
                ))}
              </div>

              {/* Retail table */}
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 500 }}>
                  <thead>
                    <tr>
                      <th style={brandDashboardTableHeaderStyle}>Expression</th>
                      {["PA", "IA", "VA", "NC"].map(st => <th key={st} style={{ ...brandDashboardTableHeaderStyle, textAlign: "center", width: 40 }}>{st}</th>)}
                      <th style={{ ...brandDashboardTableHeaderStyle, textAlign: "right" }}>Best Price</th>
                      <th style={{ ...brandDashboardTableHeaderStyle, textAlign: "right" }}>Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {retailRows.map(row => (
                      <tr key={row.id} onClick={() => { const expr = brandExpressions.find(b => b.id === row.id); if (expr) handleExprClick(expr); }}
                        style={{ cursor: "pointer" }}
                        onMouseEnter={e => { e.currentTarget.style.background = "rgba(91,154,139,0.08)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                        <td style={{ ...brandDashboardTableCellStyle, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{row.name}</td>
                        {["PA", "IA", "VA", "NC"].map(st => (
                          <td key={st} style={{ ...brandDashboardTableCellStyle, textAlign: "center" }}>
                            {row.states.has(st) ? <span style={{ color: "#5B9A8B" }}>Y</span> : <span style={{ color: "#3D3428" }}>—</span>}
                          </td>
                        ))}
                        <td style={{ ...brandDashboardTableCellStyle, textAlign: "right" }}>{row.bestPrice ? `$${row.bestPrice.toFixed(2)}` : "—"}</td>
                        <td style={{ ...brandDashboardTableCellStyle, textAlign: "right", color: row.confidence >= 0.8 ? "#5B9A8B" : row.confidence >= 0.5 ? "#D4A053" : "#A69880" }}>
                          {row.confidence ? `${(row.confidence * 100).toFixed(0)}%` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {retailRows.length === 0 && (
                <div style={{ textAlign: "center", padding: 32, color: "#A69880", fontSize: "0.875rem" }}>No retail catalog matches found for this brand.</div>
              )}

              {/* Unmatched section */}
              {retailUnmatched.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <button onClick={() => setShowUnmatched(p => !p)}
                    style={{ background: "none", border: "none", color: "#A69880", fontSize: "0.8125rem", cursor: "pointer", padding: "8px 0", fontWeight: 600 }}>
                    {showUnmatched ? "▾" : "▸"} {retailUnmatched.length} expression{retailUnmatched.length !== 1 ? "s" : ""} not in any state catalog
                  </button>
                  {showUnmatched && (
                    <div style={{ marginTop: 8 }}>
                      {retailUnmatched.map(expr => (
                        <div key={expr.id} onClick={() => handleExprClick(expr)}
                          style={{ padding: "8px 12px", borderLeft: "3px solid rgba(212,160,83,0.4)", marginBottom: 4, borderRadius: 4, cursor: "pointer", fontSize: "0.8125rem", color: "#F5E6D0" }}
                          onMouseEnter={e => { e.currentTarget.style.background = "rgba(91,154,139,0.06)"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                          {expr.name} <span style={{ color: "#A69880", fontSize: "0.75rem" }}>({expr.tier})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── COMMUNITY TAB ─── */}
      {dashTab === "community" && (
        <div>
          {/* Rating distribution */}
          {(() => {
            const maxCount = Math.max(...ratingDistribution.map(b => b.count), 1);
            const totalRatings = ratingDistribution.reduce((s, b) => s + b.count, 0);
            return (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: "0.8125rem", color: "#A69880", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Rating Distribution ({totalRatings} total)</div>
                {ratingDistribution.filter(b => b.count > 0 || parseFloat(b.label) >= 1).map(bucket => (
                  <div key={bucket.label} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: "0.6875rem", color: "#A69880", minWidth: 24, textAlign: "right" }}>{bucket.label}</span>
                    <div style={{ flex: 1, height: 16, background: "#1E1A14", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ width: `${(bucket.count / maxCount) * 100}%`, height: "100%", background: parseFloat(bucket.label) >= 4 ? "#5B9A8B" : parseFloat(bucket.label) >= 3 ? "#D4A053" : "#A69880", borderRadius: 4, transition: "width 0.3s" }} />
                    </div>
                    <span style={{ fontSize: "0.6875rem", color: "#F5E6D0", minWidth: 24, textAlign: "right" }}>{bucket.count || ""}</span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Top tasting notes */}
          {topTastingNotes.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: "0.8125rem", color: "#A69880", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Top Tasting Notes</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {topTastingNotes.map(({ note, count }) => (
                  <span key={note} style={{ padding: "4px 10px", borderRadius: 12, background: "rgba(91,154,139,0.12)", color: "#5B9A8B", fontSize: "0.75rem", fontWeight: 500 }}>
                    {note} <span style={{ color: "#A69880", fontWeight: 400 }}>({count})</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Store picks summary */}
          {brandPickBaseIds.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: "0.8125rem", color: "#A69880", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Store Picks</div>
              {storePicksLoading && <div style={{ color: "#A69880", fontSize: "0.8125rem" }}>Loading store picks...</div>}
              {storePicksSummary && (
                <div>
                  <div style={{ fontSize: "0.9375rem", color: "#F5E6D0", fontWeight: 600, marginBottom: 10 }}>{storePicksSummary.total} store pick{storePicksSummary.total !== 1 ? "s" : ""} recorded</div>
                  {storePicksSummary.topStores.length > 0 && (
                    <div>
                      <div style={{ fontSize: "0.75rem", color: "#A69880", marginBottom: 6 }}>Top stores:</div>
                      {storePicksSummary.topStores.map(({ name, count }) => (
                        <div key={name} style={{ display: "flex", justifyContent: "space-between", padding: "6px 12px", fontSize: "0.8125rem", color: "#F5E6D0" }}>
                          <span>{name}</span>
                          <span style={{ color: "#5B9A8B", fontWeight: 600 }}>{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!storePicksLoading && !storePicksSummary && <div style={{ color: "#A69880", fontSize: "0.8125rem" }}>No store picks recorded yet.</div>}
            </div>
          )}

          {/* Activity timeline */}
          {(() => {
            const maxMonth = Math.max(...activityTimeline.map(m => m.count), 1);
            return (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: "0.8125rem", color: "#A69880", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Activity (12 Months)</div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 120, padding: "0 4px" }}>
                  {activityTimeline.map(m => (
                    <div key={m.key} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      {m.count > 0 && <span style={{ fontSize: "0.5625rem", color: "#F5E6D0" }}>{m.count}</span>}
                      <div style={{ width: "100%", height: `${Math.max((m.count / maxMonth) * 90, m.count > 0 ? 6 : 2)}px`, background: m.count > 0 ? "#5B9A8B" : "#2A2218", borderRadius: 3, transition: "height 0.3s" }} />
                      <span style={{ fontSize: "0.5625rem", color: "#A69880" }}>{m.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {brandCheckins.length === 0 && topTastingNotes.length === 0 && (
            <div style={{ textAlign: "center", padding: 32, color: "#A69880", fontSize: "0.875rem" }}>No community engagement data yet for this brand.</div>
          )}
        </div>
      )}
    </div>
  );
});


// ─── ADMIN PANEL (extracted Phase 2) ────────────────────────
const AdminPanel = memo(function AdminPanel({
  bourbons, brands, facilities, currentUser, platformUsers, communityUsers,
  checkins, upcCodes, upcMap, fullCatalogLoaded,
  businessClaims, businessRoles, auditLog, flaggedContent, expressionEdits,
  setBourbons, setBrands, setBusinessClaims, setBusinessRoles,
  setAuditLog, setFlaggedContent, setExpressionEdits,
  setPlatformUsers, setFullCatalogLoaded, setUpcCodes,
}) {
  const { getBourbon, getBrand, getFacility, getCheckins, getSubmissionStatus, isBrowsableBrand, isBrowsableBourbon, db, showToast, appendAudit, sanitizeInput, normalizeColaName, useSupabase, getSupabase, generateId } = useAdmin();

  // ─── ADMIN UI STATE ─────────────────────────

  const [adminTab, setAdminTab] = useState("overview");

  const [adminSearch, setAdminSearch] = useState("");

  const [adminAuditRange, setAdminAuditRange] = useState("all");

  const [adminClaimFilter, setAdminClaimFilter] = useState("all");

  const [adminAuditFilter, setAdminAuditFilter] = useState("all");

  const [adminUpcForm, setAdminUpcForm] = useState({ upc: "", name: "", distillery: "" });

  const [adminFlagFilter, setAdminFlagFilter] = useState("pending");

  const [adminModSection, setAdminModSection] = useState("flags");

  const [adminCatalogType, setAdminCatalogType] = useState("expressions");

  const [adminDetailModal, setAdminDetailModal] = useState(null); // { type: 'bourbon'|'brand'|'user'|'claim'|'flag', data: {...} }
  const [enrichingSubmission, setEnrichingSubmission] = useState(null); // item.id of submission being enriched
  const [enrichData, setEnrichData] = useState({}); // { name, brandId, type, proof, age, mashBill, msrp, finishType }
  const [skippedDupeIds, setSkippedDupeIds] = useState(new Set());
  const dupeCheckRef = useRef({ dupes: null, checked: null });
  const [enrichNameLocked, setEnrichNameLocked] = useState(false);
  const [reviewingEditId, setReviewingEditId] = useState(null);
  const [syncModal, setSyncModal] = useState(null); // "plcb" | "iowa" | "virginia" | "nc" | null
  const [syncLive, setSyncLive] = useState(null); // { total, brandMatched, exprMatched, confBreakdown }

  const adminDuplicates = useMemo(() => {
    if (currentUser?.role !== "admin") return [];
    const dupes = db.findDuplicates(bourbons);
    return skippedDupeIds.size > 0 ? dupes.filter(p => !skippedDupeIds.has(p.loser.id)) : dupes;
  }, [bourbons, currentUser?.role, skippedDupeIds]);

  // Archive lazy-load (converted from render-time anti-pattern to useEffect)
  useEffect(() => {
    if (!fullCatalogLoaded && useSupabase()) {
      setFullCatalogLoaded(true);
      db.loadBourbons(true).then(all => {
        if (all) setBourbons(prev => {
          const existing = new Set(prev.map(b => b.id));
          const newItems = all.filter(b => !existing.has(b.id));
          return newItems.length > 0 ? [...prev, ...newItems] : prev;
        });
      });
    }
  }, [fullCatalogLoaded]);

  if (currentUser?.role !== "admin") return <div style={{ textAlign: "center", padding: 40, color: "#CD5C5C" }}>Access Denied</div>;


  const aStatusColor = (s) => ({ active: "#4CAF50", suspended: "#CD5C5C", pending: "#D4A053" }[s] || "#A69880");

  const claimStatusColor = (s) => ({ approved: "#4CAF50", rejected: "#CD5C5C", submitted: "#D4A053", draft: "#A69880", disputed: "#E88B5A" }[s] || "#A69880");

  const auditCatColor = (c) => ({ auth: "#6B9BD2", claim: "#D4A053", role: "#9B7ED8", moderation: "#CD5C5C", business: "#4CAF50", catalog: "#6B9BD2", submission: "#FFC107" }[c] || "#B5A898");

  // ── Computed admin data ──

  // Single-pass admin stats over bourbons
  const pendingExprs = [], orphanedBourbons = [], noProof = [], noType = [], noImage = [];
  for (const b of bourbons) {
    if (getSubmissionStatus(b) === "pending") pendingExprs.push(b);
    if (!b.brandId) orphanedBourbons.push(b);
    if (!b.proof) noProof.push(b);
    if (!b.type) noType.push(b);
    if (!b.imageUrl) noImage.push(b);
  }

  const pendingBrands = brands.filter(d => getSubmissionStatus(d) === "pending");

  const pendingClaims = businessClaims.filter(c => c.status === "submitted" || c.status === "disputed");

  const pendingFlags = flaggedContent.filter(f => f.status === "pending");

  const dataQualityCount = orphanedBourbons.length + noProof.length + noType.length;

  const submitter = (uid) => { const u = communityUsers.find(x => x.id === uid) || platformUsers.find(x => x.uid === uid); return u?.name || "Unknown"; };

  const reviewer = (uid) => { if (!uid) return "—"; const u = communityUsers.find(x => x.id === uid) || platformUsers.find(x => x.uid === uid); return u?.name || "Admin"; };

  // ── Admin field helper ──

  const admFld = (label, key, editObj, setEdit, opts = {}) => (
    <div style={mb8Style}>
      <label style={{ display: "block", fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3, fontWeight: 600 }}>{label}</label>
      {opts.select ? (
        <select value={editObj[key] || opts.defaultVal || ""} onChange={e => setEdit(p => ({ ...p, [key]: e.target.value }))} aria-label={opts.label || key}
          style={{ width: "100%", padding: "8px 10px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem", boxSizing: "border-box" }}>
          {opts.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : opts.textarea ? (
        <textarea aria-label={key} value={editObj[key] || ""} onChange={e => setEdit(p => ({ ...p, [key]: e.target.value }))} rows={3}
          style={{ width: "100%", padding: "8px 10px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }} maxLength={opts.max || 1000} />
      ) : opts.readonly ? (
        <div style={{ padding: "8px 10px", background: "#0D0A07", border: "1px solid #1E1A14", borderRadius: 6, color: "#A69880", fontSize: "0.8125rem", fontFamily: opts.mono ? "monospace" : "inherit" }}>{editObj[key] || "—"}</div>
      ) : (
        <input aria-label={key} value={editObj[key] || ""} onChange={e => setEdit(p => ({ ...p, [key]: e.target.value }))}
          type={opts.type || "text"} style={{ width: "100%", padding: "8px 10px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 6, color: "#F5E6D0", fontSize: "0.8125rem", boxSizing: "border-box", fontFamily: opts.mono ? "monospace" : "inherit" }} maxLength={opts.max || 200} />
      )}
    </div>
  );

  // ── Save handlers ──

  const saveBourbonDetail = async () => {

    const ei = adminDetailModal?.data;
    if (!ei) return;

    const orig = bourbons.find(b => b.id === ei.id);
    if (!orig) return;

    const fields = {};

    const changes = [];

    const check = (jsKey, dbKey, transform) => {

      const newVal = transform ? transform(ei[jsKey]) : ei[jsKey];

      const origVal = transform ? transform(orig[jsKey]) : (orig[jsKey] || "");
      if (String(newVal || "") !== String(origVal || "")) { fields[dbKey] = newVal || null; changes.push(dbKey); }
    };
    check("name", "name", v => sanitizeInput(v || "", 100));
    check("proof", "proof", v => parseFloat(v) || null);
    check("age", "age", v => sanitizeInput(v || "", 20));
    check("type", "type", v => sanitizeInput(v || "", 100));
    check("mashBill", "mash_bill", v => sanitizeInput(v || "", 200));
    check("msrp", "msrp", v => parseFloat(v) || null);
    check("finishType", "finish_type", v => sanitizeInput(v || "", 100));
    check("notes", "notes", v => sanitizeInput(v || "", 1000));
    check("website", "website", v => sanitizeInput(v || "", 500));
    check("brandId", "brand_id");
    check("facilityId", "facility_id");
    if (fields.facility_id !== undefined) fields.facility_source = "admin_override";
    check("origin", "origin", v => sanitizeInput(v || "", 100));
    check("batch", "batch", v => sanitizeInput(v || "", 100));
    if (ei.limitedRelease !== orig.limitedRelease) { fields.limited_release = !!ei.limitedRelease; changes.push("limited_release"); }
    if (ei.discontinued !== orig.discontinued) { fields.discontinued = !!ei.discontinued; changes.push("discontinued"); }
    if (changes.length === 0) { setAdminDetailModal(null); return; }
    if (useSupabase()) {

      const ok = await db.updateCatalogItem("bourbons", ei.id, fields);
      if (!ok) { showToast("\u26A0\uFE0F Save failed"); return; }

    }

    setBourbons(prev => prev.map(b => b.id === ei.id ? { ...b, ...ei, proof: fields.proof !== undefined ? fields.proof : b.proof, msrp: fields.msrp !== undefined ? fields.msrp : b.msrp } : b));
    appendAudit(currentUser.uid, currentUser.name, "catalog_edited", "catalog", "Edited bourbon " + ei.name + " [" + changes.join(", ") + "]", ei.id);
    showToast("\u2705 " + ei.name + " updated");
    setAdminDetailModal(null);
  };

  const saveBrandDetail = async () => {

    const ei = adminDetailModal?.data;
    if (!ei) return;

    const orig = brands.find(d => d.id === ei.id);
    if (!orig) return;

    const fields = {};

    const changes = [];

    const check = (jsKey, dbKey, transform) => {

      const newVal = transform ? transform(ei[jsKey]) : ei[jsKey];

      const origVal = transform ? transform(orig[jsKey]) : (orig[jsKey] || "");
      if (String(newVal || "") !== String(origVal || "")) { fields[dbKey] = newVal || null; changes.push(dbKey); }
    };
    check("name", "name", v => sanitizeInput(v || "", 100));
    check("location", "location", v => sanitizeInput(v || "", 200));
    check("description", "description", v => sanitizeInput(v || "", 1000));
    check("website", "website", v => sanitizeInput(v || "", 500));
    if (String(ei.est || "") !== String(orig.est || "")) { fields.est = ei.est ? Math.min(Math.max(parseInt(ei.est) || 0, 1700), new Date().getFullYear()) : null; changes.push("est"); }
    if (ei.brandType !== (orig.brandType || "distiller")) { fields.brand_type = ei.brandType; fields.is_sourced = ei.brandType !== "distiller"; changes.push("brand_type"); }
    if (ei.facilityId !== (orig.facilityId || "")) { fields.facility_id = ei.facilityId || null; fields.facility_confirmed = true; changes.push("facility_id"); }
    if (changes.length === 0) { setAdminDetailModal(null); return; }
    if (useSupabase()) {

      const ok = await db.updateCatalogItem("brands", ei.id, fields);
      if (!ok) { showToast("\u26A0\uFE0F Save failed"); return; }

    }

    setBrands(prev => prev.map(d => d.id === ei.id ? { ...d, ...ei, est: fields.est !== undefined ? fields.est : d.est, brandType: fields.brand_type || d.brandType, isSourced: fields.is_sourced !== undefined ? fields.is_sourced : d.isSourced, facilityId: fields.facility_id !== undefined ? fields.facility_id : d.facilityId } : d));
    appendAudit(currentUser.uid, currentUser.name, "catalog_edited", "catalog", "Edited brand " + ei.name + " [" + changes.join(", ") + "]", ei.id);
    showToast("\u2705 " + ei.name + " updated");
    setAdminDetailModal(null);
  };

  // ── Open detail modal helpers ──

  const openBourbonDetail = (item) => setAdminDetailModal({ type: "bourbon", data: { ...item, brandId: item.brandId || "", facilityId: item.facilityId || "", origin: item.origin || "", batch: item.batch || "", notes: item.notes || "", website: item.website || "", finishType: item.finishType || "", mashBill: item.mashBill || "", age: item.age || "", proof: item.proof || "", msrp: item.msrp || "", type: item.type || "", limitedRelease: item.limitedRelease || false, discontinued: item.discontinued || false } });

  const openBrandDetail = (item) => setAdminDetailModal({ type: "brand", data: { ...item, location: item.location || "", description: item.description || "", website: item.website || "", est: item.est || "", brandType: item.brandType || "distiller", facilityId: item.facilityId || "" } });

  return (
    <div>
      <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.5rem", color: "#F5E6D0", margin: "0 0 6px" }}>Admin Console</h2>
      <p style={{ color: "#B5A898", fontSize: "0.8125rem", margin: "0 0 20px" }}>Tap a tile to drill down</p>
      {/* ═══════ DASHBOARD TILES ═══════ */}
      {(() => {

        const totalPending = pendingExprs.length + pendingBrands.length;

        const tiles = [
          { key: "overview", icon: "🏠", label: "Dashboard", val: null, desc: "Overview", color: "#D4A053" },
          { key: "submissions", icon: "📋", label: "Reviews", val: totalPending, desc: totalPending > 0 ? "need review" : "all clear", color: "#FFC107", alert: totalPending > 0 },
          { key: "moderation", icon: "🚩", label: "Moderation", val: pendingFlags.length + pendingClaims.length, desc: (pendingFlags.length > 0 ? pendingFlags.length + " flags" : "") + (pendingClaims.length > 0 ? (pendingFlags.length > 0 ? " · " : "") + pendingClaims.length + " claims" : "") || "all clear", color: "#CD5C5C", alert: pendingFlags.length + pendingClaims.length > 0 },
          { key: "catalog", icon: "📚", label: "Catalog", val: bourbons.length, desc: brands.length + " brands · " + facilities.length + " facilities", color: "#6B9BD2" },
          { key: "quality", icon: "🔧", label: "Data Quality", val: dataQualityCount, desc: dataQualityCount > 0 ? "issues" : "clean", color: "#E88B5A", alert: dataQualityCount > 100 },
          { key: "users", icon: "👥", label: "Users", val: platformUsers.length, desc: platformUsers.filter(u => u.status === "active" || !u.status).length + " active", color: "#9B7ED8" },
          { key: "duplicates", icon: "🔀", label: "Duplicates", val: adminDuplicates.length, desc: adminDuplicates.length > 0 ? "pairs" : "clean", color: "#FF9800", alert: adminDuplicates.length > 0 },
          { key: "audit", icon: "📝", label: "Audit Log", val: auditLog.length, desc: "entries", color: "#4CAF50" },
        ];

        return (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 20 }}>
            {tiles.map(k => (
              <div key={k.key} onClick={() => { setAdminTab(k.key); setAdminSearch(""); if (k.key === "quality") setAdminCatalogType("no_proof"); }}
                style={{ background: adminTab === k.key ? "#3D3428" : "#1E1A14", borderRadius: 10, padding: "10px 6px", textAlign: "center", border: `1px solid ${adminTab === k.key ? k.color : "#3D3428"}`, cursor: "pointer", transition: "all 0.15s", position: "relative" }}>
                {k.alert && <div style={{ position: "absolute", top: 4, right: 6, width: 8, height: 8, borderRadius: "50%", background: k.color, boxShadow: `0 0 6px ${k.color}` }} />}
                <div style={font10Style}>{k.icon}</div>
                {k.val !== null && <div style={{ fontSize: "1.125rem", fontWeight: 700, color: adminTab === k.key ? k.color : "#B5A898", fontFamily: FONT_DISPLAY }}>{k.val}</div>}
                <div style={{ fontSize: "0.5625rem", color: adminTab === k.key ? "#F5E6D0" : "#A69880", textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2, lineHeight: 1.3 }}>{k.label}</div>
                <div style={{ fontSize: "0.5rem", color: "#5A5040", marginTop: 1 }}>{k.desc}</div>
              </div>
            ))}
          </div>
        );
      })()}
      {/* ═══════ UNIVERSAL DETAIL MODAL ═══════ */}
      {adminDetailModal && (() => {

        const { type, data: mdl } = adminDetailModal;

        const setMdl = (fn) => setAdminDetailModal(prev => ({ ...prev, data: typeof fn === "function" ? fn(prev.data) : fn }));

        const isBourbon = type === "bourbon";

        const isBrand = type === "brand";

        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 12px", overflowY: "auto" }}
            onClick={(e) => { if (e.target === e.currentTarget) setAdminDetailModal(null); }}>
            <div style={{ background: "#1A1510", borderRadius: 16, padding: 20, width: "100%", maxWidth: 480, border: "1px solid #5A4F3E", maxHeight: "85vh", overflowY: "auto" }}>
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #3D3428" }}>
                <div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0" }}>
                    {isBourbon ? "Edit Bourbon" : isBrand ? "Edit Brand" : "Detail"}
                  </div>
                  <div style={{ fontSize: "0.6875rem", color: "#B5A898", marginTop: 2, fontFamily: "monospace" }}>ID: {mdl.id?.slice(0, 20)}{mdl.id?.length > 20 ? "…" : ""}</div>
                </div>
                <button onClick={() => setAdminDetailModal(null)} style={{ background: "none", border: "none", color: "#B5A898", cursor: "pointer", fontSize: "1.25rem", padding: "8px 12px" }} aria-label="Close">{"✕"}</button>
              </div>
              {/* Bourbon Detail Form */}
              {isBourbon && (<>
                <div style={{ fontSize: "0.625rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>Core Attributes</div>
                {admFld("Name", "name", mdl, setMdl, { max: 100 })}
                <div style={grid2ColGap8Style}>
                  {admFld("Proof", "proof", mdl, setMdl, { type: "number" })}
                  {admFld("Age", "age", mdl, setMdl, { max: 20 })}
                </div>
                {admFld("Type / Category", "type", mdl, setMdl, { max: 100 })}
                <div style={grid2ColGap8Style}>
                  {admFld("MSRP ($)", "msrp", mdl, setMdl, { type: "number" })}
                  {admFld("Batch", "batch", mdl, setMdl, { max: 100 })}
                </div>
                {admFld("Mash Bill", "mashBill", mdl, setMdl, { max: 200 })}
                {admFld("Finish Type", "finishType", mdl, setMdl, { max: 100 })}
                {admFld("Origin", "origin", mdl, setMdl, { max: 100 })}
                {admFld("Website", "website", mdl, setMdl, { max: 500 })}
                {admFld("Tasting Notes", "notes", mdl, setMdl, { textarea: true })}
                <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: "0.75rem", color: "#F5E6D0" }}>
                    <input type="checkbox" aria-label="Limited release" checked={mdl.limitedRelease || false} onChange={e => setMdl(p => ({ ...p, limitedRelease: e.target.checked }))} /> Limited Release
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: "0.75rem", color: "#F5E6D0" }}>
                    <input type="checkbox" aria-label="Discontinued" checked={mdl.discontinued || false} onChange={e => setMdl(p => ({ ...p, discontinued: e.target.checked }))} /> Discontinued
                  </label>
                </div>
                <div style={{ fontSize: "0.625rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 1, margin: "16px 0 8px", fontWeight: 700 }}>Relationships</div>
                {admFld("Brand", "brandId", mdl, setMdl, { select: true, options: [{ value: "", label: "— None —" }, ...brands.sort((a, b) => a.name.localeCompare(b.name)).map(b => ({ value: b.id, label: b.name + (b.brandType !== "distiller" ? " (" + b.brandType.toUpperCase() + ")" : "") }))] })}
                {admFld("Facility (permit)", "facilityId", mdl, setMdl, { select: true, options: [{ value: "", label: "— None —" }, ...facilities.sort((a, b) => (a.companyName || "").localeCompare(b.companyName || "")).map(f => ({ value: f.permitNumber, label: (f.companyName || f.name || "Unknown") + " (" + f.permitNumber + ")" }))] })}
                <div style={{ fontSize: "0.625rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 1, margin: "16px 0 8px", fontWeight: 700 }}>COLA Provenance</div>
                {admFld("TTB ID", "colaTtbId", mdl, setMdl, { readonly: true, mono: true })}
                {admFld("COLA Brand Name", "colaBrandName", mdl, setMdl, { readonly: true })}
                {admFld("COLA Class", "colaClassName", mdl, setMdl, { readonly: true })}
                {admFld("Approval Date", "colaApprovalDate", mdl, setMdl, { readonly: true })}
                {admFld("Source", "source", mdl, setMdl, { readonly: true })}
                {mdl.imageUrl && (
                  <div style={mb8Style}>
                    <label style={{ display: "block", fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3, fontWeight: 600 }}>Label Image</label>
                    <img src={safeImageUrl(mdl.imageUrl)} alt="Label" style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8, border: "1px solid #3D3428" }}
                      onError={(e) => { e.target.style.display = "none"; }} />
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 16, borderTop: "1px solid #3D3428" }}>
                  <button onClick={() => setAdminDetailModal(null)}
                    style={{ flex: 1, padding: "10px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#B5A898", fontSize: "0.8125rem", cursor: "pointer" }}>Cancel</button>
                  <button onClick={saveBourbonDetail}
                    style={{ flex: 1, padding: "10px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.8125rem", fontWeight: 700, cursor: "pointer" }}>Save Changes</button>
                </div>
              </>)}
              {/* Brand Detail Form */}
              {isBrand && (<>
                <div style={{ fontSize: "0.625rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>Brand Information</div>
                {admFld("Name", "name", mdl, setMdl, { max: 100 })}
                {admFld("Location", "location", mdl, setMdl, { max: 200 })}
                <div style={grid2ColGap8Style}>
                  {admFld("Established", "est", mdl, setMdl, { type: "number" })}
                  {admFld("Brand Type", "brandType", mdl, setMdl, { select: true, options: [{ value: "distiller", label: "Distiller" }, { value: "ndp", label: "NDP (Non-Distiller Producer)" }, { value: "blender", label: "Blender" }] })}
                </div>
                {admFld("Facility (permit)", "facilityId", mdl, setMdl, { select: true, options: [{ value: "", label: "— None —" }, ...facilities.sort((a, b) => (a.companyName || "").localeCompare(b.companyName || "")).map(f => ({ value: f.permitNumber, label: (f.companyName || f.name || "Unknown") + " (" + f.permitNumber + ")" }))] })}
                {admFld("Website", "website", mdl, setMdl, { max: 500 })}
                {admFld("Description", "description", mdl, setMdl, { textarea: true })}
                <div style={{ fontSize: "0.625rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 1, margin: "16px 0 8px", fontWeight: 700 }}>Metadata</div>
                {admFld("ID", "id", mdl, setMdl, { readonly: true, mono: true })}
                {admFld("Source", "source", mdl, setMdl, { readonly: true })}
                {/* Brand's bourbons count */}
                {(() => {

                  const brandBourbons = bourbons.filter(b => b.brandId === mdl.id);
                  return brandBourbons.length > 0 && (
                    <div style={{ marginTop: 12, padding: 10, background: "#150F0A", borderRadius: 8, border: "1px solid #3D3428" }}>
                      <div style={{ fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>{brandBourbons.length} Expression{brandBourbons.length !== 1 ? "s" : ""}</div>
                      {brandBourbons.slice(0, 8).map(b => (
                        <div key={b.id} onClick={() => openBourbonDetail(b)}
                          style={{ fontSize: "0.75rem", color: "#D4A053", cursor: "pointer", padding: "8px 0" }}>
                          {b.name} {b.proof ? "· " + b.proof + "°" : ""}
                        </div>
                      ))}
                      {brandBourbons.length > 8 && <div style={{ fontSize: "0.6875rem", color: "#5A5040", marginTop: 4 }}>+{brandBourbons.length - 8} more</div>}
                    </div>
                  );
                })()}
                <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 16, borderTop: "1px solid #3D3428" }}>
                  <button onClick={() => setAdminDetailModal(null)}
                    style={{ flex: 1, padding: "10px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#B5A898", fontSize: "0.8125rem", cursor: "pointer" }}>Cancel</button>
                  <button onClick={saveBrandDetail}
                    style={{ flex: 1, padding: "10px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.8125rem", fontWeight: 700, cursor: "pointer" }}>Save Changes</button>
                </div>
              </>)}
            </div>
          </div>
        );
      })()}
      {/* ═══════ OVERVIEW / DASHBOARD ═══════ */}
      {adminTab === "overview" && (() => {

        const colaBourbons = bourbons.filter(b => b.source === "cola_cloud" || b.id?.startsWith("cola-")).length;

        const seedBourbons = bourbons.filter(b => !b.id?.startsWith("cola-") && !b.userAdded).length;

        const userBourbons = bourbons.filter(b => b.userAdded).length;

        const ndpBrands = brands.filter(b => b.brandType === "ndp").length;

        const distBrands = brands.filter(b => b.brandType === "distiller").length;

        const blenderBrands = brands.filter(b => b.brandType === "blender").length;

        const actionItems = [];
        if (pendingExprs.length + pendingBrands.length > 0) actionItems.push({ icon: "📋", label: `${pendingExprs.length + pendingBrands.length} pending submissions`, color: "#FFC107", tab: "submissions" });

        if (pendingClaims.length > 0) actionItems.push({ icon: "🏢", label: `${pendingClaims.length} business claims`, color: "#D4A053", tab: "moderation" });

        if (pendingFlags.length > 0) actionItems.push({ icon: "🚩", label: `${pendingFlags.length} flagged content`, color: "#CD5C5C", tab: "moderation" });

        if (orphanedBourbons.length > 0) actionItems.push({ icon: "🔗", label: `${orphanedBourbons.length} bourbons without brand`, color: "#E88B5A", tab: "quality" });

        return (
          <div>
            {actionItems.length > 0 ? (
              <div style={mb20Style}>
                <div style={{ fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 600 }}>Action Required</div>
                {actionItems.map((a, i) => (
                  <div key={i} onClick={() => setAdminTab(a.tab)}
                    style={{ background: "rgba(255,193,7,0.04)", border: `1px solid ${a.color}33`, borderRadius: 10, padding: "12px 14px", marginBottom: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={font1125Style}>{a.icon}</span>
                    <div style={{ flex: 1, fontSize: "0.8125rem", color: a.color, fontWeight: 600 }}>{a.label}</div>
                    <span style={{ color: "#5A5040", fontSize: "0.875rem" }}>{"\u2192"}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "20px 16px", color: "#4CAF50", marginBottom: 20, background: "rgba(76,175,80,0.04)", borderRadius: 10, border: "1px solid rgba(76,175,80,0.15)" }}>
                <div style={{ fontSize: "1.25rem", marginBottom: 4 }}>{"\u2705"}</div>
                <div style={{ fontSize: "0.8125rem", fontWeight: 600 }}>All clear — no pending actions</div>
              </div>
            )}
            <div style={{ fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 600 }}>Catalog Overview</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
              <div onClick={() => { setAdminTab("catalog"); setAdminCatalogType("expressions"); }} style={{ background: "#1E1A14", borderRadius: 10, padding: 12, border: "1px solid #3D3428", cursor: "pointer" }}>
                <div style={{ fontSize: "1.375rem", fontWeight: 700, color: "#6B9BD2", fontFamily: FONT_DISPLAY }}>{bourbons.length}</div>
                <div style={mutedText0625Mt4Style}>Bourbons</div>
                <div style={faintText05625Mt2Style}>{seedBourbons} seed · {colaBourbons} COLA · {userBourbons} user</div>
              </div>
              <div onClick={() => { setAdminTab("catalog"); setAdminCatalogType("brands"); }} style={{ background: "#1E1A14", borderRadius: 10, padding: 12, border: "1px solid #3D3428", cursor: "pointer" }}>
                <div style={{ fontSize: "1.375rem", fontWeight: 700, color: "#9B7ED8", fontFamily: FONT_DISPLAY }}>{brands.length}</div>
                <div style={mutedText0625Mt4Style}>Brands</div>
                <div style={faintText05625Mt2Style}>{distBrands} distiller · {ndpBrands} NDP · {blenderBrands} blender</div>
              </div>
              <div style={{ background: "#1E1A14", borderRadius: 10, padding: 12, border: "1px solid #3D3428" }}>
                <div style={{ fontSize: "1.375rem", fontWeight: 700, color: "#4CAF50", fontFamily: FONT_DISPLAY }}>{facilities.length}</div>
                <div style={mutedText0625Mt4Style}>Facilities</div>
                <div style={faintText05625Mt2Style}>{facilities.filter(f => f.permitType === "dsp").length} DSP · {facilities.filter(f => f.permitType === "brewery").length} BR · {facilities.filter(f => f.permitType === "importer").length} IMP</div>
              </div>
              <div onClick={() => { setAdminTab("catalog"); setAdminCatalogType("upc"); }} style={{ background: "#1E1A14", borderRadius: 10, padding: 12, border: "1px solid #3D3428", cursor: "pointer" }}>
                <div style={{ fontSize: "1.375rem", fontWeight: 700, color: "#D4A053", fontFamily: FONT_DISPLAY }}>{upcCodes.length}</div>
                <div style={mutedText0625Mt4Style}>UPC Codes</div>
                <div style={faintText05625Mt2Style}>{upcCodes.filter(u => u.verified).length} verified</div>
              </div>
            </div>
            {/* ── Sync Status ── */}
            {(() => {
              const syncSources = [
                { key: "cola", action: "cola_sync", label: "COLA", greenDays: 14, yellowDays: 21, importKey: "catalog" },
                { key: "plcb", action: "plcb_sync", label: "PLCB", table: "plcb_catalog", greenDays: 14, yellowDays: 21, importKey: "change" },
                { key: "iowa", action: "iowa_sync", label: "Iowa ABD", table: "iowa_catalog", greenDays: 14, yellowDays: 21, importKey: "catalog" },
                { key: "virginia", action: "virginia_sync", label: "Virginia ABC", table: "virginia_catalog", greenDays: 90, yellowDays: 120, importKey: "upload" },
                { key: "nc", action: "nc_sync", label: "NC ABC", table: "nc_catalog", greenDays: 100, yellowDays: 120, importKey: "full" },
              ];
              const parseSyncEntries = (action) => auditLog.filter(a => a.action === action).slice(0, 20).map(a => {
                try { return { ...a, _p: typeof a.detail === "string" ? JSON.parse(a.detail) : a.detail }; } catch { return { ...a, _p: null }; }
              });
              const openSyncModal = (src) => {
                setSyncModal(src.key);
                setSyncLive(null);
                const sb = getSupabase(); if (!sb) return;
                if (src.key === "cola") {
                  Promise.all([
                    sb.from("bourbons").select("*", { count: "exact", head: true }),
                    sb.from("bourbons").select("tier"),
                    sb.from("bourbons").select("*", { count: "exact", head: true }).not("cola_id", "is", null),
                    sb.from("bourbons").select("*", { count: "exact", head: true }).not("cola_enriched_at", "is", null),
                  ]).then(([rT, rTier, rCola, rEnr]) => {
                    const tiers = {};
                    (rTier.data || []).forEach(r => { const t = r.tier || "archive"; tiers[t] = (tiers[t] || 0) + 1; });
                    setSyncLive({ total: rT.count || 0, colaSourced: rCola.count || 0, enriched: rEnr.count || 0, tiers });
                  });
                } else {
                  Promise.all([
                    sb.from(src.table).select("*", { count: "exact", head: true }),
                    sb.from(src.table).select("*", { count: "exact", head: true }).not("matched_brand_id", "is", null),
                    sb.from(src.table).select("*", { count: "exact", head: true }).not("matched_bourbon_id", "is", null),
                    sb.from(src.table).select("match_confidence"),
                  ]).then(([rT, rB, rE, rC]) => {
                    const cb = {};
                    (rC.data || []).forEach(r => { const c = r.match_confidence || "none"; cb[c] = (cb[c] || 0) + 1; });
                    setSyncLive({ total: rT.count || 0, brandMatched: rB.count || 0, exprMatched: rE.count || 0, confBreakdown: cb });
                  });
                }
              };
              return (
                <>
                  <div style={{ fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 600 }}>Sync Status</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
                    {syncSources.map(src => {
                      const entries = parseSyncEntries(src.action);
                      const importEntry = entries.find(a => a._p?.source === src.importKey || a._p?.source === "manual" || a._p?.imported > 0 || a._p?.fetchProducts > 0);
                      const matchEntry = entries.find(a => a._p?.source === "match" || a._p?.phase?.includes("match") || a._p?.brandsMatched > 0);
                      const mostRecent = entries[0];
                      const bestDate = importEntry?.ts || mostRecent?.ts;
                      const lastDate = bestDate ? new Date(bestDate) : null;
                      const daysAgo = lastDate ? Math.floor((Date.now() - lastDate.getTime()) / 86400000) : null;
                      const color = daysAgo === null ? "#CD5C5C" : daysAgo <= src.greenDays ? "#4CAF50" : daysAgo <= src.yellowDays ? "#FFC107" : "#CD5C5C";
                      const imp = importEntry?._p || mostRecent?._p;
                      const mt = matchEntry?._p;
                      return (
                        <div key={src.key} onClick={() => openSyncModal(src)} style={{ background: "#1E1A14", borderRadius: 10, padding: 12, border: `1px solid ${color}33`, cursor: "pointer" }}>
                          <div style={{ fontSize: "1.375rem", fontWeight: 700, color, fontFamily: FONT_DISPLAY }}>{daysAgo !== null ? daysAgo + "d" : "—"}</div>
                          <div style={mutedText0625Mt4Style}>{src.label}</div>
                          <div style={faintText05625Mt2Style}>{imp ? (imp.imported || imp.fetchProducts || imp.newExpressions || 0) + " imp" : "—"}{mt ? " · " + (mt.exprMatches || 0) + " expr" : ""}</div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
            <div style={{ fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 600 }}>Recent Activity</div>
            {auditLog.slice(0, 5).map(a => (
              <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #1E1A14" }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: auditCatColor(a.category), flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: "0.75rem", color: "#F5E6D0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.detail}</div>
                <div style={{ fontSize: "0.5625rem", color: "#5A5040", flexShrink: 0 }}>{a.createdAt?.slice(5, 10)}</div>
              </div>
            ))}
            {auditLog.length > 5 && <div onClick={() => setAdminTab("audit")} style={{ textAlign: "center", fontSize: "0.6875rem", color: "#D4A053", cursor: "pointer", padding: "8px 0" }}>View all {auditLog.length} entries {"\u2192"}</div>}
          </div>
        );
      })()}
      {/* ═══════ SYNC DETAIL MODAL ═══════ */}
      {syncModal && (() => {
        const srcMap = {
          cola: { action: "cola_sync", label: "COLA", importKey: "catalog", schedule: "Weekly (Sun 3am UTC) + Daily enrichment (4am UTC)", auto: true },
          plcb: { action: "plcb_sync", label: "PLCB", importKey: "change", schedule: "Weekly (Sun 6am UTC)", auto: true },
          iowa: { action: "iowa_sync", label: "Iowa ABD", importKey: "catalog", schedule: "Weekly (Sun 5am UTC)", auto: true },
          virginia: { action: "virginia_sync", label: "Virginia ABC", importKey: "upload", schedule: "Manual (quarterly)", auto: false },
          nc: { action: "nc_sync", label: "NC ABC", importKey: "full", schedule: "Quarterly + bimonthly specials", auto: true },
        };
        const src = srcMap[syncModal];
        if (!src) return null;
        const entries = auditLog.filter(a => a.action === src.action).slice(0, 10).map(a => {
          try { return { ...a, _p: typeof a.detail === "string" ? JSON.parse(a.detail) : a.detail }; } catch { return { ...a, _p: null }; }
        });
        const importEntry = entries.find(a => a._p?.source === src.importKey || a._p?.source === "manual" || a._p?.imported > 0 || a._p?.fetchProducts > 0);
        const matchEntry = entries.find(a => a._p?.source === "match" || a._p?.phase?.includes("match") || a._p?.brandsMatched > 0);
        const mostRecent = entries[0];
        const bestEntry = importEntry || mostRecent;
        const imp = importEntry?._p || mostRecent?._p;
        const mt = matchEntry?._p;
        const lastDate = bestEntry?.ts ? new Date(bestEntry.ts) : null;
        const enrichEntries = syncModal === "cola" ? auditLog.filter(a => a.action === "cola_enrich").slice(0, 5).map(a => {
          try { return { ...a, _p: typeof a.detail === "string" ? JSON.parse(a.detail) : a.detail }; } catch { return { ...a, _p: null }; }
        }) : [];
        const lastEnrich = enrichEntries[0];
        const enrichDate = lastEnrich?.ts ? new Date(lastEnrich.ts) : null;
        const enrichDaysAgo = enrichDate ? Math.floor((Date.now() - enrichDate.getTime()) / 86400000) : null;
        const enrichColor = enrichDaysAgo === null ? "#CD5C5C" : enrichDaysAgo <= 2 ? "#4CAF50" : enrichDaysAgo <= 5 ? "#FFC107" : "#CD5C5C";
        const vaCmd = `$KEY = "YOUR_SUPABASE_ANON_KEY"
# 1. Download CSV from https://www.abc.virginia.gov/products/bourbon
# 2. Open in Excel, Save As CSV (UTF-8)
# 3. Upload:
$csv = Get-Content "virginia_price_list.csv" -Raw -Encoding UTF8
$body = @{ source = "upload"; csvData = $csv } | ConvertTo-Json -Depth 3
$body | Out-File -Encoding ascii -NoNewline body.json
curl.exe -X POST "https://api.barrelrank.com/functions/v1/virginia-sync" \`
-H "Authorization: Bearer $KEY" \`
-H "Content-Type: application/json" \`
-d "@body.json"
# 4. Run match phase:
'{"phase":"match"}' | Out-File -Encoding ascii -NoNewline body.json
curl.exe -X POST "https://api.barrelrank.com/functions/v1/virginia-sync" \`
-H "Authorization: Bearer $KEY" \`
-H "Content-Type: application/json" \`
-d "@body.json"`;
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 12px", overflowY: "auto" }}
            onClick={(e) => { if (e.target === e.currentTarget) setSyncModal(null); }}>
            <div style={{ background: "#1A1510", borderRadius: 16, padding: 20, width: "100%", maxWidth: 480, border: "1px solid #5A4F3E", maxHeight: "85vh", overflowY: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #3D3428" }}>
                <div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0" }}>{src.label} Sync</div>
                  <div style={{ fontSize: "0.5625rem", color: "#5A5040", marginTop: 2 }}>{src.schedule}</div>
                </div>
                <button onClick={() => setSyncModal(null)} style={{ background: "none", border: "none", color: "#B5A898", cursor: "pointer", fontSize: "1.25rem", padding: "8px 12px" }} aria-label="Close">{"\u2715"}</button>
              </div>
              {/* Section A: Last Sync Summary */}
              <div style={{ fontSize: "0.625rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>Last Sync</div>
              <div style={{ background: "#1E1A14", borderRadius: 10, padding: 12, border: "1px solid #3D3428", marginBottom: 16 }}>
                {lastDate ? (
                  <>
                    <div style={{ fontSize: "0.8125rem", color: "#F5E6D0", marginBottom: 6 }}>{lastDate.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
                    <div style={{ fontSize: "0.6875rem", color: "#B5A898", lineHeight: 1.6 }}>
                      {"Fetched: " + (imp?.fetchProducts || imp?.imported || imp?.newExpressions || 0) + " · Imported: " + (imp?.imported || imp?.newExpressions || 0)}
                      {mt ? " · Brands: " + (mt.brandMatches || 0) + " · Expressions: " + (mt.exprMatches || 0) : ""}
                    </div>
                    {(imp?.errors > 0 || mt?.errors > 0) && <div style={{ fontSize: "0.6875rem", color: "#CD5C5C", marginTop: 4 }}>{(imp?.errors || 0) + (mt?.errors || 0)} error{((imp?.errors || 0) + (mt?.errors || 0)) !== 1 ? "s" : ""}</div>}
                    {syncModal === "cola" && (
                      <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #3D3428" }}>
                        <div style={{ fontSize: "0.6875rem", color: "#B5A898", display: "flex", alignItems: "center", gap: 6 }}>
                          <span>Last enrichment:</span>
                          {enrichDate ? (
                            <>
                              <span style={{ color: enrichColor, fontWeight: 600 }}>{enrichDaysAgo}d ago</span>
                              <span style={{ color: "#5A5040" }}>({enrichDate.toLocaleDateString()})</span>
                            </>
                          ) : <span style={{ color: "#CD5C5C" }}>Never</span>}
                        </div>
                        {lastEnrich?._p && <div style={{ fontSize: "0.6875rem", color: "#5A5040", marginTop: 2 }}>{lastEnrich._p.enriched || lastEnrich._p.processed || 0} enriched{lastEnrich._p.errors > 0 ? " · " + lastEnrich._p.errors + " errors" : ""}</div>}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: "0.8125rem", color: "#CD5C5C" }}>No sync recorded</div>
                )}
              </div>
              {/* Section B: Live Match Stats */}
              <div style={{ fontSize: "0.625rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>Live Catalog Stats</div>
              <div style={{ background: "#1E1A14", borderRadius: 10, padding: 12, border: "1px solid #3D3428", marginBottom: 16 }}>
                {syncLive ? (
                  syncModal === "cola" ? (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#6B9BD2", fontFamily: FONT_DISPLAY }}>{syncLive.total}</div>
                          <div style={{ fontSize: "0.5625rem", color: "#B5A898" }}>Total</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#9B7ED8", fontFamily: FONT_DISPLAY }}>{syncLive.colaSourced}</div>
                          <div style={{ fontSize: "0.5625rem", color: "#B5A898" }}>COLA-sourced</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#4CAF50", fontFamily: FONT_DISPLAY }}>{syncLive.enriched}</div>
                          <div style={{ fontSize: "0.5625rem", color: "#B5A898" }}>Enriched</div>
                        </div>
                      </div>
                      {syncLive.tiers && Object.keys(syncLive.tiers).length > 0 && (
                        <div style={{ borderTop: "1px solid #3D3428", paddingTop: 8 }}>
                          <div style={{ fontSize: "0.5625rem", color: "#5A5040", marginBottom: 4 }}>Tier breakdown:</div>
                          {["curated", "core", "visible", "archive"].filter(t => syncLive.tiers[t]).map(t => (
                            <div key={t} style={{ display: "flex", justifyContent: "space-between", fontSize: "0.6875rem", color: "#B5A898", padding: "2px 0" }}>
                              <span>{t}</span><span>{syncLive.tiers[t]}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#6B9BD2", fontFamily: FONT_DISPLAY }}>{syncLive.total}</div>
                          <div style={{ fontSize: "0.5625rem", color: "#B5A898" }}>Total</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#9B7ED8", fontFamily: FONT_DISPLAY }}>{syncLive.brandMatched}</div>
                          <div style={{ fontSize: "0.5625rem", color: "#B5A898" }}>Brands</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#4CAF50", fontFamily: FONT_DISPLAY }}>{syncLive.exprMatched}</div>
                          <div style={{ fontSize: "0.5625rem", color: "#B5A898" }}>Expressions</div>
                        </div>
                      </div>
                      {syncLive.confBreakdown && Object.keys(syncLive.confBreakdown).length > 0 && (
                        <div style={{ borderTop: "1px solid #3D3428", paddingTop: 8 }}>
                          <div style={{ fontSize: "0.5625rem", color: "#5A5040", marginBottom: 4 }}>Confidence breakdown:</div>
                          {Object.entries(syncLive.confBreakdown).sort((a, b) => b[1] - a[1]).map(([conf, count]) => (
                            <div key={conf} style={{ display: "flex", justifyContent: "space-between", fontSize: "0.6875rem", color: "#B5A898", padding: "2px 0" }}>
                              <span>{conf}</span><span>{count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )
                ) : (
                  <div style={{ textAlign: "center", padding: 8, color: "#5A5040", fontSize: "0.75rem" }}>Loading...</div>
                )}
              </div>
              {/* Section C: Source-specific notes */}
              {syncModal === "virginia" && (
                <>
                  <div style={{ fontSize: "0.625rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>Quarterly Refresh</div>
                  <div style={{ background: "#1E1A14", borderRadius: 10, padding: 12, border: "1px solid #3D3428" }}>
                    <div style={{ fontSize: "0.6875rem", color: "#B5A898", marginBottom: 8 }}>Virginia ABC blocks automated fetches (Cloudflare). Manual CSV upload required quarterly.</div>
                    <pre style={{ background: "#150F0A", borderRadius: 8, padding: 10, fontSize: "0.625rem", color: "#D4A053", fontFamily: "monospace", overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all", margin: "0 0 8px", border: "1px solid #3D3428", lineHeight: 1.5 }}>{vaCmd}</pre>
                    <button onClick={() => { navigator.clipboard.writeText(vaCmd); }} style={{ width: "100%", padding: "8px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#D4A053", fontSize: "0.75rem", cursor: "pointer", fontWeight: 600 }}>Copy Commands</button>
                  </div>
                </>
              )}
              {syncModal === "nc" && (
                <div style={{ background: "rgba(76,175,80,0.04)", borderRadius: 10, padding: 12, border: "1px solid rgba(76,175,80,0.15)" }}>
                  <div style={{ fontSize: "0.6875rem", color: "#4CAF50", fontWeight: 600, marginBottom: 4 }}>Operational</div>
                  <div style={{ fontSize: "0.6875rem", color: "#B5A898" }}>Full price list + specials + new items all fetching successfully. Div-based parser handles current NC ABC layout.</div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
      {/* ═══════ SUBMISSIONS DRILLDOWN ═══════ */}
      {adminTab === "submissions" && (() => {

        const allUserItems = [...bourbons.filter(b => b.userAdded), ...brands.filter(d => d.userAdded)];

        const pendingEdits = expressionEdits.filter(e => e.status === "pending").map(e => {
          const bx = getBourbon(e.bourbonId);
          const changedFields = Object.keys(e.suggestedChanges || {});
          const isUpcOnly = changedFields.length === 1 && changedFields[0] === "upc";
          return { ...e, _type: isUpcOnly ? "upc-link" : "edit", _name: bx?.name || "Unknown Expression", _detail: isUpcOnly ? "UPC Link: " + (e.suggestedChanges.upc || "") : changedFields.length + " field" + (changedFields.length !== 1 ? "s" : "") + " changed" + (e.reason ? " — " + e.reason.slice(0, 60) : ""), submittedAt: e.createdAt, _bourbon: bx };
        });
        const allPending = [
          ...pendingExprs.map(b => { const upc = upcCodes.find(u => u.expression_id === b.id); return { ...b, _type: "expression", _name: b.name, _detail: (getBrand(b.brandId)?.name || "—") + " · " + (b.proof || "—") + "° · " + (b.type || "—") + (upc ? " · UPC " + upc.upc : ""), _upc: upc?.upc || null }; }),
          ...pendingBrands.map(d => ({ ...d, _type: "brand", _name: d.name, _detail: (d.location || "—") + (d.est ? " · Est. " + d.est : "") })),
          ...pendingEdits,
        ].sort((a, b) => (a.submittedAt || "").localeCompare(b.submittedAt || ""));

        const recentActions = allUserItems.filter(i => i.reviewedAt).sort((a, b) => (b.reviewedAt || "").localeCompare(a.reviewedAt || "")).slice(0, 10);

        const sq = adminSearch.toLowerCase();

        const filteredPending = sq ? allPending.filter(i => i._name.toLowerCase().includes(sq) || i._detail?.toLowerCase().includes(sq)) : allPending;

        return (
          <div>
            <div style={{ position: "relative", marginBottom: 14 }}>
              <span style={searchIconStyle}>{"🔍"}</span>
              <input value={adminSearch} onChange={e => setAdminSearch(e.target.value)} aria-label="Search submissions" placeholder="Search submissions..."
                style={searchInputIconStyle} />
            </div>
            <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0", margin: "0 0 12px" }}>Pending Review ({filteredPending.length})</h3>
            {filteredPending.length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px 16px", color: "#B5A898", background: "#150F0A", borderRadius: 12, border: "1px solid #3D3428", marginBottom: 20 }}>
                <div style={{ fontSize: "1.5rem", marginBottom: 8 }}>{"\u2713"}</div>
                <p style={sMarFon6Style}>{sq ? "No matching submissions" : "No submissions awaiting review"}</p>
              </div>
            ) : (
              <div style={mb20Style}>
                {filteredPending.map(item => {

                  const daysOld = item.submittedAt ? Math.floor((Date.now() - new Date(item.submittedAt).getTime()) / 86400000) : 0;

                  const isEnriching = enrichingSubmission === item.id && item._type === "expression";
                  return (
                    <div key={item.id} style={mb10Style}>
                      <div style={{ background: "#150F0A", borderRadius: 12, padding: 16, border: "1px solid " + (daysOld > 20 ? "rgba(255,193,7,0.3)" : "#3D3428") }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                          <div style={flexOneStyle}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: "0.5625rem", fontWeight: 700, textTransform: "uppercase", background: item._type === "edit" ? "rgba(156,39,176,0.12)" : item._type === "expression" ? "rgba(212,160,83,0.12)" : "rgba(100,149,237,0.12)", color: item._type === "edit" ? "#CE93D8" : item._type === "expression" ? "#D4A053" : "#6495ED", border: "1px solid " + (item._type === "edit" ? "rgba(156,39,176,0.25)" : item._type === "expression" ? "rgba(212,160,83,0.25)" : "rgba(100,149,237,0.25)") }}>{item._type}</span>
                              <span style={{ fontFamily: FONT_DISPLAY, fontSize: "0.9375rem", color: "#F5E6D0" }}>{item._name}</span>
                              {daysOld > 20 && <span style={{ padding: "2px 6px", borderRadius: 8, fontSize: "0.5625rem", fontWeight: 700, background: "rgba(255,193,7,0.12)", color: "#FFC107", border: "1px solid rgba(255,193,7,0.25)" }}>{30 - daysOld}d left</span>}
                            </div>
                            <div style={{ color: "#B5A898", fontSize: "0.75rem", marginTop: 4 }}>{item._detail}</div>
                            <div style={{ color: "#B5A898", fontSize: "0.6875rem", marginTop: 4 }}>by {submitter(item._type === "edit" ? item.userId : item.submittedBy)} · {item.submittedAt?.slice(0, 10)} · {daysOld}d ago</div>
                          </div>
                          {item._type !== "edit" && <button onClick={() => item._type === "expression" ? openBourbonDetail(item) : openBrandDetail(item)}
                            style={{ background: "none", border: "1px solid #5A4F3E", borderRadius: 6, color: "#D4A053", fontSize: "0.625rem", padding: "8px 12px", cursor: "pointer", flexShrink: 0 }}>{"\u270F\uFE0F"} Edit</button>}
                        </div>
                        <div style={flexGap8Style}>
                          {item._type === "edit" ? (
                            <>
                              <button onClick={() => setReviewingEditId(reviewingEditId === item.id ? null : item.id)}
                                style={{ flex: 1, padding: "10px", background: reviewingEditId === item.id ? "rgba(156,39,176,0.2)" : "rgba(156,39,176,0.1)", border: "1px solid rgba(156,39,176,0.3)", borderRadius: 8, color: "#CE93D8", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>
                                {reviewingEditId === item.id ? "Close Review" : "Review Changes"}
                              </button>
                              <button onClick={async () => {
                                const ok = await db.updateExpressionEditStatus(item.id, "dismissed", currentUser?.uid);
                                if (ok) { setExpressionEdits(prev => prev.map(e => e.id === item.id ? { ...e, status: "dismissed", reviewedBy: currentUser?.uid, reviewedAt: new Date().toISOString() } : e)); appendAudit(currentUser.uid, currentUser.name, "edit_dismissed", "submission", "Dismissed edit for " + item._name, item.bourbonId); showToast("Edit dismissed"); setReviewingEditId(null); }
                                else showToast("Failed to dismiss");
                              }}
                                style={{ flex: 1, padding: "10px", background: "rgba(205,92,92,0.08)", border: "1px solid rgba(205,92,92,0.25)", borderRadius: 8, color: "#CD5C5C", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>
                                Dismiss
                              </button>
                            </>
                          ) : item._type === "expression" ? (
                            <button onClick={() => { if (isEnriching) { setEnrichingSubmission(null); } else { setEnrichData({ name: item.name || item._name || "", brandId: item.brandId || "", type: item.type || "", proof: item.proof ?? "", age: item.age || "", mashBill: item.mashBill || "", msrp: item.msrp ?? "", finishType: item.finishType || "", batch: item.batch || "", mashCorn: item.mashCorn ?? "", mashWheat: item.mashWheat ?? "", mashRye: item.mashRye ?? "", mashBarley: item.mashBarley ?? "", mashOther: item.mashOther ?? "", proofMin: item.proofMin ?? "", proofMax: item.proofMax ?? "", expressionType: item.expressionType || "standard" }); setEnrichNameLocked(!!item.nameLocked); setEnrichingSubmission(item.id); } }}
                              style={{ flex: 1, padding: "10px", background: isEnriching ? "rgba(76,175,80,0.2)" : "rgba(76,175,80,0.1)", border: "1px solid rgba(76,175,80,0.3)", borderRadius: 8, color: "#4CAF50", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>
                              {isEnriching ? "Close Enrichment" : "\u2705 Approve & Enrich"}
                            </button>
                          ) : (
                            <button onClick={async () => {
                              const dbTable = "brands";
                              if (useSupabase()) {
                                const { error } = await getSupabase().from(dbTable).update({ submission_status: "approved", reviewed_by: currentUser?.uid, reviewed_at: new Date().toISOString() }).eq("id", item.id);
                                if (error) { showToast("\u26A0\uFE0F DB update failed"); return; }
                              }
                              setBrands(prev => prev.map(d => d.id === item.id ? { ...d, submissionStatus: "approved", reviewedBy: currentUser?.uid, reviewedAt: new Date().toISOString() } : d));
                              appendAudit(currentUser.uid, currentUser.name, "submission_approved", "submission", "Approved brand " + item._name, item.id);
                              showToast("\u2705 " + item._name + " approved");
                            }}
                              style={{ flex: 1, padding: "10px", background: "rgba(76,175,80,0.1)", border: "1px solid rgba(76,175,80,0.3)", borderRadius: 8, color: "#4CAF50", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>
                              {"\u2705"} Approve
                            </button>
                          )}
                          {item._type !== "edit" && <button onClick={async () => {
                            const rawReason = prompt("Rejection reason (visible to user):");
                            if (!rawReason) return;
                            const reason = sanitizeInput(rawReason, 200);
                            if (!reason) return;
                            const dbTable = item._type === "expression" ? "bourbons" : "brands";
                            if (useSupabase()) await db.updateSubmissionStatus(dbTable, item.id, "rejected", currentUser?.uid, reason);
                            if (item._type === "expression") setBourbons(prev => prev.map(b => b.id === item.id ? { ...b, submissionStatus: "rejected", rejectionReason: reason, reviewedBy: currentUser?.uid, reviewedAt: new Date().toISOString() } : b));
                            else setBrands(prev => prev.map(d => d.id === item.id ? { ...d, submissionStatus: "rejected", rejectionReason: reason, reviewedBy: currentUser?.uid, reviewedAt: new Date().toISOString() } : d));
                            appendAudit(currentUser.uid, currentUser.name, "submission_rejected", "submission", "Rejected " + item._name + ": " + reason, item.id);
                            showToast("\u274C " + item._name + " rejected");
                          }}
                            style={{ flex: 1, padding: "10px", background: "rgba(205,92,92,0.08)", border: "1px solid rgba(205,92,92,0.25)", borderRadius: 8, color: "#CD5C5C", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>
                            {"\u274C"} Reject
                          </button>}
                        </div>
                      </div>
                      {/* ── ENRICHMENT PANEL ── */}
                      {isEnriching && (() => {
                        const eBrand = getBrand(item.brandId);
                        const enr = enrichData;
                        const updEnr = (key, val) => setEnrichData(prev => ({ ...prev, [key]: val }));
                        const enrBrand = getBrand(enr.brandId);
                        const enrFac = enrBrand?.facilityId ? facilities.find(f => f.permitNumber === enrBrand.facilityId) : null;
                        const hasMash = [enr.mashCorn, enr.mashWheat, enr.mashRye, enr.mashBarley, enr.mashOther].some(v => parseInt(v) > 0);
                        const kpiFields = [enr.proof, enr.age, enr.type, enr.msrp, enr.finishType, enr.batch, hasMash];
                        const kpiCount = kpiFields.filter(v => v !== "" && v !== null && v !== undefined && v !== false).length;
                        // Dupe check — runs once per submission
                        if (dupeCheckRef.current.checked !== item.id) {
                          dupeCheckRef.current = { dupes: null, checked: item.id };
                          (async () => {
                            try {
                              const sb = getSupabase();
                              if (!sb || !item.brandId) return;
                              const coreName = (item._name || "").replace(/\b(bourbon|whiskey|whisky|straight|kentucky|small batch|single barrel)\b/gi, "").trim();
                              if (coreName.length < 3) return;
                              const { data } = await sb.from("bourbons").select("id, name, tier, proof, brand_id, popularity_score").eq("brand_id", item.brandId).neq("id", item.id).ilike("name", `%${sanitizeQueryParam(coreName)}%`).limit(5);
                              if (data && data.length > 0) dupeCheckRef.current.dupes = data;
                            } catch { /* silent */ }
                          })();
                        }
                        const dupeMatches = dupeCheckRef.current.dupes;
                        return (
                          <div style={{ background: "#150F0A", border: "1px solid #3D3428", borderRadius: 12, padding: 16, marginTop: 8 }}>
                            {/* SECTION 1 — Submission Reference */}
                            <div style={sectionLabelSmStyle}>Submission Reference</div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", marginBottom: 14, fontSize: "0.75rem" }}>
                              <div style={mutedTextStyle}>UPC</div><div style={lightTextStyle}>{item._upc || "No UPC submitted"}</div>
                              <div style={mutedTextStyle}>Brand</div><div style={lightTextStyle}>{eBrand?.name || "Unknown"}</div>
                              <div style={mutedTextStyle}>Expression</div><div style={lightTextStyle}>{item._name}</div>
                              <div style={mutedTextStyle}>Submitter</div><div style={lightTextStyle}>{submitter(item.submittedBy)}</div>
                            </div>
                            {/* SECTION 2 — Brand Matching */}
                            <div style={sectionLabelSmStyle}>Brand Matching</div>
                            <div style={formGroupStyle}>
                              <label style={formLabelStyle}>Brand</label>
                              <select value={enr.brandId} onChange={e => { updEnr("brandId", e.target.value); }} aria-label="Brand"
                                style={selectInputStyle}>
                                <option value="">{"— Select Brand —"}</option>
                                {brands.sort((a, b) => a.name.localeCompare(b.name)).map(b => (
                                  <option key={b.id} value={b.id}>{b.name}{b.facilityId ? " (" + b.facilityId + ")" : ""}</option>
                                ))}
                              </select>
                            </div>
                            {/* SECTION 3 — Name & Classification */}
                            <div style={sectionLabelSmStyle}>Name & Classification</div>
                            <div style={formGroupStyle}>
                              <label style={formLabelStyle}>Expression Name</label>
                              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                <input value={enr.name} onChange={e => { updEnr("name", e.target.value); }} aria-label="Expression name"
                                  style={{ ...formInputStyle, flex: 1 }} maxLength={100} />
                                <button onClick={() => { const normalized = normalizeColaName(enr.name, enrBrand?.name || ''); updEnr("name", normalized); }}
                                  style={{ fontSize: '0.7rem', padding: '4px 8px', background: '#2a1f0e', border: '1px solid #d4a04a', borderRadius: 6, color: '#d4a04a', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                  title="Apply naming convention normalization">{"\u2728"} Normalize</button>
                              </div>
                              <div style={{ fontSize: "0.625rem", color: "#5A4F3E", marginTop: 4 }}>Match naming convention: Brand Expression Variant</div>
                            </div>
                            <label style={{ ...formLabelStyle, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                              <input type="checkbox" checked={!!enrichNameLocked} onChange={e => setEnrichNameLocked(e.target.checked)} aria-label="Lock expression name" />
                              <span>{"\uD83D\uDD12"} Lock Name</span>
                              {enrichNameLocked && <span style={{ fontSize: "0.7rem", color: "#999" }}>(protected from sync overwrites)</span>}
                            </label>
                            <div style={formGroupStyle}>
                              <label style={formLabelStyle}>Type / Category</label>
                              <select value={enr.type} onChange={e => { updEnr("type", e.target.value); }} aria-label="Type"
                                style={selectInputStyle}>
                                <option value="">{"— Select Type —"}</option>
                                {["Bourbon, Straight", "Bourbon, Small Batch", "Bourbon, Single Barrel", "Bourbon, Barrel Proof", "Bourbon, Bottled in Bond", "Bourbon, Finished", "Rye, Straight", "Wheat, Straight", "Other"].map(t => (
                                  <option key={t} value={t}>{t}</option>
                                ))}
                              </select>
                            </div>
                            <div style={formGroupStyle}>
                              <label style={formLabelStyle}>Expression Type</label>
                              <select value={enr.expressionType || "standard"} onChange={e => updEnr("expressionType", e.target.value)} aria-label="Expression type"
                                style={selectInputStyle}>
                                {[{ v: "standard", l: "Standard" }, { v: "program", l: "Program (parent w/ releases)" }, { v: "program_release", l: "Program Release (child)" }, { v: "pick_base", l: "Pick Base (single barrel)" }, { v: "pick", l: "Pick (specific barrel)" }, { v: "reapproval", l: "Reapproval" }].map(o => (
                                  <option key={o.v} value={o.v}>{o.l}</option>
                                ))}
                              </select>
                            </div>
                            {/* SECTION 4 — KPI Enrichment */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                              <span style={sectionLabelSmStyle}>KPI Enrichment</span>
                              <span style={{ fontSize: "0.625rem", color: "#D4A053" }}>{kpiCount}/7 fields enriched</span>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                              <div>
                                <label style={formLabelStyle}>Proof</label>
                                <input value={enr.proof} onChange={e => { updEnr("proof", e.target.value); }} aria-label="Proof"
                                  type="number" style={formInputStyle} />
                              </div>
                              <div>
                                <label style={formLabelStyle}>MSRP ($)</label>
                                <input value={enr.msrp} onChange={e => { updEnr("msrp", e.target.value); }} aria-label="MSRP"
                                  type="number" style={formInputStyle} />
                              </div>
                              <div>
                                <label style={formLabelStyle}>Age</label>
                                <input value={enr.age} onChange={e => { updEnr("age", e.target.value); }} aria-label="Age"
                                  style={formInputStyle} maxLength={20} />
                              </div>
                              <div>
                                <label style={formLabelStyle}>Finish Type</label>
                                <input value={enr.finishType} onChange={e => { updEnr("finishType", e.target.value); }} aria-label="Finish type"
                                  style={formInputStyle} maxLength={100} />
                              </div>
                            </div>
                            <div style={formGroupStyle}>
                              <label style={formLabelStyle}>Mash Bill (%)</label>
                              <div style={flexGap8Style}>
                                {[{ k: "mashCorn", l: "Corn" }, { k: "mashWheat", l: "Wheat" }, { k: "mashRye", l: "Rye" }, { k: "mashBarley", l: "Barley" }, { k: "mashOther", l: "Other" }].map(f => (
                                  <div key={f.k} style={flexOneStyle}>
                                    <div style={{ fontSize: "0.5625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{f.l}</div>
                                    <input value={enr[f.k] ?? ""} onChange={e => updEnr(f.k, e.target.value)} aria-label={f.l + " %"}
                                      type="number" min={0} max={100} placeholder="0" style={{ ...formInputStyle, padding: "8px 6px", textAlign: "center" }} />
                                  </div>
                                ))}
                              </div>
                              {(() => { const t = [enr.mashCorn, enr.mashWheat, enr.mashRye, enr.mashBarley, enr.mashOther].reduce((s, v) => s + (parseInt(v) || 0), 0); return t > 0 ? <div style={{ fontSize: "0.5625rem", marginTop: 4, color: t === 100 ? "#4CAF50" : "#CD5C5C" }}>Total: {t}%</div> : null; })()}
                            </div>
                            <div style={formGroupStyle}>
                              <label style={formLabelStyle}>Batch</label>
                              <input value={enr.batch} onChange={e => updEnr("batch", e.target.value)} aria-label="Batch"
                                style={formInputStyle} maxLength={100} />
                            </div>
                            {(enr.proof || enr.proofMin || enr.proofMax) && (
                              <div style={formGroupStyle}>
                                <label style={formLabelStyle}>Proof Range (for batch-varying expressions)</label>
                                <div style={flexGap8Style}>
                                  <input value={enr.proofMin} onChange={e => updEnr("proofMin", e.target.value)} aria-label="Proof min"
                                    type="number" placeholder="Min" style={{ ...formInputStyle, flex: 1 }} />
                                  <input value={enr.proofMax} onChange={e => updEnr("proofMax", e.target.value)} aria-label="Proof max"
                                    type="number" placeholder="Max" style={{ ...formInputStyle, flex: 1 }} />
                                </div>
                              </div>
                            )}
                            {/* SECTION 5 — Confirm */}
                            {dupeMatches && dupeMatches.length > 0 && (
                              <div style={{ background: "rgba(255,193,7,0.06)", border: "1px solid rgba(255,193,7,0.25)", borderRadius: 10, padding: 12, marginBottom: 10 }}>
                                <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#FFC107", marginBottom: 6 }}>Possible duplicates found</div>
                                {dupeMatches.map(d => (
                                  <div key={d.id} style={{ fontSize: "0.6875rem", color: "#B5A898", marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span>{d.name} {d.proof ? `\u00B7 ${d.proof}\u00B0` : ""} <span style={{ color: "#A69880", fontSize: "0.625rem" }}>({d.tier})</span></span>
                                    <button onClick={async () => {
                                      try {
                                        const sb = getSupabase();
                                        if (sb) {
                                          await sb.from("bourbons").update({ tier: "visible" }).eq("id", d.id);
                                          await sb.from("bourbons").update({ submission_status: "rejected", reviewed_by: currentUser?.uid, reviewed_at: new Date().toISOString(), rejection_reason: "Duplicate \u2014 existing promoted" }).eq("id", item.id);
                                        }
                                        setBourbons(prev => prev.map(b => b.id === d.id ? { ...b, tier: "visible" } : b.id === item.id ? { ...b, submissionStatus: "rejected", rejectionReason: "Duplicate \u2014 existing promoted" } : b));
                                        appendAudit(currentUser.uid, currentUser.name, "submission_rejected", "submission", "Duplicate \u2014 promoted existing " + d.name, item.id);
                                        showToast("Promoted existing: " + d.name);
                                        setEnrichingSubmission(null);
                                        dupeCheckRef.current = { dupes: null, checked: null };
                                      } catch { showToast("Failed to promote existing"); }
                                    }} style={{ padding: "4px 10px", background: "rgba(255,193,7,0.12)", border: "1px solid rgba(255,193,7,0.3)", borderRadius: 6, color: "#FFC107", fontSize: "0.625rem", fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>Promote Existing</button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div style={{ borderTop: "1px solid #3D3428", paddingTop: 12 }}>
                              <div style={{ fontSize: "0.75rem", color: "#B5A898", marginBottom: 10 }}>
                                {sanitizeInput(enr.name || item._name, 100)} {"\u2192"} {enrBrand?.name || "Unknown"}{enrFac ? " at " + (enrFac.name || enrFac.companyName) : ""}
                              </div>
                              <button onClick={async () => {
                                const updates = { submission_status: "approved", reviewed_by: currentUser?.uid, reviewed_at: new Date().toISOString() };
                                if (enr.name && enr.name !== item.name) updates.name = sanitizeInput(enr.name, 100);
                                if (enr.brandId && enr.brandId !== item.brandId) { updates.brand_id = enr.brandId; const br = getBrand(enr.brandId); if (br?.facilityId) { updates.facility_id = br.facilityId; updates.facility_source = "brand_fallback"; } }
                                if (enr.type) updates.type = sanitizeInput(enr.type, 100);
                                const proofVal = parseFloat(enr.proof); if (proofVal > 0) updates.proof = proofVal;
                                else if (item.proof) updates.proof = item.proof;
                                if (enr.age) updates.age = sanitizeInput(enr.age, 20);
                                const msrpVal = parseFloat(enr.msrp); if (msrpVal > 0) updates.msrp = msrpVal;
                                else if (item.msrp) updates.msrp = item.msrp;
                                if (enr.finishType) updates.finish_type = sanitizeInput(enr.finishType, 100);
                                if (enr.batch) updates.batch = sanitizeInput(enr.batch, 100);
                                const mc = parseInt(enr.mashCorn); if (mc > 0) updates.mash_corn = mc;
                                const mw = parseInt(enr.mashWheat); if (mw > 0) updates.mash_wheat = mw;
                                const mr = parseInt(enr.mashRye); if (mr > 0) updates.mash_rye = mr;
                                const mb = parseInt(enr.mashBarley); if (mb > 0) updates.mash_barley = mb;
                                const mo = parseInt(enr.mashOther); if (mo > 0) updates.mash_other = mo;
                                const pMin = parseFloat(enr.proofMin); if (pMin > 0) updates.proof_min = pMin;
                                const pMax = parseFloat(enr.proofMax); if (pMax > 0) updates.proof_max = pMax;
                                if (enr.expressionType && enr.expressionType !== "standard") updates.expression_type = enr.expressionType;
                                else updates.expression_type = null;
                                updates.name_locked = true;
                                if (updates.mash_corn || updates.mash_rye || updates.mash_wheat || updates.mash_barley || updates.mash_other) updates.mashbill_source = "admin";
                                // Promote to visible on approval (don't downgrade curated/core)
                                if (!item.tier || item.tier === "archive") updates.tier = "visible";
                                if (useSupabase()) {
                                  const { error } = await getSupabase().from("bourbons").update(updates).eq("id", item.id);
                                  if (error) { showToast("\u26A0\uFE0F DB update failed"); return; }
                                }
                                const localUpdates = {
                                  submissionStatus: "approved", reviewedBy: currentUser?.uid, reviewedAt: new Date().toISOString(),
                                  ...(updates.name ? { name: updates.name } : {}),
                                  ...(updates.brand_id ? { brandId: updates.brand_id } : {}),
                                  ...(updates.facility_id ? { facilityId: updates.facility_id } : {}),
                                  ...(updates.type ? { type: updates.type } : {}),
                                  ...(updates.proof ? { proof: updates.proof } : {}),
                                  ...(updates.age ? { age: updates.age } : {}),
                                  ...(updates.msrp ? { msrp: updates.msrp } : {}),
                                  ...(updates.finish_type ? { finishType: updates.finish_type } : {}),
                                  ...(updates.batch ? { batch: updates.batch } : {}),
                                  ...(updates.mash_corn ? { mashCorn: updates.mash_corn } : {}),
                                  ...(updates.mash_wheat ? { mashWheat: updates.mash_wheat } : {}),
                                  ...(updates.mash_rye ? { mashRye: updates.mash_rye } : {}),
                                  ...(updates.mash_barley ? { mashBarley: updates.mash_barley } : {}),
                                  ...(updates.mash_other ? { mashOther: updates.mash_other } : {}),
                                  ...(updates.mashbill_source ? { mashbillSource: updates.mashbill_source } : {}),
                                  ...(updates.proof_min ? { proofMin: updates.proof_min } : {}),
                                  ...(updates.proof_max ? { proofMax: updates.proof_max } : {}),
                                  nameLocked: true,
                                  expressionType: updates.expression_type || null,
                                  ...(updates.tier ? { tier: updates.tier } : {}),
                                };
                                setBourbons(prev => prev.map(b => b.id === item.id ? { ...b, ...localUpdates } : b));
                                const enrichedFields = Object.keys(updates).filter(k => !["submission_status", "reviewed_by", "reviewed_at"].includes(k));
                                appendAudit(currentUser.uid, currentUser.name, "submission_approved", "submission", "Approved & enriched " + (updates.name || item._name) + (enrichedFields.length ? " [" + enrichedFields.join(", ") + "]" : ""), item.id);
                                showToast("\u2705 " + (updates.name || item._name) + " approved and enriched");
                                setEnrichingSubmission(null);
                              }}
                                style={{ width: "100%", padding: "12px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer", minHeight: 44, marginBottom: 8 }}>
                                {dupeMatches && dupeMatches.length > 0 ? "Approve as New" : "Confirm Approval"}
                              </button>
                              <button onClick={() => setEnrichingSubmission(null)}
                                style={{ width: "100%", padding: "8px", background: "none", border: "none", color: "#B5A898", fontSize: "0.75rem", cursor: "pointer" }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                      {/* ── EDIT REVIEW PANEL ── */}
                      {item._type === "edit" && reviewingEditId === item.id && (() => {
                        const bx = item._bourbon;
                        const sc = item.suggestedChanges || {};
                        const fieldLabels = { name: "Name", proof: "Proof", age: "Age", msrp: "MSRP", type: "Type", finishType: "Finish", batch: "Batch", upc: "UPC", mashCorn: "Corn %", mashWheat: "Wheat %", mashRye: "Rye %", mashBarley: "Barley %", mashOther: "Other %" };
                        return (
                          <div style={{ background: "#150F0A", border: "1px solid rgba(156,39,176,0.25)", borderRadius: 12, padding: 16, marginTop: 8 }}>
                            <div style={sectionLabelSmStyle}>Field-by-Field Comparison</div>
                            <div style={mb14Style}>
                              {Object.entries(sc).map(([key, val]) => (
                                <div key={key} style={{ display: "grid", gridTemplateColumns: "90px 1fr 20px 1fr", gap: 8, alignItems: "center", padding: "6px 0", borderBottom: "1px solid #2A2319" }}>
                                  <span style={{ fontSize: "0.6875rem", color: "#B5A898", fontWeight: 600 }}>{fieldLabels[key] || key}</span>
                                  <span style={{ fontSize: "0.75rem", color: "#CD5C5C", textDecoration: "line-through" }}>{bx?.[key] ?? "—"}</span>
                                  <span style={{ fontSize: "0.6875rem", color: "#5A4F3E", textAlign: "center" }}>{"\u2192"}</span>
                                  <span style={{ fontSize: "0.75rem", color: "#4CAF50" }}>{val ?? "—"}</span>
                                </div>
                              ))}
                            </div>
                            {item.reason && <div style={{ fontSize: "0.75rem", color: "#B5A898", marginBottom: 14, padding: "8px 12px", background: "rgba(156,39,176,0.05)", borderRadius: 8, border: "1px solid rgba(156,39,176,0.1)" }}>Reason: {item.reason}</div>}
                            <button onClick={async () => {
                              const dbUpdates = {};
                              if (sc.name) dbUpdates.name = sanitizeInput(sc.name, 100);
                              if (sc.proof) dbUpdates.proof = parseFloat(sc.proof);
                              if (sc.age) dbUpdates.age = sanitizeInput(sc.age, 20);
                              if (sc.msrp) dbUpdates.msrp = parseFloat(sc.msrp);
                              if (sc.type) dbUpdates.type = sanitizeInput(sc.type, 100);
                              if (sc.finishType) dbUpdates.finish_type = sanitizeInput(sc.finishType, 100);
                              if (sc.batch) dbUpdates.batch = sanitizeInput(sc.batch, 100);
                              if (sc.mashCorn) dbUpdates.mash_corn = parseInt(sc.mashCorn);
                              if (sc.mashWheat) dbUpdates.mash_wheat = parseInt(sc.mashWheat);
                              if (sc.mashRye) dbUpdates.mash_rye = parseInt(sc.mashRye);
                              if (sc.mashBarley) dbUpdates.mash_barley = parseInt(sc.mashBarley);
                              if (sc.mashOther) dbUpdates.mash_other = parseInt(sc.mashOther);
                              if (sc.mashCorn || sc.mashRye || sc.mashWheat || sc.mashBarley || sc.mashOther) dbUpdates.mashbill_source = "admin";
                              if (useSupabase()) {
                                const sbx = getSupabase();
                                if (Object.keys(dbUpdates).length > 0) {
                                  const { error } = await sbx.from("bourbons").update(dbUpdates).eq("id", item.bourbonId);
                                  if (error) { showToast("DB update failed"); return; }
                                }
                                if (sc.upc) {
                                  const { error: upcErr } = await sbx.from("upc_codes").insert({ expression_id: item.bourbonId, upc: sc.upc, source: "user_suggestion" });
                                  if (upcErr) logError("UPC insert:", upcErr);
                                }
                              }
                              const localUp = {};
                              if (sc.name) localUp.name = sc.name;
                              if (sc.proof) localUp.proof = parseFloat(sc.proof);
                              if (sc.age) localUp.age = sc.age;
                              if (sc.msrp) localUp.msrp = parseFloat(sc.msrp);
                              if (sc.type) localUp.type = sc.type;
                              if (sc.finishType) localUp.finishType = sc.finishType;
                              if (sc.batch) localUp.batch = sc.batch;
                              if (sc.mashCorn) localUp.mashCorn = parseInt(sc.mashCorn);
                              if (sc.mashWheat) localUp.mashWheat = parseInt(sc.mashWheat);
                              if (sc.mashRye) localUp.mashRye = parseInt(sc.mashRye);
                              if (sc.mashBarley) localUp.mashBarley = parseInt(sc.mashBarley);
                              if (sc.mashOther) localUp.mashOther = parseInt(sc.mashOther);
                              if (sc.mashCorn || sc.mashRye || sc.mashWheat || sc.mashBarley || sc.mashOther) localUp.mashbillSource = "admin";
                              setBourbons(prev => prev.map(bbn => bbn.id === item.bourbonId ? { ...bbn, ...localUp } : bbn));
                              const ok = await db.updateExpressionEditStatus(item.id, "applied", currentUser?.uid);
                              if (ok) {
                                setExpressionEdits(prev => prev.map(e => e.id === item.id ? { ...e, status: "applied", reviewedBy: currentUser?.uid, reviewedAt: new Date().toISOString() } : e));
                                appendAudit(currentUser.uid, currentUser.name, "edit_applied", "submission", "Applied edit for " + item._name + " [" + Object.keys(sc).join(", ") + "]", item.bourbonId);
                                showToast("Edit applied to " + item._name);
                                setReviewingEditId(null);
                              }
                            }}
                              style={{ width: "100%", padding: "12px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer", minHeight: 44 }}>
                              Apply {Object.keys(sc).length} Change{Object.keys(sc).length !== 1 ? "s" : ""}
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            )}
            <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0", margin: "0 0 12px" }}>Review History</h3>
            {recentActions.length === 0 ? (
              <div style={{ textAlign: "center", padding: "16px", color: "#B5A898", background: "#150F0A", borderRadius: 12, border: "1px solid #3D3428" }}>
                <p style={sMarFon6Style}>No reviews yet</p>
              </div>
            ) : recentActions.map(item => {

              const status = getSubmissionStatus(item);

              return (
                <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "#150F0A", borderRadius: 10, border: "1px solid #3D3428", marginBottom: 6, cursor: "pointer" }}
                  onClick={() => bourbons.some(b => b.id === item.id) ? openBourbonDetail(item) : openBrandDetail(item)}>
                  <span style={{ fontSize: "0.875rem", flexShrink: 0 }}>{status === "approved" ? "\u2705" : status === "rejected" ? "\u274C" : "\u23F3"}</span>
                  <div style={flexMinW0Style}>
                    <div style={{ fontSize: "0.8125rem", color: "#F5E6D0", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
                    <div style={sFonCol3Style}>
                      {status} by {reviewer(item.reviewedBy)} · {item.reviewedAt?.slice(0, 10)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
      {/* ═══════ CATALOG DRILLDOWN ═══════ */}
      {adminTab === "catalog" && (() => {

        const sq = adminSearch.toLowerCase();

        const isDist = adminCatalogType === "brands";

        const items = isDist
          ? brands.filter(d => !sq || d.name.toLowerCase().includes(sq) || d.location?.toLowerCase().includes(sq)).sort((a, b) => a.name.localeCompare(b.name))
          : adminCatalogType === "expressions"
            ? bourbons.filter(b => !sq || b.name.toLowerCase().includes(sq) || getBrand(b.brandId)?.name.toLowerCase().includes(sq)).sort((a, b) => a.name.localeCompare(b.name))
            : [];

        return (
          <div>
            <div style={{ display: "flex", gap: 0, marginBottom: 12, borderRadius: 8, overflow: "hidden", border: "1px solid #3D3428" }}>
              {["expressions", "brands", "upc"].map(t => (
                <button key={t} onClick={() => { setAdminCatalogType(t); setAdminSearch(""); }}
                  style={{ flex: 1, padding: "9px 0", background: adminCatalogType === t ? "#3D3428" : "transparent", border: "none", color: adminCatalogType === t ? "#F5E6D0" : "#A69880", fontSize: "0.75rem", fontWeight: adminCatalogType === t ? 700 : 400, cursor: "pointer", borderBottom: adminCatalogType === t ? "2px solid #D4A053" : "2px solid transparent", textTransform: "capitalize" }}>
                  {t === "upc" ? "UPC" : t} ({t === "expressions" ? bourbons.length : t === "brands" ? brands.length : upcCodes.length})
                </button>
              ))}
            </div>
            {adminCatalogType !== "upc" && (<>
              <div style={posRelMb12Style}>
                <span style={searchIconStyle}>{"🔍"}</span>
                <input value={adminSearch} onChange={e => setAdminSearch(e.target.value)} aria-label="Search catalog" placeholder={isDist ? "Search brands..." : "Search expressions..."}
                  style={searchInputIconStyle} />
              </div>
              <div style={{ fontSize: "0.6875rem", color: "#B5A898", marginBottom: 10 }}>{items.length} result{items.length !== 1 ? "s" : ""} — tap to edit</div>
              {items.slice(0, 50).map(item => (
                <div key={item.id} onClick={() => isDist ? openBrandDetail(item) : openBourbonDetail(item)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "#150F0A", borderRadius: 10, border: "1px solid #3D3428", marginBottom: 6, cursor: "pointer" }}>
                  <div style={flexMinW0Style}>
                    <div style={{ fontSize: "0.8125rem", color: "#F5E6D0", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
                      {item.name}
                      {isDist && item.brandType && item.brandType !== "distiller" && (
                        <span style={{ fontSize: "0.5rem", padding: "1px 5px", borderRadius: 3, background: item.brandType === "ndp" ? "#2A1F14" : "#1A2A1F", color: item.brandType === "ndp" ? "#D4A053" : "#7DCEA0", border: `1px solid ${item.brandType === "ndp" ? "#3A2F1E" : "#2A3A2F"}`, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5, flexShrink: 0 }}>{item.brandType}</span>
                      )}
                    </div>
                    <div style={{ fontSize: "0.6875rem", color: "#B5A898", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {isDist
                        ? [item.location || facilityMap.get(item.facilityId)?.state, item.est ? "Est. " + item.est : null, item.brandType === "distiller" && item.facilityId ? item.facilityId : (item.brandType && item.brandType !== "distiller") ? item.brandType.toUpperCase() : null].filter(Boolean).join(" \u00B7 ")
                        : [getBrand(item.brandId)?.name, item.proof ? item.proof + "\u00B0" : null, item.type].filter(Boolean).join(" \u00B7 ")

                      }

                    </div>
                  </div>
                  <span style={{ color: "#5A4F3E", fontSize: "0.75rem", flexShrink: 0 }}>{"\u270F\uFE0F"}</span>
                </div>
              ))}
              {items.length > 50 && <p style={{ textAlign: "center", color: "#A69880", fontSize: "0.6875rem", marginTop: 8 }}>Showing 50 of {items.length} — use search to narrow</p>}
              {items.length === 0 && <p style={{ textAlign: "center", color: "#A69880", fontSize: "0.8125rem" }}>No matching {isDist ? "brands" : "expressions"}</p>}
            </>)}
            {adminCatalogType === "upc" && (() => {

              const searched = adminSearch
                ? upcCodes.filter(u => u.upc.includes(adminSearch) || (u.expression_name || "").toLowerCase().includes(adminSearch.toLowerCase()))
                : upcCodes;

              return (<>
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  {[{ label: "Total", val: upcCodes.length }, { label: "Verified", val: upcCodes.filter(u => u.verified).length }, { label: "Unverified", val: upcCodes.filter(u => !u.verified).length }].map(k => (
                    <div key={k.label} style={{ flex: 1, background: "#150F0A", borderRadius: 8, padding: "10px 8px", textAlign: "center", border: "1px solid #3D3428" }}>
                      <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#D4A053" }}>{k.val}</div>
                      <div style={{ fontSize: "0.5625rem", color: "#A69880", textTransform: "uppercase", letterSpacing: 0.5 }}>{k.label}</div>
                    </div>
                  ))}
                </div>
                <input value={adminSearch} onChange={e => setAdminSearch(e.target.value)} aria-label="Search UPC" placeholder="Search UPC, expression..." style={{ width: "100%", padding: "10px 14px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 8, color: "#F5E6D0", fontSize: "0.8125rem", marginBottom: 10, boxSizing: "border-box" }} />
                <div style={{ background: "#150F0A", borderRadius: 10, padding: 14, marginBottom: 14, border: "1px solid #3D3428" }}>
                  <div style={{ fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8, fontWeight: 600 }}>Add UPC</div>
                  <div style={flexGap6WrapStyle}>
                    <input value={adminUpcForm.upc} onChange={e => setAdminUpcForm(p => ({ ...p, upc: e.target.value.replace(/\D/g, "").slice(0, 14) }))} aria-label="UPC code" placeholder="UPC" style={{ flex: "1 1 100px", padding: "7px 10px", background: "#1A1510", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.75rem", fontFamily: "monospace" }} />
                    <input value={adminUpcForm.name} onChange={e => setAdminUpcForm(p => ({ ...p, name: e.target.value }))} aria-label="Expression name" placeholder="Expression" style={{ flex: "2 1 140px", padding: "7px 10px", background: "#1A1510", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.75rem" }} />
                    <input value={adminUpcForm.distillery} onChange={e => setAdminUpcForm(p => ({ ...p, distillery: e.target.value }))} aria-label="Distillery name" placeholder="Distillery" style={{ flex: "1 1 100px", padding: "7px 10px", background: "#1A1510", border: "1px solid #3D3428", borderRadius: 6, color: "#F5E6D0", fontSize: "0.75rem" }} />
                    <button onClick={async () => {

                      const { upc, name, distillery } = adminUpcForm;
                      if (!upc || upc.length < 8) { showToast("\u26A0\uFE0F Enter a valid UPC (8+ digits)"); return; }
                      if (!name.trim()) { showToast("\u26A0\uFE0F Expression name required"); return; }
                      if (upcCodes.some(u => u.upc === upc)) { showToast("\u26A0\uFE0F UPC already exists"); return; }

                      const exprMatch = bourbons.find(b => b.name.toLowerCase() === name.trim().toLowerCase());

                      const newUpc = { id: generateId("upc_"), upc, expression_id: exprMatch?.id || null, expression_name: sanitizeInput(name.trim(), 100), distillery_name: sanitizeInput(distillery.trim(), 100), submitted_by: currentUser?.uid, verified: true, created_at: new Date().toISOString() };
                      setUpcCodes(prev => [...prev, newUpc]);

                      if (useSupabase()) {

                        const dbRow = await db.insertUPC(upc, exprMatch?.id || null, sanitizeInput(name.trim(), 100), sanitizeInput(distillery.trim(), 100), currentUser?.uid);
                        if (dbRow) { setUpcCodes(prev => prev.map(u => u.id === newUpc.id ? { ...u, id: dbRow.id } : u)); await db.verifyUPC(dbRow.id, true); }

                      }

                      appendAudit(currentUser.uid, currentUser.name, "upc_added", "upc", "Added UPC " + upc + " \u2192 " + name.trim(), newUpc.id);
                      setAdminUpcForm({ upc: "", name: "", distillery: "" });

                      showToast("\u2705 UPC added");
                    }} style={{ padding: "7px 14px", background: GRAD_PRIMARY, border: "none", borderRadius: 6, color: "#FFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>+ Add</button>
                  </div>
                </div>
                {searched.length === 0 && <p style={{ color: "#A69880", textAlign: "center", fontSize: "0.8125rem" }}>{adminSearch ? "No matching UPCs" : "No UPC codes"}</p>}
                {searched.sort((a, b) => (a.expression_name || "").localeCompare(b.expression_name || "")).map(u => (
                  <div key={u.id} style={{ background: "#1A1510", borderRadius: 10, padding: "10px 14px", marginBottom: 6, border: "1px solid #3D3428", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                    <div style={flexMinW0Style}>
                      <div style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#F5E6D0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.expression_name || getBourbon(u.expression_id)?.name || "Unknown"}</div>
                      <div style={sFonCol3Style}>{u.distillery_name || "\u2014"} · <span style={{ fontFamily: "monospace" }}>{u.upc}</span> · <span style={{ color: u.verified ? "#4CAF50" : "#FF9800" }}>{u.verified ? "\u2713" : "\u23F3"}</span></div>
                    </div>
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      {!u.verified && <button onClick={async () => { if (useSupabase()) await db.verifyUPC(u.id, true); setUpcCodes(prev => prev.map(x => x.id === u.id ? { ...x, verified: true } : x)); appendAudit(currentUser.uid, currentUser.name, "upc_verified", "upc", "Verified UPC " + u.upc, u.id); }} style={{ padding: "8px 12px", background: "rgba(76,175,80,0.1)", border: "1px solid rgba(76,175,80,0.3)", borderRadius: 6, color: "#4CAF50", fontSize: "0.625rem", fontWeight: 700, cursor: "pointer" }}>Verify</button>}
                      {u.verified && <button onClick={async () => { if (useSupabase()) await db.verifyUPC(u.id, false); setUpcCodes(prev => prev.map(x => x.id === u.id ? { ...x, verified: false } : x)); }} style={{ padding: "8px 12px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 6, color: "#B5A898", fontSize: "0.625rem", cursor: "pointer" }}>Unverify</button>}
                      <button onClick={async () => { if (confirm("Delete UPC " + u.upc + "?")) { if (useSupabase()) await db.deleteUPC(u.id); setUpcCodes(prev => prev.filter(x => x.id !== u.id)); appendAudit(currentUser.uid, currentUser.name, "upc_deleted", "upc", "Deleted UPC " + u.upc, u.id); } }} style={{ padding: "8px 12px", background: "rgba(205,92,92,0.08)", border: "1px solid rgba(205,92,92,0.2)", borderRadius: 6, color: "#CD5C5C", fontSize: "0.625rem", cursor: "pointer" }}>Del</button>
                    </div>
                  </div>
                ))}
              </>);
            })()}
          </div>
        );
      })()}
      {/* ═══════ DATA QUALITY DRILLDOWN ═══════ */}
      {adminTab === "quality" && (() => {

        const sq = adminSearch.toLowerCase();

        const qualityFilter = adminCatalogType;

        const qualityItems = qualityFilter === "orphaned" ? orphanedBourbons
          : qualityFilter === "no_proof" ? noProof
          : qualityFilter === "no_type" ? noType
          : qualityFilter === "no_image" ? noImage
          : orphanedBourbons;

        const filtered = sq ? qualityItems.filter(b => b.name.toLowerCase().includes(sq)) : qualityItems;

        return (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 14 }}>
              {[
                { key: "orphaned", label: "No Brand", val: orphanedBourbons.length, color: "#E88B5A" },
                { key: "no_proof", label: "No Proof", val: noProof.length, color: "#D4A053" },
                { key: "no_type", label: "No Type", val: noType.length, color: "#6B9BD2" },
                { key: "no_image", label: "No Image", val: noImage.length, color: "#9B7ED8" },
              ].map(q => (
                <div key={q.key} onClick={() => { setAdminCatalogType(q.key); setAdminSearch(""); }}
                  style={{ background: adminCatalogType === q.key ? "#3D3428" : "#1E1A14", borderRadius: 10, padding: "12px 10px", textAlign: "center", border: `1px solid ${adminCatalogType === q.key ? q.color : "#3D3428"}`, cursor: "pointer" }}>
                  <div style={{ fontSize: "1.25rem", fontWeight: 700, color: q.color, fontFamily: FONT_DISPLAY }}>{q.val}</div>
                  <div style={{ fontSize: "0.625rem", color: "#B5A898", marginTop: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>{q.label}</div>
                  <div style={faintText05625Mt2Style}>{Math.round(q.val / bourbons.length * 100)}% of catalog</div>
                </div>
              ))}
            </div>
            <div style={posRelMb12Style}>
              <span style={searchIconStyle}>{"🔍"}</span>
              <input value={adminSearch} onChange={e => setAdminSearch(e.target.value)} aria-label="Search" placeholder="Search..."
                style={searchInputIconStyle} />
            </div>
            <div style={{ fontSize: "0.6875rem", color: "#B5A898", marginBottom: 10 }}>{filtered.length} items — tap to edit</div>
            {filtered.slice(0, 50).map(item => (
              <div key={item.id} onClick={() => openBourbonDetail(item)}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "#150F0A", borderRadius: 10, border: "1px solid #3D3428", marginBottom: 6, cursor: "pointer" }}>
                <div style={flexMinW0Style}>
                  <div style={{ fontSize: "0.8125rem", color: "#F5E6D0", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
                  <div style={{ fontSize: "0.6875rem", color: "#B5A898", marginTop: 2 }}>
                    {[getBrand(item.brandId)?.name || (qualityFilter === "orphaned" ? "\u26A0 no brand" : ""), item.proof ? item.proof + "\u00B0" : (qualityFilter === "no_proof" ? "\u26A0 no proof" : ""), item.type || (qualityFilter === "no_type" ? "\u26A0 no type" : "")].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <span style={{ color: "#5A4F3E", fontSize: "0.75rem", flexShrink: 0 }}>{"\u270F\uFE0F"}</span>
              </div>
            ))}
            {filtered.length > 50 && <p style={{ textAlign: "center", color: "#A69880", fontSize: "0.6875rem", marginTop: 8 }}>Showing 50 of {filtered.length}</p>}
          </div>
        );
      })()}
      {/* ═══════ USERS DRILLDOWN ═══════ */}
      {adminTab === "users" && (() => {

        const filteredUsers = platformUsers.filter(u => !adminSearch || u.name.toLowerCase().includes(adminSearch.toLowerCase()) || u.email.toLowerCase().includes(adminSearch.toLowerCase()));

        return (
        <div>
          <div style={posRelMb12Style}>
            <span style={searchIconStyle}>{"🔍"}</span>
            <input value={adminSearch} onChange={e => setAdminSearch(e.target.value)} aria-label="Search users" placeholder="Search users..."
              style={searchInputIconStyle} />
          </div>
          {filteredUsers.map(u => {

            const adminCount = platformUsers.filter(x => x.role === "admin" && x.status === "active").length;

            const isLastAdmin = u.role === "admin" && adminCount <= 1;

            const isSelf = u.uid === currentUser.uid;

            return (
            <div key={u.uid} style={{ background: "#1E1A14", borderRadius: 10, padding: 14, marginBottom: 8, border: "1px solid #3D3428" }}>
              <div style={flexBetweenStartStyle}>
                <div style={flexOneStyle}>
                  <div style={flexCenterGap6WrapStyle}>
                    <span style={lightText0875BoldStyle}>{u.name}</span>
                    <span style={{ fontSize: "0.5625rem", fontWeight: 700, padding: "2px 6px", borderRadius: 4, textTransform: "uppercase", letterSpacing: 0.5,
                      background: u.role === "admin" ? "rgba(212,160,83,0.15)" : "rgba(90,78,62,0.15)",
                      color: u.role === "admin" ? "#D4A053" : "#B5A898",
                      border: "1px solid " + (u.role === "admin" ? "rgba(212,160,83,0.3)" : "rgba(90,78,62,0.3)") }}>{u.role}</span>
                    <span style={{ fontSize: "0.5625rem", fontWeight: 700, padding: "2px 6px", borderRadius: 4, textTransform: "uppercase", color: aStatusColor(u.status), background: aStatusColor(u.status) + "18", border: "1px solid " + aStatusColor(u.status) + "40" }}>{u.status}</span>
                    {isSelf && <span style={{ fontSize: "0.5625rem", color: "#B5A898" }}>(you)</span>}
                  </div>
                  <div style={{ color: "#B5A898", fontSize: "0.75rem", marginTop: 3 }}>{u.email}{!u.emailVerified ? " \u00B7 \u26A0 unverified" : ""}</div>
                  <div style={mutedText06875Mt3Style}>
                    Joined {u.createdAt} · Last {new Date(u.lastLogin).toLocaleDateString()}
                    {u.flaggedContent > 0 && <span style={{ color: "#CD5C5C", marginLeft: 6 }}>{"\u26A0\uFE0F"} {u.flaggedContent} flag{u.flaggedContent > 1 ? "s" : ""}</span>}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 10, paddingTop: 10, borderTop: "1px solid #3D3428", flexWrap: "wrap" }}>
                {u.role === "user" && !isSelf && (
                  <button onClick={async () => { if (!window.confirm("Promote " + u.name + " to admin?")) return; if (useSupabase()) await db.updateUserRole(u.uid, "admin"); setPlatformUsers(prev => prev.map(x => x.uid === u.uid ? { ...x, role: "admin" } : x)); appendAudit(currentUser.uid, currentUser.name, "role_promoted", "role", "Promoted " + u.name + " to admin", u.uid); }}
                    style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #D4A053", background: "rgba(212,160,83,0.1)", color: "#D4A053", fontSize: "0.625rem", fontWeight: 700, cursor: "pointer" }}>{"\u2B06"} Promote</button>
                )}
                {u.role === "admin" && !isSelf && !isLastAdmin && (
                  <button onClick={async () => { if (!window.confirm("Demote " + u.name + " from admin?")) return; if (useSupabase()) await db.updateUserRole(u.uid, "user"); setPlatformUsers(prev => prev.map(x => x.uid === u.uid ? { ...x, role: "user" } : x)); appendAudit(currentUser.uid, currentUser.name, "role_demoted", "role", "Demoted " + u.name, u.uid); }}
                    style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #E88B5A", background: "rgba(232,139,90,0.1)", color: "#E88B5A", fontSize: "0.625rem", fontWeight: 700, cursor: "pointer" }}>{"\u2B07"} Demote</button>
                )}
                {isLastAdmin && isSelf && <span style={{ padding: "4px 10px", fontSize: "0.625rem", color: "#B5A898", fontStyle: "italic" }}>Last admin</span>}
                {u.status === "active" && !isSelf && (
                  <button onClick={async () => { if (!window.confirm("Suspend " + u.name + "? They will be locked out.")) return; if (useSupabase()) await db.updateUserStatus(u.uid, "suspended"); setPlatformUsers(prev => prev.map(x => x.uid === u.uid ? { ...x, status: "suspended" } : x)); appendAudit(currentUser.uid, currentUser.name, "user_suspended", "moderation", "Suspended " + u.name, u.uid); }}
                    style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #CD5C5C", background: "rgba(205,92,92,0.1)", color: "#CD5C5C", fontSize: "0.625rem", fontWeight: 700, cursor: "pointer" }}>Suspend</button>
                )}
                {u.status === "suspended" && (
                  <button onClick={async () => { if (useSupabase()) await db.updateUserStatus(u.uid, "active"); setPlatformUsers(prev => prev.map(x => x.uid === u.uid ? { ...x, status: "active" } : x)); appendAudit(currentUser.uid, currentUser.name, "user_reinstated", "moderation", "Reinstated " + u.name, u.uid); }}
                    style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #4CAF50", background: "rgba(76,175,80,0.1)", color: "#4CAF50", fontSize: "0.625rem", fontWeight: 700, cursor: "pointer" }}>Reinstate</button>
                )}
              </div>
            </div>
            );
          })}
        </div>
        );
      })()}
      {/* ═══════ MODERATION DRILLDOWN (Flags + Claims) ═══════ */}
      {adminTab === "moderation" && (() => {

        const sq = adminSearch.toLowerCase();

        const filteredClaimsList = adminClaimFilter === "all" ? businessClaims : businessClaims.filter(c => c.status === adminClaimFilter);

        const filteredFlagsList = (adminFlagFilter === "all" ? flaggedContent : flaggedContent.filter(f => f.status === adminFlagFilter))
          .filter(f => !sq || f.userName?.toLowerCase().includes(sq) || f.content?.toLowerCase().includes(sq));

        return (
        <div>
          <div style={{ display: "flex", gap: 0, marginBottom: 14, borderRadius: 8, overflow: "hidden", border: "1px solid #3D3428" }}>
            {[{ key: "flags", label: "Flags", count: pendingFlags.length }, { key: "claims", label: "Claims", count: pendingClaims.length }].map(s => (
              <button key={s.key} onClick={() => { setAdminModSection(s.key); setAdminSearch(""); }}
                style={{ flex: 1, padding: "9px 0", background: adminModSection === s.key ? "#3D3428" : "transparent", border: "none", color: adminModSection === s.key ? "#F5E6D0" : "#A69880", fontSize: "0.75rem", fontWeight: adminModSection === s.key ? 700 : 400, cursor: "pointer", borderBottom: adminModSection === s.key ? "2px solid #D4A053" : "2px solid transparent", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                {s.label}
                {s.count > 0 && <span style={{ background: "#D4A053", color: "#1A1510", fontSize: "0.5625rem", fontWeight: 800, padding: "1px 6px", borderRadius: 8, minWidth: 14, textAlign: "center" }}>{s.count}</span>}
              </button>
            ))}
          </div>
          {adminModSection === "flags" && (<>
            <div style={posRelMb10Style}>
              <span style={searchIconStyle}>{"🔍"}</span>
              <input value={adminSearch} onChange={e => setAdminSearch(e.target.value)} aria-label="Search flags" placeholder="Search flags..."
                style={searchInputIconStyle} />
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
              {["pending", "actioned", "approved", "all"].map(f => (
                <button key={f} onClick={() => setAdminFlagFilter(f)}
                  style={{ padding: "5px 12px", borderRadius: 14, fontSize: "0.6875rem", cursor: "pointer", border: "1px solid", background: adminFlagFilter === f ? "rgba(212,160,83,0.15)" : "transparent", color: adminFlagFilter === f ? "#D4A053" : "#A69880", borderColor: adminFlagFilter === f ? "#D4A053" : "#3D3428", fontWeight: adminFlagFilter === f ? 700 : 400, textTransform: "capitalize" }}>{f} ({f === "all" ? flaggedContent.length : flaggedContent.filter(x => x.status === f).length})</button>
              ))}
            </div>
            {filteredFlagsList.length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px 16px", color: "#B5A898", background: "#150F0A", borderRadius: 12, border: "1px solid #3D3428" }}>
                <p style={sMarFon6Style}>No flagged content</p>
              </div>
            ) : filteredFlagsList.map(f => (
              <div key={f.id} style={{ background: "#1E1A14", borderRadius: 10, padding: 14, marginBottom: 8, border: "1px solid " + (f.status === "pending" ? "rgba(205,92,92,0.2)" : "#3D3428") }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={lightText08125BoldStyle}>{f.userName}</span>
                  <span style={{ fontSize: "0.625rem", padding: "2px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", color: f.status === "pending" ? "#D4A053" : f.status === "actioned" ? "#CD5C5C" : "#4CAF50", background: "rgba(0,0,0,0.3)" }}>{f.status}</span>
                </div>
                <div style={{ color: "#B5A898", fontSize: "0.6875rem", marginBottom: 6 }}>{f.type?.replace(/_/g, " ")} · {new Date(f.flaggedAt).toLocaleDateString()}</div>
                <div style={{ color: "#A69880", fontSize: "0.8125rem", padding: "8px 10px", background: "#150F0A", borderRadius: 8, marginBottom: 8 }}>"{f.content}"</div>
                {f.flaggedWords?.length > 0 && (
                  <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
                    {f.flaggedWords.map(w => <span key={w} style={{ padding: "2px 6px", borderRadius: 4, background: "rgba(205,92,92,0.1)", color: "#CD5C5C", fontSize: "0.625rem", border: "1px solid rgba(205,92,92,0.2)" }}>{w}</span>)}
                  </div>
                )}
                {f.status === "pending" && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={async () => { if (useSupabase()) await db.updateFlagStatus(f.id, "approved", currentUser.uid); setFlaggedContent(prev => prev.map(x => x.id === f.id ? { ...x, status: "approved", reviewedBy: currentUser.uid } : x)); appendAudit(currentUser.uid, currentUser.name, "content_approved", "moderation", "Approved content from " + f.userName, f.id); }}
                      style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #4CAF50", background: "rgba(76,175,80,0.1)", color: "#4CAF50", fontSize: "0.6875rem", fontWeight: 700, cursor: "pointer" }}>Approve</button>
                    <button onClick={async () => { if (useSupabase()) { await db.updateFlagStatus(f.id, "actioned", currentUser.uid, "removed"); await db.incrementFlagCount(f.userId); } setFlaggedContent(prev => prev.map(x => x.id === f.id ? { ...x, status: "actioned", reviewedBy: currentUser.uid, actionTaken: "removed" } : x)); setPlatformUsers(prev => prev.map(u => u.uid === f.userId ? { ...u, flaggedContent: (u.flaggedContent || 0) + 1 } : u)); appendAudit(currentUser.uid, currentUser.name, "content_removed", "moderation", "Removed: " + (f.flaggedWords || []).join(", "), f.id); }}
                      style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #CD5C5C", background: "rgba(205,92,92,0.1)", color: "#CD5C5C", fontSize: "0.6875rem", fontWeight: 700, cursor: "pointer" }}>Remove</button>
                    <button onClick={async () => { if (useSupabase()) { await db.updateFlagStatus(f.id, "actioned", currentUser.uid, "warn+remove"); await db.incrementFlagCount(f.userId); } setFlaggedContent(prev => prev.map(x => x.id === f.id ? { ...x, status: "actioned", reviewedBy: currentUser.uid, actionTaken: "warn+remove" } : x)); setPlatformUsers(prev => prev.map(u => u.uid === f.userId ? { ...u, flaggedContent: (u.flaggedContent || 0) + 1 } : u)); appendAudit(currentUser.uid, currentUser.name, "user_warned", "moderation", "Warned " + f.userName, f.userId); }}
                      style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #E88B5A", background: "rgba(232,139,90,0.1)", color: "#E88B5A", fontSize: "0.6875rem", fontWeight: 700, cursor: "pointer" }}>Warn</button>
                  </div>
                )}
              </div>
            ))}
          </>)}
          {adminModSection === "claims" && (<>
            <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
              {["all", "submitted", "approved", "rejected", "disputed"].map(f => (
                <button key={f} onClick={() => setAdminClaimFilter(f)}
                  style={{ padding: "5px 12px", borderRadius: 14, fontSize: "0.6875rem", cursor: "pointer", border: "1px solid", background: adminClaimFilter === f ? "rgba(212,160,83,0.15)" : "transparent", color: adminClaimFilter === f ? "#D4A053" : "#A69880", borderColor: adminClaimFilter === f ? "#D4A053" : "#3D3428", fontWeight: adminClaimFilter === f ? 700 : 400, textTransform: "capitalize" }}>{f} ({f === "all" ? businessClaims.length : businessClaims.filter(c => c.status === f).length})</button>
              ))}
            </div>
            {filteredClaimsList.length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px 16px", color: "#B5A898", background: "#150F0A", borderRadius: 12, border: "1px solid #3D3428" }}>
                <p style={sMarFon6Style}>No claims</p>
              </div>
            ) : filteredClaimsList.map(cl => (
              <div key={cl.id} style={{ background: "#1E1A14", borderRadius: 10, padding: 14, marginBottom: 8, border: "1px solid #3D3428" }}>
                <div style={flexBetweenStartStyle}>
                  <div>
                    <div style={lightText0875BoldStyle}>{cl.businessName}</div>
                    <div style={mutedText075Mt2Style}>{cl.claimantName} · {cl.verificationMethod || "\u2014"} · {cl.submittedAt?.slice(0, 10)}</div>
                    {cl.notes && <div style={{ color: "#A69880", fontSize: "0.6875rem", marginTop: 4, fontStyle: "italic" }}>{cl.notes}</div>}
                  </div>
                  <span style={{ fontSize: "0.625rem", padding: "3px 8px", borderRadius: 6, fontWeight: 700, textTransform: "uppercase", color: claimStatusColor(cl.status), background: "rgba(0,0,0,0.3)", border: "1px solid " + claimStatusColor(cl.status) + "40", flexShrink: 0 }}>{cl.status}</span>
                </div>
                {(cl.status === "submitted" || cl.status === "disputed") && (
                  <div style={{ display: "flex", gap: 6, marginTop: 10, borderTop: "1px solid #3D3428", paddingTop: 10 }}>
                    <button onClick={async () => { if (useSupabase()) { await db.updateClaimStatus(cl.id, "approved", currentUser.uid); await db.insertBusinessRole(cl.businessId, cl.claimantUid, "owner", "system"); } setBusinessClaims(prev => prev.map(c => c.id === cl.id ? { ...c, status: "approved", reviewedAt: new Date().toISOString(), reviewedBy: currentUser.uid } : c)); setBusinessRoles(prev => [...prev, { businessId: cl.businessId, uid: cl.claimantUid, role: "owner", grantedBy: "system", grantedAt: new Date().toISOString() }]); appendAudit(currentUser.uid, currentUser.name, "claim_approved", "claim", "Approved: " + cl.businessName, cl.id); }}
                      style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #4CAF50", background: "rgba(76,175,80,0.1)", color: "#4CAF50", fontSize: "0.6875rem", fontWeight: 700, cursor: "pointer" }}>Approve</button>
                    <button onClick={async () => { if (useSupabase()) await db.updateClaimStatus(cl.id, "rejected", currentUser.uid); setBusinessClaims(prev => prev.map(c => c.id === cl.id ? { ...c, status: "rejected", reviewedAt: new Date().toISOString(), reviewedBy: currentUser.uid } : c)); appendAudit(currentUser.uid, currentUser.name, "claim_rejected", "claim", "Rejected: " + cl.businessName, cl.id); }}
                      style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #CD5C5C", background: "rgba(205,92,92,0.1)", color: "#CD5C5C", fontSize: "0.6875rem", fontWeight: 700, cursor: "pointer" }}>Reject</button>
                    {cl.status === "disputed" && (
                      <button onClick={async () => { const oldOwnerRole = businessRoles.find(r => r.businessId === cl.businessId && r.role === "owner"); if (useSupabase()) { if (oldOwnerRole?.id) await db.deleteBusinessRole(oldOwnerRole.id); await db.insertBusinessRole(cl.businessId, cl.claimantUid, "owner", currentUser.uid); await db.updateClaimStatus(cl.id, "approved", currentUser.uid); } setBusinessRoles(prev => prev.filter(r => !(r.businessId === cl.businessId && r.role === "owner")).concat([{ businessId: cl.businessId, uid: cl.claimantUid, role: "owner", grantedBy: currentUser.uid, grantedAt: new Date().toISOString() }])); setBusinessClaims(prev => prev.map(c => c.id === cl.id ? { ...c, status: "approved", reviewedAt: new Date().toISOString(), reviewedBy: currentUser.uid } : c)); appendAudit(currentUser.uid, currentUser.name, "ownership_transferred", "claim", "Transferred " + cl.businessName, cl.id); }}
                        style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #E88B5A", background: "rgba(232,139,90,0.1)", color: "#E88B5A", fontSize: "0.6875rem", fontWeight: 700, cursor: "pointer" }}>Transfer</button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </>)}
        </div>
      ); })()}
      {/* ═══════ AUDIT LOG DRILLDOWN ═══════ */}
      {adminTab === "audit" && (() => {

        const sq = adminSearch.toLowerCase();

        const filteredAudit = adminAuditFilter === "all" ? auditLog : auditLog.filter(a => a.category === adminAuditFilter);

        const rangeMs = { "7d": 7*86400000, "30d": 30*86400000, "90d": 90*86400000 }[adminAuditRange];

        const cutoff = rangeMs ? Date.now() - rangeMs : 0;

        const searchedAudit = filteredAudit.filter(a =>
          (!sq || a.actorName?.toLowerCase().includes(sq) || a.detail?.toLowerCase().includes(sq) || a.action?.toLowerCase().includes(sq)) &&
          (!cutoff || new Date(a.ts).getTime() >= cutoff)
        );

        return (
        <div>
          <div style={posRelMb10Style}>
            <span style={searchIconStyle}>{"🔍"}</span>
            <input value={adminSearch} onChange={e => setAdminSearch(e.target.value)} aria-label="Search audit log" placeholder="Search audit log..."
              style={searchInputIconStyle} />
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
            {["all", "auth", "claim", "role", "moderation", "catalog", "submission"].map(f => (
              <button key={f} onClick={() => setAdminAuditFilter(f)}
                style={{ padding: "5px 12px", borderRadius: 14, fontSize: "0.6875rem", cursor: "pointer", border: "1px solid", background: adminAuditFilter === f ? "rgba(212,160,83,0.15)" : "transparent", color: adminAuditFilter === f ? "#D4A053" : "#A69880", borderColor: adminAuditFilter === f ? "#D4A053" : "#3D3428", fontWeight: adminAuditFilter === f ? 700 : 400, textTransform: "capitalize" }}>{f}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {["all", "7d", "30d", "90d"].map(r => (
              <button key={r} onClick={() => setAdminAuditRange(r)}
                style={{ padding: "4px 10px", borderRadius: 12, fontSize: "0.625rem", cursor: "pointer", border: "1px solid", background: adminAuditRange === r ? "rgba(107,155,210,0.15)" : "transparent", color: adminAuditRange === r ? "#6B9BD2" : "#A69880", borderColor: adminAuditRange === r ? "#6B9BD2" : "#3D3428", fontWeight: adminAuditRange === r ? 700 : 400 }}>{r === "all" ? "All Time" : "Last " + r}</button>
            ))}
          </div>
          {[...searchedAudit].reverse().map(a => (
            <div key={a.id} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: "1px solid #1A1510" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: auditCatColor(a.category), marginTop: 5, flexShrink: 0 }} />
              <div style={flexOneStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                  <span style={{ color: "#F5E6D0", fontSize: "0.75rem", fontWeight: 600 }}>{a.actorName}</span>
                  <span style={{ color: "#B5A898", fontSize: "0.625rem" }}>{new Date(a.ts).toLocaleString()}</span>
                </div>
                <div style={mutedText075Style}>{a.detail}</div>
                <div style={{ display: "flex", gap: 6, marginTop: 3 }}>
                  <span style={{ fontSize: "0.5625rem", padding: "1px 6px", borderRadius: 3, color: auditCatColor(a.category), background: auditCatColor(a.category) + "15", border: "1px solid " + auditCatColor(a.category) + "30", textTransform: "uppercase" }}>{a.category}</span>
                  <span style={{ fontSize: "0.5625rem", color: "#B5A898" }}>{a.action}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ); })()}
      {/* ═══════ DUPLICATES MERGE TOOL ═══════ */}
      {adminTab === "duplicates" && (() => {
        const sq = adminSearch.toLowerCase();
        const filtered = sq ? adminDuplicates.filter(p => {
          const wn = (p.winner.name || "").toLowerCase();
          const ln = (p.loser.name || "").toLowerCase();
          const wb = (brands.find(br => br.id === p.winner.brandId)?.name || "").toLowerCase();
          return wn.includes(sq) || ln.includes(sq) || wb.includes(sq);
        }) : adminDuplicates;
        const handleMerge = async (pair) => {
          const res = await db.mergeDuplicates(pair.winner.id, pair.loser.id);
          if (res.error) { showToast("Merge failed: " + res.error); return; }
          const brandName = brands.find(br => br.id === pair.winner.brandId)?.name || "Unknown";
          db.insertAudit(currentUser?.uid, currentUser?.name || "Admin", "merge_duplicate", "catalog", "Merged \"" + pair.loser.name + "\" into \"" + pair.winner.name + "\" (" + brandName + ")", pair.winner.id);
          setBourbons(prev => prev.map(b => b.id === pair.loser.id ? { ...b, tier: "hidden", parentExpressionId: pair.winner.id } : b));
          showToast("Merged into " + pair.winner.name);
        };
        const handleSkip = (pair) => {
          setSkippedDupeIds(prev => new Set([...prev, pair.loser.id]));
          showToast("Skipped pair");
        };
        return (
        <div>
          <div style={posRelMb10Style}>
            <span style={searchIconStyle}>{"🔍"}</span>
            <input value={adminSearch} onChange={e => setAdminSearch(e.target.value)} aria-label="Search duplicates" placeholder="Search duplicates..."
              style={searchInputIconStyle} />
          </div>
          <p style={{ color: "#B5A898", fontSize: "0.75rem", margin: "0 0 12px" }}>{filtered.length} duplicate pair{filtered.length !== 1 ? "s" : ""} detected</p>
          {filtered.length === 0 && <p style={{ color: "#5A5040", fontSize: "0.8125rem", textAlign: "center", padding: 40 }}>No duplicates found</p>}
          {filtered.map((pair, idx) => {
            const brandName = brands.find(br => br.id === pair.winner.brandId)?.name || "Unknown";
            const fieldLabel = (b) => [b.proof && b.proof + "pf", b.age && b.age + "yr", b.type, b.tier].filter(Boolean).join(" · ");
            return (
              <div key={pair.loser.id} style={{ background: "#1E1A14", borderRadius: 10, padding: 14, marginBottom: 10, border: "1px solid #3D3428" }}>
                <div style={{ fontSize: "0.6875rem", color: "#FF9800", fontWeight: 600, marginBottom: 8 }}>{brandName}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                  <div style={{ background: "#150F0A", borderRadius: 8, padding: 10, border: "1px solid #4CAF5040" }}>
                    <div style={{ fontSize: "0.5625rem", color: "#4CAF50", textTransform: "uppercase", marginBottom: 4, fontWeight: 700 }}>Keep</div>
                    <div style={lightText08125BoldStyle}>{pair.winner.name}</div>
                    <div style={{ color: "#A69880", fontSize: "0.625rem", marginTop: 2 }}>{fieldLabel(pair.winner) || "No metadata"}</div>
                    <div style={{ color: "#5A5040", fontSize: "0.5625rem", marginTop: 2 }}>ID: {String(pair.winner.id).slice(-8)}</div>
                  </div>
                  <div style={{ background: "#150F0A", borderRadius: 8, padding: 10, border: "1px solid #CD5C5C40" }}>
                    <div style={{ fontSize: "0.5625rem", color: "#CD5C5C", textTransform: "uppercase", marginBottom: 4, fontWeight: 700 }}>Merge Away</div>
                    <div style={lightText08125BoldStyle}>{pair.loser.name}</div>
                    <div style={{ color: "#A69880", fontSize: "0.625rem", marginTop: 2 }}>{fieldLabel(pair.loser) || "No metadata"}</div>
                    <div style={{ color: "#5A5040", fontSize: "0.5625rem", marginTop: 2 }}>ID: {String(pair.loser.id).slice(-8)}</div>
                  </div>
                </div>
                <div style={flexGap8Style}>
                  <button onClick={() => handleMerge(pair)} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #4CAF50, #388E3C)", color: "#fff", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>Merge</button>
                  <button onClick={() => handleSkip(pair)} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "1px solid #3D3428", background: "transparent", color: "#A69880", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>Skip</button>
                </div>
              </div>
            );
          })}
        </div>
      ); })()}
    </div>
  );
});
const universalSearchOverlayStyle = Object.freeze({ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1200, background: "rgba(17,13,8,0.97)", display: "flex", flexDirection: "column", padding: "16px", maxWidth: 600, margin: "0 auto" });
const universalSearchInputStyle = Object.freeze({ width: "100%", padding: "14px 44px 14px 16px", background: "#2D231A", border: "1px solid #5A4F3E", borderRadius: 12, color: "#F5E6D0", fontSize: "1rem", outline: "none" });
const universalSearchCategoryStyle = Object.freeze({ color: "#D4A053", fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8, marginTop: 16 });
const emptyStateCTAStyle = Object.freeze({ padding: "8px 16px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#D4A053", fontSize: "0.8125rem", cursor: "pointer", fontWeight: 600 });
const pullIndicatorStyle = Object.freeze({ textAlign: "center", color: "#B5A898", fontSize: "0.75rem", padding: "8px 0", transition: "opacity 0.2s" });
const scanAssociationBarStyle = Object.freeze({ background: "rgba(45,35,25,0.95)", border: "1px solid rgba(196,164,105,0.3)", borderRadius: 8, padding: "12px 16px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 });
const batchAddOverlayStyle = Object.freeze({ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(20,15,10,0.98)", zIndex: 1050, display: "flex", flexDirection: "column", padding: 16, maxWidth: 600, margin: "0 auto", overflowY: "auto" });
const batchAddHeaderStyle = Object.freeze({ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid rgba(196,164,105,0.2)" });

// ─── HAPTIC FEEDBACK (module scope) ────────────────────────
const haptic = () => { try { navigator?.vibrate?.(10); } catch(e) {} };
if (typeof document !== "undefined") { document.addEventListener("click", (e) => { if (e.target.closest(".tap-feedback")) haptic(); }, true); }

// ─── PULL-TO-REFRESH (module scope helper) ────────────────
function createPullToRefresh(onRefresh) {
  let startY = 0, pulling = false, indicator = null;
  const onTouchStart = (e) => {
    if (window.scrollY === 0) { startY = e.touches[0].clientY; pulling = true; }
  };
  const onTouchMove = (e) => {
    if (!pulling) return;
    const dy = e.touches[0].clientY - startY;
    if (dy > 0 && indicator) { indicator.style.opacity = Math.min(dy / 80, 1); indicator.textContent = dy > 80 ? "Release to refresh" : "Pull to refresh"; }
  };
  const onTouchEnd = (e) => {
    if (!pulling) return;
    pulling = false;
    const dy = (e.changedTouches[0]?.clientY || 0) - startY;
    if (indicator) indicator.style.opacity = "0";
    if (dy > 80 && onRefresh) onRefresh();
  };
  return {
    bind(el, ind) { indicator = ind; el.addEventListener("touchstart", onTouchStart, { passive: true }); el.addEventListener("touchmove", onTouchMove, { passive: true }); el.addEventListener("touchend", onTouchEnd, { passive: true }); },
    unbind(el) { el.removeEventListener("touchstart", onTouchStart); el.removeEventListener("touchmove", onTouchMove); el.removeEventListener("touchend", onTouchEnd); }
  };
}

// ─── PURE HELPER FUNCTIONS (module scope) ────────────────
const snap5 = (v) => Math.round(v / 5) * 5;

const fuzzyMatch = (haystack, needle) => {
  if (!haystack || !needle) return false;
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  if (h.includes(n)) return true;
  let hi = 0;
  for (let ni = 0; ni < n.length; ni++) {
    const ch = n[ni];
    const found = h.indexOf(ch, hi);
    if (found === -1) return false;
    hi = found + 1;
  }
  return true;
};


// ─── PROFANITY BLOCK LIST (single source) ────────────────
const PROFANITY_BLOCK = Object.freeze(["fuck","shit","bitch","cunt","dick","cock","pussy","motherfucker","asshole","bullshit","horseshit","goddamn","nigger","nigga","faggot","retard","retarded","twat","wanker","slut","whore"]);

// ─── AUTH SCREEN CONSTANTS (module scope) ────────────────
const authInputStyle = Object.freeze({ width: "100%", padding: "14px 16px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 12, color: "#F5E6D0", fontSize: "0.9375rem", boxSizing: "border-box", outline: "none", transition: "border-color 0.2s" });

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 019.5 24c0-1.59.28-3.14.76-4.59l-7.98-6.19A23.99 23.99 0 000 24c0 3.77.9 7.35 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
);

const authDivider = (
  <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
    <div style={dividerLineStyle} />
    <span style={{ color: "#B5A898", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 1.5 }}>or</span>
    <div style={dividerLineStyle} />
  </div>
);

// ─── BOURBON COLOR SPECTRUM ────────────────

const BOURBON_COLORS = [
  { key: "white-dog", name: "White Dog", hex: "#F5ECD7", desc: "Unaged / New Make" },
  { key: "pale-straw", name: "Pale Straw", hex: "#F0D98C", desc: "Light, young bourbon" },
  { key: "light-gold", name: "Light Gold", hex: "#E5C455", desc: "2–3 years" },
  { key: "gold", name: "Gold", hex: "#D4A033", desc: "4–5 years" },
  { key: "old-gold", name: "Old Gold", hex: "#C08B20", desc: "5–6 years" },
  { key: "amber", name: "Amber", hex: "#B8761A", desc: "6–8 years" },
  { key: "deep-amber", name: "Deep Amber", hex: "#A05A10", desc: "8–10 years" },
  { key: "russet", name: "Russet", hex: "#8B4513", desc: "10–12 years" },
  { key: "tawny", name: "Tawny", hex: "#7A3B0F", desc: "12–15 years" },
  { key: "mahogany", name: "Mahogany", hex: "#5C2E0E", desc: "15+ years" },
  { key: "burnished", name: "Burnished Oak", hex: "#4A2409", desc: "Well-aged / Finished" },
  { key: "dark-walnut", name: "Dark Walnut", hex: "#3A1A06", desc: "Extra-aged / Heavy char" },
];

// ─── BOURBON MOUTHFEEL / VISCOSITY SPECTRUM ────

const BOURBON_MOUTHFEEL = [
  { key: "thin", name: "Thin", icon: "💧", desc: "Light, watery, quick" },
  { key: "delicate", name: "Delicate", icon: "🫧", desc: "Soft, gentle, airy" },
  { key: "silky", name: "Silky", icon: "🪶", desc: "Smooth, slippery, refined" },
  { key: "round", name: "Round", icon: "⚪", desc: "Balanced, medium weight" },
  { key: "creamy", name: "Creamy", icon: "🥛", desc: "Rich, coating, lush" },
  { key: "oily", name: "Oily", icon: "🫗", desc: "Viscous, legs, full body" },
  { key: "chewy", name: "Chewy", icon: "🍯", desc: "Dense, thick, heavy" },
];

// ─── BARCODE / UPC DATABASE (module scope — never re-created) ─────

const UPC_DB = Object.freeze({
  "080686001065": { name: "Wild Turkey 101", distillery: "Wild Turkey", proof: 101, type: "Bourbon, Straight", msrp: 25, age: "6-8 years", mashBill: "75/13/12" },
  "080686001164": { name: "Wild Turkey Rare Breed", distillery: "Wild Turkey", proof: 116.8, type: "Bourbon, Barrel Proof", msrp: 45, age: "6-12 years", mashBill: "75/13/12" },
  "096749000180": { name: "Buffalo Trace", distillery: "Buffalo Trace", proof: 90, type: "Bourbon, Straight", msrp: 25, age: "NAS", mashBill: "Low Rye" },
  "088004027513": { name: "Maker's Mark", distillery: "Maker's Mark", proof: 90, type: "Bourbon, Straight", msrp: 28, age: "NAS", mashBill: "70/16/14 (Wheated)" },
  "088004027537": { name: "Maker's 46", distillery: "Maker's Mark", proof: 94, type: "Bourbon, Finished", msrp: 40, age: "NAS", mashBill: "70/16/14 (Wheated)" },
  "081128001568": { name: "Woodford Reserve", distillery: "Woodford Reserve", proof: 90.4, type: "Bourbon, Straight", msrp: 36, age: "NAS", mashBill: "72/18/10" },
  "081128001636": { name: "Woodford Reserve Double Oaked", distillery: "Woodford Reserve", proof: 90.4, type: "Bourbon, Finished", msrp: 55, age: "NAS", mashBill: "72/18/10" },
  "082184090244": { name: "Four Roses Single Barrel", distillery: "Four Roses", proof: 100, type: "Bourbon, Single Barrel", msrp: 45, age: "7-9 years", mashBill: "OBSV" },
  "082184090206": { name: "Four Roses Small Batch", distillery: "Four Roses", proof: 90, type: "Bourbon, Small Batch", msrp: 32, age: "NAS", mashBill: "Blend" },
  "096749002078": { name: "Eagle Rare", distillery: "Buffalo Trace", proof: 90, type: "Bourbon, Single Barrel", msrp: 35, age: "10 years", mashBill: "Low Rye" },
  "096749002009": { name: "Blanton's Single Barrel", distillery: "Buffalo Trace", proof: 93, type: "Bourbon, Single Barrel", msrp: 65, age: "NAS", mashBill: "Mash Bill #2 (High Rye)" },
  "080660125602": { name: "Elijah Craig Small Batch", distillery: "Heaven Hill", proof: 94, type: "Bourbon, Small Batch", msrp: 30, age: "NAS", mashBill: "78/10/12" },
  "080660125503": { name: "Elijah Craig Barrel Proof", distillery: "Heaven Hill", proof: 131, type: "Bourbon, Barrel Proof", msrp: 65, age: "12 years", mashBill: "78/10/12" },
  "096749021567": { name: "Weller Special Reserve", distillery: "Buffalo Trace", proof: 90, type: "Bourbon, Straight", msrp: 25, age: "NAS", mashBill: "Wheated" },
  "080660002453": { name: "Evan Williams Single Barrel", distillery: "Heaven Hill", proof: 86.6, type: "Bourbon, Single Barrel", msrp: 28, age: "Vintage", mashBill: "78/10/12" },
  "082000002598": { name: "Jim Beam Black", distillery: "Jim Beam", proof: 86, type: "Bourbon, Straight", msrp: 22, age: "NAS", mashBill: "75/13/12" },
  "082000730460": { name: "Knob Creek 9 Year", distillery: "Jim Beam", proof: 100, type: "Bourbon, Small Batch", msrp: 35, age: "9 years", mashBill: "75/13/12" },
  "082000730477": { name: "Knob Creek Single Barrel Reserve", distillery: "Jim Beam", proof: 120, type: "Bourbon, Single Barrel", msrp: 50, age: "9 years", mashBill: "75/13/12" },
  "082000730484": { name: "Booker's Bourbon", distillery: "Jim Beam", proof: 127, type: "Bourbon, Barrel Proof", msrp: 90, age: "6-8 years", mashBill: "75/13/12" },
  "080660125701": { name: "Henry McKenna Single Barrel", distillery: "Heaven Hill", proof: 100, type: "Bourbon, Bottled in Bond", msrp: 40, age: "10 years", mashBill: "78/10/12" },
  "080686004844": { name: "Russell's Reserve 10 Year", distillery: "Wild Turkey", proof: 90, type: "Bourbon, Straight", msrp: 38, age: "10 years", mashBill: "75/13/12" },
  "080686004851": { name: "Russell's Reserve Single Barrel", distillery: "Wild Turkey", proof: 110, type: "Bourbon, Single Barrel", msrp: 60, age: "NAS", mashBill: "75/13/12" },
  "088352131030": { name: "Old Forester 1920", distillery: "Old Forester", proof: 115, type: "Bourbon, Straight", msrp: 60, age: "NAS", mashBill: "72/18/10" },
  "088352131016": { name: "Old Forester 100", distillery: "Old Forester", proof: 100, type: "Bourbon, Bottled in Bond", msrp: 25, age: "NAS", mashBill: "72/18/10" },
  "080660125800": { name: "Larceny Small Batch", distillery: "Heaven Hill", proof: 92, type: "Bourbon, Straight", msrp: 25, age: "NAS", mashBill: "Wheated" },
  "082000757962": { name: "Old Grand-Dad 114", distillery: "Jim Beam", proof: 114, type: "Bourbon, Straight", msrp: 30, age: "NAS", mashBill: "High Rye" },
  "080432400630": { name: "1792 Small Batch", distillery: "Barton 1792", proof: 93.7, type: "Bourbon, Small Batch", msrp: 30, age: "NAS", mashBill: "High Rye" },
  "854235005002": { name: "New Riff Bourbon", distillery: "New Riff", proof: 100, type: "Bourbon, Bottled in Bond", msrp: 40, age: "4 years", mashBill: "65/30/5 (High Rye)" },

});

// ─── BRAND CONFIG (single source of truth) ────────────────
// To rebrand: change these 5 values. Nothing else in the codebase needs to change.
var BRAND = {
  name: "BarrelRank",                    // Display name everywhere
  domain: "barrelrank.com",              // Domain for URLs, emails
  storagePrefix: "barrelrank",           // sessionStorage key prefix
  referralPrefix: "BR",                  // Referral code prefix (e.g. BR-LA01)
  tagline: "Premium bourbon tracking, tasting journal, and social platform",
};

// DEMO mode: true = artifact preview (no Supabase lib), false = live site
// On Netlify: supabase-js loaded via index.html CDN script ←’ DEMO=false
// In Claude artifact: no supabase lib available ←’ DEMO=true (shows seed data)
var _sbLib = (typeof window !== "undefined" && window.supabase) || (typeof globalThis !== "undefined" && globalThis.supabase);
var DEMO = !(_sbLib && _sbLib.createClient);

const APP_VERSION = "0.84.6";

const BUILD_DATE = "2026-03-09";

// Admin role resolved from DB after auth (no hardcoded emails in bundle)

// ─── SUPABASE CLIENT ──────────────────────────────────────
// Public credentials (safe for frontend — RLS protects data)
var SUPABASE_URL = "https://ccxilvpyxxzmtrlzvldi.supabase.co";
var SUPABASE_ANON_KEY = "sb_publishable_1ppvGkid7--UG_aoyY6x8Q_E6vf36-p";
var supabase = null;

// Initialize Supabase client (lazy — created on first auth attempt)

function getSupabase() {
  if (supabase) return supabase;
  if (typeof window !== "undefined" && window.__SUPABASE_CLIENT) {
    supabase = window.__SUPABASE_CLIENT;
    return supabase;

  }

  // In Vite builds, import from npm package. In artifact, use globalThis.

  const lib = (typeof window !== "undefined" && window.supabase) || (typeof globalThis !== "undefined" && globalThis.supabase);
  if (lib && lib.createClient) {
    supabase = lib.createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
    window.__SUPABASE_CLIENT = supabase;
    return supabase;

  }

  return null;

}

// ─── SUPABASE DATA LAYER ──────────────────────────────────

const logError = import.meta.env.DEV ? console.error.bind(console) : () => {};
const ALLOWED_TABLES = Object.freeze(["bourbons", "brands", "facilities", "expression_edits", "store_picks"]);
const safeImageUrl = (url) => url && (url.startsWith("https://") || url.startsWith("data:image/")) ? url : null;

const db = {

  // ── CHECKINS ──
  async loadCheckins(userId) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("checkins").select("id,bourbon_id,rating,date,location,tasting_notes,nose,palate,finish,comment,serving_style,glassware,drinking_with,visibility,bourbon_color,mouthfeel,is_barrel_pick,pick_source,pick_proof,release_id,pick_store,pick_barrel").eq("user_id", userId).order("date", { ascending: false });

    if (error) { logError("loadCheckins:", error); return null; }
    return data.map(r => ({
      id: r.id, bourbonId: r.bourbon_id, rating: parseFloat(r.rating), date: r.date,
      location: r.location || "", tastingNotes: r.tasting_notes || [], nose: r.nose || "", palate: r.palate || "",
      finish: r.finish || "", comment: r.comment || "", servingStyle: r.serving_style || "", glassware: r.glassware || "",
      drinkingWith: r.drinking_with || [], visibility: r.visibility || "public",
      bourbonColor: r.bourbon_color || undefined, mouthfeel: r.mouthfeel || undefined,
      isBarrelPick: r.is_barrel_pick || false, pickSource: r.pick_source || "", pickProof: r.pick_proof || null,
      releaseId: r.release_id || null, pickStore: r.pick_store || "", pickBarrel: r.pick_barrel || "",
    }));

  },
  async insertCheckin(userId, c) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("checkins").insert({
      user_id: userId, bourbon_id: c.bourbonId, rating: c.rating, date: c.date,
      location: c.location, tasting_notes: c.tastingNotes || [], nose: c.nose, palate: c.palate,
      finish: c.finish, comment: c.comment, serving_style: c.servingStyle, glassware: c.glassware,
      drinking_with: c.drinkingWith || [], visibility: c.visibility || "public",
      bourbon_color: c.bourbonColor || null, mouthfeel: c.mouthfeel || null,
      is_barrel_pick: c.isBarrelPick || false, pick_source: c.pickSource || null, pick_proof: c.pickProof || null,
      release_id: c.releaseId || null, pick_store: c.pickStore || null, pick_barrel: c.pickBarrel || null,
    }).select().single();
    if (error) logError("insertCheckin:", error);
    return data;

  },
  async updateCheckin(checkinId, c) {
    const sb = getSupabase(); if (!sb) return;
    const { error } = await sb.from("checkins").update({
      rating: c.rating, location: c.location, tasting_notes: c.tastingNotes || [], nose: c.nose, palate: c.palate,
      finish: c.finish, comment: c.comment, serving_style: c.servingStyle, glassware: c.glassware,
      drinking_with: c.drinkingWith || [], visibility: c.visibility || "public",
      bourbon_color: c.bourbonColor || null, mouthfeel: c.mouthfeel || null,
      is_barrel_pick: c.isBarrelPick || false, pick_source: c.pickSource || null, pick_proof: c.pickProof || null,
      release_id: c.releaseId || null, pick_store: c.pickStore || null, pick_barrel: c.pickBarrel || null,
    }).eq("id", checkinId);
    if (error) logError("updateCheckin:", error);
  },
  async deleteCheckin(checkinId) {

    const sb = getSupabase(); if (!sb) return;

    const { error } = await sb.from("checkins").delete().eq("id", checkinId);
    if (error) logError("deleteCheckin:", error);

  },

  // ── MY BAR ──
  async loadMyBar(userId) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("my_bar").select("id,bourbon_id,price_paid,fill,batch,date_added,empty_reason,on_menu,pour_pricing,tag,purchase_location,notes,date_purchased,archived,featured,pick_store,pick_barrel,pick_proof").eq("user_id", userId);
    if (error) { logError("loadMyBar:", error); return null; }
    return data.map(r => ({
      id: r.id, bourbonId: r.bourbon_id, pricePaid: r.price_paid ? parseFloat(r.price_paid) : null,
      fill: r.fill ?? 100, batch: r.batch || "", dateAdded: r.date_added || "",
      emptyReason: r.empty_reason || undefined, onMenu: r.on_menu !== false,
      pourPricing: r.pour_pricing || {}, tag: r.tag || "",
      purchaseLocation: r.purchase_location || "", notes: r.notes || "",
      datePurchased: r.date_purchased || "", archived: r.archived || false, featured: r.featured || false,
      pickStore: r.pick_store || "", pickBarrel: r.pick_barrel || "", pickProof: r.pick_proof ? parseFloat(r.pick_proof) : null,
    }));

  },
  async insertBarItem(userId, item) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("my_bar").insert({
      user_id: userId, bourbon_id: item.bourbonId,
      price_paid: item.pricePaid || 0, fill: item.fill ?? 100,
      batch: item.batch || "", date_added: item.dateAdded || new Date().toISOString().slice(0, 10),
      empty_reason: item.emptyReason || null, on_menu: item.onMenu !== false,
      pour_pricing: item.pourPricing || {}, tag: item.tag || "",
      purchase_location: item.purchaseLocation || "", notes: item.notes || "",
      date_purchased: item.datePurchased || null, archived: false,
      pick_store: item.pickStore || null, pick_barrel: item.pickBarrel || null, pick_proof: item.pickProof || null,
    }).select().single();
    if (error) logError("insertBarItem:", error);
    return data;

  },
  async updateBarItem(itemId, updates) {

    const sb = getSupabase(); if (!sb) return;

    const dbUpdates = {};
    if ("fill" in updates) dbUpdates.fill = updates.fill;
    if ("pricePaid" in updates) dbUpdates.price_paid = updates.pricePaid;
    if ("emptyReason" in updates) dbUpdates.empty_reason = updates.emptyReason || null;
    if ("emptiedDate" in updates) dbUpdates.empty_reason = updates.emptyReason || null;
    if ("onMenu" in updates) dbUpdates.on_menu = updates.onMenu;
    if ("pourPricing" in updates) dbUpdates.pour_pricing = updates.pourPricing;
    if ("tag" in updates) dbUpdates.tag = updates.tag;
    if ("batch" in updates) dbUpdates.batch = updates.batch;
    if ("purchaseLocation" in updates) dbUpdates.purchase_location = updates.purchaseLocation;
    if ("notes" in updates) dbUpdates.notes = updates.notes;
    if ("datePurchased" in updates) dbUpdates.date_purchased = updates.datePurchased || null;
    if ("archived" in updates) { dbUpdates.archived = updates.archived; dbUpdates.archived_at = updates.archived ? new Date().toISOString().slice(0, 10) : null; }
    if ("featured" in updates) dbUpdates.featured = updates.featured;
    if ("pickStore" in updates) dbUpdates.pick_store = updates.pickStore || null;
    if ("pickBarrel" in updates) dbUpdates.pick_barrel = updates.pickBarrel || null;
    if ("pickProof" in updates) dbUpdates.pick_proof = updates.pickProof || null;

    const { error } = await sb.from("my_bar").update(dbUpdates).eq("id", itemId);
    if (error) logError("updateBarItem:", error);

  },
  async deleteBarItem(itemId) {

    const sb = getSupabase(); if (!sb) return;

    const { error } = await sb.from("my_bar").delete().eq("id", itemId);
    if (error) logError("deleteBarItem:", error);

  },

  // ── STORE PICKS ──
  async loadStorePicks(bourbonId) {
    const sb = getSupabase(); if (!sb) return [];
    const { data, error } = await sb.from("store_picks").select("id,bourbon_id,store_name,barrel_number,proof,pick_date,submitted_by,created_at").eq("bourbon_id", bourbonId).order("pick_date", { ascending: false });
    if (error) { logError("loadStorePicks:", error); return []; }
    return data.map(r => ({
      id: r.id, bourbonId: r.bourbon_id, storeName: r.store_name, barrelNumber: r.barrel_number || "",
      proof: r.proof ? parseFloat(r.proof) : null, pickDate: r.pick_date || "", submittedBy: r.submitted_by, createdAt: r.created_at,
    }));
  },
  async addStorePick(pick) {
    const sb = getSupabase(); if (!sb) return null;
    const { data, error } = await sb.from("store_picks").insert({
      bourbon_id: pick.bourbonId, store_name: pick.storeName, barrel_number: pick.barrelNumber || null,
      proof: pick.proof || null, pick_date: pick.pickDate || new Date().toISOString().slice(0, 10),
      submitted_by: pick.submittedBy,
    }).select().single();
    if (error) logError("addStorePick:", error);
    return data;
  },
  async loadStoreNames(bourbonId) {
    const sb = getSupabase(); if (!sb) return [];
    const { data, error } = await sb.from("store_picks").select("store_name").eq("bourbon_id", bourbonId);
    if (error) return [];
    return [...new Set((data || []).map(r => r.store_name).filter(Boolean))];
  },

  // ── REFERENCE DATA (bourbons, distilleries) ──
  async loadBourbons(archiveToo = false) {

    const sb = getSupabase(); if (!sb) return null;

    const PAGE = 10000;

    const cols = "id,name,brand_id,facility_id,proof,age,mash_bill,msrp,type,finish_type,batch,notes,limited_release,discontinued,user_added,added_by,website,source,origin,cola_ttb_id,cola_brand_name,cola_class_name,cola_approval_date,image_url,submission_status,submitted_by,submitted_at,reviewed_by,reviewed_at,rejection_reason,mash_corn,mash_wheat,mash_rye,mash_barley,mash_other,proof_min,proof_max,tier,popularity_score,parent_expression_id,name_locked,cola_product_name,cola_tasting_flavors,cola_description,cola_grains,cola_finishing_process,cola_barcode_value,cola_enriched_at,expression_type,mashbill_source,avg_rating,rating_count";
    let all = [], from = 0;
    while (true) {

      let query = sb.from("bourbons").select(cols).order("id").range(from, from + PAGE - 1);
      if (!archiveToo) query = query.or("tier.in.(curated,core,visible),tier.is.null");
      const { data, error } = await query;
      if (error) { logError("loadBourbons:", error); return null; }
      if (!data || data.length === 0) break;
      all = all.concat(data);
      if (data.length < PAGE) break;
      from += PAGE;

    }

    return all.map(r => ({
      id: r.id, name: r.name, brandId: r.brand_id, facilityId: r.facility_id, proof: r.proof ? parseFloat(r.proof) : null,
      age: r.age || "", mashBill: r.mash_bill || "", msrp: r.msrp ? parseFloat(r.msrp) : null,
      type: r.type || "", finishType: r.finish_type || "", batch: r.batch || "",
      notes: r.notes || "", limitedRelease: r.limited_release || false, discontinued: r.discontinued || false,
      userAdded: r.user_added || false, addedBy: r.added_by || null, website: r.website || "",
      source: r.source || null, origin: r.origin || "",
      colaTtbId: r.cola_ttb_id || null, colaBrandName: r.cola_brand_name || null,
      colaClassName: r.cola_class_name || null, colaPermitNumber: r.cola_permit_number || null,
      colaApprovalDate: r.cola_approval_date || null, imageUrl: r.image_url || null,
      submissionStatus: r.submission_status || "approved", submittedBy: r.submitted_by || null,
      submittedAt: r.submitted_at || null, reviewedBy: r.reviewed_by || null,
      reviewedAt: r.reviewed_at || null, rejectionReason: r.rejection_reason || "",
      mashCorn: r.mash_corn ?? null, mashWheat: r.mash_wheat ?? null, mashRye: r.mash_rye ?? null,
      mashBarley: r.mash_barley ?? null, mashOther: r.mash_other ?? null,
      proofMin: r.proof_min ? parseFloat(r.proof_min) : null, proofMax: r.proof_max ? parseFloat(r.proof_max) : null,
      tier: r.tier || "visible", popularityScore: r.popularity_score || 0,
      parentExpressionId: r.parent_expression_id || null,
      nameLocked: r.name_locked || false, colaProductName: r.cola_product_name || null,
      colaTastingFlavors: r.cola_tasting_flavors || null, colaDescription: r.cola_description || null,
      colaGrains: r.cola_grains || null, colaFinishingProcess: r.cola_finishing_process || null,
      colaBarcodeValue: r.cola_barcode_value || null, colaEnrichedAt: r.cola_enriched_at || null,
      expressionType: r.expression_type || null,
      mashbillSource: r.mashbill_source || null,
      avgRating: r.avg_rating ? parseFloat(r.avg_rating) : null,
      ratingCount: r.rating_count || 0,
    }));

  },
  async loadBrands() {

    const sb = getSupabase(); if (!sb) return null;

    const PAGE = 10000;

    const cols = "id,name,location,est,lat,lng,kbt,ubt,description,image,user_added,website,brand_type,is_sourced,facility_id,parent_brand_id,submission_status,submitted_by,submitted_at,reviewed_by,reviewed_at,rejection_reason";
    let all = [], from = 0;
    while (true) {

      const { data, error } = await sb.from("brands").select(cols).order("id").range(from, from + PAGE - 1);
      if (error) { logError("loadBrands:", error); return null; }
      if (!data || data.length === 0) break;
      all = all.concat(data);
      if (data.length < PAGE) break;
      from += PAGE;

    }

    return all.map(r => ({
      id: r.id, name: r.name, location: r.location, est: r.est,
      lat: r.lat || 0, lng: r.lng || 0, kbt: r.kbt || false, ubt: r.ubt || false,
      description: r.description || "", image: r.image || "", userAdded: r.user_added || false,
      website: r.website || "",
      brandType: r.brand_type || "distiller", isSourced: r.is_sourced || false, facilityId: r.facility_id || null,
      parentBrandId: r.parent_brand_id || null,
      submissionStatus: r.submission_status || "approved", submittedBy: r.submitted_by || null,
      submittedAt: r.submitted_at || null, reviewedBy: r.reviewed_by || null,
      reviewedAt: r.reviewed_at || null, rejectionReason: r.rejection_reason || "",
    }));

  },
  findDuplicates(bourbons) {
    // Client-side duplicate detection: same brand, normalized name match
    // Exclude archive tier and children (program_releases, reapprovals grouped under parents)
    const norm = s => (s || "").toLowerCase().replace(/['']/g, "'").replace(/[^a-z0-9]/g, "").trim();
    const byBrand = new Map();
    for (const b of bourbons) {
      if (b.submissionStatus !== "approved") continue;
      if (b.tier === "archive") continue;
      if (b.parentExpressionId) continue;
      const key = (b.brandId || "") + ":" + norm(b.name);
      if (!byBrand.has(key)) byBrand.set(key, []);
      byBrand.get(key).push(b);
    }
    const pairs = [];
    for (const [, group] of byBrand) {
      if (group.length < 2) continue;
      // Sort: prefer higher tier, higher popularity, more data
      group.sort((a, b) => {
        const tierRank = { curated: 4, core: 3, visible: 2 };
        const ta = tierRank[a.tier] || 0, tb = tierRank[b.tier] || 0;
        if (ta !== tb) return tb - ta;
        if ((a.popularityScore || 0) !== (b.popularityScore || 0)) return (b.popularityScore || 0) - (a.popularityScore || 0);
        const fields = x => [x.proof, x.age, x.msrp, x.type, x.finishType, x.mashCorn].filter(v => v != null).length;
        return fields(b) - fields(a);
      });
      for (let i = 1; i < group.length; i++) {
        pairs.push({ winner: group[0], loser: group[i] });
      }
    }
    return pairs;
  },
  async mergeDuplicates(winnerId, loserId) {
    const sb = getSupabase(); if (!sb) return { error: "No connection" };
    // Transfer checkins, my_bar, wishlist from loser to winner
    const transfers = await Promise.all([
      sb.from("checkins").update({ bourbon_id: winnerId }).eq("bourbon_id", loserId),
      sb.from("my_bar").update({ bourbon_id: winnerId }).eq("bourbon_id", loserId),
      sb.from("wishlist").update({ bourbon_id: winnerId }).eq("bourbon_id", loserId),
    ]);
    for (const t of transfers) {
      if (t.error) { logError("mergeDuplicates transfer:", t.error); return { error: t.error.message }; }
    }
    // Mark loser as hidden + set parent_expression_id to winner
    const { error } = await sb.from("bourbons").update({ tier: "hidden", parent_expression_id: winnerId }).eq("id", loserId);
    if (error) { logError("mergeDuplicates hide loser:", error); return { error: error.message }; }
    return { error: null };
  },
  async loadFacilities() {

    const sb = getSupabase(); if (!sb) return null;

    const PAGE = 10000;

    const cols = "*";
    let all = [], from = 0;
    while (true) {

      const { data, error } = await sb.from("facilities").select(cols).order("permit_number").range(from, from + PAGE - 1);
      if (error) { logError("loadFacilities:", error); return null; }
      if (!data || data.length === 0) break;
      all = all.concat(data);
      if (data.length < PAGE) break;
      from += PAGE;

    }

    return all.map(f => ({
      permitNumber: f.permit_number, permitType: f.permit_type || "dsp",
      companyName: f.company_name, name: f.name || f.company_name,
      state: f.state, zipCode: f.zip_code, isActive: f.is_active,
      colasTotal: f.colas_total || 0, colasApproved: f.colas_approved || 0,
      location: f.location || "", lat: f.lat || 0, lng: f.lng || 0,
      description: f.description || "", image: f.image || "", website: f.website || "",
      est: f.est || null, kbt: f.kbt || false, ubt: f.ubt || false,
      icon: f.icon || f.image || "", locationDisplay: f.location_display || f.location || "",
      brandType: f.brand_type || "distiller",
    }));

  },
  async insertBrand(d) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("brands").insert({
      id: d.id, name: d.name, location: d.location, est: d.est || null,
      description: d.description || "", image: d.image || "",
      website: d.website || "", brand_type: d.brandType || "distiller",
      is_sourced: d.isSourced || false, facility_id: d.facilityId || null,
      user_added: true, added_by: d.addedBy || null,
      submission_status: d.submissionStatus || "pending",
      submitted_by: d.submittedBy || null,
      submitted_at: d.submittedAt || new Date().toISOString(),
    }).select().single();
    if (error) { logError("insertBrand FAILED:", error.message, error.details, error.hint, error.code); return null; }
    return data;

  },
  async insertBourbon(b) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("bourbons").insert({
      id: b.id, name: (b.name || "").slice(0, 100), brand_id: b.brandId, facility_id: b.facilityId,
      proof: b.proof || null, age: (b.age || "").slice(0, 20),
      mash_bill: (b.mashBill || "").slice(0, 50), msrp: b.msrp || null,
      type: (b.type || "").slice(0, 50), finish_type: (b.finishType || "").slice(0, 50),
      batch: (b.batch || "").slice(0, 50), notes: (b.notes || "").slice(0, 1000),
      limited_release: b.limitedRelease || false, discontinued: b.discontinued || false,
      website: b.website || "",
      user_added: true, added_by: b.addedBy || null,
      submission_status: b.submissionStatus || "pending",
      submitted_by: b.submittedBy || null,
      submitted_at: b.submittedAt || new Date().toISOString(),
    }).select().single();
    if (error) { logError("insertBourbon FAILED:", error.message, error.details, error.hint, error.code); return null; }
    return data;

  },

  // ── UPC CODES ──
  async loadUPCs() {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("upc_codes").select("*");
    if (error) { logError("loadUPCs:", error); return null; }
    return data;

  },
  async loadAliases() {
    const sb = getSupabase(); if (!sb) return null;
    const { data, error } = await sb.from("expression_aliases").select("bourbon_id,alias,alias_type");
    if (error) { logError("loadAliases:", error); return null; }
    const m = new Map();
    for (const r of (data || [])) {
      if (!m.has(r.bourbon_id)) m.set(r.bourbon_id, []);
      m.get(r.bourbon_id).push({ alias: r.alias, type: r.alias_type });
    }
    return m;
  },
  async lookupUPC(upc) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("upc_codes").select("*").eq("upc", upc).limit(1).single();
    if (error) return null;
    return data;

  },
  async insertUPC(upc, expressionId, expressionName, distilleryName, submittedBy) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("upc_codes").upsert({
      upc, expression_id: expressionId || null, expression_name: expressionName,
      distillery_name: distilleryName, submitted_by: submittedBy, verified: false,
    }, { onConflict: "upc", ignoreDuplicates: true }).select().single();
    if (error) logError("insertUPC:", error);
    return data;

  },
  async verifyUPC(id, verified) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("upc_codes").update({ verified }).eq("id", id);
    if (error) { logError("verifyUPC:", error); return false; }
    return true;

  },
  async updateUPCExpression(id, expressionId) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("upc_codes").update({ expression_id: expressionId }).eq("id", id);
    if (error) { logError("updateUPCExpression:", error); return false; }
    return true;

  },
  async deleteUPC(id) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("upc_codes").delete().eq("id", id);
    if (error) { logError("deleteUPC:", error); return false; }
    return true;

  },

  // ── SUBMISSION REVIEW ──
  async updateSubmissionStatus(table, id, status, reviewedBy, rejectionReason) {
    if (!ALLOWED_TABLES.includes(table)) { logError("Invalid table:", table); return false; }
    const sb = getSupabase(); if (!sb) return false;

    const updates = {
      submission_status: status,
      reviewed_by: reviewedBy,
      reviewed_at: new Date().toISOString(),
    };
    if (rejectionReason !== undefined) updates.rejection_reason = rejectionReason;

    const { data, error } = await sb.from(table).update(updates).eq("id", id).select();
    if (error) { logError("updateSubmissionStatus:", error); return false; }
    if (!data || data.length === 0) { logError("updateSubmissionStatus: 0 rows updated — RLS may be blocking. Table:", table, "ID:", id); return false; }
    return true;

  },
  async resubmitForReview(table, id) {
    if (!ALLOWED_TABLES.includes(table)) { logError("Invalid table:", table); return false; }
    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from(table).update({
      submission_status: "pending",
      submitted_at: new Date().toISOString(),
      reviewed_by: null, reviewed_at: null, rejection_reason: "",
    }).eq("id", id);
    if (error) { logError("resubmitForReview:", error); return false; }
    return true;

  },

  // ── AUDIT LOG ──
  async insertAudit(actor, actorName, action, category, detail, target) {

    const sb = getSupabase(); if (!sb) return null;

    // target_id is UUID — only pass valid UUIDs, store non-UUID IDs in detail instead

    const isUUID = target && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(target);

    const { error } = await sb.from("audit_log").insert({
      actor, actor_name: actorName, action, category,
      detail: isUUID ? detail : (detail + (target ? " [" + target + "]" : "")),
      target: isUUID ? target : null,

    });

    if (error) logError("insertAudit:", error);

  },
  async loadAuditLog(limit = 200) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("audit_log").select("*").order("created_at", { ascending: false }).limit(limit);
    if (error) { logError("loadAuditLog:", error); return null; }
    return data.map(r => ({
      id: r.id, ts: r.created_at, actor: r.actor, actorName: r.actor_name,
      action: r.action, category: r.category, detail: r.detail, target: r.target,
    }));

  },

  // ── BUSINESS CLAIMS ──
  async loadClaims() {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("business_claims").select("*").order("created_at", { ascending: false });

    if (error) { logError("loadClaims:", error); return null; }
    return data.map(r => ({
      id: r.id, businessId: r.business_id, businessName: r.business_name,
      claimantUid: r.claimant_uid, claimantName: r.claimant_name, status: r.status,
      verificationMethod: r.verification_method, evidence: r.evidence, notes: r.notes || "",
      submittedAt: r.submitted_at, reviewedAt: r.reviewed_at, reviewedBy: r.reviewed_by,
    }));

  },
  async insertClaim(cl) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("business_claims").insert({
      business_id: cl.businessId, business_name: cl.businessName,
      claimant_uid: cl.claimantUid, claimant_name: cl.claimantName,
      status: cl.status || "submitted", verification_method: cl.verificationMethod,
      evidence: cl.evidence, notes: cl.notes || "", submitted_at: cl.submittedAt,
    }).select().single();
    if (error) { logError("insertClaim:", error); return null; }
    return data;

  },
  async updateClaimStatus(id, status, reviewedBy, notes) {

    const sb = getSupabase(); if (!sb) return false;

    const updates = { status, reviewed_by: reviewedBy, reviewed_at: new Date().toISOString() };
    if (notes !== undefined) updates.notes = notes;

    const { error } = await sb.from("business_claims").update(updates).eq("id", id);
    if (error) { logError("updateClaimStatus:", error); return false; }
    return true;

  },

  // ── BUSINESS ROLES ──
  async loadBusinessRoles() {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("business_roles").select("*");
    if (error) { logError("loadBusinessRoles:", error); return null; }
    return data.map(r => ({
      id: r.id, businessId: r.business_id, uid: r.uid, role: r.role,
      grantedBy: r.granted_by, grantedAt: r.granted_at,
    }));

  },
  async insertBusinessRole(businessId, uid, role, grantedBy) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("business_roles").upsert({
      business_id: businessId, uid, role, granted_by: grantedBy,
      granted_at: new Date().toISOString(),
    }, { onConflict: "business_id,uid" }).select().single();
    if (error) { logError("insertBusinessRole:", error); return null; }
    return data;

  },
  async deleteBusinessRole(id) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("business_roles").delete().eq("id", id);
    if (error) { logError("deleteBusinessRole:", error); return false; }
    return true;

  },

  // ── FLAGGED CONTENT ──
  async loadFlags() {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("flagged_content").select("*").order("flagged_at", { ascending: false });

    if (error) { logError("loadFlags:", error); return null; }
    return data.map(r => ({
      id: r.id, type: r.type, userId: r.user_id, userName: r.user_name,
      content: r.content, flaggedWords: r.flagged_words || [], status: r.status,
      actionTaken: r.action_taken, flaggedAt: r.flagged_at,
      reviewedBy: r.reviewed_by, reviewedAt: r.reviewed_at,
    }));

  },
  async insertFlag(flag) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("flagged_content").insert({
      type: flag.type, user_id: flag.userId, user_name: flag.userName,
      content: flag.content, flagged_words: flag.flaggedWords || [],
      status: "pending", flagged_at: new Date().toISOString(),
    }).select().single();
    if (error) { logError("insertFlag:", error); return null; }
    return data;

  },
  async updateFlagStatus(id, status, reviewedBy, actionTaken) {

    const sb = getSupabase(); if (!sb) return false;

    const updates = { status, reviewed_by: reviewedBy, reviewed_at: new Date().toISOString() };
    if (actionTaken) updates.action_taken = actionTaken;

    const { error } = await sb.from("flagged_content").update(updates).eq("id", id);
    if (error) { logError("updateFlagStatus:", error); return false; }
    return true;

  },

  // ── USER ADMIN ──
  async updateUserStatus(uid, status) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("users").update({ status }).eq("uid", uid);
    if (error) { logError("updateUserStatus:", error); return false; }
    return true;

  },
  async updateUserRole(uid, role) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("users").update({ role }).eq("uid", uid);
    if (error) { logError("updateUserRole:", error); return false; }
    return true;

  },
  async incrementFlagCount(uid) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("users").rpc("increment_flag_count", { user_uid: uid });

    if (error) {

      // Fallback: read current count and increment manually

      const { data } = await sb.from("users").select("flagged_content_count").eq("uid", uid).single();

      const { error: e2 } = await sb.from("users").update({ flagged_content_count: (data?.flagged_content_count || 0) + 1 }).eq("uid", uid);
      if (e2) { logError("incrementFlagCount:", e2); return false; }

    }

    return true;

  },
  async updateUsername(uid, username) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("users").update({ username }).eq("uid", uid);
    if (error) { logError("updateUsername:", error); return false; }
    return true;

  },
  async updateAvatar(uid, avatar) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("users").update({ avatar }).eq("uid", uid);
    if (error) { logError("updateAvatar:", error); return false; }
    return true;

  },

  // ── FRIENDS ──
  async searchUsers(query) {

    const sb = getSupabase(); if (!sb) return [];

    const { data, error } = await sb.from("users").select("uid, name, email, created_at")
      .ilike("name", `%${sanitizeQueryParam(query)}%`).limit(10);
    if (error) { logError("searchUsers:", error); return []; }
    return data.map(u => ({ id: u.uid, name: u.name, email: u.email, since: u.created_at }));

  },
  async loadFriends(userId) {

    const sb = getSupabase(); if (!sb) return null;

    // Get friendships where I'm either user_id or friend_id

    const { data, error } = await sb.from("friendships").select(`
      id, user_id, friend_id, status, created_at
    `).or(`user_id.eq.${userId},friend_id.eq.${userId}`).eq("status", "accepted");
    if (error) { logError("loadFriends:", error); return null; }

    // Get the friend's user info

    const friendIds = data.map(f => f.user_id === userId ? f.friend_id : f.user_id);
    if (friendIds.length === 0) return [];

    const { data: users, error: uErr } = await sb.from("users").select("uid, name, email, created_at")
      .in("uid", friendIds);
    if (uErr) { logError("loadFriends users:", uErr); return []; }
    return users.map(u => {

      const friendship = data.find(f => f.user_id === u.uid || f.friend_id === u.uid);
      return { id: u.uid, name: u.name, avatar: "🥃", since: friendship?.created_at?.slice(0, 10) || "" };

    });

  },
  async addFriend(userId, friendId) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("friendships").insert({
      user_id: userId, friend_id: friendId, status: "pending",
    }).select().single();
    if (error) logError("addFriend:", error);
    return data;

  },
  async acceptFriend(friendshipId) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("friendships").update({ status: "accepted" }).eq("id", friendshipId).select().single();
    if (error) logError("acceptFriend:", error);
    return data;

  },
  async declineFriend(friendshipId) {

    const sb = getSupabase(); if (!sb) return null;

    const { error } = await sb.from("friendships").delete().eq("id", friendshipId);
    if (error) logError("declineFriend:", error);

  },
  async loadPendingRequests(userId) {

    const sb = getSupabase(); if (!sb) return { incoming: [], outgoing: [] };

    const { data, error } = await sb.from("friendships").select("id, user_id, friend_id, status, created_at")
      .or(`user_id.eq.${userId},friend_id.eq.${userId}`).eq("status", "pending");
    if (error) { logError("loadPendingRequests:", error); return { incoming: [], outgoing: [] }; }

    const incomingIds = data.filter(f => f.friend_id === userId).map(f => f.user_id);

    const outgoingIds = data.filter(f => f.user_id === userId).map(f => f.friend_id);

    const allIds = [...new Set([...incomingIds, ...outgoingIds])];
    if (allIds.length === 0) return { incoming: [], outgoing: [] };

    const { data: users, error: uErr } = await sb.from("users").select("uid, name").in("uid", allIds);
    if (uErr) { logError("loadPendingRequests users:", uErr); return { incoming: [], outgoing: [] }; }

    const userMap = {};
    users.forEach(u => { userMap[u.uid] = u; });

    return {
      incoming: data.filter(f => f.friend_id === userId).map(f => ({
        friendshipId: f.id, userId: f.user_id, name: userMap[f.user_id]?.name || "Unknown", avatar: "🥃", date: f.created_at?.slice(0, 10)
      })),
      outgoing: data.filter(f => f.user_id === userId).map(f => ({
        friendshipId: f.id, userId: f.friend_id, name: userMap[f.friend_id]?.name || "Unknown", avatar: "🥃", date: f.created_at?.slice(0, 10)
      }))
    };

  },
  async removeFriend(userId, friendId) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("friendships").delete()
      .or(`and(user_id.eq.${userId},friend_id.eq.${friendId}),and(user_id.eq.${friendId},friend_id.eq.${userId})`);
    if (error) { logError("removeFriend:", error); return false; }
    return true;

  },
  async loadFriendCheckins(friendIds) {

    const sb = getSupabase(); if (!sb) return [];
    if (friendIds.length === 0) return [];

    const { data, error } = await sb.from("checkins").select("id,user_id,bourbon_id,rating,date,location,comment,tasting_notes,visibility,is_barrel_pick,pick_source,pick_proof")
      .in("user_id", friendIds)
      .in("visibility", ["public", "friends"])
      .order("date", { ascending: false }).limit(20);
    if (error) { logError("loadFriendCheckins:", error); return []; }
    return data.map(r => ({
      id: r.id, userId: r.user_id, bourbonId: r.bourbon_id, rating: parseFloat(r.rating), date: r.date,
      location: r.location || "", comment: r.comment || "", tastingNotes: r.tasting_notes || [],
      visibility: r.visibility || "public",
      isBarrelPick: r.is_barrel_pick || false, pickSource: r.pick_source || "", pickProof: r.pick_proof || null,
    }));

  },

  // ── WISHLIST ──
  async loadWishlist(userId) {

    const sb = getSupabase(); if (!sb) return null;

    const { data, error } = await sb.from("wishlist").select("bourbon_id").eq("user_id", userId);
    if (error) { logError("loadWishlist:", error); return null; }
    return data.map(r => r.bourbon_id);

  },
  async addToWishlist(userId, bourbonId) {

    const sb = getSupabase(); if (!sb) return;

    const { error } = await sb.from("wishlist").insert({ user_id: userId, bourbon_id: bourbonId });

    if (error) logError("addToWishlist:", error);

  },
  async removeFromWishlist(userId, bourbonId) {

    const sb = getSupabase(); if (!sb) return;

    const { error } = await sb.from("wishlist").delete().eq("user_id", userId).eq("bourbon_id", bourbonId);
    if (error) logError("removeFromWishlist:", error);

  },

  // ── PUBLIC FEED ──
  async loadPublicCheckins(limit = 30) {

    const sb = getSupabase(); if (!sb) return [];

    const { data, error } = await sb.from("checkins").select("id,user_id,bourbon_id,rating,date,location,comment,tasting_notes,visibility,is_barrel_pick,pick_source,pick_proof")
      .eq("visibility", "public").order("date", { ascending: false }).limit(limit);
    if (error) { logError("loadPublicCheckins:", error); return []; }
    return data.map(r => ({
      id: r.id, userId: r.user_id, bourbonId: r.bourbon_id, rating: parseFloat(r.rating), date: r.date,
      location: r.location || "", comment: r.comment || "", tastingNotes: r.tasting_notes || [],
      visibility: "public",
      isBarrelPick: r.is_barrel_pick || false, pickSource: r.pick_source || "", pickProof: r.pick_proof || null,
    }));

  },
  async loadUserNames(userIds) {

    const sb = getSupabase(); if (!sb) return {};
    if (userIds.length === 0) return {};

    const { data, error } = await sb.from("users").select("uid, name, username, avatar, shelf_staple_id, shelf_staple_note, signup_order").in("uid", userIds);
    if (error) { logError("loadUserNames:", error); return {}; }

    const map = {};
    data.forEach(u => { map[u.uid] = { id: u.uid, name: u.username || u.name, realName: u.name, avatar: u.avatar || "🥃", shelf_staple_id: u.shelf_staple_id, shelf_staple_note: u.shelf_staple_note, signup_order: u.signup_order ?? null }; });

    return map;

  },

  // ─── TOASTS (persistent kudos) ─────
  async updateBrandDescription(distilleryId, description) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("brands").update({ description }).eq("id", distilleryId);
    if (error) { logError("updateBrandDesc:", error); return false; }
    return true;

  },
  async updateBrandWebsite(distilleryId, website) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("brands").update({ website }).eq("id", distilleryId);
    if (error) { logError("updateBrandWebsite:", error); return false; }
    return true;

  },
  async updateCatalogItem(table, id, fields) {
    if (!ALLOWED_TABLES.includes(table)) { logError("Invalid table:", table); return false; }
    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from(table).update(fields).eq("id", id);
    if (error) { logError("updateCatalogItem:", error); return false; }
    return true;

  },
  async updateBourbonNotes(bourbonId, notes) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("bourbons").update({ notes }).eq("id", bourbonId);
    if (error) { logError("updateBourbonNotes:", error); return false; }
    return true;

  },
  async updateBourbonWebsite(bourbonId, website) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("bourbons").update({ website }).eq("id", bourbonId);
    if (error) { logError("updateBourbonWebsite:", error); return false; }
    return true;

  },
  async loadToastsForCheckins(checkinIds) {
    if (!checkinIds.length) return {};

    const sb = getSupabase(); if (!sb) return {};

    const { data, error } = await sb.from("toasts").select("checkin_id, user_id").in("checkin_id", checkinIds);
    if (error) { logError("loadToasts:", error); return {}; }

    const result = {};
    (data || []).forEach(t => {
      if (!result[t.checkin_id]) result[t.checkin_id] = [];
      result[t.checkin_id].push({ uid: t.user_id });

    });

    return result;

  },
  async addToast(checkinId, userId) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("toasts").upsert(
      { checkin_id: checkinId, user_id: userId },
      { onConflict: "checkin_id,user_id", ignoreDuplicates: true }
    );
    if (error) { logError("addToast:", error); return false; }
    return true;

  },
  async removeToast(checkinId, userId) {

    const sb = getSupabase(); if (!sb) return false;

    const { error } = await sb.from("toasts").delete().eq("checkin_id", checkinId).eq("user_id", userId);
    if (error) { logError("removeToast:", error); return false; }
    return true;

  },

  // ── EXPRESSION EDITS ──
  async loadExpressionEdits() {
    const sb = getSupabase(); if (!sb) return null;
    const { data, error } = await sb.from("expression_edits").select("*").order("created_at", { ascending: false });
    if (error) { logError("loadExpressionEdits:", error); return null; }
    return data.map(r => ({
      id: r.id, bourbonId: r.bourbon_id, userId: r.user_id,
      suggestedChanges: r.suggested_changes || {}, reason: r.reason || "",
      status: r.status || "pending", reviewedBy: r.reviewed_by || null,
      reviewedAt: r.reviewed_at || null, createdAt: r.created_at,
    }));
  },
  async insertExpressionEdit(bourbonId, userId, suggestedChanges, reason) {
    const sb = getSupabase(); if (!sb) return null;
    const { data, error } = await sb.from("expression_edits").insert({
      bourbon_id: bourbonId, user_id: userId,
      suggested_changes: suggestedChanges, reason: reason || "",
      status: "pending",
    }).select().single();
    if (error) { logError("insertExpressionEdit:", error); return null; }
    return data;
  },
  async updateExpressionEditStatus(id, status, reviewedBy) {
    if (!ALLOWED_TABLES.includes("expression_edits")) return false;
    const sb = getSupabase(); if (!sb) return false;
    const { error } = await sb.from("expression_edits").update({
      status, reviewed_by: reviewedBy, reviewed_at: new Date().toISOString(),
    }).eq("id", id);
    if (error) { logError("updateExpressionEditStatus:", error); return false; }
    return true;
  },
  // ── SHELF STAPLE ──
  updateShelfStaple: async (userId, bourbonId, note) => {
    const sb = getSupabase(); if (!sb) return false;
    const { error } = await sb.from('users').update({ shelf_staple_id: bourbonId, shelf_staple_note: note ? note.slice(0, 140) : null }).eq('uid', userId);
    if (error) logError('updateShelfStaple', error);
    return !error;
  },
  clearShelfStaple: async (userId) => {
    const sb = getSupabase(); if (!sb) return false;
    const { error } = await sb.from('users').update({ shelf_staple_id: null, shelf_staple_note: null }).eq('uid', userId);
    if (error) logError('clearShelfStaple', error);
    return !error;
  },
  // ── ACCOUNT DELETION ──
  async deleteUserAccount() {

    const sb = getSupabase(); if (!sb) return { success: false, error: "No database connection" };

    const { data, error } = await sb.rpc("delete_user_account");
    if (error) { logError("deleteUserAccount:", error); return { success: false, error: error.message }; }
    return { success: true, counts: data };

  },
};

// ─── BADGE SYSTEM (module level — pure function) ─────
function getUserBadges(user, bourbons) {
  const badges = [];
  if (user?.signup_order != null && user.signup_order <= 1000) {
    badges.push({ key: 'early_adopter', label: 'Early Adopter', icon: '🏅', color: '#C4A469' });
  }
  const contributions = bourbons.filter(b => b.addedBy === user?.uid && b.userAdded).length;
  if (contributions >= 100) badges.push({ key: 'master_curator', label: 'Master Curator', icon: '👑', color: '#FFD700' });
  else if (contributions >= 25) badges.push({ key: 'connoisseur', label: 'Connoisseur', icon: '🎖️', color: '#C4A469' });
  else if (contributions >= 5) badges.push({ key: 'curator', label: 'Curator', icon: '✨', color: '#B0C4DE' });
  else if (contributions >= 1) badges.push({ key: 'contributor', label: 'Contributor', icon: '🏷️', color: '#98D8A8' });
  return badges;
}

// ─── UTILITY FUNCTIONS (module level — no React deps) ─────
// QR Code encoder — generates a real scannable QR code matrix
// Supports Version 1-4, ECC L, byte mode. Returns 2D boolean array.

function generateQR(text) {

  const data = new TextEncoder().encode(text);

  // Pick version based on data length (ECC L byte capacity: v1=17, v2=32, v3=53, v4=78)

  const caps = [0, 17, 32, 53, 78];
  let ver = 1;
  for (let v = 1; v <= 4; v++) { if (data.length <= caps[v]) { ver = v; break; } if (v === 4) ver = 4; }

  const size = 17 + ver * 4;

  const grid = Array.from({ length: size }, () => Array(size).fill(null));

  const mask = Array.from({ length: size }, () => Array(size).fill(false));

  // Set module helper

  const set = (x, y, v, isFunc) => { if (x >= 0 && x < size && y >= 0 && y < size) { grid[y][x] = v ? 1 : 0; if (isFunc) mask[y][x] = true; } };

  // Finder patterns

  const finder = (cx, cy) => {
    for (let dy = -4; dy <= 4; dy++) for (let dx = -4; dx <= 4; dx++) {

      const ax = Math.abs(dx), ay = Math.abs(dy);

      const v = (ax === 4 || ay === 4) ? false : (ax === 3 || ay === 3) ? true : (ax <= 1 && ay <= 1) ? true : !(ax === 2 || ay === 2);
      if (ax === 4 || ay === 4) set(cx + dx, cy + dy, false, true);
      else set(cx + dx, cy + dy, v, true);

    }

  };
  finder(3, 3); finder(size - 4, 3); finder(3, size - 4);

  // Timing patterns
  for (let i = 8; i < size - 8; i++) { set(i, 6, i % 2 === 0, true); set(6, i, i % 2 === 0, true); }

  // Alignment pattern (v2+)
  if (ver >= 2) {

    const pos = [6, size - 7];
    for (const ay of pos) for (const ax of pos) {
      if (mask[ay]?.[ax]) continue;
      for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
        set(ax + dx, ay + dy, Math.abs(dx) === 2 || Math.abs(dy) === 2 || (dx === 0 && dy === 0), true);

      }

    }

  }

  // Format + version info reserved
  for (let i = 0; i < 8; i++) { set(i, 8, false, true); set(8, i, false, true); set(size - 1 - i, 8, false, true); set(8, size - 1 - i, false, true); }
  set(8, 8, false, true);
  set(8, size - 8, true, true); // dark module

  // Encode data: byte mode, ECC L

  const eccL = [0, 7, 10, 15, 20]; // ECC codewords per version

  const totalCW = [0, 26, 44, 70, 100]; // total codewords

  const dataCW = totalCW[ver] - eccL[ver];

  const bits = [];

  const addBits = (val, len) => { for (let i = len - 1; i >= 0; i--) bits.push((val >> i) & 1); };
  addBits(0b0100, 4); // byte mode
  addBits(data.length, ver <= 9 ? 8 : 16); // char count (8 bits for v1-9)
  for (const b of data) addBits(b, 8);

  // Terminator

  const maxBits = dataCW * 8;

  const termLen = Math.min(4, maxBits - bits.length);
  addBits(0, termLen);
  while (bits.length % 8 !== 0) bits.push(0);

  // Pad codewords

  const pads = [0b11101100, 0b00010001];
  let pi = 0;
  while (bits.length < maxBits) { addBits(pads[pi % 2], 8); pi++; }

  // Convert to codewords

  const codewords = [];
  for (let i = 0; i < bits.length; i += 8) codewords.push(bits.slice(i, i + 8).reduce((a, b) => (a << 1) | b, 0));

  // Reed-Solomon ECC (GF(256) with poly 0x11d)

  const gfExp = new Uint8Array(512), gfLog = new Uint8Array(256);
  let val = 1;
  for (let i = 0; i < 255; i++) { gfExp[i] = val; gfLog[val] = i; val = (val << 1) ^ (val >= 128 ? 0x11d : 0); }
  for (let i = 255; i < 512; i++) gfExp[i] = gfExp[i - 255];

  const gfMul = (a, b) => a === 0 || b === 0 ? 0 : gfExp[gfLog[a] + gfLog[b]];

  const numEcc = eccL[ver];

  // Generator polynomial
  let gen = [1];
  for (let i = 0; i < numEcc; i++) {

    const ng = new Array(gen.length + 1).fill(0);
    for (let j = 0; j < gen.length; j++) { ng[j] ^= gen[j]; ng[j + 1] ^= gfMul(gen[j], gfExp[i]); }
    gen = ng;

  }

  // Divide

  const msgPad = new Uint8Array(codewords.length + numEcc);
  msgPad.set(codewords);
  for (let i = 0; i < codewords.length; i++) {

    const coef = msgPad[i];
    if (coef !== 0) for (let j = 0; j < gen.length; j++) msgPad[i + j] ^= gfMul(gen[j], coef);

  }

  const ecc = Array.from(msgPad.slice(codewords.length));

  const allCW = [...codewords, ...ecc];

  // Place data bits

  const allBits = [];
  for (const cw of allCW) for (let b = 7; b >= 0; b--) allBits.push((cw >> b) & 1);
  let bi = 0;
  for (let right = size - 1; right >= 0; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++) {

      const up = ((Math.floor((size - 1 - right) / 2)) % 2) === 0;

      const y = up ? size - 1 - vert : vert;
      for (let dx = 0; dx <= 1; dx++) {

        const x = right - dx;
        if (x < 0 || x >= size) continue;
        if (mask[y][x]) continue;
        grid[y][x] = bi < allBits.length ? allBits[bi++] : 0;

      }

    }

  }

  // Apply mask pattern 0: (y + x) % 2 === 0
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    if (!mask[y][x] && grid[y][x] !== null) {
      if ((y + x) % 2 === 0) grid[y][x] ^= 1;

    }

  }

  // Write format info (ECC L = 01, mask 0 = 000 ←’ 01000, with BCH)

  const fmtBits = [0,1,0,0,0,1,1,1,1,0,1,0,1,1,0]; // ECC L + mask 0, XORed with 101010000010010

  const fmtPos1 = [[0,8],[1,8],[2,8],[3,8],[4,8],[5,8],[7,8],[8,8],[8,7],[8,5],[8,4],[8,3],[8,2],[8,1],[8,0]];

  const fmtPos2 = [[8,size-1],[8,size-2],[8,size-3],[8,size-4],[8,size-5],[8,size-6],[8,size-7],[size-8,8],[size-7,8],[size-6,8],[size-5,8],[size-4,8],[size-3,8],[size-2,8],[size-1,8]];
  for (let i = 0; i < 15; i++) {

    const [x1, y1] = fmtPos1[i]; grid[y1][x1] = fmtBits[i];

    const [x2, y2] = fmtPos2[i]; grid[y2][x2] = fmtBits[i];

  }

  return grid.map(row => row.map(v => v === 1));

}

// Check if we should use Supabase for data (real auth user, not demo)

function useSupabase() { return !!getSupabase(); }

function formatAge(age) {

  const a = (age || "").trim();
  if (!a || a.toLowerCase() === "nas") return "NAS";
  if (/^\d+$/.test(a)) return a + " YRS";
  return a;

}

function formatMsrp(msrp) {

  const val = parseFloat(msrp);
  return val && val > 0 ? "$" + val.toFixed(0) : "$\u2014";

}

function formatProof(b) {
  if (b.proofMin && b.proofMax && b.proofMin !== b.proofMax) return b.proofMin + "\u2013" + b.proofMax + "\u00B0";
  return b.proof ? b.proof + "\u00B0" : "\u2014";
}

function sanitizeInput(text, maxLen = 500) {
  if (!text) return "";
  return text.replace(/<[^>]*>/g, "").replace(/[<>"'`]/g, "").trim().slice(0, maxLen);

}

function sanitizeQueryParam(text) {
  if (!text) return "";
  return text.replace(/[%_,().\\]/g, "");
}

function sanitizeUrl(url) {
  if (!url) return "";

  const trimmed = url.trim();
  if (!trimmed) return "";

  // Only allow http:// and https:// protocols
  try {

    const parsed = new URL(trimmed.startsWith("http://") || trimmed.startsWith("https://") ? trimmed : "https://" + trimmed);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
    return parsed.href;
  } catch { return ""; }

}

function generateId(prefix = "") {

  const arr = new Uint8Array(12);
  crypto.getRandomValues(arr);
  return prefix + Array.from(arr, b => b.toString(36).padStart(2, "0")).join("").slice(0, 16);

}

// ─── APP ──────────────────────────────────────────────────

// ─── LOCATION SEARCH COMPONENT (module-level to prevent re-mount on parent re-render) ─────────

const LocationSearch = ({ value, onChange }) => {

  const [query, setQuery] = useState(value || "");

  const [results, setResults] = useState([]);

  const [loading, setLoading] = useState(false);

  const [focused, setFocused] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState("all");

  const debounceRef = useRef(null);

  const sessionTokenRef = useRef(null);

  const placesReadyRef = useRef(false);

  const categories = [
    { key: "all", label: "All", icon: "📍" },
    { key: "bar", label: "Bars", icon: "🍸" },
    { key: "restaurant", label: "Dining", icon: "🍽️" },
    { key: "distillery", label: "Brands", icon: "🏛️" },
    { key: "liquor_store", label: "Stores", icon: "🛒" },
  ];

  const setAtHome = () => { setQuery("Home"); onChange("Home"); setResults([]); setFocused(false); };

  const isHome = query.trim().toLowerCase() === "home";

  useEffect(() => {

    const loadPlaces = () => {
      if (placesReadyRef.current) return;
      if (window.google?.maps?.places?.AutocompleteSuggestion) {
        placesReadyRef.current = true;
        return;

      }

      if (document.getElementById("gplaces-script")) {

        // Script already loading, poll for readiness

        const check = setInterval(() => {
          if (window.google?.maps?.places?.AutocompleteSuggestion) {
            placesReadyRef.current = true;
            clearInterval(check);

          }

        }, 200);
        setTimeout(() => clearInterval(check), 15000);
        return;

      }

      const script = document.createElement("script");
      script.id = "gplaces-script";
      script.async = true;
      script.src = "https://maps.googleapis.com/maps/api/js?key=AIzaSyAL3locw1gxvgWYq0fVWoMjPe1Q0cOtaEM&libraries=places&v=weekly";
      script.onload = () => {

        const check = setInterval(() => {
          if (window.google?.maps?.places?.AutocompleteSuggestion) {
            placesReadyRef.current = true;
            clearInterval(check);

          }

        }, 100);
        setTimeout(() => clearInterval(check), 10000);
      };
      document.head.appendChild(script);
    };
    loadPlaces();

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const getSessionToken = () => {
    if (!sessionTokenRef.current && window.google?.maps?.places?.AutocompleteSessionToken) {
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();

    }

    return sessionTokenRef.current;
  };

  const searchPlaces = async (q, cat) => {
    if (!q.trim() || q.trim().length < 2) { setResults([]); setLoading(false); return; }
    if (!placesReadyRef.current || !google.maps.places.AutocompleteSuggestion) { setLoading(false); return; }
    setLoading(true);
    try {

      const request = {
        input: q.trim() + (cat === "distillery" ? " distillery" : ""),
        sessionToken: getSessionToken(),
        includedPrimaryTypes: cat === "bar" ? ["bar", "night_club"] : cat === "restaurant" ? ["restaurant", "cafe"] : cat === "liquor_store" ? ["liquor_store", "store"] : undefined,
      };

      const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      setResults((suggestions || []).slice(0, 8).map(s => {

        const p = s.placePrediction;
        return {
          name: p?.mainText?.text || p?.text?.text || "",
          address: p?.secondaryText?.text || "",
          icon: "📍",
          placeId: p?.placeId || "",
        };
      }));
    } catch (e) {
      logError("Places search error:", e);
      setResults([]);

    }

    setLoading(false);
  };

  const handleInputChange = (val) => {
    setQuery(val); onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length >= 2) { debounceRef.current = setTimeout(() => searchPlaces(val, selectedCategory), 400); }
    else { setResults([]); }
  };

  const selectPlace = (place) => {

    const display = place.address ? `${place.name} — ${place.address}` : place.name;
    setQuery(display); onChange(display); setResults([]); setFocused(false);
    sessionTokenRef.current = null;
  };

  const handleCatChange = (cat) => {
    setSelectedCategory(cat);
    if (query.trim().length >= 2) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => searchPlaces(query, cat), 300);

    }

  };

  return (
    <div style={posRelativeStyle}>
      <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={setAtHome} type="button"
          style={{ padding: "8px 14px", borderRadius: 16, border: "1px solid", fontSize: "0.6875rem", cursor: "pointer", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 5,
            background: isHome ? "rgba(212,160,83,0.2)" : "transparent", color: isHome ? "#D4A053" : "#A69880", borderColor: isHome ? "#D4A053" : "#3D3428", fontWeight: isHome ? 700 : 400 }}>
          <span style={font075Style}>🏠</span> At Home {isHome && "✓"}
        </button>
        <span style={{ color: "#3D3428", fontSize: "0.625rem", margin: "0 2px" }}>│</span>
        {categories.map(cat => (
          <button key={cat.key} type="button" onClick={() => { if (isHome) { setQuery(""); onChange(""); } handleCatChange(cat.key); }}
            style={{ padding: "8px 14px", borderRadius: 16, border: "1px solid", fontSize: "0.6875rem", cursor: "pointer", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 4,
              background: (!isHome && selectedCategory === cat.key) ? "rgba(212,160,83,0.15)" : "transparent",
              color: (!isHome && selectedCategory === cat.key) ? "#D4A053" : "#A69880",
              borderColor: (!isHome && selectedCategory === cat.key) ? "#D4A053" : "#3D3428",
              fontWeight: (!isHome && selectedCategory === cat.key) ? 700 : 400 }}>
            <span style={font075Style}>{cat.icon}</span> {cat.label}
          </button>
        ))}
      </div>
      {isHome ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "rgba(212,160,83,0.08)", border: "1px solid rgba(212,160,83,0.2)", borderRadius: 8 }}>
          <span style={font1125Style}>🏠</span>
          <span style={{ color: "#D4A053", fontSize: "0.875rem", fontWeight: 600, flex: 1 }}>At Home</span>
          <button onClick={() => { setQuery(""); onChange(""); setFocused(false); }} type="button"
            style={{ background: "none", border: "none", color: "#B5A898", fontSize: "0.75rem", cursor: "pointer", textDecoration: "underline" }}>Change</button>
        </div>
      ) : (<>
      <div style={posRelativeStyle}>
        <input aria-label="Search locations" value={query} onChange={e => handleInputChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setTimeout(() => setFocused(false), 200)}
          placeholder="Search bars, restaurants, brands..."
          style={{ width: "100%", padding: "10px 36px 10px 12px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, color: "#F5E6D0", fontSize: "0.875rem", boxSizing: "border-box" }} />
        {loading && <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#D4A053", fontSize: "0.875rem" }}>⟳</div>}
      </div>
      {focused && results.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50, marginTop: 4, background: "#1E1A14", border: "1px solid #5A4F3E", borderRadius: 10, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.5)", maxHeight: 280, overflowY: "auto" }}>
          {results.map((place, i) => (
            <div key={i} onMouseDown={() => selectPlace(place)}
              style={{ padding: "10px 14px", cursor: "pointer", borderBottom: i < results.length - 1 ? "1px solid #3D3428" : "none", transition: "background 0.15s", display: "flex", alignItems: "flex-start", gap: 10 }}
              onMouseEnter={e => e.currentTarget.style.background = "#3D3428"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span style={{ fontSize: "1.125rem", flexShrink: 0, marginTop: 1 }}>{place.icon}</span>
              <div style={flexMinW0Style}>
                <div style={{ color: "#F5E6D0", fontSize: "0.875rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{place.name}</div>
                {place.address && <div style={{ color: "#B5A898", fontSize: "0.75rem", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{place.address}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
};

// ─── BADGE DEFINITIONS (static metadata, module scope) ─────
const BADGE_DEFS = Object.freeze([
  { key: "first-pour", icon: "🥃", name: "First Pour", desc: "Log your first check-in", unit: "check-in", step: 1, max: 1, val: "total" },
  { key: "ky-native", icon: "🐴", name: "Kentucky Native", desc: "Try KY distilleries", unit: "KY distilleries", step: 5, val: "kyDist" },
  { key: "proof-seeker", icon: "🔥", name: "Proof Seeker", desc: "Check in barrel proofs (100+°)", unit: "barrel proofs", step: 5, val: "barrelProofs" },
  { key: "wheater", icon: "🌾", name: "Wheater", desc: "Try wheated bourbons", unit: "wheated", step: 3, val: "wheated" },
  { key: "high-rye", icon: "🌿", name: "High Rye", desc: "Try High Rye bourbons", unit: "High Rye", step: 3, val: "highRye" },
  { key: "hazmat", icon: "☢️", name: "Haz-Mat", desc: "Check in 140+° proof bourbons", unit: "haz-mats", step: 3, val: "hazmat" },
  { key: "single-barrel", icon: "🏆", name: "Single Barrel Club", desc: "Try single barrels", unit: "single barrels", step: 5, val: "singleBarrel" },
  { key: "century", icon: "💯", name: "Century Club", desc: "Log check-ins", unit: "check-ins", step: 25, val: "total" },
  { key: "collector", icon: "🗄️", name: "Collector", desc: "Bottles in your bar", unit: "bottles", step: 5, val: "_myBar" },
  { key: "trail-blazer", icon: "🗺️", name: "Trail Blazer", desc: "Try distilleries from distinct states", unit: "states", step: 5, val: "states" },
  { key: "social", icon: "🤝", name: "Social Sipper", desc: "Make friends", unit: "friends", step: 3, val: "_friends" },
  { key: "critic", icon: "📝", name: "Tasting Critic", desc: "Write check-in comments", unit: "comments", step: 5, val: "comments" },
  { key: "pilgrim", icon: "🏛️", name: "Distillery Pilgrim", desc: "Check in from a distillery", unit: "distillery visits", step: 5, val: "pilgrim" },
  { key: "bourbon-day", icon: "🇺🇸", name: "National Bourbon Day", desc: "Check in on June 14th", unit: "years", step: 1, max: Infinity, val: "bourbonDayYears" },
  { key: "ambassador", icon: "📨", name: "Ambassador", desc: "Invite users who join", unit: "referrals", step: 3, val: "_referrals" },
  { key: "evangelist", icon: "🏢", name: "Evangelist", desc: "Refer commercial venues", unit: "venues", step: 1, val: "_commercialReferrals" },
  { key: "bourbon-trail", icon: "🏞️", name: "Bourbon Trail", desc: "Check in KBT distillery expressions", unit: "KBT expressions", step: 3, val: "kbt" },
  { key: "urban-trail", icon: "🏙️", name: "Urban Explorer", desc: "Check in Urban Bourbon Trail expressions", unit: "UBT expressions", step: 2, val: "ubt" },
  { key: "early-adopter", icon: "🏅", name: "Early Adopter", desc: "Among the first 1,000 members", unit: "member", step: 1, max: 1, val: "_earlyAdopter" },
  { key: "contributor", icon: "🏷️", name: "Contributor", desc: "Submit your first edit", unit: "submissions", step: 1, max: Infinity, val: "_contributions", _progressive: true, _tiers: [{ at: 1, icon: "🏷️", name: "First Contribution" }, { at: 5, icon: "✨", name: "Regular Contributor" }, { at: 25, icon: "🎖️", name: "Trusted Contributor" }, { at: 100, icon: "👑", name: "Master Contributor" }] },
]);

// ─── SUBMISSION BADGE (module scope) ─────
const submissionBadgeBaseStyle = Object.freeze({ padding: "2px 8px", borderRadius: 20, fontSize: "0.5625rem", fontWeight: 600 });
const SubmissionBadgeInline = ({ item, getSubmissionStatus }) => {
  const sub = getSubmissionStatus(item);
  if (sub === "pending") return <span style={{ ...submissionBadgeBaseStyle, background: "rgba(255,193,7,0.12)", color: "#FFC107", border: "1px solid rgba(255,193,7,0.3)" }}>⏳ REVIEW</span>;
  if (sub === "rejected") return <span style={{ ...submissionBadgeBaseStyle, background: "rgba(205,92,92,0.12)", color: "#CD5C5C", border: "1px solid rgba(205,92,92,0.3)" }}>❌</span>;
  if (sub === "expired") return <span style={{ ...submissionBadgeBaseStyle, background: "rgba(139,125,107,0.15)", color: "#B5A898", border: "1px solid rgba(139,125,107,0.3)" }}>⏰</span>;
  return null;
};

// ─── PURE HELPER FUNCTIONS ────────────

const fmtRating = (r) => typeof r === "number" ? r.toFixed(2) : r;
const fmtDate = (d) => { if (!d) return ""; try { const [y, m, day] = d.slice(0, 10).split("-"); return `${parseInt(m)}/${parseInt(day)}/${y}`; } catch { return d; } };
const stripUSA = (loc) => loc?.replace(/, USA$/i, "") || loc;
const snapToQuarter = (val) => Math.round(val * 4) / 4;

// ─── RATING DISTRIBUTION COMPONENT ────────────
const RatingDistribution = ({ allCheckins, myCheckins, totalCount, fmtDate, fmtRating, avgRating }) => {
  const dist = allCheckins.reduce((acc, c) => { const s = Math.round(c.rating); if (s >= 1 && s <= 5) acc[s - 1]++; return acc; }, [0, 0, 0, 0, 0]);
  const maxCount = Math.max(1, ...dist);
  const myAvg = myCheckins.length > 0 ? fmtRating(myCheckins.reduce((s, c) => s + c.rating, 0) / myCheckins.length) : null;
  const mySorted = myCheckins.length > 0 ? [...myCheckins].sort((a, z) => (z.date || "").localeCompare(a.date || "")) : [];
  return (
    <div style={{ padding: "8px 16px 14px", background: "#150F0A", borderTop: "1px solid #3D3428" }}>
      <div style={{ fontSize: "0.6875rem", color: "#A69880", marginBottom: 8, fontWeight: 600 }}>Rating Distribution{avgRating ? ` · ${fmtRating(avgRating)} avg` : ""} ({totalCount} total)</div>
      {[5, 4, 3, 2, 1].map(star => (
        <div key={star} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <span style={{ color: "#D4A053", fontSize: "0.625rem", width: 14, textAlign: "right" }}>{star}{"\u2605"}</span>
          <div style={{ flex: 1, height: 8, background: "#3D3428", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ height: "100%", background: "#D4A053", borderRadius: 4, width: `${(dist[star - 1] / maxCount) * 100}%`, transition: "width 0.3s" }} />
          </div>
          <span style={{ color: "#B5A898", fontSize: "0.5625rem", width: 16, textAlign: "right" }}>{dist[star - 1]}</span>
        </div>
      ))}
      {mySorted.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid #3D3428" }}>
          <div style={{ fontSize: "0.625rem", color: "#A69880", marginBottom: 4 }}>Your check-ins ({"\u2605"} {myAvg} avg)</div>
          {mySorted.slice(0, 5).map(c => (
            <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0" }}>
              <span style={{ color: "#B5A898", fontSize: "0.625rem" }}>{fmtDate(c.date)}</span>
              <span style={{ color: "#D4A053", fontSize: "0.6875rem", fontWeight: 600 }}>{fmtRating(c.rating)}{"\u2605"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── STAR RATING COMPONENT ────────────

const StarRating = memo(({ rating = 0, size = 18, color = '#C4A469' }) => {
  const r0 = typeof rating === 'number' && !isNaN(rating) ? rating : 0;
  const gap = size * 0.15;
  const w = size * 5 + gap * 4;
  const [id] = useState(() => `sr${Math.random().toString(36).slice(2,6)}`);

  const starPath = (cx, cy, r) => {
    const pts = [];
    for (let i = 0; i < 10; i++) {
      const a = -Math.PI / 2 + (Math.PI / 5) * i;
      const rad = i % 2 === 0 ? r : r * 0.38;
      pts.push(`${cx + Math.cos(a) * rad},${cy + Math.sin(a) * rad}`);
    }
    return `M${pts.join('L')}Z`;
  };

  return (
    <svg width={w} height={size} viewBox={`0 0 ${w} ${size}`}
         role="img" aria-label={`${r0} out of 5 stars`}
         style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <defs>
        {[0,1,2,3,4].map(i => {
          const fill = Math.max(0, Math.min(1, r0 - i));
          return <clipPath key={i} id={`${id}-${i}`}>
            <rect x={i * (size + gap)} y={0} width={fill * size} height={size} />
          </clipPath>;
        })}
      </defs>
      {[0,1,2,3,4].map(i => {
        const cx = i * (size + gap) + size / 2;
        const cy = size / 2;
        const r = size / 2 - 1;
        const d = starPath(cx, cy, r);
        return <Fragment key={i}>
          <path d={d} fill="none" stroke={color} strokeWidth={0.8} opacity={0.35} />
          <path d={d} fill={color} clipPath={`url(#${id}-${i})`} />
        </Fragment>;
      })}
    </svg>
  );
});

// ─── FILL LEVEL VISUAL ────────────────

const FillLevel = ({ percent, showLabel = false }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
    <div style={{ width: 24, height: 50, border: "1.5px solid #A69880", borderRadius: 4, position: "relative", overflow: "hidden", background: "#1A1510" }}>
      <div style={{ position: "absolute", bottom: 0, width: "100%", height: `${percent}%`, background: "linear-gradient(to top, #8B5E34, #D4A053)", borderRadius: "0 0 2px 2px", transition: "height 0.4s ease" }} />
    </div>
    {showLabel && <div style={{ fontSize: "0.625rem", color: percent <= 25 ? "#C0392B" : "#B5A898", fontWeight: 600 }}>{percent}%</div>}
  </div>
);

// ─── BARCODE SCANNER COMPONENT ─────────

const BarcodeScanner = ({ onResult, onClose, autoResolve = false, onMatch, onNoMatch }) => {

  const videoRef = useRef(null);
  const onResultRef = useRef(onResult);
  const onMatchRef = useRef(onMatch);
  const onNoMatchRef = useRef(onNoMatch);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  useEffect(() => { onMatchRef.current = onMatch; }, [onMatch]);
  useEffect(() => { onNoMatchRef.current = onNoMatch; }, [onNoMatch]);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const [scanning, setScanning] = useState(true);
  const [error, setError] = useState("");
  const [captured, setCaptured] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [lastRead, setLastRead] = useState(null);
  const scanReadBufferRef = useRef([]);

  useEffect(() => {
    scanReadBufferRef.current = [];
    let stream = null;
    let animId = null;
    let detector = null;
    let cancelled = false;

    const handleConfirmedScan = async (code) => {
      setScanning(false);
      if (autoResolve) {
        setCaptured(code);
        setResolving(true);
        try {
          const sb = getSupabase();
          const { data } = sb ? await sb.rpc("resolve_upc", { p_upc: code }) : { data: null };
          if (cancelled) return;
          if (data && data.length > 0) {
            onMatchRef.current?.({ ...data[0], _scannedUpc: code });
          } else {
            onNoMatchRef.current?.(code);
          }
        } catch {
          if (!cancelled) onNoMatchRef.current?.(code);
        }
      } else {
        setCaptured(code);
        setTimeout(() => { if (!cancelled) onResultRef.current?.(code); }, 800);
      }
    };

    const startCamera = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment", width: { ideal: 640 }, height: { ideal: 480 } } });
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
        if ("BarcodeDetector" in window) {
          detector = new BarcodeDetector({ formats: ["ean_13", "ean_8", "upc_a", "upc_e"] });
          const scan = async () => {
            if (!videoRef.current || cancelled) return;
            try {
              const barcodes = await detector.detect(videoRef.current);
              if (barcodes.length > 0) {
                const raw = barcodes[0].rawValue;
                setLastRead(raw);
                const buffer = scanReadBufferRef.current;
                buffer.push(raw);
                if (buffer.length > 4) buffer.shift();
                if (buffer.length >= 2 && buffer[buffer.length - 1] === buffer[buffer.length - 2]) {
                  const confirmed = buffer[buffer.length - 1];
                  buffer.length = 0;
                  handleConfirmedScan(confirmed);
                  return;
                }
              }
            } catch {}
            animId = requestAnimationFrame(scan);
          };
          scan();
        } else {
          setError("Barcode scanning not supported in this browser. Try Chrome on Android or desktop.");
        }
      } catch {
        setError("Camera access denied. Please allow camera permission.");
      }
    };
    startCamera();

    return () => {
      cancelled = true;
      scanReadBufferRef.current = [];
      if (stream) stream.getTracks().forEach(t => t.stop());
      if (animId) cancelAnimationFrame(animId);
    };
  }, [autoResolve]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(0,0,0,0.95)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ marginBottom: 16, textAlign: "center" }}>
        <h3 style={{ color: "#F5E6D0", fontSize: "1.125rem", fontFamily: FONT_DISPLAY, margin: "0 0 8px" }}>Scan Barcode</h3>
        <p style={{ color: "#B5A898", fontSize: "0.8125rem", margin: 0 }}>Point your camera at the bottle{"\u2019"}s barcode</p>
      </div>
      {captured ? (
        <div style={{ padding: 24, textAlign: "center" }}>
          {resolving ? (
            <>
              <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>{"\uD83D\uDD0D"}</div>
              <p style={{ color: "#D4A053", fontSize: "1rem", fontWeight: 700, margin: "0 0 8px" }}>Looking up bottle...</p>
              <p style={{ color: "#B5A898", fontSize: "0.8125rem", margin: 0, fontFamily: "monospace" }}>{captured}</p>
            </>
          ) : (
            <>
              <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>{"\u2705"}</div>
              <p style={{ color: "#4CAF50", fontSize: "1rem", fontWeight: 700, margin: "0 0 8px" }}>Barcode Captured!</p>
              <p style={{ color: "#B5A898", fontSize: "0.8125rem", margin: 0, fontFamily: "monospace" }}>{captured}</p>
            </>
          )}
        </div>
      ) : error ? (
        <div style={{ padding: 20, textAlign: "center" }}>
          <div style={{ fontSize: "2.0rem", marginBottom: 12 }}><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#B5A898" strokeWidth="2" strokeLinecap="round"><path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2"/><line x1="7" y1="7" x2="7" y2="17"/><line x1="10" y1="7" x2="10" y2="17"/><line x1="13" y1="7" x2="13" y2="17"/><line x1="17" y1="7" x2="17" y2="17"/></svg></div>
          <p style={{ color: "#CD5C5C", fontSize: "0.875rem" }}>{error}</p>
        </div>
      ) : (
        <div style={{ position: "relative", width: 300, height: 220, borderRadius: 12, overflow: "hidden", border: "2px solid #D4A053" }}>
          <video ref={videoRef} style={{ width: "100%", height: "100%", objectFit: "cover" }} playsInline muted />
          <div style={{ position: "absolute", top: "50%", left: "10%", right: "10%", height: 2, background: "#D4A053", opacity: 0.7, animation: "none", boxShadow: "0 0 8px rgba(212,160,83,0.5)" }} />
          {lastRead && <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, fontSize: 11, fontFamily: "monospace", color: "#F5E6D0", background: "rgba(0,0,0,0.6)", padding: "2px 8px", textAlign: "center" }}>{lastRead}</div>}
        </div>
      )}
      <button onClick={onClose} style={{ marginTop: 20, padding: "10px 24px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#B5A898", fontSize: "0.875rem", cursor: "pointer" }}>Cancel</button>
    </div>
  );
};

// ─── CHECK-IN MODAL ───────────────────

const CheckinModal = ({ editingCheckinId, checkins, checkinBourbon, checkinPrivacy, myBar, friends, bourbons,
  setEditingCheckinId, setShowCheckinModal, setCheckinBourbon, setCheckins, setMyBar,
  setCelebrationData, setOverconsumptionNotice, setBarSharePrompt,
  getBourbon, getBrand, getDisplayName, checkProfanity, showToast, db, currentUser }) => {

  const editSrc = editingCheckinId ? checkins.find(c => c.id === editingCheckinId) : null;

  const [rating, setRating] = useState(editSrc?.rating || 0);

  const [nose, setNose] = useState(editSrc?.nose || "");

  const [palate, setPalate] = useState(editSrc?.palate || "");

  const [finish, setFinish] = useState(editSrc?.finish || "");

  const [comment, setComment] = useState(editSrc?.comment || "");

  const [location, setLocation] = useState(editSrc?.location || "");

  const [servingStyle, setServingStyle] = useState(editSrc?.servingStyle || "Neat");

  const [glassware, setGlassware] = useState(editSrc?.glassware || "Glencairn");

  const [selectedNotes, setSelectedNotes] = useState(editSrc?.tastingNotes || []);

  const [expandedCategory, setExpandedCategory] = useState(null);

  const [drinkingWith, setDrinkingWith] = useState(editSrc?.drinkingWith || []);

  const [dwSearch, setDwSearch] = useState("");

  const [barPrompt, setBarPrompt] = useState(null);

  const [barPromptFill, setBarPromptFill] = useState(50);

  const [bourbonColor, setBourbonColor] = useState(editSrc?.bourbonColor || null);

  const [mouthfeel, setMouthfeel] = useState(editSrc?.mouthfeel || null);

  const [isBarrelPick, setIsBarrelPick] = useState(editSrc?.isBarrelPick || false);

  const [pickSource, setPickSource] = useState("");

  const [pickProof, setPickProof] = useState("");

  const [expandedTastingField, setExpandedTastingField] = useState("palate"); // "nose"|"palate"|"finish"
  const [selectedReleaseId, setSelectedReleaseId] = useState(editSrc?.releaseId || null);
  const [isStorePick, setIsStorePick] = useState(!!(editSrc?.pickStore));
  const [spStore, setSpStore] = useState(editSrc?.pickStore || "");
  const [spBarrel, setSpBarrel] = useState(editSrc?.pickBarrel || "");
  const [spProof, setSpProof] = useState(editSrc?.pickProof ?? "");
  const [storeAutocomplete, setStoreAutocomplete] = useState([]);

  const twLastTapRef = useRef({});

  const toggleNote = (note) => {
    setSelectedNotes(prev => prev.includes(note) ? prev.filter(n => n !== note) : [...prev, note]);

  };

  const handleWheelTap = (cat) => {

    const now = Date.now();

    const last = twLastTapRef.current[cat] || 0;
    twLastTapRef.current[cat] = now;

    const isAct = selectedNotes.some(n => n === cat || TASTING_WHEEL[cat].notes.includes(n));

    // Double-tap on active segment: clear all notes for this category
    if (now - last < 400 && isAct) {
      setSelectedNotes(prev => prev.filter(n => n !== cat && !TASTING_WHEEL[cat].notes.includes(n)));
      if (expandedCategory === cat) setExpandedCategory(null);
      return;

    }

    // Single tap: select / drilldown / close
    if (!isAct) { toggleNote(cat); setExpandedCategory(null); }
    else if (expandedCategory !== cat) { setExpandedCategory(cat); }
    else { setExpandedCategory(null); }
  };

  const isAtHome = (loc) => {

    const l = (loc || "").toLowerCase().trim();
    return l === "home" || l.startsWith("home ") || l.includes("home bar") || l.includes("at home") || l.includes("my place") || l.includes("my house");
  };

  const submit = async () => {
    if (!rating || !checkinBourbon) return;

    const profCheck = checkProfanity(comment);

    const checkinData = {
      bourbonId: checkinBourbon.id, rating, date: new Date().toISOString().slice(0, 10),
      location: sanitizeInput(location, 200) || "Unknown", tastingNotes: selectedNotes, nose: sanitizeInput(nose, 200), palate: sanitizeInput(palate, 200), finish: sanitizeInput(finish, 200), comment: sanitizeInput(comment, 1000), servingStyle, glassware, visibility: checkinPrivacy,
      drinkingWith: drinkingWith.length > 0 ? drinkingWith : undefined,
      bourbonColor: bourbonColor || undefined,
      mouthfeel: mouthfeel || undefined,
      isBarrelPick: isBarrelPick || undefined,
      pickSource: isBarrelPick ? sanitizeInput(pickSource, 100) : undefined,
      pickProof: isBarrelPick && pickProof ? Math.min(Math.max(parseFloat(pickProof) || 0, 80), 160) : undefined,
      releaseId: selectedReleaseId || undefined,
      pickStore: isStorePick ? sanitizeInput(spStore, 100) : undefined,
      pickBarrel: isStorePick ? sanitizeInput(spBarrel, 50) : undefined,
      flagged: !profCheck.clean ? profCheck.flagged : undefined,
    };
    // Store pick: also override pick proof from store pick fields if set
    if (isStorePick && spProof) {
      checkinData.isBarrelPick = true;
      checkinData.pickProof = Math.min(Math.max(parseFloat(spProof) || 0, 80), 160);
      checkinData.pickStore = sanitizeInput(spStore, 100);
      checkinData.pickBarrel = sanitizeInput(spBarrel, 50);
    }

    // Edit mode
    if (editingCheckinId) {
      const existing = checkins.find(c => c.id === editingCheckinId);
      const updated = { ...existing, ...checkinData };
      setCheckins(prev => prev.map(c => c.id === editingCheckinId ? updated : c));
      if (useSupabase()) db.updateCheckin(editingCheckinId, updated);
      setEditingCheckinId(null);
      setShowCheckinModal(false);
      setCheckinBourbon(null);
      showToast("Check-in updated!");
      return;
    }

    // New check-in
    const newCheckin = { id: generateId("c_"), ...checkinData };
    setCheckins(prev => [newCheckin, ...prev]);

    if (useSupabase() && currentUser?.uid) {

      const dbRow = await db.insertCheckin(currentUser.uid, newCheckin);

      // Sync Supabase UUID back so toasts reference the real DB id
      if (dbRow?.id && dbRow.id !== newCheckin.id) {
        setCheckins(prev => prev.map(c => c.id === newCheckin.id ? { ...c, id: dbRow.id } : c));

      }

    }

    // Auto-create store pick record for pick_base check-ins
    if (isStorePick && spStore && checkinBourbon.expressionType === "pick_base" && useSupabase() && currentUser?.uid) {
      db.addStorePick({ bourbonId: checkinBourbon.id, storeName: sanitizeInput(spStore, 100), barrelNumber: sanitizeInput(spBarrel, 50), proof: spProof ? parseFloat(spProof) : null, submittedBy: currentUser.uid });
    }

    // Check if at-home pour ←’ prompt for bar inventory
    if (isAtHome(location)) {

      const existing = myBar.find(item => item.bourbonId === checkinBourbon.id);
      if (existing) {
        setBarPromptFill(Math.max(existing.fill - 10, 5));
        setBarPrompt({ mode: "update", bourbonId: checkinBourbon.id, currentFill: existing.fill });

      } else {
        setBarPromptFill(75);
        setBarPrompt({ mode: "add", bourbonId: checkinBourbon.id });

      }

    }

    setShowCheckinModal(false);

    const savedBourbon = checkinBourbon;
    const savedRating = rating;
    setCheckinBourbon(null);

    // Celebration + overconsumption check
    const today = new Date().toISOString().slice(0, 10);
    const todayCount = checkins.filter(c => c.date === today).length + 1; // +1 for this one
    const uniqueCount = new Set([...checkins.map(c => c.bourbonId), checkinData.bourbonId]).size;
    const statCallout = uniqueCount % 10 === 0 ? `${uniqueCount} unique bourbons!` : `Pour #${checkins.length + 1}`;
    if (todayCount >= 4) {
      setOverconsumptionNotice({ count: todayCount, bourbon: getDisplayName(savedBourbon), rating: savedRating, statCallout });
    } else {
      setCelebrationData({ bourbon: getDisplayName(savedBourbon), rating: savedRating, statCallout });
      setTimeout(() => setCelebrationData(null), 4000);
    }

  };

  const handleBarPromptConfirm = () => {
    if (barPrompt.mode === "update") {
      setMyBar(prev => {

        const updated = prev.map(item => item.bourbonId === barPrompt.bourbonId ? { ...item, fill: barPromptFill } : item);
        if (useSupabase() && currentUser?.uid) { const item = updated.find(i => i.bourbonId === barPrompt.bourbonId); if (item?.id) db.updateBarItem(item.id, { fill: barPromptFill }); }
        return updated;

      });

    } else {

      const newItem = { bourbonId: barPrompt.bourbonId, fill: Math.min(Math.max(barPromptFill, 0), 100), pricePaid: null, dateAdded: new Date().toISOString().slice(0, 10) };
      setMyBar(prev => [...prev, newItem]);

      if (useSupabase() && currentUser?.uid) db.insertBarItem(currentUser.uid, newItem);

      const bx = getBourbon(barPrompt.bourbonId);
      if (bx) setBarSharePrompt({ bourbonId: barPrompt.bourbonId, bourbonName: getDisplayName(bx) });

    }

    setBarPrompt(null);
    setShowCheckinModal(false);
    setCheckinBourbon(null);
  };

  const handleBarPromptSkip = () => {
    setBarPrompt(null);
    setShowCheckinModal(false);
    setCheckinBourbon(null);
  };
  if (!checkinBourbon) return null;

  const bourbon = checkinBourbon;

  const brand = getBrand(bourbon.brandId);

  return (
    <div style={modalOverlayStyle}>
      <div style={{ background: "#1E1A14", border: "1px solid #5A4F3E", borderRadius: 16, width: "100%", maxWidth: 560, maxHeight: "90vh", overflow: "auto", padding: 32 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.375rem", color: "#F5E6D0", margin: 0 }}>{editingCheckinId ? "Edit Check-In" : "Check In"}</h2>
          <button onClick={() => { setShowCheckinModal(false); setCheckinBourbon(null); setEditingCheckinId(null); }} style={mutedText15Style} aria-label="Close">✕</button>
        </div>
        <div style={{ background: "#150F0A", borderRadius: 12, padding: 16, marginBottom: 24, border: "1px solid #3D3428" }}>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#D4A053" }}>{getDisplayName(bourbon)}</div>
          <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginTop: 4 }}>{brand?.name} · {bourbon.proof}° · {bourbon.type}</div>
          {/* Release picker for program expressions */}
          {bourbon.expressionType === "program" && (() => {
            const releases = bourbons.filter(b => b.parentExpressionId === bourbon.id && b.expressionType === "program_release").sort((a, z) => (z.colaApprovalDate || "").localeCompare(a.colaApprovalDate || ""));
            return releases.length > 0 ? (
              <div style={{ marginTop: 12, borderTop: "1px solid #3D3428", paddingTop: 12 }}>
                <div style={{ fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6, fontWeight: 600 }}>Which release?</div>
                <div style={{ maxHeight: 160, overflowY: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
                  <div onClick={() => setSelectedReleaseId(null)}
                    style={{ padding: "8px 10px", borderRadius: 8, cursor: "pointer", background: !selectedReleaseId ? "rgba(212,160,83,0.12)" : "transparent", border: !selectedReleaseId ? "1px solid rgba(212,160,83,0.3)" : "1px solid #3D3428", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: "0.75rem", color: !selectedReleaseId ? "#D4A053" : "#A69880", fontWeight: !selectedReleaseId ? 600 : 400 }}>Not sure / General</span>
                  </div>
                  {releases.map(r => (
                    <div key={r.id} onClick={() => setSelectedReleaseId(r.id)}
                      style={{ padding: "8px 10px", borderRadius: 8, cursor: "pointer", background: selectedReleaseId === r.id ? "rgba(212,160,83,0.12)" : "transparent", border: selectedReleaseId === r.id ? "1px solid rgba(212,160,83,0.3)" : "1px solid #3D3428" }}>
                      <div style={{ fontSize: "0.75rem", color: selectedReleaseId === r.id ? "#D4A053" : "#F5E6D0", fontWeight: selectedReleaseId === r.id ? 600 : 400 }}>{r.name}</div>
                      <div style={{ fontSize: "0.625rem", color: "#5A4F3E", marginTop: 1 }}>{r.proof ? r.proof + "\u00B0" : ""}{r.proof && r.colaApprovalDate ? " \u00B7 " : ""}{r.colaApprovalDate ? r.colaApprovalDate.slice(0, 10) : ""}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;
          })()}
          {/* Store pick toggle for pick_base expressions */}
          {bourbon.expressionType === "pick_base" && (
            <div style={{ marginTop: 12, borderTop: "1px solid #3D3428", paddingTop: 12 }}>
              <button onClick={() => { if (!isStorePick && storeAutocomplete.length === 0 && useSupabase()) { db.loadStoreNames(bourbon.id).then(setStoreAutocomplete); } setIsStorePick(!isStorePick); }}
                style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: 0, background: "none", border: "none", cursor: "pointer" }}>
                <div style={{ width: 32, height: 18, borderRadius: 9, background: isStorePick ? "#6495ED" : "#3D3428", transition: "background 0.2s", position: "relative", flexShrink: 0 }}>
                  <div style={{ width: 14, height: 14, borderRadius: 7, background: "#F5E6D0", position: "absolute", top: 2, left: isStorePick ? 16 : 2, transition: "left 0.2s" }} />
                </div>
                <span style={{ fontSize: "0.75rem", color: isStorePick ? "#6495ED" : "#B5A898", fontWeight: isStorePick ? 600 : 400 }}>{"\uD83C\uDFF7\uFE0F"} This is a store pick</span>
              </button>
              {isStorePick && (
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div>
                    <label style={sColFonTex5Style}>Store Name *</label>
                    <input value={spStore} onChange={e => setSpStore(e.target.value)} aria-label="Store name" placeholder="e.g. Total Wine Lexington" maxLength={100} list="sp-stores"
                      style={lightText08125Style} />
                    {storeAutocomplete.length > 0 && <datalist id="sp-stores">{storeAutocomplete.map(s => <option key={s} value={s} />)}</datalist>}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <label style={sColFonTex5Style}>Barrel #</label>
                      <input value={spBarrel} onChange={e => setSpBarrel(e.target.value)} aria-label="Barrel number" placeholder="Optional" maxLength={50}
                        style={lightText08125Style} />
                    </div>
                    <div>
                      <label style={sColFonTex5Style}>Pick Proof</label>
                      <input type="number" value={spProof} onChange={e => setSpProof(e.target.value)} aria-label="Pick proof" placeholder={String(bourbon.proof || "")} min="80" max="160" step="0.1"
                        style={lightText08125Style} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Generic barrel pick toggle (for non-pick_base expressions) */}
          {bourbon.expressionType !== "pick_base" && (
            <div style={{ marginTop: 12, borderTop: "1px solid #3D3428", paddingTop: 12 }}>
              <button onClick={() => setIsBarrelPick(!isBarrelPick)}
                style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: 0, background: "none", border: "none", cursor: "pointer" }}>
                <div style={{ width: 32, height: 18, borderRadius: 9, background: isBarrelPick ? "#D4A053" : "#3D3428", transition: "background 0.2s", position: "relative", flexShrink: 0 }}>
                  <div style={{ width: 14, height: 14, borderRadius: 7, background: "#F5E6D0", position: "absolute", top: 2, left: isBarrelPick ? 16 : 2, transition: "left 0.2s" }} />
                </div>
                <span style={{ fontSize: "0.75rem", color: isBarrelPick ? "#D4A053" : "#B5A898", fontWeight: isBarrelPick ? 600 : 400 }}>{"\uD83C\uDFF7\uFE0F"} Barrel Pick / Store Pick</span>
              </button>
              {isBarrelPick && (
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ color: "#B5A898", fontSize: "0.6875rem", lineHeight: 1.4 }}>A unique single barrel selected by a retailer or group</div>
                  <div>
                    <label style={sColFonTex5Style}>Picked By</label>
                    <input value={pickSource} onChange={e => setPickSource(e.target.value)} aria-label="Picked by" placeholder="e.g. Total Wine Lexington, r/bourbon" maxLength={100}
                      style={lightText08125Style} />
                  </div>
                  <div>
                    <label style={sColFonTex5Style}>Proof (if different from {checkinBourbon?.proof}°)</label>
                    <input type="number" value={pickProof} onChange={e => setPickProof(e.target.value)} aria-label="Barrel pick proof" placeholder={String(checkinBourbon?.proof || "")} min="80" max="160" step="0.1"
                      style={lightText08125Style} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
          <div>
            <label style={formLabelStyle}>Serving</label>
            <select aria-label="Serving style" value={servingStyle} onChange={e => setServingStyle(e.target.value)} style={{ width: "100%", padding: "10px 12px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, color: "#F5E6D0", fontSize: "0.875rem" }}>
              {["Flight", "Ice - Big Cube", "Ice - On the Rocks", "Neat", "With Water", "Cocktail - Manhattan", "Cocktail - Mint Julep", "Cocktail - Old Fashioned", "Cocktail - Other"].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={formLabelStyle}>Glassware</label>
            <select aria-label="Glassware" value={glassware} onChange={e => setGlassware(e.target.value)} style={{ width: "100%", padding: "10px 12px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, color: "#F5E6D0", fontSize: "0.875rem" }}>
              {["Copita", "Coupe", "Glencairn", "Highball", "Kentucky Bourbon Glass", "Mason Jar", "Rocks Glass", "Snifter", "Tumbler"].map(g => <option key={g}>{g}</option>)}
            </select>
          </div>
        </div>
        {/* Bourbon Color */}
        <div style={mb24Style}>
          <label style={{ color: "#B5A898", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 1.5, display: "block", marginBottom: 10 }}>Color</label>
          {/* Interactive gradient slider */}
          <div style={{ position: "relative", height: 32, borderRadius: 16, background: `linear-gradient(90deg, ${BOURBON_COLORS.map(c => c.hex).join(", ")})`, border: "1px solid #3D3428", cursor: "pointer", userSelect: "none" }}
            onClick={(e) => {

              const rect = e.currentTarget.getBoundingClientRect();

              const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));

              const idx = Math.round(pct * (BOURBON_COLORS.length - 1));
              setBourbonColor(BOURBON_COLORS[idx].key);
            }}>
            {/* Tick marks */}
            {BOURBON_COLORS.map((c, i) => {

              const pct = (i / (BOURBON_COLORS.length - 1)) * 100;

              return <div key={c.key} style={{ position: "absolute", left: `${pct}%`, top: 0, bottom: 0, width: 1, background: "rgba(245,230,208,0.12)", transform: "translateX(-0.5px)", pointerEvents: "none" }} />;
            })}
            {/* Thumb */}
            {bourbonColor && (() => {

              const idx = BOURBON_COLORS.findIndex(c => c.key === bourbonColor);

              const pct = idx >= 0 ? (idx / (BOURBON_COLORS.length - 1)) * 100 : 0;

              return <div style={{ position: "absolute", top: -2, left: `${pct}%`, transform: "translateX(-50%)", width: 36, height: 36, borderRadius: "50%", border: "3px solid #F5E6D0", background: BOURBON_COLORS[idx]?.hex, boxShadow: "0 0 12px rgba(0,0,0,0.7), 0 0 4px rgba(212,160,83,0.3)", transition: "left 0.12s ease-out", pointerEvents: "none" }} />;
            })()}
          </div>
          {/* End labels */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, padding: "0 2px" }}>
            <span style={dimText05625UpperSpacedStyle}>Pale</span>
            <span style={dimText05625UpperSpacedStyle}>Dark</span>
          </div>
          {/* Selected label */}
          {bourbonColor && (() => {

            const sel = BOURBON_COLORS.find(c => c.key === bourbonColor);
            return sel ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, justifyContent: "center" }}>
                <div style={{ width: 14, height: 14, borderRadius: "50%", background: sel.hex, border: "1px solid #5A4F3E" }} />
                <span style={goldText08125BoldStyle}>{sel.name}</span>
                <span style={mutedText06875Style}>— {sel.desc}</span>
                <button onClick={() => setBourbonColor(null)} style={{ background: "none", border: "none", color: "#A69880", fontSize: "0.75rem", cursor: "pointer", padding: "0 0 0 4px" }} aria-label="Close">✕</button>
              </div>
            ) : null;
          })()}
        </div>
        {/* Nose — standalone collapsible section */}
        {(() => {
          const isOpen = expandedTastingField === "nose";
          const hasValue = nose.trim().length > 0;
          return (
            <div style={{ marginBottom: 16 }}>
              <button onClick={() => setExpandedTastingField(isOpen ? null : "nose")}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "12px 14px", background: isOpen ? "#3D3428" : hasValue ? "rgba(212,160,83,0.06)" : "#150F0A",
                  border: `1px solid ${isOpen ? "#D4A053" : hasValue ? "rgba(212,160,83,0.2)" : "#3D3428"}`,
                  borderRadius: isOpen ? "10px 10px 0 0" : 10, cursor: "pointer", transition: "all 0.15s" }}>
                <div style={flexCenterGap10Style}>
                  <span style={font0875Style}>👃</span>
                  <span style={{ color: hasValue ? "#D4A053" : "#F5E6D0", fontSize: "0.8125rem", fontWeight: 600 }}>Nose</span>
                  <span style={dimText075Style}>{hasValue ? `— ${nose.slice(0, 40)}${nose.length > 40 ? "…" : ""}` : "What do you smell?"}</span>
                </div>
                <span style={{ color: "#A69880", fontSize: "0.625rem", transition: "transform 0.15s", transform: isOpen ? "rotate(180deg)" : "none" }}>▼</span>
              </button>
              {isOpen && (
                <div style={{ padding: "10px 14px 14px", background: "#1E1A14", border: "1px solid #D4A053", borderTop: "none", borderRadius: "0 0 10px 10px" }}>
                  <TastingSuggestionPills field="nose" value={nose} onChange={setNose} />
                  <textarea value={nose} onChange={e => { setNose(e.target.value); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }} aria-label="Nose" placeholder="What do you smell?" maxLength={200} autoFocus
                    ref={el => { if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } }}
                    style={{ width: "100%", padding: "10px 12px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, color: "#F5E6D0", fontSize: "0.875rem", boxSizing: "border-box", resize: "none", overflow: "hidden", minHeight: 38, lineHeight: 1.4, fontFamily: "inherit", wordBreak: "break-word", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }} />
                  {nose.length > 160 && <div style={{ textAlign: "right", fontSize: "0.6875rem", marginTop: 2, color: nose.length >= 195 ? "#E57373" : nose.length >= 180 ? "#C4A469" : "#8B7E6A" }}>{nose.length}/200</div>}
                </div>
              )}
            </div>
          );
        })()}
        {/* Mouthfeel */}
        <div style={mb24Style}>
          <label style={{ color: "#B5A898", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 1.5, display: "block", marginBottom: 10 }}>Mouthfeel</label>
          <div style={{ display: "flex", gap: 0, borderRadius: 10, overflow: "hidden", border: "1px solid #3D3428" }}>
            {BOURBON_MOUTHFEEL.map((m, i) => {

              const selected = mouthfeel === m.key;

              const idx = BOURBON_MOUTHFEEL.findIndex(x => x.key === mouthfeel);

              const fillOpacity = idx >= 0 && i <= idx ? 0.12 + (i / (BOURBON_MOUTHFEEL.length - 1)) * 0.2 : 0;

              return (
                <button key={m.key} onClick={() => setMouthfeel(mouthfeel === m.key ? null : m.key)} title={`${m.name} — ${m.desc}`}
                  style={{ flex: 1, padding: "10px 2px", background: selected ? "rgba(212,160,83,0.25)" : idx >= 0 && i <= idx ? `rgba(212,160,83,${fillOpacity})` : "#150F0A",
                    border: "none", borderRight: i < BOURBON_MOUTHFEEL.length - 1 ? "1px solid #3D3428" : "none", cursor: "pointer", transition: "all 0.15s",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                  <span style={font10Style}>{m.icon}</span>
                  <span style={{ fontSize: "0.5625rem", color: selected ? "#D4A053" : "#A69880", fontWeight: selected ? 700 : 400, letterSpacing: 0.3, textTransform: "uppercase" }}>{m.name}</span>
                </button>
              );
            })}
          </div>
          {mouthfeel && (() => {

            const sel = BOURBON_MOUTHFEEL.find(m => m.key === mouthfeel);
            return sel ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, justifyContent: "center" }}>
                <span style={font0875Style}>{sel.icon}</span>
                <span style={goldText08125BoldStyle}>{sel.name}</span>
                <span style={mutedText06875Style}>— {sel.desc}</span>
              </div>
            ) : null;
          })()}
        </div>
                            <div style={mb24Style}>
          <label style={{ color: "#B5A898", fontSize: "0.875rem", textTransform: "uppercase", letterSpacing: 1.5, display: "block", marginBottom: 10, fontWeight: 700 }}>Palate</label>
          <div style={{ color: "#9A8E7F", fontSize: "0.6875rem", marginBottom: 10, textAlign: "center" }}>Tap a segment to select. Double-tap for deeper notes.</div>
          <div style={posRelativeStyle}>
            <div style={{ position: "absolute", top: "50%", left: "50%", width: "85%", aspectRatio: "1", transform: "translate(-50%, -50%)", borderRadius: "50%", background: selectedNotes.length > 0 ? "radial-gradient(circle, rgba(212,160,83,0.12) 0%, transparent 72%)" : "radial-gradient(circle, rgba(212,160,83,0.06) 0%, transparent 72%)", transition: "background 0.6s", pointerEvents: "none", filter: "blur(18px)" }} />
            <svg viewBox="0 0 400 400" width="100%" style={{ display: "block", position: "relative", maxWidth: 380, margin: "0 auto" }}>
              <defs>
                {TW_CATS.map((cat, i) => { const d = TASTING_WHEEL[cat], isAct = selectedNotes.some(n => n === cat || d.notes.includes(n)), isExp = expandedCategory === cat;

                  return <radialGradient key={`twg-${cat}`} id={`twg-${cat}`} cx="0.5" cy="0.5" r="0.5" gradientUnits="objectBoundingBox">
                    {isAct ? <><stop offset="0%" stopColor={d.color} stopOpacity={isExp ? "0.45" : "0.30"} /><stop offset="100%" stopColor={d.color} stopOpacity={isExp ? "0.20" : "0.12"} /></>
                      : <><stop offset="0%" stopColor={d.color} stopOpacity="0.08" /><stop offset="100%" stopColor={d.color} stopOpacity="0.04" /></>}
                  </radialGradient>; })}
              </defs>
              <circle cx={TW_CX} cy={TW_CY} r="182" fill="none" stroke="rgba(212,160,83,0.12)" strokeWidth="8" />
              <circle cx={TW_CX} cy={TW_CY} r="176" fill="none" stroke="#5A4F3E" strokeWidth="2" />
              {TW_CATS.map((cat, i) => { const d = TASTING_WHEEL[cat], isAct = selectedNotes.some(n => n === cat || d.notes.includes(n)), isExp = expandedCategory === cat;

                const nc = twAllNotes(cat).filter(n => selectedNotes.includes(n)).length;

                return <g key={cat}>
                  <path d={twSegPath(i, 42, 168)} fill={`url(#twg-${cat})`} stroke={isAct ? `${d.color}CC` : "#5A4F3E"} strokeWidth={isAct ? 2.5 : 1.5}
                    style={{ cursor: "pointer", transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)" }}
                    onClick={() => handleWheelTap(cat)} />
                  {isAct && <><path d={twRimArc(i, 166)} fill="none" stroke={d.glow} strokeWidth={isExp ? 3 : 2} strokeLinecap="round" opacity={isExp ? 0.8 : 0.5} style={{ transition: "all 0.3s", filter: `drop-shadow(0 0 4px ${d.glow}66)` }} pointerEvents="none" />
                    <path d={twRimArc(i, 44)} fill="none" stroke={d.color} strokeWidth="2" strokeLinecap="round" opacity="0.4" pointerEvents="none" /></>}
                  {nc > 0 && (() => { const bp = twLabelPos(i, 155); return <g style={{ filter: `drop-shadow(0 0 3px ${d.color}88)` }}><circle cx={bp.x} cy={bp.y} r="10" fill={d.color} opacity="0.95" /><text x={bp.x} y={bp.y} textAnchor="middle" dominantBaseline="central" fill="#1A1510" fontSize="9" fontWeight="800">{nc}</text></g>; })()}
                </g>; })}
              {TW_CATS.map((_, i) => { const s = twSpoke(i); return <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke="#5A4F3E" strokeWidth="1.5" pointerEvents="none" opacity="0.8" />; })}
              {TW_CATS.map((cat, i) => { const d = TASTING_WHEEL[cat], isAct = selectedNotes.some(n => n === cat || d.notes.includes(n)), pos = twLabelPos(i, 120), ipos = twLabelPos(i, 78);

                return <g key={`tl-${cat}`} pointerEvents="none" style={{ filter: isAct ? `drop-shadow(0 0 6px ${d.color}44)` : "none" }}>
                  <text x={ipos.x} y={ipos.y} textAnchor="middle" dominantBaseline="central" fontSize="24">{d.icon}</text>
                  <text x={pos.x} y={pos.y} textAnchor="middle" dominantBaseline="central" fill={isAct ? d.color : "#B5A898"} fontSize="13" fontWeight={isAct ? "800" : "600"} letterSpacing="0.5">{cat.toUpperCase()}</text>
                </g>; })}
              {Array.from({ length: 32 }).map((_, i) => { const a = (i * (360 / 32) - 90) * Math.PI / 180; return <line key={`tt-${i}`} x1={TW_CX + 170 * Math.cos(a)} y1={TW_CY + 170 * Math.sin(a)} x2={TW_CX + 174 * Math.cos(a)} y2={TW_CY + 174 * Math.sin(a)} stroke="#5A4F3E" strokeWidth="0.75" />; })}
              <circle cx={TW_CX} cy={TW_CY} r="40" fill="#1A1510" stroke="#5A4F3E" strokeWidth="2.5" />
              <circle cx={TW_CX} cy={TW_CY} r="36" fill="none" stroke="rgba(212,160,83,0.15)" strokeWidth="1" />
              <circle cx={TW_CX} cy={TW_CY} r="32" fill="none" stroke="rgba(212,160,83,0.10)" strokeWidth="0.75" />
              {selectedNotes.length > 0 ? <><text x={TW_CX} y={TW_CY - 8} textAnchor="middle" dominantBaseline="central" fill="#D4A053" fontSize="26" fontWeight="800" fontFamily={FONT_DISPLAY} style={{ filter: "drop-shadow(0 0 6px rgba(212,160,83,0.3))" }}>{selectedNotes.length}</text><text x={TW_CX} y={TW_CY + 12} textAnchor="middle" dominantBaseline="central" fill="#B5A898" fontSize="8.5" fontFamily={FONT_DISPLAY} letterSpacing="1.5" fontWeight="700">{selectedNotes.length === 1 ? "NOTE" : "NOTES"}</text></>
                : <><text x={TW_CX} y={TW_CY - 4} textAnchor="middle" dominantBaseline="central" fill="#5A5045" fontSize="8" fontFamily={FONT_DISPLAY} letterSpacing="0.5" fontWeight="600">TAP TO</text><text x={TW_CX} y={TW_CY + 9} textAnchor="middle" dominantBaseline="central" fill="#5A5045" fontSize="8" fontFamily={FONT_DISPLAY} letterSpacing="0.5" fontWeight="600">START</text></>}
            </svg>
          </div>
          {/* Expanded sub-notes panel */}
          {expandedCategory && (() => { const ed = TASTING_WHEEL[expandedCategory]; return (
            <div style={{ marginTop: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", background: `${ed.color}14`, border: `1px solid ${ed.color}33`, borderRadius: "10px 10px 0 0", borderBottom: "none" }}>
                <span style={{ fontSize: "0.9375rem" }}>{ed.icon}</span>
                <span style={{ color: ed.color, fontSize: "0.75rem", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", flex: 1 }}>{expandedCategory}</span>
                <button onClick={() => setExpandedCategory(null)} style={{ background: "none", border: "none", color: "#B5A898", fontSize: "0.6875rem", cursor: "pointer", padding: "4px 8px" }} aria-label="Collapse category">▲</button>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 12px", background: "#150F0A", border: `1px solid ${ed.color}33`, borderRadius: "0 0 10px 10px" }}>
                <button onClick={() => toggleNote(expandedCategory)} style={{ padding: "7px 14px", borderRadius: 18, fontSize: "0.6875rem", cursor: "pointer", border: `1.5px solid ${ed.color}`, background: selectedNotes.includes(expandedCategory) ? ed.color : "transparent", color: selectedNotes.includes(expandedCategory) ? "#1A1510" : ed.color, fontWeight: 700, transition: "all 0.15s" }} aria-label={`Toggle ${expandedCategory}`}>{expandedCategory}{selectedNotes.includes(expandedCategory) ? " ✕" : ""}</button>
                <div style={{ width: 3, height: 3, borderRadius: "50%", background: "#3D3428", alignSelf: "center" }} />
                {ed.notes.map(note => { const sel = selectedNotes.includes(note); return (
                  <button key={note} onClick={() => toggleNote(note)} style={{ padding: "7px 14px", borderRadius: 18, fontSize: "0.6875rem", cursor: "pointer", border: `1px solid ${sel ? ed.color : "#5A4F3E88"}`, background: sel ? ed.color : "transparent", color: sel ? "#1A1510" : "#B5A898", fontWeight: sel ? 600 : 400, transition: "all 0.15s" }} aria-label={`Toggle ${note}`}>{note}{sel ? " ✕" : ""}</button>
                ); })}
              </div>
            </div>
          ); })()}
          {/* Palate free-text below wheel */}
          <div style={{ marginTop: 12 }}>
            {selectedNotes.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {selectedNotes.map(note => { const cat = NOTE_TO_DIMENSION[note]; const color = cat && TASTING_WHEEL[cat] ? TASTING_WHEEL[cat].color : "#C4A469"; return (
                  <span key={note} style={{ display: "inline-block", padding: "4px 10px", borderRadius: 14, fontSize: "0.75rem", border: `1px solid ${color}66`, background: `${color}18`, color }}>{note}</span>
                ); })}
              </div>
            )}
            <textarea value={palate} onChange={e => { setPalate(e.target.value); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }} aria-label="Palate" placeholder="Flavors on your tongue" maxLength={200}
              ref={el => { if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } }}
              style={{ width: "100%", padding: "10px 12px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, color: "#F5E6D0", fontSize: "0.875rem", boxSizing: "border-box", resize: "none", overflow: "hidden", minHeight: 38, lineHeight: 1.4, fontFamily: "inherit", wordBreak: "break-word", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }} />
            {palate.length > 160 && <div style={{ textAlign: "right", fontSize: "0.6875rem", marginTop: 2, color: palate.length >= 195 ? "#E57373" : palate.length >= 180 ? "#C4A469" : "#8B7E6A" }}>{palate.length}/200</div>}
          </div>
        </div>
        {/* Finish — standalone collapsible section */}
        {(() => {
          const isOpen = expandedTastingField === "finish";
          const hasValue = finish.trim().length > 0;
          return (
            <div style={{ marginBottom: 16 }}>
              <button onClick={() => setExpandedTastingField(isOpen ? null : "finish")}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "12px 14px", background: isOpen ? "#3D3428" : hasValue ? "rgba(212,160,83,0.06)" : "#150F0A",
                  border: `1px solid ${isOpen ? "#D4A053" : hasValue ? "rgba(212,160,83,0.2)" : "#3D3428"}`,
                  borderRadius: isOpen ? "10px 10px 0 0" : 10, cursor: "pointer", transition: "all 0.15s" }}>
                <div style={flexCenterGap10Style}>
                  <span style={font0875Style}>✨</span>
                  <span style={{ color: hasValue ? "#D4A053" : "#F5E6D0", fontSize: "0.8125rem", fontWeight: 600 }}>Finish</span>
                  <span style={dimText075Style}>{hasValue ? `— ${finish.slice(0, 40)}${finish.length > 40 ? "…" : ""}` : "How does it linger?"}</span>
                </div>
                <span style={{ color: "#A69880", fontSize: "0.625rem", transition: "transform 0.15s", transform: isOpen ? "rotate(180deg)" : "none" }}>▼</span>
              </button>
              {isOpen && (
                <div style={{ padding: "10px 14px 14px", background: "#1E1A14", border: "1px solid #D4A053", borderTop: "none", borderRadius: "0 0 10px 10px" }}>
                  <TastingSuggestionPills field="finish" value={finish} onChange={setFinish} />
                  <textarea value={finish} onChange={e => { setFinish(e.target.value); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }} aria-label="Finish" placeholder="How does it linger?" maxLength={200} autoFocus
                    ref={el => { if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } }}
                    style={{ width: "100%", padding: "10px 12px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, color: "#F5E6D0", fontSize: "0.875rem", boxSizing: "border-box", resize: "none", overflow: "hidden", minHeight: 38, lineHeight: 1.4, fontFamily: "inherit", wordBreak: "break-word", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }} />
                  {finish.length > 160 && <div style={{ textAlign: "right", fontSize: "0.6875rem", marginTop: 2, color: finish.length >= 195 ? "#E57373" : finish.length >= 180 ? "#C4A469" : "#8B7E6A" }}>{finish.length}/200</div>}
                </div>
              )}
            </div>
          );
        })()}
        <div style={mb16Style}>
          <label style={formLabelStyle}>Location</label>
          <LocationSearch value={location} onChange={setLocation} />
        </div>
        {/* Tasting With */}
        <div style={mb24Style}>
          <label style={{ color: "#B5A898", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 1.5, display: "block", marginBottom: 8 }}>Tasting With</label>
          {drinkingWith.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
              {drinkingWith.map(fId => {

                const f = friends.find(x => x.id === fId);
                if (!f) return null;

                return (
                  <div key={fId} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", background: "rgba(212,160,83,0.1)", border: "1px solid rgba(212,160,83,0.25)", borderRadius: 20 }}>
                    <span style={font0875Style}>{f.avatar}</span>
                    <span style={goldText08125BoldStyle}>{f.name.split(" ")[0]}</span>
                    <button onClick={() => setDrinkingWith(prev => prev.filter(id => id !== fId))}
                      style={{ background: "none", border: "none", color: "#B5A898", fontSize: "0.875rem", cursor: "pointer", padding: 0, lineHeight: 1 }} aria-label="Close">✕</button>
                  </div>
                );
              })}
            </div>
          )}
          {friends.length > 0 ? (
            <div>
              <input value={dwSearch} onChange={e => setDwSearch(e.target.value)} aria-label="Search friends to tag" placeholder="Search friends to tag..."
                style={{ width: "100%", padding: "8px 12px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, color: "#F5E6D0", fontSize: "0.8125rem", boxSizing: "border-box", marginBottom: 6 }} />
              {dwSearch.trim().length > 0 && (
                <div style={{ maxHeight: 130, overflow: "auto" }}>
                  {friends.filter(f => !drinkingWith.includes(f.id) && f.name.toLowerCase().includes(dwSearch.toLowerCase())).map(f => (
                    <div key={f.id} onClick={() => { setDrinkingWith(prev => [...prev, f.id]); setDwSearch(""); }}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", cursor: "pointer", borderRadius: 8, transition: "background 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#3D3428"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <span style={font10Style}>{f.avatar}</span>
                      <span style={{ color: "#F5E6D0", fontSize: "0.8125rem" }}>{f.name}</span>
                    </div>
                  ))}
                  {friends.filter(f => !drinkingWith.includes(f.id) && f.name.toLowerCase().includes(dwSearch.toLowerCase())).length === 0 && (
                    <div style={{ padding: 8, color: "#B5A898", fontSize: "0.75rem" }}>No matching friends.</div>
                  )}
                </div>
              )}
              {dwSearch.trim().length === 0 && drinkingWith.length === 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {friends.slice(0, 4).map(f => (
                    <button key={f.id} onClick={() => setDrinkingWith(prev => [...prev, f.id])}
                      style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 20, cursor: "pointer", transition: "border-color 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = "#D4A053"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
                      <span style={font08125Style}>{f.avatar}</span>
                      <span style={mutedText075Style}>{f.name.split(" ")[0]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={mutedText075Style}>Add friends on your profile to tag them here.</div>
          )}
        </div>
        {/* Notes / Comments */}
        <div style={mb24Style}>
          <label style={formLabelStyle}>Notes / Comments</label>
          <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Share your thoughts..." maxLength={1000} aria-label="Notes or comments"
            style={{ width: "100%", padding: "10px 12px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, color: "#F5E6D0", fontSize: "0.875rem", minHeight: 80, resize: "vertical", boxSizing: "border-box" }} />
        </div>
        <div style={mb24Style}>
          <label style={{ color: "#B5A898", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 1.5, display: "block", marginBottom: 8 }}>Your Rating</label>
          {/* Star slider — 16px inner padding so 0 and 5 are easy to reach */}
          <div style={{ position: "relative", height: 32, borderRadius: 16, background: "#150F0A", border: "1px solid #3D3428", cursor: "pointer", userSelect: "none" }}
            onClick={(e) => {

              const rect = e.currentTarget.getBoundingClientRect();

              const PAD = 16;

              const x = e.clientX - rect.left - PAD;

              const usable = rect.width - PAD * 2;

              const pct = Math.max(0, Math.min(1, x / usable));
              setRating(snapToQuarter(pct * 5));
            }}>
            {/* Filled track */}
            {rating > 0 && <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `calc(16px + (100% - 32px) * ${rating / 5})`, borderRadius: 16, background: "linear-gradient(90deg, #8B5E34, #D4A053, #F0D98C)", transition: "width 0.12s ease-out" }} />}
            {/* Star markers at 1–5 */}
            {[1, 2, 3, 4, 5].map(s => (
              <div key={s} style={{ position: "absolute", left: `calc(16px + (100% - 32px) * ${s / 5})`, top: "50%", transform: "translate(-50%, -50%)", fontSize: "0.8125rem", color: rating >= s ? "#1A1510" : "#5A4F3E", pointerEvents: "none", zIndex: 1, transition: "color 0.12s" }}>★</div>
            ))}
            {/* Thumb */}
            {rating > 0 && (
              <div style={{ position: "absolute", top: "50%", left: `calc(16px + (100% - 32px) * ${rating / 5})`, transform: "translate(-50%, -50%)", width: 28, height: 28, borderRadius: "50%", background: "#D4A053", border: "3px solid #F5E6D0", boxShadow: "0 0 10px rgba(0,0,0,0.6)", transition: "left 0.12s ease-out", pointerEvents: "none", zIndex: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: "0.75rem", color: "#1A1510", fontWeight: 700 }}>★</span>
              </div>
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, padding: "0 16px" }}>
            <span style={dimText05625UpperSpacedStyle}>0</span>
            <span style={dimText05625UpperSpacedStyle}>5</span>
          </div>
          {rating > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, justifyContent: "center" }}>
              <span style={{ color: "#D4A053", fontWeight: 700, fontSize: "1.375rem", fontFamily: FONT_DISPLAY }}>{fmtRating(rating)}</span>
              <span style={mutedText075Style}>/ 5.00</span>
              <button onClick={() => setRating(0)} style={{ background: "none", border: "none", color: "#A69880", fontSize: "0.75rem", cursor: "pointer", padding: "0 0 0 4px" }} aria-label="Close">✕</button>
            </div>
          )}
        </div>
        <button onClick={submit} disabled={!rating || barPrompt}
          style={{ width: "100%", padding: "14px 24px", background: (rating && !barPrompt) ? "linear-gradient(135deg, #D4A053, #8B5E34)" : "#5A4F3E", border: "none", borderRadius: 10, color: (rating && !barPrompt) ? "#FFF" : "#A69880", fontSize: "1.0rem", fontWeight: 700, cursor: (rating && !barPrompt) ? "pointer" : "default", fontFamily: FONT_DISPLAY, letterSpacing: 0.5 }}>
          Log This Pour
        </button>
        {/* Bar Inventory Prompt (after at-home check-in) */}
        {barPrompt && (
          <div style={{ marginTop: 20, background: "#150F0A", border: "1px solid #D4A053", borderRadius: 14, padding: 20, animation: "fadeIn 0.2s ease" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{ fontSize: "1.375rem" }}>{barPrompt.mode === "update" ? "📊" : "➕"}</span>
              <div>
                <div style={{ color: "#F5E6D0", fontSize: "0.9375rem", fontWeight: 700, fontFamily: FONT_DISPLAY }}>
                  {barPrompt.mode === "update" ? "Update Your Bottle?" : "Add to Your Bar?"}
                </div>
                <div style={mutedText075Mt2Style}>
                  {barPrompt.mode === "update"
                    ? `This bottle is in your bar at ${barPrompt.currentFill}% — update the fill level?`
                    : "This bottle isn't in your bar yet — want to add it?"}
                </div>
              </div>
            </div>
            {/* Fill Level Slider */}
            <div style={mb16Style}>
              <div style={flexBetweenCenterMb6Style}>
                <label style={{ color: "#B5A898", fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: 1.5 }}>Fill Level</label>
                <span style={{ color: "#D4A053", fontSize: "0.875rem", fontWeight: 700 }}>{barPromptFill}%</span>
              </div>
              <div style={{ position: "relative", height: 32, background: "#1E1A14", borderRadius: 8, border: "1px solid #3D3428", overflow: "hidden" }}>
                <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${barPromptFill}%`, background: "linear-gradient(90deg, #8B5E34, #D4A053)", borderRadius: "7px 0 0 7px", transition: "width 0.15s" }} />
                <input type="range" min="0" max="100" step="5" value={barPromptFill} aria-label="Bottle fill level" onChange={e => setBarPromptFill(parseInt(e.target.value))}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer", margin: 0 }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                {[0, 25, 50, 75, 100].map(v => (
                  <button key={v} onClick={() => setBarPromptFill(v)}
                    style={{ background: "none", border: "none", color: barPromptFill === v ? "#D4A053" : "#5A4F3E", fontSize: "0.625rem", cursor: "pointer", padding: "6px 8px", fontWeight: barPromptFill === v ? 700 : 400 }}>
                    {v}%
                  </button>
                ))}
              </div>
            </div>
            {/* Action Buttons */}
            <div style={flexGap10Style}>
              <button onClick={handleBarPromptConfirm}
                style={{ flex: 1, padding: "11px 16px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer", fontFamily: FONT_DISPLAY }}>
                {barPrompt.mode === "update" ? "Update Fill" : "Add to Bar"}
              </button>
              <button onClick={handleBarPromptSkip}
                style={{ padding: "11px 16px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#B5A898", fontSize: "0.8125rem", cursor: "pointer" }}>
                Skip
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── UNIFIED ADD MISSING MODAL ─────────────
// Single entry point for adding brands or expressions.
// UPC scan is primary input (source of truth via COLA data).
// Brand name + expression name are secondary fuzzy-match hints.

const AddMissingModal = ({ addModalFormRef, brands, setBrands, bourbons, setBourbons,
  currentUser, selectedBourbon, setSelectedBourbon, addBourbonPreselectedBrand,
  setShowAddBrandModal, setShowAddBourbonModal, setAddBourbonPreselectedBrand,
  getBrand, isBrowsableBrand, resolveUPC, showToast, navigateTo, db }) => {
  // Restore from sessionStorage if ref is empty (webview restart)
  let f = addModalFormRef.current;
  if (!f.brandGuess && !f.exprGuess && !f.upcInput) {
    try { const saved = sessionStorage.getItem("br_add_form"); if (saved) f = { ...f, ...JSON.parse(saved) }; } catch {}
  }

  const [step, setStep] = useState(f.step || "scan");

  const [brandGuess, setBrandGuess] = useState(f.brandGuess || (addBourbonPreselectedBrand ? (getBrand(addBourbonPreselectedBrand)?.name || "") : ""));

  const [exprGuess, setExprGuess] = useState(f.exprGuess || "");

  const [upcInput, setUpcInput] = useState(f.upcInput || "");

  const [showScanner, setShowScanner] = useState(false);

  const [upcResult, setUpcResult] = useState(null);

  const [error, setError] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [dismissedMatches, setDismissedMatches] = useState(false);

  // Persist form state back to ref on every change
  useEffect(() => {
    const state = { step, brandGuess, exprGuess, upcInput };
    addModalFormRef.current = state;
    try { sessionStorage.setItem("br_add_form", JSON.stringify(state)); } catch {}
  }, [step, brandGuess, exprGuess, upcInput]);

  // Fuzzy match results

  const brandMatches = brandGuess.trim().length >= 2
    ? brands.filter(b => isBrowsableBrand(b, false) && b.name.toLowerCase().includes(brandGuess.toLowerCase())).slice(0, 5)
    : [];

  const exprMatches = exprGuess.trim().length >= 2
    ? bourbons.filter(b => b.name.toLowerCase().includes(exprGuess.toLowerCase())).slice(0, 5)
    : [];

  const lookupUPC = (code) => {

    const match = resolveUPC(code);
    if (match) {
      setUpcResult(match);
      if (!brandGuess && match.distillery) setBrandGuess(match.distillery);
      if (!exprGuess && match.name) setExprGuess(match.name);
      setError("");
      setStep("details");
    } else {
      setUpcResult(null);
      setError("");
      setStep("details");

    }

  };

  const close = () => { setShowAddBourbonModal(false); setShowAddBrandModal(false); setAddBourbonPreselectedBrand(null); addModalFormRef.current = { step: "scan", brandGuess: "", exprGuess: "", upcInput: "" }; try { sessionStorage.removeItem("br_add_form"); } catch {} };

  const submit = async () => {
    if (!upcInput.trim()) { setError("UPC scan is required to submit."); return; }
    if (!brandGuess.trim()) { setError("Brand name is required."); return; }
    if (!exprGuess.trim()) { setError("Expression name is required."); return; }
    setSubmitting(true);
    setError("");

    // Find or flag brand

    const matchedBrand = brands.find(b => b.name.toLowerCase() === brandGuess.trim().toLowerCase());
    let brandId = matchedBrand?.id;

    // If no exact brand match, create a pending brand submission
    if (!brandId) {

      const newBrand = {
        id: generateId("b_"),
        name: sanitizeInput(brandGuess.trim(), 100),
        location: "",
        brandType: "ndp",
        isSourced: true,
        image: "barrel",
        userAdded: true,
        submissionStatus: "pending",
        submittedBy: currentUser?.uid,
        submittedAt: new Date().toISOString(),
      };
      setBrands(prev => [...prev, newBrand]);

      if (useSupabase()) {

        const dbRow = await db.insertBrand({ ...newBrand, addedBy: currentUser?.uid });

        if (dbRow) {
          setBrands(prev => prev.map(b => b.id === newBrand.id ? { ...b, id: dbRow.id } : b));
          brandId = dbRow.id;
        } else { brandId = newBrand.id; }
      } else { brandId = newBrand.id; }

    }

    // Create the expression submission

    const normalizedExprName = normalizeColaName(exprGuess.trim(), brandGuess || '');
    const newBourbon = {
      id: generateId("e_"),
      name: sanitizeInput(normalizedExprName, 100),
      brandId,
      proof: upcResult?.proof ? Math.min(Math.max(parseFloat(upcResult.proof), 80), 160) : null,
      age: upcResult?.age || "",
      mashBill: upcResult?.mashBill || "",
      msrp: upcResult?.msrp ? parseFloat(upcResult.msrp) : null,
      type: upcResult?.type || "Bourbon",
      notes: "",
      userAdded: true,
      submissionStatus: "pending",
      submittedBy: currentUser?.uid,
      submittedAt: new Date().toISOString(),
    };
    setBourbons(prev => [...prev, newBourbon]);

    if (useSupabase()) {

      const dbRow = await db.insertBourbon({ ...newBourbon, addedBy: currentUser?.uid });

      if (dbRow) {
        setBourbons(prev => prev.map(b => b.id === newBourbon.id ? { ...b, id: dbRow.id } : b));

      }

    }

    // Store UPC mapping if we have one
    if (upcInput.trim()) {

      const finalBourbonId = newBourbon.id;
      if (useSupabase()) {

        const sb = getSupabase();
        if (sb) {
          await sb.from("upc_codes").upsert({
            upc: upcInput.trim().replace(/\D/g, ""),
            expression_id: finalBourbonId,
            submitted_by: currentUser?.uid,
          }, { onConflict: "upc" });

        }

      }

    }

    showToast("📋 Submitted! You can check in with it now while it's reviewed.");
    setSubmitting(false);
    close();
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={{ background: "#1E1A14", border: "1px solid #5A4F3E", borderRadius: 16, width: "100%", maxWidth: 520, maxHeight: "90vh", overflow: "auto", padding: 32 }}>
        <div style={flexBetweenCenterMb16Style}>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.375rem", color: "#F5E6D0", margin: 0 }}>
            {step === "scan" ? "Scan to Add" : "Add Missing Expression"}
          </h2>
          <button onClick={close} style={mutedText15Style} aria-label="Close">✕</button>
        </div>
        <div style={{ background: "rgba(212,160,83,0.04)", borderRadius: 10, padding: "12px 14px", marginBottom: 20, border: "1px solid rgba(212,160,83,0.12)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ fontSize: "1.25rem", flexShrink: 0, lineHeight: 1 }}>💡</span>
            <div style={{ fontSize: "0.6875rem", color: "#B5A898", lineHeight: 1.6 }}>
              <span style={{ color: "#D4A053", fontWeight: 700 }}>Why are some expressions missing?</span> Our catalog is built from TTB label approvals (COLAs), which often cover an entire product line under one generic name — like "Cask Strength" for all Maker's Mark cask strength variants. Specific releases like <em>46 Cask Strength French Oaked</em> don't get separate filings. Your submissions fill these gaps, and <span style={goldTextStyle}>scanning the UPC</span> lets us link your entry to official records for verification.
            </div>
          </div>
        </div>
        {error && <div style={{ background: "rgba(178,34,34,0.12)", border: "1px solid rgba(178,34,34,0.3)", borderRadius: 8, padding: "10px 14px", marginBottom: 16, color: "#CD5C5C", fontSize: "0.8125rem" }}>{error}</div>}
        {/* STEP 1: UPC Scan */}
        <div style={{ background: "#150F0A", borderRadius: 12, padding: 16, marginBottom: 20, border: `1px solid ${upcInput ? "#3D6B28" : "#3D3428"}` }}>
          <div style={{ fontSize: "0.6875rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10, fontWeight: 600 }}>
            {upcInput ? "✓ UPC Captured" : "Scan or Enter Barcode"}
          </div>
          <div style={{ display: "flex", gap: 8, maxWidth: "100%", boxSizing: "border-box" }}>
<input value={upcInput} onChange={e => setUpcInput(e.target.value)} aria-label="UPC code" placeholder="Enter UPC barcode..." style={{ ...formInputStyle, padding: "0.75rem 0.85rem", fontSize: "1rem", fontFamily: "monospace", letterSpacing: "0.12em", flex: 1, minWidth: 0 }}
  onKeyDown={e => { if (e.key === "Enter" && upcInput.trim()) lookupUPC(upcInput.trim()); }} maxLength={20} />
<button onClick={() => setShowScanner(true)} aria-label="Scan barcode with camera"
  style={{ width: 48, minWidth: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center", background: upcInput ? "#2A3D20" : "rgba(212,160,83,0.08)", border: `1px solid ${upcInput ? "#3D6B28" : "#5A4F3E"}`, borderRadius: 8, color: upcInput ? "#4CAF50" : "#D4A053", cursor: "pointer", flexShrink: 0, transition: "all 0.15s" }}>
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2"/><line x1="7" y1="7" x2="7" y2="17"/><line x1="10" y1="7" x2="10" y2="17"/><line x1="13" y1="7" x2="13" y2="17"/><line x1="17" y1="7" x2="17" y2="17"/></svg>
</button>
</div>
          {upcResult && (
            <div style={{ marginTop: 10, padding: "8px 12px", background: "rgba(212,160,83,0.06)", borderRadius: 8, border: "1px solid rgba(212,160,83,0.15)" }}>
              <div style={goldText075BoldStyle}>UPC Match Found</div>
              <div style={{ color: "#F5E6D0", fontSize: "0.8125rem", marginTop: 4 }}>{upcResult.name} — {upcResult.proof}°</div>
            </div>
          )}
          {upcInput && !upcResult && step === "details" && (
            <div style={{ marginTop: 10, padding: "8px 12px", background: "rgba(139,126,107,0.06)", borderRadius: 8, border: "1px solid #3D3428" }}>
              <div style={mutedText075Style}>UPC not in our database yet — we'll register it with your submission.</div>
            </div>
          )}
        </div>
        {showScanner && <BarcodeScanner autoResolve={true} onMatch={(match) => { setShowScanner(false); setPendingScanUpc(null); logScan(match._scannedUpc, true, match.expression_id, "addmissing"); showToast("This expression already exists \u2014 taking you there."); close(); const b = bourbons.find(bb => bb.id === match.expression_id); if (b) { setSelectedBourbon(b); navigateTo("bourbon-detail"); } }} onNoMatch={(code) => { setShowScanner(false); logScan(code, false, null, "addmissing"); setUpcInput(code); showToast("UPC not in our database \u2014 fill in the details below."); }} onClose={() => setShowScanner(false)} />}
        {/* STEP 2: Brand + Expression names (always visible) */}
        <div style={mb16Style}>
          <label style={formLabelStyle}>Brand Name *</label>
          <input aria-label="Brand name" value={brandGuess} onChange={e => { setBrandGuess(e.target.value); setError(""); }}
            placeholder="e.g. Buffalo Trace, Willett, Smoke Wagon..."
            style={formInputStyle} maxLength={100} />
          {brandMatches.length > 0 && brandGuess && !brands.some(b => b.name.toLowerCase() === brandGuess.toLowerCase()) && (
            <div style={{ marginTop: 4, background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "4px 10px", fontSize: "0.5625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1 }}>Existing brands</div>
              {brandMatches.map(b => (
                <div key={b.id} onClick={() => setBrandGuess(b.name)}
                  style={{ padding: "8px 10px", cursor: "pointer", borderTop: "1px solid #1A1510", fontSize: "0.8125rem", color: "#F5E6D0" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#3D3428"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  {b.name} {b.brandType !== "distiller" && <span style={{ color: "#B5A898", fontSize: "0.625rem", marginLeft: 6 }}>({b.brandType})</span>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={mb16Style}>
          <label style={formLabelStyle}>Expression Name *</label>
          <input aria-label="Expression name" value={exprGuess} onChange={e => { setExprGuess(e.target.value); setError(""); setDismissedMatches(false); }}
            placeholder="e.g. Single Barrel Select, Cask Strength, 12 Year..."
            style={formInputStyle} maxLength={100} />
          {exprGuess && exprGuess.trim().length > 2 && (() => {
            const normalized = normalizeColaName(exprGuess.trim(), brandGuess || '');
            return normalized !== exprGuess.trim() ? (
              <div style={{ fontSize: '0.7rem', color: '#d4a04a', marginTop: 2, fontStyle: 'italic' }}>
                Will display as: {normalized}
              </div>
            ) : null;
          })()}
          {exprGuess && (() => {
            const hints = [];
            const upper = exprGuess.toUpperCase();
            if (exprGuess === upper && exprGuess.length > 5) hints.push('Name appears to be ALL CAPS \u2014 it will be title-cased automatically.');
            if (/\b(WHISKEY|WHISKY|BOURBON|STRAIGHT|KENTUCKY)\b/i.test(exprGuess) && exprGuess.trim().split(/\s+/).length > 3)
              hints.push('Whiskey type suffixes (e.g., "Kentucky Straight Bourbon Whiskey") will be stripped automatically.');
            if (/[\u2018\u2019\u201C\u201D]/.test(exprGuess))
              hints.push('Smart quotes will be normalized to standard quotes.');
            return hints.length > 0 ? (
              <div style={{ fontSize: '0.7rem', color: '#999', marginTop: 4 }}>
                {hints.map((h, i) => <div key={i}>{"\u2139\uFE0F"} {h}</div>)}
              </div>
            ) : null;
          })()}
          {exprMatches.length > 0 && exprGuess && !dismissedMatches && (
            <div style={{ marginTop: 4, background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "4px 10px", fontSize: "0.5625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1 }}>Already in catalog</div>
              {exprMatches.map(b => (
                <div key={b.id} onClick={() => { close(); setSelectedBourbon(b); navigateTo("bourbon-detail"); }}
                  style={{ padding: "8px 10px", borderTop: "1px solid #1A1510", fontSize: "0.8125rem", color: "#F5E6D0", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#3D3428"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  {b.name} <span style={{ color: "#B5A898", fontSize: "0.625rem", marginLeft: 4 }}>({getBrand(b.brandId)?.name || "?"})</span>
                </div>
              ))}
              <div onClick={() => setDismissedMatches(true)}
                style={{ padding: "8px 10px", borderTop: "1px solid #3D3428", fontSize: "0.75rem", color: "#D4A053", cursor: "pointer", textAlign: "center", fontWeight: 600 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(212,160,83,0.06)"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                None of these {"\u2014"} submit as new
              </div>
            </div>
          )}
        </div>
        {upcResult && (
          <div style={{ background: "#150F0A", borderRadius: 12, padding: 16, marginBottom: 20, border: "1px solid #3D3428" }}>
            <div style={{ fontSize: "0.6875rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8, fontWeight: 600 }}>Auto-filled from UPC</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px", fontSize: "0.8125rem" }}>
              {upcResult.proof && <div><span style={mutedTextStyle}>Proof:</span> <span style={lightTextStyle}>{upcResult.proof}°</span></div>}
              {upcResult.age && <div><span style={mutedTextStyle}>Age:</span> <span style={lightTextStyle}>{upcResult.age}</span></div>}
              {upcResult.msrp && <div><span style={mutedTextStyle}>MSRP:</span> <span style={lightTextStyle}>${upcResult.msrp}</span></div>}
              {upcResult.type && <div><span style={mutedTextStyle}>Type:</span> <span style={lightTextStyle}>{upcResult.type}</span></div>}
              {upcResult.mashBill && <div style={{ gridColumn: "1 / -1" }}><span style={mutedTextStyle}>Mash:</span> <span style={lightTextStyle}>{upcResult.mashBill}</span></div>}
            </div>
          </div>
        )}
<button onClick={submit} disabled={submitting}
          style={{ width: "100%", padding: "14px 24px", background: submitting ? "#5A4F3E" : "linear-gradient(135deg, #D4A053, #8B5E34)", border: "none", borderRadius: 10, color: "#FFF", fontSize: "1.0rem", fontWeight: 700, cursor: submitting ? "default" : "pointer", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, opacity: submitting ? 0.6 : 1 }}>
          {submitting ? "Submitting..." : "Submit for Review"}
        </button>
        <p style={{ color: "#A69880", fontSize: "0.6875rem", textAlign: "center", marginTop: 12, lineHeight: 1.5 }}>
          Submissions are reviewed within <strong style={goldTextStyle}>72 hours</strong>. You can check in immediately while it{"'"}s pending. Thank you for helping grow the catalog! 🥃
        </p>
      </div>
    </div>
  );
};

// ─── STATUS BADGES ────────────────────

const NosePalateFinish = ({ checkin: c }) => (c.nose || c.palate || c.finish) ? (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
    {[{ l: "Nose", v: c.nose }, { l: "Palate", v: c.palate }, { l: "Finish", v: c.finish }].filter(x => x.v).map(x => (
      <div key={x.l} style={{ padding: 10, background: "#150F0A", borderRadius: 8 }}>
        <div style={{ fontSize: "0.625rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{x.l}</div>
        <div style={{ fontSize: "0.8125rem", color: "#A69880", lineHeight: 1.4, wordBreak: "break-word", overflowWrap: "anywhere" }}>{x.v}</div>
      </div>
    ))}
  </div>
) : null;

const StatusBadge = ({ bourbon, size = "sm" }) => {

  const pad = size === "lg" ? "5px 12px" : "2px 8px";

  const fs = size === "lg" ? 12 : 10;

  return (
    <span style={{ display: "inline-flex", gap: 5 }}>
      {bourbon.limitedRelease && (
        <span style={{ padding: pad, borderRadius: 20, fontSize: fs, fontWeight: 700, letterSpacing: 0.5,
          background: "rgba(218,165,32,0.15)", color: "#DAA520", border: "1px solid rgba(218,165,32,0.35)" }}>
          ⚡ LIMITED
        </span>
      )}
      {bourbon.discontinued && (
        <span style={{ padding: pad, borderRadius: 20, fontSize: fs, fontWeight: 700, letterSpacing: 0.5,
          background: "rgba(178,34,34,0.12)", color: "#CD5C5C", border: "1px solid rgba(178,34,34,0.3)" }}>
          ✕ DISCONTINUED
        </span>
      )}
    </span>
  );
};

// ─── GRAIN MAP HELPERS (module scope) ────────────────

const GRAIN_COLORS = Object.freeze({ corn: "#D4A017", rye: "#B8733A", wheat: "#D4B896", barley: "#8B6914" });

const getGrainDescriptor = (rye, wheat) => {
  if (rye >= 18) return "a high-rye recipe";
  if (wheat > 0 && rye === 0) return "a wheated bourbon";
  if (rye >= 8) return "a traditional bourbon recipe";
  return "a low-rye, corn-forward recipe";
};

const getGrainProfile = (mashData) => {
  const { ratings } = mashData;
  if (ratings.length < 3) return "Early Insights";
  const highRye = ratings.filter(r => r.rye >= 18);
  const wheated = ratings.filter(r => r.wheat > 0 && r.rye === 0);
  const traditional = ratings.filter(r => r.rye >= 8 && r.rye < 18);
  // Count 3-4: require 75%+ in one style AND avg ≥ 3.5
  if (ratings.length < 5) {
    const n = ratings.length;
    const styleOf = (arr, label) => arr.length >= Math.ceil(n * 0.75) && arr.reduce((s, r) => s + r.rating, 0) / arr.length >= 3.5 ? label : null;
    return styleOf(highRye, "High-Rye Fan") || styleOf(wheated, "Wheated Whiskey Lover") || styleOf(traditional, "Traditional Bourbon Purist") || "Early Insights";
  }
  const avgOf = (arr) => arr.length >= 2 ? arr.reduce((s, r) => s + r.rating, 0) / arr.length : null;
  const hrAvg = avgOf(highRye), wAvg = avgOf(wheated), trAvg = avgOf(traditional);
  const avgs = [hrAvg, wAvg, trAvg].filter(v => v !== null);
  if (avgs.length < 2) return "Versatile Palate";
  const maxAvg = Math.max(...avgs);
  const others = avgs.filter(v => v !== maxAvg);
  const gap = maxAvg - Math.max(...others);
  if (gap <= 0.3) return "Versatile Palate";
  if (maxAvg === hrAvg) return "High-Rye Fan";
  if (maxAvg === wAvg) return "Wheated Whiskey Lover";
  return "Traditional Bourbon Purist";
};

const generateGrainInsights = (mashData) => {
  const insights = [];
  const { ratings, sweetSpots } = mashData;
  const highRye = ratings.filter(r => r.rye >= 18);
  const lowRye = ratings.filter(r => r.rye < 18);
  if (highRye.length >= 3 && lowRye.length >= 3) {
    const hrAvg = highRye.reduce((s, r) => s + r.rating, 0) / highRye.length;
    const lrAvg = lowRye.reduce((s, r) => s + r.rating, 0) / lowRye.length;
    const delta = hrAvg - lrAvg;
    if (delta > 0.3) insights.push({ emoji: "\uD83C\uDF3E", text: `Your ratings trend ${delta.toFixed(1)}\u2605 higher on high-rye bourbons` });
    else if (delta < -0.3) insights.push({ emoji: "\uD83C\uDF3D", text: `You rate traditional recipes ${(-delta).toFixed(1)}\u2605 higher than high-rye` });
  }
  const wheated = ratings.filter(r => r.wheat > 0 && r.rye === 0);
  if (wheated.length <= 2 && ratings.length >= 5) {
    insights.push({ emoji: "\uD83C\uDF3E", text: `Only ${wheated.length} wheated bourbon${wheated.length !== 1 ? "s" : ""} tried \u2014 room to explore` });
  } else if (wheated.length >= 3) {
    const wAvg = wheated.reduce((s, r) => s + r.rating, 0) / wheated.length;
    const nonW = ratings.filter(r => !(r.wheat > 0 && r.rye === 0));
    if (nonW.length >= 3) {
      const nwAvg = nonW.reduce((s, r) => s + r.rating, 0) / nonW.length;
      if (wAvg - nwAvg > 0.3) insights.push({ emoji: "\uD83C\uDF3E", text: `You rate wheated bourbons ${(wAvg - nwAvg).toFixed(1)}\u2605 above average` });
    }
  }
  if (sweetSpots && sweetSpots.corn.min !== sweetSpots.corn.max) {
    insights.push({ emoji: "\uD83C\uDF3D", text: `Corn sweet spot: ${sweetSpots.corn.min}\u2013${sweetSpots.corn.max}%` });
  }
  const styles = { highRye: 0, wheated: 0, traditional: 0 };
  const highRated = ratings.filter(r => r.rating >= 4);
  for (const r of highRated) {
    if (r.rye >= 18) styles.highRye++;
    else if (r.wheat > 0 && r.rye === 0) styles.wheated++;
    else styles.traditional++;
  }
  if (highRated.length >= 5) {
    const dominant = Object.entries(styles).sort((a, b) => b[1] - a[1])[0];
    const pct = Math.round(dominant[1] / highRated.length * 100);
    if (pct >= 60) {
      const label = dominant[0] === "highRye" ? "high-rye" : dominant[0] === "wheated" ? "wheated" : "traditional";
      insights.push({ emoji: "\uD83D\uDCCA", text: `${pct}% of your top-rated pours are ${label}` });
    }
  }
  return insights.slice(0, 3);
};

const MashBillPreference = ({ mashData, expanded, onToggle, showGrainTooltip, setShowGrainTooltip }) => {
  if (!mashData || mashData.count === 0) return null;
  const { count, fingerprints, sweetSpots } = mashData;
  const profile = getGrainProfile(mashData);
  const insights = generateGrainInsights(mashData);

  // Lane chart computation
  const grains = ["corn", "rye", "wheat"];
  const grainLabels = { corn: "CORN", rye: "RYE", wheat: "WHEAT" };
  const fps = [...fingerprints.values()];
  const svgW = 300, laneH = 28, laneGap = 10, padL = 52, padR = 12, padT = 8, padB = 4;
  const svgH = padT + grains.length * laneH + (grains.length - 1) * laneGap + padB;
  const chartW = svgW - padL - padR;

  // Per-grain min/max from data
  const grainRanges = {};
  for (const g of grains) {
    const vals = fps.map(fp => fp[g]);
    if (vals.length === 0) { grainRanges[g] = { min: 0, max: 100 }; continue; }
    const lo = Math.min(...vals), hi = Math.max(...vals);
    grainRanges[g] = { min: Math.max(0, lo - 5), max: Math.min(100, hi + 5) };
  }
  const xScale = (g, v) => {
    const r = grainRanges[g];
    const range = r.max - r.min || 1;
    return padL + ((v - r.min) / range) * chartW;
  };

  return (
    <div style={grainMapSectionStyle}>
      <button onClick={onToggle}
        style={grainMapHeaderStyle}>
        <div style={flexCenterGap10Style}>
          <h3 style={grainSectionTitleStyle}>Grain Map</h3>
          <span style={grainBadgePillStyle}>{profile}</span>
          <span onClick={(e) => { e.stopPropagation(); setShowGrainTooltip(p => !p); }}
            style={{ ...grainInfoBtnBaseStyle, border: "1px solid " + (showGrainTooltip ? "#D4A053" : "#5A4F3E"), background: showGrainTooltip ? "rgba(212,160,83,0.15)" : "transparent", color: showGrainTooltip ? "#D4A053" : "#A69880" }}>?</span>
        </div>
        <span style={{ ...grainChevronStyle, transition: "transform 0.15s", transform: expanded ? "rotate(180deg)" : "none" }}>{"\u25BC"}</span>
      </button>
      {showGrainTooltip && (
        <div style={grainTooltipStyle}>
          <div style={flexBetweenCenterMb10Style}>
            <span style={grainTooltipTitleStyle}>How It Works</span>
            <button onClick={() => setShowGrainTooltip(false)} style={mutedText10Style} aria-label="Close">{"\u2715"}</button>
          </div>
          <p style={grainTooltipBodyStyle}>Grain Map shows how you rate bourbons based on their mash bill {"\u2014"} the grain recipe that shapes flavor. Higher-rye recipes tend to be spicier, wheated bourbons are softer and sweeter, and corn-forward recipes emphasize sweetness and body. The highlighted bands show where your ratings are highest. Your profile becomes more accurate as you check in more bourbons with known mash bills {"\u2014"} early insights are based on limited data and may shift as you explore more.</p>
        </div>
      )}
      {expanded && (
        <>
          {/* Lane Chart */}
          <div style={grainChartWrapStyle}>
            <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" style={grainSvgStyle}>
              {grains.map((g, gi) => {
                const y0 = padT + gi * (laneH + laneGap);
                const yMid = y0 + laneH / 2;
                const range = grainRanges[g];
                return (
                  <g key={g}>
                    {/* Lane background */}
                    <rect x={padL} y={y0} width={chartW} height={laneH} rx={4} fill="#1E1A14" />
                    {/* Sweet spot band — only with enough data */}
                    {count >= 5 && sweetSpots && sweetSpots[g] && sweetSpots[g].min !== sweetSpots[g].max && (
                      <rect x={xScale(g, sweetSpots[g].min)} y={y0} width={Math.max(1, xScale(g, sweetSpots[g].max) - xScale(g, sweetSpots[g].min))} height={laneH} rx={4} fill="rgba(196, 164, 105, 0.15)" />
                    )}
                    {/* Label — positioned in dedicated left margin */}
                    <text x={padL - 8} y={yMid} textAnchor="end" dominantBaseline="central" style={grainLaneLabelStyle}>{grainLabels[g]}</text>
                    {/* Axis ticks */}
                    <text x={padL} y={y0 + laneH + 9} textAnchor="start" fill="#F5E6D0" fontSize="7">{range.min}%</text>
                    <text x={padL + chartW} y={y0 + laneH + 9} textAnchor="end" fill="#F5E6D0" fontSize="7">{range.max}%</text>
                    {/* Dots for each fingerprint */}
                    {fps.map((fp, fi) => {
                      const avg = fp.sum / fp.total;
                      const color = avg >= 4 ? "#C4A469" : avg >= 3 ? "#B8733A" : "#999";
                      const r = Math.min(7, Math.max(3, fp.total * 1.5));
                      const jitter = (fi % 3 - 1) * 2;
                      return (
                        <circle key={fi} cx={xScale(g, fp[g])} cy={yMid + jitter} r={r} fill={color} opacity={0.85} />
                      );
                    })}
                  </g>
                );
              })}
            </svg>
            <div style={grainLegendStyle}>
              <span><span style={{ color: "#C4A469" }}>{"\u25CF"}</span> 4+ stars</span>
              <span><span style={{ color: "#B8733A" }}>{"\u25CF"}</span> 3{"\u2013"}3.99</span>
              <span><span style={{ color: "#999" }}>{"\u25CB"}</span> {"<"} 3</span>
            </div>
          </div>
          {/* Insight Chips */}
          {insights.length > 0 && (
            <div style={grainInsightsWrapStyle}>
              {insights.map((ins, i) => (
                <div key={i} style={grainInsightChipStyle}>{ins.emoji} {ins.text}</div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── BOURBON DETAIL VIEW ──────────────

const BourbonDetail = ({ bourbon, bourbons, selectedBourbon, variantFromParent,
  wishlist, myBar, checkins, currentUser, businessRoles, view, prevView, communityCheckins,
  setBourbons, setSelectedBourbon, setVariantFromParent, setSelectedBrand, setSelectedFacility,
  setShowCheckinModal, setCheckinBourbon, setWishlist, setMyBar, setBarSharePrompt,
  setViewCheckinDetail, setSuggestEditData, setShowSuggestEdit, setBusinessRoles,
  getBourbon, getDisplayBrand, getFacility, isBrowsableBourbon, getCheckins, getAvgRating,
  getCheckinCount, isBrandClaimed, getSubmissionStatus, canSeeSubmission, getExpressionStatus,
  getBrandBourbons, showToast, navigateTo, sanitizeText, sanitizeUrl, copyToClipboard,
  formatCount, appendAudit, communityRatingsMap, getBrand, getDisplayName, db,
  pendingScanUpc, setPendingScanUpc, upcMap }) => {

  const dist = getDisplayBrand(bourbon.brandId);
  const childBrand = getBrand(bourbon.brandId); // original brand (may differ from display parent)
  const showChildLabel = childBrand && dist && childBrand.id !== dist.id;

  const exprFacility = getFacility(bourbon.facilityId || dist?.facilityId);

  // COLA permit tells us the actual DSP that distilled this expression

  const colaDistillery = bourbon.colaPermitNumber ? getFacility(bourbon.colaPermitNumber) : null;

  const myCheckins = getCheckins(bourbon.id);

  const avgRating = getAvgRating(bourbon.id);

  const [ratingDrillOpen, setRatingDrillOpen] = useState(false);

  const inWishlist = wishlist.includes(bourbon.id);

  const inBar = myBar.find(b => b.bourbonId === bourbon.id && !b.emptyReason);

  const activeCount = myBar.filter(x => x.bourbonId === bourbon.id && !x.emptyReason).length;

  const canEditExpression = currentUser && businessRoles.some(r => r.businessId === bourbon.brandId && (r.role === "owner" || r.role === "manager") && r.uid === currentUser.uid);

  const [editingNotes, setEditingNotes] = useState(false);

  const [notesDraft, setNotesDraft] = useState(bourbon.notes || "");

  const [editingExprUrl, setEditingExprUrl] = useState(false);

  const [shareCopied, setShareCopied] = useState(false);

  const [exprUrlDraft, setExprUrlDraft] = useState(bourbon.website || "");

  const [showVariants, setShowVariants] = useState(true);
  const [releaseSearch, setReleaseSearch] = useState("");
  const [releaseSort, setReleaseSort] = useState({ key: "date", asc: false });
  const [storePicks, setStorePicks] = useState([]);
  const storePicksLoadedRef = useRef(false);

  useEffect(() => {
    if (bourbon.expressionType === "pick_base" && !storePicksLoadedRef.current && useSupabase()) {
      storePicksLoadedRef.current = true;
      db.loadStorePicks(bourbon.id).then(setStorePicks);
    }
  }, [bourbon.id]);

  return (
    <div style={posRelativeStyle}>
      <button onClick={() => { if (variantFromParent && bourbon.parentExpressionId) { const parent = getBourbon(bourbon.parentExpressionId); if (parent) { setVariantFromParent(false); setSelectedBourbon(parent); return; } } setVariantFromParent(false); setSelectedBourbon(null); navigateTo(prevView); }} style={closeButtonStyle} aria-label="Close">✕</button>
      {bourbon.parentExpressionId && (() => {
        const parent = getBourbon(bourbon.parentExpressionId);
        return parent ? (
          <div onClick={() => setSelectedBourbon(parent)}
            style={{ padding: "10px 14px", background: "rgba(212,160,83,0.06)", border: "1px solid rgba(212,160,83,0.15)", borderRadius: 10, marginBottom: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={dimText075Style}>Part of:</span>
            <span style={goldText08125BoldStyle}>{getDisplayName(parent)} {"\u2192"}</span>
          </div>
        ) : null;
      })()}
      <div style={{ background: "linear-gradient(180deg, #3D3428 0%, #1E1A14 100%)", borderRadius: 16, padding: 32, marginBottom: 24, border: "1px solid #5A4F3E", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -20, right: -15, opacity: 0.06 }}><DistilleryIconRender iconKey={dist?.image} size={120} /></div>
        <div style={posRelativeStyle}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            {getExpressionStatus(bourbon) === "official" && dist?.image && (
              <div style={{ width: 48, height: 48, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#150F0A", borderRadius: 10, border: "1px solid rgba(76,175,80,0.3)", marginTop: 4 }}>
                <DistilleryIconRender iconKey={dist.image} size={28} />
              </div>
            )}
            <div style={flexOneStyle}>
              <div style={{ fontSize: "0.75rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 2, marginBottom: 4, cursor: "pointer" }} onClick={() => { setSelectedBrand(dist); navigateTo("distillery-detail"); }}>
                {dist?.name} {"\u2192"}
              </div>
              {dist && (() => {

                const fac = getFacility(dist.facilityId);

                const loc = dist.location || (fac?.state ? (fac.location || fac.state) : "");
                return loc ? <div style={{ fontSize: "0.6875rem", color: "#B5A898", marginBottom: 2 }}>{"📍"} {loc}{fac ? <span> · <span onClick={(e) => { e.stopPropagation(); setSelectedFacility(fac); navigateTo("facility-detail"); }} style={{ color: "#D4A053", cursor: "pointer" }}>{fac.name || fac.companyName}</span></span> : ""}</div> : fac ? <div style={{ fontSize: "0.6875rem", color: "#B5A898", marginBottom: 2 }}>{"🏭"} <span onClick={(e) => { e.stopPropagation(); setSelectedFacility(fac); navigateTo("facility-detail"); }} style={{ color: "#D4A053", cursor: "pointer" }}>{fac.name || fac.companyName}</span></div> : null;
              })()}
              {showChildLabel && <div style={{ fontSize: "0.6875rem", color: "#A69880", marginBottom: 2 }}>{"🏷️"} {childBrand.name}</div>}
              {dist?.isSourced && exprFacility && <div style={{ fontSize: "0.6875rem", color: "#B5A898", marginBottom: 4 }}>{"🏭"} Sourced from: {exprFacility.name || exprFacility.companyName}</div>}
              {colaDistillery && <div style={{ fontSize: "0.6875rem", color: "#B5A898", marginBottom: 4 }}>{"🧪"} Distilled by: {colaDistillery.companyName || colaDistillery.name} ({bourbon.colaPermitNumber})</div>}
              <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "2.0rem", color: "#F5E6D0", margin: "0 0 8px", lineHeight: 1.1 }}>{getDisplayName(bourbon)}</h1>
              {bourbon.aliases && bourbon.aliases.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                  {bourbon.aliases.filter(a => a.type === "abbreviation" || a.type === "common_name").map((a, i) => (
                    <span key={i} style={{ padding: "2px 8px", borderRadius: 10, fontSize: "0.625rem", color: "#A69880", background: "rgba(90,79,62,0.2)", border: "1px solid rgba(90,79,62,0.3)" }}>{a.alias}</span>
                  ))}
                </div>
              )}
              {bourbon.colaProductName && bourbon.colaProductName !== bourbon.name && (
                <div style={{ fontSize: "0.75rem", color: "#999", marginTop: 2 }}>COLA: {bourbon.colaProductName}</div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
            {bourbon.tier === "curated" && <span style={goldText075BoldStyle}>Curated</span>}
            {bourbon.tier === "curated" && bourbon.type && <span style={{ color: "#5A4F3E", fontSize: "0.75rem" }}> · </span>}
            {bourbon.tier === "archive" && <span style={{ color: "#A69880", fontSize: "0.75rem", fontStyle: "italic" }}>Archive</span>}
            {bourbon.tier === "archive" && bourbon.type && <span style={{ color: "#5A4F3E", fontSize: "0.75rem" }}> · </span>}
            <span style={{ color: "#B5A898", fontSize: "0.875rem" }}>{bourbon.type}</span>
            {bourbon.expressionType === "program" && <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.625rem", fontWeight: 600, background: "rgba(212,160,83,0.12)", color: "#D4A053", border: "1px solid rgba(212,160,83,0.3)" }}>PROGRAM</span>}
            {bourbon.expressionType === "pick_base" && <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.625rem", fontWeight: 600, background: "rgba(100,149,237,0.15)", color: "#6495ED", border: "1px solid rgba(100,149,237,0.25)" }}>SINGLE BARREL</span>}
            {currentUser?.role === "admin" && (
              <select value={bourbon.expressionType || "standard"} onChange={async (e) => {
                const val = e.target.value === "standard" ? null : e.target.value;
                const sb = getSupabase(); if (!sb) return;
                const { error } = await sb.from("bourbons").update({ expression_type: val }).eq("id", bourbon.id);
                if (!error) {
                  setBourbons(prev => prev.map(b => b.id === bourbon.id ? { ...b, expressionType: val } : b));
                  showToast("Type: " + (val || "standard"));
                }
              }} aria-label="Expression type"
                style={{ padding: "2px 6px", fontSize: "0.625rem", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 6, color: "#5A4F3E", cursor: "pointer" }}>
                <option value="standard">standard</option>
                <option value="program">program</option>
                <option value="program_release">program_release</option>
                <option value="pick_base">pick_base</option>
                <option value="pick">pick</option>
                <option value="reapproval">reapproval</option>
              </select>
            )}
            <StatusBadge bourbon={bourbon} size="lg" />
            {(() => {

              const sub = getSubmissionStatus(bourbon);
              if (sub === "pending") return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.625rem", fontWeight: 600, background: "rgba(255,193,7,0.12)", color: "#FFC107", border: "1px solid rgba(255,193,7,0.3)", display: "inline-flex", alignItems: "center", gap: 3 }}>⏳ PENDING REVIEW</span>;
              if (sub === "rejected") return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.625rem", fontWeight: 600, background: "rgba(205,92,92,0.12)", color: "#CD5C5C", border: "1px solid rgba(205,92,92,0.3)", display: "inline-flex", alignItems: "center", gap: 3 }}>❌ REJECTED</span>;
              if (sub === "expired") return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.625rem", fontWeight: 600, background: "rgba(139,125,107,0.15)", color: "#B5A898", border: "1px solid rgba(139,125,107,0.3)", display: "inline-flex", alignItems: "center", gap: 3 }}>⏰ EXPIRED</span>;
              if (getExpressionStatus(bourbon) === "official") return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.625rem", fontWeight: 600, background: "rgba(76,175,80,0.12)", color: "#4CAF50", border: "1px solid rgba(76,175,80,0.3)", display: "inline-flex", alignItems: "center", gap: 3 }}>{"\u2713"} OFFICIAL</span>;
              if (bourbon.userAdded && sub === "approved") return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.625rem", fontWeight: 600, background: "rgba(76,175,80,0.12)", color: "#4CAF50", border: "1px solid rgba(76,175,80,0.3)" }}>✓ VERIFIED</span>;
              return null;
            })()}
          </div>
          {/* Share Button */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            {[{ label: "Proof", value: formatProof(bourbon) }, { label: "Age", value: formatAge(bourbon.age) }, { label: "MSRP", value: formatMsrp(bourbon.msrp) }, ...(bourbon.batch ? [{ label: "Batch", value: bourbon.batch }] : [])].map(s => (
              <div key={s.label} style={{ flex: 1, textAlign: "center", padding: "12px 8px", background: "#150F0A", borderRadius: 10, border: "1px solid #3D3428" }}>
                <div style={{ fontSize: "0.6875rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: "1.0rem", color: "#F5E6D0", fontWeight: 700, fontFamily: FONT_DISPLAY }}>{s.value}</div>
              </div>
            ))}
          </div>
          {pendingScanUpc && currentUser?.role !== "admin" && !(upcMap && upcMap.get(pendingScanUpc)?.expression_id === bourbon.id) && (
            <div style={{ background: "rgba(212,160,83,0.08)", border: "1px solid rgba(212,160,83,0.35)", borderRadius: 10, padding: "14px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D4A053" strokeWidth="1.5" strokeLinecap="round" style={{ flexShrink: 0 }}><path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2"/><line x1="7" y1="8" x2="7" y2="16"/><line x1="10.5" y1="8" x2="10.5" y2="16"/><line x1="14" y1="8" x2="14" y2="16"/><line x1="17" y1="8" x2="17" y2="16"/></svg>
              <div style={{ flex: 1 }}>
                <div style={{ color: "#F5E6D0", fontSize: "0.875rem", fontWeight: 600, marginBottom: 2 }}>Is this what you scanned?</div>
                <div style={{ color: "#B5A898", fontSize: "0.6875rem" }}>UPC: {pendingScanUpc}</div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button className="tap-feedback" onClick={async () => {
                  const sb = getSupabase();
                  if (sb && currentUser?.uid) { await sb.from("expression_edits").insert({ bourbon_id: bourbon.id, submitted_by: currentUser.uid, suggested_changes: { upc: pendingScanUpc }, reason: "Barcode scan association", status: "pending" }); }
                  showToast("Barcode link submitted for review");
                  setPendingScanUpc(null);
                }} style={{ padding: "8px 14px", borderRadius: 8, background: "#C4A469", color: "#1A1409", border: "none", fontSize: "0.8125rem", fontWeight: 700, cursor: "pointer" }}>
                  Link Barcode
                </button>
                <button className="tap-feedback" onClick={() => setPendingScanUpc(null)}
                  style={{ padding: "8px 10px", borderRadius: 8, background: "transparent", color: "#B5A898", border: "1px solid rgba(196,164,105,0.2)", fontSize: "0.75rem", cursor: "pointer" }}>
                  Dismiss
                </button>
              </div>
            </div>
          )}
          {pendingScanUpc && currentUser && (upcMap && upcMap.get(pendingScanUpc)?.expression_id === bourbon.id) && (
            <div style={{ textAlign: "center", marginBottom: 12 }}>
              <span onClick={async () => {
                const savedUpc = pendingScanUpc;
                try {
                  const sb = getSupabase();
                  if (sb && currentUser.uid) {
                    await sb.from("expression_edits").insert({ bourbon_id: bourbon.id, submitted_by: currentUser.uid, suggested_changes: { wrong_upc: savedUpc, type: "upc_mismatch" }, reason: "Barcode scan returned wrong expression", status: "pending" });
                  }
                  showToast("Mismatch reported \u2014 thanks!");
                } catch { showToast("Could not submit report."); }
                navigateTo("home");
                setTimeout(() => setPendingScanUpc(savedUpc), 50);
              }} style={{ fontSize: 12, color: "#A69880", cursor: "pointer", padding: "4px 8px" }}>Not this bottle?</span>
            </div>
          )}
          {(() => {
            const hasStructured = bourbon.mashCorn || bourbon.mashWheat || bourbon.mashRye || bourbon.mashBarley || bourbon.mashOther;
            const mbSrc = bourbon.mashbillSource;
            const mbBadge = mbSrc === "published" ? <span title="Mashbill confirmed by distillery" style={{ padding: "1px 6px", borderRadius: 10, fontSize: "0.5625rem", fontWeight: 600, background: "rgba(76,175,80,0.12)", color: "#4CAF50", border: "1px solid rgba(76,175,80,0.3)", marginLeft: 8 }}>✓ Verified</span>
              : mbSrc === "estimated" ? <span title="Industry consensus — not officially published by distillery" style={{ padding: "1px 6px", borderRadius: 10, fontSize: "0.5625rem", fontWeight: 600, background: "rgba(212,160,83,0.1)", color: "#D4A053", border: "1px solid rgba(212,160,83,0.25)", marginLeft: 8 }}>Estimated</span>
              : mbSrc === "admin" ? <span title="Enriched by BarrelRank admin" style={{ padding: "1px 6px", borderRadius: 10, fontSize: "0.5625rem", fontWeight: 600, background: "rgba(176,196,222,0.12)", color: "#B0C4DE", border: "1px solid rgba(176,196,222,0.3)", marginLeft: 8 }}>Admin</span>
              : null;
            if (hasStructured) {
              const ingredients = [{ l: "Corn", v: bourbon.mashCorn }, { l: "Wheat", v: bourbon.mashWheat }, { l: "Rye", v: bourbon.mashRye }, { l: "Barley", v: bourbon.mashBarley }, { l: "Other", v: bourbon.mashOther }].filter(x => x.v > 0);
              return (
                <div style={mb20Style}>
                  <div style={{ ...sectionLabelSmStyle, display: "flex", alignItems: "center" }}>Mash Bill{mbBadge}</div>
                  {ingredients.map(ig => (
                    <div key={ig.l} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <div style={{ width: 50, fontSize: "0.6875rem", color: "#A69880", flexShrink: 0 }}>{ig.l}</div>
                      <div style={{ flex: 1, height: 16, background: "#1E1A14", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ width: ig.v + "%", height: "100%", background: "#D4A053", opacity: 0.3 + (ig.v / 100) * 0.7, borderRadius: 4 }} />
                      </div>
                      <div style={{ width: 35, fontSize: "0.6875rem", color: "#C4B5A0", textAlign: "right", flexShrink: 0 }}>{ig.v}%</div>
                    </div>
                  ))}
                </div>
              );
            }
            if (bourbon.mashBill) {
              const parts = bourbon.mashBill.match(/\d+/g);
              if (parts && parts.length >= 3) {
                const nums = parts.map(Number);
                const labels = nums.length === 4 ? ["Corn", "Rye", "Wheat", "Barley"] : nums.length === 3 ? ["Corn", "Rye", "Barley"] : null;
                if (labels) {
                  const ingredients = labels.map((l, i) => ({ l, v: nums[i] })).filter(x => x.v > 0);
                  return (
                    <div style={mb20Style}>
                      <div style={{ ...sectionLabelSmStyle, display: "flex", alignItems: "center" }}>Mash Bill{mbBadge}</div>
                      {ingredients.map(ig => (
                        <div key={ig.l} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          <div style={{ width: 50, fontSize: "0.6875rem", color: "#A69880", flexShrink: 0 }}>{ig.l}</div>
                          <div style={{ flex: 1, height: 16, background: "#1E1A14", borderRadius: 4, overflow: "hidden" }}>
                            <div style={{ width: ig.v + "%", height: "100%", background: "#D4A053", opacity: 0.3 + (ig.v / 100) * 0.7, borderRadius: 4 }} />
                          </div>
                          <div style={{ width: 35, fontSize: "0.6875rem", color: "#C4B5A0", textAlign: "right", flexShrink: 0 }}>{ig.v}%</div>
                        </div>
                      ))}
                    </div>
                  );
                }
              }
              return (
                <div style={mb20Style}>
                  <div style={{ ...sectionLabelSmStyle, display: "flex", alignItems: "center" }}>Mash Bill{mbBadge}</div>
                  <div style={{ fontSize: "0.875rem", color: "#F5E6D0" }}>{bourbon.mashBill}</div>
                </div>
              );
            }
            return null;
          })()}
          {/* Distilled By — from COLA permit data */}
          {colaDistillery && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#150F0A", borderRadius: 10, border: "1px solid #3D3428", marginBottom: 16 }}>
              <span style={font0875Style}>{"🧪"}</span>
              <div>
                <div style={{ fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 0.5 }}>Distilled By</div>
                <div style={{ fontSize: "0.8125rem", color: "#F5E6D0", fontWeight: 600 }}>{colaDistillery.companyName || colaDistillery.name}</div>
                <div style={{ fontSize: "0.6875rem", color: "#B5A898", fontFamily: "monospace" }}>{bourbon.colaPermitNumber}{colaDistillery.state ? " \u00B7 " + colaDistillery.state : ""}</div>
              </div>
            </div>
          )}
          {bourbon.ratingCount > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <div onClick={() => setRatingDrillOpen(prev => !prev)} style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", padding: "4px 0", borderRadius: 8 }}>
                <div style={{ fontSize: "2.25rem", fontWeight: 700, color: "#D4A053", fontFamily: FONT_DISPLAY }}>{fmtRating(bourbon.avgRating)}</div>
                <div><StarRating rating={bourbon.avgRating} size={18} /><div style={{ fontSize: "0.75rem", color: "#B5A898", marginTop: 2 }}>{formatCount(bourbon.ratingCount)} {bourbon.ratingCount === 1 ? "rating" : "ratings"} {ratingDrillOpen ? "\u25B2" : "\u25BC"}</div></div>
              </div>
              {ratingDrillOpen && <RatingDistribution allCheckins={[...checkins, ...(communityCheckins || [])].filter(c => c.bourbonId === bourbon.id)} myCheckins={myCheckins} totalCount={bourbon.ratingCount} fmtDate={fmtDate} fmtRating={fmtRating} avgRating={bourbon.avgRating} />}
              {!ratingDrillOpen && myCheckins.length > 0 && (() => { const myAvg = (myCheckins.reduce((s, c) => s + c.rating, 0) / myCheckins.length).toFixed(2); return <div style={{ fontSize: "0.75rem", color: "#B5A898", marginTop: 6 }}>You: <span style={{ color: "#D4A053" }}>{"\u2605"}</span> {myAvg} ({myCheckins.length})</div>; })()}
            </div>
          ) : null}
          <p style={{ color: "#A69880", lineHeight: 1.7, fontSize: "0.9375rem", margin: "0 0 8px" }}>{bourbon.notes}</p>
          {canEditExpression && !editingNotes && (
            <button onClick={() => { setNotesDraft(bourbon.notes || ""); setEditingNotes(true); }}
              style={{ marginBottom: 12, padding: "8px 14px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 6, color: "#B5A898", fontSize: "0.6875rem", cursor: "pointer" }}>
              ✏️ Edit Expression Summary
            </button>
          )}
          {canEditExpression && editingNotes && (
            <div style={mb16Style}>
              <textarea value={notesDraft} onChange={e => setNotesDraft(e.target.value.slice(0, 150))} rows={2} aria-label="Expression notes"
                style={{ width: "100%", padding: "10px 12px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, color: "#F5E6D0", fontSize: "0.875rem", resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 }}
                placeholder="Official expression summary — purpose, features, tasting notes (150 characters max)" />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                <span style={{ fontSize: "0.6875rem", color: notesDraft.length >= 140 ? "#D4A053" : "#B5A898" }}>{notesDraft.length}/150</span>
                <div style={flexGap8Style}>
                  <button onClick={() => setEditingNotes(false)}
                    style={sPadBacBor7Style}>Cancel</button>
                  <button onClick={() => {

                    const trimmed = sanitizeInput(notesDraft.trim(), 150);

                    const updated = { ...bourbon, notes: trimmed };
                    setBourbons(prev => prev.map(b => b.id === bourbon.id ? updated : b));
                    setSelectedBourbon(updated);
                    if (useSupabase()) db.updateBourbonNotes(bourbon.id, trimmed);
                    setEditingNotes(false);
                    showToast("✓ Expression summary updated!");
                  }}
                    style={{ padding: "6px 14px", background: GRAD_PRIMARY, border: "none", borderRadius: 6, color: "#FFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>Save</button>
                </div>
              </div>
            </div>
          )}
          {/* Verified Expression URL */}
          {(bourbon.website || canEditExpression) && (
            <div style={{ marginBottom: 12 }}>
              {bourbon.website && !editingExprUrl && (() => { const safeUrl = sanitizeUrl(bourbon.website); return safeUrl ? (
                <a href={safeUrl} target="_blank" rel="noopener noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", background: "rgba(76,175,80,0.08)", border: "1px solid rgba(76,175,80,0.25)", borderRadius: 8, color: "#4CAF50", fontSize: "0.8125rem", fontWeight: 600, textDecoration: "none", transition: "all 0.15s" }}>
                  🔗 {bourbon.website.replace(/^https?:\/\//, "").replace(/\/$/, "")} <span style={{ fontSize: "0.625rem", opacity: 0.7 }}>←—</span>
                </a>
              ) : null; })()}
              {canEditExpression && !editingExprUrl && (
                <button onClick={() => { setExprUrlDraft(bourbon.website || ""); setEditingExprUrl(true); }}
                  style={{ marginLeft: bourbon.website ? 8 : 0, padding: "8px 14px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 6, color: "#B5A898", fontSize: "0.6875rem", cursor: "pointer" }}>
                  {bourbon.website ? "✏️ Edit URL" : "🔗 Add Product Page"}
                </button>
              )}
              {canEditExpression && editingExprUrl && (
                <div style={{ marginTop: 6 }}>
                  <input value={exprUrlDraft} onChange={e => setExprUrlDraft(e.target.value.slice(0, 200))} aria-label="Expression website URL" placeholder="https://yourdistillery.com/expressions/this-bourbon"
                    style={{ width: "100%", padding: "8px 12px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, color: "#F5E6D0", fontSize: "0.8125rem", boxSizing: "border-box" }} />
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
                    <button onClick={() => setEditingExprUrl(false)}
                      style={sPadBacBor7Style}>Cancel</button>
                    <button onClick={() => {

                      const trimmed = sanitizeUrl(exprUrlDraft.trim()) || sanitizeInput(exprUrlDraft.trim(), 200);

                      const updated = { ...bourbon, website: trimmed };
                      setBourbons(prev => prev.map(b => b.id === bourbon.id ? updated : b));
                      setSelectedBourbon(updated);
                      if (useSupabase()) db.updateBourbonWebsite(bourbon.id, trimmed);
                      setEditingExprUrl(false);
                      showToast("✓ Product page updated!");
                    }}
                      style={{ padding: "6px 14px", background: GRAD_PRIMARY, border: "none", borderRadius: 6, color: "#FFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>Save</button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <button className="tap-feedback" onClick={() => { setCheckinBourbon(bourbon); setShowCheckinModal(true); }}
              style={{ padding: "12px 0", background: GRAD_PRIMARY, border: "none", borderRadius: 10, color: "#FFF", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer", textAlign: "center" }}>
              🥃 Check In
            </button>
            <button onClick={() => { const url = `https://${BRAND.domain}/#/bourbon/${bourbon.id}`; if (navigator.share) { navigator.share({ title: `${getDisplayName(bourbon)} — ${BRAND.name}`, url }).catch(() => {}); } else { copyToClipboard(url); setShareCopied(true); setTimeout(() => setShareCopied(false), 2000); } }}
              style={{ padding: "12px 0", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 10, color: "#D4A053", fontSize: "0.875rem", cursor: "pointer", textAlign: "center" }} aria-label="Share bourbon">
              {shareCopied ? "✓ Copied!" : "←— Share"}
            </button>
            <button onClick={() => { const wasIn = inWishlist; setWishlist(prev => wasIn ? prev.filter(id => id !== bourbon.id) : [...prev, bourbon.id]); if (useSupabase() && currentUser?.uid) { wasIn ? db.removeFromWishlist(currentUser.uid, bourbon.id) : db.addToWishlist(currentUser.uid, bourbon.id); } showToast(wasIn ? "Removed from wishlist" : "★ Added to wishlist!"); }}
              style={{ padding: "12px 0", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 10, color: inWishlist ? "#D4A053" : "#B5A898", fontSize: "0.875rem", cursor: "pointer", textAlign: "center" }}>
              {inWishlist ? "★ On Wishlist" : "☆ Wishlist"}
            </button>
            <button onClick={() => { setBarSharePrompt({ bourbonId: bourbon.id, bourbonName: getDisplayName(bourbon), fromDetail: true, confirmAdd: true }); }}
              style={{ padding: "12px 0", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 10, color: "#B5A898", fontSize: "0.875rem", cursor: "pointer", textAlign: "center" }}>
              + Bar{activeCount > 0 ? " (" + activeCount + ")" : ""}
            </button>
          </div>
          {(() => {
            const hasOwnEnrichment = bourbon.colaTastingFlavors || bourbon.colaDescription || bourbon.colaGrains || bourbon.colaFinishingProcess || bourbon.colaBarcodeValue;
            const parentHasNoEnrichment = !bourbon.colaTastingFlavors && !bourbon.colaGrains && !bourbon.colaDescription;
            const childBourbons = bourbons.filter(b => b.parentExpressionId === bourbon.id);
            const enrichedChildren = childBourbons.filter(b => b.colaEnrichedAt);
            const useAggregated = parentHasNoEnrichment && enrichedChildren.length > 0;
            let aggFlavors = null, aggGrains = null, aggCount = 0;
            if (useAggregated) {
              aggCount = enrichedChildren.length;
              const freq = {};
              enrichedChildren.forEach(ch => { if (ch.colaTastingFlavors) ch.colaTastingFlavors.filter(Boolean).forEach(f => { freq[f] = (freq[f] || 0) + 1; }); });
              const sorted = Object.entries(freq).sort((a, z) => z[1] - a[1]).slice(0, 8).map(e => e[0]);
              if (sorted.length > 0) aggFlavors = sorted;
              const grainChild = enrichedChildren.find(ch => ch.colaGrains);
              if (grainChild) aggGrains = grainChild.colaGrains;
            }
            if (!hasOwnEnrichment && !useAggregated) return null;
            if (useAggregated) return (
              <div style={{ marginTop: 16, marginBottom: 16, borderLeft: "3px solid #c9a84c", background: "rgba(255,255,255,0.04)", borderRadius: "0 10px 10px 0", padding: "14px 16px" }}>
                <div style={{ fontSize: "0.6875rem", color: "#c9a84c", textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, marginBottom: 10 }}>{"\uD83D\uDCCB"} From the Label</div>
                {aggFlavors && (
                  <div style={mb10Style}>
                    <div style={mutedText0625Mb4UpperSpacedStyle}>Tasting Notes</div>
                    <div style={flexGap6WrapStyle}>
                      {aggFlavors.map((f, i) => <span key={i} style={{ padding: "3px 10px", borderRadius: 12, fontSize: "0.6875rem", color: "#c9a84c", background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.2)" }}>{f}</span>)}
                    </div>
                    <div style={{ fontSize: "0.5625rem", color: "#5A4F3E", marginTop: 4 }}>(across {aggCount} release{aggCount !== 1 ? "s" : ""})</div>
                  </div>
                )}
                {aggGrains && (() => { const parsed = aggGrains.replace(/[\[\]"]/g, "").split(",").map(g => g.trim()).filter(Boolean); if (parsed.length === 1 && parsed[0].toLowerCase() === "corn") return null; return parsed.length > 0 ? (
                  <div>
                    <div style={mutedText0625Mb4UpperSpacedStyle}>Grains</div>
                    <div style={flexGap6WrapStyle}>
                      {parsed.map((g, i) => <span key={i} style={{ padding: "3px 10px", borderRadius: 12, fontSize: "0.6875rem", color: "#A69880", background: "rgba(90,79,62,0.2)", border: "1px solid rgba(90,79,62,0.3)" }}>{g}</span>)}
                    </div>
                  </div>
                ) : null; })()}
              </div>
            );
            return (
              <div style={{ marginTop: 16, marginBottom: 16, borderLeft: "3px solid #c9a84c", background: "rgba(255,255,255,0.04)", borderRadius: "0 10px 10px 0", padding: "14px 16px" }}>
                <div style={{ fontSize: "0.6875rem", color: "#c9a84c", textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, marginBottom: 10 }}>{"\uD83D\uDCCB"} From the Label</div>
                {bourbon.colaTastingFlavors && (() => {
                  const flavors = bourbon.colaTastingFlavors.filter(Boolean);
                  return flavors.length > 0 ? (
                    <div style={mb10Style}>
                      <div style={mutedText0625Mb4UpperSpacedStyle}>Tasting Notes</div>
                      <div style={flexGap6WrapStyle}>
                        {flavors.map((f, i) => <span key={i} style={{ padding: "3px 10px", borderRadius: 12, fontSize: "0.6875rem", color: "#c9a84c", background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.2)" }}>{f}</span>)}
                      </div>
                    </div>
                  ) : null;
                })()}
                {bourbon.colaGrains && (() => { const parsed = bourbon.colaGrains.replace(/[\[\]"]/g, "").split(",").map(g => g.trim()).filter(Boolean); if (parsed.length === 1 && parsed[0].toLowerCase() === "corn") return null; return parsed.length > 0 ? (
                  <div style={mb10Style}>
                    <div style={mutedText0625Mb4UpperSpacedStyle}>Grains</div>
                    <div style={flexGap6WrapStyle}>
                      {parsed.map((g, i) => <span key={i} style={{ padding: "3px 10px", borderRadius: 12, fontSize: "0.6875rem", color: "#A69880", background: "rgba(90,79,62,0.2)", border: "1px solid rgba(90,79,62,0.3)" }}>{g}</span>)}
                    </div>
                  </div>
                ) : null; })()}
                {bourbon.id.startsWith("cola-") && bourbon.colaFinishingProcess && (
                  <div style={mb10Style}>
                    <div style={mutedText0625Mb4UpperSpacedStyle}>Finishing Process</div>
                    <div style={{ fontSize: "0.8125rem", color: "#F5E6D0" }}>{bourbon.colaFinishingProcess}</div>
                  </div>
                )}
                {bourbon.id.startsWith("cola-") && bourbon.colaDescription && (
                  <div style={{ marginBottom: bourbon.colaBarcodeValue ? 10 : 0 }}>
                    <div style={mutedText0625Mb4UpperSpacedStyle}>Description</div>
                    <div style={{ fontSize: "0.8125rem", color: "#A69880", lineHeight: 1.6 }}>{bourbon.colaDescription}</div>
                  </div>
                )}
                {bourbon.colaBarcodeValue && (
                  <div>
                    <div style={{ fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>UPC</div>
                    <div style={{ fontSize: "0.6875rem", color: "#5A4F3E", fontFamily: "monospace" }}>{bourbon.colaBarcodeValue}</div>
                  </div>
                )}
              </div>
            );
          })()}
          {bourbon.expressionType === "pick_base" && (() => {
            return (
              <div style={{ marginTop: 16, marginBottom: 16 }}>
                <h3 style={{ fontFamily: FONT_DISPLAY, color: "#F5E6D0", fontSize: "1.125rem", marginBottom: 12 }}>{storePicks.length > 0 ? `${storePicks.length} Store Pick${storePicks.length !== 1 ? "s" : ""} Logged` : "Store Picks"}</h3>
                {storePicks.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {storePicks.map(sp => (
                      <div key={sp.id} style={{ padding: "8px 14px", background: "#1E1A14", borderRadius: 8, border: "1px solid #3D3428" }}>
                        <div style={flexBetweenCenterStyle}>
                          <span style={{ color: "#6495ED", fontSize: "0.8125rem", fontWeight: 600 }}>{sp.storeName}</span>
                          <span style={{ color: "#5A4F3E", fontSize: "0.625rem", flexShrink: 0, marginLeft: 8 }}>{sp.pickDate ? sp.pickDate.slice(0, 10) : ""}</span>
                        </div>
                        <div style={{ color: "#5A4F3E", fontSize: "0.625rem", marginTop: 2 }}>
                          {sp.barrelNumber ? "Barrel #" + sp.barrelNumber : ""}{sp.barrelNumber && sp.proof ? " \u00B7 " : ""}{sp.proof ? sp.proof + "\u00B0" : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: "#5A4F3E", fontSize: "0.8125rem", fontStyle: "italic" }}>No store picks yet. Check in to log the first!</div>
                )}
              </div>
            );
          })()}
          {currentUser?.uid && getSubmissionStatus(bourbon) !== "pending" && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
              <button onClick={() => {
                setSuggestEditData({ name: bourbon.name || "", proof: bourbon.proof ?? "", age: bourbon.age || "", msrp: bourbon.msrp ?? "", type: bourbon.type || "", finishType: bourbon.finishType || "", mashCorn: bourbon.mashCorn ?? "", mashWheat: bourbon.mashWheat ?? "", mashRye: bourbon.mashRye ?? "", mashBarley: bourbon.mashBarley ?? "", mashOther: bourbon.mashOther ?? "", batch: bourbon.batch || "", upc: "", reason: "", scannerOpen: false });
                setShowSuggestEdit(true);
              }}
                style={{ flex: 1, padding: "12px 0", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 10, color: "#B5A898", fontSize: "0.8125rem", cursor: "pointer", textAlign: "center", minHeight: 44 }}
                aria-label="Suggest edit for this expression">
                {"✏️"} Suggest Edit
              </button>
              {currentUser?.role === "admin" && bourbon.source === "cola_cloud" && (
                <button
                  onClick={async () => {
                    const newLocked = !bourbon.nameLocked;
                    const sb = getSupabase(); if (!sb) return;
                    const { error } = await sb.from("bourbons").update({ name_locked: newLocked }).eq("id", bourbon.id);
                    if (!error) {
                      setBourbons(prev => prev.map(b => b.id === bourbon.id ? { ...b, nameLocked: newLocked } : b));
                      showToast(newLocked ? "\uD83D\uDD12 Name locked" : "\uD83D\uDD13 Name unlocked");
                    }
                  }}
                  style={{ ...closeButtonStyle, fontSize: "0.75rem", padding: "4px 10px", background: bourbon.nameLocked ? "#2d5016" : "transparent", border: "1px solid #555", borderRadius: 8, color: bourbon.nameLocked ? "#fff" : "#999", minHeight: 44 }}
                  aria-label={bourbon.nameLocked ? "Unlock name" : "Lock name"}
                >
                  {bourbon.nameLocked ? "\uD83D\uDD12 Locked" : "\uD83D\uDD13 Lock Name"}
                </button>
              )}
            </div>
          )}
          {getSubmissionStatus(bourbon) === "pending" && bourbon.submittedBy === currentUser?.uid && (
            <div style={{ padding: "10px 14px", background: "rgba(255,193,7,0.06)", border: "1px solid rgba(255,193,7,0.15)", borderRadius: 10, marginTop: 10 }}>
              <div style={{ fontSize: "0.75rem", color: "#FFC107", fontWeight: 600, marginBottom: 2 }}>📋 You submitted this expression</div>
              <div style={sFonCol3Style}>You can check in while it's under review. If it's not approved, associated check-ins will be reassigned.</div>
            </div>
          )}
        </div>
      </div>
      {bourbon.expressionType === "program" && bourbon.childCount > 0 && (() => {
        const allReleases = bourbons.filter(b => b.parentExpressionId === bourbon.id && b.expressionType === "program_release");
        if (allReleases.length === 0) return null;
        const q = releaseSearch.toLowerCase().trim();
        const filtered = q ? allReleases.filter(c => (c.name || "").toLowerCase().includes(q) || (c.batch || "").toLowerCase().includes(q) || (c.colaApprovalDate || "").includes(q)) : allReleases;
        const sorted = [...filtered].sort((a, z) => {
          const sk = releaseSort.key;
          let cmp = 0;
          if (sk === "name") cmp = (a.name || "").localeCompare(z.name || "");
          else if (sk === "proof") cmp = (a.proof || 0) - (z.proof || 0);
          else cmp = (a.colaApprovalDate || "").localeCompare(z.colaApprovalDate || "");
          return releaseSort.asc ? cmp : -cmp;
        });
        const toggleSort = (key) => setReleaseSort(prev => prev.key === key ? { key, asc: !prev.asc } : { key, asc: key === "name" });
        const arrow = (key) => releaseSort.key === key ? (releaseSort.asc ? " \u25B2" : " \u25BC") : "";
        const colHdr = (key, label) => (
          <span onClick={(e) => { e.stopPropagation(); toggleSort(key); }}
            style={{ cursor: "pointer", color: releaseSort.key === key ? "#D4A053" : "#5A4F3E", fontSize: "0.5625rem", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, userSelect: "none" }}>
            {label}{arrow(key)}
          </span>
        );
        return (
          <div style={mb24Style}>
            <button onClick={() => setShowVariants(prev => !prev)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 8, width: "100%", marginBottom: showVariants ? 12 : 0 }}>
              <h3 style={{ fontFamily: FONT_DISPLAY, color: "#F5E6D0", fontSize: "1.125rem", margin: 0, flex: 1, textAlign: "left" }}>{allReleases.length} Release{allReleases.length !== 1 ? "s" : ""}</h3>
              <span style={{ color: "#B5A898", fontSize: "0.75rem", transition: "transform 0.15s", transform: showVariants ? "rotate(180deg)" : "none" }}>{"\u25BC"}</span>
            </button>
            {showVariants && (<>
              {allReleases.length > 5 && (
                <div style={{ position: "relative", marginBottom: 8 }}>
                  <span style={searchIconStyle}>{"\uD83D\uDD0D"}</span>
                  <input value={releaseSearch} onChange={e => setReleaseSearch(e.target.value)} placeholder="Search releases..." aria-label="Search releases"
                    style={{ ...searchInputIconStyle, padding: "8px 12px 8px 32px", fontSize: "0.75rem", borderRadius: 8 }} />
                </div>
              )}
              <div style={{ display: "flex", gap: 16, padding: "0 14px", marginBottom: 6 }}>
                {colHdr("name", "Name")}
                {colHdr("proof", "Proof")}
                {colHdr("date", "Date")}
              </div>
              {q && <div style={{ fontSize: "0.5625rem", color: "#5A4F3E", marginBottom: 6 }}>Showing {sorted.length} of {allReleases.length} release{allReleases.length !== 1 ? "s" : ""}</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {sorted.map(child => (
                  <div key={child.id} onClick={() => { setVariantFromParent(true); setSelectedBourbon(child); }}
                    style={{ padding: "8px 14px", background: "#1E1A14", borderRadius: 8, border: "1px solid #3D3428", cursor: "pointer", transition: "border-color 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "#5A4F3E"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
                    <div style={flexBetweenCenterStyle}>
                      <span style={{ color: "#F5E6D0", fontSize: "0.8125rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>{child.name}</span>
                      <span style={{ color: "#D4A053", fontSize: "0.75rem", flexShrink: 0, marginLeft: 8 }}>{child.proof ? child.proof + "\u00B0" : ""}</span>
                    </div>
                    <div style={{ color: "#5A4F3E", fontSize: "0.625rem", marginTop: 2 }}>
                      {child.batch || ""}{child.batch && child.colaApprovalDate ? " \u00B7 " : ""}{child.colaApprovalDate ? child.colaApprovalDate.slice(0, 10) : ""}
                    </div>
                  </div>
                ))}
              </div>
            </>)}
          </div>
        );
      })()}
      {myCheckins.length > 0 && (
        <div>
          <h3 style={{ fontFamily: FONT_DISPLAY, color: "#F5E6D0", fontSize: "1.25rem", marginBottom: 16 }}>Your Tasting History</h3>
          {myCheckins.map(c => (
            <div key={c.id} onClick={() => setViewCheckinDetail(c)}
              style={{ background: "#1E1A14", borderRadius: 12, padding: 20, marginBottom: 12, border: "1px solid #3D3428", cursor: "pointer", transition: "border-color 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "#5A4F3E"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={flexCenterGap8Style}>
                  <StarRating rating={c.rating} size={16} />
                  <span style={{ color: "#D4A053", fontWeight: 700 }}>{fmtRating(c.rating)}</span>
                </div>
                <span style={mutedText08125Style}>{fmtDate(c.date)}</span>
              </div>
              <div style={{ fontSize: "0.8125rem", color: "#B5A898", marginBottom: 8 }}>📍 {stripUSA(c.location)} · {c.servingStyle} · {c.glassware}{c.isBarrelPick && <span style={{ marginLeft: 6, padding: "2px 7px", background: "rgba(212,160,83,0.1)", border: "1px solid rgba(212,160,83,0.25)", borderRadius: 10, fontSize: "0.625rem", fontWeight: 600, color: "#D4A053" }}>🏷️ Pick{c.pickSource ? ` · ${c.pickSource}` : ""}{c.pickProof ? ` · ${c.pickProof}°` : ""}</span>}</div>
              {c.tastingNotes?.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                  {c.tastingNotes.map(n => <span key={n} style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.6875rem", background: "#3D3428", color: "#D4A053", border: "1px solid #5A4F3E" }}>{n}</span>)}
                </div>
              )}
              <NosePalateFinish checkin={c} />
              {c.comment && <p style={{ color: "#A69880", fontSize: "0.875rem", lineHeight: 1.6, margin: 0, fontStyle: "italic" }}>"{sanitizeText(c.comment)}"</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── BRAND DETAIL VIEW ───────────

const BrandDetail = ({ brand, businessClaims, businessRoles, currentUser, platformUsers,
  selectedBourbon, selectedFacility, selectedBrand, prevView, view, brands, isAuthenticated,
  setBusinessClaims, setSelectedBrand, setSelectedBourbon, setSelectedFacility, setBrands,
  setAddBourbonPreselectedBrand, setShowAddBourbonModal,
  getFacility, getBrandBourbons, canSeeSubmission, getSubmissionStatus, getAvgRating,
  getExpressionStatus, navigateTo, showToast, appendAudit, copyToClipboard,
  getDisplayName, sanitizeUrl, SubmissionBadgeInline, db }) => {

  const facility = getFacility(brand.facilityId);

  const distBourbons = getBrandBourbons(brand.id).filter(b => canSeeSubmission(b));

  const approvedClaim = businessClaims.find(c => c.businessId === brand.id && c.status === "approved");

  const ownerRole = businessRoles.find(r => r.businessId === brand.id && r.role === "owner");

  const ownerUser = ownerRole ? platformUsers.find(u => u.uid === ownerRole.uid) : null;

  const isOwner = currentUser && ownerRole?.uid === currentUser.uid;

  const hasManager = currentUser && businessRoles.some(r => r.businessId === brand.id && (r.role === "owner" || r.role === "manager") && r.uid === currentUser?.uid);

  const pendingClaim = currentUser && businessClaims.find(c => c.businessId === brand.id && c.claimantUid === currentUser.uid && (c.status === "submitted" || c.status === "draft"));

  const [showClaimForm, setShowClaimForm] = useState(false);

  const [claimMethod, setClaimMethod] = useState("domain-email");

  const [claimEvidence, setClaimEvidence] = useState("");

  const [claimNotes, setClaimNotes] = useState("");

  const [editingDesc, setEditingDesc] = useState(false);

  const [descDraft, setDescDraft] = useState(brand.description || "");

  const [editingWebsite, setEditingWebsite] = useState(false);

  const [webDraft, setWebDraft] = useState(brand.website || "");

  const [brandShareCopied, setBrandShareCopied] = useState(false);

  const submitClaim = async () => {
    if (!claimEvidence.trim()) return;

    const newClaim = {
      id: generateId("claim_"),
      businessId: brand.id,
      businessName: brand.name,
      claimantUid: currentUser.uid,
      claimantName: currentUser.name,
      status: "submitted",
      verificationMethod: claimMethod,
      evidence: sanitizeInput(claimEvidence, 500),
      submittedAt: new Date().toISOString(),
      reviewedAt: null,
      reviewedBy: null,
      notes: sanitizeInput(claimNotes, 200),
    };
    setBusinessClaims(prev => [...prev, newClaim]);

    if (useSupabase()) {

      const dbClaim = await db.insertClaim(newClaim);
      if (dbClaim) setBusinessClaims(prev => prev.map(c => c.id === newClaim.id ? { ...c, id: dbClaim.id } : c));

    }

    appendAudit(currentUser.uid, currentUser.name, "claim_submitted", "claim", "Submitted claim for " + brand.name, newClaim.id);
    setShowClaimForm(false);
    setClaimEvidence("");
    setClaimNotes("");
  };

  return (
    <div style={posRelativeStyle}>
      <button onClick={() => {
        if (prevView === "bourbon-detail" && selectedBourbon) {
          setSelectedBrand(null);
          navigateTo("bourbon-detail");
        } else {
          setSelectedBrand(null);
          navigateTo(prevView === "distillery-detail" ? "brands" : prevView);

        }

      }} style={closeButtonStyle} aria-label="Close">✕</button>
      <div style={{ background: "linear-gradient(180deg, #3D3428 0%, #1E1A14 100%)", borderRadius: 16, padding: 32, marginBottom: 24, border: "1px solid #5A4F3E", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -10, right: -10, opacity: 0.06 }}><DistilleryIconRender iconKey={brand.image} size={100} /></div>
        <div style={posRelativeStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 52, height: 52, display: "flex", alignItems: "center", justifyContent: "center", background: "#150F0A", borderRadius: 12, border: "1px solid #5A4F3E" }}><DistilleryIconRender iconKey={brand.image} size={30} /></div>
            {(() => {

              const sub = getSubmissionStatus(brand);
              if (sub === "pending") return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.625rem", fontWeight: 600, background: "rgba(255,193,7,0.12)", color: "#FFC107", border: "1px solid rgba(255,193,7,0.3)" }}>⏳ PENDING REVIEW</span>;
              if (sub === "rejected") return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.625rem", fontWeight: 600, background: "rgba(205,92,92,0.12)", color: "#CD5C5C", border: "1px solid rgba(205,92,92,0.3)" }}>❌ REJECTED</span>;
              if (sub === "expired") return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.625rem", fontWeight: 600, background: "rgba(139,125,107,0.15)", color: "#B5A898", border: "1px solid rgba(139,125,107,0.3)" }}>⏰ EXPIRED</span>;
              if (brand.userAdded && sub === "approved") return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.625rem", fontWeight: 600, background: "rgba(76,175,80,0.12)", color: "#4CAF50", border: "1px solid rgba(76,175,80,0.3)" }}>✓ VERIFIED</span>;
              return null;
            })()}
            {approvedClaim && (
              <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.625rem", fontWeight: 600, background: "rgba(76,175,80,0.12)", color: "#4CAF50", border: "1px solid rgba(76,175,80,0.3)", display: "flex", alignItems: "center", gap: 4 }}>
                {"\u2713"} VERIFIED
              </span>
            )}
          </div>
          <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "2.0rem", color: "#F5E6D0", margin: "0 0 8px" }}>{brand.name}</h1>
          {brand.brandType !== "distiller" && <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: "0.6875rem", fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", background: brand.brandType === "ndp" ? "#2A1F14" : "#1A2A1F", color: brand.brandType === "ndp" ? "#D4A053" : "#7DCEA0", border: `1px solid ${brand.brandType === "ndp" ? "#3A2F1E" : "#2A3A2F"}`, marginBottom: 6 }}>{brand.brandType === "ndp" ? "Non-Distiller Producer" : "Blender"}</span>}
          {(() => {

            // Derive display location: prefer brand.location, fall back to facility state

            const loc = brand.location || (facility?.state || "");

            const city = facility?.companyName?.split(",")[0] || "";

            const displayLoc = loc || (city && facility?.state ? `${facility.state}` : "");
            return loc ? <div style={{ color: "#D4A053", fontSize: "0.875rem", marginBottom: 6 }}>{"📍"} {loc}</div> : null;
          })()}
          {brand.est && <div style={{ color: "#B5A898", fontSize: "0.875rem", marginBottom: 6 }}>Est. {brand.est}</div>}
          {brand.facilityId && facility && <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginBottom: 6 }}>{"🏭"} Made at: <span onClick={(e) => { e.stopPropagation(); setSelectedFacility(facility); navigateTo("facility-detail"); }} style={{ color: "#D4A053", cursor: "pointer", textDecoration: "underline" }}>{facility.name || facility.companyName}</span> ({brand.facilityId})</div>}
          {brand.isSourced && facility && <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginBottom: 6 }}>{"🏭"} Sourced from: {facility.name || facility.companyName} ({facility.permitNumber})</div>}
          {brand.kbt && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 20, background: "rgba(212,160,83,0.1)", border: "1px solid rgba(212,160,83,0.25)", marginBottom: 8, marginRight: 6 }}>
              <span style={font08125Style}>{"🏞\uFE0F"}</span>
              <span style={{ fontSize: "0.6875rem", fontWeight: 700, color: "#D4A053", letterSpacing: 0.5 }}>Kentucky Bourbon Trail®</span>
            </div>
          )}
          {brand.ubt && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 20, background: "rgba(100,149,237,0.1)", border: "1px solid rgba(100,149,237,0.25)", marginBottom: 8 }}>
              <span style={font08125Style}>{"🏙\uFE0F"}</span>
              <span style={{ fontSize: "0.6875rem", fontWeight: 700, color: "#6495ED", letterSpacing: 0.5 }}>Urban Bourbon Trail®</span>
            </div>
          )}
          {ownerUser && (
            <div style={{ color: "#B5A898", fontSize: "0.75rem", marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 18, height: 18, borderRadius: "50%", background: "#5A4F3E", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.625rem" }}>
                {ownerUser.name.charAt(0)}
              </span>
              Managed by {ownerUser.name}
              {isOwner && <span style={{ color: "#4CAF50", fontSize: "0.625rem", fontWeight: 700 }}>(You)</span>}
            </div>
          )}
          <p style={{ color: "#A69880", lineHeight: 1.7, fontSize: "0.9375rem", margin: 0 }}>{brand.description}</p>
          {hasManager && !editingDesc && (
            <button onClick={() => { setDescDraft(brand.description || ""); setEditingDesc(true); }}
              style={{ marginTop: 8, padding: "8px 14px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 6, color: "#B5A898", fontSize: "0.6875rem", cursor: "pointer" }}>
              ✏️ Edit Description
            </button>
          )}
          {hasManager && editingDesc && (
            <div style={{ marginTop: 10 }}>
              <textarea value={descDraft} onChange={e => setDescDraft(e.target.value.slice(0, 250))} rows={3} aria-label="Brand description"
                style={{ width: "100%", padding: "10px 12px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, color: "#F5E6D0", fontSize: "0.875rem", resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 }}
                placeholder="Official brand profile — history, craft, legacy (250 characters max)" />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                <span style={{ fontSize: "0.6875rem", color: descDraft.length >= 240 ? "#D4A053" : "#B5A898" }}>{descDraft.length}/250</span>
                <div style={flexGap8Style}>
                  <button onClick={() => setEditingDesc(false)}
                    style={sPadBacBor7Style}>Cancel</button>
                  <button onClick={() => {

                    const trimmed = sanitizeInput(descDraft.trim(), 250);

                    const updated = { ...brand, description: trimmed };
                    setBrands(prev => prev.map(d => d.id === brand.id ? updated : d));
                    setSelectedBrand(updated);
                    if (useSupabase()) db.updateBrandDescription(brand.id, trimmed);
                    setEditingDesc(false);
                    showToast("✓ Description updated!");
                  }}
                    style={{ padding: "6px 14px", background: GRAD_PRIMARY, border: "none", borderRadius: 6, color: "#FFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>Save</button>
                </div>
              </div>
            </div>
          )}
          {/* Verified Website Link */}
          {/* Share Button */}
          <div style={{ marginTop: 12, marginBottom: 4 }}>
            <button onClick={() => { const url = `https://${BRAND.domain}/#/brand/${brand.id}`; if (navigator.share) { navigator.share({ title: `${brand.name} — ${BRAND.name}`, url }).catch(() => {}); } else { copyToClipboard(url); setBrandShareCopied(true); setTimeout(() => setBrandShareCopied(false), 2000); } }} style={{ padding: "8px 16px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 10, color: "#D4A053", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }} aria-label="Share brand">
              {brandShareCopied ? "✓ Copied!" : "←— Share"}
            </button>
          </div>
          {(brand.website || hasManager) && (
            <div style={{ marginTop: 12 }}>
              {brand.website && !editingWebsite && (() => { const safeUrl = sanitizeUrl(brand.website); return safeUrl ? (
                <a href={safeUrl} target="_blank" rel="noopener noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", background: "rgba(76,175,80,0.08)", border: "1px solid rgba(76,175,80,0.25)", borderRadius: 8, color: "#4CAF50", fontSize: "0.8125rem", fontWeight: 600, textDecoration: "none", transition: "all 0.15s" }}>
                  🔗 {brand.website.replace(/^https?:\/\//, "").replace(/\/$/, "")} <span style={{ fontSize: "0.625rem", opacity: 0.7 }}>←—</span>
                </a>
              ) : null; })()}
              {hasManager && !editingWebsite && (
                <button onClick={() => { setWebDraft(brand.website || ""); setEditingWebsite(true); }}
                  style={{ marginLeft: brand.website ? 8 : 0, padding: "8px 14px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 6, color: "#B5A898", fontSize: "0.6875rem", cursor: "pointer" }}>
                  {brand.website ? "✏️ Edit URL" : "🔗 Add Website"}
                </button>
              )}
              {hasManager && editingWebsite && (
                <div style={{ marginTop: 6 }}>
                  <input value={webDraft} onChange={e => setWebDraft(e.target.value.slice(0, 200))} aria-label="Brand website URL" placeholder="https://yourbrand.com"
                    style={{ width: "100%", padding: "8px 12px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 8, color: "#F5E6D0", fontSize: "0.8125rem", boxSizing: "border-box" }} />
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
                    <button onClick={() => setEditingWebsite(false)}
                      style={sPadBacBor7Style}>Cancel</button>
                    <button onClick={() => {

                      const trimmed = sanitizeUrl(webDraft.trim()) || sanitizeInput(webDraft.trim(), 200);

                      const updated = { ...brand, website: trimmed };
                      setBrands(prev => prev.map(d => d.id === brand.id ? updated : d));
                      setSelectedBrand(updated);
                      if (useSupabase()) db.updateBrandWebsite(brand.id, trimmed);
                      setEditingWebsite(false);
                      showToast("✓ Website updated!");
                    }}
                      style={{ padding: "6px 14px", background: GRAD_PRIMARY, border: "none", borderRadius: 6, color: "#FFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>Save</button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Claim / Status section */}
          {isAuthenticated && !approvedClaim && !pendingClaim && !showClaimForm && (
            <button onClick={() => setShowClaimForm(true)}
              style={{ marginTop: 16, padding: "10px 20px", background: "transparent", border: "1.5px solid #D4A053", borderRadius: 8, color: "#D4A053", fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer" }}>
              {"🏢"} Claim This Distillery
            </button>
          )}
          {pendingClaim && (
            <div style={{ marginTop: 16, padding: "10px 16px", background: "rgba(212,160,83,0.08)", borderRadius: 8, border: "1px solid rgba(212,160,83,0.2)" }}>
              <div style={goldText08125BoldStyle}>{"\u23F3"} Claim Pending Review</div>
              <div style={{ color: "#B5A898", fontSize: "0.6875rem", marginTop: 4 }}>Submitted {new Date(pendingClaim.submittedAt).toLocaleDateString()} via {pendingClaim.verificationMethod}</div>
            </div>
          )}
          {/* Claim form */}
          {showClaimForm && (
            <div style={{ marginTop: 16, padding: 20, background: "#150F0A", borderRadius: 12, border: "1px solid #5A4F3E" }}>
              <h4 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0", margin: "0 0 4px" }}>Claim {brand.name}</h4>
              <p style={{ color: "#B5A898", fontSize: "0.75rem", margin: "0 0 16px" }}>Verify your affiliation to manage this distillery's profile, expressions, and public page.</p>
              <label style={sectionLabelMdStyle}>Verification Method</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                {[
                  { key: "domain-email", label: "Business Email" },
                  { key: "business-license", label: "Business License" },
                  { key: "ttb-permit", label: "TTB Permit" },
                  { key: "admin-review", label: "Other / Admin Review" },
                ].map(m => (
                  <button key={m.key} onClick={() => setClaimMethod(m.key)}
                    style={{ padding: "6px 14px", borderRadius: 16, fontSize: "0.75rem", cursor: "pointer", border: "1px solid", transition: "all 0.15s",
                      background: claimMethod === m.key ? "rgba(212,160,83,0.15)" : "transparent", color: claimMethod === m.key ? "#D4A053" : "#A69880",
                      borderColor: claimMethod === m.key ? "#D4A053" : "#3D3428", fontWeight: claimMethod === m.key ? 700 : 400 }}>
                    {m.label}
                  </button>
                ))}
              </div>
              <label style={sectionLabelMdStyle}>
                {claimMethod === "domain-email" ? "Business Email Address" : claimMethod === "business-license" ? "License Number / Details" : claimMethod === "ttb-permit" ? "TTB Permit Number" : "Describe Your Affiliation"}
              </label>
              <input aria-label="Claim evidence" value={claimEvidence} onChange={e => setClaimEvidence(e.target.value)} maxLength={500}
                placeholder={claimMethod === "domain-email" ? "you@" + brand.name.toLowerCase().replace(/[^a-z]/g, "") + ".com" : claimMethod === "ttb-permit" ? "DSP-KY-XXXXX" : "Provide verification details"}
                style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #5A4F3E", background: "#1A1409", color: "#F5E6D0", fontSize: "0.875rem", outline: "none", marginBottom: 12 }} />
              <label style={sectionLabelMdStyle}>Additional Notes (optional)</label>
              <input aria-label="Claim notes" value={claimNotes} onChange={e => setClaimNotes(e.target.value)} maxLength={200}
                placeholder="Your role, how long you've been with the brand, etc."
                style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #5A4F3E", background: "#1A1409", color: "#F5E6D0", fontSize: "0.875rem", outline: "none", marginBottom: 16 }} />
              <div style={flexGap8Style}>
                <button onClick={submitClaim} disabled={!claimEvidence.trim()}
                  style={{ flex: 1, padding: "12px 0", borderRadius: 8, border: "none", background: claimEvidence.trim() ? "linear-gradient(135deg, #D4A053, #8B5E34)" : "#3D3428", color: claimEvidence.trim() ? "#FFF" : "#A69880", fontSize: "0.875rem", fontWeight: 700, cursor: claimEvidence.trim() ? "pointer" : "default" }}>
                  Submit Claim
                </button>
                <button onClick={() => { setShowClaimForm(false); setClaimEvidence(""); setClaimNotes(""); }}
                  style={{ padding: "12px 20px", borderRadius: 8, border: "1px solid #5A4F3E", background: "transparent", color: "#B5A898", fontSize: "0.875rem", cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div style={flexBetweenCenterMb16Style}>
        <h3 style={{ fontFamily: FONT_DISPLAY, color: "#F5E6D0", fontSize: "1.25rem", margin: 0 }}>Expressions ({distBourbons.length})</h3>
        <button onClick={() => { setAddBourbonPreselectedBrand(brand.id); setShowAddBourbonModal(true); }}
          style={{ padding: "8px 14px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>
          + Expression
        </button>
      </div>
      {distBourbons.map(b => {

        const avg = getAvgRating(b.id);

        const exprStatus = getExpressionStatus(b);

        return (
          <div key={b.id} onClick={() => { setSelectedBourbon(b); navigateTo("bourbon-detail"); }}
            style={{ background: "#1E1A14", borderRadius: 12, padding: 16, marginBottom: 10, border: "1px solid #3D3428", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "border-color 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = "#D4A053"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
              {exprStatus === "official" && brand.image && (
                <div style={{ width: 36, height: 36, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#150F0A", borderRadius: 8, border: "1px solid rgba(76,175,80,0.3)" }}>
                  <DistilleryIconRender iconKey={brand.image} size={20} />
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={flexCenterGap6WrapStyle}>
                  <span style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0" }}>{getDisplayName(b)}</span>
                  <StatusBadge bourbon={b} />
                  {exprStatus === "official" && <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: "0.5625rem", fontWeight: 600, background: "rgba(76,175,80,0.12)", color: "#4CAF50", border: "1px solid rgba(76,175,80,0.3)" }}>{"\u2713"}</span>}
                  {exprStatus === "pending-review" && <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: "0.5625rem", fontWeight: 600, background: "rgba(255,193,7,0.12)", color: "#FFC107", border: "1px solid rgba(255,193,7,0.3)" }}>⏳</span>}
                  <SubmissionBadgeInline item={b} getSubmissionStatus={getSubmissionStatus} />
                </div>
                <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginTop: 4 }}>{formatProof(b)} · {formatAge(b.age)} · {formatMsrp(b.msrp)}</div>
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
              {avg && <div style={{ color: "#D4A053", fontWeight: 700, fontSize: "1.125rem" }}>{fmtRating(avg)}</div>}
              <div style={sFonCol3Style}>{b.type}</div>
            </div>
          </div>
        );
      })}
      {distBourbons.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "#B5A898" }}>
          <div style={{ fontSize: "2.0rem", marginBottom: 12 }}>🥃</div>
          <p style={{ margin: "0 0 12px" }}>No expressions yet for this brand.</p>
          <button onClick={() => { setAddBourbonPreselectedBrand(brand.id); setShowAddBourbonModal(true); }}
            style={{ padding: "10px 20px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.8125rem", fontWeight: 700, cursor: "pointer" }}>
            Add the First Expression
          </button>
        </div>
      )}
    </div>
  );
};

// ─── FACILITY DETAIL VIEW ───────────
const FacilityDetail = ({ facility, brandsByFacility, isBrowsableBrand, canSeeSubmission,
  getBrandBourbons, setSelectedFacility, navigateTo, getAvgRating, communityRatingsMap,
  ratingDrillIn, setRatingDrillIn, getDisplayName, getCheckins, checkins, communityCheckins,
  setSelectedBourbon, setSelectedBrand, brands }) => {
  const facBrands = (brandsByFacility.get(facility.permitNumber) || [])
    .filter(b => isBrowsableBrand(b) && canSeeSubmission(b));

  // Group brands: parent brands absorb their children
  const parentGroups = new Map(); // parentId → { parent, children: [] }
  const standalone = []; // brands with no parent and no children

  // First pass: identify parents that exist in this facility's brands
  const facBrandIds = new Set(facBrands.map(b => b.id));
  for (const b of facBrands) {
    if (facBrands.some(c => c.parentBrandId === b.id)) {
      parentGroups.set(b.id, { parent: b, children: [] });
    }
  }
  // Second pass: assign children, collect standalone
  for (const b of facBrands) {
    if (parentGroups.has(b.id)) continue; // already a parent
    if (b.parentBrandId && parentGroups.has(b.parentBrandId)) {
      parentGroups.get(b.parentBrandId).children.push(b);
    } else if (b.parentBrandId && facBrandIds.has(b.parentBrandId)) {
      // parent exists but wasn't detected (no other children) — create group
      const p = facBrands.find(x => x.id === b.parentBrandId);
      if (p) { parentGroups.set(p.id, { parent: p, children: [b] }); }
      else standalone.push(b);
    } else {
      standalone.push(b);
    }
  }

  // Build display list: parent groups + standalone, sorted by expression count
  const getGroupExprCount = (brands) => brands.reduce((sum, b) => sum + getBrandBourbons(b.id).filter(x => canSeeSubmission(x)).length, 0);
  const displayGroups = [
    ...Array.from(parentGroups.values()).map(g => ({
      key: g.parent.id, label: g.parent.name,
      brands: [g.parent, ...g.children].filter(b => getBrandBourbons(b.id).filter(x => canSeeSubmission(x)).length > 0),
      exprCount: getGroupExprCount([g.parent, ...g.children]),
      isGroup: true,
    })),
    ...standalone.map(b => ({
      key: b.id, label: b.name,
      brands: [b],
      exprCount: getBrandBourbons(b.id).filter(x => canSeeSubmission(x)).length,
      isGroup: false,
    })),
  ].filter(g => g.exprCount > 0).sort((a, b) => b.exprCount - a.exprCount);

  const totalExprs = displayGroups.reduce((sum, g) => sum + g.exprCount, 0);
  const totalBrands = displayGroups.length;
  const [expandedBrand, setExpandedBrand] = useState(null);
  const [plSort, setPlSort] = useState("count"); // "name" | "count"

  return (
    <div style={posRelativeStyle}>
      <button onClick={() => { setSelectedFacility(null); navigateTo("brands"); }}
        style={closeButtonStyle} aria-label="Close">✕</button>
      <div style={{ background: "linear-gradient(180deg, #3D3428 0%, #1E1A14 100%)", borderRadius: 16, padding: 32, marginBottom: 24, border: "1px solid #5A4F3E", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -10, right: -10, opacity: 0.06 }}><DistilleryIconRender iconKey={facility.icon || facility.image} size={100} /></div>
        <div style={posRelativeStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 52, height: 52, display: "flex", alignItems: "center", justifyContent: "center", background: "#150F0A", borderRadius: 12, border: "1px solid #5A4F3E" }}>
              <DistilleryIconRender iconKey={facility.icon || facility.image} size={30} />
            </div>
          </div>
          <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "2.0rem", color: "#F5E6D0", margin: "0 0 8px" }}>{facility.name || facility.companyName}</h1>
          {facility.locationDisplay && <div style={{ color: "#D4A053", fontSize: "0.875rem", marginBottom: 6 }}>{"📍"} {facility.locationDisplay}</div>}
          {facility.est && <div style={{ color: "#B5A898", fontSize: "0.875rem", marginBottom: 6 }}>Est. {facility.est}</div>}
          <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginBottom: 6 }}>{"🏭"} {facility.permitNumber}</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {facility.kbt && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 20, background: "rgba(212,160,83,0.1)", border: "1px solid rgba(212,160,83,0.25)" }}>
              <span style={font08125Style}>{"🏞️"}</span>
              <span style={{ fontSize: "0.6875rem", fontWeight: 700, color: "#D4A053", letterSpacing: 0.5 }}>Kentucky Bourbon Trail®</span>
            </span>}
            {facility.ubt && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 20, background: "rgba(100,149,237,0.1)", border: "1px solid rgba(100,149,237,0.25)" }}>
              <span style={font08125Style}>{"🏙️"}</span>
              <span style={{ fontSize: "0.6875rem", fontWeight: 700, color: "#6495ED", letterSpacing: 0.5 }}>Urban Bourbon Trail®</span>
            </span>}
          </div>
          {facility.description && <p style={{ color: "#B5A898", fontSize: "0.875rem", lineHeight: 1.7, margin: 0 }}>{facility.description}</p>}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 24 }}>
        <div style={{ background: "#1E1A14", borderRadius: 10, padding: 12, border: "1px solid #3D3428", textAlign: "center" }}>
          <div style={{ fontSize: "1.375rem", fontWeight: 700, color: "#D4A053", fontFamily: FONT_DISPLAY }}>{totalBrands}</div>
          <div style={{ fontSize: "0.6875rem", color: "#B5A898", marginTop: 2 }}>Product Line{totalBrands !== 1 ? "s" : ""}</div>
        </div>
        <div style={{ background: "#1E1A14", borderRadius: 10, padding: 12, border: "1px solid #3D3428", textAlign: "center" }}>
          <div style={{ fontSize: "1.375rem", fontWeight: 700, color: "#D4A053", fontFamily: FONT_DISPLAY }}>{totalExprs}</div>
          <div style={{ fontSize: "0.6875rem", color: "#B5A898", marginTop: 2 }}>Expression{totalExprs !== 1 ? "s" : ""}</div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "0 0 12px" }}>
        <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.25rem", color: "#F5E6D0", margin: 0 }}>Product Lines</h2>
        <div style={{ display: "flex", gap: 4 }}>
          {[["name", "A–Z"], ["count", "# Expressions"]].map(([key, label]) => (
            <button key={key} onClick={() => setPlSort(key)}
              style={{ padding: "4px 10px", borderRadius: 6, fontSize: "0.6875rem", fontWeight: plSort === key ? 700 : 400, border: "1px solid", cursor: "pointer",
                background: plSort === key ? "rgba(212,160,83,0.12)" : "transparent",
                color: plSort === key ? "#D4A053" : "#A69880",
                borderColor: plSort === key ? "rgba(212,160,83,0.3)" : "#3D3428" }}>{label}</button>
          ))}
        </div>
      </div>
      {[...displayGroups].sort((a, b) => plSort === "name" ? a.label.localeCompare(b.label) : b.exprCount - a.exprCount).map(group => {
        const allExprs = group.brands.flatMap(b => getBrandBourbons(b.id).filter(x => canSeeSubmission(x)))
          .sort((a, b) => a.name.localeCompare(b.name));
        const isExpanded = expandedBrand === group.key;

        return (
          <div key={group.key} style={{ background: "#1E1A14", borderRadius: 12, marginBottom: 10, border: "1px solid #3D3428", overflow: "hidden" }}>
            <div onClick={() => setExpandedBrand(isExpanded ? null : group.key)}
              style={{ padding: "14px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "background 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#2A2419"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div>
                <div style={flexCenterGap8Style}>
                  <span style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0" }}>{group.label}</span>
                  {group.isGroup && <span style={{ fontSize: "0.5625rem", padding: "2px 6px", borderRadius: 8, background: "rgba(212,160,83,0.08)", color: "#A69880", border: "1px solid #3D3428" }}>{group.brands.length} lines</span>}
                </div>
                <div style={mutedText075Mt2Style}>{group.exprCount} expression{group.exprCount !== 1 ? "s" : ""}</div>
              </div>
              <span style={{ color: "#B5A898", fontSize: "0.75rem", transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "none" }}>▼</span>
            </div>
            {isExpanded && (
              <div style={{ borderTop: "1px solid #3D3428", padding: "8px 0" }}>
                {group.isGroup ? group.brands.map(brand => {
                  const brandExprs = getBrandBourbons(brand.id).filter(x => canSeeSubmission(x)).sort((a, b) => a.name.localeCompare(b.name));
                  if (brandExprs.length === 0) return null;
                  return (
                    <div key={brand.id}>
                      {group.brands.length > 1 && <div style={{ padding: "6px 16px 2px", fontSize: "0.6875rem", color: "#A69880", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{brand.name}</div>}
                      {brandExprs.map(b => {
                        const avg = b.avgRating;
                        const hasRatings = b.ratingCount > 0;
                        const isDrillOpen = ratingDrillIn === b.id;
                        return (
                          <div key={b.id}>
                            <div style={{ padding: "10px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "background 0.15s" }}
                              onMouseEnter={e => e.currentTarget.style.background = "#2A2419"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                              <div style={{ minWidth: 0, flex: 1 }} onClick={() => { setSelectedBourbon(b); navigateTo("bourbon-detail"); }}>
                                <div style={{ color: "#F5E6D0", fontSize: "0.875rem" }}>{getDisplayName(b)}</div>
                                <div style={mutedText06875Mt2Style}>{(b.proof || b.proofMin) ? formatProof(b) : ""}{(b.proof || b.proofMin) && b.type ? " · " : ""}{b.type || ""}</div>
                              </div>
                              {avg && <div onClick={(e) => { e.stopPropagation(); setRatingDrillIn(isDrillOpen ? null : b.id); }} style={{ color: "#D4A053", fontWeight: 700, fontSize: "0.875rem", fontFamily: FONT_DISPLAY, flexShrink: 0, marginLeft: 12, cursor: "pointer", padding: "4px 6px", borderRadius: 6, background: isDrillOpen ? "rgba(212,160,83,0.1)" : "transparent", transition: "background 0.15s" }}>{fmtRating(avg)}</div>}
                            </div>
                            {isDrillOpen && hasRatings && <RatingDistribution allCheckins={[...checkins, ...(communityCheckins || [])].filter(c => c.bourbonId === b.id)} myCheckins={getCheckins(b.id)} totalCount={b.ratingCount} fmtDate={fmtDate} fmtRating={fmtRating} avgRating={b.avgRating} />}
                          </div>
                        );
                      })}
                    </div>
                  );
                }) : allExprs.map(b => {
                  const avg = b.avgRating;
                  const hasRatings = b.ratingCount > 0;
                  const isDrillOpen = ratingDrillIn === b.id;
                  return (
                    <div key={b.id}>
                      <div style={{ padding: "10px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "background 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#2A2419"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <div style={{ minWidth: 0, flex: 1 }} onClick={() => { setSelectedBourbon(b); navigateTo("bourbon-detail"); }}>
                          <div style={{ color: "#F5E6D0", fontSize: "0.875rem" }}>{getDisplayName(b)}</div>
                          <div style={mutedText06875Mt2Style}>{(b.proof || b.proofMin) ? formatProof(b) : ""}{(b.proof || b.proofMin) && b.type ? " · " : ""}{b.type || ""}</div>
                        </div>
                        {avg && <div onClick={(e) => { e.stopPropagation(); setRatingDrillIn(isDrillOpen ? null : b.id); }} style={{ color: "#D4A053", fontWeight: 700, fontSize: "0.875rem", fontFamily: FONT_DISPLAY, flexShrink: 0, marginLeft: 12, cursor: "pointer", padding: "4px 6px", borderRadius: 6, background: isDrillOpen ? "rgba(212,160,83,0.1)" : "transparent", transition: "background 0.15s" }}>{fmtRating(avg)}</div>}
                      </div>
                      {isDrillOpen && hasRatings && <RatingDistribution allCheckins={[...checkins, ...(communityCheckins || [])].filter(c => c.bourbonId === b.id)} myCheckins={getCheckins(b.id)} totalCount={b.ratingCount} fmtDate={fmtDate} fmtRating={fmtRating} avgRating={b.avgRating} />}
                    </div>
                  );
                })}
                <div style={{ padding: "8px 16px" }}>
                  <button onClick={(e) => { e.stopPropagation(); setSelectedBrand(group.brands[0]); navigateTo("distillery-detail"); }}
                    style={{ background: "none", border: "none", color: "#D4A053", fontSize: "0.75rem", cursor: "pointer", padding: 0 }}>
                    View {group.label} details →
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

function BarrelRankApp() {

  const [view, setView] = useState(() => { try { const saved = sessionStorage.getItem("br_ptr_view"); if (saved) { sessionStorage.removeItem("br_ptr_view"); return saved; } } catch {} return "home"; });

  const [prevView, setPrevView] = useState("home");

  const [subView, setSubView] = useState(null);

  const [brands, setBrands] = useState(DEMO ? BRANDS_SEED : []);

  const [facilities, setFacilities] = useState([]);

  const dataLoadedRef = useRef(false);
  const pullIndicatorRef = useRef(null);
  const pullRefreshRef = useRef(null);
  const viewRef = useRef(view);

  const bourbonIdRemapRef = useRef(new Map()); // DB bourbon ID ←’ seed bourbon ID for deduped items
  const brandIdRemapRef = useRef(new Map()); // dropped COLA brand ID -> surviving seed brand ID

  const [dataLoading, setDataLoading] = useState(false);
  const [fullCatalogLoaded, setFullCatalogLoaded] = useState(false);

  const [bourbons, setBourbons] = useState(DEMO ? BOURBONS_SEED : []);

  const [showAddBrandModal, setShowAddBrandModal] = useState(false);

  const [showAddBourbonModal, setShowAddBourbonModal] = useState(() => { try { return sessionStorage.getItem("br_add_form") ? true : false; } catch { return false; } });

  const [addBourbonPreselectedBrand, setAddBourbonPreselectedBrand] = useState(null);

  const addModalFormRef = useRef({ step: "scan", brandGuess: "", exprGuess: "", upcInput: "", proof: "", age: "", mashBill: "", type: "Bourbon", msrp: "", notes: "", batch: "", limitedRelease: false, selectedBrandId: null, selectedExprId: null });

  const [checkins, setCheckins] = useState(DEMO ? [
    { id: "c1", bourbonId: "b4", rating: 4.5, date: "2026-01-28", location: "The Silver Dollar, Louisville", tastingNotes: ["Vanilla", "Caramel", "Black Pepper", "Oak"], nose: "Rich caramel and vanilla waft up immediately", palate: "Full-bodied with baking spice and toffee", finish: "Long, warm, and peppery", comment: "The quintessential bourbon experience. At 101 proof, it's got enough backbone for cocktails but smooth enough to sip. Jimmy Russell is a national treasure.", servingStyle: "Neat", glassware: "Glencairn", drinkingWith: ["u2", "u4"] },
    { id: "c2", bourbonId: "b3", rating: 4.0, date: "2026-01-20", location: "Home Bar", tastingNotes: ["Citrus", "Vanilla", "Caramel", "Rye Spice"], nose: "Bright citrus and vanilla", palate: "Creamy mouthfeel with orange and caramel", finish: "Medium, clean with spice", comment: "Finally found one at MSRP! The horse stopper bottle is iconic. Great bourbon but the hype pushes expectations too high.", servingStyle: "Neat", glassware: "Glencairn" },
    { id: "c3", bourbonId: "b10", rating: 4.5, date: "2026-01-15", location: "Bourbon & Beyond Festival", tastingNotes: ["Char", "Vanilla", "Butterscotch", "Nutmeg"], nose: "Toasted char and sweet vanilla", palate: "Butterscotch, dark fruit, nutmeg", finish: "Long and warming with char", comment: "The best value in bourbon, period. This punches way above its weight class. If people knew what was in this bottle, it would be as hard to find as Blanton's.", servingStyle: "Neat", glassware: "Rocks Glass", drinkingWith: ["u3"] },
  ] : []);

  const [wishlist, setWishlist] = useState(DEMO ? ["b13", "b15", "b9"] : []);

  const [myBar, setMyBar] = useState(DEMO ? [
    { bourbonId: "b4", pricePaid: 24, fill: 60, dateAdded: "2025-12-01" },
    { bourbonId: "b10", pricePaid: 30, fill: 40, dateAdded: "2025-11-15" },
    { bourbonId: "b7", pricePaid: 27, fill: 90, dateAdded: "2026-01-05" },
    { bourbonId: "b6", pricePaid: 36, fill: 100, dateAdded: "2026-01-20" },
    { bourbonId: "b3", pricePaid: 65, fill: 75, dateAdded: "2026-01-20" },
  ] : []);

  const [selectedBourbon, setSelectedBourbon] = useState(null);
  const [variantFromParent, setVariantFromParent] = useState(false);

  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);

  const [showCheckinModal, setShowCheckinModal] = useState(false);

  const [checkinBourbon, setCheckinBourbon] = useState(null);
  const [editingCheckinId, setEditingCheckinId] = useState(null);
  const [viewCheckinDetail, setViewCheckinDetail] = useState(null); // checkin object for read-only detail
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [celebrationData, setCelebrationData] = useState(null);
  const [overconsumptionNotice, setOverconsumptionNotice] = useState(null);
  const [barSharePrompt, setBarSharePrompt] = useState(null); // { bourbonId, bourbonName, fromDetail? }
  const [ratingDrillIn, setRatingDrillIn] = useState(null); // bourbonId for rating distribution panel

  const [searchQuery, setSearchQuery] = useState("");
  const [archiveSearchResults, setArchiveSearchResults] = useState([]);
  const [archiveSearching, setArchiveSearching] = useState(false);
  const [universalSearchOpen, setUniversalSearchOpen] = useState(false);
  const [universalSearchQ, setUniversalSearchQ] = useState("");
  const [pendingScanUpc, setPendingScanUpc] = useState(null);
  const [addFillLevel, setAddFillLevel] = useState(100);
  const [batchAddMode, setBatchAddMode] = useState(null); // null | { searchQ, selected, scanning, flash }
  const [batchAddResults, setBatchAddResults] = useState([]);

  const [barSearchQ, setBarSearchQ] = useState("");

  const [barAddOverrides, setBarAddOverrides] = useState({}); // { [bourbonId]: { price, fill, batch } }
  const [customProof, setCustomProof] = useState(null);
  const [barrelInfo, setBarrelInfo] = useState('');
  const [variantPickerData, setVariantPickerData] = useState(null); // { parent, variants } or null

  const [showBarScanner, setShowBarScanner] = useState(false);
  const [showBrowseScanner, setShowBrowseScanner] = useState(false);
  const [scanTransition, setScanTransition] = useState(null);

  const [upcCodes, setUpcCodes] = useState([]); // DB-backed UPC lookup table

  const [archiveSearch, setArchiveSearch] = useState("");

  const [distSearch, setDistSearch] = useState("");
  const [brandsSort, setBrandsSort] = useState({ by: "alpha", alphaDir: "asc", exprDir: "desc" });

  const [archiveFilter, setArchiveFilter] = useState("");

  const [expandedArchiveItem, setExpandedArchiveItem] = useState(null);

  const [emptyBottlePrompt, setEmptyBottlePrompt] = useState(null);

  const [deleteConfirm, setDeleteConfirm] = useState(null); // { idx, bourbonId, isArchived }

  const [barQuickFill, setBarQuickFill] = useState(null); // index into myBar or null

  const [barSort, setBarSort] = useState({ key: "name", dir: "asc" });

  const [barFilterQ, setBarFilterQ] = useState("");

  const [barImport, setBarImport] = useState({ open: false, matches: [], step: "upload", stats: null });

  const [activeFilters, setActiveFilters] = useState(new Set()); // multi-select browse filters

  const [distRegion, setDistRegion] = useState(new Set()); // multi-select distillery filters

  const [expandedFilterGroup, setExpandedFilterGroup] = useState(null);

  const [browsePrice, setBrowsePrice] = useState([0, 200]);

  const [browseProof, setBrowseProof] = useState([80, 160]);

  const [browseSort, setBrowseSort] = useState({ key: "popular", dir: "desc" });
  const [browseShowAll, setBrowseShowAll] = useState(false);

  // ─── SOCIAL / PRIVACY ─────────────────────

  const [checkinPrivacy, setCheckinPrivacy] = useState("public");

  const [barPrivacy, setBarPrivacy] = useState("friends");

  const [profilePrivacy, setProfilePrivacy] = useState("public");

  const [profileAvatar, setProfileAvatar] = useState("🥃"); // emoji string or data URL

  const [showAvatarPicker, setShowAvatarPicker] = useState(false);

  const [notifyToasts, setNotifyToasts] = useState(true);

  // ─── REFERRAL SYSTEM ────────────────────────

  const [referralCode, setReferralCode] = useState(""); // generated on auth

  const [referrals, setReferrals] = useState([]); // [{ uid, name, joinedAt, type: "personal"|"commercial" }]

  const [showReferralQR, setShowReferralQR] = useState(false);

  const [showInviteSheet, setShowInviteSheet] = useState(false);

  // ─── AUTH / IDENTITY SYSTEM ─────────────────

  const [currentUser, setCurrentUser] = useState(null);
  const isAuthenticated = currentUser !== null;

  const [authScreen, setAuthScreen] = useState("landing"); // "landing" | "login" | "signup" | "verify" | "forgot"

  const [authForm, setAuthForm] = useState({ name: "", email: "", password: "", confirmPassword: "", ageConfirmed: false });

  const [authError, setAuthError] = useState("");

  const [authLoading, setAuthLoading] = useState(true); // true initially to check persisted session

  const [biometricAvailable, setBiometricAvailable] = useState(false);

  const [biometricEnrolled, setBiometricEnrolled] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const [pendingVerification, setPendingVerification] = useState(null);

  const [forgotEmailSent, setForgotEmailSent] = useState(false);

  // ─── SESSION MANAGEMENT ─────────────────────────────────
  // Check persisted session + biometric availability on mount

  useEffect(() => {

    const restoreSession = () => {
      try {

        const raw = sessionStorage.getItem(`${BRAND.storagePrefix}_session`);
        if (raw) {

          const data = JSON.parse(raw);
          if (data.appVersion === APP_VERSION && data.uid && data.expiresAt > Date.now()) {

            const user = platformUsers.find(u => u.uid === data.uid);
            if (user && user.status === "active") {
              setCurrentUser(prev => { const d = { uid: user.uid, name: user.name, email: user.email, emailVerified: user.emailVerified, authMethods: user.authMethods, role: user.role, createdAt: user.createdAt, lastLogin: new Date().toISOString() }; return prev ? { ...prev, ...d } : d; });
            } else {
              try { sessionStorage.removeItem(`${BRAND.storagePrefix}_session`); } catch {}

            }

          } else {
            try { sessionStorage.removeItem(`${BRAND.storagePrefix}_session`); } catch {}

          }

        }

      } catch {}
      // Don't clear authLoading if OAuth hash is pending — the auth listener will handle it
      const hasOAuthHash = window.location.hash && window.location.hash.includes("access_token");
      if (!hasOAuthHash) setAuthLoading(false);
    };
    restoreSession();
    // Clear stuck authLoading when user returns via browser Back from OAuth redirect (bfcache restore)
    const onPageShow = (e) => { if (e.persisted) { const h = window.location.hash; if (!h || !h.includes("access_token")) setAuthLoading(false); } };
    window.addEventListener("pageshow", onPageShow);
    if (window.PublicKeyCredential) {
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable?.().then(avail => {
        setBiometricAvailable(avail);
        try { if (avail && sessionStorage.getItem(`${BRAND.storagePrefix}_biometric`)) setBiometricEnrolled(true); } catch {}
      }).catch(() => {});

    }
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── SUPABASE AUTH STATE LISTENER ───────────────────────
  // Handles the redirect callback after Google OAuth sign-in

  const loadUserData = useCallback(async (userId, userRole) => {
    if (!useSupabase()) return;
    if (dataLoadedRef.current) return; // prevent duplicate loads from auth race
    dataLoadedRef.current = true;
    setDataLoading(true);

    // ── Load user profile ──
    const sb = getSupabase();
    if (sb) {
      try {
        const { data: userProfile, error: profileErr } = await sb.from("users").select("username, avatar, shelf_staple_id, shelf_staple_note, signup_order").eq("uid", userId).single();
        if (!profileErr && userProfile) {
          if (userProfile.username) setUsername(userProfile.username);
          if (userProfile.avatar) setProfileAvatar(userProfile.avatar);
          setCurrentUser(prev => prev ? { ...prev, shelf_staple_id: userProfile.shelf_staple_id || null, shelf_staple_note: userProfile.shelf_staple_note || null, signup_order: userProfile.signup_order ?? null } : prev);
        }
      } catch (e) { /* username/avatar columns may not exist yet */ }
    }

    // ── Fetch brand roles for this user (lightweight, non-blocking) ──
    if (sb) {
      try {
        const { data: brData, error: brErr } = await sb.from("business_roles").select("id, business_id, role").eq("uid", userId);
        if (!brErr && brData && brData.length > 0) setUserBrandRoles(brData);
      } catch (e) { /* business_roles may not exist yet */ }
    }

    // ── PHASE 1: Personal data + catalog ──
    const loadStart = Date.now();
    const { userCheckins, userFriends, publicCheckins, dbBourbons } = await loadCatalog(userId);

    // All personal data + catalog merged — drop the loading screen (min 750ms display)
    const elapsed = Date.now() - loadStart;
    if (elapsed < 1500) await new Promise(r => setTimeout(r, 1500 - elapsed));
    setDataLoading(false);

    // ── PHASE 2: Community/social data (deferred — UI is already interactive) ──
    await loadSocialData(userId, userCheckins, userFriends, publicCheckins);

    // ── PHASE 3: Admin data ──
    if (userRole === "admin") await loadAdminData(dbBourbons);

    // ── Sub-functions ──

    async function loadCatalog(uid) {
      const [userCheckins, userBar, userFriends, userWishlist, publicCheckins, userPending, dbBourbons, dbBrands, dbUPCs, dbFacilities, dbAliases] = await Promise.all([
        db.loadCheckins(uid),
        db.loadMyBar(uid),
        db.loadFriends(uid),
        db.loadWishlist(uid),
        db.loadPublicCheckins(30),
        db.loadPendingRequests(uid),
        db.loadBourbons(),
        db.loadBrands(),
        db.loadUPCs(),
        db.loadFacilities(),
        db.loadAliases(),
      ]);

      // Set personal data immediately
      if (userCheckins) setCheckins(userCheckins);
      if (userBar) setMyBar(userBar);
      if (userWishlist) setWishlist(userWishlist);
      if (userPending) setPendingRequests(userPending);
      if (userFriends) setFriends(userFriends);

      // ── BRAND DEDUP + BOURBON REMAP ──
      const normalizeBrandName = (n) => (n || "").toLowerCase().replace(/[‘’`,.]/g, "").replace(/\b(inc|llc|ltd|co|corp|company|distillery|distilleries|distilling|spirits|whiskey)\b/g, "").replace(/\s+/g, " ").trim();

      const brandIdRemap = new Map();
      if (dbBrands) {
        const fromDb = DEMO ? dbBrands.filter(d => !SEED_BRAND_IDS.has(d.id)) : dbBrands;

        const dupeGroups = new Map();
        for (const d of fromDb) {
          const norm = normalizeBrandName(d.name);
          if (!dupeGroups.has(norm)) dupeGroups.set(norm, []);
          dupeGroups.get(norm).push(d);
        }

        const deduped = new Map();
        for (const [norm, group] of dupeGroups) {
          const score = (r) => (r.description ? 2 : 0) + (r.est ? 1 : 0) + (r.facilityId ? 2 : 0) + (r.lat ? 1 : 0) + (r.website ? 1 : 0);
          group.sort((a, b) => score(b) - score(a));
          const winner = group[0];
          deduped.set(norm, winner);
          for (let i = 1; i < group.length; i++) brandIdRemap.set(group[i].id, winner.id);
        }

        const dedupedBrands = Array.from(deduped.values());

        setBrands(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const seedNorms = prev.map(p => [normalizeBrandName(p.name), p.id]);
          const findSeedMatch = (colaNorm) => {
            for (const [sn, sid] of seedNorms) {
              if (colaNorm === sn || colaNorm.startsWith(sn + " ")) return sid;
            }
            return null;
          };
          const filteredToAdd = [];
          for (const d of dedupedBrands) {
            if (existingIds.has(d.id)) continue;
            const matchingSeedId = findSeedMatch(normalizeBrandName(d.name));
            if (matchingSeedId) {
              brandIdRemap.set(d.id, matchingSeedId);
            } else {
              filteredToAdd.push(d);
            }
          }
          const dbMap = new Map(dbBrands.map(d => [d.id, d]));
          const updated = prev.map(p => {
            const dbr = dbMap.get(p.id);
            if (dbr && p.userAdded && dbr.submissionStatus) return { ...p, submissionStatus: dbr.submissionStatus, reviewedBy: dbr.reviewedBy, reviewedAt: dbr.reviewedAt, rejectionReason: dbr.rejectionReason };
            return p;
          });
          return [...updated, ...filteredToAdd];
        });

        brandIdRemapRef.current = brandIdRemap;
      }

      // ── BOURBONS (single setBourbons: dedup + aliases + childCounts) ──
      if (dbBourbons) {
        const fromDb = dbBourbons.map(b => {
          let brandId = b.brandId;
          const remapped = brandIdRemap.get(brandId);
          if (remapped) brandId = remapped;
          if (!brandId && b.name) {
            const bNameLower = b.name.toLowerCase();
            for (const [sid, sname] of Object.entries(SEED_BRAND_NAMES)) {
              const seedNameLower = sname.toLowerCase().replace(/ distill.*| \(.*/, "");
              if (bNameLower.startsWith(seedNameLower)) { brandId = sid; break; }
            }
          }
          return brandId !== b.brandId ? { ...b, brandId } : b;
        });

        setBourbons(prev => {
          const existing = new Set(prev.map(p => p.id));
          const nameKeyMap = new Map(prev.map(p => [
            (p.name || "").toLowerCase().replace(/[‘’`,.]/g, "").trim() + "|" + (p.brandId || ""), p.id
          ]));
          const idRemap = new Map();
          const tierRank = { curated: 4, core: 3, visible: 2, archive: 1 };
          const dedupScore = (b) => (tierRank[b.tier] || 2) * 10000 + (b.popularityScore || 0) * 10 + (b.parentExpressionId ? 0 : 1);
          const bestByKey = new Map(prev.map(p => [
            (p.name || "").toLowerCase().replace(/[‘’`,.]/g, "").trim() + "|" + (p.brandId || ""), p
          ]));

          for (const b of fromDb) {
            if (existing.has(b.id)) continue;
            if (b.parentExpressionId) { bestByKey.set(b.id, b); continue; }
            const key = (b.name || "").toLowerCase().replace(/[‘’`,.]/g, "").trim() + "|" + (b.brandId || "");
            const rival = bestByKey.get(key);
            if (rival) {
              if (dedupScore(b) > dedupScore(rival)) {
                idRemap.set(rival.id, b.id); bestByKey.set(key, b); nameKeyMap.set(key, b.id);
              } else {
                idRemap.set(b.id, rival.id);
              }
            } else {
              bestByKey.set(key, b); nameKeyMap.set(key, b.id);
            }
          }

          const winners = new Set([...bestByKey.values()].map(b => b.id));
          const toAdd = fromDb.filter(b => winners.has(b.id) && !existing.has(b.id));
          bourbonIdRemapRef.current = idRemap;

          const dbMap = new Map(dbBourbons.map(b => [b.id, b]));
          const updated = prev.map(p => {
            const dbr = dbMap.get(p.id);
            if (dbr && p.userAdded && dbr.submissionStatus) return { ...p, submissionStatus: dbr.submissionStatus, reviewedBy: dbr.reviewedBy, reviewedAt: dbr.reviewedAt, rejectionReason: dbr.rejectionReason };
            return p;
          });

          const evicted = new Set(idRemap.keys());
          const merged = [...updated.filter(p => !evicted.has(p.id)), ...toAdd];

          // Attach aliases + compute child counts in same pass (eliminates double setBourbons)
          const childCounts = new Map();
          for (const b of merged) {
            if (b.parentExpressionId) childCounts.set(b.parentExpressionId, (childCounts.get(b.parentExpressionId) || 0) + 1);
          }
          return merged.map(b => {
            const a = dbAliases ? dbAliases.get(b.id) : null;
            const cc = childCounts.get(b.id) || 0;
            if (!a && !cc) return b;
            return { ...b, ...(a ? { aliases: a } : {}), childCount: cc };
          });
        });
      }

      if (dbFacilities) setFacilities(dbFacilities);
      if (dbUPCs && dbUPCs.length > 0) setUpcCodes(dbUPCs);

      // ── ORPHAN RESCUE ──
      if (userCheckins && userCheckins.length > 0) {
        const allKnownIds = new Set([...SEED_BOURBON_IDS, ...(dbBourbons || []).map(b => b.id)]);
        const orphanIds = userCheckins.map(c => c.bourbonId).filter(id => !allKnownIds.has(id));
        if (orphanIds.length > 0) {
          const sb2 = getSupabase();
          if (sb2) {
            const uniqueOrphans = [...new Set(orphanIds)];
            const { data: rescued } = await sb2.from("bourbons").select("id,name,brand_id,facility_id,proof,age,mash_bill,msrp,type,finish_type,batch,notes,limited_release,discontinued,user_added,website,source,origin,image_url,submission_status").in("id", uniqueOrphans);
            if (rescued && rescued.length > 0) {
              const mapped = rescued.map(r => ({
                id: r.id, name: r.name, brandId: r.brand_id, facilityId: r.facility_id,
                proof: r.proof ? parseFloat(r.proof) : null, age: r.age || "", mashBill: r.mash_bill || "",
                msrp: r.msrp ? parseFloat(r.msrp) : null, type: r.type || "", finishType: r.finish_type || "",
                batch: r.batch || "", notes: r.notes || "", limitedRelease: r.limited_release || false,
                discontinued: r.discontinued || false, userAdded: r.user_added || false,
                website: r.website || "", source: r.source || null, imageUrl: r.image_url || null,
                submissionStatus: r.submission_status || "approved",
              }));
              setBourbons(prev => {
                const existing = new Set(prev.map(p => p.id));
                const toAdd = mapped.filter(b => !existing.has(b.id));
                return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
              });
            }
          }
        }
      }

      return { userCheckins, userFriends, publicCheckins, dbBourbons };
    }

    async function loadSocialData(uid, userCheckins, userFriends, publicCheckins) {
      let allCommunity = publicCheckins || [];
      if (userFriends) {
        setCommunityUsers(prev => {
          const merged = [...prev];
          userFriends.forEach(f => { if (!merged.find(m => m.id === f.id)) merged.push({ id: f.id, name: f.name, avatar: f.avatar || "🥃" }); });
          return merged;
        });
        const friendIds = userFriends.map(f => f.id);
        if (friendIds.length > 0) {
          const fc = await db.loadFriendCheckins(friendIds);
          if (fc) {
            const ids = new Set(allCommunity.map(c => c.id));
            fc.forEach(c => { if (!ids.has(c.id)) { allCommunity.push(c); ids.add(c.id); } });
          }
        }
      }
      allCommunity = allCommunity.filter(c => c.userId !== uid);
      allCommunity.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setCommunityCheckins(allCommunity);

      const authorIds = [...new Set(allCommunity.map(c => c.userId))];
      const allCheckinIds = [...(userCheckins || []).map(c => c.id), ...allCommunity.map(c => c.id)].filter(Boolean);
      const [nameMap, toastData] = await Promise.all([
        authorIds.length > 0 ? db.loadUserNames(authorIds) : {},
        allCheckinIds.length > 0 ? db.loadToastsForCheckins(allCheckinIds) : [],
      ]);
      if (authorIds.length > 0) {
        setCommunityUsers(prev => {
          const merged = [...(DEMO ? prev : [])];
          Object.values(nameMap).forEach(u => { if (!merged.find(m => m.id === u.id)) merged.push(u); });
          return merged;
        });
      }
      if (allCheckinIds.length > 0) setToasts(toastData);
    }

    async function loadAdminData(dbBourbons) {
      const [dbClaims, dbRoles, dbFlags, dbAudit, dbExprEdits] = await Promise.all([
        db.loadClaims(), db.loadBusinessRoles(), db.loadFlags(), db.loadAuditLog(500), db.loadExpressionEdits(),
      ]);
      if (dbClaims && dbClaims.length > 0) setBusinessClaims(dbClaims);
      if (dbRoles && dbRoles.length > 0) setBusinessRoles(dbRoles);
      if (dbFlags && dbFlags.length > 0) setFlaggedContent(dbFlags);
      if (dbAudit && dbAudit.length > 0) setAuditLog(dbAudit);
      if (dbExprEdits && dbExprEdits.length > 0) setExpressionEdits(dbExprEdits);
    }

  }, []);

  useEffect(() => {

    const sb = getSupabase();
    if (!sb) return;

    // Guard: prevent double-fire from INITIAL_SESSION + SIGNED_IN + TOKEN_REFRESHED
    let authHandled = false;

    function handleSession(session) {
      if (authHandled || !session?.user) return;
      authHandled = true;

      const u = session.user;
      const name = u.user_metadata?.full_name || u.user_metadata?.name || u.email?.split("@")[0] || "User";

      // Set auth + dataLoading atomically — transitions from auth splash → data splash with no gap
      const newUser = {
        uid: u.id, name: sanitizeInput(name, 30), realName: sanitizeInput(name, 30), email: u.email,
        emailVerified: u.email_confirmed_at != null,
        authMethods: [{ provider: u.app_metadata?.provider || "google" }],
        role: "user", createdAt: u.created_at, lastLogin: new Date().toISOString()
      };
      setCurrentUser(prev => prev ? { ...prev, ...newUser } : newUser);
      persistSession(u.id);
      setDataLoading(true);
      setAuthLoading(false);

      // Query actual role async — upgrades to admin if applicable, then loads data
      // loadUserData sets dataLoading(false) when done
      sb.from("users").select("role").eq("uid", u.id).single().then(({ data: dbUser }) => {
        const role = dbUser?.role || "user";
        if (role !== "user") setCurrentUser(prev => prev ? { ...prev, role } : prev);
        loadUserData(u.id, role);
      }).catch(() => {
        // New user or DB error — proceed with default role
        loadUserData(u.id, "user");
      });
    }

    // Supabase v2 fires INITIAL_SESSION on listener registration (replays current state),
    // then SIGNED_IN on new logins, TOKEN_REFRESHED on token refresh.
    // Handle all auth-bearing events; the authHandled guard deduplicates.
    const { data: { subscription } } = sb.auth.onAuthStateChange((event, session) => {
      if ((event === "SIGNED_IN" || event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED") && session?.user) {
        handleSession(session);
      }
    });

    // Extract OAuth tokens from hash SYNCHRONOUSLY before any other effect can clear them.
    // The deep link useEffect (later in the file) clears window.location.hash on every render
    // when view=home. Supabase's async _initialize() may not read the hash in time.

    const hash = window.location.hash;
    if (hash && hash.includes("access_token")) {

      const params = new URLSearchParams(hash.replace("#", ""));
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");
      if (accessToken && refreshToken) {

        // Explicitly set session — fires onAuthStateChange(SIGNED_IN) above
        sb.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });

        // Clean up the URL hash so it doesn't linger
        window.history.replaceState(null, "", window.location.pathname);

      }

    }
    // No else needed — onAuthStateChange(INITIAL_SESSION) replays any existing session

    return () => subscription?.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentUser?.uid) {

      // Deterministic short code from UID: take last 4 chars + name initials

      const initials = (currentUser.name || "").split(" ").map(w => w[0]?.toUpperCase() || "").join("").slice(0, 2);

      const uidSuffix = currentUser.uid.replace(/[^a-zA-Z0-9]/g, "").slice(-4).toUpperCase();

      const code = `${BRAND.referralPrefix}-` + initials + uidSuffix;
      setReferralCode(code);

      // Seed demo referrals for preview (u1 = Lance)
      if (DEMO && currentUser.uid === "u1" && referrals.length === 0) {
        setReferrals([
          { uid: "u3", name: "Sarah Chen", joinedAt: "2025-10-01", type: "personal" },
          { uid: "u5", name: "Maria Gonzalez", joinedAt: "2025-12-15", type: "personal" },
          { uid: "u6", name: "Dean Park", joinedAt: "2026-01-20", type: "commercial" },

        ]);

      }

    } else {
      setReferralCode("");

    }

  }, [currentUser?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist session — in production this stores JWT tokens from backend
  // No secrets in the client. Session validity is enforced server-side.

  const persistSession = useCallback((uid) => {
    try {
      sessionStorage.setItem(`${BRAND.storagePrefix}_session`, JSON.stringify({
        uid, appVersion: APP_VERSION,
        createdAt: Date.now(),
        expiresAt: Date.now() + (90 * 24 * 60 * 60 * 1000),
      }));
    } catch {}
  }, []); // APP_VERSION is a module constant — stable across renders

  const clearSession = useCallback(() => {
    try { sessionStorage.removeItem(`${BRAND.storagePrefix}_session`); } catch {}
  }, []);

  const [platformUsers, setPlatformUsers] = useState(DEMO ? [
    { uid: "u1", name: "Lance", email: `lance@${BRAND.domain}`, emailVerified: true, authMethods: [{ provider: "email" }, { provider: "google" }], role: "admin", status: "active", createdAt: "2025-06-01", lastLogin: "2026-02-06T09:32:00Z", flaggedContent: 0 },
    { uid: "u2", name: "Jake Morrison", email: "jake@bourbon.fan", emailVerified: true, authMethods: [{ provider: "email" }], role: "user", status: "active", createdAt: "2025-07-10", lastLogin: "2026-02-05T14:20:00Z", flaggedContent: 0 },
    { uid: "u3", name: "Sarah Chen", email: "sarah@texasbourbon.com", emailVerified: true, authMethods: [{ provider: "google" }], role: "user", status: "active", createdAt: "2025-08-20", lastLogin: "2026-02-04T18:45:00Z", flaggedContent: 0 },
    { uid: "u4", name: "Tom Whitfield", email: "tom@rickhouse.bar", emailVerified: true, authMethods: [{ provider: "email" }, { provider: "facebook" }], role: "user", status: "active", createdAt: "2025-09-05", lastLogin: "2026-02-06T08:10:00Z", flaggedContent: 1 },
    { uid: "u5", name: "Maria Gonzalez", email: "maria@denverbourbon.co", emailVerified: false, authMethods: [{ provider: "email" }], role: "user", status: "active", createdAt: "2025-11-12", lastLogin: "2026-01-28T22:00:00Z", flaggedContent: 0 },
    { uid: "u6", name: "Dean Park", email: "dean@craftspirits.nyc", emailVerified: true, authMethods: [{ provider: "google" }, { provider: "facebook" }], role: "user", status: "suspended", createdAt: "2025-10-01", lastLogin: "2026-01-15T10:30:00Z", flaggedContent: 3 },
    { uid: "u7", name: "Ellie Summers", email: "ellie@musiccity.tn", emailVerified: true, authMethods: [{ provider: "email" }], role: "user", status: "active", createdAt: "2025-12-01", lastLogin: "2026-02-03T20:15:00Z", flaggedContent: 0 },
    { uid: "u8", name: "Chris Hayden", email: "chris@btfanatic.com", emailVerified: true, authMethods: [{ provider: "email" }], role: "user", status: "active", createdAt: "2026-01-10", lastLogin: "2026-02-06T07:00:00Z", flaggedContent: 0 },
  ] : []);

  // ─── AUTH HANDLERS ──────────────────────────
  // Login rate limiter

  const [loginAttempts, setLoginAttempts] = useState({ count: 0, lockUntil: 0 });

  const MAX_LOGIN_ATTEMPTS = 5;

  const LOCKOUT_DURATION = 300000; // 5 minutes

  const handleLogin = useCallback(async (email, password) => {
    setAuthError("");
    if (loginAttempts.lockUntil > Date.now()) {

      const secs = Math.ceil((loginAttempts.lockUntil - Date.now()) / 1000);
      setAuthError(`Too many failed attempts. Try again in ${secs} seconds.`);
      return;

    }

    if (!email || !password) { setAuthError("Email and password are required."); return; }
    setAuthLoading(true);

    const sb = getSupabase();
    if (sb) {

      const { data, error } = await sb.auth.signInWithPassword({ email, password });

      if (error) {
        setLoginAttempts(prev => {

          const c = prev.count + 1;
          return { count: c, lockUntil: c >= MAX_LOGIN_ATTEMPTS ? Date.now() + LOCKOUT_DURATION : prev.lockUntil };

        });

        setAuthError("Invalid email or password.");
        setAuthLoading(false);
        return;

      }

      // Success — auth state listener handles setting currentUser
      setLoginAttempts({ count: 0, lockUntil: 0 });

      setAuthForm({ name: "", email: "", password: "", confirmPassword: "" });

      // Auth state listener will fire and set user
    } else {

      // Demo fallback
      setTimeout(() => {

        const user = platformUsers.find(u => u.email.toLowerCase() === email.toLowerCase());
        if (!user) {
          setLoginAttempts(prev => {

            const c = prev.count + 1;
            return { count: c, lockUntil: c >= MAX_LOGIN_ATTEMPTS ? Date.now() + LOCKOUT_DURATION : prev.lockUntil };

          });

          setAuthError("Invalid email or password."); setAuthLoading(false); return;

        }

        if (user.status === "suspended") { setAuthError("Invalid email or password."); setAuthLoading(false); return; }
        if (!password || password.length < 6) {
          setLoginAttempts(prev => {

            const c = prev.count + 1;
            return { count: c, lockUntil: c >= MAX_LOGIN_ATTEMPTS ? Date.now() + LOCKOUT_DURATION : prev.lockUntil };

          });

          setAuthError("Invalid email or password."); setAuthLoading(false); return;

        }

        setLoginAttempts({ count: 0, lockUntil: 0 });

        setCurrentUser(prev => { const d = { uid: user.uid, name: user.name, email: user.email, emailVerified: user.emailVerified, authMethods: user.authMethods, role: user.role, createdAt: user.createdAt, lastLogin: new Date().toISOString() }; return prev ? { ...prev, ...d } : d; });
        persistSession(user.uid);
        setAuthLoading(false);
        setAuthForm({ name: "", email: "", password: "", confirmPassword: "" });

      }, 800);

    }

  }, [platformUsers, persistSession, loginAttempts]);

  const handleSignup = useCallback(async (name, email, password, confirmPassword) => {
    setAuthError("");
    if (!authForm.ageConfirmed) { setAuthError("You must confirm you are 21 or older to create an account."); return; }
    if (!name.trim()) { setAuthError("Name is required."); return; }
    if (name.trim().length < 3) { setAuthError("Name must be at least 3 characters."); return; }
    if (!email || email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { setAuthError("Enter a valid email address."); return; }
    if (password.length < 8) { setAuthError("Password must be at least 8 characters."); return; }
    if (!/[A-Z]/.test(password) || !/[0-9]/.test(password)) { setAuthError("Password needs at least one uppercase letter and one number."); return; }
    if (password !== confirmPassword) { setAuthError("Passwords don't match."); return; }

    const profanityCheck = (() => { const w = name.toLowerCase().replace(/[^a-z\s]/g, "").split(/\s+/); return w.some(x => PROFANITY_BLOCK.some(p => x.includes(p))); })();
    if (profanityCheck) { setAuthError("Display name contains inappropriate language."); return; }
    setAuthLoading(true);

    const sb = getSupabase();
    if (sb) {

      const { data, error } = await sb.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: sanitizeInput(name.trim(), 30) },
          emailRedirectTo: window.location.origin

        }

      });

      if (error) {
        if (error.message?.includes("already registered")) {
          setAuthError("An account with that email already exists. Try logging in.");
        } else {
          setAuthError(error.message || "Unable to create account. Please try again.");

        }

        setAuthLoading(false);
        return;

      }

      // Supabase sends a confirmation email
      setPendingVerification(email);
      setAuthScreen("verify");
      setAuthLoading(false);
    } else {

      // Demo fallback
      if (platformUsers.find(u => u.name.toLowerCase() === name.trim().toLowerCase())) { setAuthError("\"" + name.trim() + "\" is already taken. Choose a unique display name."); setAuthLoading(false); return; }
      if (platformUsers.find(u => u.email.toLowerCase() === email.toLowerCase())) { setAuthError("An account with that email already exists. Try logging in."); setAuthLoading(false); return; }
      setTimeout(() => {

        const newUid = generateId("u_");

        const newUser = { uid: newUid, name: sanitizeInput(name, 30), email: email.toLowerCase(), emailVerified: false, authMethods: [{ provider: "email" }], role: "user", status: "active", createdAt: new Date().toISOString().split("T")[0], lastLogin: new Date().toISOString(), flaggedContent: 0 };
        setPlatformUsers(prev => [...prev, newUser]);

        setPendingVerification(email);
        setAuthScreen("verify");
        setAuthLoading(false);
      }, 800);

    }

  }, [platformUsers, authForm.ageConfirmed]);

  const handleSocialLogin = useCallback(async (provider) => {
    setAuthError("");

    // Rate limit social login attempts
    if (loginAttempts.lockUntil > Date.now()) {

      const secs = Math.ceil((loginAttempts.lockUntil - Date.now()) / 1000);
      setAuthError(`Too many attempts. Try again in ${secs} seconds.`);
      return;

    }

    // Only Google is currently configured
    if (provider !== "google") {
      setAuthError(`${provider.charAt(0).toUpperCase() + provider.slice(1)} sign-in is not yet available.`);
      return;

    }

    setAuthLoading(true);

    const sb = getSupabase();
    if (sb) {

      // Real Supabase OAuth — redirects to Google, then back to our site

      const { error } = await sb.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: window.location.origin }

      });

      if (error) {
        setAuthError("Unable to sign in with Google. Please try again.");
        setAuthLoading(false);

      }

      // On success, page redirects — auth state listener handles the rest
    } else {

      // Demo fallback when Supabase client not available
      setTimeout(() => {

        const mockEmail = "lance@gmail.com";

        const existing = platformUsers.find(u => u.email === `lance@${BRAND.domain}` || u.authMethods?.some(a => a.provider === provider));
        let socialUid = null;
        if (existing) {
          if (existing.status === "suspended") { setAuthError("Unable to sign in. Please try another method or contact support."); setAuthLoading(false); return; }
          if (!existing.authMethods.find(a => a.provider === provider)) {
            setPlatformUsers(prev => prev.map(u => u.uid === existing.uid ? { ...u, authMethods: [...u.authMethods, { provider }] } : u));

          }

          socialUid = existing.uid;
          setCurrentUser(prev => { const d = { uid: existing.uid, name: existing.name, email: existing.email, emailVerified: true, authMethods: [...existing.authMethods, { provider }], role: existing.role, createdAt: existing.createdAt, lastLogin: new Date().toISOString() }; return prev ? { ...prev, ...d } : d; });

        } else {
          socialUid = generateId("u_");

          const newUser = { uid: socialUid, name: "Google User", email: mockEmail, emailVerified: true, authMethods: [{ provider }], role: "user", status: "active", createdAt: new Date().toISOString().split("T")[0], lastLogin: new Date().toISOString(), flaggedContent: 0 };
          setPlatformUsers(prev => [...prev, newUser]);

          setCurrentUser(prev => prev ? { ...prev, ...newUser } : { ...newUser });

        }

        persistSession(socialUid);
        setAuthLoading(false);
      }, 1000);

    }

  }, [platformUsers, persistSession, loginAttempts]);

  const handleBiometricLogin = useCallback(() => {
    setAuthError("");
    if (loginAttempts.lockUntil > Date.now()) {

      const secs = Math.ceil((loginAttempts.lockUntil - Date.now()) / 1000);
      setAuthError(`Too many attempts. Try again in ${secs} seconds.`);
      return;

    }

    setAuthLoading(true);
    setTimeout(() => {

      const user = platformUsers.find(u => u.uid === "u1");
      if (user) {
        setCurrentUser(prev => { const d = { uid: user.uid, name: user.name, email: user.email, emailVerified: user.emailVerified, authMethods: user.authMethods, role: user.role, createdAt: user.createdAt, lastLogin: new Date().toISOString() }; return prev ? { ...prev, ...d } : d; });
        persistSession(user.uid);

      }

      setAuthLoading(false);
    }, 600);
  }, [platformUsers, persistSession, loginAttempts]);

  const handleLogout = useCallback(async () => {

    // Sign out of Supabase (clears Google/OAuth session)

    const sb = getSupabase();
    if (sb) { try { await sb.auth.signOut(); } catch {} }
    setCurrentUser(null);
    setAuthScreen("landing");
    setAuthForm({ name: "", email: "", password: "", confirmPassword: "" });

    setAuthError("");
    clearSession();
    dataLoadedRef.current = false;
    bourbonIdRemapRef.current = new Map();
  }, [clearSession]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [toastMsg, setToastMsg] = useState("");

  const [toastVisible, setToastVisible] = useState(false);

  const toastTimeout = useRef(null);

  const showToast = useCallback((msg) => {
    if (toastTimeout.current) clearTimeout(toastTimeout.current);
    setToastMsg(msg); setToastVisible(true);
    toastTimeout.current = setTimeout(() => setToastVisible(false), 3000);
  }, []);

  const handleDeleteAccount = useCallback(async () => {

    // Call server-side cascade delete (deletes all user data + auth row)
    if (useSupabase()) {

      const result = await db.deleteUserAccount();
      if (!result.success) {
        showToast("Failed to delete account: " + (result.error || "Unknown error"));
        setShowDeleteConfirm(false);
        return;

      }

    }

    // Clear all local data

    const sb = getSupabase();
    if (sb) { try { await sb.auth.signOut(); } catch {} }

    // Clear localStorage items
    try {

      const prefix = BRAND.storagePrefix;
      ["_blocked", "_seenToasts", "_gate", "_biometric"].forEach(k => {
        try { localStorage.removeItem(prefix + k); } catch {}

      });

      try { sessionStorage.removeItem(prefix + "_gate"); } catch {}
    } catch {}
    setCurrentUser(null);
    setAuthScreen("landing");
    setAuthForm({ name: "", email: "", password: "", confirmPassword: "", ageConfirmed: false });

    setAuthError("");
    clearSession();
    setCheckins([]);

    setMyBar([]);

    setWishlist([]);

    setFriends([]);

    setShowDeleteConfirm(false);
    dataLoadedRef.current = false;
    bourbonIdRemapRef.current = new Map();
    showToast("Account deleted. All your data has been removed.");
  }, [clearSession, showToast]);

  // ─── PROFANITY / CONTENT MODERATION ─────────

  const PROFANITY_LIST = PROFANITY_BLOCK;

  const checkProfanity = useCallback((text) => {
    if (!text) return { clean: true, flagged: [] };

    const words = text.toLowerCase().replace(/[^a-z\s]/g, "").split(/\s+/);

    const flagged = [];
    for (const w of words) { for (const p of PROFANITY_LIST) { if (w === p || w.includes(p)) flagged.push(p); } }
    return { clean: flagged.length === 0, flagged: [...new Set(flagged)] };
  }, [PROFANITY_LIST]);

  const profanityRegex = useMemo(() => {

    const escaped = PROFANITY_LIST.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    return escaped.map(p => ({ regex: new RegExp(p, "gi"), len: p.length }));
  }, [PROFANITY_LIST]);

  const sanitizeText = useCallback((text) => {
    if (!text) return text;
    let r = text;
    for (const { regex, len } of profanityRegex) r = r.replace(regex, "\u2588".repeat(len));
    return r;
  }, [profanityRegex]);

  // XSS-safe input sanitizer: strips HTML tags, trims, enforces max length
  // sanitizeInput and generateId are now module-level functions (defined above component)
  // Clipboard with fallback

  const copyToClipboard = useCallback((text) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {

        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        try { document.execCommand("copy"); } catch {}
        document.body.removeChild(ta);

      });

    } else {

      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(ta);

    }

  }, []);

  // ─── BUSINESS CLAIMS & VERIFICATION ─────────

  const [businessClaims, setBusinessClaims] = useState(DEMO ? [
    { id: "claim1", businessId: "d8", businessName: "Angel's Envy", claimantUid: "u4", claimantName: "Tom Whitfield", status: "approved", verificationMethod: "domain-email", evidence: "tom@angelsenvy.com verified", submittedAt: "2025-11-01", reviewedAt: "2025-11-03", reviewedBy: "u1", notes: "Domain email confirmed" },
    { id: "claim2", businessId: "d17", businessName: "Rabbit Hole", claimantUid: "u3", claimantName: "Sarah Chen", status: "submitted", verificationMethod: "admin-review", evidence: "Business license photo uploaded", submittedAt: "2026-01-28", reviewedAt: null, reviewedBy: null, notes: "" },
    { id: "claim3", businessId: "d19", businessName: "Bardstown Bourbon Company", claimantUid: "u6", claimantName: "Dean Park", status: "rejected", verificationMethod: "domain-email", evidence: "Email not from business domain", submittedAt: "2026-01-10", reviewedAt: "2026-01-12", reviewedBy: "u1", notes: "Could not verify affiliation" },
    { id: "claim4", businessId: "d14", businessName: "Castle & Key", claimantUid: "u2", claimantName: "Jake Morrison", status: "draft", verificationMethod: null, evidence: null, submittedAt: "2026-02-05", reviewedAt: null, reviewedBy: null, notes: "" },
    { id: "claim5", businessId: "d8", businessName: "Angel's Envy", claimantUid: "u6", claimantName: "Dean Park", status: "disputed", verificationMethod: "admin-review", evidence: "Claims to be new GM", submittedAt: "2026-02-01", reviewedAt: null, reviewedBy: null, notes: "Dispute: existing owner Tom Whitfield contests" },
  ] : []);

  const [businessRoles, setBusinessRoles] = useState(DEMO ? [
    { businessId: "d8", uid: "u4", role: "owner", grantedBy: "system", grantedAt: "2025-11-03" },
    { businessId: "d8", uid: "u7", role: "manager", grantedBy: "u4", grantedAt: "2025-12-15" },
  ] : []);

  // ─── BRAND DASHBOARD ROLES (current user's brand roles) ───
  const [userBrandRoles, setUserBrandRoles] = useState([]);

  // ─── AUDIT LOG ──────────────────────────────

  const [auditLog, setAuditLog] = useState(DEMO ? [
    { id: "a1", ts: "2026-02-06T09:32:00Z", actor: "u1", actorName: "Lance", action: "login", category: "auth", detail: "Email/password login", target: null },
    { id: "a2", ts: "2026-02-05T14:20:00Z", actor: "u2", actorName: "Jake Morrison", action: "login", category: "auth", detail: "Email/password login", target: null },
    { id: "a3", ts: "2025-11-03T10:00:00Z", actor: "u1", actorName: "Lance", action: "claim_approved", category: "claim", detail: "Approved claim for Angel's Envy", target: "claim1" },
    { id: "a4", ts: "2025-11-03T10:01:00Z", actor: "system", actorName: "System", action: "role_granted", category: "role", detail: "Owner role granted to Tom Whitfield for Angel's Envy", target: "d8" },
    { id: "a5", ts: "2025-12-15T16:30:00Z", actor: "u4", actorName: "Tom Whitfield", action: "role_granted", category: "role", detail: "Manager role granted to Ellie Summers for Angel's Envy", target: "d8" },
    { id: "a6", ts: "2026-01-12T09:00:00Z", actor: "u1", actorName: "Lance", action: "claim_rejected", category: "claim", detail: "Rejected claim by Dean Park for Bardstown Bourbon Co.", target: "claim3" },
    { id: "a7", ts: "2026-01-15T11:00:00Z", actor: "u1", actorName: "Lance", action: "user_suspended", category: "moderation", detail: "Suspended Dean Park - repeated profanity violations", target: "u6" },
    { id: "a8", ts: "2026-02-01T08:00:00Z", actor: "u6", actorName: "Dean Park", action: "claim_disputed", category: "claim", detail: "Dispute filed for Angel's Envy ownership", target: "claim5" },
    { id: "a9", ts: "2026-02-05T14:25:00Z", actor: "u2", actorName: "Jake Morrison", action: "claim_draft", category: "claim", detail: "Draft claim created for Castle & Key", target: "claim4" },
    { id: "a10", ts: "2026-02-04T12:00:00Z", actor: "u4", actorName: "Tom Whitfield", action: "business_field_edit", category: "business", detail: "Updated Angel's Envy hours", target: "d8" },
  ] : []);

  const appendAudit = useCallback((actor, actorName, action, category, detail, target) => {
    setAuditLog(prev => [...prev, { id: generateId("a_"), ts: new Date().toISOString(), actor, actorName, action, category, detail, target }]);

    if (useSupabase()) db.insertAudit(actor, actorName, action, category, detail, target);
  }, []);

  // ─── FLAGGED CONTENT QUEUE ──────────────────

  const [flaggedContent, setFlaggedContent] = useState(DEMO ? [
    { id: "f1", type: "checkin_comment", userId: "u6", userName: "Dean Park", content: "This bourbon is f***ing amazing, best s**t I've ever had", flaggedWords: ["fuck", "shit"], status: "pending", flaggedAt: "2026-01-14T20:00:00Z", reviewedBy: null },
    { id: "f2", type: "checkin_comment", userId: "u6", userName: "Dean Park", content: "Anyone who drinks this is a complete a**hole", flaggedWords: ["asshole"], status: "actioned", flaggedAt: "2026-01-15T10:00:00Z", reviewedBy: "u1", actionTaken: "removed" },
    { id: "f3", type: "checkin_comment", userId: "u4", userName: "Tom Whitfield", content: "Hell yeah, this is damn good bourbon", flaggedWords: [], status: "approved", flaggedAt: "2026-01-20T15:00:00Z", reviewedBy: "u1" },
    { id: "f4", type: "bar_name", userId: "u6", userName: "Dean Park", content: "Dean's Sh*thole Bar", flaggedWords: ["shit"], status: "pending", flaggedAt: "2026-02-02T11:00:00Z", reviewedBy: null },
  ] : []);

  // ─── SHARED HOOKS (used by admin + suggest edit) ─────
  const [expressionEdits, setExpressionEdits] = useState([]);
  const [showSuggestEdit, setShowSuggestEdit] = useState(false);
  const [suggestEditData, setSuggestEditData] = useState({});

  const [activeInfoPanel, setActiveInfoPanel] = useState(null); // "xpHelp" | "xpBreakdown" | "recoInfo" | "flavorRadarInfo" | null

  const [showFlavorProfile, setShowFlavorProfile] = useState(true);

  const [showBadges, setShowBadges] = useState(false);
  const [showProofSpot, setShowProofSpot] = useState(false);
  const [showProofInfo, setShowProofInfo] = useState(false);
  const [grainMapExpanded, setGrainMapExpanded] = useState(false);
  const [showGrainTooltip, setShowGrainTooltip] = useState(false);

  const [showMySubmissions, setShowMySubmissions] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const [username, setUsername] = useState(DEMO ? "Lance" : "");

  const [editingUsername, setEditingUsername] = useState(false);

  const [usernameInput, setUsernameInput] = useState("");

  const [usernameError, setUsernameError] = useState("");

  // Sync username from currentUser on login

  useEffect(() => {
    if (currentUser?.name && (!username || username === "Lance")) setUsername(currentUser.name);
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const [accountType, setAccountType] = useState("personal"); // "personal" | "commercial"

  const [barProfile, setBarProfile] = useState({
    name: "The Rickhouse",
    venueType: "bar", // bar | distillery | tasting_room | restaurant | retail | hotel | event_venue
    tagline: "Craft bourbon bar & tasting room",
    location: "412 Main St, Louisville, KY 40202",
    hours: "Tue-Sat 4pm-12am \u00B7 Sun 2pm-10pm",
    vibe: "Speakeasy",
    singlePourOz: 1.5,

  });

  const [pourPricing, setPourPricing] = useState({}); // { [bourbonId]: { neat: price, rocks: price, dbl: price, flight: bool } }

  const [menuView, setMenuView] = useState(false);

  const [showBarQR, setShowBarQR] = useState(false);

  const [menuCategory, setMenuCategory] = useState("all"); // "all"|"available"|"premium"|"flights"

  const [friends, setFriends] = useState(DEMO ? [
    { id: "u2", name: "Jake Morrison", location: "Louisville, KY", avatar: "🤠", since: "2025-08-12" },
    { id: "u3", name: "Sarah Chen", location: "Austin, TX", avatar: "🥃", since: "2025-10-01" },
    { id: "u4", name: "Tom Whitfield", location: "Bardstown, KY", avatar: "🏛️", since: "2025-11-20" },
  ] : []);

  const [pendingRequests, setPendingRequests] = useState({ incoming: [], outgoing: [] });

  const [communityUsers, setCommunityUsers] = useState(DEMO ? [
    { id: "u2", name: "Jake Morrison", location: "Louisville, KY", avatar: "🤠", tagline: "Bourbon trail veteran · 200+ check-ins", checkinPrivacy: "public", barPrivacy: "friends", bar: [
      { bourbonId: "b4", fill: 45, pricePaid: 25 }, { bourbonId: "b13", fill: 80, pricePaid: 90 }, { bourbonId: "b2", fill: 30, pricePaid: 40 }, { bourbonId: "b10", fill: 60, pricePaid: 28 }, { bourbonId: "b8", fill: 100, pricePaid: 50 },
    ]},
    { id: "u3", name: "Sarah Chen", location: "Austin, TX", avatar: "🥃", tagline: "Wheated bourbon enthusiast", checkinPrivacy: "public", barPrivacy: "public", bar: [
      { bourbonId: "b15", fill: 70, pricePaid: 65 }, { bourbonId: "b6", fill: 90, pricePaid: 36 }, { bourbonId: "b103", fill: 50, pricePaid: 45 }, { bourbonId: "b3", fill: 25, pricePaid: 60 },
    ]},
    { id: "u4", name: "Tom Whitfield", location: "Bardstown, KY", avatar: "🏛️", tagline: "Distillery tour guide · Barrel proof or bust", checkinPrivacy: "friends", barPrivacy: "friends", bar: [
      { bourbonId: "b22", fill: 15, pricePaid: 300 }, { bourbonId: "b10", fill: 55, pricePaid: 30 }, { bourbonId: "b1", fill: 40, pricePaid: 25 }, { bourbonId: "b125", fill: 85, pricePaid: 90 }, { bourbonId: "b7", fill: 100, pricePaid: 28 }, { bourbonId: "b9", fill: 70, pricePaid: 30 },
    ]},
    { id: "u5", name: "Maria Gonzalez", location: "Denver, CO", avatar: "🏔️", tagline: "High altitude sipping", checkinPrivacy: "public", barPrivacy: "private", bar: [] },
    { id: "u6", name: "Dean Park", location: "Brooklyn, NY", avatar: "👑", tagline: "Craft bourbon collector", checkinPrivacy: "public", barPrivacy: "public", bar: [
      { bourbonId: "b107", fill: 60, pricePaid: 40 }, { bourbonId: "b34", fill: 35, pricePaid: 45 },
    ]},
    { id: "u7", name: "Ellie Summers", location: "Nashville, TN", avatar: "🎵", tagline: "Music city bourbon lover", checkinPrivacy: "public", barPrivacy: "friends", bar: [
      { bourbonId: "b85", fill: 80, pricePaid: 55 }, { bourbonId: "b4", fill: 50, pricePaid: 24 },
    ]},
    { id: "u8", name: "Chris Hayden", location: "Frankfort, KY", avatar: "🌟", tagline: "Buffalo Trace fanatic", checkinPrivacy: "public", barPrivacy: "public", bar: [
      { bourbonId: "b1", fill: 30, pricePaid: 25 }, { bourbonId: "b2", fill: 65, pricePaid: 40 }, { bourbonId: "b3", fill: 10, pricePaid: 55 },
    ]},
  ] : []);

  const [communityCheckins, setCommunityCheckins] = useState(DEMO ? [
    { id: "cc1", userId: "u2", bourbonId: "b4", rating: 5, date: "2026-02-04", location: "The Silver Dollar, Louisville", comment: "101 proof perfection. Jimmy Russell doesn't miss.", tastingNotes: ["Vanilla", "Black Pepper", "Oak"], visibility: "public" },
    { id: "cc2", userId: "u3", bourbonId: "b103", rating: 4.5, date: "2026-02-04", location: "Still Austin Tasting Room", comment: "Their straight bourbon is seriously underrated. Great TX grain-to-glass.", tastingNotes: ["Honey", "Caramel", "Wheat"], visibility: "public" },
    { id: "cc3", userId: "u4", bourbonId: "b22", rating: 5, date: "2026-02-03", location: "Bardstown Bourbon Company", comment: "15 year Pappy at the BBC restaurant. Bucket list moment.", tastingNotes: ["Cherry", "Vanilla", "Oak", "Maple"], visibility: "public" },
    { id: "cc4", userId: "u2", bourbonId: "b13", rating: 4.5, date: "2026-02-03", location: "Home Bar", comment: "Springfield Batch is one of the better recent Booker's releases.", tastingNotes: ["Brown Sugar", "Tobacco", "Vanilla"], visibility: "friends" },
    { id: "cc5", userId: "u5", bourbonId: "b109", rating: 4, date: "2026-02-03", location: "Breckenridge Distillery Tasting Room", comment: "High altitude bourbon hits different up at 9600 feet.", tastingNotes: ["Caramel", "Oak", "Vanilla"], visibility: "public" },
    { id: "cc6", userId: "u6", bourbonId: "b107", rating: 4, date: "2026-02-02", location: "Kings County Distillery", comment: "The peated bourbon from Kings County is really something special.", tastingNotes: ["Smoke", "Chocolate", "Malt"], visibility: "public" },
    { id: "cc7", userId: "u7", bourbonId: "b85", rating: 4.5, date: "2026-02-02", location: "Nelson's Green Brier Distillery", comment: "Belle Meade Reserve is an absolute gem. Nashville bourbon scene is growing!", tastingNotes: ["Cherry", "Cinnamon", "Vanilla"], visibility: "public" },
    { id: "cc8", userId: "u3", bourbonId: "b15", rating: 5, date: "2026-02-01", location: "Home Bar", comment: "C924 might be the best ECBP batch yet. Dark fruit and char for days.", tastingNotes: ["Dark Fruit", "Char", "Cinnamon"], visibility: "public" },
    { id: "cc9", userId: "u8", bourbonId: "b1", rating: 3.5, date: "2026-02-01", location: "Buffalo Trace Distillery Gift Shop", comment: "Grabbed a bottle at the distillery. Solid daily sipper.", tastingNotes: ["Vanilla", "Caramel", "Toffee"], visibility: "public" },
    { id: "cc10", userId: "u4", bourbonId: "b10", rating: 4.5, date: "2026-01-31", location: "Bourbon & Beyond Festival", comment: "Old Grand-Dad 114 continues to be the best value in bourbon.", tastingNotes: ["Char", "Rye Spice", "Vanilla"], visibility: "public" },
    { id: "cc11", userId: "u2", bourbonId: "b7", rating: 4.25, date: "2026-02-05", location: "The Rickhouse", comment: "Eagle Rare on the rocks at the Rickhouse. Perfect Friday vibe.", tastingNotes: ["Toffee", "Cherry", "Oak"], visibility: "public" },
    { id: "cc12", userId: "u7", bourbonId: "b6", rating: 4.5, date: "2026-02-05", location: "The Rickhouse", comment: "First time here. The Elijah Craig Small Batch pour was generous and delicious.", tastingNotes: ["Vanilla", "Caramel", "Nutmeg"], visibility: "public" },
    { id: "cc13", userId: "u3", bourbonId: "b4", rating: 4.75, date: "2026-02-04", location: "The Rickhouse", comment: "Wild Turkey 101 never disappoints. Great speakeasy atmosphere.", tastingNotes: ["Vanilla", "Black Pepper", "Honey"], visibility: "public" },
    { id: "cc14", userId: "u8", bourbonId: "b3", rating: 4.0, date: "2026-02-03", location: "The Rickhouse", comment: "Tried the Woodford Reserve double. Smooth and easy drinking.", tastingNotes: ["Citrus", "Chocolate", "Vanilla"], visibility: "public" },
    { id: "cc15", userId: "u4", bourbonId: "b10", rating: 4.5, date: "2026-02-02", location: "The Rickhouse", comment: "OGD 114 flight was a highlight. Staff really knows their stuff.", tastingNotes: ["Char", "Butterscotch", "Rye Spice"], visibility: "public" },
  ] : null);

  const [friendSearch, setFriendSearch] = useState("");

  const [friendListSort, setFriendListSort] = useState("name"); // "name" | "since"

  const [friendListSearch, setFriendListSearch] = useState("");

  const [friendSearchResults, setFriendSearchResults] = useState([]);

  const [showFriendSearch, setShowFriendSearch] = useState(false);

  const [blockedUsers, setBlockedUsers] = useState(() => {
    try { const raw = localStorage.getItem(`${BRAND.storagePrefix}_blocked`); return raw ? JSON.parse(raw) : []; } catch { return []; }

  });

  const [reportModal, setReportModal] = useState(null); // { userId, userName, contentId, contentType, contentText }

  const blockedUserSet = useMemo(() => new Set(blockedUsers), [blockedUsers]);
  const isBlocked = useCallback((uid) => blockedUserSet.has(uid), [blockedUserSet]);

  const blockUser = useCallback((uid) => {
    setBlockedUsers(prev => {

      const next = prev.includes(uid) ? prev : [...prev, uid];
      try { localStorage.setItem(`${BRAND.storagePrefix}_blocked`, JSON.stringify(next)); } catch {}
      return next;

    });

    setFriends(prev => prev.filter(f => f.id !== uid));
    showToast("User blocked");
  }, []);

  const unblockUser = useCallback((uid) => {
    setBlockedUsers(prev => {

      const next = prev.filter(id => id !== uid);
      try { localStorage.setItem(`${BRAND.storagePrefix}_blocked`, JSON.stringify(next)); } catch {}
      return next;

    });

    showToast("User unblocked");
  }, []);

  // A1: Recommendation engine + community feed search state

  const [dismissedRecos, setDismissedRecos] = useState(new Set());

  const [recoRotationIdx, setRecoRotationIdx] = useState(0);

  const [communityFeedSearch, setCommunityFeedSearch] = useState("");

  const [communityFeedFilter, setCommunityFeedFilter] = useState("all"); // "all" | "friends"

  const [feedSearchFocused, setFeedSearchFocused] = useState(false);

  const [selectedFriend, setSelectedFriend] = useState(null);

  const [friendProfileTab, setFriendProfileTab] = useState("pours");

  const [toasts, setToasts] = useState({}); // { [checkinId]: [{ uid }, ...] }

  const [seenToastKeys, setSeenToastKeys] = useState(() => {
    try { const raw = localStorage.getItem(`${BRAND.storagePrefix}_seenToasts`); return raw ? new Set(JSON.parse(raw)) : new Set(); } catch { return new Set(); }

  });

  const markToastsSeen = useCallback((keys) => {
    setSeenToastKeys(prev => {

      const next = new Set(prev);
      keys.forEach(k => next.add(k));
      try { localStorage.setItem(`${BRAND.storagePrefix}_seenToasts`, JSON.stringify([...next])); } catch {}
      return next;

    });

  }, []);

  const [profileStatDetail, setProfileStatDetail] = useState(null); // "pours"|"unique"|"rating"|"distilleries"|"friends"

  const [ratingFilterRange, setRatingFilterRange] = useState(null); // [min, max] or null

  const [journalSearch, setJournalSearch] = useState("");

  const [journalFilter, setJournalFilter] = useState("all"); // all|expression|distillery|location|notes|rating

  const [shelfStaplePicker, setShelfStaplePicker] = useState({ open: false, search: '', note: '' });
  const [shelfStapleQuery, setShelfStapleQuery] = useState('');
  const shelfSearchTimer = useRef(null);
  const barWriteTimer = useRef({});
  const archiveSearchTimer = useRef(null);
  const bourbonsRef = useRef(bourbons);
  useEffect(() => { bourbonsRef.current = bourbons; }, [bourbons]);

  const friendIdSet = useMemo(() => new Set(friends.map(f => f.id)), [friends]);
  const isFriend = (userId) => friendIdSet.has(userId);

  const communityUserMap = useMemo(() => {
    const m = new Map();
    for (const u of communityUsers) m.set(u.id, { checkinPrivacy: "public", barPrivacy: "public", location: "", tagline: "", bar: [], ...u });
    for (const f of friends) { if (!m.has(f.id)) m.set(f.id, { id: f.id, name: f.name, avatar: f.avatar || "🥃", checkinPrivacy: "public", barPrivacy: "public", location: "", tagline: "", bar: [], since: f.since }); }
    return m;
  }, [communityUsers, friends]);
  const getCommunityUser = useCallback((userId) => communityUserMap.get(userId) || null, [communityUserMap]);

  const validateUsername = useCallback((name) => {

    const trimmed = name.trim();
    if (!trimmed) return "Username cannot be empty.";
    if (trimmed.length < 3) return "Username must be at least 3 characters.";
    if (trimmed.length > 30) return "Username cannot exceed 30 characters.";
    if (trimmed.toLowerCase() === username.toLowerCase()) return "";

    // Profanity check (same list as signup)

    const profCheck = (() => { const w = trimmed.toLowerCase().replace(/[^a-z\s]/g, "").split(/\s+/); return w.some(x => PROFANITY_BLOCK.some(p => x.includes(p))); })();
    if (profCheck) return "Username contains inappropriate language.";

    // Uniqueness check

    const taken = platformUsers.some(u => u.name.toLowerCase() === trimmed.toLowerCase() && u.uid !== currentUser?.uid)
      || communityUsers.some(u => u.name.toLowerCase() === trimmed.toLowerCase() && u.id !== currentUser?.uid);
    if (taken) return "\"" + trimmed + "\" is already taken. Choose a unique name.";
    return "";
  }, [platformUsers, communityUsers, currentUser, username]);

  const toggleToast = useCallback(async (checkinId) => {
    if (!currentUser?.uid) return;

    const myUid = currentUser.uid;

    const myName = currentUser.name || username || "You";

    const existing = toasts[checkinId] || [];

    const alreadyToasted = existing.some(t => t.uid === myUid);
    if (alreadyToasted) {

      // Optimistic remove
      setToasts(prev => ({ ...prev, [checkinId]: (prev[checkinId] || []).filter(t => t.uid !== myUid) }));
      if (useSupabase()) {

        const ok = await db.removeToast(checkinId, myUid);
        if (!ok) {

          // Rollback: re-add toast
          setToasts(prev => ({ ...prev, [checkinId]: [...(prev[checkinId] || []), { uid: myUid, name: myName }] }));
          showToast("⚠️ Couldn't remove toast");

        }

      }

    } else {

      // Optimistic add
      setToasts(prev => ({ ...prev, [checkinId]: [...(prev[checkinId] || []), { uid: myUid, name: myName }] }));
      if (useSupabase()) {

        const ok = await db.addToast(checkinId, myUid);
        if (!ok) {

          // Rollback: remove toast
          setToasts(prev => ({ ...prev, [checkinId]: (prev[checkinId] || []).filter(t => t.uid !== myUid) }));
          showToast("⚠️ Couldn't save toast");

        }

      }

    }

  }, [currentUser, toasts, username]);

  const ToastButton = memo(({ checkinId, toasters, myUid, myName, communityUserMap: cuMap }) => {
    const count = toasters.length;
    const myToast = myUid ? toasters.some(t => t.uid === myUid) : false;
    const getName = (uid) => {
      if (uid === myUid) return myName || "You";
      return cuMap.get(uid)?.name || "Someone";
    };
    const lastOther = toasters.filter(t => t.uid !== myUid).slice(-1)[0];
    let label = "Toast";
    if (myToast && count === 1) label = "Toasted!";
    else if (myToast && count === 2) label = `You and ${getName(lastOther?.uid)}!`;
    else if (myToast && count > 2) label = `You and ${count - 1} others!`;
    else if (!myToast && count === 1) label = `${getName(toasters[0]?.uid)} toasted!`;
    else if (!myToast && count > 1) label = `${getName(lastOther?.uid)} and ${count - 1} other${count - 1 > 1 ? "s" : ""}!`;
    return (
      <button onClick={() => toggleToast(checkinId)}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", background: myToast ? "rgba(212,160,83,0.12)" : "transparent", border: `1px solid ${myToast ? "rgba(212,160,83,0.3)" : "#3D3428"}`, borderRadius: 20, cursor: "pointer", transition: "all 0.2s" }}>
        <span style={{ fontSize: "0.9375rem", transition: "transform 0.2s", transform: myToast ? "rotate(-15deg)" : "none" }}>🥃</span>
        <span style={{ fontSize: "0.75rem", fontWeight: 600, color: myToast ? "#D4A053" : "#A69880" }}>{label}</span>
        {count > 0 && !myToast && <span style={{ fontSize: "0.6875rem", color: "#A69880", marginLeft: 2 }}>🥃</span>}
      </button>
    );
  });

  // ─── MEMOIZED LOOKUP MAPS (O(1) instead of O(n) .find()) ────

  const bourbonMap = useMemo(() => new Map(bourbons.map(b => [b.id, b])), [bourbons]);

  const brandMap = useMemo(() => new Map(brands.map(d => [d.id, d])), [brands]);

  const facilityMap = useMemo(() => new Map(facilities.map(f => [f.permitNumber, f])), [facilities]);

  const facilityStateItems = useMemo(() => {
    const sts = new Set();
    facilities.forEach(fac => { const st = fac.state || (fac.location || "").split(" ")[0] || ""; if (st.length === 2 && /^[A-Z]{2}$/.test(st)) sts.add(st); });
    return [...sts].sort().map(s => ({ key: `state-${s.toLowerCase()}`, label: s }));
  }, [facilities]);

  const bourbonsByBrand = useMemo(() => {

    const m = new Map();
    bourbons.forEach(b => {
      if (!m.has(b.brandId)) m.set(b.brandId, []);

      m.get(b.brandId).push(b);

    });

    return m;
  }, [bourbons]);

  const getBourbon = useCallback((id) => {

    const direct = bourbonMap.get(id);
    if (direct) return direct;

    // Check if this ID was deduped to a seed bourbon during catalog merge

    const remappedId = bourbonIdRemapRef.current.get(id);
    return remappedId ? bourbonMap.get(remappedId) : undefined;
  }, [bourbonMap]);

  const getBrand = useCallback((id) => { const direct = brandMap.get(id); if (direct) return direct; const remapped = brandIdRemapRef.current.get(id); return remapped ? brandMap.get(remapped) : undefined; }, [brandMap]);

  // Returns parent brand if exists, else the brand itself. Use for display on bourbon cards.
  const getDisplayBrand = useCallback((brandId) => {
    const brand = getBrand(brandId);
    if (!brand) return undefined;
    if (brand.parentBrandId) {
      const parent = brandMap.get(brand.parentBrandId);
      if (parent) return parent;
    }
    return brand;
  }, [getBrand, brandMap]);

  const getFacility = useCallback((permit) => facilityMap.get(permit), [facilityMap]);

  // Filter helpers: hide non-bourbon records from default browsing
  // Breweries are mostly bad data (beer COLAs). Importers bring import-only spirits.
  // Wine/beer/liqueur products are noise from COLA search results.

  const NON_SPIRIT_TYPES = /\b(wine|ale|beer|lager|stout|cider|mead|cream liqueur|cordial|vermouth|sake|champagne|sparkling|brandy|port|sherry|grappa|vineyard|hard seltzer|ready to drink|cocktail|vodka|rum|gin|tequila|mezcal|absinthe|liqueur|aperitif|amaro)\b|malt beverage/i;

  const NON_SPIRIT_BRAND = /\b(winery|wineries|wine|vineyard|vineyards|brewing|brewery|brewpub|cidery|meadery)\b/i;

  const isBrowsableBrand = useCallback((brand) => {
    if (!brand.facilityId) return true; // user-added brands always show
    if (NON_SPIRIT_BRAND.test(brand.name)) return false;

    const fac = facilityMap.get(brand.facilityId);
    if (!fac) return true;
    if (fac.permitType === "importer" || fac.permitType === "brewery") return false;

    // Hide brands whose ONLY expressions are non-spirit types (wine, beer, etc.)

    const brandBourbons = bourbonsByBrand.get(brand.id);
    if (!brandBourbons || brandBourbons.length === 0) return false; // no expressions at all

    const hasSpiritExpr = brandBourbons.some(b => !b.type || !NON_SPIRIT_TYPES.test(b.type));
    if (!hasSpiritExpr) return false;
    return true;
  }, [facilityMap, bourbonsByBrand]);

  const isBrowsableBourbon = useCallback((bourbon, isExplicitSearch) => {

    // Always hide non-spirit product types (wine, beer, liqueur, etc.)
    if (bourbon.type && NON_SPIRIT_TYPES.test(bourbon.type)) return false;

    const fac = bourbon.facilityId ? facilityMap.get(bourbon.facilityId) : null;
    if (!fac) return true;
    if (fac.permitType === "brewery") return isExplicitSearch; // brewery permit products only via search (NON_SPIRIT_TYPES already filters actual beer)
    if (fac.permitType === "importer") return isExplicitSearch; // importers only via search
    return true;
  }, [facilityMap]);

  const getBrandBourbons = useCallback((brandId) => bourbonsByBrand.get(brandId) || [], [bourbonsByBrand]);

  // Facility-level groupings for hybrid brand architecture
  const brandsByFacility = useMemo(() => {
    const m = new Map();
    brands.forEach(b => {
      if (!b.facilityId) return;
      if (!m.has(b.facilityId)) m.set(b.facilityId, []);
      m.get(b.facilityId).push(b);
    });
    return m;
  }, [brands]);

  // UPC resolution: DB-backed state first, then hardcoded fallback

  const upcMap = useMemo(() => new Map(upcCodes.map(u => [u.upc, u])), [upcCodes]);

  const resolveUPC = useCallback((code) => {
    const clean = code.replace(/\D/g, "");
    const dbMatch = upcMap.get(clean);
    if (dbMatch) {
      if (dbMatch.expression_id) {
        const b = bourbonMap.get(dbMatch.expression_id);
        if (b) return { name: b.name, distillery: getBrand(b.brandId)?.name || "", proof: b.proof, type: b.type, msrp: b.msrp, age: b.age, mashBill: b.mashBill, expressionId: b.id };
      }
      return { name: dbMatch.expression_name, distillery: dbMatch.distillery_name, expressionId: dbMatch.expression_id };
    }
    const legacy = UPC_DB[clean];
    if (legacy) return legacy;
    return null;
  }, [upcMap, bourbonMap, getBrand]);

  const getDisplayName = useCallback((b) => b ? (b.batch ? `${b.name} — ${b.batch}` : b.name) : "", []);

  // ─── SEARCH INDEX (pre-computed lowercase fields — avoids ~72K .toLowerCase() calls per keystroke) ────
  const bourbonSearchIndex = useMemo(() => {
    const idx = new Map();
    for (const b of bourbons) {
      idx.set(b.id, {
        name: normalizeSearch((b.name || "").toLowerCase()),
        batch: (b.batch || "").toLowerCase(),
        type: (b.type || "").toLowerCase(),
        brandName: normalizeSearch((brandMap.get(b.brandId)?.name || "").toLowerCase()),
        aliases: (b.aliases || []).map(a => normalizeSearch(a.alias.toLowerCase())),
      });
    }
    return idx;
  }, [bourbons, brandMap]);

  // ─── CHECKINS BY BOURBON MAP (O(1) lookup instead of O(n) filter) ────
  const checkinsByBourbon = useMemo(() => {
    const m = new Map();
    for (const c of checkins) {
      if (!m.has(c.bourbonId)) m.set(c.bourbonId, []);
      m.get(c.bourbonId).push(c);
    }
    return m;
  }, [checkins]);

  const getCheckins = useCallback((bourbonId) => checkinsByBourbon.get(bourbonId) || [], [checkinsByBourbon]);

  const avgRatingsMap = useMemo(() => {

    const map = new Map();
    for (const c of checkins) {
      if (!map.has(c.bourbonId)) map.set(c.bourbonId, { sum: 0, count: 0 });

      const entry = map.get(c.bourbonId);
      entry.sum += c.rating;
      entry.count += 1;

    }

    const result = new Map();
    for (const [id, { sum, count }] of map) {
      result.set(id, { avg: parseFloat((sum / count).toFixed(2)), count });

    }

    return result;
  }, [checkins]);

  // A1: Community-wide rating + popularity for recommendations

  const communityRatingsMap = useMemo(() => {

    const map = new Map();

    const allCheckins = [...checkins, ...(communityCheckins || [])];
    for (const c of allCheckins) {
      if (!map.has(c.bourbonId)) map.set(c.bourbonId, { sum: 0, count: 0, ids: new Set() });

      const entry = map.get(c.bourbonId);
      if (!entry.ids.has(c.id)) {
        entry.sum += c.rating;
        entry.count += 1;
        entry.ids.add(c.id);

      }

    }

    const result = new Map();
    for (const [id, { sum, count }] of map) {
      result.set(id, { avg: sum / count, count });

    }

    return result;
  }, [checkins, communityCheckins]);

  // Memoized profile stats — single-pass over checkins for all profile panel data
  const profileStats = useMemo(() => {
    const brandIds = new Set();
    let ratingSum = 0;
    const noteCounts = {};
    const dimScores = {};
    TW_CATS.forEach(cat => { dimScores[cat] = 0; });
    const ratingBuckets = [0, 0, 0, 0, 0, 0, 0]; // 4.5-5.01, 4.0-4.5, 3.5-4.0, 3.0-3.5, 2.5-3.0, 2.0-2.5, 1.0-2.0
    const brandCheckins = new Map(); // brandId → { count, bourbonIds: Set }
    const mashRatings = [];
    let checksWithNotes = 0;
    for (const c of checkins) {
      ratingSum += c.rating;
      const r = c.rating;
      if (r >= 4.5) ratingBuckets[0]++;
      else if (r >= 4.0) ratingBuckets[1]++;
      else if (r >= 3.5) ratingBuckets[2]++;
      else if (r >= 3.0) ratingBuckets[3]++;
      else if (r >= 2.5) ratingBuckets[4]++;
      else if (r >= 2.0) ratingBuckets[5]++;
      else if (r >= 1.0) ratingBuckets[6]++;
      const b = getBourbon(c.bourbonId);
      if (b?.brandId) {
        brandIds.add(b.brandId);
        let entry = brandCheckins.get(b.brandId);
        if (!entry) { entry = { count: 0, bourbonIds: new Set() }; brandCheckins.set(b.brandId, entry); }
        entry.count++;
        entry.bourbonIds.add(c.bourbonId);
      }
      if (b && b.mashCorn > 0) {
        mashRatings.push({ corn: b.mashCorn, rye: b.mashRye || 0, wheat: b.mashWheat || 0, barley: b.mashBarley || 0, rating: c.rating, bourbonId: b.id });
      }
      const weight = (c.rating || 3) / 3;
      let hasFlavorData = (c.tastingNotes || []).length > 0;
      (c.tastingNotes || []).forEach(note => {
        noteCounts[note] = (noteCounts[note] || 0) + 1;
        const dim = NOTE_TO_DIMENSION[note]; if (dim) dimScores[dim] += weight;
      });
      // Also accumulate flavor dimensions from nose/palate/finish pill selections (0.5x weight)
      ["nose", "palate", "finish"].forEach(field => {
        const val = c[field];
        if (!val) return;
        val.split(",").forEach(term => {
          const trimmed = term.trim();
          if (!trimmed) return;
          const dim = resolveFlavorDimension(trimmed);
          if (dim) {
            dimScores[dim] = (dimScores[dim] || 0) + weight * 0.5;
            noteCounts[trimmed] = (noteCounts[trimmed] || 0) + 1;
            hasFlavorData = true;
          }
        });
      });
      if (hasFlavorData) checksWithNotes++;
    }
    const uniqueDistilleries = brandIds.size;
    const avgAllRating = checkins.length ? (ratingSum / checkins.length).toFixed(2) : "\u2014";
    const topNotes = Object.entries(noteCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);
    const maxDimScore = Math.max(1, ...Object.values(dimScores));
    const activeDims = TW_CATS.filter(cat => dimScores[cat] > 0).length;
    const showRadar = checksWithNotes >= 3 && activeDims >= 3;
    const palateArchetype = (() => {
      if (!showRadar) return null;
      const ranked = TW_CATS.map(cat => ({ cat, score: dimScores[cat] })).sort((a, b) => b.score - a.score);
      const top1 = ranked[0].cat, top2 = ranked[1].cat;
      const topSet = new Set([top1, top2]);
      if (topSet.has("Sweet") && topSet.has("Rich")) return { title: "Rich & Indulgent", desc: "You love the decadent side \u2014 caramel, chocolate, and deep sweetness" };
      if (topSet.has("Sweet") && topSet.has("Fruit")) return { title: "Bright & Sweet", desc: "Fruity and honeyed pours light you up \u2014 cherry, apple, and vanilla" };
      if (topSet.has("Sweet") && topSet.has("Grain")) return { title: "Smooth & Mellow", desc: "You prefer easy-going sweetness with a soft, grainy foundation" };
      if (topSet.has("Sweet") && topSet.has("Nutty")) return { title: "Warm & Comforting", desc: "Toasty vanilla, caramel, and roasted nuts \u2014 your comfort zone" };
      if (topSet.has("Sweet") && topSet.has("Spice")) return { title: "Sweet & Fiery", desc: "You like your sweetness with a kick \u2014 caramel meets cinnamon and pepper" };
      if (topSet.has("Sweet") && topSet.has("Wood")) return { title: "Smooth & Oaky", desc: "Vanilla sweetness balanced by warm oak \u2014 classic and refined" };
      if (topSet.has("Spice") && topSet.has("Wood")) return { title: "Bold & Smoky", desc: "Charred oak, black pepper, and baking spice \u2014 you drink with intensity" };
      if (topSet.has("Spice") && topSet.has("Rich")) return { title: "Dark & Peppery", desc: "Tobacco, espresso, and cracked pepper \u2014 complex and commanding" };
      if (topSet.has("Spice") && topSet.has("Fruit")) return { title: "Zesty & Layered", desc: "Fruit and spice play off each other \u2014 cinnamon cherry and ginger plum" };
      if (topSet.has("Wood") && topSet.has("Rich")) return { title: "Deep & Roasted", desc: "Charred wood, dark chocolate, and coffee \u2014 full-bodied and brooding" };
      if (topSet.has("Wood") && topSet.has("Grain")) return { title: "Earthy & Honest", desc: "Toasted grain and clean oak \u2014 you appreciate the craft behind the pour" };
      if (topSet.has("Floral") && topSet.has("Fruit")) return { title: "Light & Aromatic", desc: "Delicate florals and fresh fruit \u2014 you find bourbon's softer side" };
      if (topSet.has("Floral") && topSet.has("Sweet")) return { title: "Soft & Fragrant", desc: "Honeysuckle, lavender, and gentle sweetness \u2014 refined and graceful" };
      if (topSet.has("Nutty") && topSet.has("Rich")) return { title: "Toasty & Deep", desc: "Roasted nuts, espresso, and dark cocoa \u2014 warm and satisfying" };
      if (topSet.has("Nutty") && topSet.has("Wood")) return { title: "Woody & Nutty", desc: "Pecan, oak, and warm earthiness \u2014 grounded and understated" };
      const labels = { Sweet: "Sweet & Easy", Fruit: "Fruity & Bright", Spice: "Spicy & Bold", Wood: "Oaky & Smoky", Grain: "Clean & Grainy", Rich: "Dark & Complex", Floral: "Floral & Delicate", Nutty: "Nutty & Warm" };
      const descs = { Sweet: "Vanilla, caramel, and honey lead the way for you", Fruit: "Cherry, apple, and citrus define your favorite pours", Spice: "Cinnamon, pepper, and clove are what you reach for", Wood: "Oak, char, and smoke are the heart of your palate", Grain: "You appreciate clean, grainy character in your bourbon", Rich: "Chocolate, coffee, and tobacco are your signatures", Floral: "You pick up bourbon's most delicate aromatic notes", Nutty: "Almond, pecan, and roasted notes are your sweet spot" };
      return { title: labels[top1] || "Adventurous & Curious", desc: descs[top1] || "Your palate is still taking shape \u2014 keep exploring" };
    })();
    // Mash bill analytics
    const mashFingerprints = new Map();
    for (const mr of mashRatings) {
      const key = `${Math.round(mr.corn / 5) * 5}-${Math.round(mr.rye / 5) * 5}-${Math.round(mr.wheat / 5) * 5}`;
      if (!mashFingerprints.has(key)) mashFingerprints.set(key, { corn: mr.corn, rye: mr.rye, wheat: mr.wheat, ratings: [], total: 0, sum: 0 });
      const fp = mashFingerprints.get(key);
      fp.ratings.push(mr.rating);
      fp.total++;
      fp.sum += mr.rating;
    }
    const computeGrainSweetSpot = (grain) => {
      const buckets = new Map();
      for (const mr of mashRatings) {
        const pct = Math.round(mr[grain] / 5) * 5;
        if (!buckets.has(pct)) buckets.set(pct, { sum: 0, count: 0 });
        const bk = buckets.get(pct);
        bk.sum += mr.rating;
        bk.count++;
      }
      const entries = [...buckets.entries()].map(([pct, bk]) => ({ pct, avg: bk.sum / bk.count, count: bk.count }));
      entries.sort((a, b) => b.avg - a.avg);
      const topN = Math.max(1, Math.ceil(entries.length * 0.25));
      const topEntries = entries.slice(0, topN);
      const min = Math.min(...topEntries.map(e => e.pct));
      const max = Math.max(...topEntries.map(e => e.pct));
      return { min, max, entries };
    };
    const mashData = {
      ratings: mashRatings,
      count: mashRatings.length,
      fingerprints: mashFingerprints,
      sweetSpots: mashRatings.length >= 5 ? { corn: computeGrainSweetSpot("corn"), rye: computeGrainSweetSpot("rye"), wheat: computeGrainSweetSpot("wheat") } : null
    };
    return { uniqueDistilleries, avgAllRating, topNotes, dimScores, maxDimScore, showRadar, palateArchetype, ratingBuckets, brandCheckins, mashData };
  }, [checkins, getBourbon]);

  const getAvgRating = useCallback((bourbonId) => {

    const entry = avgRatingsMap.get(bourbonId);
    return entry ? entry.avg : null;
  }, [avgRatingsMap]);

  const getCheckinCount = useCallback((bourbonId) => {

    const entry = avgRatingsMap.get(bourbonId);
    return entry ? entry.count : 0;
  }, [avgRatingsMap]);

  const formatCount = useCallback((n) => {
    if (!n || n < 0) return "0";
    if (n >= 1000000) return (n / 1000000).toFixed(n >= 10000000 ? 0 : 1).replace(/\.0$/, '') + 'M';
    if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, '') + 'k';
    return String(n);
  }, []);

  // ─── DISTILLERY CLAIM ←’ EXPRESSION HIERARCHY ─────

  const claimedBrandIds = useMemo(() => new Set(businessClaims.filter(c => c.status === "approved").map(c => c.businessId)), [businessClaims]);

  const isBrandClaimed = useCallback((distId) => claimedBrandIds.has(distId), [claimedBrandIds]);

  // Submission status: "approved" (or no status = legacy seed data), "pending", "rejected", "expired"

  const getSubmissionStatus = useCallback((item) => {
    if (!item) return null;
    if (!item.userAdded) return "approved"; // seed data

    const status = item.submissionStatus || "approved";

    // Auto-expire: pending > 30 days old
    if (status === "pending" && item.submittedAt) {

      const age = Date.now() - new Date(item.submittedAt).getTime();
      if (age > 30 * 24 * 60 * 60 * 1000) return "expired";

    }

    return status;
  }, []);

  // Can current user see this item? Approved = everyone. Pending/rejected/expired = creator + admins only.

  const canSeeSubmission = useCallback((item) => {

    const status = getSubmissionStatus(item);
    if (status === "approved") return true;
    if (!currentUser) return false;
    if (item.submittedBy === currentUser.uid) return true;
    if (currentUser.role === "admin") return true;
    return false;
  }, [getSubmissionStatus, currentUser]);

  const facExprCountMap = useMemo(() => {
    const m = new Map();
    brandsByFacility.forEach((facBrands, permit) => {
      const browsable = facBrands.filter(b => isBrowsableBrand(b));
      m.set(permit, browsable.reduce((s, br) => s + getBrandBourbons(br.id).filter(x => canSeeSubmission(x)).length, 0));
    });
    return m;
  }, [brandsByFacility, isBrowsableBrand, getBrandBourbons, canSeeSubmission]);

  const isBrowsableFacility = useCallback((fac) => {
    if (!fac) return false;
    if (fac.permitType === "importer" || fac.permitType === "brewery") return false;
    const facBrands = brandsByFacility.get(fac.permitNumber) || [];
    const browsable = facBrands.filter(b => isBrowsableBrand(b));
    if (browsable.length === 0) return false;
    const totalExprs = browsable.reduce((sum, b) => sum + (getBrandBourbons(b.id).filter(x => canSeeSubmission(x)).length), 0);
    return totalExprs >= 2;
  }, [brandsByFacility, isBrowsableBrand, getBrandBourbons, canSeeSubmission]);

  const getExpressionStatus = useCallback((bourbon) => {
    if (!bourbon) return null;

    const sub = getSubmissionStatus(bourbon);
    if (sub !== "approved") return sub;
    if (!isBrandClaimed(bourbon.brandId)) return null;
    return bourbon.userAdded ? "pending-review" : "official";
  }, [isBrandClaimed, getSubmissionStatus]);

  // Parse mash bill string into { corn, rye, wheat, barley, other } percentages

  const parseMashBillPct = useCallback((mashBill) => {
    if (!mashBill) return { corn: 0, rye: 0, wheat: 0, barley: 0, other: 0 };

    const m = mashBill.toLowerCase().trim();

    // Structured: "75% Corn, 13% Rye, 12% Barley"

    const structured = m.match(/(\d+)%\s*(corn|rye|wheat|barley|other|malt)/gi);
    if (structured && structured.length >= 2) {

      const result = { corn: 0, rye: 0, wheat: 0, barley: 0, other: 0 };
      structured.forEach(s => {

        const [, pct, grain] = s.match(/(\d+)%\s*(\w+)/i) || [];
        if (pct && grain) {

          const g = grain.toLowerCase();
          if (g === "corn") result.corn = parseInt(pct);
          else if (g === "rye") result.rye = parseInt(pct);
          else if (g === "wheat") result.wheat = parseInt(pct);
          else if (g === "barley" || g === "malt") result.barley = parseInt(pct);
          else result.other = parseInt(pct);

        }

      });

      return result;

    }

    // Slash format: "75/13/12" or "70/16/14 (wheat)"

    const slash = m.match(/^(\d+)\/(\d+)\/(\d+)/);
    if (slash) {

      const [, a, b, c] = slash.map(Number);

      const isWheatMash = m.includes("wheat");
      return { corn: a, rye: isWheatMash ? 0 : b, wheat: isWheatMash ? b : 0, barley: c, other: 0 };

    }

    // Slash with 4 parts: "70/10/10/10"

    const slash4 = m.match(/^(\d+)\/(\d+)\/(\d+)\/(\d+)/);
    if (slash4) {

      const [, a, b, c, d] = slash4.map(Number);
      return { corn: a, rye: b, wheat: 0, barley: c, other: d };

    }

    // Text hints: "Wheated", "#2 (high rye)", etc.
    if (m.includes("wheat")) return { corn: 0, rye: 0, wheat: 20, barley: 0, other: 0 };
    if (m.includes("high rye") || m.includes("rye")) return { corn: 0, rye: 20, wheat: 0, barley: 0, other: 0 };
    return { corn: 0, rye: 0, wheat: 0, barley: 0, other: 0 };
  }, []);

  const isHighRye = useCallback((b) => b ? parseMashBillPct(b.mashBill).rye >= 15 : false, [parseMashBillPct]);

  const isWheated = useCallback((b) => b ? parseMashBillPct(b.mashBill).wheat >= 16 : false, [parseMashBillPct]);

  const isTraditional = useCallback((b) => b ? !isHighRye(b) && !isWheated(b) : false, [isHighRye, isWheated]);

  // Memoized Set for O(1) "has user tried this bourbon" checks

  const checkedInBourbonIds = useMemo(() => {
    const ids = new Set(checkins.map(c => c.bourbonId));
    for (const [dbId, seedId] of bourbonIdRemapRef.current) {
      if (ids.has(dbId)) ids.add(seedId);
      if (ids.has(seedId)) ids.add(dbId);
    }
    return ids;
  }, [checkins]);

  // Admin pending count — only computed for admins, used in footer badge
  const adminPendingCount = useMemo(() => {
    if (currentUser?.role !== "admin") return 0;
    let count = 0;
    for (const b of bourbons) { if (getSubmissionStatus(b) === "pending") count++; }
    for (const d of brands) { if (getSubmissionStatus(d) === "pending") count++; }
    return count;
  }, [bourbons, brands, currentUser?.role]);

  // ─── ADMIN CONTEXT VALUE (Phase 1 — all deps are useCallback or module-scope) ───
  const adminContextValue = useMemo(() => ({
    // Lookup functions (all useCallback)
    getBourbon,
    getBrand,
    getFacility,
    getCheckins,
    getSubmissionStatus,
    isBrowsableBrand,
    isBrowsableBourbon,
    // Utilities (all useCallback or module-scope)
    db,
    showToast,
    appendAudit,
    // Module-scope functions (stable by definition)
    sanitizeInput,
    normalizeColaName,
    useSupabase,
    getSupabase,
    generateId,
  }), [getBourbon, getBrand, getFacility, getCheckins, getSubmissionStatus, isBrowsableBrand, isBrowsableBourbon, showToast, appendAudit]);

  // ─── A1: RECOMMENDATION ENGINE ───────────────────────

  const recoBuffer = useMemo(() => {

    const hasEnough = checkins.length >= RECO_COLD_START_THRESHOLD;

    // ── Build user palate profile ──

    const userProfile = (() => {
      if (!hasEnough) return null;

      const typeCounts = {};

      const mashCounts = { wheated: 0, highRye: 0, traditional: 0 };

      const topProofs = [];

      const wheelCounts = {};
      for (const c of checkins) {

        const b = getBourbon(c.bourbonId);
        if (!b) continue;
        typeCounts[b.type] = (typeCounts[b.type] || 0) + 1;
        if (isWheated(b)) mashCounts.wheated++;
        else if (isHighRye(b)) mashCounts.highRye++;
        else mashCounts.traditional++;
        if (c.rating >= 4.0 && b.proof) topProofs.push(b.proof);
        if (c.tastingNotes) {
          for (const note of c.tastingNotes) {

            const cat = NOTE_TO_WHEEL_CATEGORY[note.toLowerCase()];
            if (cat) wheelCounts[cat] = (wheelCounts[cat] || 0) + 1;

          }

        }

      }

      const topTypes = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([t]) => t);

      const mashEntries = Object.entries(mashCounts).sort((a, b) => b[1] - a[1]);

      const topMash = mashEntries[0][1] > 0 ? mashEntries[0][0] : "traditional";

      const avgTopProof = topProofs.length > 0 ? topProofs.reduce((a, b) => a + b, 0) / topProofs.length : 95;

      const proofBracket = PROOF_BRACKETS.find(pb => avgTopProof >= pb.min && avgTopProof <= pb.max) || PROOF_BRACKETS[0];

      const topWheel = Object.entries(wheelCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([cat]) => cat);
      return { topTypes, topMash, proofBracket, avgTopProof, topWheel };
    })();

    // ── Candidate pool ──

    const candidates = bourbons.filter(b =>
      !checkedInBourbonIds.has(b.id) && !dismissedRecos.has(b.id) &&
      !wishlist.includes(b.id) && getSubmissionStatus(b) === "approved" &&
      !b.discontinued && (!b.type || BOURBON_SUBTYPES.has(b.type))
    );

    // ── Cold start: community picks ──
    if (!hasEnough) {

      const starterScored = candidates.map(b => {

        const cr = communityRatingsMap.get(b.id);

        const rating = cr ? cr.avg : 0;

        const pop = cr ? Math.min(cr.count / 5, 1) : 0;
        return { bourbon: b, score: rating * 0.6 + pop * 0.4 };
      }).filter(s => s.score > 0).sort((a, b) => b.score - a.score);

      const buffer = starterScored.slice(0, 10).map(s => ({
        bourbon: s.bourbon,
        why: s.bourbon.proof ? `Popular with the community \u00B7 ${s.bourbon.proof}\u00B0` : "Popular with the BarrelRank community",
        isStarter: true,
      }));

      return { buffer, remaining: RECO_COLD_START_THRESHOLD - checkins.length, isStarter: true };

    }

    // ── Personalized scoring ──

    const profile = userProfile;

    const scored = candidates.map(b => {
      let score = 0;

      const signals = {};

      // Style match (30%)

      const typeIdx = profile.topTypes.indexOf(b.type);

      const styleS = typeIdx === 0 ? 1.0 : typeIdx === 1 ? 0.7 : typeIdx === 2 ? 0.4 : 0.1;
      signals.style = 0.30 * styleS; score += signals.style;

      // Mash bill match (20%)

      const bMash = isWheated(b) ? "wheated" : isHighRye(b) ? "highRye" : "traditional";

      const mashS = bMash === profile.topMash ? 1.0 : 0.3;
      signals.mash = 0.20 * mashS; score += signals.mash;

      // Proof bracket (15%)

      const bBracket = PROOF_BRACKETS.find(pb => b.proof >= pb.min && b.proof <= pb.max) || PROOF_BRACKETS[0];

      const bracketDist = Math.abs(PROOF_BRACKETS.indexOf(bBracket) - PROOF_BRACKETS.indexOf(profile.proofBracket));

      const proofS = bracketDist === 0 ? 1.0 : bracketDist === 1 ? 0.5 : 0.1;
      signals.proof = 0.15 * proofS; score += signals.proof;

      // Tasting note overlap (15%)

      const noteWords = (b.notes || "").toLowerCase().split(/[\s,;.]+/);

      const matchedCats = new Set();
      noteWords.forEach(w => { const cat = NOTE_TO_WHEEL_CATEGORY[w]; if (cat && profile.topWheel.includes(cat)) matchedCats.add(cat); });

      const noteS = profile.topWheel.length > 0 ? matchedCats.size / profile.topWheel.length : 0.5;
      signals.notes = 0.15 * noteS; score += signals.notes;

      // Community rating (10%)

      const cr = communityRatingsMap.get(b.id);

      const cAvg = cr ? cr.avg : 0;

      const ratingN = cAvg > 0 ? Math.max(0, Math.min(1, (cAvg - 3.0) / 2.0)) : 0.5;
      signals.rating = 0.10 * ratingN; score += signals.rating;

      // Community popularity (10%)

      const popN = cr ? Math.min(cr.count / 10, 1.0) : 0;
      signals.pop = 0.10 * popN; score += signals.pop;
      return { bourbon: b, score, signals, bMash, bBracket, matchedCats: [...matchedCats], cAvg, popCount: cr?.count || 0 };
    }).sort((a, b) => b.score - a.score);

    // ── Generate "why" text ──

    const generateWhy = (item) => {

      const { bourbon: b, signals: s, bMash, bBracket, matchedCats, cAvg, popCount } = item;

      const mashLabel = bMash === "wheated" ? "wheated" : bMash === "highRye" ? "high-rye" : "traditional";

      const contribs = [
        { k: "style", v: s.style }, { k: "mash", v: s.mash }, { k: "proof", v: s.proof },
        { k: "notes", v: s.notes }, { k: "rating", v: s.rating }, { k: "pop", v: s.pop },
      ].sort((a, b) => b.v - a.v);

      const top = contribs[0].k;
      if (top === "mash" && bMash === profile.topMash)
        return `A ${mashLabel} bourbon \u2014 the style you rate highest`;
      if (top === "style")
        return `Right in your wheelhouse \u2014 ${b.type ? (b.type.split(", ")[1] || b.type) : mashLabel} at ${b.proof}\u00B0`;
      if (top === "proof")
        return `${bBracket.label} (${b.proof}\u00B0), your preferred proof range`;
      if (top === "notes" && matchedCats.length > 0)
        return `Notes of ${matchedCats.slice(0, 2).join(" & ").toLowerCase()} \u2014 flavors you reach for`;
      if (top === "rating" && cAvg >= 4.0)
        return `Rated ${cAvg.toFixed(1)}\u2605 by the community \u2014 matches your ${mashLabel} preference`;
      if (top === "pop" && popCount >= 3)
        return `Trending with ${popCount} check-ins \u2014 a ${mashLabel} you haven\u2019t tried`;
      return `Matches your style \u2014 ${b.type ? (b.type.split(", ")[1] || b.type) : mashLabel} at ${b.proof}\u00B0`;
    };

    // ── Diversity constraint + buffer ──
    if (scored.length === 0) return { buffer: [], remaining: 0, isStarter: false };

    const buffer = [];
    for (let i = 0; i < scored.length && buffer.length < 10; i++) {

      const cand = scored[i];
      if (buffer.length === 1) {

        const first = buffer[0];

        const diffBrand = cand.bourbon.brandId !== first.bourbon.brandId;

        const diffMash = cand.bMash !== scored[0].bMash;

        const diffType = cand.bourbon.type !== first.bourbon.type;
        if (!diffBrand && !diffMash && !diffType) continue;

      }

      buffer.push({ bourbon: cand.bourbon, why: generateWhy(cand), isStarter: false });

    }

    return { buffer, remaining: 0, isStarter: false };
  }, [checkins, bourbons, communityCheckins, communityRatingsMap, wishlist,
      dismissedRecos, checkedInBourbonIds,
      getBourbon, getBrand, getSubmissionStatus, isWheated, isHighRye]);

  const recommendations = useMemo(() => {
    const { buffer, remaining, isStarter } = recoBuffer;
    const visible = buffer.slice(recoRotationIdx, recoRotationIdx + 1);
    return { visible, buffer, remaining, isStarter };
  }, [recoBuffer, recoRotationIdx]);

  const recommendedIds = useMemo(() => new Set(recommendations.buffer.map(r => r.bourbon.id)), [recommendations.buffer]);

  // ─── BADGE SYSTEM (computed, iterative levels) ─────
  // Single-pass aggregation over checkins for badge computations
  const badgeAgg = useMemo(() => {
    const a = { total: checkins.length, kyDist: new Set(), barrelProofs: 0, wheated: new Set(), highRye: new Set(), hazmat: new Set(), singleBarrel: new Set(), states: new Set(), kbt: new Set(), ubt: new Set(), comments: 0, pilgrim: 0, bourbonDayYears: new Set() };
    const brandNamesLower = brands.map(d => d.name.toLowerCase());
    for (const c of checkins) {
      const b = getBourbon(c.bourbonId);
      const d = b ? getBrand(b.brandId) : null;
      if (b && b.proof >= 100) a.barrelProofs++;
      if (b && b.proof >= 140) a.hazmat.add(b.id);
      if (b && isWheated(b)) a.wheated.add(b.id);
      if (b && isHighRye(b)) a.highRye.add(b.id);
      if (b?.type?.toLowerCase().includes("single barrel")) a.singleBarrel.add(b.id);
      if (d?.location?.includes("KY")) a.kyDist.add(d.id);
      const loc = d?.location || ""; const state = loc.split(",").pop()?.trim(); if (state) a.states.add(state);
      if (d?.kbt && b) a.kbt.add(b.id);
      if (d?.ubt && b) a.ubt.add(b.id);
      if (c.comment?.trim().length > 0) a.comments++;
      const cLoc = (c.location || "").toLowerCase();
      if (cLoc && (cLoc.includes("distillery") || cLoc.includes("tasting room") || brandNamesLower.some(n => cLoc.includes(n)))) a.pilgrim++;
      if (c.date?.slice(5) === "06-14") a.bourbonDayYears.add(c.date.slice(0, 4));
    }
    return a;
  }, [checkins, getBourbon, getBrand, isWheated, isHighRye, brands]);

  const computedBadges = useMemo(() => {
    // Build value map from live state — BADGE_DEFS references keys into this
    const sizeOf = (v) => v instanceof Set ? v.size : (typeof v === "number" ? v : 0);
    const valMap = {
      total: badgeAgg.total, kyDist: badgeAgg.kyDist.size, barrelProofs: badgeAgg.barrelProofs,
      wheated: badgeAgg.wheated.size, highRye: badgeAgg.highRye.size, hazmat: badgeAgg.hazmat.size,
      singleBarrel: badgeAgg.singleBarrel.size, states: badgeAgg.states.size, comments: badgeAgg.comments,
      pilgrim: badgeAgg.pilgrim, bourbonDayYears: badgeAgg.bourbonDayYears.size, kbt: badgeAgg.kbt.size, ubt: badgeAgg.ubt.size,
      _myBar: myBar.length, _friends: friends.length, _referrals: referrals.length,
      _commercialReferrals: referrals.filter(r => r.type === "commercial").length,
      _earlyAdopter: (currentUser?.signup_order != null && currentUser.signup_order <= 1000) ? 1 : 0,
      _contributions: bourbons.filter(b => b.addedBy === currentUser?.uid && b.userAdded).length,
    };
    return BADGE_DEFS.map(def => {
    const current = def.key === "first-pour" ? Math.min(valMap[def.val] || 0, 1) : (valMap[def.val] || 0);
    const defWithComputed = { ...def, current };
    return defWithComputed;
  }).map(def => {

    const current = def.current;

    if (def._progressive && def._tiers) {
      const tiers = def._tiers;
      let earnedTier = null, nextTier = tiers[0];
      for (let i = tiers.length - 1; i >= 0; i--) { if (current >= tiers[i].at) { earnedTier = tiers[i]; nextTier = tiers[i + 1] || null; break; } }
      const earned = current >= tiers[0].at;
      const progress = nextTier ? (current - (earnedTier?.at || 0)) / (nextTier.at - (earnedTier?.at || 0)) : 1;
      return { ...def, current, level: earnedTier ? tiers.indexOf(earnedTier) + 1 : 0, earned,
        icon: earnedTier?.icon || def.icon, name: earnedTier?.name || def.name,
        nextThreshold: nextTier?.at || tiers[tiers.length - 1].at, progress: Math.min(Math.max(progress, 0), 1),
        levelLabel: "", descFull: !earned ? "Submit your first edit" : nextTier ? `${current} of ${nextTier.at} for ${nextTier.name}` : `${current} submissions — max tier reached` };
    }

    const maxLevel = def.max || Infinity;

    const level = Math.min(Math.floor(current / def.step), maxLevel === 1 ? 1 : 99);

    const nextThreshold = def.max === 1 ? 1 : (level + 1) * def.step;

    const currentThreshold = level * def.step;

    const earned = current >= def.step || (def.max === 1 && current >= 1);

    const progress = def.max === 1
      ? (current >= 1 ? 1 : current)
      : (current - currentThreshold) / (nextThreshold - currentThreshold);
    return { ...def, current, level, nextThreshold, earned, progress: Math.min(Math.max(progress, 0), 1),
      levelLabel: def.max === 1 ? "" : level > 0 ? `Lvl ${level}` : "",
      descFull: def.max === 1 ? def.desc : `${current}/${nextThreshold} ${def.unit}` };
  }); }, [badgeAgg, myBar, friends, referrals, currentUser, bourbons]);

  // Memoized palate XP (used in profile subtitle + palate card)

  const palateData = useMemo(() => {

    const uniqueBourbons = [...new Set(checkins.map(c => c.bourbonId))].length;

    const repeatPours = Math.max(0, checkins.length - uniqueBourbons);

    const uniqDistilleries = [...new Set(checkins.map(c => getBourbon(c.bourbonId)?.brandId).filter(Boolean))].length;

    const commentCount = checkins.filter(c => c.comment && c.comment.trim().length > 0).length;

    const badgeLevelSum = computedBadges.reduce((s, b) => s + b.level, 0);

    const notesCount = checkins.filter(c => c.tastingNotes && c.tastingNotes.length > 0).length;

    const referralCount = referrals.length;

    const commercialReferrals = referrals.filter(r => r.type === "commercial").length;

    const totalXP = (uniqueBourbons * 10) + (repeatPours * 3) + (uniqDistilleries * 8) + (friends.length * 5) + (badgeLevelSum * 15) + (commentCount * 2) + (notesCount * 2) + (referralCount * 20) + (commercialReferrals * 10);

    const levelThresholds = [0, 30, 80, 160, 280, 450, 680, 1000, 1400, 1900, 2500, 3200, 4100, 5200, 6500, 8000, 10000, 12500, 15500, 19000, 23000];
    let palateLevel = 0;
    for (let li = levelThresholds.length - 1; li >= 0; li--) { if (totalXP >= levelThresholds[li]) { palateLevel = li; break; } }

    const currentThresh = levelThresholds[palateLevel] || 0;

    const nextThresh = levelThresholds[palateLevel + 1] || levelThresholds[palateLevel] + 3000;

    const levelProgress = (totalXP - currentThresh) / (nextThresh - currentThresh);

    const titles = ["Newcomer", "Curious Sipper", "Taster", "Enthusiast", "Connoisseur", "Aficionado", "Barrel Chaser", "Master Taster", "Bourbon Scholar", "Oak Sage", "Rickhouse Legend", "Bourbon Patriarch", "Distiller's Circle", "Cooper Elite", "Master Blender", "Living Proof", "Barrel Vault", "Golden Rickhouse", "Proof Eternal", "Spirit Incarnate", "The Last Drop"];

    const title = titles[Math.min(palateLevel, titles.length - 1)];

    const xpBreakdown = [
      { key: "unique", label: "Unique Bourbons", val: uniqueBourbons, xp: uniqueBourbons * 10, icon: "🥃" },
      { key: "pours", label: "Repeat Pours", val: repeatPours, xp: repeatPours * 3, icon: "📋" },
      { key: "brands", label: "Brands", val: uniqDistilleries, xp: uniqDistilleries * 8, icon: "🏭" },
      { key: "badges", label: "Badge Levels", val: badgeLevelSum, xp: badgeLevelSum * 15, icon: "🏅" },
      { key: "friends", label: "Friends", val: friends.length, xp: friends.length * 5, icon: "🤝" },
      { key: "notes", label: "Tasting Notes", val: notesCount + commentCount, xp: (commentCount * 2) + (notesCount * 2), icon: "📝" },
      { key: "referrals", label: "Referrals", val: referralCount, xp: (referralCount * 20) + (commercialReferrals * 10), icon: "📨" },
    ];
    return { totalXP, palateLevel, title, levelProgress, currentThresh, nextThresh, xpBreakdown };
  }, [checkins, friends, referrals, computedBadges, getBourbon]);

  // Memoized myBar derived data

  const barDerived = useMemo(() => {

    const active = myBar.filter(item => !item.emptyReason);

    const archived = myBar.filter(item => !!item.emptyReason);

    const totalValue = active.reduce((sum, item) => sum + (getBourbon(item.bourbonId)?.msrp || 0), 0);

    const totalPaid = active.reduce((sum, item) => sum + item.pricePaid, 0);
    const uniqueExpressions = new Set(active.map(item => item.bourbonId)).size;
    const uniqueBrands = new Set(active.map(item => getBourbon(item.bourbonId)?.brandId).filter(Boolean)).size;
    return { active, archived, totalValue, totalPaid, uniqueExpressions, uniqueBrands };
  }, [myBar, getBourbon]);

  const navigateTo = useCallback((v, sub = null) => { setPrevView(viewRef.current); setView(v); setSubView(sub); viewRef.current = v; window.scrollTo(0, 0); if (v !== "bourbon-detail") setPendingScanUpc(null); }, []);

  const loadArchiveExpressions = useCallback(() => {
    if (!fullCatalogLoaded && useSupabase()) {
      setFullCatalogLoaded(true);
      db.loadBourbons(true).then(all => {
        if (all) setBourbons(prev => {
          const existing = new Set(prev.map(b => b.id));
          const newItems = all.filter(b => !existing.has(b.id));
          return newItems.length > 0 ? [...prev, ...newItems] : prev;
        });
      });
    }
  }, [fullCatalogLoaded]);

  // ─── PULL-TO-REFRESH ───
  useEffect(() => {
    const el = document.getElementById("root");
    const ind = pullIndicatorRef.current;
    if (!el || !ind) return;
    const ptr = createPullToRefresh(() => { try { sessionStorage.setItem("br_ptr_view", viewRef.current); } catch {} window.location.reload(); });
    pullRefreshRef.current = ptr;
    ptr.bind(el, ind);
    return () => ptr.unbind(el);
  }, []);

  // ─── DEEP LINK: Write hash on navigation (replaceState only — no history entries) ───

  useEffect(() => {
    let newHash = "";
    if (view === "bourbon-detail" && selectedBourbon) {
      newHash = `#/bourbon/${selectedBourbon.id}`;
    } else if (view === "distillery-detail" && selectedBrand) {
      newHash = `#/brand/${selectedBrand.id}`;
    } else if (view === "facility-detail" && selectedFacility) {
      newHash = `#/facility/${selectedFacility.permitNumber}`;
    } else if (view === "friend-profile" && selectedFriend) {
      newHash = `#/profile/${selectedFriend.id}`;
    }

    if (newHash) {
      window.history.replaceState(null, "", window.location.pathname + newHash);
    } else if (window.location.hash && !window.location.hash.includes("access_token") && !window.location.hash.includes("error_description") && !window.location.hash.includes("refresh_token")) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [view, selectedBourbon, selectedBrand, selectedFacility, selectedFriend]);

  // ─── DEEP LINK: Parse hash on app load ───

  const deepLinkProcessedRef = useRef(false);

  useEffect(() => {
    if (deepLinkProcessedRef.current) return;
    if (!bourbons.length || !brands.length) return; // wait for data

    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    deepLinkProcessedRef.current = true;

    const parts = hash.split("/").filter(Boolean);
    if (parts[0] === "bourbon" && parts[1]) {

      const b = bourbons.find(x => x.id === parts[1]);

      if (b) { setSelectedBourbon(b); setView("bourbon-detail"); }
    } else if (parts[0] === "brand" && parts[1]) {

      const d = brands.find(x => x.id === parts[1]);

      if (d) { setSelectedBrand(d); setView("distillery-detail"); }
    } else if (parts[0] === "facility" && parts[1]) {

      const f = facilities.find(x => x.permitNumber === parts[1]);

      if (f) { setSelectedFacility(f); setView("facility-detail"); }
    } else if (parts[0] === "profile" && parts[1]) {

      const u = communityUsers.find(x => x.id === parts[1]);

      if (u) { setSelectedFriend(u); setFriendProfileTab("pours"); setView("friend-profile"); }
    } else if (parts[0] === "checkin" && parts[1]) {

      // Checkin share link — resolve bourbon and navigate to its detail

      const ci = [...checkins, ...(communityCheckins || [])].find(x => x.id === parts[1]);

      if (ci) { const b = bourbons.find(x => x.id === ci.bourbonId); if (b) { setSelectedBourbon(b); setView("bourbon-detail"); } }
    } else if (parts[0] === "bar" && parts[1]) {

      // Bar share link — navigate to friend profile if we can resolve the user

      const u = communityUsers.find(x => x.id === decodeURIComponent(parts[1]));
      if (u) { setSelectedFriend(u); setFriendProfileTab("bar"); setView("friend-profile"); }
    } else if (parts[0] === "menu" && parts[1]) {

      // Menu share link — resolve commercial user by slug

      const slug = decodeURIComponent(parts[1]);

      const u = communityUsers.find(x => x.name && x.name.toLowerCase().replace(/\s+/g, "-") === slug);
      if (u) { setSelectedFriend(u); setFriendProfileTab("bar"); setView("friend-profile"); }

    }

  }, [bourbons, brands, communityUsers, checkins, communityCheckins]);

  // fmtRating, fmtDate, stripUSA, snapToQuarter moved to module scope


  // NosePalateFinish, StatusBadge, SubmissionBadgeInline moved to module scope

  // UPC_DB is at module scope




  // ─── SEARCH / BROWSE ─────────────────

  const filteredBourbons = useMemo(() => {

    const q = normalizeSearch(searchQuery.toLowerCase());
    return bourbons.filter(b => {
    if (!canSeeSubmission(b)) return false;
    if (!isBrowsableBourbon(b, !!q)) return false;
    // Tier filter: hide archive unless searching or "All Expressions" is on
    if (!q && !browseShowAll && b.tier === "archive") return false;
    // Hide child expressions (batch variants) unless "All Expressions" is on
    if (!browseShowAll && b.parentExpressionId) return false;

    const idx = bourbonSearchIndex.get(b.id);

    const matchesSearch = !q || (idx && (idx.name.includes(q) || idx.batch.includes(q) || idx.brandName.includes(q) || idx.type.includes(q) || idx.aliases.some(a => a.includes(q))));

    const matchesPrice = b.msrp == null || (b.msrp >= browsePrice[0] && (browsePrice[1] >= 200 || b.msrp <= browsePrice[1]));

    const matchesProof = b.proof == null || (b.proof >= browseProof[0] && b.proof <= browseProof[1]);

    const base = matchesSearch && matchesPrice && matchesProof;
    if (activeFilters.size === 0) return base;
    return base && [...activeFilters].some(f => {
      if (f === "discontinued-flag") return b.discontinued;
      if (f === "high-rye") return isHighRye(b);
      if (f === "limited-flag") return b.limitedRelease;
      if (f === "rye") { const t = bourbonSearchIndex.get(b.id); return t && t.type.startsWith("rye"); }
      if (f === "traditional") return isTraditional(b);
      if (f === "wheated") return isWheated(b);
      if (f === "wishlist") return wishlist.includes(b.id);
      if (f === "tried") return checkedInBourbonIds.has(b.id);
      if (f === "highly-rated") return parseFloat(getAvgRating(b.id) || 0) >= 4.5;
      if (f === "recommended") return recommendedIds.has(b.id);
      if (f === "kbt") return getBrand(b.brandId)?.kbt === true;
      if (f === "ubt") return getBrand(b.brandId)?.ubt === true;
      if (f.startsWith("state-")) { const st = f.slice(6).toUpperCase(); const br = getBrand(b.brandId); const fac = facilityMap?.get(br?.facilityId); if (!fac) return false; if (fac.state && fac.state.toUpperCase() === st) return true; const loc = fac.location || ""; if (loc.split(" ")[0]?.toUpperCase() === st) return true; const m = loc.match(/,\s*([A-Z]{2})\b/i); return m ? m[1].toUpperCase() === st : false; }
      return false;

    });

  }); }, [bourbons, searchQuery, browsePrice, browseProof, activeFilters, bourbonSearchIndex,
    canSeeSubmission, isBrowsableBourbon, browseShowAll, isHighRye, isTraditional, isWheated,
    wishlist, checkedInBourbonIds, getAvgRating, recommendedIds, getBrand, facilityMap]);


  // Sorted browse results — memoized separately from filtering
  const sortedBourbons = useMemo(() => {
    const q = normalizeSearch(searchQuery.toLowerCase());
    const tierBoost = { curated: 50, core: 30, visible: 10, archive: 0 };
    const searchScore = q ? (b) => {
      const idx = bourbonSearchIndex.get(b.id);
      const nm = idx ? idx.name : "";
      const bn = idx ? idx.brandName : "";
      const al = idx ? idx.aliases : [];
      let s = 0;
      if (nm === q) s += 1000;
      else if (al.some(a => a === q)) s += 800;
      else if (nm.startsWith(q)) s += 500;
      else if (al.some(a => a.startsWith(q))) s += 400;
      else if (nm.includes(q)) s += 100;
      if (bn.includes(q)) s += 50;
      s += (tierBoost[b.tier] || 0);
      s += Math.min(50, (b.popularityScore || 0) / 10);
      return s;
    } : null;
    return [...filteredBourbons].sort((a, z) => {
      if (searchScore) return searchScore(z) - searchScore(a);
      let cmp = 0;
      if (browseSort.key === "popular") { cmp = (a.popularityScore || 0) - (z.popularityScore || 0); if (cmp === 0) return (a.name || "").localeCompare(z.name || "", undefined, { sensitivity: "base" }); }
      else if (browseSort.key === "name") cmp = a.name.localeCompare(z.name, undefined, { sensitivity: "base" });
      else if (browseSort.key === "proof") cmp = (a.proof || 0) - (z.proof || 0);
      else if (browseSort.key === "rating") cmp = (parseFloat(getAvgRating(a.id)) || 0) - (parseFloat(getAvgRating(z.id)) || 0);
      return browseSort.dir === "desc" ? -cmp : cmp;
    });
  }, [filteredBourbons, searchQuery, browseSort, bourbonSearchIndex, getAvgRating]);


  // Archive search fallback — queries server when browse yields no results
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 3 || filteredBourbons.length > 0) {
      setArchiveSearchResults([]);
      setArchiveSearching(false);
      if (archiveSearchTimer.current) clearTimeout(archiveSearchTimer.current);
      return;
    }
    setArchiveSearching(true);
    if (archiveSearchTimer.current) clearTimeout(archiveSearchTimer.current);
    archiveSearchTimer.current = setTimeout(async () => {
      try {
        const sb = getSupabase();
        if (!sb) { setArchiveSearching(false); return; }
        const { data } = await sb.from("bourbons").select("id,name,brand_id,proof,age,msrp,type,tier,popularity_score,expression_type,parent_expression_id,facility_id,finish_type,batch,mash_corn,mash_wheat,mash_rye,mash_barley,mash_other,proof_min,proof_max,submission_status").or(`name.ilike.%${sanitizeQueryParam(q)}%`).eq("submission_status", "approved").order("popularity_score", { ascending: false }).limit(8);
        if (data && data.length > 0) {
          const existing = new Set(bourbonsRef.current.map(b => b.id));
          const mapped = data.filter(r => !existing.has(r.id)).map(r => ({
            id: r.id, name: r.name, brandId: r.brand_id, proof: r.proof ? parseFloat(r.proof) : null,
            age: r.age || "", msrp: r.msrp ? parseFloat(r.msrp) : null, type: r.type || "",
            tier: r.tier || "archive", popularityScore: r.popularity_score || 0,
            expressionType: r.expression_type || null, parentExpressionId: r.parent_expression_id || null,
            facilityId: r.facility_id || null, finishType: r.finish_type || "", batch: r.batch || "",
            mashCorn: r.mash_corn ?? null, mashWheat: r.mash_wheat ?? null, mashRye: r.mash_rye ?? null,
            mashBarley: r.mash_barley ?? null, mashOther: r.mash_other ?? null,
            proofMin: r.proof_min ? parseFloat(r.proof_min) : null, proofMax: r.proof_max ? parseFloat(r.proof_max) : null,
            submissionStatus: r.submission_status || "approved",
          }));
          setArchiveSearchResults(mapped);
        } else {
          setArchiveSearchResults([]);
        }
      } catch { setArchiveSearchResults([]); }
      setArchiveSearching(false);
    }, 300);
    return () => { if (archiveSearchTimer.current) clearTimeout(archiveSearchTimer.current); };
  }, [searchQuery, filteredBourbons.length]);

  // Silent scan event logger
  const logScan = useCallback(async (upc, matched, expressionId, source) => {
    try {
      const sb = getSupabase();
      if (sb && currentUser?.uid) await sb.from("scan_log").insert({ upc, matched, expression_id: expressionId || null, source, user_id: currentUser.uid });
    } catch { /* silent — never break scan flow */ }
  }, [currentUser?.uid]);

  // Memoized community feed — avoids re-filtering on every render
  const communityFeedItems = useMemo(() => {
    if (!communityCheckins) return [];
    let feedItems = communityCheckins.filter(cc => {
      const exprItem = getBourbon(cc.bourbonId);
      if (exprItem && getSubmissionStatus(exprItem) !== "approved") return false;
      if (communityFeedFilter === "friends") {
        return isFriend(cc.userId) && (cc.visibility === "public" || cc.visibility === "friends");
      }
      if (accountType === "commercial") {
        return (isFriend(cc.userId) && (cc.visibility === "public" || cc.visibility === "friends")) ||
               (cc.visibility === "public");
      }
      return cc.visibility === "public" || (isFriend(cc.userId) && cc.visibility === "friends");
    });
    const feedQ = communityFeedSearch.toLowerCase().trim();
    if (feedQ) {
      feedItems = feedItems.filter(cc => {
        const user = getCommunityUser(cc.userId);
        const b = getBourbon(cc.bourbonId);
        const d = b ? getBrand(b.brandId) : null;
        return (
          (b?.name || "").toLowerCase().includes(feedQ) ||
          (d?.name || "").toLowerCase().includes(feedQ) ||
          (user?.name || "").toLowerCase().includes(feedQ) ||
          (cc.comment || "").toLowerCase().includes(feedQ) ||
          (cc.tastingNotes || []).some(n => n.toLowerCase().includes(feedQ))
        );
      });
    }
    return feedItems;
  }, [communityCheckins, communityFeedFilter, communityFeedSearch, friends, accountType]);

  // ─── RENDER VIEWS ─────────────────────

  const renderContent = () => {
    if (selectedBourbon && view === "bourbon-detail") return <BourbonDetail bourbon={selectedBourbon}
      bourbons={bourbons} selectedBourbon={selectedBourbon} variantFromParent={variantFromParent}
      wishlist={wishlist} myBar={myBar} checkins={checkins} currentUser={currentUser}
      businessRoles={businessRoles} view={view} prevView={prevView} communityCheckins={communityCheckins}
      setBourbons={setBourbons} setSelectedBourbon={setSelectedBourbon} setVariantFromParent={setVariantFromParent}
      setSelectedBrand={setSelectedBrand} setSelectedFacility={setSelectedFacility}
      setShowCheckinModal={setShowCheckinModal} setCheckinBourbon={setCheckinBourbon}
      setWishlist={setWishlist} setMyBar={setMyBar} setBarSharePrompt={setBarSharePrompt}
      setViewCheckinDetail={setViewCheckinDetail} setSuggestEditData={setSuggestEditData}
      setShowSuggestEdit={setShowSuggestEdit} setBusinessRoles={setBusinessRoles}
      getBourbon={getBourbon} getDisplayBrand={getDisplayBrand} getFacility={getFacility}
      isBrowsableBourbon={isBrowsableBourbon} getCheckins={getCheckins} getAvgRating={getAvgRating}
      getCheckinCount={getCheckinCount} isBrandClaimed={isBrandClaimed} getSubmissionStatus={getSubmissionStatus}
      canSeeSubmission={canSeeSubmission} getExpressionStatus={getExpressionStatus}
      getBrandBourbons={getBrandBourbons} showToast={showToast} navigateTo={navigateTo}
      sanitizeText={sanitizeText} sanitizeUrl={sanitizeUrl} copyToClipboard={copyToClipboard}
      formatCount={formatCount} appendAudit={appendAudit} communityRatingsMap={communityRatingsMap}
      getBrand={getBrand} getDisplayName={getDisplayName} db={db}
      pendingScanUpc={pendingScanUpc} setPendingScanUpc={setPendingScanUpc} upcMap={upcMap}
    />;
    if (selectedBrand && view === "distillery-detail") return <BrandDetail brand={selectedBrand}
      businessClaims={businessClaims} businessRoles={businessRoles} currentUser={currentUser}
      platformUsers={platformUsers} selectedBourbon={selectedBourbon} selectedFacility={selectedFacility}
      selectedBrand={selectedBrand} prevView={prevView} view={view} brands={brands}
      isAuthenticated={isAuthenticated}
      setBusinessClaims={setBusinessClaims} setSelectedBrand={setSelectedBrand}
      setSelectedBourbon={setSelectedBourbon} setSelectedFacility={setSelectedFacility}
      setBrands={setBrands} setAddBourbonPreselectedBrand={setAddBourbonPreselectedBrand}
      setShowAddBourbonModal={setShowAddBourbonModal}
      getFacility={getFacility} getBrandBourbons={getBrandBourbons} canSeeSubmission={canSeeSubmission}
      getSubmissionStatus={getSubmissionStatus} getAvgRating={getAvgRating}
      getExpressionStatus={getExpressionStatus} navigateTo={navigateTo} showToast={showToast}
      appendAudit={appendAudit} copyToClipboard={copyToClipboard}
      getDisplayName={getDisplayName} sanitizeUrl={sanitizeUrl} SubmissionBadgeInline={SubmissionBadgeInline} db={db}
    />;
    if (selectedFacility && view === "facility-detail") return <FacilityDetail facility={selectedFacility}
      brandsByFacility={brandsByFacility} isBrowsableBrand={isBrowsableBrand} canSeeSubmission={canSeeSubmission}
      getBrandBourbons={getBrandBourbons} setSelectedFacility={setSelectedFacility} navigateTo={navigateTo}
      getAvgRating={getAvgRating} communityRatingsMap={communityRatingsMap}
      ratingDrillIn={ratingDrillIn} setRatingDrillIn={setRatingDrillIn} getDisplayName={getDisplayName}
      getCheckins={getCheckins} checkins={checkins} communityCheckins={communityCheckins}
      setSelectedBourbon={setSelectedBourbon} setSelectedBrand={setSelectedBrand} brands={brands}
    />;

    // ─── FRIEND PROFILE VIEW ───
    if (view === "friend-profile" && selectedFriend) {

      const fu = selectedFriend;

      const friendCheckins = (communityCheckins || []).filter(cc => cc.userId === fu.id);

      const canSeePours = fu.checkinPrivacy === "public" || (fu.checkinPrivacy === "friends" && isFriend(fu.id));

      const canSeeBar = fu.barPrivacy === "public" || (fu.barPrivacy === "friends" && isFriend(fu.id));

      const visibleCheckins = canSeePours ? friendCheckins.filter(cc => cc.visibility === "public" || (cc.visibility === "friends" && isFriend(fu.id))) : [];

      const friendAvgRating = visibleCheckins.length ? (visibleCheckins.reduce((a, c) => a + c.rating, 0) / visibleCheckins.length).toFixed(2) : "—";

      const friendDistilleries = [...new Set(visibleCheckins.map(c => getBourbon(c.bourbonId)?.brandId).filter(Boolean))].length;

      return (
        <div style={posRelativeStyle}>
          <button onClick={() => { setSelectedFriend(null); navigateTo(prevView); }} style={closeButtonStyle} aria-label="Close">✕</button>
          {/* Profile Header */}
          <div style={{ textAlign: "center", padding: "12px 0 28px" }}>
            <div style={{ width: 80, height: 80, borderRadius: "50%", background: GRAD_PRIMARY, margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.25rem" }}>{fu.avatar}</div>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.625rem", color: "#F5E6D0", margin: "0 0 4px" }}>{fu.name}</h2>
            {fu.realName && fu.realName !== fu.name && (fu.profilePrivacy === "public" || (fu.profilePrivacy === "friends" && isFriend(fu.id))) && (
              <p style={{ color: "#B5A898", fontSize: "0.8125rem", margin: "0 0 4px", fontStyle: "italic" }}>{fu.realName}</p>
            )}
            <p style={{ color: "#B5A898", fontSize: "0.875rem", margin: "0 0 4px" }}>📍 {fu.location}</p>
            {fu.tagline && <p style={{ color: "#B5A898", fontSize: "0.8125rem", margin: 0, fontStyle: "italic" }}>{fu.tagline}</p>}
            {isFriend(fu.id) && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 10, padding: "4px 12px", background: "rgba(212,160,83,0.1)", borderRadius: 20, border: "1px solid rgba(212,160,83,0.2)" }}>
                <span style={font075Style}>👥</span>
                <span style={goldText075BoldStyle}>Friends since {friends.find(f => f.id === fu.id)?.since}</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
              <button onClick={() => setReportModal({ userId: fu.id, userName: fu.name, contentId: fu.id, contentType: "user_profile", contentText: fu.tagline || "" })}
                style={{ padding: "6px 14px", background: "transparent", border: "1px solid #3D3428", borderRadius: 8, color: "#A69880", fontSize: "0.6875rem", cursor: "pointer", transition: "all 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#CD5C5C"; e.currentTarget.style.color = "#CD5C5C"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#3D3428"; e.currentTarget.style.color = "#A69880"; }}>
                ⚑ Report
              </button>
              <button onClick={() => { isBlocked(fu.id) ? unblockUser(fu.id) : blockUser(fu.id); }}
                style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${isBlocked(fu.id) ? "rgba(76,175,80,0.25)" : "#3D3428"}`, borderRadius: 8, color: isBlocked(fu.id) ? "#4CAF50" : "#A69880", fontSize: "0.6875rem", cursor: "pointer", transition: "all 0.15s" }}>
                {isBlocked(fu.id) ? "✓ Unblock" : "🚫 Block"}
              </button>
            </div>
          </div>
          {/* Friend's Shelf Staple */}
          {(() => {
            const stapleId = fu?.shelf_staple_id;
            const stapleNote = fu?.shelf_staple_note;
            const staple = stapleId ? bourbons.find(b => b.id === stapleId) : null;
            if (!staple) return null;
            const stapleBrand = getDisplayBrand(staple.brandId);
            return (
              <div style={{ margin: '0 1rem 1rem', padding: '0.75rem 1rem', background: 'rgba(196, 164, 105, 0.08)', borderLeft: '4px solid #C4A469', borderRadius: '0 12px 12px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.1rem' }}>🥃</span>
                  <span style={{ color: '#C4A469', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Shelf Staple</span>
                </div>
                <div onClick={() => { setSelectedBourbon(staple); navigateTo("bourbon-detail"); }} style={{ cursor: 'pointer' }}>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: '#fff' }}>{staple.name}</div>
                  <div style={{ color: '#aaa', fontSize: '0.85rem', marginTop: '0.15rem' }}>{stapleBrand?.name}
                    {staple.proof && <span style={{ color: '#C4A469', marginLeft: '0.5rem' }}>{staple.proof}°</span>}
                  </div>
                </div>
                {stapleNote && (
                  <div style={{ color: '#ccc', fontSize: '0.85rem', fontStyle: 'italic', marginTop: '0.5rem' }}>"{stapleNote}"</div>
                )}
              </div>
            );
          })()}
          {/* Stats */}
          {canSeePours && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 24 }}>
              {[{ label: "Pours", value: visibleCheckins.length }, { label: "Avg Rating", value: friendAvgRating }, { label: "Brands", value: friendDistilleries }].map(s => (
                <div key={s.label} style={{ background: "#1E1A14", borderRadius: 12, padding: 16, textAlign: "center", border: "1px solid #3D3428" }}>
                  <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#D4A053", fontFamily: FONT_DISPLAY }}>{s.value}</div>
                  <div style={{ fontSize: "0.6875rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1, marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}
          {/* Tabs */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            {["pours", "bar"].map(t => (
              <button key={t} onClick={() => setFriendProfileTab(t)}
                style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "1px solid", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
                  background: friendProfileTab === t ? "#D4A053" : "transparent", color: friendProfileTab === t ? "#1A1510" : "#B5A898", borderColor: friendProfileTab === t ? "#D4A053" : "#5A4F3E" }}>
                {t === "pours" ? "🥃 Recent Pours" : "🗄️ Their Bar"}
              </button>
            ))}
          </div>
          {/* Pours Tab */}
          {friendProfileTab === "pours" && (
            canSeePours ? (
              visibleCheckins.length > 0 ? visibleCheckins.map(cc => {

                const b = getBourbon(cc.bourbonId);

                const d = b ? getBrand(b.brandId) : null;

                const myLatest = getCheckins(cc.bourbonId).reduce((best, c) => !best || (c.date || '') > (best.date || '') ? c : best, null);
                if (!b) return null;

                return (
                  <div key={cc.id} style={{ background: "#1E1A14", borderRadius: 12, padding: 16, marginBottom: 10, border: "1px solid #3D3428" }}>
                    <div style={flexBetweenStartStyle}>
                      <div onClick={() => { setSelectedBourbon(b); navigateTo("bourbon-detail"); }} style={{ cursor: "pointer", flex: 1 }}>
                        <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0" }}>{getDisplayName(b)}</div>
                        <div style={mutedText075Mt2Style}>{d?.name} · {b.proof}°</div>
                        <div style={{ color: "#B5A898", fontSize: "0.6875rem", marginTop: 4 }}>📍 {stripUSA(cc.location)} · {fmtDate(cc.date)}</div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, background: "#150F0A", padding: "5px 10px", borderRadius: 20, border: "1px solid #3D3428" }}>
                          <span style={goldText06875Style}>★</span>
                          <span style={{ color: "#D4A053", fontWeight: 700, fontSize: "0.875rem" }}>{fmtRating(cc.rating)}</span>
                        </div>
                        {myLatest && (
                          <div style={{ display: "flex", alignItems: "center", gap: 3, background: "rgba(212,160,83,0.08)", padding: "3px 8px", borderRadius: 12, border: "1px solid rgba(212,160,83,0.15)" }}>
                            <span style={{ color: "#B5A898", fontSize: "0.5625rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>You</span>
                            <span style={{ color: "#B5A898", fontSize: "0.5625rem" }}>★</span>
                            <span style={{ color: "#A69880", fontWeight: 700, fontSize: "0.6875rem" }}>{fmtRating(myLatest.rating)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {cc.comment && <p style={{ color: "#A69880", fontSize: "0.875rem", margin: "10px 0 0", lineHeight: 1.5, fontStyle: "italic" }}>"{sanitizeText(cc.comment)}"</p>}
                    {cc.tastingNotes?.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 10 }}>
                        {cc.tastingNotes.map(n => <span key={n} style={{ padding: "2px 8px", borderRadius: 20, fontSize: "0.6875rem", background: "#3D3428", color: "#B5A898" }}>{n}</span>)}
                      </div>
                    )}
                    <div style={{ marginTop: 10, display: "flex", alignItems: "center" }}>
                      <ToastButton checkinId={cc.id} toasters={toasts[cc.id] || []} myUid={currentUser?.uid} myName={currentUser?.name || username} communityUserMap={communityUserMap} />
                    </div>
                  </div>
                );
              }) : (
                <div style={{ textAlign: "center", padding: "32px 16px", color: "#B5A898" }}>
                  <div style={sFonMar1Style}>🥃</div>
                  <p style={{ margin: 0, fontSize: "0.875rem" }}>{fu.name} hasn't shared any pours yet.</p>
                </div>
              )
            ) : (
              <div style={{ textAlign: "center", padding: "32px 16px" }}>
                <div style={sFonMar1Style}>🔒</div>
                <p style={{ color: "#B5A898", margin: "0 0 4px", fontSize: "0.875rem" }}>{fu.name}'s pours are {fu.checkinPrivacy === "private" ? "private" : "friends only"}.</p>
                {!isFriend(fu.id) && fu.checkinPrivacy === "friends" && (
                  <button onClick={() => { setFriends(prev => [...prev, { id: fu.id, name: fu.name, location: fu.location, avatar: fu.avatar, since: new Date().toISOString().slice(0, 10) }]); }}
                    style={{ marginTop: 10, padding: "8px 16px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.8125rem", fontWeight: 700, cursor: "pointer" }}>
                    + Add Friend to See Pours
                  </button>
                )}
              </div>
            )
          )}
          {/* Bar Tab */}
          {friendProfileTab === "bar" && (
            canSeeBar ? (
              (fu.bar || []).length > 0 ? (
                <div>
                  <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginBottom: 12 }}>{fu.bar.length} bottle{fu.bar.length !== 1 ? "s" : ""} in {fu.name.split(" ")[0]}'s bar</div>
                  {fu.bar.map((item, i) => {

                    const b = getBourbon(item.bourbonId);

                    const d = b ? getBrand(b.brandId) : null;
                    if (!b) return null;

                    const fillColor = item.fill <= 25 ? "#B22222" : "#B5A898";

                    return (
                      <div key={i} onClick={() => { setSelectedBourbon(b); navigateTo("bourbon-detail"); }}
                        style={{ display: "flex", alignItems: "center", gap: 14, padding: 14, background: "#1E1A14", borderRadius: 12, border: "1px solid #3D3428", marginBottom: 8, cursor: "pointer", transition: "border-color 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = "#5A4F3E"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
                        {/* Mini fill indicator */}
                        <div style={{ width: 28, height: 44, background: "#150F0A", borderRadius: 6, border: "1px solid #3D3428", position: "relative", overflow: "hidden", flexShrink: 0 }}>
                          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: `${item.fill}%`, background: item.fill <= 25 ? "rgba(178,34,34,0.4)" : "rgba(212,160,83,0.25)", transition: "height 0.3s" }} />
                        </div>
                        <div style={flexOneStyle}>
                          <div style={{ fontFamily: FONT_DISPLAY, fontSize: "0.9375rem", color: "#F5E6D0" }}>{getDisplayName(b)}</div>
                          <div style={mutedText075Mt2Style}>{d?.name} · {b.proof}°</div>
                        </div>
                        <div style={textRightShrinkStyle}>
                          <div style={{ color: fillColor, fontSize: "0.875rem", fontWeight: 700 }}>{item.fill}%</div>
                          <div style={mutedText06875Mt2Style}>${item.pricePaid}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "32px 16px", color: "#B5A898" }}>
                  <div style={sFonMar1Style}>🗄️</div>
                  <p style={{ margin: 0, fontSize: "0.875rem" }}>{fu.name}'s bar is empty.</p>
                </div>
              )
            ) : (
              <div style={{ textAlign: "center", padding: "32px 16px" }}>
                <div style={sFonMar1Style}>🔒</div>
                <p style={{ color: "#B5A898", margin: "0 0 4px", fontSize: "0.875rem" }}>{fu.name}'s bar is {fu.barPrivacy === "private" ? "private" : "friends only"}.</p>
                {!isFriend(fu.id) && fu.barPrivacy === "friends" && (
                  <button onClick={() => { setFriends(prev => [...prev, { id: fu.id, name: fu.name, location: fu.location, avatar: fu.avatar, since: new Date().toISOString().slice(0, 10) }]); }}
                    style={{ marginTop: 10, padding: "8px 16px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.8125rem", fontWeight: 700, cursor: "pointer" }}>
                    + Add Friend to See Bar
                  </button>
                )}
              </div>
            )
          )}
        </div>
      );

    }

    switch (view) {

      case "home": {
        return (
          <div>
            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 18 }}>
              {[
                { label: "Pours", value: checkins.length, action: () => navigateTo("activity") },
                { label: "Unique", value: [...new Set(checkins.map(c => c.bourbonId))].length, action: () => { setActiveFilters(new Set(["tried"])); navigateTo("browse"); } },
                { label: "Bar", value: myBar.filter(item => !item.emptyReason).length, action: () => navigateTo("mybar") },
                { label: "Friends", value: friends.length, action: () => { navigateTo("profile"); setTimeout(() => { const el = document.getElementById("profile-friends"); if (el) { const y = el.getBoundingClientRect().top + window.scrollY - 80; window.scrollTo({ top: y, behavior: "smooth" }); } }, 150); } },
              ].map(s => (
                <div key={s.label} className="tap-feedback" onClick={s.action}
                  style={{ textAlign: "center", padding: "10px 6px", background: "#1E1A14", borderRadius: 10, border: "1px solid #3D3428", cursor: "pointer", transition: "border-color 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#D4A053"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
                  <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#D4A053", fontFamily: FONT_DISPLAY, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: "0.5625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 0.8, marginTop: 3 }}>{s.label}</div>
                </div>
              ))}
            </div>
            {/* Recent Activity */}
            {checkins.length === 0 ? (
              <div style={{ background: "linear-gradient(135deg, rgba(212,160,83,0.08), rgba(139,94,52,0.08))", borderRadius: 16, padding: 28, marginBottom: 20, border: "1px solid rgba(212,160,83,0.15)", textAlign: "center" }}>
                <div style={sFonMar2Style}>🥃</div>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.25rem", color: "#F5E6D0", margin: "0 0 8px" }}>Welcome to {BRAND.name}!</h3>
                <p style={{ color: "#B5A898", fontSize: "0.875rem", margin: "0 0 20px", lineHeight: 1.6 }}>Start your bourbon journey by checking in your first pour. Browse our catalog of thousands of bourbons and share your tasting experience.</p>
                <button onClick={() => navigateTo("browse")} style={{ padding: "12px 28px", background: GRAD_PRIMARY, border: "none", borderRadius: 10, color: "#1A1409", fontSize: "0.9375rem", fontWeight: 700, cursor: "pointer", marginBottom: 12 }}>Browse Bourbons</button>
                <p style={{ color: "#A69880", fontSize: "0.75rem", margin: 0 }}>Tap any bourbon to check in, rate, and add tasting notes</p>
              </div>
            ) : (<>
            {/* ── LAST POUR (single compact card) ── */}
            {(() => {

              const c = checkins[0];
              if (!c) return null;

              const b = getBourbon(c.bourbonId);

              const d = getBrand(b?.brandId);

              const myCheckinIds = new Set(checkins.map(x => x.id));

              const unseenByCheckin = {};
              Object.entries(toasts).forEach(([cid, toasters]) => {
                if (!myCheckinIds.has(cid)) return;

                const otherToasts = toasters.filter(t => t.uid !== currentUser?.uid);

                const unseen = otherToasts.filter(t => !seenToastKeys.has(`${cid}:${t.uid}`));
                if (unseen.length > 0) { unseenByCheckin[cid] = unseen; }

              });

              const cardUnseen = unseenByCheckin[c.id] || [];

              const getName = (uid) => {

                const u = communityUsers.find(x => x.id === uid);
                return u?.name?.split(" ")[0] || friends.find(x => x.id === uid)?.name?.split(" ")[0] || "Someone";
              };

              return (
                <div style={mb20Style}>
                  <div style={flexBetweenCenterMb10Style}>
                    <div style={flexCenterGap10Style}>
                      <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.375rem", color: "#F5E6D0", margin: 0 }}>Last Pour</h2>
                    </div>
                  </div>
                  <div onClick={() => { navigateTo("activity"); if (cardUnseen.length > 0) markToastsSeen(cardUnseen.map(t => `${c.id}:${t.uid}`)); }}
                    style={{ background: "#1E1A14", borderRadius: 12, padding: 14, border: `1px solid ${cardUnseen.length > 0 ? "rgba(212,160,83,0.3)" : "#3D3428"}`, cursor: "pointer", transition: "border-color 0.2s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "#5A4F3E"} onMouseLeave={e => e.currentTarget.style.borderColor = cardUnseen.length > 0 ? "rgba(212,160,83,0.3)" : "#3D3428"}>
                    <div style={flexBetweenCenterStyle}>
                      <div style={flexOneStyle}>
                        <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0" }}>{getDisplayName(b)}</div>
                        <div style={mutedText075Mt2Style}>
                          {d?.name} {"\u00B7"} {fmtDate(c.date)} {"\u00B7"} {"📍"} {stripUSA(c.location)}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#150F0A", padding: "6px 12px", borderRadius: 20, border: "1px solid #3D3428", flexShrink: 0 }}>
                        <span style={goldText06875Style}>{"\u2605"}</span>
                        <span style={{ color: "#D4A053", fontWeight: 700, fontSize: "0.9375rem" }}>{fmtRating(c.rating)}</span>
                      </div>
                    </div>
                    {cardUnseen.length > 0 && (
                      <div style={{ marginTop: 8, padding: "6px 10px", background: "rgba(212,160,83,0.06)", borderRadius: 8, border: "1px solid rgba(212,160,83,0.15)", display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={font08125Style}>{"🥃"}</span>
                        <span style={{ fontSize: "0.6875rem", color: "#D4A053", fontWeight: 600 }}>
                          {cardUnseen.length === 1 ? `${getName(cardUnseen[0].uid)} toasted this!` : `${getName(cardUnseen[0].uid)} and ${cardUnseen.length - 1} other${cardUnseen.length > 2 ? "s" : ""} toasted this!`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            {/* ── YOUR NEXT POUR? (single recommendation with arrows) ── */}
            {(() => {

              const { visible: rawVisible, buffer, remaining, isStarter } = recommendations;
              const visible = rawVisible.filter(r => !checkedInBourbonIds.has(r.bourbon.id));
              if (visible.length === 0 && !isStarter) return null;

              const canPrev = recoRotationIdx > 0;

              const canNext = recoRotationIdx < buffer.length - 1;

              return (
                <div style={mb24Style}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <div style={flexCenterGap8Style}>
                      <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.375rem", color: "#F5E6D0", margin: 0 }}>Your Next Pour?</h2>
                      <button onClick={(e) => { e.stopPropagation(); setActiveInfoPanel(activeInfoPanel === "recoInfo" ? null : "recoInfo"); }}
                        style={{ width: 20, height: 20, borderRadius: "50%", border: "1px solid " + (activeInfoPanel === "recoInfo" ? "#D4A053" : "#5A4F3E"), background: activeInfoPanel === "recoInfo" ? "rgba(212,160,83,0.15)" : "transparent", color: activeInfoPanel === "recoInfo" ? "#D4A053" : "#A69880", fontSize: "0.6875rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0, transition: "all 0.2s" }}>?</button>
                    </div>
                    {buffer.length > 1 && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <button onClick={() => setRecoRotationIdx(prev => Math.max(0, prev - 1))} disabled={!canPrev}
                          style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", background: canPrev ? "rgba(212,160,83,0.08)" : "transparent", border: `1px solid ${canPrev ? "#5A4F3E" : "#3D3428"}`, borderRadius: 8, color: canPrev ? "#D4A053" : "#5A4F3E", fontSize: "1rem", cursor: canPrev ? "pointer" : "default", transition: "all 0.15s" }}
                          aria-label="Previous recommendation">{"\u2039"}</button>
                        <span style={{ color: "#A69880", fontSize: "0.625rem", minWidth: 32, textAlign: "center" }}>{recoRotationIdx + 1}/{buffer.length}</span>
                        <button onClick={() => setRecoRotationIdx(prev => Math.min(buffer.length - 1, prev + 1))} disabled={!canNext}
                          style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", background: canNext ? "rgba(212,160,83,0.08)" : "transparent", border: `1px solid ${canNext ? "#5A4F3E" : "#3D3428"}`, borderRadius: 8, color: canNext ? "#D4A053" : "#5A4F3E", fontSize: "1rem", cursor: canNext ? "pointer" : "default", transition: "all 0.15s" }}
                          aria-label="Next recommendation">{"\u203A"}</button>
                      </div>
                    )}
                  </div>
                  {activeInfoPanel === "recoInfo" && (
                    <div style={{ background: "rgba(21,15,10,0.8)", borderRadius: 10, padding: 14, marginBottom: 12, border: "1px solid #5A4F3E" }}>
                      <div style={flexBetweenCenterMb10Style}>
                        <span style={{ fontFamily: FONT_DISPLAY, fontSize: "0.875rem", color: "#D4A053", fontWeight: 700 }}>How Recommendations Work</span>
                        <button onClick={() => setShowRecoInfo(false)} style={mutedText10Style} aria-label="Close">✕</button>
                      </div>
                      <p style={{ color: "#D4CFC7", fontSize: "0.75rem", lineHeight: 1.5, margin: "0 0 10px" }}>After {RECO_COLD_START_THRESHOLD} check-ins, we build a palate profile from your tasting history and match you with bourbons you haven't tried.</p>
                      {[
                        { signal: "Style match", pct: "30%", note: "Bourbon types you check in most" },
                        { signal: "Mash bill", pct: "20%", note: "Wheated, high-rye, or traditional" },
                        { signal: "Proof range", pct: "15%", note: "Based on your highest-rated pours" },
                        { signal: "Tasting notes", pct: "15%", note: "Flavor categories from the wheel" },
                        { signal: "Community rating", pct: "10%", note: "Average score from all users" },
                        { signal: "Popularity", pct: "10%", note: "Check-in volume across community" },
                      ].map(r => (
                        <div key={r.signal} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #1A1510" }}>
                          <div>
                            <div style={{ color: "#F5E6D0", fontSize: "0.75rem", fontWeight: 600 }}>{r.signal}</div>
                            <div style={{ color: "#B5A898", fontSize: "0.625rem", marginTop: 1 }}>{r.note}</div>
                          </div>
                          <span style={{ color: "#D4A053", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0 }}>{r.pct}</span>
                        </div>
                      ))}
                      <p style={{ color: "#B5A898", fontSize: "0.625rem", margin: "10px 0 0", lineHeight: 1.4 }}>Before {RECO_COLD_START_THRESHOLD} check-ins, picks are based on community ratings and popularity. Swipe through up to 10 picks or dismiss ones that don't interest you.</p>
                    </div>
                  )}
                  {isStarter && remaining > 0 ? (() => {
                    const done = RECO_COLD_START_THRESHOLD - remaining;
                    const pct = done / RECO_COLD_START_THRESHOLD;
                    const ringR = 28, ringC = 36, strokeW = 5;
                    const circ = 2 * Math.PI * ringR;
                    return (
                      <div>
                        {/* Progress ring + label */}
                        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
                          <svg width={ringC * 2} height={ringC * 2} style={{ flexShrink: 0 }}>
                            <circle cx={ringC} cy={ringC} r={ringR} fill="none" stroke="#3D3428" strokeWidth={strokeW} />
                            <circle cx={ringC} cy={ringC} r={ringR} fill="none" stroke="#D4A053" strokeWidth={strokeW}
                              strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} strokeLinecap="round"
                              transform={`rotate(-90 ${ringC} ${ringC})`} style={{ transition: "stroke-dashoffset 0.4s" }} />
                            <text x={ringC} y={ringC} textAnchor="middle" dominantBaseline="central" fill="#D4A053" fontSize="14" fontWeight="700">{done}/{RECO_COLD_START_THRESHOLD}</text>
                          </svg>
                          <div>
                            <div style={lightText0875BoldStyle}>Unlock Personalized Picks</div>
                            <div style={mutedText06875Mt2Style}>{remaining} more pour{remaining !== 1 ? "s" : ""} to build your palate profile</div>
                          </div>
                        </div>
                        {/* 2x4 starter grid */}
                        <div style={grid2ColGap8Style}>
                          {STARTER_BOURBONS.map((sb, i) => {
                            const sbn = normalizeQuotes(sb.name.toLowerCase());
                            const matched = bourbons.find(bx => { const bn = normalizeQuotes(bx.name.toLowerCase()); return bn === sbn || bn.includes(sbn) || sbn.includes(bn); });
                            const tried = matched && checkedInBourbonIds.has(matched.id);
                            return (
                              <div key={i} onClick={() => { if (matched) { setSelectedBourbon(matched); navigateTo("bourbon-detail"); } else { setSearchQuery(sb.name); navigateTo("browse"); showToast("Search for this bourbon in Browse"); } }}
                                style={{ background: tried ? "rgba(212,160,83,0.08)" : "#1E1A14", borderRadius: 10, padding: "10px 10px 8px", border: `1px solid ${tried ? "rgba(212,160,83,0.3)" : "#3D3428"}`, cursor: "pointer", opacity: tried ? 0.7 : 1, position: "relative", transition: "border-color 0.2s" }}>
                                {tried && <span style={{ position: "absolute", top: 6, right: 8, fontSize: "0.625rem", color: "#D4A053" }}>{"\u2713"}</span>}
                                <div style={{ fontFamily: FONT_DISPLAY, fontSize: "0.8125rem", color: "#F5E6D0", lineHeight: 1.2, marginBottom: 3 }}>{sb.name}</div>
                                <div style={{ color: "#B5A898", fontSize: "0.625rem", lineHeight: 1.3, marginBottom: 4 }}>{sb.profile}</div>
                                <div style={flexBetweenCenterStyle}>
                                  <span style={{ fontSize: "0.5625rem", color: "#D4A053", fontWeight: 600, padding: "1px 6px", background: "rgba(212,160,83,0.1)", borderRadius: 8 }}>{sb.tag}</span>
                                  <span style={{ fontSize: "0.5625rem", color: "#C4B5A0" }}>{sb.proof}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })() : visible.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "20px 16px", color: "#B5A898", background: "#1E1A14", borderRadius: 12, border: "1px solid #3D3428" }}>
                      <p style={sMarFon6Style}>Check in more bourbons to unlock recommendations!</p>
                    </div>
                  ) : (() => {

                    const reco = visible[0];

                    const b = reco.bourbon;

                    const d = getDisplayBrand(b.brandId);

                    const cr = communityRatingsMap.get(b.id);

                    return (
                      <div style={{ background: "#1E1A14", borderRadius: 12, padding: 16, border: "1px solid #3D3428", transition: "border-color 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = "#5A4F3E"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
                        <div onClick={() => { setSelectedBourbon(b); navigateTo("bourbon-detail"); }} style={{ cursor: "pointer" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(212,160,83,0.08)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              {d && <DistilleryIconRender iconKey={d.image} size={28} />}
                            </div>
                            <div style={flexOneStyle}>
                              <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0" }}>{b.name}</div>
                              <div style={mutedText075Mt2Style}>
                                {d?.name}{b.type ? ` \u00B7 ${b.type.split(", ")[1] || b.type}` : ""}{b.proof ? ` \u00B7 ${b.proof}\u00B0` : ""}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 6, padding: "8px 10px", background: "rgba(212,160,83,0.05)", borderRadius: 8, border: "1px solid rgba(212,160,83,0.1)", marginBottom: 8 }}>
                            <span style={{ fontSize: "0.75rem", flexShrink: 0, marginTop: 1 }}>{"💡"}</span>
                            <span style={{ color: "#D4A053", fontSize: "0.75rem", lineHeight: 1.4, fontStyle: "italic" }}>{reco.why}</span>
                          </div>
                          {cr && cr.count > 0 && (
                            <div style={mutedText06875Style}>
                              {"\u2605"} {cr.avg.toFixed(2)} avg {"\u00B7"} {cr.count} check-in{cr.count !== 1 ? "s" : ""}
                              {b.msrp ? ` \u00B7 ~$${b.msrp}` : ""}
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, paddingTop: 10, borderTop: "1px solid #5A4F3E" }}>
                          <button onClick={(e) => { e.stopPropagation(); setDismissedRecos(prev => { const next = new Set(prev); next.add(b.id); return next; }); showToast("Got it \u2014 we\u2019ll skip that one"); }}
                            style={{ padding: "6px 12px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#A69880", fontSize: "0.6875rem", cursor: "pointer", transition: "all 0.15s" }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = "#6B5F4D"; e.currentTarget.style.color = "#D4CFC7"; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = "#5A4F3E"; e.currentTarget.style.color = "#A69880"; }}
                            aria-label="Dismiss recommendation">
                            {"✕"} Not interested
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); const wasIn = wishlist.includes(b.id); if (!wasIn) { setWishlist(prev => [...prev, b.id]); if (useSupabase() && currentUser?.uid) db.addToWishlist(currentUser.uid, b.id); showToast("\u2605 Added to wishlist!"); } }}
                            style={{ padding: "6px 14px", background: wishlist.includes(b.id) ? "rgba(212,160,83,0.1)" : "transparent", border: `1px solid ${wishlist.includes(b.id) ? "#D4A053" : "#5A4F3E"}`, borderRadius: 8, color: wishlist.includes(b.id) ? "#D4A053" : "#A69880", fontSize: "0.6875rem", fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}
                            aria-label={wishlist.includes(b.id) ? "On wishlist" : "Add to wishlist"}>
                            {wishlist.includes(b.id) ? "\u2605 On Wishlist" : "\u2606 Wishlist"}
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })()}            </>)}
            {/* Social Feed */}
            <div style={sMar8Style}>
              <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.375rem", color: "#F5E6D0", margin: "0 0 6px" }}>Community Feed</h2>
              <p style={{ color: "#B5A898", fontSize: "0.8125rem", margin: "0 0 12px" }}>{communityFeedFilter === "friends" ? "What your friends are pouring" : "Recent pours from the community"}</p>
              {/* Feed Filter + Search */}
              <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <select value={communityFeedFilter} onChange={e => setCommunityFeedFilter(e.target.value)}
                    style={{ appearance: "none", WebkitAppearance: "none", padding: "9px 28px 9px 10px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 10, color: "#D4A053", fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer", outline: "none" }}>
                    <option value="all">All</option>
                    <option value="friends">Friends</option>
                  </select>
                  <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", color: "#D4A053", fontSize: "0.5rem", pointerEvents: "none" }}>{"\u25BC"}</span>
                </div>
                <div style={{ position: "relative", flex: 1, minWidth: 0, transition: "all 0.2s" }}>
                  <input value={communityFeedSearch} onChange={e => setCommunityFeedSearch(e.target.value)}
                    onFocus={() => setFeedSearchFocused(true)} onBlur={() => setFeedSearchFocused(false)}
                    placeholder={feedSearchFocused || communityFeedSearch ? "Search by bourbon, friend, or note..." : "Search..."}
                    aria-label="Search community feed"
                    maxLength={100}
                    style={{ width: "100%", padding: "9px 32px 9px 32px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 10, color: "#F5E6D0", fontSize: "0.8125rem", boxSizing: "border-box" }} />
                  <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#A69880", fontSize: "0.8125rem", pointerEvents: "none" }}>{"🔍"}</span>
                  {communityFeedSearch && (
                    <button onClick={() => setCommunityFeedSearch("")}
                      style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#A69880", fontSize: "0.875rem", cursor: "pointer", padding: 4 }}
                      aria-label="Clear search">{"✕"}</button>
                  )}
                </div>
              </div>
              {communityCheckins === null ? (
                <>{[0,1,2,3].map(i => <SkeletonCard key={i} />)}</>
              ) : communityFeedItems.length === 0 ? (
                <div style={{ textAlign: "center", padding: "32px 16px", color: "#B5A898" }}>
                  <div style={sFonMar1Style}>{"👥"}</div>
                  <p style={{ margin: "0 0 8px", fontSize: "0.875rem" }}>
                    {communityFeedSearch.trim() ? "No matching pours found." :
                     communityFeedFilter === "friends" && friends.length === 0 ? "Add friends to see their pours here." :
                     communityFeedFilter === "friends" ? "No recent pours from friends." :
                     "No recent community pours yet."}
                  </p>
                  {communityFeedSearch.trim() ? (
                    <button onClick={() => setCommunityFeedSearch("")}
                      style={{ padding: "8px 16px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#D4A053", fontSize: "0.8125rem", cursor: "pointer" }}>
                      Clear Search
                    </button>
                  ) : communityFeedFilter === "friends" && friends.length === 0 ? (
                    <button onClick={() => navigateTo("profile")} style={emptyStateCTAStyle}>
                      Find Friends {"\u2192"}
                    </button>
                  ) : communityFeedFilter === "friends" ? null : (
                    <button onClick={() => navigateTo("browse")} style={emptyStateCTAStyle}>
                      Browse Bourbons {"\u2192"}
                    </button>
                  )}
                </div>
              ) : communityFeedItems.filter(cc => !isBlocked(cc.userId)).slice(0, 6).map(cc => {

                  const user = getCommunityUser(cc.userId);

                  const b = getBourbon(cc.bourbonId);

                  const d = b ? getBrand(b.brandId) : null;

                  const myLatestCheckin = getCheckins(cc.bourbonId).reduce((best, c) => !best || (c.date || '') > (best.date || '') ? c : best, null);
                  if (!user || !b) return null;

                  // Bar add activity card
                  if (cc.type === "bar_add") {
                    const d = getBrand(b.brandId);
                    return (
                      <div key={cc.id} className="tap-feedback" style={{ background: "#1E1A14", borderRadius: 12, padding: 14, marginBottom: 10, border: "1px solid #3D3428" }}>
                        <div style={flexCenterGap10Style}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#3D3428", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.0rem", flexShrink: 0 }}>{user.avatar}</div>
                          <div style={flexOneStyle}>
                            <div style={{ fontSize: "0.8125rem", color: "#F5E6D0" }}><span style={{ fontWeight: 600 }}>{user.name}</span> <span style={mutedTextStyle}>added</span> <span onClick={() => { setSelectedBourbon(b); navigateTo("bourbon-detail"); }} style={{ color: "#D4A053", cursor: "pointer", fontWeight: 600 }}>{getDisplayName(b)}</span> <span style={mutedTextStyle}>to their bar</span></div>
                            <div style={{ fontSize: "0.625rem", color: "#A69880", marginTop: 2 }}>{fmtDate(cc.date)}{d ? " \u00B7 " + d.name : ""}</div>
                          </div>
                          <span style={{ fontSize: "1.25rem" }}>{"🍾"}</span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={cc.id} className="tap-feedback" style={{ background: "#1E1A14", borderRadius: 12, padding: 16, marginBottom: 10, border: "1px solid #3D3428", transition: "border-color 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = "#5A4F3E"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
                      {/* User Header */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                        <div onClick={() => { setSelectedFriend(user); setFriendProfileTab("pours"); navigateTo("friend-profile"); }}
                          style={{ width: 36, height: 36, borderRadius: "50%", background: "#3D3428", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.125rem", flexShrink: 0, cursor: "pointer" }}>{user.avatar}</div>
                        <div style={{ flex: 1, cursor: "pointer" }} onClick={() => { setSelectedFriend(user); setFriendProfileTab("pours"); navigateTo("friend-profile"); }}>
                          <div style={{ fontSize: "0.875rem", color: "#F5E6D0", fontWeight: 600 }}>{user.name}</div>
                          <div style={sFonCol3Style}>{fmtDate(cc.date)} · 📍 {stripUSA(cc.location)}</div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4, background: "#150F0A", padding: "5px 10px", borderRadius: 20, border: "1px solid #3D3428" }}>
                            <span style={goldText06875Style}>★</span>
                            <span style={{ color: "#D4A053", fontWeight: 700, fontSize: "0.875rem" }}>{fmtRating(cc.rating)}</span>
                          </div>
                          {myLatestCheckin && (
                            <div style={{ display: "flex", alignItems: "center", gap: 3, background: "rgba(212,160,83,0.08)", padding: "3px 8px", borderRadius: 12, border: "1px solid rgba(212,160,83,0.15)" }}>
                              <span style={{ color: "#B5A898", fontSize: "0.5625rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>You</span>
                              <span style={{ color: "#B5A898", fontSize: "0.5625rem" }}>★</span>
                              <span style={{ color: "#A69880", fontWeight: 700, fontSize: "0.6875rem" }}>{fmtRating(myLatestCheckin.rating)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Bourbon info */}
                      <div onClick={() => { setSelectedBourbon(b); navigateTo("bourbon-detail"); }} style={{ cursor: "pointer" }}>
                        <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0" }}>{getDisplayName(b)}</div>
                        <div style={mutedText075Mt2Style}>{d?.name} · {b.proof}°</div>
                      </div>
                      {/* Comment */}
                      {cc.comment && <p style={{ color: "#A69880", fontSize: "0.875rem", margin: "10px 0 0", lineHeight: 1.5, fontStyle: "italic" }}>"{sanitizeText(cc.comment)}"</p>}
                      {/* Tasting notes */}
                      {cc.tastingNotes?.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 10 }}>
                          {cc.tastingNotes.map(n => <span key={n} style={{ padding: "2px 8px", borderRadius: 20, fontSize: "0.6875rem", background: "#3D3428", color: "#B5A898" }}>{n}</span>)}
                        </div>
                      )}
                      <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <ToastButton checkinId={cc.id} toasters={toasts[cc.id] || []} myUid={currentUser?.uid} myName={currentUser?.name || username} communityUserMap={communityUserMap} />
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {cc.userId === currentUser?.uid && (
                          <button onClick={(e) => { e.stopPropagation(); const url = `https://${BRAND.domain}/#/checkin/${cc.id}`; if (navigator.share) { navigator.share({ title: `${getDisplayName(b)} check-in — ${BRAND.name}`, url }).catch(() => {}); } else { copyToClipboard(url); } }}
                            style={{ background: "none", border: "none", color: "#A69880", fontSize: "0.6875rem", cursor: "pointer", padding: "8px 12px", transition: "color 0.15s" }}
                            onMouseEnter={e => e.currentTarget.style.color = "#D4A053"} onMouseLeave={e => e.currentTarget.style.color = "#A69880"}
                            aria-label="Share check-in">
                            ←—
                          </button>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); setReportModal({ userId: cc.userId, userName: user.name, contentId: cc.id, contentType: "checkin_comment", contentText: cc.comment || "" }); }}
                          style={{ background: "none", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 4, color: "#A69880", fontSize: "0.6875rem", cursor: "pointer", padding: "4px", transition: "color 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.color = "#CD5C5C"} onMouseLeave={e => e.currentTarget.style.color = "#A69880"}>
                          ⚑
                        </button>
                        </div>
                      </div>
                    </div>
                  );

                })}
            </div>
          </div>
        );
      }

      case "browse": {

        return (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.625rem", color: "#F5E6D0", margin: 0 }}>Browse Bourbons</h2>
              <div style={flexGap8Style}>
                <button onClick={() => setShowAddBourbonModal(true)}
                  style={{ padding: "8px 14px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                  + Expression
                </button>
              </div>
            </div>
            <div style={{ position: "sticky", top: 48, zIndex: 20, background: "#1A1409", paddingTop: 4, paddingBottom: 12, marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ ...posRelativeStyle, flex: 1 }}>
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} aria-label="Search bourbons or brands" placeholder="Search bourbons or brands..." maxLength={100}
                style={{ width: "100%", padding: "12px 16px", paddingRight: searchQuery ? 40 : 16, background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 10, color: "#F5E6D0", fontSize: "0.9375rem", boxSizing: "border-box" }} />
              {searchQuery && <button onClick={() => { setSearchQuery(""); window.scrollTo(0, 0); }} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#A69880", fontSize: "1rem", cursor: "pointer", padding: "4px 6px", lineHeight: 1 }}>✕</button>}
            </div>
            <button onClick={() => setShowBrowseScanner(true)} style={scanButtonStyle} aria-label="Scan barcode to find expression" title="Scan Barcode">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D4A053" strokeWidth="1.8" strokeLinecap="round"><path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2"/><line x1="7" y1="7" x2="7" y2="17"/><line x1="10" y1="7" x2="10" y2="17"/><line x1="13" y1="7" x2="13" y2="17"/><line x1="17" y1="7" x2="17" y2="17"/></svg>
            </button>
            </div>
            {showBrowseScanner && <BarcodeScanner autoResolve={true} onMatch={async (match) => { setShowBrowseScanner(false); setPendingScanUpc(match._scannedUpc); logScan(match._scannedUpc, true, match.expression_id, "browse"); let b = bourbons.find(bb => bb.id === match.expression_id); if (!b) { try { const sb = getSupabase(); if (sb) { const { data: rows } = await sb.from("bourbons").select("*").eq("id", match.expression_id).limit(1); if (rows && rows[0]) { b = rows[0]; setBourbons(prev => [...prev, b]); } } } catch { /* silent */ } } if (!b) { showToast("Couldn't load this expression. Try searching instead."); return; } /* Check if parent — show variant picker */ let children = bourbons.filter(c => c.parentExpressionId === b.id); if (children.length === 0) { try { const sb = getSupabase(); if (sb) { const { data } = await sb.from("bourbons").select("id, name, proof, tier, parent_expression_id").eq("parent_expression_id", b.id).order("name", { ascending: false }).limit(20); if (data && data.length > 0) children = data.map(r => ({ id: r.id, name: r.name, proof: r.proof ? parseFloat(r.proof) : null, tier: r.tier || "archive", parentExpressionId: r.parent_expression_id })); } } catch { /* silent */ } } if (children.length > 0) { setVariantPickerData({ parent: b, variants: children, source: "browse" }); } else { setSelectedBourbon(b); navigateTo("bourbon-detail"); } }} onNoMatch={(upc) => { setShowBrowseScanner(false); setPendingScanUpc(upc); logScan(upc, false, null, "browse"); const msg = SCAN_NO_MATCH_MESSAGES[Math.floor(Math.random() * SCAN_NO_MATCH_MESSAGES.length)]; setScanTransition(msg); setTimeout(() => setScanTransition(null), 4000); }} onClose={() => setShowBrowseScanner(false)} />}
            {scanTransition && (
              <div style={scanTransitionOverlayStyle} onClick={() => setScanTransition(null)}>
                <div style={scanTransitionCardStyle}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#D4A574" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 12, opacity: 0.7 }}>
                    <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2"/>
                    <line x1="7" y1="8" x2="7" y2="16"/><line x1="10.5" y1="8" x2="10.5" y2="16"/>
                    <line x1="14" y1="8" x2="14" y2="16"/><line x1="17" y1="8" x2="17" y2="16"/>
                  </svg>
                  <div style={{ color: "#F5E6D0", fontSize: "1rem", fontWeight: 600, textAlign: "center", marginBottom: 6 }}>{scanTransition.message}</div>
                  <div style={{ color: "#A69880", fontSize: "0.8125rem", textAlign: "center" }}>{scanTransition.submessage}</div>
                </div>
              </div>
            )}
            </div>
            <div style={mb20Style}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: activeFilters.size > 0 ? 8 : 0 }}>
                {[
                  { group: "For Me", items: [{ key: "highly-rated", label: "🔥 Highly Rated" }, { key: "recommended", label: "🎯 Recommended" }, { key: "tried", label: "✓ Tried" }, { key: "wishlist", label: "★ Wishlist" }] },
                  { group: "Availability", items: [{ key: "limited-flag", label: "⚡ Limited" }, { key: "discontinued-flag", label: "✕ Discontinued" }, { key: "all-expressions", label: "📦 All Expressions" }] },
                  { group: "Profile", items: [{ key: "high-rye", label: "High Rye" }, { key: "traditional", label: "Traditional" }, { key: "wheated", label: "Wheated" }] },
                  { group: "State", items: facilityStateItems },
                ].map(g => {

                  const isExpanded = expandedFilterGroup === "browse-" + g.group;

                  const hasActive = g.items.some(i => i.key === "all-expressions" ? browseShowAll : activeFilters.has(i.key));

                  return (
                    <div key={g.group} style={posRelativeStyle}>
                      <button onClick={() => setExpandedFilterGroup(isExpanded ? null : "browse-" + g.group)}
                        style={{ padding: "8px 14px", borderRadius: 20, border: `1px solid ${hasActive ? "#D4A053" : "#5A4F3E"}`, fontSize: "0.75rem", cursor: "pointer", transition: "all 0.15s",
                          background: hasActive ? "rgba(212,160,83,0.12)" : "transparent", color: hasActive ? "#D4A053" : "#B5A898", fontWeight: hasActive ? 700 : 400, display: "flex", alignItems: "center", gap: 5 }}>
                        {g.group} <span style={{ fontSize: "0.5625rem", transition: "transform 0.15s", transform: isExpanded ? "rotate(180deg)" : "none" }}>▼</span>
                      </button>
                      {isExpanded && (
                        <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 6, background: "#1E1A14", border: "1px solid #5A4F3E", borderRadius: 10, padding: 6, zIndex: 50, minWidth: 180, boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}>
                          {g.items.map(item => {

                            const isOn = item.key === "all-expressions" ? browseShowAll : activeFilters.has(item.key);

                            return (
                              <button key={item.key} onClick={() => {
                                if (item.key === "all-expressions") {
                                  setBrowseShowAll(prev => {
                                    if (!prev && !fullCatalogLoaded && useSupabase()) {
                                      setFullCatalogLoaded(true);
                                      db.loadBourbons(true).then(all => {
                                        if (all) setBourbons(p => {
                                          const existing = new Set(p.map(b => b.id));
                                          const newItems = all.filter(b => !existing.has(b.id));
                                          return newItems.length > 0 ? [...p, ...newItems] : p;
                                        });
                                      });
                                    }
                                    return !prev;
                                  });
                                  return;
                                }
                                setActiveFilters(prev => {

                                  const next = new Set(prev);
                                  isOn ? next.delete(item.key) : next.add(item.key);
                                  return next;

                                });

                              }}
                                style={{ width: "100%", padding: "9px 12px", background: isOn ? "rgba(212,160,83,0.1)" : "none", border: "none", cursor: "pointer", textAlign: "left", borderRadius: 6, display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${isOn ? "#D4A053" : "#5A4F3E"}`, background: isOn ? "#D4A053" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "0.625rem" }}>
                                  {isOn && <span style={{ color: "#1A1510", fontWeight: 700 }}>✓</span>}
                                </span>
                                <span style={{ fontSize: "0.8125rem", color: isOn ? "#D4A053" : "#B5A898", fontWeight: isOn ? 600 : 400 }}>{item.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {activeFilters.size > 0 && (
                  <button onClick={() => setActiveFilters(new Set())}
                    style={{ padding: "8px 12px", borderRadius: 20, border: "1px solid #5A4F3E", fontSize: "0.75rem", cursor: "pointer", background: "transparent", color: "#B5A898" }}>
                    ✕ Clear
                  </button>
                )}
              </div>
              {activeFilters.size > 0 && (
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {[...activeFilters].map(f => (
                    <span key={f} onClick={() => setActiveFilters(prev => { const next = new Set(prev); next.delete(f); return next; })}
                      style={{ padding: "8px 12px", borderRadius: 12, fontSize: "0.6875rem", background: "rgba(212,160,83,0.1)", border: "1px solid rgba(212,160,83,0.25)", color: "#D4A053", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                      {{ recommended: "🎯 Recommended", wishlist: "★ Wishlist", tried: "✓ Tried", "highly-rated": "🔥 Highly Rated", "discontinued-flag": "✕ Discontinued", "high-rye": "High Rye", "limited-flag": "⚡ Limited", traditional: "Traditional", wheated: "Wheated" }[f] || (f.startsWith("state-") ? f.slice(6) : f)} <span style={{ fontSize: "0.625rem" }}>✕</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
            {/* Price & Proof Range Sliders */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
              {[
                { label: "Price", lo: browsePrice[0], hi: browsePrice[1], min: 0, max: 200, step: 10, unit: "$", suffix: browsePrice[1] >= 200 ? "+" : "", set: setBrowsePrice },
                { label: "Proof", lo: browseProof[0], hi: browseProof[1], min: 80, max: 160, step: 5, unit: "", suffix: "°", set: setBrowseProof },
              ].map(s => {

                const pctLo = ((s.lo - s.min) / (s.max - s.min)) * 100;

                const pctHi = ((s.hi - s.min) / (s.max - s.min)) * 100;

                return (
                  <div key={s.label}>
                    <div style={flexBetweenCenterMb6Style}>
                      <span style={{ fontSize: "0.6875rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</span>
                      <span style={{ fontSize: "0.75rem", color: "#D4A053", fontWeight: 600 }}>{s.unit === "$" ? `$${s.lo} – $${s.hi}${s.suffix}` : `${s.lo}${s.suffix} – ${s.hi}${s.suffix}`}</span>
                    </div>
                    <div style={{ position: "relative", height: 28 }}>
                      {/* Track background */}
                      <div style={{ position: "absolute", top: 12, left: 0, right: 0, height: 4, borderRadius: 2, background: "#3D3428" }} />
                      {/* Active range fill */}
                      <div style={{ position: "absolute", top: 12, left: pctLo + "%", width: (pctHi - pctLo) + "%", height: 4, borderRadius: 2, background: "#D4A053" }} />
                      {/* Low thumb */}
                      <input type="range" min={s.min} max={s.max} step={s.step} value={s.lo} aria-label="Filter minimum"
                        onChange={e => { const v = parseInt(e.target.value); s.set(p => [Math.min(v, p[1] - s.step), p[1]]); }}
                        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: 28, WebkitAppearance: "none", appearance: "none", background: "transparent", pointerEvents: "none", margin: 0, zIndex: 2 }}
                        className="range-thumb" />
                      {/* High thumb */}
                      <input type="range" min={s.min} max={s.max} step={s.step} value={s.hi} aria-label="Filter maximum"
                        onChange={e => { const v = parseInt(e.target.value); s.set(p => [p[0], Math.max(v, p[0] + s.step)]); }}
                        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: 28, WebkitAppearance: "none", appearance: "none", background: "transparent", pointerEvents: "none", margin: 0, zIndex: 3 }}
                        className="range-thumb" />
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Sort Headers */}
            <div style={{ display: "flex", alignItems: "center", padding: "0 4px 8px", gap: 12 }}>
              {[
                { key: "popular", label: "Popular", flex: 0, w: 52 },
                { key: "name", label: "Name", flex: 1 },
                { key: "proof", label: "Proof", w: 48 },
                { key: "rating", label: "Rating", w: 52 },
              ].map(col => {

                const active = browseSort.key === col.key;

                return (
                  <div key={col.key} onClick={() => setBrowseSort(prev => prev.key === col.key ? { key: col.key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key: col.key, dir: col.key === "name" ? "asc" : "desc" })}
                    style={{ width: col.w, flex: col.flex || undefined, fontSize: "0.625rem", color: active ? "#D4A053" : "#A69880", textTransform: "uppercase", letterSpacing: 1, cursor: "pointer", userSelect: "none", display: "flex", alignItems: "center", gap: 3, fontWeight: active ? 700 : 400, transition: "color 0.15s", textAlign: col.key === "name" ? "left" : "right", justifyContent: col.key === "name" ? "flex-start" : "flex-end" }}>
                    {col.label}
                    <span style={{ fontSize: "0.5rem", opacity: active ? 1 : 0.3 }}>{active ? (browseSort.dir === "asc" ? "▲" : "\u25BC") : "▲"}</span>
                  </div>
                );
              })}
            </div>
            {sortedBourbons.map(b => {

              const d = getDisplayBrand(b.brandId);

              const exprStatus = getExpressionStatus(b);

              return (
                <div key={b.id} className="tap-feedback browse-card" onClick={() => { setSelectedBourbon(b); navigateTo("bourbon-detail"); }}
                  style={browseCardStyle}>
                  <div style={browseCardLeftStyle}>
                    {exprStatus === "official" && d?.image && (
                      <div style={browseCardImgStyle}>
                        <DistilleryIconRender iconKey={d.image} size={20} />
                      </div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={flexCenterGap6WrapStyle}>
                        <span style={browseCardNameStyle}>{getDisplayName(b)}</span>
                        <StatusBadge bourbon={b} />
                        {exprStatus === "official" && <span style={browseCardOfficialBadgeStyle}>{"\u2713"}</span>}
                        {exprStatus === "pending-review" && <span style={browseCardPendingBadgeStyle}>⏳</span>}
                        <SubmissionBadgeInline item={b} getSubmissionStatus={getSubmissionStatus} />
                        {b.tier === "curated" && <span style={browseCardCuratedBadgeStyle}>{"\u2605"}</span>}
                      </div>
                      <div style={browseCardBrandStyle}>{d?.name}{b.expressionType === "program" && b.childCount > 0 && <span style={browseCardProgramBadgeStyle}>Program {"\u00B7"} {b.childCount} release{b.childCount !== 1 ? "s" : ""}</span>}{b.expressionType === "pick_base" && <span style={browseCardPickBaseBadgeStyle}>Single Barrel</span>}</div>
                      <div style={mutedText075Mt2Style}>{formatProof(b)} · {formatAge(b.age)} · {b.type}</div>
                    </div>
                  </div>
                  <div style={browseCardRatingWrapStyle}>
                    {b.ratingCount > 0 && b.avgRating ? (<div>
                      <div style={browseCardRatingRowStyle}>
                        <span style={goldText06875Style}>{"\u2605"}</span>
                        <span style={browseCardRatingNumStyle}>{fmtRating(b.avgRating)}</span>
                        <span style={browseCardRatingCountStyle}>({formatCount(b.ratingCount)})</span>
                      </div>
                    </div>) : <div style={dimText075Style}>No rating</div>}
                    {(() => { const myC = getCheckins(b.id); if (myC.length === 0) return null; const myAvg = (myC.reduce((s, c) => s + c.rating, 0) / myC.length).toFixed(2); return <div style={browseCardYouRatingStyle}>You: {"\u2605"} {myAvg} ({myC.length})</div>; })()}
                    <div style={sFonCol4Style}>{formatMsrp(b.msrp)}</div>
                  </div>
                </div>
              );
            })}
            {dataLoading && filteredBourbons.length === 0 && <>{[0,1,2,3].map(i => <SkeletonCard key={i} />)}</>}
            {!dataLoading && filteredBourbons.length === 0 && <div style={{ textAlign: "center", color: "#B5A898", padding: 40 }}>
              <div style={sFonMar1Style}>🔍</div>
              <p style={{ margin: "0 0 4px", fontSize: "0.875rem" }}>No bourbons match your current filters.</p>
              {(browsePrice[0] > 0 || browsePrice[1] < 200 || browseProof[0] > 80 || browseProof[1] < 160) && <div style={{ fontSize: "0.75rem", marginTop: 8 }}>Try adjusting the price or proof sliders.</div>}
              {searchQuery.trim().length >= 3 && <div style={{ marginTop: 16, padding: "12px 16px", background: "rgba(212,160,83,0.04)", borderRadius: 10, border: "1px solid rgba(212,160,83,0.12)", textAlign: "left" }}>
                <div style={{ fontSize: "0.75rem", color: "#D4A053", fontWeight: 600, marginBottom: 4 }}>Can't find it?</div>
                <div style={{ fontSize: "0.6875rem", color: "#B5A898", lineHeight: 1.5, marginBottom: 10 }}>Many specific releases aren't in the federal label database. If you have the bottle, you can add it to the catalog — scan the barcode and we'll verify it.</div>
                <button onClick={() => { setSearchQuery(""); setShowAddBourbonModal(true); }} style={{ padding: "8px 16px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>+ Add Missing Expression</button>
              </div>}
              {archiveSearching && <div style={{ color: "#A69880", fontSize: "0.75rem", marginTop: 12 }}>Searching extended catalog...</div>}
              {!archiveSearching && archiveSearchResults.length > 0 && (
                <div style={{ marginTop: 16, textAlign: "left" }}>
                  <div style={{ fontSize: "0.75rem", color: "#D4A053", fontWeight: 600, marginBottom: 8 }}>Also found in our catalog:</div>
                  {archiveSearchResults.map(b => {
                    const d = getBrand(b.brandId);
                    return (
                      <div key={b.id} className="tap-feedback" onClick={async () => {
                        const existing = bourbons.find(bb => bb.id === b.id);
                        if (existing) { setSelectedBourbon(existing); navigateTo("bourbon-detail"); return; }
                        try {
                          const sb = getSupabase();
                          if (sb) {
                            const { data: rows } = await sb.from("bourbons").select("*").eq("id", b.id).limit(1);
                            if (rows && rows[0]) {
                              const r = rows[0];
                              const full = { id: r.id, name: r.name, brandId: r.brand_id, facilityId: r.facility_id, proof: r.proof ? parseFloat(r.proof) : null, age: r.age || "", mashBill: r.mash_bill || "", msrp: r.msrp ? parseFloat(r.msrp) : null, type: r.type || "", finishType: r.finish_type || "", batch: r.batch || "", notes: r.notes || "", limitedRelease: r.limited_release || false, discontinued: r.discontinued || false, userAdded: r.user_added || false, addedBy: r.added_by || null, website: r.website || "", source: r.source || null, origin: r.origin || "", colaTtbId: r.cola_ttb_id || null, colaBrandName: r.cola_brand_name || null, colaClassName: r.cola_class_name || null, colaApprovalDate: r.cola_approval_date || null, imageUrl: r.image_url || null, submissionStatus: r.submission_status || "approved", submittedBy: r.submitted_by || null, submittedAt: r.submitted_at || null, reviewedBy: r.reviewed_by || null, reviewedAt: r.reviewed_at || null, rejectionReason: r.rejection_reason || "", mashCorn: r.mash_corn ?? null, mashWheat: r.mash_wheat ?? null, mashRye: r.mash_rye ?? null, mashBarley: r.mash_barley ?? null, mashOther: r.mash_other ?? null, proofMin: r.proof_min ? parseFloat(r.proof_min) : null, proofMax: r.proof_max ? parseFloat(r.proof_max) : null, tier: r.tier || "archive", popularityScore: r.popularity_score || 0, parentExpressionId: r.parent_expression_id || null, nameLocked: r.name_locked || false, colaProductName: r.cola_product_name || null, colaTastingFlavors: r.cola_tasting_flavors || null, colaDescription: r.cola_description || null, colaGrains: r.cola_grains || null, colaFinishingProcess: r.cola_finishing_process || null, colaBarcodeValue: r.cola_barcode_value || null, colaEnrichedAt: r.cola_enriched_at || null, expressionType: r.expression_type || null, mashbillSource: r.mashbill_source || null };
                              setBourbons(prev => [...prev, full]);
                              setSelectedBourbon(full);
                              navigateTo("bourbon-detail");
                            }
                          }
                        } catch { showToast("Could not load expression details."); }
                      }} style={{ display: "flex", alignItems: "center", padding: "10px 0", borderBottom: "1px solid rgba(61,52,40,0.3)", cursor: "pointer", gap: 10 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: "#F5E6D0", fontSize: "0.875rem", fontWeight: 600 }}>{b.name}</div>
                          <div style={{ color: "#B5A898", fontSize: "0.75rem" }}>{d?.name}{b.proof ? ` \u00B7 ${b.proof}\u00B0` : ""}{b.type ? ` \u00B7 ${b.type}` : ""}</div>
                        </div>
                        <div style={{ color: "#A69880", fontSize: "0.625rem", flexShrink: 0 }}>{b.tier}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>}
          </div>
        );
      }

      case "brands": {

        const distFuzzy = (haystack, needle) => {
          if (!haystack || !needle) return false;
          return haystack.toLowerCase().includes(needle.toLowerCase());
        };

        const distQ = distSearch.trim();

        // Filter facilities for the Brands tab (facility-first view)
        const filteredFacilities = facilities.filter(fac => {
          if (!isBrowsableFacility(fac)) return false;
          const regionMatch = distRegion.size === 0 ? true
            : [...distRegion].some(r => r === "kbt" ? fac.kbt === true
            : r === "ubt" ? fac.ubt === true
            : (() => { const st = r.replace(/^state-/, "").toUpperCase(); if (fac.state && fac.state.toUpperCase() === st) return true; const loc = fac.location || fac.locationDisplay || ""; if (loc.split(" ")[0]?.toUpperCase() === st) return true; const m = loc.match(/,\s*([A-Z]{2})\b/i); return m ? m[1].toUpperCase() === st : false; })());
          if (!regionMatch) return false;
          if (!distQ) return true;
          const facBrands = brandsByFacility.get(fac.permitNumber) || [];
          const brandNames = facBrands.map(b => b.name).join(" ");
          const exprNames = facBrands.flatMap(b => getBrandBourbons(b.id).map(x => x.name)).join(" ");
          return distFuzzy(fac.name || fac.companyName, distQ) || distFuzzy(fac.locationDisplay, distQ)
            || distFuzzy(brandNames, distQ)
            || distFuzzy(exprNames, distQ) || distFuzzy(fac.permitNumber, distQ)
            || (fac.est && String(fac.est).includes(distQ));
        });

        return (
          <div>
            <div style={flexBetweenCenterMb6Style}>
              <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.625rem", color: "#F5E6D0", margin: 0 }}>Brands</h2>
              <button onClick={() => setShowAddBrandModal(true)}
                style={{ padding: "8px 14px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                + Brand
              </button>
            </div>

            <div style={{ position: "sticky", top: 48, zIndex: 20, background: "#1A1409", paddingTop: 4, paddingBottom: 12, marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16 }}>
            <div style={posRelativeStyle}>
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#B5A898", fontSize: "0.875rem", pointerEvents: "none" }}>{"🔍"}</span>
              <input value={distSearch} onChange={e => setDistSearch(e.target.value)} aria-label="Search brands" placeholder="Search distilleries, brands, expressions..." maxLength={100}
                style={{ width: "100%", padding: "12px 36px 12px 36px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 10, color: "#F5E6D0", fontSize: "0.875rem", boxSizing: "border-box", outline: "none", transition: "border-color 0.2s" }}
                onFocus={e => { e.target.style.borderColor = "#D4A053"; }} onBlur={e => { e.target.style.borderColor = "#3D3428"; }} />
              {distSearch && (
                <button onClick={() => { setDistSearch(""); window.scrollTo(0, 0); }} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#B5A898", cursor: "pointer", fontSize: "0.875rem", padding: 4 }} aria-label="Close">{"✕"}</button>
              )}
            </div>
            </div>
            <div style={mb20Style}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: distRegion.size > 0 ? 8 : 0 }}>
                {[
                  { group: "Trail", items: [{ key: "kbt", label: "🥃 KBT" }, { key: "ubt", label: "🏙️ UBT" }] },
                  { group: "State", items: US_STATES.map(s => ({ key: s.toLowerCase(), label: s })) },
                ].map(g => {
                  const isExpanded = expandedFilterGroup === "dist-" + g.group;
                  const hasActive = g.items.some(i => distRegion.has(i.key));
                  return (
                    <div key={g.group} style={posRelativeStyle}>
                      <button onClick={() => setExpandedFilterGroup(isExpanded ? null : "dist-" + g.group)}
                        style={{ padding: "8px 14px", borderRadius: 20, border: `1px solid ${hasActive ? "#D4A053" : "#5A4F3E"}`, fontSize: "0.75rem", cursor: "pointer", transition: "all 0.15s",
                          background: hasActive ? "rgba(212,160,83,0.12)" : "transparent", color: hasActive ? "#D4A053" : "#B5A898", fontWeight: hasActive ? 700 : 400, display: "flex", alignItems: "center", gap: 5 }}>
                        {g.group} <span style={{ fontSize: "0.5625rem", transition: "transform 0.15s", transform: isExpanded ? "rotate(180deg)" : "none" }}>▼</span>
                      </button>
                      {isExpanded && (
                        <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 6, background: "#1E1A14", border: "1px solid #5A4F3E", borderRadius: 10, padding: 6, zIndex: 50, minWidth: 180, maxHeight: 320, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}>
                          {g.items.map(item => {
                            const isOn = distRegion.has(item.key);
                            return (
                              <button key={item.key} onClick={() => {
                                setDistRegion(prev => {
                                  const next = new Set(prev);
                                  isOn ? next.delete(item.key) : next.add(item.key);
                                  return next;
                                });
                              }}
                                style={{ width: "100%", padding: "9px 12px", background: isOn ? "rgba(212,160,83,0.1)" : "none", border: "none", cursor: "pointer", textAlign: "left", borderRadius: 6, display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${isOn ? "#D4A053" : "#5A4F3E"}`, background: isOn ? "#D4A053" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "0.625rem" }}>
                                  {isOn && <span style={{ color: "#1A1510", fontWeight: 700 }}>✓</span>}
                                </span>
                                <span style={{ fontSize: "0.8125rem", color: isOn ? "#D4A053" : "#B5A898", fontWeight: isOn ? 600 : 400 }}>{item.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {distRegion.size > 0 && (
                  <button onClick={() => setDistRegion(new Set())}
                    style={{ padding: "8px 12px", borderRadius: 20, border: "1px solid #5A4F3E", fontSize: "0.75rem", cursor: "pointer", background: "transparent", color: "#B5A898" }}>
                    ✕ Clear
                  </button>
                )}
              </div>
              {distRegion.size > 0 && (
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {[...distRegion].map(r => (
                    <span key={r} onClick={() => setDistRegion(prev => { const next = new Set(prev); next.delete(r); return next; })}
                      style={{ padding: "8px 12px", borderRadius: 12, fontSize: "0.6875rem", background: "rgba(212,160,83,0.1)", border: "1px solid rgba(212,160,83,0.25)", color: "#D4A053", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                      {r === "kbt" ? "🥃 KBT" : r === "ubt" ? "🏙️ UBT" : r.toUpperCase()} <span style={{ fontSize: "0.625rem" }}>✕</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
            {/* Sort header row */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, padding: "6px 0", borderBottom: "1px solid #3D3428" }}>
              <button onClick={() => setBrandsSort(prev => prev.by === "alpha" ? { ...prev, alphaDir: prev.alphaDir === "asc" ? "desc" : "asc" } : { ...prev, by: "alpha" })}
                style={{ background: "none", border: "none", cursor: "pointer", color: brandsSort.by === "alpha" ? "#D4A053" : "#B5A898", fontSize: "0.625rem", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", padding: 0 }}>
                NAME {brandsSort.by === "alpha" ? (brandsSort.alphaDir === "asc" ? "\u25B2" : "\u25BC") : ""}
              </button>
              <button onClick={() => setBrandsSort(prev => prev.by === "expr" ? { ...prev, exprDir: prev.exprDir === "desc" ? "asc" : "desc" } : { ...prev, by: "expr" })}
                style={{ background: "none", border: "none", cursor: "pointer", color: brandsSort.by === "expr" ? "#D4A053" : "#B5A898", fontSize: "0.625rem", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", padding: 0 }}>
                EXPRESSIONS {brandsSort.by === "expr" ? (brandsSort.exprDir === "desc" ? "\u25BC" : "\u25B2") : ""}
              </button>
            </div>
            {distQ && <div style={{ color: "#B5A898", fontSize: "0.75rem", marginBottom: 10 }}>{filteredFacilities.length} result{filteredFacilities.length !== 1 ? "s" : ""} for "{distQ}"</div>}
            {dataLoading && filteredFacilities.length === 0 ? (
              <>{[0,1,2,3].map(i => <SkeletonCard key={i} />)}</>
            ) : filteredFacilities.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 16px", color: "#B5A898" }}>
                <div style={sFonMar1Style}>{"🏭"}</div>
                <p style={{ margin: "0 0 4px", fontSize: "0.875rem" }}>No results match "{distQ}"</p>
                <p style={{ margin: 0, fontSize: "0.75rem", color: "#B5A898" }}>Try a different search or <button onClick={() => { setDistSearch(""); window.scrollTo(0, 0); }} style={{ background: "none", border: "none", color: "#D4A053", cursor: "pointer", fontSize: "0.75rem" }}>clear the search</button></p>
              </div>
            ) : (() => {
              return [...filteredFacilities].sort((a, b) => {
                if (brandsSort.by === "expr") {
                  return brandsSort.exprDir === "desc" ? (facExprCountMap.get(b.permitNumber) || 0) - (facExprCountMap.get(a.permitNumber) || 0) : (facExprCountMap.get(a.permitNumber) || 0) - (facExprCountMap.get(b.permitNumber) || 0);
                }
                const cmp = (a.name || a.companyName || "").localeCompare(b.name || b.companyName || "", undefined, { sensitivity: "base" });
                return brandsSort.alphaDir === "asc" ? cmp : -cmp;
              }).map(fac => {
                const facBrands = (brandsByFacility.get(fac.permitNumber) || []).filter(b => isBrowsableBrand(b));
                const exprCount = facExprCountMap.get(fac.permitNumber) || 0;
                return { fac, facBrands, exprCount };
              });
            })().map(({ fac, facBrands, exprCount }) => {
              // Count top-level brands (parent absorbs children)
              const childIds = new Set(facBrands.filter(b => b.parentBrandId && facBrands.some(p => p.id === b.parentBrandId)).map(b => b.id));
              const topLevelBrands = facBrands.filter(b => !childIds.has(b.id));
              const topBrandNames = topLevelBrands.sort((a, b) => {
                const ac = getBrandBourbons(a.id).length; const bc = getBrandBourbons(b.id).length;
                return bc - ac;
              }).slice(0, 4).map(b => b.name);

              return (
                <div key={fac.permitNumber} className="tap-feedback" onClick={() => { setSelectedFacility(fac); navigateTo("facility-detail"); }}
                  style={{ background: "#1E1A14", borderRadius: 12, padding: 20, marginBottom: 10, border: "1px solid #3D3428", cursor: "pointer", display: "flex", alignItems: "center", gap: 16, transition: "border-color 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#D4A053"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
                  <div style={{ width: 56, height: 56, display: "flex", alignItems: "center", justifyContent: "center", background: "#150F0A", borderRadius: 12, border: "1px solid #3D3428", flexShrink: 0 }}>
                    <DistilleryIconRender iconKey={fac.icon || fac.image} size={32} />
                  </div>
                  <div style={flexMinW0Style}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0" }}>{fac.name || fac.companyName}</span>
                      {fac.kbt && <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: "0.5625rem", fontWeight: 600, background: "rgba(212,160,83,0.1)", color: "#D4A053", border: "1px solid rgba(212,160,83,0.25)" }}>KBT</span>}
                      {fac.ubt && <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: "0.5625rem", fontWeight: 600, background: "rgba(100,149,237,0.1)", color: "#6495ED", border: "1px solid rgba(100,149,237,0.25)" }}>UBT</span>}
                    </div>
                    <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginTop: 3 }}>
                      {[
                        fac.locationDisplay || fac.state ? "📍 " + (fac.locationDisplay || fac.state) : "",
                        fac.est ? "Est. " + fac.est : "",
                      ].filter(Boolean).join(" · ")}
                    </div>
                    <div style={{ color: "#B5A898", fontSize: "0.75rem", marginTop: 3 }}>{topLevelBrands.length} brand{topLevelBrands.length !== 1 ? "s" : ""} · {exprCount} expression{exprCount !== 1 ? "s" : ""}</div>
                    {topBrandNames.length > 0 && <div style={{ color: "#A69880", fontSize: "0.6875rem", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{topBrandNames.join(", ")}{topLevelBrands.length > 4 ? ", ..." : ""}</div>}
                  </div>
                  <div style={{ color: "#A69880", fontSize: "1.125rem" }}>›</div>
                </div>
              );
            })}
          </div>
        );
      }

      case "mybar": {

        const { active: activeBar, archived: archivedBar, totalValue, totalPaid } = barDerived;

        const [showAddToBarModal, setShowAddToBarModal] = [subView === "bar-add", (v) => setSubView(v ? "bar-add" : null)];

        const barTab = subView === "bar-archive" ? "archive" : "current";

        const setBarTab = (t) => setSubView(t === "archive" ? "bar-archive" : null);

        const archiveFiltered = archiveSearch.trim().length < 1 ? archivedBar : archivedBar.filter(item => {

          const q = archiveSearch.toLowerCase();

          const b = getBourbon(item.bourbonId);

          const d = b ? getBrand(b.brandId) : null;

          return (b?.name || "").toLowerCase().includes(q) || (d?.name || "").toLowerCase().includes(q) || (item.batch || "").toLowerCase().includes(q) || (item.emptyReason || "").toLowerCase().includes(q);

        });

        const archiveDisplayed = archiveFilter ? archiveFiltered.filter(item => (item.emptyReason || "").includes(archiveFilter)) : archiveFiltered;

        const barSearchResults = barSearchQ.trim().length >= 2
          ? bourbons.filter(b => {

              const q = barSearchQ.toLowerCase();

              const alreadyInBar = false; // Allow multiple bottles of same expression
              return !alreadyInBar && (b.name.toLowerCase().includes(q) || (b.batch && b.batch.toLowerCase().includes(q)) || getBrand(b.brandId)?.name.toLowerCase().includes(q));
            }).slice(0, 8)
          : [];

        const addToBar = (bourbon) => {

          const ov = barAddOverrides[bourbon.id] || {};

          const newItem = {
            bourbonId: bourbon.id,
            pricePaid: ov.price ? Math.min(Math.max(parseFloat(ov.price) || 0, 0), 100000) : bourbon.msrp,
            fill: Math.min(Math.max(parseInt(ov.fill) || 100, 0), 100),
            dateAdded: new Date().toISOString().slice(0, 10),
            batch: ov.batch?.trim() ? sanitizeInput(ov.batch, 50) : undefined,
            purchaseLocation: ov.purchasedAt?.trim() ? sanitizeInput(ov.purchasedAt, 200) : undefined,
          };
          const notesMeta = {};
          if (ov.customProof && parseFloat(ov.customProof) !== bourbon.proof) notesMeta.custom_proof = parseFloat(ov.customProof);
          if (Object.keys(notesMeta).length > 0) newItem.notes = JSON.stringify(notesMeta);
          setMyBar(prev => [...prev, newItem]);

          if (useSupabase() && currentUser?.uid) db.insertBarItem(currentUser.uid, newItem);
          setBarSearchQ("");
          setBarAddOverrides({});
          setAddFillLevel(100);

          setShowAddToBarModal(false);
          showToast("Added to your bar!");
          setBarSharePrompt({ bourbonId: bourbon.id, bourbonName: getDisplayName(bourbon) });

        };

        const updateFill = (idx, newFill) => {

          const clamped = Math.max(0, Math.min(100, Math.round(Number(newFill) || 0)));
          if (clamped === 0 && myBar[idx]?.fill !== 0) {
            setEmptyBottlePrompt({ idx, bourbonId: myBar[idx].bourbonId });

          } else {
            setMyBar(prev => prev.map((item, i) => i === idx ? { ...item, fill: clamped } : item));
            if (useSupabase() && myBar[idx]?.id) db.updateBarItem(myBar[idx].id, { fill: clamped });

          }

        };

        const handleEmptyBottleChoice = (choice) => {
          if (!emptyBottlePrompt) return;

          const { idx } = emptyBottlePrompt;

          const reasons = { kill: "Bottle Kill 🎉", giveaway: "Gave Away 🎁", drain: "Drain Pour 🚫" };

          const emptyReason = reasons[choice];
          setMyBar(prev => prev.map((item, i) => i === idx ? { ...item, fill: 0, emptyReason, emptiedDate: new Date().toISOString().slice(0, 10) } : item));
          if (useSupabase() && myBar[idx]?.id) db.updateBarItem(myBar[idx].id, { fill: 0, emptyReason });

          setEmptyBottlePrompt(null);
        };

        const updatePrice = (idx, newPrice) => {

          const price = Math.min(Math.max(parseFloat(newPrice) || 0, 0), 100000);
          setMyBar(prev => prev.map((item, i) => i === idx ? { ...item, pricePaid: price } : item));
          if (useSupabase() && myBar[idx]?.id) db.updateBarItem(myBar[idx].id, { pricePaid: price });

        };

        const removeFromBar = (idx) => {
          if (useSupabase() && myBar[idx]?.id) db.deleteBarItem(myBar[idx].id);
          setMyBar(prev => prev.filter((_, i) => i !== idx));
        };

        // ─── FUZZY MATCH ENGINE ─────────────────────

        const fuzzyScore = (needle, haystack) => {
          if (!needle || !haystack) return 0;

          const a = needle.toLowerCase().replace(/[^a-z0-9]/g, "");

          const b = haystack.toLowerCase().replace(/[^a-z0-9]/g, "");
          if (a === b) return 1;
          if (b.includes(a) || a.includes(b)) return 0.85;

          // Levenshtein-based similarity

          const m = a.length, n = b.length;
          if (m === 0 || n === 0) return 0;

          const dp = Array.from({ length: m + 1 }, (_, i) => {

            const row = new Array(n + 1);
            row[0] = i;
            return row;

          });

          for (let j = 1; j <= n; j++) dp[0][j] = j;
          for (let i = 1; i <= m; i++)
            for (let j = 1; j <= n; j++)
              dp[i][j] = Math.min(dp[i-1][j]+1, dp[i][j-1]+1, dp[i-1][j-1] + (a[i-1]!==b[j-1]?1:0));
          return 1 - dp[m][n] / Math.max(m, n);
        };

        const matchBourbon = (name, distilleryName, proof) => {
          if (!name) return { match: null, confidence: 0, alternatives: [] };

          const scored = bourbons.map(b => {

            const dist = getBrand(b.brandId);

            const nameScore = fuzzyScore(name, b.name) * 0.6;

            const distScore = distilleryName && dist ? fuzzyScore(distilleryName, dist.name) * 0.25 : 0;

            const proofScore = proof && b.proof && Math.abs(parseFloat(proof) - b.proof) < 2 ? 0.15 : 0;
            return { bourbon: b, distillery: dist, score: nameScore + distScore + proofScore };
          }).filter(s => s.score > 0.3).sort((a, b) => b.score - a.score);

          const best = scored[0] || null;
          return {
            match: best && best.score >= 0.5 ? best.bourbon : null,
            confidence: best ? Math.round(best.score * 100) : 0,
            alternatives: scored.slice(0, 5).map(s => ({ ...s.bourbon, distName: s.distillery?.name, score: Math.round(s.score * 100) })),
          };
        };

        const processImportData = (rows) => {

          const results = rows.map((row, idx) => {

            const name = (row["Expression Name"] || row["Expression Name*"] || row["Name"] || row["name"] || row["expression"] || "").trim();

            const distillery = (row["Distillery"] || row["distillery"] || row["Producer"] || "").trim();

            const proof = row["Proof"] || row["proof"] || "";

            const { match, confidence, alternatives } = matchBourbon(name, distillery, proof);
            return {
              idx,
              raw: row,
              name,
              distillery,
              proof,
              price: row["Price Paid"] || row["price"] || row["Price"] || "",
              fill: row["Fill %"] || row["fill"] || row["Fill"] || "100",
              batch: row["Batch"] || row["batch"] || row["Barrel #"] || "",
              purchaseLocation: row["Purchase Location"] || row["location"] || row["Store"] || "",
              datePurchased: row["Date Purchased"] || row["date"] || row["Date"] || "",
              notes: row["Notes"] || row["notes"] || "",
              match,
              confidence,
              alternatives,
              skip: false,
              manualSelect: null,
            };
          }).filter(r => r.name);
          setBarImport(prev => ({ ...prev, matches: results, step: "review" }));
        };

        const handleFileImport = (e) => {

          const file = e.target.files?.[0];
          if (!file) return;

          const ext = file.name.split(".").pop().toLowerCase();

          const reader = new FileReader();
          if (ext === "csv") {
            reader.onload = (ev) => {

              const text = ev.target.result;

              const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
              if (lines.length < 2) return;

              const headers = lines[0].split(",").map(h => h.replace(/^"|"$/g, "").trim());

              const rows = lines.slice(1).map(line => {

                const vals = [];
                let cur = "", inQ = false;
                for (const ch of line) {
                  if (ch === '"') { inQ = !inQ; }
                  else if (ch === ',' && !inQ) { vals.push(cur.trim()); cur = ""; }
                  else { cur += ch; }

                }

                vals.push(cur.trim());

                const obj = {};
                headers.forEach((h, i) => { obj[h] = (vals[i] || "").replace(/^"|"$/g, ""); });

                return obj;

              });

              processImportData(rows);
            };
            reader.readAsText(file);
          } else if (ext === "xlsx" || ext === "xls") {
            reader.onload = (ev) => {
              try {

                const XLSX = window.XLSX;
                if (!XLSX) { showToast("⚠️ XLSX support loading..."); return; }

                const wb = XLSX.read(ev.target.result, { type: "array" });

                const ws = wb.Sheets[wb.SheetNames[0]];

                const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

                processImportData(rows);
              } catch { showToast("⚠️ Could not parse file. Try CSV format."); }
            };
            reader.readAsArrayBuffer(file);
          } else {
            showToast("⚠️ Please use .csv or .xlsx files");

          }

          e.target.value = "";
        };

        const executeImport = () => {

          const toImport = barImport.matches.filter(m => !m.skip && (m.manualSelect || m.match));
          let added = 0, skipped = 0, dupes = 0;
          toImport.forEach(m => {

            const bourbon = m.manualSelect || m.match;
            if (!bourbon) { skipped++; return; }

            const alreadyInBar = myBar.some(item => item.bourbonId === bourbon.id && !item.archived);
            if (alreadyInBar) { dupes++; return; }

            const newItem = {
              bourbonId: bourbon.id,
              pricePaid: m.price ? Math.min(Math.max(parseFloat(m.price) || 0, 0), 100000) : bourbon.msrp,
              fill: Math.min(Math.max(parseInt(m.fill) || 100, 0), 100),
              dateAdded: new Date().toISOString().slice(0, 10),
              batch: m.batch ? sanitizeInput(m.batch, 50) : "",
              purchaseLocation: m.purchaseLocation ? sanitizeInput(m.purchaseLocation, 200) : "",
              datePurchased: m.datePurchased || "",
              notes: m.notes ? sanitizeInput(m.notes, 200) : "",
            };
            setMyBar(prev => [...prev, newItem]);

            if (useSupabase() && currentUser?.uid) db.insertBarItem(currentUser.uid, newItem);
            added++;

          });

          skipped += barImport.matches.filter(m => m.skip).length;
          skipped += barImport.matches.filter(m => !m.skip && !m.manualSelect && !m.match).length;
          setBarImport(prev => ({ ...prev, stats: { added, skipped, dupes, total: prev.matches.length }, step: "done" }));
        };

        const exportBarCsv = () => {

          const header = ["Expression Name","Brand","Brand Type","Proof","Age","Type","MSRP","Price Paid","Fill %","Batch","Mash Bill","Finish","Origin","Distilled By","Permit","Purchase Location","Date Purchased","Notes","Status","Date Emptied","Empty Reason","Limited Release","Discontinued"];

          const rows = myBar.map(item => {

            const b = getBourbon(item.bourbonId);

            const d = b ? getBrand(b.brandId) : null;

            const colaFac = b?.colaPermitNumber ? getFacility(b.colaPermitNumber) : null;

            const status = item.archived ? "Archived" : item.fill === 0 ? "Empty" : "Active";
            return [
              b?.name || "Unknown", d?.name || "", d?.brandType || "",
              b?.proof || "", formatAge(b?.age), b?.type || "", formatMsrp(b?.msrp),
              item.pricePaid ?? "", item.fill ?? 100, item.batch || b?.batch || "",
              b?.mashBill || "", b?.finishType || "", b?.origin || "",
              colaFac ? (colaFac.companyName || colaFac.name) : "", b?.colaPermitNumber || "",
              item.purchaseLocation || "", item.datePurchased || item.dateAdded || "",
              item.notes || "", status, item.emptiedDate || "", item.emptyReason || "",
              b?.limitedRelease ? "Yes" : "", b?.discontinued ? "Yes" : ""
            ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(",");

          });

          const csv = [header.join(","), ...rows].join("\n");

          const blob = new Blob([csv], { type: "text/csv" });

          const url = URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.href = url; a.download = `barrelrank-my-bar-${new Date().toISOString().slice(0,10)}.csv`;
          a.click(); URL.revokeObjectURL(url);
          showToast("✓ Bar exported! (" + myBar.length + " bottles — " + header.length + " attributes)");
        };

        const downloadTemplate = () => {

          const header = "Expression Name,Distillery,Proof,Price Paid,Fill %,Batch,Purchase Location,Date Purchased";

          const examples = [
            '"Buffalo Trace","Buffalo Trace Distillery","90","28","100","","Total Wine","2025-06-15"',
            '"Blanton\'s Single Barrel","Buffalo Trace Distillery","93","65","75","B-924","Local Liquor","2025-08-01"',
            '"Wild Turkey 101","Wild Turkey","101","24","50","","",""'
          ];

          const csv = [header, ...examples].join("\n");

          const blob = new Blob([csv], { type: "text/csv" });

          const url = URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.href = url; a.download = "barrelrank-import-template.csv";
          a.click(); URL.revokeObjectURL(url);
          showToast("✓ Template downloaded!");
        };

        return (
          <div>
            {/* Header */}
            <div style={flexBetweenCenterMb6Style}>
              <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.625rem", color: "#F5E6D0", margin: 0 }}>My Bar</h2>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {barTab === "current" && (<>
                  <button onClick={() => setBarImport(prev => ({ ...prev, open: true }))}
                    style={{ padding: "8px 12px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#B5A898", fontSize: "0.6875rem", fontWeight: 700, cursor: "pointer" }}>
                    {"📥"} Import
                  </button>
                  <button onClick={() => setMenuView(true)}
                    style={{ padding: "8px 12px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#B5A898", fontSize: "0.6875rem", fontWeight: 700, cursor: "pointer" }}>
                    {"📋"} {accountType === "commercial" ? (barProfile.venueType === "retail" ? "Inventory" : "Menu") : "Share"}
                  </button>
                  <button onClick={() => setShowAddToBarModal(true)}
                    style={{ padding: "8px 12px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.6875rem", fontWeight: 700, cursor: "pointer" }}>
                    + Bottle
                  </button>
                </>)}
              </div>
            </div>
            {/* Tabs */}
            <div style={{ display: "flex", gap: 0, marginBottom: 16, borderRadius: 10, overflow: "hidden", border: "1px solid #3D3428" }}>
              {[{ key: "current", label: "Current (" + activeBar.length + ")", icon: "🥃" }, { key: "archive", label: "Archive (" + archivedBar.length + ")", icon: "📦" }].map(tab => (
                <button key={tab.key} onClick={() => { setBarTab(tab.key); if (tab.key === "archive") setBarEditMode(false); }}
                  style={{ flex: 1, padding: "10px 12px", background: barTab === tab.key ? "rgba(212,160,83,0.12)" : "#150F0A", border: "none", cursor: "pointer", transition: "all 0.15s",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    color: barTab === tab.key ? "#D4A053" : "#A69880", fontWeight: barTab === tab.key ? 700 : 400, fontSize: "0.8125rem",
                    borderBottom: barTab === tab.key ? "2px solid #D4A053" : "2px solid transparent" }}>
                  <span style={font0875Style}>{tab.icon}</span> {tab.label}
                </button>
              ))}
            </div>
            {barTab === "current" && (<>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 20 }}>
              {[
                { label: "Bottles", value: activeBar.length },
                { label: "Expressions", value: barDerived.uniqueExpressions },
                { label: "Brands", value: barDerived.uniqueBrands },
              ].map(s => (
                <div key={s.label} style={{ textAlign: "center", padding: "10px 6px", background: "#1E1A14", borderRadius: 10, border: "1px solid #3D3428" }}>
                  <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#D4A053", fontFamily: FONT_DISPLAY, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: "0.5625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 0.8, marginTop: 3 }}>{s.label}</div>
                </div>
              ))}
            </div>
            {/* Add to Bar Modal */}
            {showAddToBarModal && (
              <div style={modalOverlayStyle}>
                <div style={{ background: "#1E1A14", border: "1px solid #5A4F3E", borderRadius: 16, width: "100%", maxWidth: 520, maxHeight: "90vh", overflow: "auto", padding: 32 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                    <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.375rem", color: "#F5E6D0", margin: 0 }}>Add Bottle to Bar</h2>
                    <button onClick={() => { setShowAddToBarModal(false); setBarSearchQ(""); setBarAddOverrides({}); }} style={mutedText15Style} aria-label="Close">✕</button>
                  </div>
                  <div style={formGroupStyle}>
                    <label style={formLabelStyle}>Search Bourbons</label>
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      <div style={{ flex: 1, position: "relative" }}>
                        <input value={barSearchQ} onChange={e => setBarSearchQ(e.target.value)} aria-label="Search bar inventory" placeholder="Type to search by name, distillery, or UPC..." style={{ ...formInputStyle, width: "100%", paddingRight: barSearchQ ? 36 : undefined }} autoFocus />
                        {barSearchQ && <button onClick={() => setBarSearchQ("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#B5A898", cursor: "pointer", fontSize: "1rem", padding: 4 }} aria-label="Clear search">✕</button>}
                      </div>
                      <button onClick={() => setShowBarScanner(true)} style={{ padding: "8px 12px", background: "#3D3428", border: "1px solid #5A4F3E", borderRadius: 8, color: "#D4A053", fontSize: "0.875rem", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }} title="Scan Barcode">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2"/>
                          <line x1="7" y1="7" x2="7" y2="17"/><line x1="10" y1="7" x2="10" y2="17"/>
                          <line x1="13" y1="7" x2="13" y2="17"/><line x1="17" y1="7" x2="17" y2="17"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div style={{ marginBottom: 10, textAlign: "right" }}>
                    <span onClick={() => { setShowAddToBarModal(false); setBarSearchQ(""); setBarAddOverrides({}); setBatchAddMode({ searchQ: "", selected: null, scanning: false, flash: null }); setBatchAddResults([]); setAddFillLevel(100); }}
                      style={{ color: "#B5A898", fontSize: "0.75rem", cursor: "pointer" }}>Adding multiple bottles? <span style={{ color: "#C4A469", fontWeight: 600 }}>Batch mode {"\u2192"}</span></span>
                  </div>
                  {showBarScanner && <BarcodeScanner autoResolve={true} onMatch={async (match) => { setShowBarScanner(false); setPendingScanUpc(null); logScan(match._scannedUpc, true, match.expression_id, "mybar"); const b = bourbons.find(bb => bb.id === match.expression_id); if (b) { setBarSearchQ(b.name || match.name || ""); showToast("Found it! Select below to add."); } else { try { const sb = getSupabase(); if (sb) { const { data: rows } = await sb.from("bourbons").select("*").eq("id", match.expression_id).limit(1); if (rows && rows[0]) { const r = rows[0]; const full = { id: r.id, name: r.name, brandId: r.brand_id, facilityId: r.facility_id, proof: r.proof ? parseFloat(r.proof) : null, age: r.age || "", mashBill: r.mash_bill || "", msrp: r.msrp ? parseFloat(r.msrp) : null, type: r.type || "", finishType: r.finish_type || "", batch: r.batch || "", notes: r.notes || "", limitedRelease: r.limited_release || false, discontinued: r.discontinued || false, userAdded: r.user_added || false, addedBy: r.added_by || null, website: r.website || "", source: r.source || null, origin: r.origin || "", colaTtbId: r.cola_ttb_id || null, colaBrandName: r.cola_brand_name || null, colaClassName: r.cola_class_name || null, colaApprovalDate: r.cola_approval_date || null, imageUrl: r.image_url || null, submissionStatus: r.submission_status || "approved", submittedBy: r.submitted_by || null, submittedAt: r.submitted_at || null, reviewedBy: r.reviewed_by || null, reviewedAt: r.reviewed_at || null, rejectionReason: r.rejection_reason || "", mashCorn: r.mash_corn ?? null, mashWheat: r.mash_wheat ?? null, mashRye: r.mash_rye ?? null, mashBarley: r.mash_barley ?? null, mashOther: r.mash_other ?? null, proofMin: r.proof_min ? parseFloat(r.proof_min) : null, proofMax: r.proof_max ? parseFloat(r.proof_max) : null, tier: r.tier || "archive", popularityScore: r.popularity_score || 0, parentExpressionId: r.parent_expression_id || null, nameLocked: r.name_locked || false, colaProductName: r.cola_product_name || null, colaTastingFlavors: r.cola_tasting_flavors || null, colaDescription: r.cola_description || null, colaGrains: r.cola_grains || null, colaFinishingProcess: r.cola_finishing_process || null, colaBarcodeValue: r.cola_barcode_value || null, colaEnrichedAt: r.cola_enriched_at || null, expressionType: r.expression_type || null, mashbillSource: r.mashbill_source || null }; setBourbons(prev => [...prev, full]); setBarSearchQ(full.name || ""); showToast("Found it! Select below to add."); } else { showToast("Couldn't load this expression. Try searching instead."); } } } catch { showToast("Couldn't load this expression. Try searching instead."); } } }} onNoMatch={(upc) => { setShowBarScanner(false); logScan(upc, false, null, "mybar"); const msg = SCAN_NO_MATCH_MESSAGES[Math.floor(Math.random() * SCAN_NO_MATCH_MESSAGES.length)]; setScanTransition(msg); setTimeout(() => setScanTransition(null), 4000); }} onClose={() => setShowBarScanner(false)} />}
                  {barSearchResults.length > 0 && (
                    <div style={mb20Style}>
                      {barSearchResults.map(b => {

                        const d = getDisplayBrand(b.brandId);

                        const ov = barAddOverrides[b.id] || {};

                        const setOv = (field, val) => setBarAddOverrides(prev => ({ ...prev, [b.id]: { ...prev[b.id], [field]: val } }));

                        return (
                          <div key={b.id} style={{ background: "#150F0A", borderRadius: 10, padding: 14, marginBottom: 8, border: "1px solid #3D3428", cursor: "pointer", transition: "border-color 0.15s" }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = "#D4A053"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
                            <div style={flexBetweenCenterStyle}>
                              <div>
                                <div style={{ fontFamily: FONT_DISPLAY, fontSize: "0.9375rem", color: "#F5E6D0" }}>{getDisplayName(b)}</div>
                                <div style={mutedText075Mt2Style}>{d?.name} · {b.proof}° · {formatMsrp(b.msrp)}</div>
                              </div>
                              <button onClick={(e) => { e.stopPropagation(); addToBar(b); }}
                                style={{ padding: "6px 14px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                                + Add
                              </button>
                            </div>
                            {/* Per-item options */}
                            <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "flex-end" }} onClick={e => e.stopPropagation()}>
                              <div style={flexOneStyle}>
                                <label style={mutedText0625UpperSpacedStyle}>Price Paid</label>
                                <input type="number" value={ov.price ?? ""} onChange={e => setOv("price", e.target.value)} aria-label="Price paid" placeholder={`$${b.msrp}`}
                                  style={{ ...formInputStyle, padding: "6px 8px", fontSize: "0.8125rem", marginTop: 3 }} />
                              </div>
                              <div style={flexOneStyle}>
                                <label style={mutedText0625UpperSpacedStyle}>Batch / Barrel</label>
                                <input value={ov.batch ?? ""} onChange={e => setOv("batch", e.target.value)} aria-label="Batch number" placeholder={b.batch || "e.g. C924"}
                                  style={{ ...formInputStyle, padding: "6px 8px", fontSize: "0.8125rem", marginTop: 3 }} />
                              </div>
                              <div style={flexOneStyle}>
                                <label style={mutedText0625UpperSpacedStyle}>Fill Level</label>
                                <div style={{ display: "flex", gap: 4, marginTop: 3, alignItems: "center" }}>
                                  {[100, 75, 50, 25].map(lv => (
                                    <button key={lv} className="tap-feedback" onClick={() => setOv("fill", lv)}
                                      style={{ flex: 1, padding: "5px 2px", borderRadius: 6, fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", background: (ov.fill ?? 100) === lv ? "#C4A469" : "transparent", color: (ov.fill ?? 100) === lv ? "#1A1409" : "#C4A469", border: `1px solid ${(ov.fill ?? 100) === lv ? "#C4A469" : "rgba(196,164,105,0.3)"}` }}>
                                      {lv}%
                                    </button>
                                  ))}
                                  <input type="number" min={1} max={100} placeholder="%" value={(ov.fill ?? 100) !== 100 && (ov.fill ?? 100) !== 75 && (ov.fill ?? 100) !== 50 && (ov.fill ?? 100) !== 25 ? (ov.fill ?? "") : ""} onChange={e => { const v = Math.min(100, Math.max(1, parseInt(e.target.value) || 1)); setOv("fill", v); }} aria-label="Custom fill level" style={{ width: 44, padding: "4px 2px", borderRadius: 6, border: "1px solid #3A2E25", background: "#150F0A", color: "#F5E6D0", fontSize: "0.75rem", textAlign: "center" }} />
                                </div>
                              </div>
                            </div>
                            {/* Proof + Purchased At */}
                            <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "flex-end" }} onClick={e => e.stopPropagation()}>
                              <div style={{ width: 80 }}>
                                <label style={mutedText0625UpperSpacedStyle}>My Proof</label>
                                <input type="number" step={0.1} min={60} max={160} value={ov.customProof ?? ""} onChange={e => setOv("customProof", e.target.value)} aria-label="Custom proof" placeholder={b.proof ? `${b.proof}` : "Proof"}
                                  style={{ ...formInputStyle, padding: "6px 8px", fontSize: "0.8125rem", marginTop: 3, width: "100%", boxSizing: "border-box" }} />
                              </div>
                              <div style={flexOneStyle}>
                                <label style={mutedText0625UpperSpacedStyle}>Purchased At</label>
                                <input value={ov.purchasedAt ?? ""} onChange={e => setOv("purchasedAt", e.target.value)} aria-label="Purchase location" placeholder="e.g. Total Wine, Lexington KY"
                                  style={{ ...formInputStyle, padding: "6px 8px", fontSize: "0.8125rem", marginTop: 3, width: "100%", boxSizing: "border-box" }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {barSearchQ.trim().length >= 2 && barSearchResults.length === 0 && (
                    <div style={{ textAlign: "center", padding: "24px 0", color: "#B5A898" }}>
                      <p>No matching bourbons found.</p>
                      <button onClick={() => { setShowAddToBarModal(false); setShowAddBourbonModal(true); }}
                        style={{ padding: "8px 16px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#D4A053", fontSize: "0.8125rem", cursor: "pointer", marginTop: 8 }}>
                        + Add a New Expression First
                      </button>
                    </div>
                  )}
                  {barSearchQ.trim().length < 2 && (
                    <div style={{ textAlign: "center", padding: "20px 0", color: "#B5A898", fontSize: "0.8125rem" }}>
                      Type at least 2 characters to search...
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* ── BAR IMPORT MODAL ───────────────────── */}
            {barImport.open && (
              <div style={modalOverlayStyle}>
                <div style={{ background: "#1E1A14", border: "1px solid #5A4F3E", borderRadius: 16, width: "100%", maxWidth: 640, maxHeight: "90vh", overflow: "auto", padding: 32 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                    <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.375rem", color: "#F5E6D0", margin: 0 }}>
                      {barImport.step === "upload" ? "Import / Export" : barImport.step === "import-upload" ? "Import Bar" : barImport.step === "review" ? "Review Matches" : "Import Complete"}
                    </h2>
                    <button onClick={() => { setBarImport({ open: false, matches: [], step: "upload", stats: null }); }}
                      style={mutedText15Style} aria-label="Close">{"✕"}</button>
                  </div>
                  {/* STEP 1: Choose */}
                  {barImport.step === "upload" && (
                    <div>
                      <p style={{ color: "#C4B5A0", fontSize: "0.875rem", lineHeight: 1.6, margin: "0 0 20px" }}>
                        Move your bar inventory into BarrelRank from a spreadsheet, or export your current collection.
                      </p>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
                        {/* Import card */}
                        <button onClick={() => setBarImport(prev => ({ ...prev, step: "import-upload" }))}
                          style={{ background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 12, padding: 24, cursor: "pointer", textAlign: "center", transition: "all 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = "#D4A053"} onMouseLeave={e => e.currentTarget.style.borderColor = "#5A4F3E"}>
                          <div style={{ fontSize: "2.0rem", marginBottom: 8 }}>{"📥"}</div>
                          <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", marginBottom: 6 }}>Import</div>
                          <div style={{ color: "#B5A898", fontSize: "0.75rem", lineHeight: 1.4 }}>Bring your existing bar from a CSV or Excel spreadsheet</div>
                        </button>
                        {/* Export card */}
                        <button onClick={() => { exportBarCsv(); }}
                          style={{ background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 12, padding: 24, cursor: "pointer", textAlign: "center", transition: "all 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = "#D4A053"} onMouseLeave={e => e.currentTarget.style.borderColor = "#5A4F3E"}>
                          <div style={{ fontSize: "2.0rem", marginBottom: 8 }}>{"📤"}</div>
                          <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", marginBottom: 6 }}>Export</div>
                          <div style={{ color: "#B5A898", fontSize: "0.75rem", lineHeight: 1.4 }}>Download your full bar as CSV ({myBar.length} bottle{myBar.length !== 1 ? "s" : ""} — active + archived)</div>
                        </button>
                      </div>
                    </div>
                  )}
                  {/* STEP 1b: Import upload */}
                  {barImport.step === "import-upload" && (
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                        <button onClick={() => setBarImport(prev => ({ ...prev, step: "upload" }))}
                          style={{ background: "none", border: "none", color: "#B5A898", fontSize: "0.875rem", cursor: "pointer", padding: 0 }}>← Back</button>
                      </div>
                      <p style={{ color: "#C4B5A0", fontSize: "0.875rem", lineHeight: 1.6, margin: "0 0 16px" }}>
                        Upload your bar inventory. We'll fuzzy-match each row to our bourbon database so you can review before importing.
                      </p>
                      <div style={{ background: "#150F0A", border: "2px dashed #5A4F3E", borderRadius: 12, padding: 32, textAlign: "center", marginBottom: 20 }}>
                        <div style={{ fontSize: "2.0rem", marginBottom: 8 }}>{"📁"}</div>
                        <label style={{ display: "inline-block", padding: "10px 24px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer" }}>
                          Choose File (.csv or .xlsx)
                          <input type="file" accept=".csv,.xlsx,.xls" aria-label="Import bar inventory file" onChange={handleFileImport} style={{ display: "none" }} />
                        </label>
                        <p style={{ color: "#A69880", fontSize: "0.75rem", margin: "12px 0 0" }}>Or drag and drop your file here</p>
                      </div>
                      <div style={{ background: "#150F0A", borderRadius: 10, padding: 16, border: "1px solid #3D3428" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                          <span style={{ color: "#D4A053", fontSize: "0.8125rem", fontWeight: 700 }}>{"📋"} Column Guide</span>
                          <button onClick={downloadTemplate}
                            style={{ padding: "6px 14px", background: "transparent", border: "1px solid #D4A053", borderRadius: 6, color: "#D4A053", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>
                            {"⬇\uFE0F"} Download Template
                          </button>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px", fontSize: "0.8125rem" }}>
                          <div style={lightTextStyle}><span style={{ color: "#FF6B6B", fontWeight: 700 }}>*</span> Expression Name <span style={{ color: "#FF6B6B", fontSize: "0.625rem", fontWeight: 600 }}>REQUIRED</span></div>
                          <div style={mutedTextStyle}>e.g. "Buffalo Trace"</div>
                          <div style={warmTextStyle}>{"○"} Distillery</div>
                          <div style={mutedTextStyle}>Improves match accuracy</div>
                          <div style={warmTextStyle}>{"○"} Proof</div>
                          <div style={mutedTextStyle}>Helps disambiguate</div>
                          <div style={warmTextStyle}>{"○"} Price Paid</div>
                          <div style={mutedTextStyle}>What you paid ($)</div>
                          <div style={warmTextStyle}>{"○"} Fill %</div>
                          <div style={mutedTextStyle}>0–100, default 100</div>
                          <div style={warmTextStyle}>{"○"} Batch</div>
                          <div style={mutedTextStyle}>Batch or barrel number</div>
                          <div style={warmTextStyle}>{"○"} Purchase Location</div>
                          <div style={mutedTextStyle}>Where you bought it</div>
                          <div style={warmTextStyle}>{"○"} Date Purchased</div>
                          <div style={mutedTextStyle}>YYYY-MM-DD</div>
                        </div>
                        <div style={{ marginTop: 12, padding: "8px 10px", background: "rgba(212,160,83,0.08)", borderRadius: 6 }}>
                          <span style={goldText06875Style}>{"💡"} Tip: Column headers are flexible — we recognize "Name", "Expression", "Producer", "Store", etc.</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* STEP 2: Review Matches */}
                  {barImport.step === "review" && (
                    <div>
                      <div style={flexBetweenCenterMb16Style}>
                        <p style={{ color: "#C4B5A0", fontSize: "0.8125rem", margin: 0 }}>
                          {barImport.matches.length} row{barImport.matches.length !== 1 ? "s" : ""} found · 
                          <span style={{ color: "#4CAF50" }}> {barImport.matches.filter(m => (m.match || m.manualSelect) && !m.skip).length} matched</span> · 
                          <span style={{ color: "#FF6B6B" }}> {barImport.matches.filter(m => !m.match && !m.manualSelect && !m.skip).length} unmatched</span> · 
                          <span style={mutedTextStyle}> {barImport.matches.filter(m => m.skip).length} skipped</span>
                        </p>
                        <button onClick={() => { setBarImport(prev => ({ ...prev, step: "import-upload", matches: [] })); }}
                          style={{ padding: "6px 12px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 6, color: "#B5A898", fontSize: "0.6875rem", cursor: "pointer" }}>
                          ← Back
                        </button>
                      </div>
                      <div style={{ maxHeight: "50vh", overflowY: "auto", marginBottom: 16 }}>
                        {barImport.matches.map((m, idx) => {

                          const resolved = m.manualSelect || m.match;

                          const conf = m.manualSelect ? 100 : m.confidence;

                          const confColor = conf >= 80 ? "#4CAF50" : conf >= 50 ? "#D4A053" : "#FF6B6B";

                          const dist = resolved ? getBrand(resolved.brandId) : null;

                          return (
                            <div key={idx} style={{ background: m.skip ? "#0D0A07" : "#150F0A", borderRadius: 10, padding: 14, marginBottom: 8, border: `1px solid ${m.skip ? "#1A1510" : conf >= 80 ? "rgba(76,175,80,0.2)" : conf >= 50 ? "rgba(212,160,83,0.2)" : "rgba(255,107,107,0.2)"}`, opacity: m.skip ? 0.5 : 1, transition: "all 0.15s" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                                <div style={flexMinW0Style}>
                                  <div style={{ color: "#F5E6D0", fontSize: "0.875rem", fontWeight: 600, marginBottom: 2 }}>
                                    {m.name} {m.distillery && <span style={{ color: "#B5A898", fontWeight: 400 }}>· {m.distillery}</span>}
                                  </div>
                                  {resolved ? (
                                    <div style={font075Style}>
                                      <span style={{ color: confColor, fontWeight: 700 }}>{conf}% match</span>
                                      <span style={mutedTextStyle}> ←’ </span>
                                      <span style={warmTextStyle}>{resolved.name}</span>
                                      <span style={{ color: "#A69880" }}> · {dist?.name} · {resolved.proof}°</span>
                                    </div>
                                  ) : (
                                    <div style={{ fontSize: "0.75rem", color: "#FF6B6B" }}>No match found — select manually or skip</div>
                                  )}
                                </div>
                                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                  <button onClick={() => setBarImport(prev => ({ ...prev, matches: prev.matches.map((x, i) => i === idx ? { ...x, skip: !x.skip } : x) }))}
                                    style={{ padding: "8px 12px", background: m.skip ? "rgba(212,160,83,0.12)" : "rgba(255,107,107,0.08)", border: `1px solid ${m.skip ? "#D4A053" : "rgba(255,107,107,0.3)"}`, borderRadius: 6, color: m.skip ? "#D4A053" : "#FF6B6B", fontSize: "0.6875rem", cursor: "pointer", fontWeight: 600 }}>
                                    {m.skip ? "Unskip" : "Skip"}
                                  </button>
                                </div>
                              </div>
                              {/* Low confidence or no match — show alternatives */}
                              {!m.skip && (conf < 80 || !resolved) && m.alternatives.length > 0 && (
                                <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #3D3428" }}>
                                  <div style={{ color: "#B5A898", fontSize: "0.625rem", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                                    {conf >= 50 ? "Confirm or select alternative:" : "Select match:"}
                                  </div>
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {m.alternatives.map((alt, ai) => {

                                      const selected = m.manualSelect?.id === alt.id || (!m.manualSelect && m.match?.id === alt.id);

                                      return (
                                        <button key={ai} onClick={() => setBarImport(prev => ({ ...prev, matches: prev.matches.map((x, i) => i === idx ? { ...x, manualSelect: alt, skip: false } : x) }))}
                                          style={{ padding: "5px 10px", background: selected ? "rgba(212,160,83,0.15)" : "#0D0A07", border: `1px solid ${selected ? "#D4A053" : "#3D3428"}`, borderRadius: 6, color: selected ? "#D4A053" : "#C4B5A0", fontSize: "0.75rem", cursor: "pointer", transition: "all 0.15s" }}>
                                          {alt.name} <span style={{ color: "#A69880", fontSize: "0.625rem" }}>{alt.distName} · {alt.score}%</span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {/* Optional field summary */}
                              {!m.skip && resolved && (m.price || m.batch || m.fill !== "100") && (
                                <div style={{ marginTop: 8, display: "flex", gap: 12, fontSize: "0.6875rem", color: "#A69880" }}>
                                  {m.price && <span>${m.price}</span>}
                                  {m.fill && m.fill !== "100" && <span>{m.fill}% fill</span>}
                                  {m.batch && <span>Batch: {m.batch}</span>}
                                  {m.purchaseLocation && <span>{m.purchaseLocation}</span>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div style={flexGap10Style}>
                        <button onClick={executeImport}
                          disabled={barImport.matches.filter(m => !m.skip && (m.manualSelect || m.match)).length === 0}
                          style={{ flex: 1, padding: "12px 20px", background: barImport.matches.filter(m => !m.skip && (m.manualSelect || m.match)).length > 0 ? "linear-gradient(135deg, #D4A053, #8B5E34)" : "#3D3428", border: "none", borderRadius: 10, color: "#FFF", fontSize: "0.9375rem", fontWeight: 700, cursor: "pointer" }}>
                          Import {barImport.matches.filter(m => !m.skip && (m.manualSelect || m.match)).length} Bottle{barImport.matches.filter(m => !m.skip && (m.manualSelect || m.match)).length !== 1 ? "s" : ""}
                        </button>
                      </div>
                    </div>
                  )}
                  {/* STEP 3: Done */}
                  {barImport.step === "done" && barImport.stats && (
                    <div style={{ textAlign: "center", padding: "20px 0" }}>
                      <div style={sFonMar2Style}>{"🥃"}</div>
                      <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.5rem", color: "#F5E6D0", margin: "0 0 16px" }}>Import Complete!</h3>
                      <div style={{ display: "flex", justifyContent: "center", gap: 24, marginBottom: 24 }}>
                        <div style={textCenterStyle}>
                          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#4CAF50" }}>{barImport.stats.added}</div>
                          <div style={sFonCol4Style}>Added</div>
                        </div>
                        {barImport.stats.dupes > 0 && (
                          <div style={textCenterStyle}>
                            <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#D4A053" }}>{barImport.stats.dupes}</div>
                            <div style={sFonCol4Style}>Already in bar</div>
                          </div>
                        )}
                        {barImport.stats.skipped > 0 && (
                          <div style={textCenterStyle}>
                            <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#B5A898" }}>{barImport.stats.skipped}</div>
                            <div style={sFonCol4Style}>Skipped</div>
                          </div>
                        )}
                      </div>
                      <button onClick={() => { setBarImport({ open: false, matches: [], step: "upload", stats: null }); }}
                        style={{ padding: "12px 32px", background: GRAD_PRIMARY, border: "none", borderRadius: 10, color: "#FFF", fontSize: "0.9375rem", fontWeight: 700, cursor: "pointer" }}>
                        Done
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Search & Sort */}
            {activeBar.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={posRelMb10Style}>
                  <span style={searchIconStyle}>{"🔍"}</span>
                  <input value={barFilterQ} onChange={e => setBarFilterQ(e.target.value)} aria-label="Filter bar" placeholder="Search by name, distillery, batch..."
                    style={{ width: "100%", padding: "10px 36px 10px 36px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 10, color: "#F5E6D0", fontSize: "0.875rem", boxSizing: "border-box" }} />
                  {barFilterQ && (
                    <button onClick={() => setBarFilterQ("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#B5A898", fontSize: "1.0rem", cursor: "pointer" }} aria-label="Close">{"✕"}</button>
                  )}
                </div>
                {/* Column Headers */}
                <div style={{ display: "flex", alignItems: "center", padding: "0 16px 8px", gap: 14 }}>
                  <div style={{ width: 42, flexShrink: 0 }} />
                  {[
                    { key: "name", label: "Name", flex: 1 },
                    { key: "proof", label: "Proof", w: 48 },
                    { key: "fill", label: "Fill", w: 42 },
                    { key: "price", label: "Paid", w: 52 },
                  ].map(col => {

                    const active = barSort.key === col.key;

                    return (
                      <div key={col.key} onClick={() => setBarSort(prev => prev.key === col.key ? { key: col.key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key: col.key, dir: col.key === "name" ? "asc" : "desc" })}
                        style={{ width: col.w, flex: col.flex || undefined, fontSize: "0.625rem", color: active ? "#D4A053" : "#A69880", textTransform: "uppercase", letterSpacing: 1, cursor: "pointer", userSelect: "none", display: "flex", alignItems: "center", gap: 3, fontWeight: active ? 700 : 400, transition: "color 0.15s", textAlign: col.key === "name" ? "left" : "right", justifyContent: col.key === "name" ? "flex-start" : "flex-end" }}>
                        {col.label}
                        <span style={{ fontSize: "0.5rem", opacity: active ? 1 : 0.3 }}>{active ? (barSort.dir === "asc" ? "▲" : "\u25BC") : "▲"}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Bottle List */}
            {(() => {

              const q = barFilterQ.toLowerCase().trim();

              const filtered = q.length > 0 ? activeBar.filter(item => {

                const b = getBourbon(item.bourbonId);

                const d = b ? getBrand(b.brandId) : null;

                return (b?.name || "").toLowerCase().includes(q) || (getDisplayName(b) || "").toLowerCase().includes(q) || (d?.name || "").toLowerCase().includes(q) || (item.batch || "").toLowerCase().includes(q) || (b?.type || "").toLowerCase().includes(q) || (item.purchasedAt || "").toLowerCase().includes(q);
              }) : activeBar;

              const sorted = [...filtered].sort((a, z) => {

                // Featured items always sort to top
                if (a.featured && !z.featured) return -1;
                if (!a.featured && z.featured) return 1;

                const bA = getBourbon(a.bourbonId); const bZ = getBourbon(z.bourbonId);

                const dA = bA ? getBrand(bA.brandId) : null; const dZ = bZ ? getBrand(bZ.brandId) : null;
                let cmp = 0;
                if (barSort.key === "name") cmp = (getDisplayName(bA) || "").localeCompare(getDisplayName(bZ) || "");
                else if (barSort.key === "proof") cmp = (bA?.proof || 0) - (bZ?.proof || 0);
                else if (barSort.key === "fill") cmp = (a.fill || 0) - (z.fill || 0);
                else if (barSort.key === "price") cmp = (a.pricePaid || 0) - (z.pricePaid || 0);
                return barSort.dir === "desc" ? -cmp : cmp;

              });

              if (q && sorted.length === 0) return (
                <div style={{ textAlign: "center", padding: "24px 16px", color: "#B5A898" }}>
                  <p style={{ margin: "0 0 8px", fontSize: "0.875rem" }}>No bottles match "{barFilterQ}"</p>
                  <button onClick={() => setBarFilterQ("")} style={{ padding: "6px 14px", background: "transparent", border: "1px solid #3D3428", borderRadius: 8, color: "#B5A898", fontSize: "0.75rem", cursor: "pointer" }}>Clear Search</button>
                </div>
              );

            // Group bottles by bourbonId

            const grouped = {};
            sorted.forEach(item => {
              if (!grouped[item.bourbonId]) grouped[item.bourbonId] = [];
              grouped[item.bourbonId].push(item);

            });

            const sortedGroupKeys = [...new Set(sorted.map(s => s.bourbonId))];
            return sortedGroupKeys.map(bourbonId => {

              const bottles = grouped[bourbonId];

              const b = getBourbon(bourbonId);
              if (!b) return null;

              const d = getDisplayBrand(b.brandId);

              const count = bottles.length;

              const isMulti = count > 1;

              const avgFill = Math.round(bottles.reduce((s, bt) => s + (bt.fill || 0), 0) / count);

              const totalSpent = bottles.reduce((s, bt) => s + (bt.pricePaid || 0), 0);

              const isExpanded = barQuickFill === bourbonId;

              // ── SINGLE BOTTLE (flat, clean) ──
              if (!isMulti) {

                const item = bottles[0];

                const i = myBar.indexOf(item);


                const myCheckins = getCheckins(bourbonId);

                const myAvg = myCheckins.length > 0 ? (myCheckins.reduce((s, c) => s + c.rating, 0) / myCheckins.length).toFixed(1) : null;

                return (
                  <div key={bourbonId} className="tap-feedback" style={{ background: "#1E1A14", borderRadius: 12, marginBottom: 10, border: "1px solid " + (isExpanded ? "#5A4F3E" : "#3D3428"), transition: "all 0.2s", overflow: "hidden" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: 16, cursor: "pointer" }}
                      onClick={() => setBarQuickFill(isExpanded ? null : bourbonId)}>
                      <FillLevel percent={item.fill} showLabel />
                      <div style={flexOneStyle}>
                        <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0" }}>
                          {item.featured && <span style={{ color: "#D4A053", marginRight: 4 }}>{"\u2605"}</span>}{getDisplayName(b)}
                        </div>
                        <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginTop: 3 }}>
                          {d?.name} · {b?.proof}°{item.batch ? " · " + item.batch : ""}
                          {myAvg && <span style={{ marginLeft: 6 }}><span style={goldText06875Style}>★</span> <span style={goldText075BoldStyle}>{fmtRating(parseFloat(myAvg))}</span></span>}
                          {getAvgRating(bourbonId) && <span style={{ marginLeft: myAvg ? 2 : 6 }}>{myAvg ? <span style={dimText06875Style}>/</span> : <span style={goldText06875Style}>★</span>} <span style={{ color: "#A69880", fontSize: "0.75rem", fontWeight: 600 }}>{fmtRating(parseFloat(getAvgRating(bourbonId)))}</span></span>}
                          {b && getSubmissionStatus(b) !== "approved" && <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 10, fontSize: "0.5625rem", fontWeight: 600, background: "rgba(255,193,7,0.1)", color: "#FFC107", border: "1px solid rgba(255,193,7,0.25)" }}>⚠️ Unverified</span>}
                        </div>
                        {(() => { try { const nm = item.notes ? JSON.parse(item.notes) : null; if (!nm) return null; return (<div style={{ marginTop: 2 }}>{nm.custom_proof && nm.custom_proof !== b?.proof && <span style={{ fontSize: "0.6875rem", color: "#D4A574" }}>{nm.custom_proof}° (my bottle)</span>}{nm.barrel_info && <span style={{ fontSize: "0.6875rem", color: "#A69880", marginLeft: nm.custom_proof ? 8 : 0 }}>Barrel #{nm.barrel_info}</span>}</div>); } catch { return null; } })()}
                      </div>
                      <div style={textRightShrinkStyle}>
                        <div style={{ color: "#D4A053", fontWeight: 700, fontSize: "1.0rem" }}>${(item.pricePaid || 0).toFixed(0)}</div>
                        <div style={mutedText06875Style}>MSRP {formatMsrp(b?.msrp)}</div>
                      </div>
                      <span style={{ color: "#B5A898", fontSize: "0.875rem", transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "none", flexShrink: 0 }}>›</span>
                    </div>
                    {isExpanded && (
                      <div style={{ padding: "0 16px 16px", borderTop: "1px solid #3D3428" }}>
                        <div style={{ paddingTop: 14 }}>
                          {/* Quick actions row */}
                          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                            <button onClick={() => { setSelectedBourbon(b); navigateTo("bourbon-detail"); }}
                              style={{ padding: "6px 12px", background: "rgba(212,160,83,0.08)", border: "1px solid rgba(212,160,83,0.2)", borderRadius: 8, color: "#D4A053", fontSize: "0.75rem", cursor: "pointer" }}>
                              View Expression
                            </button>
                            <button onClick={() => { setCheckinBourbon(b); setShowCheckinModal(true); }}
                              style={{ padding: "6px 12px", background: "rgba(212,160,83,0.08)", border: "1px solid rgba(212,160,83,0.2)", borderRadius: 8, color: "#D4A053", fontSize: "0.75rem", cursor: "pointer" }}>
                              + Check In
                            </button>
                            <button onClick={() => {
                              const newFeatured = !item.featured;
                              setMyBar(prev => prev.map((it, idx) => idx === i ? { ...it, featured: newFeatured } : it));
                              if (item.id) db.updateBarItem(item.id, { featured: newFeatured });
                              showToast(newFeatured ? "Marked as featured!" : "Removed from featured");
                            }}
                              style={{ padding: "6px 12px", background: item.featured ? "rgba(212,160,83,0.15)" : "rgba(212,160,83,0.08)", border: `1px solid ${item.featured ? "#D4A053" : "rgba(212,160,83,0.2)"}`, borderRadius: 8, color: "#D4A053", fontSize: "0.75rem", cursor: "pointer", marginLeft: "auto" }}
                              aria-label={item.featured ? "Remove from featured" : "Mark as featured"}>
                              {item.featured ? "\u2605 Featured" : "\u2606 Feature"}
                            </button>
                          </div>
                          <div style={mb14Style}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                              <label style={{ color: "#B5A898", fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: 1.5 }}>Fill Level</label>
                              <span style={{ color: "#D4A053", fontSize: "1.0rem", fontWeight: 700 }}>{item.fill}%</span>
                            </div>
                            <input type="range" min="0" max="100" step="5" value={snap5(item.fill)} aria-label="Bottle fill level"
                              onChange={e => updateFill(i, parseInt(e.target.value))}
                              style={{ width: "100%", accentColor: "#D4A053", height: 6, cursor: "pointer" }} />
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                            <div>
                              <label style={sectionLabelSmStyle}>Price Paid ($)</label>
                              <input type="number" aria-label="Price paid" value={item.pricePaid || ""} onChange={e => updatePrice(i, e.target.value)}
                                style={selectInputStyle} />
                            </div>
                            <div>
                              <label style={sectionLabelSmStyle}>Date Purchased</label>
                              <input type="date" aria-label="Date purchased" value={item.datePurchased || ""} onChange={e => {
                                setMyBar(prev => prev.map((it, idx) => idx === i ? { ...it, datePurchased: e.target.value } : it));
                                if (item.id) db.updateBarItem(item.id, { datePurchased: e.target.value });

                              }} style={selectInputStyle} />
                            </div>
                          </div>
                          <div style={mb14Style}>
                            <label style={sectionLabelSmStyle}>Purchase Location</label>
                            <input aria-label="Purchase location" value={item.purchaseLocation || ""} onChange={e => {
                              const v = e.target.value;
                              setMyBar(prev => prev.map((it, idx) => idx === i ? { ...it, purchaseLocation: v } : it));
                              if (item.id) { clearTimeout(barWriteTimer.current[item.id + 'loc']); barWriteTimer.current[item.id + 'loc'] = setTimeout(() => db.updateBarItem(item.id, { purchaseLocation: v }), 800); }
                            }} placeholder="e.g. Total Wine, Lexington KY"
                              style={selectInputStyle} />
                          </div>
                          <div style={mb14Style}>
                            <label style={sectionLabelSmStyle}>Notes</label>
                            <input aria-label="Notes" value={item.notes || ""} onChange={e => {
                              const v = e.target.value;
                              setMyBar(prev => prev.map((it, idx) => idx === i ? { ...it, notes: v } : it));
                              if (item.id) { clearTimeout(barWriteTimer.current[item.id + 'note']); barWriteTimer.current[item.id + 'note'] = setTimeout(() => db.updateBarItem(item.id, { notes: v }), 800); }
                            }} placeholder="Personal notes"
                              style={selectInputStyle} />
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "space-between" }}>
                            <button onClick={() => setDeleteConfirm({ idx: i, bourbonId, isArchived: false })}
                              style={{ padding: "6px 12px", background: "rgba(178,34,34,0.1)", border: "1px solid rgba(178,34,34,0.25)", borderRadius: 8, color: "#CD5C5C", fontSize: "0.75rem", cursor: "pointer" }}>
                              ✕ Remove Bottle
                            </button>
                            <button onClick={(e) => {
                              e.stopPropagation();

                              const newItem = { bourbonId, pricePaid: b.msrp || 0, fill: addFillLevel, dateAdded: new Date().toISOString().slice(0, 10) };
                              setMyBar(prev => [...prev, newItem]);

                              if (useSupabase() && currentUser?.uid) db.insertBarItem(currentUser.uid, newItem);
                              setAddFillLevel(100);
                              showToast("Second bottle added!");
                            }} style={{ padding: "6px 12px", background: "transparent", border: "1px dashed #5A4F3E", borderRadius: 8, color: "#B5A898", fontSize: "0.75rem", cursor: "pointer", transition: "all 0.15s" }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = "#D4A053"; e.currentTarget.style.color = "#D4A053"; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = "#5A4F3E"; e.currentTarget.style.color = "#B5A898"; }}>
                              + Add Another
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );

              }

              // ── MULTIPLE BOTTLES (accordion with Bottle 1, 2, etc.) ──

              const myCheckinsMulti = getCheckins(bourbonId);

              const myAvgMulti = myCheckinsMulti.length > 0 ? (myCheckinsMulti.reduce((s, c) => s + c.rating, 0) / myCheckinsMulti.length).toFixed(1) : null;

              return (
                <div key={bourbonId} className="tap-feedback" style={{ background: "#1E1A14", borderRadius: 12, marginBottom: 10, border: "1px solid " + (isExpanded ? "#5A4F3E" : "#3D3428"), transition: "all 0.2s", overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, padding: 16, cursor: "pointer" }}
                    onClick={() => { setBarQuickFill(isExpanded ? null : bourbonId); }}>
                    <FillLevel percent={avgFill} showLabel />
                    <div style={flexOneStyle}>
                      <div style={flexCenterGap8Style}>
                        <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0" }}>
                          {getDisplayName(b)}
                        </div>
                        <span style={{ fontSize: "0.6875rem", fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: "rgba(212,160,83,0.15)", color: "#D4A053", border: "1px solid rgba(212,160,83,0.25)" }}>×{count}</span>
                      </div>
                      <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginTop: 3 }}>{d?.name} · {b?.proof}°{myAvgMulti && <span style={{ marginLeft: 6 }}><span style={goldText06875Style}>★</span> <span style={goldText075BoldStyle}>{fmtRating(parseFloat(myAvgMulti))}</span></span>}{getAvgRating(bourbonId) && <span style={{ marginLeft: myAvgMulti ? 2 : 6 }}>{myAvgMulti ? <span style={dimText06875Style}>/</span> : <span style={goldText06875Style}>★</span>} <span style={{ color: "#A69880", fontSize: "0.75rem", fontWeight: 600 }}>{fmtRating(parseFloat(getAvgRating(bourbonId)))}</span></span>}{b && getSubmissionStatus(b) !== "approved" && <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 10, fontSize: "0.5625rem", fontWeight: 600, background: "rgba(255,193,7,0.1)", color: "#FFC107", border: "1px solid rgba(255,193,7,0.25)" }}>⚠️ Unverified</span>}</div>
                    </div>
                    <div style={textRightShrinkStyle}>
                      <div style={{ color: "#D4A053", fontWeight: 700, fontSize: "1.0rem" }}>${totalSpent.toFixed(0)}</div>
                      <div style={mutedText06875Style}>MSRP {formatMsrp(b?.msrp)} ea</div>
                    </div>
                    <span style={{ color: "#B5A898", fontSize: "0.875rem", transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "none", flexShrink: 0 }}>›</span>
                  </div>
                  {isExpanded && (
                    <div style={{ padding: "0 16px 16px", borderTop: "1px solid #3D3428" }}>
                      {/* Quick actions row */}
                      <div style={{ display: "flex", gap: 8, paddingTop: 12, marginBottom: 6 }}>
                        <button onClick={() => { setSelectedBourbon(b); navigateTo("bourbon-detail"); }}
                          style={{ padding: "6px 12px", background: "rgba(212,160,83,0.08)", border: "1px solid rgba(212,160,83,0.2)", borderRadius: 8, color: "#D4A053", fontSize: "0.75rem", cursor: "pointer" }}>
                          View Expression
                        </button>
                        <button onClick={() => { setCheckinBourbon(b); setShowCheckinModal(true); }}
                          style={{ padding: "6px 12px", background: "rgba(212,160,83,0.08)", border: "1px solid rgba(212,160,83,0.2)", borderRadius: 8, color: "#D4A053", fontSize: "0.75rem", cursor: "pointer" }}>
                          + Check In
                        </button>
                      </div>
                      {bottles.map((item, bi) => {

                        const getIdx = () => { const idx = myBar.findIndex(x => x.id ? x.id === item.id : x.bourbonId === item.bourbonId && x.dateAdded === item.dateAdded && x.pricePaid === item.pricePaid); return idx >= 0 ? idx : 0; };

                        const itemKey = item.id || (bourbonId + "-" + bi);


                        const isBottleOpen = subView === ("bottle-" + itemKey);

                        return (
                          <div key={itemKey} style={{ padding: "14px 0", borderBottom: bi < bottles.length - 1 ? "1px solid #1A1510" : "none" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}
                              onClick={() => setSubView(isBottleOpen ? null : "bottle-" + itemKey)}>
                              <FillLevel percent={item.fill} showLabel size="sm" />
                              <div style={flexOneStyle}>
                                <div style={lightText08125BoldStyle}>
                                  Bottle {bi + 1}{item.batch ? " · " + item.batch : ""}
                                </div>
                                <div style={mutedText06875Mt2Style}>
                                  ${item.pricePaid || 0} paid{item.purchaseLocation ? " · " + item.purchaseLocation : ""}{item.notes ? " · " + item.notes : ""}
                                </div>
                              </div>
                              <span style={{ color: "#B5A898", fontSize: "0.75rem", transition: "transform 0.2s", transform: isBottleOpen ? "rotate(90deg)" : "none" }}>›</span>
                            </div>
                            {isBottleOpen && (
                              <div style={{ marginTop: 12, padding: 14, background: "#150F0A", borderRadius: 10, border: "1px solid #3D3428" }} onClick={e => e.stopPropagation()}>
                                <div style={mb14Style}>
                                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                    <label style={{ color: "#B5A898", fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: 1.5 }}>Fill Level</label>
                                    <span style={{ color: "#D4A053", fontSize: "1.0rem", fontWeight: 700 }}>{item.fill}%</span>
                                  </div>
                                  <input type="range" min="0" max="100" step="5" value={snap5(item.fill)} aria-label="Bottle fill level"
                                    onChange={e => updateFill(getIdx(), parseInt(e.target.value))}
                                    style={{ width: "100%", accentColor: "#D4A053", height: 6, cursor: "pointer" }} />
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                                  <div>
                                    <label style={sectionLabelSmStyle}>Price Paid ($)</label>
                                    <input type="number" aria-label="Price paid" value={item.pricePaid || ""} onChange={e => updatePrice(getIdx(), e.target.value)}
                                      style={selectInputStyle} />
                                  </div>
                                  <div>
                                    <label style={sectionLabelSmStyle}>Date Purchased</label>
                                    <input type="date" aria-label="Date purchased" value={item.datePurchased || ""} onChange={e => {

                                      const idx = getIdx(); setMyBar(prev => prev.map((it, j) => j === idx ? { ...it, datePurchased: e.target.value } : it));
                                      if (item.id) db.updateBarItem(item.id, { datePurchased: e.target.value });

                                    }} style={selectInputStyle} />
                                  </div>
                                </div>
                                <div style={mb14Style}>
                                  <label style={sectionLabelSmStyle}>Purchase Location</label>
                                  <input aria-label="Purchase location" value={item.purchaseLocation || ""} onChange={e => {
                                    const v = e.target.value; const idx = getIdx(); setMyBar(prev => prev.map((it, j) => j === idx ? { ...it, purchaseLocation: v } : it));
                                    if (item.id) { clearTimeout(barWriteTimer.current[item.id + 'loc']); barWriteTimer.current[item.id + 'loc'] = setTimeout(() => db.updateBarItem(item.id, { purchaseLocation: v }), 800); }
                                  }} placeholder="e.g. Total Wine, Lexington KY"
                                    style={selectInputStyle} />
                                </div>
                                <div style={mb14Style}>
                                  <label style={sectionLabelSmStyle}>Notes</label>
                                  <input aria-label="Notes" value={item.notes || ""} onChange={e => {
                                    const v = e.target.value; const idx = getIdx(); setMyBar(prev => prev.map((it, j) => j === idx ? { ...it, notes: v } : it));
                                    if (item.id) { clearTimeout(barWriteTimer.current[item.id + 'note']); barWriteTimer.current[item.id + 'note'] = setTimeout(() => db.updateBarItem(item.id, { notes: v }), 800); }
                                  }} placeholder="Personal notes"
                                    style={selectInputStyle} />
                                </div>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                  <button onClick={() => setDeleteConfirm({ idx: getIdx(), bourbonId, isArchived: false })}
                                    style={{ padding: "6px 12px", background: "rgba(178,34,34,0.1)", border: "1px solid rgba(178,34,34,0.25)", borderRadius: 8, color: "#CD5C5C", fontSize: "0.75rem", cursor: "pointer" }}>
                                    ✕ Remove Bottle
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <button onClick={(e) => {
                        e.stopPropagation();

                        const newItem = { bourbonId, pricePaid: b.msrp || 0, fill: addFillLevel, dateAdded: new Date().toISOString().slice(0, 10) };
                        setMyBar(prev => [...prev, newItem]);

                        if (useSupabase() && currentUser?.uid) db.insertBarItem(currentUser.uid, newItem);
                        setAddFillLevel(100);
                        showToast("Another bottle added!");
                      }} style={{ marginTop: 10, padding: "8px 14px", background: "transparent", border: "1px dashed #5A4F3E", borderRadius: 8, color: "#B5A898", fontSize: "0.75rem", cursor: "pointer", width: "100%", transition: "all 0.15s" }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = "#D4A053"; e.currentTarget.style.color = "#D4A053"; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = "#5A4F3E"; e.currentTarget.style.color = "#B5A898"; }}>
                        + Add Another Bottle
                      </button>
                    </div>
                  )}
                </div>
              );

            });

            })()}
            {dataLoading && activeBar.length === 0 && <>{[0,1,2,3].map(i => <SkeletonCard key={i} />)}</>}
            {!dataLoading && activeBar.length === 0 && (
              <div style={{ textAlign: "center", padding: "48px 20px", color: "#B5A898" }}>
                <div style={sFonMar2Style}>🍾</div>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.25rem", color: "#F5E6D0", margin: "0 0 8px" }}>Your Bar is Empty</h3>
                <p style={{ fontSize: "0.875rem", margin: "0 0 20px", lineHeight: 1.6 }}>Add the bottles on your shelf to track fill levels, value, and build your personal bourbon collection.</p>
                <button onClick={() => setShowAddToBarModal(true)}
                  style={{ padding: "12px 24px", background: GRAD_PRIMARY, border: "none", borderRadius: 10, color: "#1A1409", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer", marginBottom: 8 }}>
                  Add Your First Bottle
                </button>
                <p style={{ fontSize: "0.75rem", margin: 0, color: "#A69880" }}>Tip: Bottles also get added when you check in</p>
              </div>
            )}
            </>)}
            {/* Empty Bottle Prompt Modal */}
            {emptyBottlePrompt && (() => {

              const b = getBourbon(emptyBottlePrompt.bourbonId);

              return (
                <div style={modalOverlayStyle}>
                  <div style={{ background: "#1E1A14", border: "1px solid #5A4F3E", borderRadius: 16, width: "100%", maxWidth: 400, padding: 28 }}>
                    <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.25rem", color: "#F5E6D0", margin: "0 0 6px", textAlign: "center" }}>Bottle Empty!</h3>
                    <p style={{ color: "#B5A898", fontSize: "0.8125rem", textAlign: "center", margin: "0 0 20px" }}>
                      How did <span style={{ color: "#D4A053", fontWeight: 600 }}>{getDisplayName(b)}</span> meet its end?
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {[
                        { key: "kill", icon: "🎉", label: "Bottle Kill", desc: "Finished to the last drop!", color: "#D4A053" },
                        { key: "giveaway", icon: "🎁", label: "Gave It Away", desc: "Passed along to a fellow enthusiast", color: "#7CB9E8" },
                        { key: "drain", icon: "🚫", label: "Drain Pour", desc: "Not every bottle is a winner", color: "#CD5C5C" },
                      ].map(opt => (
                        <button key={opt.key} onClick={() => handleEmptyBottleChoice(opt.key)}
                          style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 12, cursor: "pointer", textAlign: "left", transition: "all 0.15s", width: "100%" }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = opt.color; e.currentTarget.style.background = opt.color + "11"; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = "#3D3428"; e.currentTarget.style.background = "#150F0A"; }}>
                          <span style={{ fontSize: "1.75rem", flexShrink: 0 }}>{opt.icon}</span>
                          <div>
                            <div style={{ color: opt.color, fontSize: "0.9375rem", fontWeight: 700 }}>{opt.label}</div>
                            <div style={mutedText075Mt2Style}>{opt.desc}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setEmptyBottlePrompt(null)}
                      style={{ width: "100%", marginTop: 14, padding: "10px", background: "transparent", border: "1px solid #3D3428", borderRadius: 8, color: "#B5A898", fontSize: "0.8125rem", cursor: "pointer" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              );
            })()}
            {/* Delete Confirmation Modal */}
            {deleteConfirm && (() => {

              const b = getBourbon(deleteConfirm.bourbonId);

              const item = myBar[deleteConfirm.idx];
              if (!b || !item) { setDeleteConfirm(null); return null; }

              const isArchived = deleteConfirm.isArchived;

              return (
                <div style={modalOverlayStyle}>
                  <div style={{ background: "#1E1A14", border: "1px solid #5A4F3E", borderRadius: 16, width: "100%", maxWidth: 420, padding: 28 }}>
                    <div style={flexBetweenCenterMb16Style}>
                      <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.25rem", color: "#F5E6D0", margin: 0 }}>Remove Bottle</h3>
                      <button onClick={() => setDeleteConfirm(null)}
                        style={{ background: "none", border: "none", color: "#B5A898", fontSize: "1.25rem", cursor: "pointer" }} aria-label="Close">{"✕"}</button>
                    </div>
                    <div style={{ textAlign: "center", marginBottom: 20 }}>
                      <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#D4A053", marginBottom: 4 }}>{getDisplayName(b)}</div>
                      {item && !isArchived && <div style={mutedText08125Style}>{item.fill}% remaining</div>}
                    </div>
                    {isArchived ? (
                      <div>
                        <p style={{ color: "#C4B5A0", fontSize: "0.8125rem", lineHeight: 1.6, margin: "0 0 20px", textAlign: "center" }}>
                          This will permanently delete this archived bottle from your history. This cannot be undone.
                        </p>
                        <div style={flexGap10Style}>
                          <button onClick={() => setDeleteConfirm(null)}
                            style={{ flex: 1, padding: "12px", background: "transparent", border: "1px solid #3D3428", borderRadius: 10, color: "#B5A898", fontSize: "0.875rem", cursor: "pointer" }}>
                            Keep
                          </button>
                          <button onClick={() => { if (!window.confirm(`Permanently delete ${getDisplayName(b)} from your bar? This cannot be undone.`)) return; removeFromBar(deleteConfirm.idx); setDeleteConfirm(null); showToast("Bottle removed from history"); }}
                            style={{ flex: 1, padding: "12px", background: "rgba(178,34,34,0.15)", border: "1px solid rgba(178,34,34,0.3)", borderRadius: 10, color: "#CD5C5C", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer" }}>
                            Delete Permanently
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p style={{ color: "#C4B5A0", fontSize: "0.8125rem", lineHeight: 1.6, margin: "0 0 16px", textAlign: "center" }}>
                          Archive this bottle to keep it in your history, or delete it permanently.
                        </p>
                        <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                          <button onClick={() => {
                            const idx = deleteConfirm.idx;
                            setMyBar(prev => prev.map((it, i) => i === idx ? { ...it, emptyReason: "Archived", emptiedDate: new Date().toISOString().slice(0, 10) } : it));
                            if (useSupabase() && myBar[idx]?.id) db.updateBarItem(myBar[idx].id, { emptyReason: "Archived" });
                            setDeleteConfirm(null);
                            showToast("Bottle archived");
                          }}
                            style={{ flex: 1, padding: "12px", background: GRAD_PRIMARY, border: "none", borderRadius: 10, color: "#1A1409", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer", minHeight: 44 }}>
                            Archive Bottle
                          </button>
                          <button onClick={() => setDeleteConfirm(null)}
                            style={{ flex: 1, padding: "12px", background: "transparent", border: "1px solid #3D3428", borderRadius: 10, color: "#B5A898", fontSize: "0.875rem", cursor: "pointer", minHeight: 44 }}>
                            Cancel
                          </button>
                        </div>
                        <div style={textCenterStyle}>
                          <button onClick={() => { if (!window.confirm(`Permanently delete ${getDisplayName(b)} from your bar? This cannot be undone.`)) return; removeFromBar(deleteConfirm.idx); setDeleteConfirm(null); showToast("Bottle deleted"); }}
                            style={{ color: "#ff6b6b", fontSize: "0.8rem", background: "none", border: "none", cursor: "pointer", marginTop: 8 }}>
                            Delete permanently instead
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            {/* Archive Tab */}
            {barTab === "archive" && (<>
              {(() => {

                const kills = archivedBar.filter(i => (i.emptyReason || "").includes("Kill")).length;

                const gifts = archivedBar.filter(i => (i.emptyReason || "").includes("Gave")).length;

                const drains = archivedBar.filter(i => (i.emptyReason || "").includes("Drain")).length;

                const totalSpent = archivedBar.reduce((s, i) => s + (i.pricePaid || 0), 0);

                return <p style={{ color: "#B5A898", fontSize: "0.8125rem", margin: "0 0 14px", lineHeight: 1.6 }}>
                  {archivedBar.length} finished bottle{archivedBar.length !== 1 ? "s" : ""} {" \u00B7 $"}{totalSpent} total
                  {archivedBar.length > 0 && <span style={{ display: "block", fontSize: "0.75rem", color: "#A69880", marginTop: 2 }}>{"🎉"} {kills} killed {"\u00B7"} {"🎁"} {gifts} gifted {"\u00B7"} {"🚫"} {drains} drained</span>}
                </p>;
              })()}
              {archivedBar.length > 0 && (<>
                <div style={posRelMb10Style}>
                  <span style={searchIconStyle}>{"🔍"}</span>
                  <input value={archiveSearch} onChange={e => setArchiveSearch(e.target.value)} aria-label="Search archive" placeholder="Search by name, distillery, reason..."
                    style={{ width: "100%", padding: "10px 36px 10px 36px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 10, color: "#F5E6D0", fontSize: "0.875rem", boxSizing: "border-box" }} />
                  {archiveSearch && (
                    <button onClick={() => setArchiveSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#B5A898", fontSize: "1.0rem", cursor: "pointer" }} aria-label="Close">{"✕"}</button>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                  {[{ key: "", label: "All", icon: "📦" }, { key: "Kill", label: "Bottle Kills", icon: "🎉" }, { key: "Gave", label: "Given Away", icon: "🎁" }, { key: "Drain", label: "Drain Pours", icon: "🚫" }].map(f => (
                    <button key={f.key} onClick={() => setArchiveFilter(f.key)}
                      style={{ padding: "5px 12px", borderRadius: 16, border: "1px solid", fontSize: "0.75rem", cursor: "pointer", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 5,
                        background: archiveFilter === f.key ? "rgba(212,160,83,0.15)" : "transparent", color: archiveFilter === f.key ? "#D4A053" : "#A69880", borderColor: archiveFilter === f.key ? "#D4A053" : "#3D3428", fontWeight: archiveFilter === f.key ? 700 : 400 }}>
                      <span style={font08125Style}>{f.icon}</span> {f.label}
                    </button>
                  ))}
                </div>
              </>)}
              {(archiveSearch || archiveFilter) && archiveDisplayed.length > 0 && (
                <div style={{ color: "#A69880", fontSize: "0.75rem", marginBottom: 10 }}>{archiveDisplayed.length} of {archivedBar.length} bottles</div>
              )}
              {archiveDisplayed.length > 0 ? archiveDisplayed.map((item) => {

                const b = getBourbon(item.bourbonId);

                const d = b ? getBrand(b.brandId) : null;

                const rc = (item.emptyReason || "").includes("Kill") ? "#D4A053" : (item.emptyReason || "").includes("Gave") ? "#7CB9E8" : "#CD5C5C";

                const archKey = "arch-" + item.bourbonId + "-" + item.emptiedDate + "-" + item.dateAdded;

                const isExpanded = expandedArchiveItem === archKey;

                const myRating = getCheckins(item.bourbonId);

                const personalAvg = myRating.length > 0 ? (myRating.reduce((s, c) => s + c.rating, 0) / myRating.length).toFixed(2) : null;

                const globalAvg = getAvgRating(item.bourbonId);

                return (
                  <div key={archKey} style={{ background: "#1E1A14", borderRadius: 12, marginBottom: 10, border: "1px solid " + (isExpanded ? "#5A4F3E" : "#3D3428"), transition: "all 0.2s", overflow: "hidden" }}>
                    <div onClick={() => setExpandedArchiveItem(isExpanded ? null : archKey)}
                      style={{ display: "flex", alignItems: "center", gap: 14, padding: 16, cursor: "pointer", opacity: 0.85 }}
                      onMouseEnter={e => { e.currentTarget.parentElement.style.borderColor = "#5A4F3E"; }} onMouseLeave={e => { if (!isExpanded) e.currentTarget.parentElement.style.borderColor = "#3D3428"; }}>
                      <div style={{ width: 40, height: 50, borderRadius: 6, background: "#150F0A", border: "1px solid #3D3428", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.25rem", flexShrink: 0 }}>{"📦"}</div>
                      <div style={flexOneStyle}>
                        <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0" }}>{getDisplayName(b)}</div>
                        <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginTop: 2 }}>{d?.name} {"\u00B7"} {b?.proof}{"\u00B0"}{item.batch ? " \u00B7 " + item.batch : ""}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 10px", borderRadius: 14, fontSize: "0.6875rem", fontWeight: 600, background: rc + "18", color: rc, border: "1px solid " + rc + "40" }}>{item.emptyReason}</span>
                          {item.emptiedDate && <span style={dimText06875Style}>{item.emptiedDate}</span>}
                        </div>
                      </div>
                      <div style={textRightShrinkStyle}>
                        <div style={{ color: "#B5A898", fontWeight: 600, fontSize: "0.875rem" }}>${item.pricePaid}</div>
                        {personalAvg && <div style={{ color: "#D4A053", fontSize: "0.6875rem", marginTop: 2 }}>★ {personalAvg}</div>}
                      </div>
                    </div>
                    {isExpanded && (
                      <div style={{ padding: "0 16px 16px", borderTop: "1px solid #3D3428" }}>
                        {/* Historical Record */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginTop: 14 }}>
                          {[
                            { label: "Purchased", value: item.datePurchased || item.dateAdded || "—" },
                            { label: "Emptied", value: item.emptiedDate || "—" },
                            { label: "Disposition", value: (item.emptyReason || "").replace(/ [🎉🎁🚫]/, "") || "—" },
                            { label: "Price Paid", value: "$" + (item.pricePaid || 0) },
                            ...(item.purchaseLocation ? [{ label: "Purchased At", value: item.purchaseLocation }] : []),
                            ...(item.batch ? [{ label: "Batch", value: item.batch }] : []),
                          ].map(f => (
                            <div key={f.label} style={{ padding: "10px 12px", background: "#150F0A", borderRadius: 8, border: "1px solid #3D3428" }}>
                              <div style={{ fontSize: "0.625rem", color: "#A69880", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>{f.label}</div>
                              <div style={{ fontSize: "0.8125rem", color: "#C4B5A0", fontWeight: 600 }}>{f.value}</div>
                            </div>
                          ))}
                        </div>
                        {/* Ratings */}
                        {(personalAvg || globalAvg) && (
                          <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                            {personalAvg && (
                              <div style={{ flex: 1, padding: "10px 12px", background: "#150F0A", borderRadius: 8, border: "1px solid #3D3428", textAlign: "center" }}>
                                <div style={{ fontSize: "0.625rem", color: "#A69880", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>My Rating</div>
                                <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#D4A053" }}>{personalAvg} <span style={{ fontSize: "0.6875rem" }}>({myRating.length})</span></div>
                              </div>
                            )}
                            {globalAvg && (
                              <div style={{ flex: 1, padding: "10px 12px", background: "#150F0A", borderRadius: 8, border: "1px solid #3D3428", textAlign: "center" }}>
                                <div style={{ fontSize: "0.625rem", color: "#A69880", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>Global Rating</div>
                                <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#C4B5A0" }}>{globalAvg}</div>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Duration */}
                        {item.dateAdded && item.emptiedDate && (() => {

                          const days = Math.round((new Date(item.emptiedDate) - new Date(item.dateAdded)) / 86400000);
                          return days > 0 ? <div style={{ marginTop: 10, fontSize: "0.6875rem", color: "#A69880", textAlign: "center" }}>Lasted {days} day{days !== 1 ? "s" : ""}</div> : null;
                        })()}
                        {/* Actions */}
                        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                          <button onClick={(e) => { e.stopPropagation(); setSelectedBourbon(b); navigateTo("bourbon-detail"); }}
                            style={{ padding: "8px 14px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#D4A053", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>
                            View Expression
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); const wasIn = wishlist.includes(item.bourbonId); setWishlist(prev => wasIn ? prev.filter(id => id !== item.bourbonId) : [...prev, item.bourbonId]); if (useSupabase() && currentUser?.uid) { wasIn ? db.removeFromWishlist(currentUser.uid, item.bourbonId) : db.addToWishlist(currentUser.uid, item.bourbonId); } showToast(wasIn ? "Removed from wishlist" : "★ Added to wishlist!"); }}
                            style={{ padding: "8px 14px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: wishlist.includes(item.bourbonId) ? "#D4A053" : "#B5A898", fontSize: "0.75rem", cursor: "pointer" }}>
                            {wishlist.includes(item.bourbonId) ? "★ On Wishlist" : "☆ Wishlist"}
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); const idx = myBar.indexOf(item); if (idx >= 0) { setMyBar(prev => prev.map((it, i) => i === idx ? { ...it, fill: 100, emptyReason: undefined, emptiedDate: undefined } : it)); if (useSupabase() && item.id) db.updateBarItem(item.id, { fill: 100, emptyReason: null }); showToast("🍾 Restored to your bar!"); setExpandedArchiveItem(null); } }}
                            style={{ padding: "8px 14px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#4CAF50", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer" }}>
                            Restore to Bar
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); const idx = myBar.indexOf(item); if (idx >= 0) setDeleteConfirm({ idx, bourbonId: item.bourbonId, isArchived: true }); }}
                            style={{ padding: "8px 14px", background: "transparent", border: "1px solid rgba(178,34,34,0.2)", borderRadius: 8, color: "#B5A898", fontSize: "0.75rem", cursor: "pointer" }}>
                            Delete Record
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }) : (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#B5A898" }}>
                  <div style={{ fontSize: "2.0rem", marginBottom: 12 }}>{"📦"}</div>
                  <p style={{ margin: 0, fontSize: "0.875rem" }}>{archiveSearch || archiveFilter ? "No archived bottles match your search" : "No finished bottles yet."}</p>
                  {(archiveSearch || archiveFilter) && (
                    <button onClick={() => { setArchiveSearch(""); setArchiveFilter(""); }}
                      style={{ marginTop: 10, padding: "6px 14px", background: "transparent", border: "1px solid #3D3428", borderRadius: 8, color: "#B5A898", fontSize: "0.75rem", cursor: "pointer" }}>
                      Clear Filters
                    </button>
                  )}
                </div>
              )}
            </>)}
          </div>
        );
      }

      case "activity": {

        // fuzzyMatch is at module scope

        const journalQ = journalSearch.trim();

        const filteredCheckins = journalQ.length < 1 ? checkins : checkins.filter(c => {

          const b = getBourbon(c.bourbonId);

          const d = b ? getBrand(b.brandId) : null;

          const displayName = getDisplayName(b);

          const f = journalFilter;
          if (f === "expression" || f === "all") {
            if (fuzzyMatch(displayName, journalQ)) return true;
            if (fuzzyMatch(b?.type, journalQ)) return true;

          }

          if (f === "brand" || f === "all") {
            if (fuzzyMatch(d?.name, journalQ)) return true;
            if (fuzzyMatch(d?.location, journalQ)) return true;

          }

          if (f === "location" || f === "all") {
            if (fuzzyMatch(c.location, journalQ)) return true;

          }

          if (f === "notes" || f === "all") {
            if (fuzzyMatch(c.comment, journalQ)) return true;
            if (fuzzyMatch(c.nose, journalQ)) return true;
            if (fuzzyMatch(c.palate, journalQ)) return true;
            if (fuzzyMatch(c.finish, journalQ)) return true;
            if (c.tastingNotes?.some(n => fuzzyMatch(n, journalQ))) return true;

          }

          if (f === "rating" || f === "all") {
            if (journalQ.startsWith(">=") || journalQ.startsWith(">") || journalQ.startsWith("<=") || journalQ.startsWith("<") || journalQ.startsWith("=")) {

              const op = journalQ.match(/^([><=]+)/)?.[1];

              const val = parseFloat(journalQ.slice(op.length));
              if (!isNaN(val)) {
                if (op === ">=" && c.rating >= val) return true;
                if (op === ">" && c.rating > val) return true;
                if (op === "<=" && c.rating <= val) return true;
                if (op === "<" && c.rating < val) return true;
                if (op === "=" && c.rating === val) return true;

              }

            } else {

              const numQ = parseFloat(journalQ);
              if (!isNaN(numQ) && c.rating === numQ) return true;

            }

          }

          if (f === "all") {
            if (fuzzyMatch(c.servingStyle, journalQ)) return true;
            if (fuzzyMatch(c.glassware, journalQ)) return true;
            if (fuzzyMatch(c.date, journalQ)) return true;

          }

          return false;

        });

        const filterCategories = [
          { key: "all", label: "All", icon: "🔍" },
          { key: "expression", label: "Expression", icon: "🥃" },
          { key: "brand", label: "Brand", icon: "🏛️" },
          { key: "location", label: "Location", icon: "📍" },
          { key: "rating", label: "Rating", icon: "⭐" },
        ];

        return (
          <div>
            <div style={flexBetweenCenterMb16Style}>
              <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.625rem", color: "#F5E6D0", margin: 0 }}>Tasting Journal</h2>
              <span style={mutedText075Style}>{filteredCheckins.length}{journalQ ? ` / ${checkins.length}` : ""} pour{filteredCheckins.length !== 1 ? "s" : ""}</span>
            </div>
            {/* Search Bar */}
            <div style={mb14Style}>
              <div style={posRelativeStyle}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#A69880", fontSize: "0.9375rem", pointerEvents: "none" }}>🔍</span>
                <input aria-label="Search journal" value={journalSearch} onChange={e => setJournalSearch(e.target.value)}
                  placeholder={journalFilter === "rating" ? "e.g. >=4.5 or 5 or <3" : journalFilter === "all" ? "Search expressions, brands, locations, notes..." : `Search by ${journalFilter}...`}
                  style={{ width: "100%", padding: "10px 36px 10px 36px", background: "#150F0A", border: "1px solid #5A4F3E", borderRadius: 10, color: "#F5E6D0", fontSize: "0.875rem", boxSizing: "border-box", transition: "border-color 0.15s" }}
                  onFocus={e => e.target.style.borderColor = "#D4A053"} onBlur={e => e.target.style.borderColor = "#5A4F3E"} />
                {journalSearch && (
                  <button onClick={() => setJournalSearch("")}
                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#B5A898", fontSize: "1.0rem", cursor: "pointer", padding: 0 }} aria-label="Close">✕</button>
                )}
              </div>
              {/* Filter pills */}
              <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
                {filterCategories.map(cat => (
                  <button key={cat.key} onClick={() => setJournalFilter(cat.key)}
                    style={{ padding: "8px 14px", borderRadius: 16, border: "1px solid", fontSize: "0.6875rem", cursor: "pointer", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 4,
                      background: journalFilter === cat.key ? "rgba(212,160,83,0.15)" : "transparent",
                      color: journalFilter === cat.key ? "#D4A053" : "#A69880",
                      borderColor: journalFilter === cat.key ? "#D4A053" : "#3D3428",
                      fontWeight: journalFilter === cat.key ? 700 : 400 }}>
                    <span style={{ fontSize: "0.6875rem" }}>{cat.icon}</span> {cat.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Results */}
            {dataLoading && checkins.length === 0 && <>{[0,1,2,3].map(i => <SkeletonCard key={i} />)}</>}
            {!dataLoading && checkins.length === 0 && !journalQ && (
              <div style={{ textAlign: "center", padding: "48px 20px" }}>
                <div style={sFonMar2Style}>📖</div>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.25rem", color: "#F5E6D0", margin: "0 0 8px" }}>Your Journal is Empty</h3>
                <p style={{ color: "#B5A898", fontSize: "0.875rem", margin: "0 0 20px" }}>Every great bourbon journey starts with a single pour. Check in your first bourbon to start building your tasting journal.</p>
                <button onClick={() => navigateTo("browse")} style={{ padding: "10px 24px", background: GRAD_PRIMARY, border: "none", borderRadius: 10, color: "#1A1409", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer" }}>Browse Bourbons</button>
              </div>
            )}
            {filteredCheckins.length === 0 && journalQ && (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <div style={{ fontSize: "2.25rem", marginBottom: 12 }}>🔍</div>
                <div style={{ color: "#B5A898", fontSize: "0.9375rem", marginBottom: 6 }}>No pours match "{journalSearch}"</div>
                <div style={dimText075Style}>Try a different search or change the filter category</div>
              </div>
            )}
            {filteredCheckins.map(c => {

              const b = getBourbon(c.bourbonId);

              const d = getBrand(b?.brandId);

              return (
                <div key={c.id} className="tap-feedback" style={{ background: "#1E1A14", borderRadius: 14, padding: 20, marginBottom: 14, border: "1px solid #3D3428" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                    <div onClick={() => { if (b) { setSelectedBourbon(b); navigateTo("bourbon-detail"); } }} style={{ cursor: b ? "pointer" : "default" }}>
                      <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: b ? "#F5E6D0" : "#8B7D6B" }}>{getDisplayName(b) || "Unknown Expression"}</div>
                      <div style={{ color: "#D4A053", fontSize: "0.8125rem", marginTop: 3 }}>
                        {d?.name}
                        {b && getSubmissionStatus(b) !== "approved" && <span style={{ marginLeft: 6, padding: "2px 7px", borderRadius: 10, fontSize: "0.5625rem", fontWeight: 600, background: "rgba(255,193,7,0.1)", color: "#FFC107", border: "1px solid rgba(255,193,7,0.25)" }}>⚠️ Unverified</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#150F0A", padding: "6px 14px", borderRadius: 20, border: "1px solid #3D3428" }}>
                      <span style={{ color: "#D4A053", fontSize: "0.75rem" }}>★</span>
                      <span style={{ color: "#D4A053", fontWeight: 700, fontSize: "1.0rem" }}>{fmtRating(c.rating)}</span>
                    </div>
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: "#B5A898", marginBottom: 12, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span>📍 {stripUSA(c.location)} · {fmtDate(c.date)} · {c.servingStyle} · {c.glassware}{c.isBarrelPick && <span style={{ marginLeft: 6, padding: "2px 7px", background: "rgba(212,160,83,0.1)", border: "1px solid rgba(212,160,83,0.25)", borderRadius: 10, fontSize: "0.625rem", fontWeight: 600, color: "#D4A053" }}>🏷️ Pick{c.pickProof ? ` ${c.pickProof}°` : ""}</span>}</span>
                  </div>
                  {c.drinkingWith?.length > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                      <span style={mutedText075Style}>🥂 with</span>
                      {c.drinkingWith.map(fId => { const f = friends.find(x => x.id === fId) || getCommunityUser(fId); return f ? (
                        <span key={fId} onClick={(e) => { e.stopPropagation(); const fu = getCommunityUser(fId); if (fu) { setSelectedFriend(fu); setFriendProfileTab("pours"); navigateTo("friend-profile"); } }}
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "8px 12px", background: "rgba(212,160,83,0.08)", border: "1px solid rgba(212,160,83,0.2)", borderRadius: 20, cursor: "pointer" }}>
                          <span style={font08125Style}>{f.avatar}</span>
                          <span style={{ fontSize: "0.75rem", color: "#D4A053", fontWeight: 600 }}>{f.name.split(" ")[0]}</span>
                        </span>
                      ) : null; })}
                    </div>
                  )}
                  {c.tastingNotes?.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                      {c.tastingNotes.map(n => <span key={n} style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.6875rem", background: "#3D3428", color: "#D4A053", border: "1px solid #5A4F3E" }}>{n}</span>)}
                    </div>
                  )}
                  <NosePalateFinish checkin={c} />
                  {c.comment && <p style={{ color: "#A69880", fontSize: "0.875rem", lineHeight: 1.6, margin: 0, fontStyle: "italic", borderLeft: "2px solid #5A4F3E", paddingLeft: 12 }}>"{sanitizeText(c.comment)}"</p>}
                  <div style={{ display: "flex", gap: 8, marginTop: 12, paddingTop: 10, borderTop: "1px solid #3D3428" }}>
                    {deleteConfirmId === c.id ? (
                      <>
                        <span style={{ color: "#B5A898", fontSize: "0.75rem", lineHeight: "32px" }}>Delete this check-in?</span>
                        <button onClick={() => { if (!window.confirm(`Delete this check-in for ${getDisplayName(b)}?`)) return; setCheckins(prev => prev.filter(x => x.id !== c.id)); if (useSupabase()) db.deleteCheckin(c.id); setDeleteConfirmId(null); showToast("Check-in deleted"); }}
                          style={{ padding: "6px 12px", background: "rgba(192,57,43,0.15)", border: "1px solid rgba(192,57,43,0.4)", borderRadius: 8, color: "#E74C3C", fontSize: "0.6875rem", fontWeight: 600, cursor: "pointer" }}>
                          Yes, delete
                        </button>
                        <button onClick={() => setDeleteConfirmId(null)}
                          style={{ padding: "6px 12px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#B5A898", fontSize: "0.6875rem", cursor: "pointer" }}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => { const bx = getBourbon(c.bourbonId); if (bx) { setCheckinBourbon(bx); setEditingCheckinId(c.id); setShowCheckinModal(true); } }}
                          style={{ padding: "6px 12px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#B5A898", fontSize: "0.6875rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                          aria-label="Edit check-in">
                          {"✏️"} Edit
                        </button>
                        <button onClick={() => setDeleteConfirmId(c.id)}
                          style={{ padding: "6px 12px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#B5A898", fontSize: "0.6875rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                          aria-label="Delete check-in">
                          {"🗑️"} Delete
                        </button>
                        <button onClick={() => {
                          const bx = getBourbon(c.bourbonId);
                          const text = `${getDisplayName(bx) || "A bourbon"} \u2014 ${fmtRating(c.rating)}\u2605 on BarrelRank`;
                          if (navigator.share) { navigator.share({ text }).catch(() => {}); }
                          else { navigator.clipboard.writeText(text).then(() => showToast("Copied to clipboard!")); }
                        }}
                          style={{ padding: "6px 12px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#B5A898", fontSize: "0.6875rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" }}
                          aria-label="Share check-in">
                          {"📤"} Share
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      case "profile": {

        const { uniqueDistilleries, avgAllRating, topNotes, dimScores, maxDimScore, showRadar, palateArchetype } = profileStats;

        return (
          <div>
            {/* Avatar Picker Modal */}
            {showAvatarPicker && (
              <div style={modalOverlayStyle}>
                <div style={{ background: "#1E1A14", border: "1px solid #5A4F3E", borderRadius: 16, width: "100%", maxWidth: 420, maxHeight: "85vh", overflow: "auto", padding: 28 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                    <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.25rem", color: "#F5E6D0", margin: 0 }}>Choose Avatar</h2>
                    <button onClick={() => setShowAvatarPicker(false)} style={mutedText15Style} aria-label="Close">✕</button>
                  </div>
                  {/* Current preview */}
                  <div style={{ textAlign: "center", marginBottom: 20 }}>
                    <div style={{ width: 72, height: 72, borderRadius: "50%", background: GRAD_PRIMARY, margin: "0 auto 8px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", border: "2px solid #D4A053" }}>
                      {typeof profileAvatar === "string" && profileAvatar.startsWith("data:") ? (
                        <img src={profileAvatar} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <span style={{ fontSize: "2.125rem", lineHeight: 1 }}>{profileAvatar || "🥃"}</span>
                      )}
                    </div>
                    <div style={sFonCol4Style}>Current avatar</div>
                  </div>
                  {/* Upload Photo — only for verified commercial accounts */}
                  {(() => {

                    const isVerifiedCommercial = accountType === "commercial" && businessClaims.some(c => c.businessId === barProfile.venueType && c.status === "approved") || (accountType === "commercial" && businessRoles.some(r => r.uid === currentUser?.uid && r.role === "owner"));

                    const isVerifiedDistillery = currentUser && businessRoles.some(r => r.uid === currentUser.uid && (r.role === "owner" || r.role === "manager"));

                    const canUpload = isVerifiedCommercial || isVerifiedDistillery;

                    return (
                    <div style={mb20Style}>
                      {canUpload ? (
                      <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px 16px", background: "#150F0A", border: "1.5px dashed #5A4F3E", borderRadius: 10, cursor: "pointer", transition: "border-color 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = "#D4A053"} onMouseLeave={e => e.currentTarget.style.borderColor = "#5A4F3E"}>
                        <span style={font1125Style}>📷</span>
                        <span style={{ color: "#B5A898", fontSize: "0.875rem", fontWeight: 600 }}>Upload a Photo</span>
                        <input type="file" accept="image/*" aria-label="Upload avatar" style={{ display: "none" }} onChange={e => {

                          const file = e.target.files?.[0];
                          if (!file) return;

                          const reader = new FileReader();
                          reader.onload = (ev) => {

                            const img = new Image();
                            img.onload = () => {

                              const canvas = document.createElement("canvas");

                              const size = 200;
                              canvas.width = size; canvas.height = size;

                              const ctx = canvas.getContext("2d");

                              const min = Math.min(img.width, img.height);

                              const sx = (img.width - min) / 2, sy = (img.height - min) / 2;
                              ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
                              setProfileAvatar(canvas.toDataURL("image/jpeg", 0.85));
                              if (useSupabase() && currentUser?.uid) db.updateAvatar(currentUser.uid, canvas.toDataURL("image/jpeg", 0.85));
                            };
                            img.src = ev.target.result;
                          };
                          reader.readAsDataURL(file);
                          e.target.value = "";
                        }} />
                      </label>
                      ) : (
                      <div style={{ padding: "12px 16px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 10, textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 4 }}>
                          <span style={font0875Style}>🔒</span>
                          <span style={{ color: "#A69880", fontSize: "0.8125rem", fontWeight: 600 }}>Photo Upload Locked</span>
                        </div>
                        <p style={{ color: "#A69880", fontSize: "0.6875rem", margin: 0 }}>
                          {accountType === "commercial"
                            ? "Verify your venue to unlock custom photo uploads and protect logo copyrights."
                            : "Photo uploads with logos are reserved for verified commercial accounts. Choose an icon below."}
                        </p>
                      </div>
                      )}
                    </div>
                    );
                  })()}
                  {/* Emoji picker */}
                  <div style={mb8Style}>
                    <div style={{ fontSize: "0.75rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, fontWeight: 600 }}>Choose an icon</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
                      {["🥃", "🥂", "🍺", "🏛️", "🤠", "👑", "🌟", "🔥", "🐴", "🌾", "🏔️", "🎵", "🏆", "💎", "🦅", "⭐", "🌙", "🍂", "🎯", "🧭", "🗺️"].map(em => (
                        <button key={em} onClick={() => { setProfileAvatar(em); if (useSupabase() && currentUser?.uid) db.updateAvatar(currentUser.uid, em); }}
                          style={{ width: 48, height: 48, borderRadius: 12, border: `2px solid ${profileAvatar === em ? "#D4A053" : "#3D3428"}`, background: profileAvatar === em ? "rgba(212,160,83,0.1)" : "#150F0A", cursor: "pointer", fontSize: "1.5rem", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>
                          {em}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button onClick={() => setShowAvatarPicker(false)}
                    style={{ width: "100%", padding: "12px 24px", background: GRAD_PRIMARY, border: "none", borderRadius: 10, color: "#FFF", fontSize: "0.9375rem", fontWeight: 700, cursor: "pointer", fontFamily: FONT_DISPLAY, marginTop: 16 }}>
                    Done
                  </button>
                </div>
              </div>
            )}
            <div style={{ textAlign: "center", padding: "24px 0 32px" }}>
              <div onClick={() => setShowAvatarPicker(true)}
                style={{ width: 80, height: 80, borderRadius: "50%", background: GRAD_PRIMARY, margin: "0 auto 8px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.25rem", cursor: "pointer", position: "relative", overflow: "hidden", border: "2px solid transparent", transition: "border-color 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "#D4A053"} onMouseLeave={e => e.currentTarget.style.borderColor = "transparent"}>
                {typeof profileAvatar === "string" && profileAvatar.startsWith("data:") ? (
                  <img src={profileAvatar} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <span style={{ fontSize: "2.25rem", lineHeight: 1 }}>{profileAvatar || "🥃"}</span>
                )}
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 22, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: "0.625rem", color: "#F5E6D0", fontWeight: 600 }}>EDIT</span>
                </div>
              </div>
              {editingUsername ? (
                <div style={{ margin: "0 0 4px" }}>
                  <div style={flexCenterGap8Style}>
                    <input aria-label="Username" value={usernameInput} onChange={e => { setUsernameInput(e.target.value); setUsernameError(""); }} autoFocus maxLength={30}
                      placeholder="Choose a username..."
                      style={{ fontFamily: FONT_DISPLAY, fontSize: "1.375rem", color: "#F5E6D0", background: "#150F0A", border: "1px solid " + (usernameError ? "#CD5C5C" : "#D4A053"), borderRadius: 8, padding: "4px 10px", width: "100%", boxSizing: "border-box", outline: "none", textAlign: "center" }}
                      onKeyDown={e => { if (e.key === "Enter") { const err = validateUsername(usernameInput); if (err) { setUsernameError(err); } else { const clean = sanitizeInput(usernameInput.trim(), 30); setUsername(clean); setCurrentUser(prev => prev ? { ...prev, name: clean } : prev); setEditingUsername(false); setUsernameError(""); if (useSupabase() && currentUser?.uid) db.updateUsername(currentUser.uid, clean); } } if (e.key === "Escape") { setEditingUsername(false); setUsernameError(""); } }} />
                    <button onClick={() => { const err = validateUsername(usernameInput); if (err) { setUsernameError(err); } else { const clean = sanitizeInput(usernameInput.trim(), 30); setUsername(clean); setCurrentUser(prev => prev ? { ...prev, name: clean } : prev); setEditingUsername(false); setUsernameError(""); if (useSupabase() && currentUser?.uid) db.updateUsername(currentUser.uid, clean); } }}
                      style={{ background: "none", border: "none", color: "#4CAF50", cursor: "pointer", fontSize: "1.125rem", padding: 0, flexShrink: 0 }}>{"\u2713"}</button>
                    <button onClick={() => { setEditingUsername(false); setUsernameError(""); }}
                      style={{ background: "none", border: "none", color: "#CD5C5C", cursor: "pointer", fontSize: "1.0rem", padding: 0, flexShrink: 0 }} aria-label="Close">{"✕"}</button>
                  </div>
                  <div style={{ fontSize: "0.625rem", color: "#A69880", textAlign: "center", marginTop: 2 }}>This is your public display name</div>
                  {usernameError && <div style={{ color: "#CD5C5C", fontSize: "0.6875rem", textAlign: "center", marginTop: 4 }}>{usernameError}</div>}
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, margin: "0 0 4px" }}>
                  <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.625rem", color: "#F5E6D0", margin: 0 }}>{accountType === "commercial" ? barProfile.name : username}</h2>
                  {accountType !== "commercial" && (
                    <button onClick={() => { setUsernameInput(username); setEditingUsername(true); }}
                      style={{ background: "none", border: "none", color: "#A69880", cursor: "pointer", fontSize: "0.8125rem", padding: 2, transition: "color 0.2s" }}
                      onMouseEnter={e => { e.currentTarget.style.color = "#D4A053"; }} onMouseLeave={e => { e.currentTarget.style.color = "#5A4F3E"; }}>{"\u270F\uFE0F"}</button>
                  )}
                </div>
              )}
              {accountType === "commercial" ? (() => {

                const vtLabels = { bar: "Bar", distillery: "Distillery", tasting_room: "Tasting Room", restaurant: "Restaurant", retail: "Retail", hotel: "Hotel", event_venue: "Event Venue" };

                return <p style={mutedText0875Style}>{vtLabels[barProfile.venueType] || "Venue"} {"\u00B7"} {barProfile.vibe} {"\u00B7"} <span style={font075Style}>{barProfile.tagline}</span></p>;
              })() : (<>
                  {currentUser?.realName && currentUser.realName !== username && (
                    <p style={{ color: "#B5A898", fontSize: "0.8125rem", margin: "2px 0 0", fontStyle: "italic" }}>{currentUser.realName}</p>
                  )}
                  <p style={{ color: "#D4A053", fontSize: "0.875rem", margin: "4px 0 0", fontWeight: 600 }}>Lvl {palateData.palateLevel} {palateData.title}</p>
              </>)}
            </div>
            {/* Palate Level */}
            {(() => {

              const { totalXP, palateLevel, title, levelProgress, currentThresh, nextThresh, xpBreakdown } = palateData;

              return (
                <div style={{ background: "linear-gradient(135deg, #1E1A14, #3D3428)", borderRadius: 16, padding: 20, marginBottom: 20, border: "1px solid #5A4F3E", position: "relative", overflow: "hidden" }}>
                  {/* Subtle glow */}
                  <div style={{ position: "absolute", top: -40, right: -40, width: 120, height: 120, borderRadius: "50%", background: "radial-gradient(circle, rgba(212,160,83,0.08), transparent 70%)", pointerEvents: "none" }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 14 }}>
                    <div style={{ width: 56, height: 56, borderRadius: "50%", background: GRAD_PRIMARY, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 0 16px rgba(212,160,83,0.25)", position: "relative" }}>
                      <span style={{ fontSize: "1.375rem", fontWeight: 900, color: "#1A1510", fontFamily: FONT_DISPLAY }}>{palateLevel}</span>
                    </div>
                    <div style={flexOneStyle}>
                      <div style={flexCenterGap8Style}>
                        <span style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", fontWeight: 700, color: "#F5E6D0" }}>{title}</span>
                        <button onClick={(e) => { e.stopPropagation(); setActiveInfoPanel(activeInfoPanel === "xpHelp" ? null : "xpHelp"); }}
                          style={{ width: 20, height: 20, borderRadius: "50%", border: "1px solid " + (activeInfoPanel === "xpHelp" ? "#D4A053" : "#5A4F3E"), background: activeInfoPanel === "xpHelp" ? "rgba(212,160,83,0.15)" : "transparent", color: activeInfoPanel === "xpHelp" ? "#D4A053" : "#A69880", fontSize: "0.6875rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0, transition: "all 0.2s" }}>?</button>
                      </div>
                      <div style={mutedText075Mt2Style}>Palate Level {palateLevel} {"\u00B7"} {totalXP.toLocaleString()} XP</div>
                    </div>
                  </div>
                  {/* XP Help Panel */}
                  {activeInfoPanel === "xpHelp" && (
                    <div style={{ background: "rgba(21,15,10,0.8)", borderRadius: 10, padding: 14, marginBottom: 14, border: "1px solid #5A4F3E" }}>
                      <div style={flexBetweenCenterMb10Style}>
                        <span style={{ fontFamily: FONT_DISPLAY, fontSize: "0.875rem", color: "#D4A053", fontWeight: 700 }}>How to Earn XP</span>
                        <button onClick={() => setShowXpHelp(false)} style={mutedText10Style} aria-label="Close">{"✕"}</button>
                      </div>
                      {[
                        { action: "Try a new bourbon", xp: "+10 XP", note: "First check-in per expression" },
                        { action: "Visit a new distillery", xp: "+8 XP", note: "Unique distillery in your pours" },
                        { action: "Pour it again", xp: "+3 XP", note: "Repeat check-ins on known bourbons" },
                        { action: "Earn badge levels", xp: "+15 XP", note: "Per badge level achieved" },
                        { action: "Add friends", xp: "+5 XP", note: "Per friend connection" },
                        { action: "Write tasting notes", xp: "+2 XP", note: "Notes or comments on check-ins" },
                        { action: "Refer a friend", xp: "+20 XP", note: "When they sign up via your link" },
                        { action: "Refer a venue", xp: "+30 XP", note: "Commercial sign-ups via your link" },
                      ].map(r => (
                        <div key={r.action} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #1A1510" }}>
                          <div>
                            <div style={{ color: "#F5E6D0", fontSize: "0.75rem", fontWeight: 600 }}>{r.action}</div>
                            <div style={{ color: "#B5A898", fontSize: "0.625rem", marginTop: 1 }}>{r.note}</div>
                          </div>
                          <span style={{ color: "#D4A053", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0 }}>{r.xp}</span>
                        </div>
                      ))}
                      <p style={{ color: "#B5A898", fontSize: "0.625rem", margin: "10px 0 0", lineHeight: 1.4 }}>Level up to unlock new titles — from Newcomer to The Last Drop. Your title shows on your profile.</p>
                    </div>
                  )}
                  {/* XP Progress Bar */}
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ height: 8, background: "#150F0A", borderRadius: 4, overflow: "hidden", border: "1px solid #3D3428" }}>
                      <div style={{ height: "100%", width: Math.min(levelProgress * 100, 100) + "%", background: "linear-gradient(90deg, #8B5E34, #D4A053, #F0D98C)", borderRadius: 4, transition: "width 0.6s ease-out", boxShadow: "0 0 8px rgba(212,160,83,0.3)" }} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                      <span style={{ fontSize: "0.625rem", color: "#B5A898" }}>Lvl {palateLevel}</span>
                      <span style={{ fontSize: "0.625rem", color: "#B5A898" }}>{totalXP - currentThresh} / {nextThresh - currentThresh} XP to Lvl {palateLevel + 1}</span>
                    </div>
                  </div>
                  {/* Tap to expand */}
                  <button onClick={() => setActiveInfoPanel(activeInfoPanel === "xpBreakdown" ? null : "xpBreakdown")}
                    style={{ width: "100%", padding: "8px 0 2px", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                    <span style={sFonCol3Style}>{activeInfoPanel === "xpBreakdown" ? "Hide" : "View"} XP Breakdown</span>
                    <span style={{ fontSize: "0.625rem", color: "#B5A898", transition: "transform 0.15s", transform: activeInfoPanel === "xpBreakdown" ? "rotate(180deg)" : "none" }}>▼</span>
                  </button>
                  {/* XP Breakdown — revealed on tap */}
                  {activeInfoPanel === "xpBreakdown" && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginTop: 8 }}>
                      {xpBreakdown.map(r => {

                        const targetMap = { unique: "unique", pours: "pours", distilleries: "brands", friends: "friends", notes: "pours", badges: "badges", referrals: "referrals" };

                        const tgt = targetMap[r.key];

                        const active = profileStatDetail === tgt;

                        return (
                          <div key={r.key} onClick={() => {
                            if (tgt === "badges") { const el = document.getElementById("profile-badges"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }
                            else if (tgt === "referrals") { const el = document.getElementById("profile-referrals"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }
                            else { setProfileStatDetail(active ? null : tgt); setRatingFilterRange(null); }
                          }}
                            style={{ background: active ? "rgba(212,160,83,0.12)" : "rgba(21,15,10,0.6)", borderRadius: 8, padding: "8px 6px", textAlign: "center", cursor: "pointer", border: active ? "1px solid rgba(212,160,83,0.3)" : "1px solid transparent", transition: "all 0.15s" }}>
                            <div style={font0875Style}>{r.icon}</div>
                            <div style={{ fontSize: "0.8125rem", fontWeight: 700, color: "#D4A053", marginTop: 2 }}>{r.xp}</div>
                            <div style={{ fontSize: "0.5rem", color: active ? "#D4A053" : "#B5A898", textTransform: "uppercase", letterSpacing: 0.5, marginTop: 1, lineHeight: 1.2 }}>{r.label}</div>
                            <div style={{ fontSize: "0.4375rem", color: "#A69880", marginTop: 2 }}>+{r.xp} XP</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
            {/* Shelf Staple */}
            {(() => {
              const stapleId = currentUser?.shelf_staple_id;
              const stapleNote = currentUser?.shelf_staple_note;
              const staple = stapleId ? bourbons.find(b => b.id === stapleId) : null;
              const stapleBrand = staple ? getDisplayBrand(staple.brandId) : null;
              const stapleRating = staple ? getAvgRating(staple.id) : null;
              if (!staple && stapleId) {
                // Archive fallback: fetch staple expression from server
                (async () => {
                  try {
                    const sb = getSupabase();
                    if (sb) {
                      const { data: rows } = await sb.from("bourbons").select("*").eq("id", stapleId).limit(1);
                      if (rows && rows[0]) {
                        const r = rows[0];
                        const full = { id: r.id, name: r.name, brandId: r.brand_id, facilityId: r.facility_id, proof: r.proof ? parseFloat(r.proof) : null, age: r.age || "", mashBill: r.mash_bill || "", msrp: r.msrp ? parseFloat(r.msrp) : null, type: r.type || "", finishType: r.finish_type || "", batch: r.batch || "", notes: r.notes || "", limitedRelease: r.limited_release || false, discontinued: r.discontinued || false, userAdded: r.user_added || false, addedBy: r.added_by || null, website: r.website || "", source: r.source || null, origin: r.origin || "", colaTtbId: r.cola_ttb_id || null, colaBrandName: r.cola_brand_name || null, colaClassName: r.cola_class_name || null, colaApprovalDate: r.cola_approval_date || null, imageUrl: r.image_url || null, submissionStatus: r.submission_status || "approved", submittedBy: r.submitted_by || null, submittedAt: r.submitted_at || null, reviewedBy: r.reviewed_by || null, reviewedAt: r.reviewed_at || null, rejectionReason: r.rejection_reason || "", mashCorn: r.mash_corn ?? null, mashWheat: r.mash_wheat ?? null, mashRye: r.mash_rye ?? null, mashBarley: r.mash_barley ?? null, mashOther: r.mash_other ?? null, proofMin: r.proof_min ? parseFloat(r.proof_min) : null, proofMax: r.proof_max ? parseFloat(r.proof_max) : null, tier: r.tier || "archive", popularityScore: r.popularity_score || 0, parentExpressionId: r.parent_expression_id || null, nameLocked: r.name_locked || false, colaProductName: r.cola_product_name || null, colaTastingFlavors: r.cola_tasting_flavors || null, colaDescription: r.cola_description || null, colaGrains: r.cola_grains || null, colaFinishingProcess: r.cola_finishing_process || null, colaBarcodeValue: r.cola_barcode_value || null, colaEnrichedAt: r.cola_enriched_at || null, expressionType: r.expression_type || null, mashbillSource: r.mashbill_source || null };
                        setBourbons(prev => prev.some(b => b.id === full.id) ? prev : [...prev, full]);
                      }
                    }
                  } catch { /* silent */ }
                })();
                return (
                  <div style={{ margin: '0 0 16px', padding: '0.75rem 1rem', background: 'rgba(196, 164, 105, 0.08)', borderLeft: '4px solid #C4A469', borderRadius: '0 12px 12px 0' }}>
                    <div style={{ color: '#A69880', fontSize: '0.8125rem' }}>Loading shelf staple...</div>
                  </div>
                );
              }
              if (!staple) return (
                <div onClick={() => { setShelfStaplePicker({ open: true, search: '', note: '' }); setShelfStapleQuery(''); }}
                  style={{ margin: '0 0 16px', padding: '1rem', border: '2px dashed rgba(196, 164, 105, 0.4)', borderRadius: 12, textAlign: 'center', cursor: 'pointer' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>🥃</div>
                  <div style={{ color: '#C4A469', fontWeight: 600, fontSize: '0.95rem' }}>Set Your Shelf Staple</div>
                  <div style={{ color: '#999', fontSize: '0.8rem', marginTop: '0.25rem' }}>What bourbon is always on your shelf?</div>
                </div>
              );
              return (
                <div style={{ margin: '0 0 16px', padding: '0.75rem 1rem', background: 'rgba(196, 164, 105, 0.08)', borderLeft: '4px solid #C4A469', borderRadius: '0 12px 12px 0', position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: '1.125rem', color: '#F5E6D0', margin: 0 }}>Shelf Staple</h3>
                    <div style={{ marginLeft: 'auto' }}>
                      <button onClick={(e) => { e.stopPropagation(); setShelfStaplePicker({ open: true, search: '', note: currentUser?.shelf_staple_note || '' }); setShelfStapleQuery(''); }}
                        aria-label="Change shelf staple" style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', padding: 4, fontSize: '0.85rem', minWidth: 44, minHeight: 44 }}>✏️</button>
                    </div>
                  </div>
                  <div onClick={() => { setSelectedBourbon(staple); navigateTo("bourbon-detail"); }} style={{ cursor: 'pointer' }}>
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: '#F5E6D0' }}>{staple.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
                      <span style={{ color: '#A69880', fontSize: '0.8125rem' }}>{stapleBrand?.name}</span>
                      {staple.proof && <span style={{ color: '#D4A053', fontSize: '0.75rem', fontWeight: 600 }}>{staple.proof}°</span>}
                      {stapleRating && <span style={{ color: '#f5c518', fontSize: '0.75rem' }}>★ {stapleRating}</span>}
                    </div>
                  </div>
                  {stapleNote && (
                    <div style={{ color: '#B5A898', fontSize: '0.8125rem', fontStyle: 'italic', marginTop: '0.5rem', lineHeight: 1.4 }}>"{stapleNote}"</div>
                  )}
                  {staple.mashCorn > 0 && (
                    <div style={grainStapleMashRowStyle}>
                      <div style={grainStapleBarContainerStyle}>
                        {[{ g: "corn", v: staple.mashCorn }, { g: "rye", v: staple.mashRye }, { g: "wheat", v: staple.mashWheat }, { g: "barley", v: staple.mashBarley }].filter(x => x.v > 0).map(x => (
                          <div key={x.g} style={{ width: x.v + "%", height: "100%", background: GRAIN_COLORS[x.g] }} title={`${x.g}: ${x.v}%`} />
                        ))}
                      </div>
                      <span style={grainStaplePctStyle}>
                        {[staple.mashCorn && `${staple.mashCorn}C`, staple.mashRye && `${staple.mashRye}R`, staple.mashWheat && `${staple.mashWheat}W`, staple.mashBarley && `${staple.mashBarley}B`].filter(Boolean).join("/")}
                      </span>
                      <span style={grainStapleDescStyle}>{getGrainDescriptor(staple.mashRye || 0, staple.mashWheat || 0)}</span>
                    </div>
                  )}
                </div>
              );
            })()}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 16 }}>
              {[
                { key: "pours", label: "Total Pours", value: checkins.length },
                { key: "unique", label: "Unique", value: [...new Set(checkins.map(c => c.bourbonId))].length },
                { key: "rating", label: "Avg Rating", value: avgAllRating },
                { key: "brands", label: "Brands", value: uniqueDistilleries },
                { key: "friends", label: "Friends", value: friends.length },
              ].map(s => (
                <div key={s.key} onClick={() => { setProfileStatDetail(profileStatDetail === s.key ? null : s.key); setRatingFilterRange(null); }}
                  style={{ background: profileStatDetail === s.key ? "rgba(212,160,83,0.08)" : "#1E1A14", borderRadius: 12, padding: 14, textAlign: "center", border: `1.5px solid ${profileStatDetail === s.key ? "#D4A053" : "#3D3428"}`, cursor: "pointer", transition: "all 0.15s" }}
                  onMouseEnter={e => { if (profileStatDetail !== s.key) e.currentTarget.style.borderColor = "#5A4F3E"; }} onMouseLeave={e => { if (profileStatDetail !== s.key) e.currentTarget.style.borderColor = "#3D3428"; }}>
                  <div style={{ fontSize: "1.375rem", fontWeight: 700, color: "#D4A053", fontFamily: FONT_DISPLAY }}>{s.value}</div>
                  <div style={{ fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1, marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>
            {/* ─── STAT DETAIL PANELS ─── */}
            {profileStatDetail && (() => {

              const uniqueBourbonIds = [...new Set(checkins.map(c => c.bourbonId))];

              // ─── RATING DISTRIBUTION ───
              if (profileStatDetail === "rating") {

                const buckets = [
                  { label: "4.50–5.00", min: 4.5, max: 5.01, idx: 0 },
                  { label: "4.00–4.49", min: 4.0, max: 4.5, idx: 1 },
                  { label: "3.50–3.99", min: 3.5, max: 4.0, idx: 2 },
                  { label: "3.00–3.49", min: 3.0, max: 3.5, idx: 3 },
                  { label: "2.50–2.99", min: 2.5, max: 3.0, idx: 4 },
                  { label: "2.00–2.49", min: 2.0, max: 2.5, idx: 5 },
                  { label: "1.00–1.99", min: 1.0, max: 2.0, idx: 6 },
                ];

                const rb = profileStats.ratingBuckets;
                const maxCount = Math.max(...rb, 1);

                const filtered = ratingFilterRange ? checkins.filter(c => c.rating >= ratingFilterRange[0] && c.rating < ratingFilterRange[1]) : [];

                return (
                  <div style={cardPanelStyle}>
                    <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0", margin: "0 0 16px" }}>Rating Distribution</h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {buckets.map(bk => {

                        const count = rb[bk.idx];

                        const active = ratingFilterRange && ratingFilterRange[0] === bk.min;

                        return (
                          <div key={bk.label} onClick={() => setRatingFilterRange(active ? null : [bk.min, bk.max])}
                            style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "8px 0", opacity: count === 0 ? 0.35 : 1 }}>
                            <div style={{ width: 70, fontSize: "0.6875rem", color: active ? "#D4A053" : "#B5A898", fontWeight: active ? 700 : 400, fontFamily: "monospace", flexShrink: 0 }}>{bk.label}</div>
                            <div style={{ flex: 1, height: 16, background: "#150F0A", borderRadius: 4, overflow: "hidden", position: "relative" }}>
                              <div style={{ height: "100%", width: `${(count / maxCount) * 100}%`, background: active ? "#D4A053" : "rgba(212,160,83,0.35)", borderRadius: 4, transition: "all 0.3s" }} />
                            </div>
                            <div style={{ width: 24, fontSize: "0.75rem", color: active ? "#D4A053" : "#A69880", fontWeight: 700, textAlign: "right" }}>{count}</div>
                          </div>
                        );
                      })}
                    </div>
                    {ratingFilterRange && filtered.length > 0 && (
                      <div style={{ marginTop: 16, borderTop: "1px solid #3D3428", paddingTop: 14 }}>
                        <div style={{ fontSize: "0.75rem", color: "#B5A898", marginBottom: 10 }}>{filtered.length} check-in{filtered.length !== 1 ? "s" : ""} rated {ratingFilterRange[0].toFixed(2)}–{(ratingFilterRange[1] - 0.01).toFixed(2)}</div>
                        {filtered.slice(0, 8).map(c => {

                          const b = getBourbon(c.bourbonId); const d = b ? getBrand(b.brandId) : null;
                          return b ? (
                            <div key={c.id} onClick={() => { setSelectedBourbon(b); navigateTo("bourbon-detail"); }}
                              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #1A1510", cursor: "pointer" }}>
                              <div>
                                <div style={lightText08125BoldStyle}>{getDisplayName(b)}</div>
                                <div style={mutedText06875Style}>{d?.name} · {fmtDate(c.date)}</div>
                              </div>
                              <div style={{ color: "#D4A053", fontWeight: 700, fontSize: "0.875rem" }}>★ {fmtRating(c.rating)}</div>
                            </div>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                );

              }

              // ─── TOTAL POURS ───
              if (profileStatDetail === "pours") {

                const byMonth = {};
                checkins.forEach(c => { const m = c.date.slice(0, 7); byMonth[m] = (byMonth[m] || 0) + 1; });

                const months = Object.entries(byMonth).sort((a, b) => b[0].localeCompare(a[0]));

                const maxM = Math.max(...months.map(m => m[1]), 1);

                return (
                  <div style={cardPanelStyle}>
                    <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0", margin: "0 0 16px" }}>Pours by Month</h3>
                    {months.map(([m, count]) => (
                      <div key={m} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <div style={{ width: 65, fontSize: "0.6875rem", color: "#B5A898", fontFamily: "monospace", flexShrink: 0 }}>{m}</div>
                        <div style={{ flex: 1, height: 14, background: "#150F0A", borderRadius: 4, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${(count / maxM) * 100}%`, background: "rgba(212,160,83,0.4)", borderRadius: 4 }} />
                        </div>
                        <div style={{ width: 20, fontSize: "0.75rem", color: "#B5A898", fontWeight: 700, textAlign: "right" }}>{count}</div>
                      </div>
                    ))}
                  </div>
                );

              }

              // ─── UNIQUE EXPRESSIONS ───
              if (profileStatDetail === "unique") {

                return (
                  <div style={cardPanelStyle}>
                    <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0", margin: "0 0 4px" }}>Unique Expressions Tried</h3>
                    <p style={{ color: "#B5A898", fontSize: "0.75rem", margin: "0 0 14px" }}>{uniqueBourbonIds.length} expressions from {checkins.length} total pours</p>
                    {uniqueBourbonIds.map(bId => {

                      const b = getBourbon(bId); const d = b ? getBrand(b.brandId) : null;

                      const bCheckins = getCheckins(bId);
                      const pourCount = bCheckins.length;
                      const lastRating = bCheckins.reduce((best, c) => !best || (c.date || '') > (best.date || '') ? c : best, null)?.rating;
                      return b ? (
                        <div key={bId} onClick={() => { setSelectedBourbon(b); navigateTo("bourbon-detail"); }}
                          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #1A1510", cursor: "pointer", gap: 8 }}>
                          <div style={flexMinW0Style}>
                            <div style={{ color: "#F5E6D0", fontSize: "0.8125rem", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{getDisplayName(b)}</div>
                            <div style={mutedText06875Style}>{d?.name} · {pourCount} pour{pourCount !== 1 ? "s" : ""}</div>
                          </div>
                          {lastRating && <div style={{ color: "#D4A053", fontWeight: 700, fontSize: "0.8125rem", flexShrink: 0, whiteSpace: "nowrap" }}>★ {fmtRating(lastRating)}</div>}
                        </div>
                      ) : null;
                    })}
                  </div>
                );

              }

              // ─── DISTILLERIES ───
              if (profileStatDetail === "brands") {

                const distIds = [...profileStats.brandCheckins.keys()];

                return (
                  <div style={cardPanelStyle}>
                    <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0", margin: "0 0 14px" }}>Brands Explored</h3>
                    {distIds.map(dId => {

                      const d = getBrand(dId); if (!d) return null;

                      const entry = profileStats.brandCheckins.get(dId);

                      const expressions = entry.bourbonIds.size;

                      return (
                        <div key={dId} onClick={() => { setSelectedBrand(d); navigateTo("distillery-detail"); }}
                          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #1A1510", cursor: "pointer" }}>
                          <div>
                            <div style={lightText08125BoldStyle}>{d.name}</div>
                            <div style={mutedText06875Style}>📍 {d.location} · {expressions} expression{expressions !== 1 ? "s" : ""} · {entry.count} pour{entry.count !== 1 ? "s" : ""}</div>
                          </div>
                          <span style={{ color: "#D4A053", fontSize: "1.0rem" }}>›</span>
                        </div>
                      );
                    })}
                  </div>
                );

              }

              // ─── FRIENDS ───
              if (profileStatDetail === "friends") {

                return (
                  <div style={cardPanelStyle}>
                    <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.0rem", color: "#F5E6D0", margin: "0 0 14px" }}>Your Friends</h3>
                    {friends.length > 0 ? friends.map(f => {

                      const sharedBourbons = checkins.filter(c => (communityCheckins || []).some(cc => cc.userId === f.id && cc.bourbonId === c.bourbonId)).length;

                      return (
                        <div key={f.id} onClick={() => { const fu = getCommunityUser(f.id); if (fu) { setSelectedFriend(fu); setFriendProfileTab("pours"); navigateTo("friend-profile"); } }}
                          style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #1A1510", cursor: "pointer" }}>
                          <div style={flexCenterCenterStyle}>{f.avatar}</div>
                          <div style={flexOneStyle}>
                            <div style={lightText08125BoldStyle}>{f.name}</div>
                            <div style={mutedText06875Style}>📍 {f.location} · Friends since {fmtDate(f.since)}{sharedBourbons > 0 ? ` · ${sharedBourbons} shared pour${sharedBourbons !== 1 ? "s" : ""}` : ""}</div>
                          </div>
                          <span style={{ color: "#D4A053", fontSize: "1.0rem" }}>›</span>
                        </div>
                      );
                    }) : <div style={mutedText08125Style}>No friends yet.</div>}
                  </div>
                );

              }

              return null;
            })()}
            {topNotes.length > 0 && (
              <div style={{ marginBottom: 28 }}>
                <button onClick={() => setShowFlavorProfile(!showFlavorProfile)}
                    style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", cursor: "pointer", padding: "0 0 12px" }}>
                    <div style={flexCenterGap10Style}>
                      <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: 0 }}>Flavor Profile</h3>
                      {palateArchetype && showRadar && <span style={{ fontSize: "0.75rem", color: "#D4A053", fontWeight: 700, padding: "2px 8px", background: "rgba(212,160,83,0.15)", borderRadius: 10 }}>{palateArchetype.title}</span>}
                      <button onClick={(e) => { e.stopPropagation(); setActiveInfoPanel(activeInfoPanel === "flavorRadarInfo" ? null : "flavorRadarInfo"); }}
                        style={{ width: 20, height: 20, borderRadius: "50%", border: "1px solid " + (activeInfoPanel === "flavorRadarInfo" ? "#D4A053" : "#5A4F3E"), background: activeInfoPanel === "flavorRadarInfo" ? "rgba(212,160,83,0.15)" : "transparent", color: activeInfoPanel === "flavorRadarInfo" ? "#D4A053" : "#A69880", fontSize: "0.6875rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0, transition: "all 0.2s" }}>?</button>
                    </div>
                    <span style={{ fontSize: "0.625rem", color: "#B5A898", transition: "transform 0.15s", transform: showFlavorProfile ? "rotate(180deg)" : "none" }}>{"▼"}</span>
                  </button>
                {activeInfoPanel === "flavorRadarInfo" && (
                  <div style={{ background: "rgba(21,15,10,0.8)", borderRadius: 10, padding: 14, marginBottom: 14, border: "1px solid #5A4F3E" }}>
                    <div style={flexBetweenCenterMb10Style}>
                      <span style={{ fontFamily: FONT_DISPLAY, fontSize: "0.875rem", color: "#D4A053", fontWeight: 700 }}>How Your Flavor Radar Works</span>
                      <button onClick={() => setShowFlavorRadarInfo(false)} style={mutedText10Style} aria-label="Close">{"\u2715"}</button>
                    </div>
                    <p style={{ color: "#D4CFC7", fontSize: "0.75rem", lineHeight: 1.5, margin: "0 0 10px" }}>Your radar maps tasting notes from your check-ins into 8 flavor dimensions. Higher ratings push the shape outward more {"\u2014"} so it reflects what you <em style={goldTextStyle}>enjoy</em>, not just what you{"'"}ve tasted.</p>
                    {[
                      { signal: "Rating weight", detail: "A 5-star pour contributes ~1.7\u00D7; a 2-star only ~0.7\u00D7" },
                      { signal: "Note mapping", detail: "Each tasting note maps to one of 8 dimensions from the wheel" },
                      { signal: "Relative scale", detail: "Your strongest dimension = 100%, others shown proportionally" },
                      { signal: "Minimum data", detail: "Requires 3+ check-ins with tasting notes across 3+ dimensions" },
                    ].map(r => (
                      <div key={r.signal} style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "5px 0", borderBottom: "1px solid #1A1510" }}>
                        <span style={{ color: "#F5E6D0", fontSize: "0.75rem", fontWeight: 600, flexShrink: 0 }}>{r.signal}</span>
                        <span style={mutedText06875Style}>{r.detail}</span>
                      </div>
                    ))}
                    <p style={{ color: "#B5A898", fontSize: "0.625rem", margin: "10px 0 0", lineHeight: 1.4 }}>The more you check in, the more defined your palate shape becomes. Your top 3 specific notes are shown below the chart.</p>
                  </div>
                )}
                {/* Palate Archetype */}
                {showFlavorProfile && palateArchetype && showRadar && (
                  <div style={{ textAlign: "center", marginBottom: 10 }}>
                    <span style={{ fontFamily: FONT_DISPLAY, fontSize: "0.9375rem", fontWeight: 700, color: "#D4A053" }}>{palateArchetype.title}</span>
                    <p style={{ color: "#B5A898", fontSize: "0.6875rem", margin: "3px 0 0", lineHeight: 1.4 }}>{palateArchetype.desc}</p>
                  </div>
                )}
                {showFlavorProfile && showRadar ? (() => {

                  const rCX = 150, rCY = 150, rR = 100, rAngSlice = (2 * Math.PI) / 8;

                  const radarPt = (i, v) => ({ x: rCX + rR * v * Math.cos(rAngSlice * i - Math.PI / 2), y: rCY + rR * v * Math.sin(rAngSlice * i - Math.PI / 2) });

                  const polyPts = TW_CATS.map((cat, i) => radarPt(i, dimScores[cat] / maxDimScore)).map(p => `${p.x},${p.y}`).join(" ");

                  return (
                    <div>
                      <svg viewBox="0 0 300 300" style={{ width: "100%", maxWidth: 380, display: "block", margin: "0 auto" }}>
                        <defs><filter id="radarGlow"><feGaussianBlur stdDeviation="3" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
                        {/* Reference rings */}
                        {[0.25, 0.5, 0.75, 1].map(pct => (
                          <polygon key={pct} points={TW_CATS.map((_, i) => radarPt(i, pct)).map(p => `${p.x},${p.y}`).join(" ")} fill="none" stroke="#5A4F3E" strokeWidth={pct === 1 ? 1.5 : 0.75} strokeDasharray={pct === 1 ? "none" : "4,4"} opacity={pct === 1 ? 0.9 : 0.5} />
                        ))}
                        {/* Axis lines */}
                        {TW_CATS.map((_, i) => { const p = radarPt(i, 1); return <line key={i} x1={rCX} y1={rCY} x2={p.x} y2={p.y} stroke="#5A4F3E" strokeWidth={1} opacity={0.7} />; })}
                        {/* Data polygon */}
                        <polygon points={polyPts} fill="rgba(212,160,83,0.22)" stroke="#D4A053" strokeWidth={2} filter="url(#radarGlow)" />
                        {/* Data points */}
                        {TW_CATS.map((cat, i) => { const p = radarPt(i, dimScores[cat] / maxDimScore); return dimScores[cat] > 0 ? <circle key={cat} cx={p.x} cy={p.y} r={4.5} fill={TASTING_WHEEL[cat].color} stroke="#1A1510" strokeWidth={1.5} /> : null; })}
                        {/* Labels */}
                        {TW_CATS.map((cat, i) => {
                          const angle = rAngSlice * i - Math.PI / 2;
                          const cosA = Math.cos(angle), sinA = Math.sin(angle);
                          const isLeft = cosA < -0.3;
                          const isRight = cosA > 0.3;
                          const iconR = rR + 12;
                          const ix = rCX + iconR * cosA, iy = rCY + iconR * sinA;
                          const anchor = Math.abs(cosA) < 0.3 ? "middle" : isRight ? "start" : "end";
                          const labelColor = dimScores[cat] > 0 ? TASTING_WHEEL[cat].color : "#5A4F3E";
                          const pct = dimScores[cat] > 0 ? Math.round(dimScores[cat] / maxDimScore * 100) : 0;
                          const dataVal = dimScores[cat] / maxDimScore;
                          const pctR = Math.max(0.35, dataVal * 0.65 + 0.15);
                          const px = rCX + rR * pctR * cosA, py = rCY + rR * pctR * sinA;
                          return (
                            <g key={cat}>
                              <text x={ix} y={iy - 7} textAnchor={anchor} dominantBaseline="central" fill={TASTING_WHEEL[cat].color} fontSize="13">{TASTING_WHEEL[cat].icon}</text>
                              <text x={ix} y={iy + 6} textAnchor={anchor} dominantBaseline="central" fill={labelColor} fontSize="7.5" fontWeight="800" letterSpacing="0.5">{cat.toUpperCase()}</text>
                              {pct > 0 && <text x={px} y={py} textAnchor="middle" dominantBaseline="central" fill="#F5E6D0" fontSize="7" fontWeight="700" opacity={0.85}>{pct}%</text>}
                            </g>
                          );
                        })}
                      </svg>
                      {/* Condensed top-3 pills */}
                      <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                        {topNotes.slice(0, 3).map(([note, count]) => (
                          <div key={note} style={{ padding: "4px 12px", borderRadius: 20, fontSize: "0.75rem", background: "rgba(212,160,83,0.15)", color: "#D4A053", border: "1px solid #D4A053" }}>
                            {note} <span style={{ opacity: 0.6, fontSize: "0.625rem" }}>{"\u00D7"}{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })() : showFlavorProfile ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {topNotes.map(([note, count], i) => (
                      <div key={note} style={{ padding: "6px 14px", borderRadius: 20, fontSize: "0.8125rem", background: i < 3 ? "rgba(212,160,83,0.15)" : "#1E1A14", color: i < 3 ? "#D4A053" : "#B5A898", border: `1px solid ${i < 3 ? "#D4A053" : "#3D3428"}` }}>
                        {note} <span style={{ opacity: 0.6, fontSize: "0.6875rem" }}>{"\u00D7"}{count}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
            {/* Proof Sweet Spot — scatter plot */}
            {(() => {
              const proofPts = checkins.map(c => {
                const bx = getBourbon(c.bourbonId);
                return bx && bx.proof && c.rating ? { proof: bx.proof, rating: c.rating } : null;
              }).filter(Boolean);
              if (proofPts.length < 3) return null;
              const minP = Math.floor(Math.min(...proofPts.map(p => p.proof)) / 5) * 5;
              const maxP = Math.ceil(Math.max(...proofPts.map(p => p.proof)) / 5) * 5;
              const svgW = 300, svgH = 180, pad = { t: 20, r: 16, b: 28, l: 32 };
              const plotW = svgW - pad.l - pad.r, plotH = svgH - pad.t - pad.b;
              const xScale = v => pad.l + ((v - minP) / (maxP - minP || 1)) * plotW;
              const yScale = v => pad.t + plotH - ((v - 1) / 4) * plotH;
              // Compute sweet spot via 10-proof buckets
              const buckets = new Map();
              for (const pt of proofPts) {
                const bk = Math.floor(pt.proof / 10) * 10;
                if (!buckets.has(bk)) buckets.set(bk, []);
                buckets.get(bk).push(pt);
              }
              let bestBucket = 0, bestAvg = 0;
              for (const [bk, pts] of buckets) {
                const avg = pts.reduce((s, p) => s + p.rating, 0) / pts.length;
                if (avg > bestAvg || (avg === bestAvg && pts.length > (buckets.get(bestBucket)?.length || 0))) { bestBucket = bk; bestAvg = avg; }
              }
              const sweetProof = bestBucket + 5; // midpoint of winning bucket
              const sweetRating = bestAvg;
              return (
                <div style={mb20Style}>
                  <button onClick={() => setShowProofSpot(!showProofSpot)}
                    style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", cursor: "pointer", padding: "0 0 12px" }}>
                    <div style={flexCenterGap10Style}>
                      <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: 0 }}>Proof Sweet Spot</h3>
                      <span style={{ fontSize: "0.75rem", color: "#D4A053", fontWeight: 700, padding: "2px 8px", background: "rgba(212,160,83,0.15)", borderRadius: 10 }}>{bestBucket}{"\u2013"}{bestBucket + 10}{"\u00B0"}</span>
                      <button onClick={(e) => { e.stopPropagation(); setShowProofInfo(p => !p); }}
                        style={{ width: 20, height: 20, borderRadius: "50%", border: "1px solid " + (showProofInfo ? "#D4A053" : "#5A4F3E"), background: showProofInfo ? "rgba(212,160,83,0.15)" : "transparent", color: showProofInfo ? "#D4A053" : "#A69880", fontSize: "0.6875rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0, transition: "all 0.2s" }}>?</button>
                    </div>
                    <span style={{ fontSize: "0.625rem", color: "#B5A898", transition: "transform 0.15s", transform: showProofSpot ? "rotate(180deg)" : "none" }}>{"\u25BC"}</span>
                  </button>
                  {showProofInfo && (
                    <div style={{ background: "#1E1A14", borderRadius: 10, padding: 14, marginBottom: 12, border: "1px solid #5A4F3E" }}>
                      <div style={flexBetweenCenterMb10Style}>
                        <span style={{ fontFamily: FONT_DISPLAY, fontSize: "0.875rem", color: "#D4A053", fontWeight: 700 }}>How It Works</span>
                        <button onClick={() => setShowProofInfo(false)} style={mutedText10Style} aria-label="Close">✕</button>
                      </div>
                      <p style={{ color: "#D4CFC7", fontSize: "0.75rem", lineHeight: 1.5, margin: 0 }}>We group your check-ins into 10-proof ranges and find the range where your average rating is highest. The dashed crosshair marks the midpoint of your top-rated range. Dot colors reflect your rating: gold for 4+, amber for 3\u20134, gray for under 3.</p>
                    </div>
                  )}
                  {showProofSpot && (
                  <div style={{ borderRadius: 12, padding: 14 }}>
                    <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" style={{ display: "block" }}>
                      {/* Grid lines */}
                      {[1, 2, 3, 4, 5].map(r => (
                        <g key={r}>
                          <line x1={pad.l} y1={yScale(r)} x2={svgW - pad.r} y2={yScale(r)} stroke="#5A4F3E" strokeWidth={0.5} strokeDasharray={r === 1 || r === 5 ? "none" : "3,3"} />
                          <text x={pad.l - 4} y={yScale(r)} textAnchor="end" dominantBaseline="central" fill="#B5A898" fontSize="8">{r}</text>
                        </g>
                      ))}
                      {/* X-axis labels */}
                      {Array.from({ length: Math.min(6, Math.floor((maxP - minP) / 5) + 1) }, (_, i) => {
                        const step = Math.max(5, Math.ceil((maxP - minP) / 5 / 5) * 5);
                        const v = minP + i * step;
                        if (v > maxP) return null;
                        return <text key={v} x={xScale(v)} y={svgH - 6} textAnchor="middle" fill="#B5A898" fontSize="8">{v}{"\u00B0"}</text>;
                      })}
                      {/* Data points — color-coded by rating, bucketed when dense */}
                      {(() => {
                        const ptColor = r => r >= 4 ? { fill: "rgba(196,164,105,0.5)", stroke: "#C4A469" } : r >= 3 ? { fill: "rgba(184,115,58,0.5)", stroke: "#B8733A" } : { fill: "rgba(153,153,153,0.5)", stroke: "#999" };
                        if (proofPts.length <= 40) {
                          return proofPts.map((pt, i) => {
                            const c = ptColor(pt.rating);
                            return <circle key={i} cx={xScale(pt.proof)} cy={yScale(pt.rating)} r={4} fill={c.fill} stroke={c.stroke} strokeWidth={1} />;
                          });
                        }
                        // Aggregate into 5-proof bins for dense data
                        const bins = new Map();
                        for (const pt of proofPts) {
                          const bk = Math.round(pt.proof / 5) * 5;
                          if (!bins.has(bk)) bins.set(bk, []);
                          bins.get(bk).push(pt);
                        }
                        return Array.from(bins, ([bk, pts]) => {
                          const avg = pts.reduce((s, p) => s + p.rating, 0) / pts.length;
                          const c = ptColor(avg);
                          const r = Math.min(8, Math.max(3, Math.sqrt(pts.length) * 2));
                          return <circle key={bk} cx={xScale(bk)} cy={yScale(avg)} r={r} fill={c.fill} stroke={c.stroke} strokeWidth={1.5} />;
                        });
                      })()}
                      {/* Sweet spot crosshair */}
                      <line x1={xScale(sweetProof)} y1={pad.t} x2={xScale(sweetProof)} y2={pad.t + plotH} stroke="#D4A053" strokeWidth={1} strokeDasharray="4,3" opacity={0.6} />
                      <circle cx={xScale(sweetProof)} cy={yScale(sweetRating)} r={7} fill="rgba(212,160,83,0.2)" stroke="#D4A053" strokeWidth={2} />
                    </svg>
                    <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 8 }}>
                      {[{ c: "#C4A469", l: "4+" }, { c: "#B8733A", l: "3\u20134" }, { c: "#999", l: "<3" }].map(d => (
                        <span key={d.l} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.5625rem", color: "#B5A898" }}>
                          <svg width="8" height="8"><circle cx="4" cy="4" r="3.5" fill={d.c} /></svg>{d.l}{"\u2605"}
                        </span>
                      ))}
                    </div>
                    <div style={{ textAlign: "center", marginTop: 6 }}>
                      <span style={{ color: "#D4C4B0", fontSize: "0.6875rem" }}>You rate highest in the {bestBucket}{"\u00B0"}{"\u2013"}{bestBucket + 10}{"\u00B0"} range ({"\u2605"} {bestAvg.toFixed(1)} avg)</span>
                    </div>
                  </div>
                  )}
                </div>
              );
            })()}
            {/* Grain Map — own profile only */}
            {profileStats.mashData.count > 0 && (
              <MashBillPreference
                mashData={profileStats.mashData}
                expanded={grainMapExpanded}
                onToggle={() => setGrainMapExpanded(prev => !prev)}
                showGrainTooltip={showGrainTooltip}
                setShowGrainTooltip={setShowGrainTooltip}
              />
            )}
            {/* Badges — collapsible with earned count summary */}
            {(() => {

              const earnedBadges = computedBadges.filter(b => b.earned);

              const unearnedBadges = computedBadges.filter(b => !b.earned);

              return (
                <div id="profile-badges" style={mb20Style}>
                  <button onClick={() => setShowBadges(!showBadges)}
                    style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", cursor: "pointer", padding: "0 0 12px" }}>
                    <div style={flexCenterGap10Style}>
                      <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: 0 }}>Badges</h3>
                      <span style={{ fontSize: "0.75rem", color: "#D4A053", fontWeight: 700, padding: "2px 8px", background: "rgba(212,160,83,0.15)", borderRadius: 10 }}>{earnedBadges.length}/{computedBadges.length}</span>
                    </div>
                    <span style={{ fontSize: "0.625rem", color: "#B5A898", transition: "transform 0.15s", transform: showBadges ? "rotate(180deg)" : "none" }}>{"\u25BC"}</span>
                  </button>
                  {/* Collapsed: earned badge icons */}
                  {!showBadges && earnedBadges.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 4 }}>
                      {earnedBadges.map(b => (
                        <div key={b.key} title={b.name} style={{ width: 36, height: 36, borderRadius: 10, background: "#1E1A14", border: "1px solid #5A4F3E", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.125rem" }}>{b.icon}</div>
                      ))}
                    </div>
                  )}
                  {/* Expanded: full badge grid */}
                  {showBadges && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
                      {earnedBadges.concat(unearnedBadges).map(b => (
                        <div key={b.key} style={{ background: "#1E1A14", borderRadius: 12, padding: 14, border: `1px solid ${b.earned ? "#5A4F3E" : "#1E1A14"}`, opacity: b.earned ? 1 : 0.5 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                            <div style={{ fontSize: "1.625rem", flexShrink: 0 }}>{b.icon}</div>
                            <div style={flexOneStyle}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <span style={{ fontSize: "0.8125rem", color: b.earned ? "#F5E6D0" : "#A69880", fontWeight: 700, lineHeight: 1.2 }}>{b.name}</span>
                                {b.levelLabel && <span style={{ fontSize: "0.625rem", color: "#D4A053", fontWeight: 700, padding: "1px 6px", background: "rgba(212,160,83,0.15)", borderRadius: 8 }}>{b.levelLabel}</span>}
                              </div>
                              <div style={{ fontSize: "0.6875rem", color: "#B5A898", marginTop: 2, lineHeight: 1.3 }}>{b.descFull}</div>
                            </div>
                          </div>
                          {b.max !== 1 && (
                            <div style={{ height: 4, background: "#3D3428", borderRadius: 2, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${b.progress * 100}%`, background: b.earned ? "linear-gradient(90deg, #D4A053, #8B5E34)" : "#5A4F3E", borderRadius: 2, transition: "width 0.4s" }} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            {/* My Submissions */}
            {(() => {

              const myExprs = bourbons.filter(b => b.submittedBy === currentUser?.uid && b.userAdded);

              const myDists = brands.filter(d => d.submittedBy === currentUser?.uid && d.userAdded);

              const myEdits = expressionEdits.filter(e => e.userId === currentUser?.uid);

              const allSubs = [
                ...myExprs.map(b => ({ ...b, _type: "expression", _name: b.name, _detail: getBrand(b.brandId)?.name || "", submittedAt: b.submittedAt })),
                ...myDists.map(d => ({ ...d, _type: "distillery", _name: d.name, _detail: d.location, submittedAt: d.submittedAt })),
                ...myEdits.map(e => {
                  const bbn = getBourbon(e.bourbonId);
                  const changedKeys = Object.keys(e.suggestedChanges || {});
                  const isUpcOnly = changedKeys.length === 1 && changedKeys[0] === "upc";
                  return { ...e, _type: isUpcOnly ? "barcode link" : "edit", _name: bbn?.name || "Unknown Expression", _detail: isUpcOnly ? `UPC: ${e.suggestedChanges.upc}` : changedKeys.join(", "), _isEdit: true, submittedAt: e.createdAt };
                }),
              ].sort((a, b) => (b.submittedAt || "").localeCompare(a.submittedAt || ""));
              if (allSubs.length === 0) return null;

              const statusBadge = (item) => {

                const s = item._isEdit ? (item.status || "pending") : getSubmissionStatus(item);
                if (s === "approved") return <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: "0.5625rem", fontWeight: 700, background: "rgba(76,175,80,0.12)", color: "#4CAF50", border: "1px solid rgba(76,175,80,0.3)" }}>✓ APPROVED</span>;
                if (s === "pending") return <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: "0.5625rem", fontWeight: 700, background: "rgba(255,193,7,0.12)", color: "#FFC107", border: "1px solid rgba(255,193,7,0.3)" }}>⏳ PENDING</span>;
                if (s === "rejected") return <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: "0.5625rem", fontWeight: 700, background: "rgba(205,92,92,0.12)", color: "#CD5C5C", border: "1px solid rgba(205,92,92,0.3)" }}>❌ REJECTED</span>;
                if (s === "expired") return <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: "0.5625rem", fontWeight: 700, background: "rgba(139,125,107,0.15)", color: "#B5A898", border: "1px solid rgba(139,125,107,0.3)" }}>⏰ EXPIRED</span>;
                return null;
              };

              return (
                <div style={{ marginBottom: 28 }}>
                  <button onClick={() => setShowMySubmissions(!showMySubmissions)}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 8, marginBottom: showMySubmissions ? 14 : 0, width: "100%" }}>
                    <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: 0, flex: 1, textAlign: "left" }}>My Submissions ({allSubs.length})</h3>
                    <span style={{ color: "#B5A898", fontSize: "0.75rem", transition: "transform 0.15s", transform: showMySubmissions ? "rotate(180deg)" : "none" }}>▼</span>
                  </button>
                  {showMySubmissions && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {allSubs.map(item => {

                        const s = getSubmissionStatus(item);

                        return (
                          <div key={item.id} style={{ padding: "12px 14px", background: "#150F0A", borderRadius: 10, border: "1px solid #3D3428" }}>
                            <div style={flexBetweenStartStyle}>
                              <div style={flexOneStyle}>
                                <div style={flexCenterGap6WrapStyle}>
                                  <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#F5E6D0" }}>{item._name}</span>
                                  <span style={{ fontSize: "0.5625rem", color: "#B5A898", textTransform: "uppercase" }}>{item._type}</span>
                                  {statusBadge(item)}
                                </div>
                                <div style={mutedText06875Mt3Style}>{item._detail} · {item.submittedAt?.slice(0, 10)}</div>
                                {s === "rejected" && item.rejectionReason && (
                                  <div style={{ marginTop: 6, padding: "8px 10px", background: "rgba(205,92,92,0.06)", borderRadius: 8, border: "1px solid rgba(205,92,92,0.15)" }}>
                                    <div style={{ fontSize: "0.625rem", color: "#CD5C5C", fontWeight: 600, marginBottom: 2 }}>Rejection Reason</div>
                                    <div style={{ fontSize: "0.75rem", color: "#A69880", lineHeight: 1.4 }}>{item.rejectionReason}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                            {(s === "rejected" || s === "expired") && (
                              <button onClick={async () => {

                                const dbTable = item._type === "expression" ? "bourbons" : "brands";
                                if (useSupabase()) await db.resubmitForReview(dbTable, item.id);
                                if (item._type === "expression") setBourbons(prev => prev.map(b => b.id === item.id ? { ...b, submissionStatus: "pending", submittedAt: new Date().toISOString(), rejectionReason: null, reviewedBy: null, reviewedAt: null } : b));
                                else setBrands(prev => prev.map(d => d.id === item.id ? { ...d, submissionStatus: "pending", submittedAt: new Date().toISOString(), rejectionReason: null, reviewedBy: null, reviewedAt: null } : d));
                                showToast("📋 " + item._name + " resubmitted for review");
                              }}
                                style={{ marginTop: 10, padding: "8px 16px", background: "rgba(212,160,83,0.08)", border: "1px solid rgba(212,160,83,0.2)", borderRadius: 8, color: "#D4A053", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", width: "100%" }}>
                                {s === "rejected" ? "Request Re-review" : "Resubmit for Review"}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
            {/* Blocked Users */}
            {blockedUsers.length > 0 && (
              <div style={{ marginTop: 16, padding: 20, background: "#1E1A14", borderRadius: 14, border: "1px solid #3D3428" }}>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: "0 0 14px" }}>Blocked Users ({blockedUsers.length})</h3>
                {blockedUsers.map(uid => {

                  const u = getCommunityUser(uid);

                  return (
                    <div key={uid} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #1A1510" }}>
                      <span style={mutedText08125Style}>{u?.name || uid}</span>
                      <button onClick={() => unblockUser(uid)}
                        style={{ padding: "5px 12px", background: "rgba(76,175,80,0.08)", border: "1px solid rgba(76,175,80,0.25)", borderRadius: 8, color: "#4CAF50", fontSize: "0.6875rem", fontWeight: 600, cursor: "pointer" }}>
                        Unblock
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Commercial Venue Profile */}
            {accountType === "commercial" && (() => {

              const venueTypes = [
                { key: "bar", icon: "🍺", label: "Bar / Lounge" },
                { key: "distillery", icon: "🏭", label: "Distillery" },
                { key: "tasting_room", icon: "🥃", label: "Tasting Room" },
                { key: "restaurant", icon: "🍽\uFE0F", label: "Restaurant" },
                { key: "retail", icon: "🛒", label: "Retail / Liquor Store" },
                { key: "hotel", icon: "🏨", label: "Hotel / Resort" },
                { key: "event_venue", icon: "🎉", label: "Event Venue" },
              ];

              const vibesByType = {
                bar: ["Speakeasy", "Sports Bar", "Cocktail Lounge", "Dive Bar", "Hotel Bar", "Rooftop", "Wine Bar", "Gastropub"],
                distillery: ["Craft", "Heritage", "Micro-Distillery", "Farm-to-Bottle", "Industrial", "Bourbon Trail"],
                tasting_room: ["Intimate", "Educational", "Flight-Focused", "Private Barrel", "Walk-In", "Reservation Only"],
                restaurant: ["Fine Dining", "Steakhouse", "Southern / BBQ", "Casual Dining", "Farm-to-Table", "Gastropub"],
                retail: ["Specialty Bourbon", "Full Spirits", "Wine & Spirits", "Grocery / Big Box", "Online + Pickup"],
                hotel: ["Lobby Bar", "Rooftop", "Resort Pool Bar", "Speakeasy", "Fine Dining"],
                event_venue: ["Private Events", "Bourbon Tastings", "Corporate", "Weddings", "Festival / Outdoor"],
              };

              const vibeOptions = vibesByType[barProfile.venueType] || vibesByType.bar;

              const typeLabel = venueTypes.find(v => v.key === barProfile.venueType)?.label || "Venue";

              const showPourPricing = ["bar", "tasting_room", "restaurant", "hotel"].includes(barProfile.venueType);

              return (
              <div style={{ marginTop: 16, padding: 20, background: "#1E1A14", borderRadius: 14, border: "1px solid #5A4F3E" }}>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: "0 0 16px", display: "flex", alignItems: "center", gap: 8 }}>{"🏢"} Venue Profile</h3>
                {/* Venue Type Selector */}
                <label style={formLabelStyle}>Venue Type</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
                  {venueTypes.map(vt => (
                    <button key={vt.key} onClick={() => setBarProfile(p => ({ ...p, venueType: vt.key, vibe: vibesByType[vt.key]?.[0] || p.vibe }))}
                      style={{ padding: "7px 14px", borderRadius: 16, fontSize: "0.75rem", cursor: "pointer", border: "1px solid", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 5,
                        background: barProfile.venueType === vt.key ? "rgba(212,160,83,0.15)" : "transparent", color: barProfile.venueType === vt.key ? "#D4A053" : "#A69880", borderColor: barProfile.venueType === vt.key ? "#D4A053" : "#3D3428", fontWeight: barProfile.venueType === vt.key ? 700 : 400 }}>
                      <span style={font0875Style}>{vt.icon}</span> {vt.label}
                    </button>
                  ))}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div>
                    <label style={formLabelStyle}>{typeLabel} Name</label>
                    <input aria-label="Business name" value={barProfile.name} onChange={e => setBarProfile(p => ({ ...p, name: e.target.value.slice(0, 100) }))} maxLength={100} style={formInputStyle} />
                  </div>
                  <div>
                    <label style={formLabelStyle}>Tagline</label>
                    <input aria-label="Tagline" value={barProfile.tagline} onChange={e => setBarProfile(p => ({ ...p, tagline: e.target.value.slice(0, 200) }))} maxLength={200} style={formInputStyle} placeholder={barProfile.venueType === "distillery" ? "Small-batch craft distillery" : barProfile.venueType === "retail" ? "Your local bourbon specialist" : "Craft bourbon bar & tasting room"} />
                  </div>
                  <div>
                    <label style={formLabelStyle}>Address</label>
                    <input aria-label="Location" value={barProfile.location} onChange={e => setBarProfile(p => ({ ...p, location: e.target.value.slice(0, 200) }))} maxLength={200} style={formInputStyle} />
                  </div>
                  <div>
                    <label style={formLabelStyle}>Hours</label>
                    <input aria-label="Hours" value={barProfile.hours} onChange={e => setBarProfile(p => ({ ...p, hours: e.target.value }))} style={formInputStyle} />
                  </div>
                  <div>
                    <label style={formLabelStyle}>Style / Vibe</label>
                    <div style={flexGap6WrapStyle}>
                      {vibeOptions.map(v => (
                        <button key={v} onClick={() => setBarProfile(p => ({ ...p, vibe: v }))}
                          style={{ padding: "6px 14px", borderRadius: 16, fontSize: "0.75rem", cursor: "pointer", border: "1px solid", transition: "all 0.15s",
                            background: barProfile.vibe === v ? "rgba(212,160,83,0.15)" : "transparent", color: barProfile.vibe === v ? "#D4A053" : "#A69880", borderColor: barProfile.vibe === v ? "#D4A053" : "#3D3428", fontWeight: barProfile.vibe === v ? 700 : 400 }}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Pour Size — only for venues that serve pours */}
                {showPourPricing && (
                <div style={{ marginTop: 24 }}>
                  <h4 style={{ fontFamily: FONT_DISPLAY, fontSize: "0.9375rem", color: "#F5E6D0", margin: "0 0 4px" }}>Pour Size</h4>
                  <p style={{ color: "#B5A898", fontSize: "0.6875rem", margin: "0 0 12px" }}>Set your standard single pour size. Manage per-bottle pricing in My Bar.</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#150F0A", borderRadius: 10, border: "1px solid #3D3428" }}>
                    <span style={{ color: "#B5A898", fontSize: "0.75rem", fontWeight: 600, flexShrink: 0 }}>Single Pour Size:</span>
                    <div style={{ display: "flex", gap: 6 }}>
                      {[1.0, 1.5].map(oz => (
                        <button key={oz} onClick={() => setBarProfile(p => ({ ...p, singlePourOz: oz }))}
                          style={{ padding: "6px 16px", borderRadius: 8, fontSize: "0.8125rem", fontWeight: 700, cursor: "pointer", border: "1.5px solid", transition: "all 0.15s",
                            background: barProfile.singlePourOz === oz ? "rgba(212,160,83,0.15)" : "transparent",
                            color: barProfile.singlePourOz === oz ? "#D4A053" : "#A69880",
                            borderColor: barProfile.singlePourOz === oz ? "#D4A053" : "#3D3428" }}>
                          {oz} oz
                        </button>
                      ))}
                    </div>
                    <span style={{ color: "#A69880", fontSize: "0.6875rem", marginLeft: "auto" }}>Dbl = {barProfile.singlePourOz * 2} oz</span>
                  </div>
                </div>
                )}
                <button onClick={() => setMenuView(true)}
                  style={{ width: "100%", marginTop: 16, padding: "14px 20px", background: GRAD_PRIMARY, border: "none", borderRadius: 10, color: "#FFF", fontSize: "0.9375rem", fontWeight: 700, cursor: "pointer", fontFamily: FONT_DISPLAY, letterSpacing: 0.5 }}>
                  {"📋"} Preview Public Menu
                </button>
              </div>
              );
            })()}
            {/* ─── REFERRAL / INVITE SECTION ────────────────── */}
            <div id="profile-referrals" style={{ marginTop: 28, padding: 20, background: "linear-gradient(135deg, #1E1A14 0%, #3D3428 100%)", borderRadius: 14, border: "1px solid #5A4F3E" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: 0 }}>{"📨"} Referrals</h3>
                {referrals.length > 0 && (
                  <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.6875rem", fontWeight: 700, background: "rgba(212,160,83,0.12)", color: "#D4A053", border: "1px solid rgba(212,160,83,0.2)" }}>
                    {referrals.length} joined
                  </span>
                )}
              </div>
              <button onClick={() => setShowInviteSheet(true)}
                style={{ width: "100%", padding: "14px 20px", borderRadius: 12, border: "none", background: GRAD_PRIMARY, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 16 }}>
                <span style={font1125Style}>{"📨"}</span>
                <span style={{ color: "#FFF", fontSize: "0.9375rem", fontWeight: 700, fontFamily: FONT_DISPLAY }}>Invite to {BRAND.name}</span>
              </button>
              <p style={{ color: "#B5A898", fontSize: "0.75rem", margin: "0 0 14px", lineHeight: 1.5 }}>
                Earn <span style={{ color: "#D4A053", fontWeight: 600 }}>+20 XP</span> per sign-up. Commercial venues earn <span style={{ color: "#D4A053", fontWeight: 600 }}>+30 XP total</span>.
              </p>
              {referrals.length > 0 ? (
                <div>
                  <div style={{ fontSize: "0.6875rem", color: "#A69880", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10, fontWeight: 600 }}>Your Referrals</div>
                  {referrals.map((ref, i) => (
                    <div key={ref.uid || i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: i === 0 ? "none" : "1px solid #3D3428" }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: ref.type === "commercial" ? "rgba(212,160,83,0.15)" : "#3D3428", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.875rem", flexShrink: 0, border: ref.type === "commercial" ? "1px solid rgba(212,160,83,0.3)" : "1px solid #5A4F3E" }}>
                        {ref.type === "commercial" ? "🏢" : "🧑"}
                      </div>
                      <div style={flexOneStyle}>
                        <div style={lightText08125BoldStyle}>{ref.name}</div>
                        <div style={dimText06875Style}>Joined {new Date(ref.joinedAt).toLocaleDateString()}{ref.type === "commercial" ? " · Commercial" : ""}</div>
                      </div>
                      <div style={{ color: "#D4A053", fontSize: "0.75rem", fontWeight: 700 }}>+{ref.type === "commercial" ? 30 : 20} XP</div>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, padding: "10px 14px", background: "#150F0A", borderRadius: 8, border: "1px solid #3D3428" }}>
                    <span style={mutedText075Style}>Total referral XP</span>
                    <span style={{ color: "#D4A053", fontSize: "0.875rem", fontWeight: 700 }}>{(referrals.length * 20) + (referrals.filter(r => r.type === "commercial").length * 10)} XP</span>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "12px 0 0" }}>
                  <div style={{ color: "#A69880", fontSize: "0.8125rem" }}>No referrals yet. Tap invite to share your code.</div>
                </div>
              )}
            </div>
            {/* ─── INVITE MODAL (QR + Link + Share) ─── */}
            {showInviteSheet && (() => {

              const refUrl = `https://${BRAND.domain}/join?ref=` + referralCode;

              const shareMsg = `Track your bourbon journey with me on ${BRAND.name}! Use my referral code: ` + referralCode;

              const qrGrid = generateQR(refUrl);
              const qrSize = qrGrid.length;
              const cellSize = Math.max(4, Math.floor(150 / qrSize));
              const svgSize = qrSize * cellSize;

              return (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 20 }} onClick={() => setShowInviteSheet(false)}>
                  <div onClick={e => e.stopPropagation()} style={{ background: "#1E1A14", borderRadius: 20, padding: "28px 24px", width: "100%", maxWidth: 360, border: "1px solid #5A4F3E" }}>
                    <div style={{ textAlign: "center", marginBottom: 18 }}>
                      <div style={{ fontSize: "0.6875rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 3, marginBottom: 4 }}>Invite to</div>
                      <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.5rem", color: "#F5E6D0", margin: "0 0 4px" }}>{BRAND.name}</h3>
                      <div style={sFonCol4Style}>Referred by {currentUser?.name || "a friend"}</div>
                    </div>
                    <div style={{ textAlign: "center", marginBottom: 16 }}>
                      <div style={{ display: "inline-block", padding: 10, background: "#FFF", borderRadius: 10 }}>
                        <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
                          {qrGrid.flatMap((row, y) => row.map((on, x) => on ? (
                            <rect key={y * qrSize + x} x={x * cellSize} y={y * cellSize}
                              width={cellSize} height={cellSize} fill="#1A1510" />
                          ) : null))}
                        </svg>
                      </div>
                      <div style={{ fontSize: "0.625rem", color: "#A69880", marginTop: 6 }}>Scan to join {BRAND.name}</div>
                    </div>
                    <div style={{ padding: "10px 14px", background: "#150F0A", borderRadius: 10, border: "1px solid #3D3428", marginBottom: 14, textAlign: "center" }}>
                      <div style={{ fontSize: "0.5625rem", color: "#A69880", textTransform: "uppercase", letterSpacing: 2, marginBottom: 2 }}>Referral Code</div>
                      <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#D4A053", fontFamily: FONT_DISPLAY, letterSpacing: 3 }}>{referralCode}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                      <button onClick={() => copyToClipboard(refUrl)} style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "1px solid #5A4F3E", background: "#150F0A", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }} onMouseEnter={e => e.currentTarget.style.borderColor = "#D4A053"} onMouseLeave={e => e.currentTarget.style.borderColor = "#5A4F3E"}>
                        <span style={{ fontSize: "0.9375rem" }}>{"🔗"}</span>
                        <span style={lightText08125BoldStyle}>Copy Link</span>
                      </button>
                      <button onClick={() => { if (typeof navigator !== "undefined" && navigator.share) { navigator.share({ title: `Join me on ${BRAND.name}`, text: shareMsg, url: refUrl }).catch(() => {}); } else { copyToClipboard(shareMsg + "\n" + refUrl); } }} style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "none", background: GRAD_PRIMARY, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                        <span style={{ fontSize: "0.9375rem" }}>{"📤"}</span>
                        <span style={{ color: "#FFF", fontSize: "0.8125rem", fontWeight: 700 }}>Share</span>
                      </button>
                    </div>
                    <button onClick={() => setShowInviteSheet(false)} style={{ width: "100%", padding: "10px 0", borderRadius: 10, border: "1px solid #3D3428", background: "transparent", color: "#B5A898", fontSize: "0.8125rem", cursor: "pointer" }}>Done</button>
                    <p style={{ color: "#A69880", fontSize: "0.625rem", textAlign: "center", margin: "12px 0 0", lineHeight: 1.4 }}>Account type at sign-up determines credit: <span style={goldTextStyle}>Ambassador</span> for personal, <span style={goldTextStyle}>Evangelist</span> for commercial.</p>
                  </div>
                </div>
              );
            })()}
            {/* Friend Requests Section */}
            {(pendingRequests.incoming.length > 0 || pendingRequests.outgoing.length > 0) && (
              <div style={sMar8Style}>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: "0 0 14px" }}>
                  Friend Requests {pendingRequests.incoming.length > 0 && <span style={{ background: "#D4A053", color: "#1A1409", fontSize: "0.6875rem", fontWeight: 700, padding: "2px 8px", borderRadius: 10, marginLeft: 8 }}>{pendingRequests.incoming.length}</span>}
                </h3>
                {/* Incoming */}
                {pendingRequests.incoming.length > 0 && pendingRequests.incoming.map(req => (
                  <div key={req.friendshipId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #1A1510" }}>
                    <div style={flexCenterCenterStyle}>{req.avatar}</div>
                    <div style={flexOneStyle}>
                      <div style={lightText0875BoldStyle}>{req.name}</div>
                      <div style={mutedText06875Style}>Wants to be friends · {fmtDate(req.date)}</div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={async () => {
                        try {
                          if (useSupabase()) await db.acceptFriend(req.friendshipId);
                          setFriends(prev => [...prev, { id: req.userId, name: req.name, avatar: req.avatar, since: new Date().toISOString().slice(0, 10) }]);

                          setPendingRequests(prev => ({ ...prev, incoming: prev.incoming.filter(r => r.friendshipId !== req.friendshipId) }));
                          setCommunityUsers(prev => { if (!prev.find(u => u.id === req.userId)) return [...prev, { id: req.userId, name: req.name, avatar: req.avatar }]; return prev; });

                          showToast("👥 " + req.name + " is now your friend!");
                        } catch (e) { showToast("⚠️ Failed to accept request"); }
                      }} style={{ padding: "6px 12px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>Accept</button>
                      <button onClick={async () => {
                        try {
                          if (useSupabase()) await db.declineFriend(req.friendshipId);
                          setPendingRequests(prev => ({ ...prev, incoming: prev.incoming.filter(r => r.friendshipId !== req.friendshipId) }));
                          showToast("Request declined");
                        } catch (e) { showToast("⚠️ Failed to decline request"); }
                      }} style={{ padding: "6px 12px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#B5A898", fontSize: "0.75rem", cursor: "pointer" }}>Decline</button>
                    </div>
                  </div>
                ))}
                {/* Outgoing */}
                {pendingRequests.outgoing.length > 0 && (
                  <div style={{ marginTop: pendingRequests.incoming.length > 0 ? 16 : 0 }}>
                    <div style={{ color: "#B5A898", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Sent Requests</div>
                    {pendingRequests.outgoing.map(req => (
                      <div key={req.friendshipId || req.userId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #1A1510" }}>
                        <div style={flexCenterCenterStyle}>{req.avatar}</div>
                        <div style={flexOneStyle}>
                          <div style={{ color: "#F5E6D0", fontSize: "0.8125rem" }}>{req.name}</div>
                          <div style={mutedText06875Style}>Pending · {fmtDate(req.date)}</div>
                        </div>
                        <span style={{ color: "#B5A898", fontSize: "0.6875rem", fontStyle: "italic" }}>Awaiting response</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Friends Section */}
            <div id="profile-friends" style={sMar8Style}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: 0 }}>Friends ({friends.length})</h3>
                <button onClick={() => setShowFriendSearch(!showFriendSearch)}
                  style={{ padding: "7px 14px", background: showFriendSearch ? "#D4A053" : "linear-gradient(135deg, #D4A053, #8B5E34)", border: "none", borderRadius: 8, color: showFriendSearch ? "#1A1510" : "#FFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>
                  {showFriendSearch ? "✓ Done" : "+ Add Friends"}
                </button>
              </div>
              {/* Friend Search */}
              {showFriendSearch && (
                <div style={{ background: "#1E1A14", borderRadius: 12, padding: 16, border: "1px solid #5A4F3E", marginBottom: 16 }}>
                  <div style={posRelMb12Style}>
                  <input aria-label="Add friend" value={friendSearch} onChange={e => {
                    setFriendSearch(e.target.value);

                    const q = e.target.value.trim();
                    if (q.length >= 2 && useSupabase()) {
                      db.searchUsers(q).then(results => setFriendSearchResults(results.filter(u => u.id !== currentUser?.uid && !isFriend(u.id) && !isBlocked(u.id) && !pendingRequests.outgoing.some(p => p.userId === u.id) && !pendingRequests.incoming.some(p => p.userId === u.id))));
                    } else if (q.length >= 1 && DEMO) {
                      setFriendSearchResults(communityUsers.filter(u => !isFriend(u.id) && u.id !== currentUser?.uid && !isBlocked(u.id) && !pendingRequests.outgoing.some(p => p.userId === u.id) && !pendingRequests.incoming.some(p => p.userId === u.id) && u.name.toLowerCase().includes(q.toLowerCase())));
                    } else {
                      setFriendSearchResults([]);

                    }

                  }} placeholder="Search by name..." style={{ ...formInputStyle, marginBottom: 0, paddingRight: friendSearch ? 36 : 16, width: "100%", boxSizing: "border-box" }} autoFocus />
                  {friendSearch && <button onClick={() => { setFriendSearch(""); setFriendSearchResults([]); }} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#A69880", fontSize: "1rem", cursor: "pointer", padding: "4px 6px", lineHeight: 1 }}>✕</button>}
                  </div>
                  {friendSearchResults.map(u => (
                    <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #3D3428" }}>
                      <div style={flexCenterCenterStyle}>🥃</div>
                      <div style={flexOneStyle}>
                        <div style={lightText0875BoldStyle}>{u.name}</div>
                        <div style={mutedText075Style}>{u.email ? u.email.replace(/(.{2}).*(@.*)/, "$1***$2") : ""}</div>
                      </div>
                      <button onClick={async () => {
                        if (!useSupabase() || !currentUser?.uid) return;
                        const result = await db.addFriend(currentUser.uid, u.id);
                        if (!result) { showToast("Failed to send friend request"); return; }
                        setPendingRequests(prev => ({ ...prev, outgoing: [...prev.outgoing, { friendshipId: result.id, userId: u.id, name: u.name, avatar: "🥃", date: new Date().toISOString().slice(0, 10) }] }));
                        setFriendSearchResults(prev => prev.filter(r => r.id !== u.id));
                        showToast("📨 Friend request sent to " + u.name + "!");
                      }}
                        style={{ padding: "6px 14px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>
                        + Request
                      </button>
                    </div>
                  ))}
                  {friendSearch.trim().length >= 2 && friendSearchResults.length === 0 && (
                    <div style={{ textAlign: "center", padding: 16, color: "#B5A898", fontSize: "0.8125rem" }}>No users found.</div>
                  )}
                  {friendSearch.trim().length < 2 && (
                    <div style={{ textAlign: "center", padding: 12, color: "#B5A898", fontSize: "0.8125rem" }}>Type a name to search...</div>
                  )}
                </div>
              )}
              {/* Friends List */}
              {friends.length > 0 && (<>
                <input value={friendListSearch} onChange={e => setFriendListSearch(e.target.value)} aria-label="Filter friends" placeholder="Filter friends..."
                  style={{ width: "100%", padding: "10px 12px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 8, color: "#F5E6D0", fontSize: "0.8125rem", marginBottom: 10, boxSizing: "border-box" }} />
                <div style={{ display: "flex", gap: 0, marginBottom: 10, borderRadius: 8, overflow: "hidden", border: "1px solid #3D3428" }}>
                  {[{ key: "name", label: "Name" }, { key: "since", label: "Friends Since" }].map(s => (
                    <button key={s.key} onClick={() => setFriendListSort(s.key)}
                      style={{ flex: 1, padding: "7px 10px", background: friendListSort === s.key ? "rgba(212,160,83,0.12)" : "#150F0A", border: "none", color: friendListSort === s.key ? "#D4A053" : "#A69880", fontSize: "0.6875rem", fontWeight: friendListSort === s.key ? 700 : 400, cursor: "pointer", transition: "all 0.15s" }}>
                      {s.label} {friendListSort === s.key ? "▼" : ""}
                    </button>
                  ))}
                </div>
              </>)}
              {(() => {

                const q = friendListSearch.toLowerCase().trim();

                const filtered = q ? friends.filter(f => f.name.toLowerCase().includes(q) || (f.location || "").toLowerCase().includes(q)) : friends;

                const sorted = [...filtered].sort((a, b) => friendListSort === "since" ? (b.since || "").localeCompare(a.since || "") : a.name.localeCompare(b.name));
                return sorted.map(f => {

                const fu = getCommunityUser(f.id);

                return (
                <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 14, background: "#1E1A14", borderRadius: 12, border: "1px solid #3D3428", marginBottom: 8, cursor: "pointer", transition: "border-color 0.2s" }}
                  onClick={() => { if (fu) { setSelectedFriend(fu); setFriendProfileTab("pours"); navigateTo("friend-profile"); } }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#5A4F3E"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#3D3428", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.375rem", flexShrink: 0 }}>{f.avatar}</div>
                  <div style={flexOneStyle}>
                    <div style={{ color: "#F5E6D0", fontSize: "0.9375rem", fontWeight: 600 }}>{f.name}</div>
                    <div style={mutedText075Mt2Style}>📍 {f.location}</div>
                    <div style={mutedText06875Mt2Style}>Friends since {fmtDate(f.since)}</div>
                  </div>
                  <button onClick={async (e) => { e.stopPropagation(); if (!window.confirm(`Remove ${f.name} from friends?`)) return; if (useSupabase() && currentUser?.uid) { const ok = await db.removeFriend(currentUser.uid, f.id); if (!ok) { showToast("Failed to remove friend"); return; } } setFriends(prev => prev.filter(x => x.id !== f.id)); }}
                    style={{ padding: "6px 12px", background: "rgba(178,34,34,0.1)", border: "1px solid rgba(178,34,34,0.25)", borderRadius: 8, color: "#CD5C5C", fontSize: "0.6875rem", fontWeight: 600, cursor: "pointer" }}>
                    Remove
                  </button>
                </div>
              );});

              })()}
              {friends.length === 0 && !showFriendSearch && (
                <div style={{ textAlign: "center", padding: "24px 16px", color: "#B5A898" }}>
                  <p style={{ margin: "0 0 12px", fontSize: "0.875rem" }}>No friends yet. Add some to see their pours!</p>
                  <button onClick={() => setShowFriendSearch(true)} style={emptyStateCTAStyle}>Find Friends {"\u2192"}</button>
                </div>
              )}
            </div>
            {/* Settings (Privacy, Notifications, Account Type) */}
            <div style={sMar8Style}>
              <button onClick={() => setShowSettings(!showSettings)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: 0, flex: 1, textAlign: "left" }}>Settings</h3>
                <span style={{ color: "#B5A898", fontSize: "0.75rem", transition: "transform 0.15s", transform: showSettings ? "rotate(180deg)" : "none" }}>▼</span>
              </button>
              {showSettings && (
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 16 }}>
                  {/* Privacy */}
                  <div style={{ padding: 20, background: "#1E1A14", borderRadius: 14, border: "1px solid #3D3428" }}>
                    <h4 style={{ fontFamily: FONT_DISPLAY, fontSize: "1rem", color: "#F5E6D0", margin: "0 0 14px" }}>Privacy</h4>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                      {[
                        { key: "public", icon: "🌍", label: "Public", desc: "Visible to anyone in the BarrelRank community" },
                        { key: "friends", icon: "👥", label: "Friends Only", desc: "Visible only to users you've added as friends" },
                        { key: "private", icon: "🔒", label: "Private", desc: "Visible only to you — completely hidden from others" },
                      ].map(opt => (
                        <div key={opt.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "rgba(21,15,10,0.5)", borderRadius: 8 }}>
                          <span style={font10Style}>{opt.icon}</span>
                          <div>
                            <span style={lightText08125BoldStyle}>{opt.label}</span>
                            <span style={mutedText075Style}> — {opt.desc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {[
                        { label: "Check-ins", desc: "Default visibility for new pours", value: checkinPrivacy, set: setCheckinPrivacy },
                        { label: "My Bar", desc: "Who can see your bottle collection", value: barPrivacy, set: setBarPrivacy },
                        { label: "Profile", desc: "Who can view your profile page", value: profilePrivacy, set: setProfilePrivacy },
                      ].map(row => (
                        <div key={row.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "#150F0A", borderRadius: 10, border: "1px solid #3D3428" }}>
                          <div>
                            <div style={lightText0875BoldStyle}>{row.label}</div>
                            <div style={mutedText06875Mt2Style}>{row.desc}</div>
                          </div>
                          <div style={{ display: "flex", gap: 4 }}>
                            {["public", "friends", "private"].map(opt => (
                              <button key={opt} onClick={() => row.set(opt)}
                                style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${row.value === opt ? "#D4A053" : "#3D3428"}`, background: row.value === opt ? "rgba(212,160,83,0.12)" : "transparent", color: row.value === opt ? "#D4A053" : "#A69880", fontSize: "0.6875rem", fontWeight: row.value === opt ? 700 : 400, cursor: "pointer", transition: "all 0.15s" }}>
                                {{ public: "🌍", friends: "👥", private: "🔒" }[opt]}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Notifications */}
                  <div style={{ padding: 20, background: "#1E1A14", borderRadius: 14, border: "1px solid #3D3428" }}>
                    <h4 style={{ fontFamily: FONT_DISPLAY, fontSize: "1rem", color: "#F5E6D0", margin: "0 0 14px" }}>Notifications</h4>
                    <div onClick={() => setNotifyToasts(!notifyToasts)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 10, cursor: "pointer", transition: "border-color 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = "#5A4F3E"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ fontSize: "1.25rem" }}>🥃</span>
                        <div>
                          <div style={lightText0875BoldStyle}>Toast Notifications</div>
                          <div style={mutedText075Mt2Style}>Get notified when someone toasts your check-in</div>
                        </div>
                      </div>
                      <div style={{ width: 44, height: 24, borderRadius: 12, background: notifyToasts ? "#D4A053" : "#3D3428", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                        <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#FFF", position: "absolute", top: 2, left: notifyToasts ? 22 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                      </div>
                    </div>
                  </div>
                  {/* Account Type */}
                  <div style={{ padding: 20, background: "#1E1A14", borderRadius: 14, border: "1px solid #3D3428" }}>
                    <h4 style={{ fontFamily: FONT_DISPLAY, fontSize: "1rem", color: "#F5E6D0", margin: "0 0 6px" }}>Account Type</h4>
                    <p style={{ color: "#B5A898", fontSize: "0.75rem", margin: "0 0 14px" }}>Switch to a commercial account to manage your venue's public menu</p>
                    <div style={flexGap8Style}>
                      {[
                        { key: "personal", icon: "🧑", label: "Personal" },
                        { key: "commercial", icon: "🏢", label: "Commercial" },
                      ].map(opt => (
                        <button key={opt.key} onClick={() => setAccountType(opt.key)}
                          style={{ flex: 1, padding: "12px 14px", borderRadius: 10, border: "1.5px solid " + (accountType === opt.key ? "#D4A053" : "#3D3428"), background: accountType === opt.key ? "rgba(212,160,83,0.08)" : "#150F0A", cursor: "pointer", transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                          <span style={font1125Style}>{opt.icon}</span>
                          <span style={{ color: accountType === opt.key ? "#D4A053" : "#B5A898", fontWeight: accountType === opt.key ? 700 : 400, fontSize: "0.875rem" }}>{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Sign Out & Delete Account */}
            <div style={{ marginTop: 32, textAlign: "center", padding: "20px 0", borderTop: "1px solid #1A1510" }}>
              <button onClick={handleLogout}
                style={{ width: "100%", padding: "14px", background: "transparent", border: "1px solid rgba(205,92,92,0.25)", borderRadius: 12, color: "#CD5C5C", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", marginBottom: 12, transition: "all 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(205,92,92,0.08)"; }} onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                Sign Out
              </button>
              {!showDeleteConfirm ? (
                <button onClick={() => setShowDeleteConfirm(true)}
                  style={{ width: "100%", padding: "12px", background: "transparent", border: "none", color: "#B5A898", fontSize: "0.75rem", cursor: "pointer", marginBottom: 20 }}>
                  Delete Account
                </button>
              ) : (
                <div style={{ background: "rgba(205,92,92,0.06)", border: "1px solid rgba(205,92,92,0.2)", borderRadius: 12, padding: 16, marginBottom: 20 }}>
                  <p style={{ color: "#CD5C5C", fontSize: "0.8125rem", fontWeight: 600, margin: "0 0 8px" }}>Delete your account?</p>
                  <p style={{ color: "#B5A898", fontSize: "0.75rem", margin: "0 0 16px", lineHeight: 1.5 }}>This will permanently remove all your data including check-ins, bar items, and profile. This cannot be undone.</p>
                  <div style={flexGap10Style}>
                    <button onClick={() => setShowDeleteConfirm(false)}
                      style={{ flex: 1, padding: "10px", background: "#1E1A14", border: "1px solid #3D3428", borderRadius: 8, color: "#F5E6D0", fontSize: "0.8125rem", cursor: "pointer" }}>
                      Cancel
                    </button>
                    <button onClick={handleDeleteAccount}
                      style={{ flex: 1, padding: "10px", background: "rgba(205,92,92,0.15)", border: "1px solid rgba(205,92,92,0.3)", borderRadius: 8, color: "#CD5C5C", fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer" }}>
                      Delete Forever
                    </button>
                  </div>
                </div>
              )}
              <div style={{ fontSize: "0.6875rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 2 }}>Powered by</div>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#D4A053", marginTop: 4 }}>{BRAND.name}</div>
              <div style={{ fontSize: "0.6875rem", color: "#A69880", marginTop: 8 }}>v{APP_VERSION} ({BUILD_DATE})</div>
              <div style={{ marginTop: 8 }}><a href="/privacy.html" style={{ color: "#A69880", fontSize: "0.6875rem", textDecoration: "underline" }}>Privacy</a> · <a href="/terms.html" style={{ color: "#A69880", fontSize: "0.6875rem", textDecoration: "underline" }}>Terms</a> · <a href="mailto:support@barrelrank.com" style={{ color: "#A69880", fontSize: "0.6875rem", textDecoration: "underline" }}>Support</a></div>
            </div>
          </div>
        );
      }

      case "mybrand":
        return (
          <BrandDashboard
            brandRoles={userBrandRoles} brands={brands} bourbons={bourbons}
            checkins={checkins} communityCheckins={communityCheckins}
            upcCodes={upcCodes} fullCatalogLoaded={fullCatalogLoaded}
            navigateTo={navigateTo} setSelectedBourbon={setSelectedBourbon}
            onLoadArchive={loadArchiveExpressions}
            currentUser={currentUser} setBourbons={setBourbons} setUpcCodes={setUpcCodes}
          />
        );

      case "admin":
        return (
          <AdminContext.Provider value={adminContextValue}>
            <AdminPanel
              bourbons={bourbons} brands={brands} facilities={facilities}
              currentUser={currentUser} platformUsers={platformUsers}
              communityUsers={communityUsers} checkins={checkins}
              upcCodes={upcCodes} upcMap={upcMap}
              fullCatalogLoaded={fullCatalogLoaded}
              businessClaims={businessClaims} businessRoles={businessRoles}
              auditLog={auditLog} flaggedContent={flaggedContent}
              expressionEdits={expressionEdits}
              setBourbons={setBourbons} setBrands={setBrands}
              setBusinessClaims={setBusinessClaims} setBusinessRoles={setBusinessRoles}
              setAuditLog={setAuditLog} setFlaggedContent={setFlaggedContent}
              setExpressionEdits={setExpressionEdits}
              setPlatformUsers={setPlatformUsers}
              setFullCatalogLoaded={setFullCatalogLoaded}
              setUpcCodes={setUpcCodes}
            />
          </AdminContext.Provider>
        );

      default: return null;

    }

  };

  // ─── MAIN LAYOUT ──────────────────────

  // ─── AUTH GATE: Landing / Login / Signup ──
  if (!isAuthenticated && authLoading) {

    // Show splash while checking persisted session

    return (
      <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: "#110D08", color: "#F5E6D0", minHeight: "100vh", maxWidth: 600, margin: "0 auto", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <div style={{ width: 80, height: 80, borderRadius: 20, background: GRAD_PRIMARY, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 32px rgba(212,160,83,0.3)", marginBottom: 20, animation: "pulse 1.5s ease-in-out infinite" }}>
          <BarrelIcon />
        </div>
        <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.75rem", color: "#F5E6D0", margin: "0 0 8px" }}>{BRAND.name}</h1>
        <p style={{ color: "#B5A898", fontSize: "0.8125rem", margin: 0 }}>Loading your session...</p>
        {/* Pulse animation */}
      </div>
    );

  }

  if (isAuthenticated && dataLoading) {

    // Auth confirmed, loading user data from Supabase

    return (
      <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: "#110D08", color: "#F5E6D0", minHeight: "100vh", maxWidth: 600, margin: "0 auto", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <div style={{ width: 120, height: 120, borderRadius: 28, background: GRAD_PRIMARY, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 32px rgba(212,160,83,0.3)", marginBottom: 20, animation: "pulse 1.5s ease-in-out infinite" }}>
          <div style={{ transform: "scale(2.14)" }}><BarrelIcon /></div>
        </div>
        <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "2.25rem", color: "#F5E6D0", margin: "0 0 8px" }}>{BRAND.name}</h1>
        <p style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontWeight: 400, fontSize: "1.25rem", color: "#F5E6D0", margin: "0 0 6px" }}>{(() => { const h = new Date().getHours(); return h < 12 ? "Good Morning" : h < 17 ? "Good Afternoon" : "Good Evening"; })()}{currentUser?.name ? `, ${currentUser.name.split(" ")[0]}` : ""}</p>
        <p style={{ color: "#B5A898", fontSize: "0.6875rem", margin: 0, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 600 }}>Time for a pour?</p>
        <style>{`@keyframes pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.05); opacity: 0.85; } }`}</style>
      </div>
    );

  }

  if (!isAuthenticated) {

    // authInputStyle, GoogleLogo, divider at module scope

    const btnPrimary = { width: "100%", padding: "16px", background: GRAD_PRIMARY, border: "none", borderRadius: 14, color: "#FFF", fontSize: "1.0rem", fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s", opacity: authLoading ? 0.6 : 1 };



    const socialBtn = (bg, icon, label, provider) => (
      <button key={provider} onClick={() => handleSocialLogin(provider)} disabled={authLoading}
        style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 12px", background: bg, border: "1px solid #3D3428", borderRadius: 12, color: "#F5E6D0", fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer", transition: "all 0.2s", opacity: authLoading ? 0.5 : 1 }}>
        {provider === "google" ? <GoogleLogo /> : <span style={font1125Style}>{icon}</span>}{label}
      </button>
    );



    return (
      <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: "#110D08", color: "#F5E6D0", minHeight: "100vh", maxWidth: 600, margin: "0 auto", display: "flex", flexDirection: "column" }}>
        {/* ── LANDING ── */}
        {authScreen === "landing" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "40px 24px" }}>
            {/* Hero */}
            <div style={{ textAlign: "center", marginBottom: 48 }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                <div style={{ width: 80, height: 80, borderRadius: 20, background: GRAD_PRIMARY, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 32px rgba(212,160,83,0.3)" }}>
                  <BarrelIcon />
                </div>
              </div>
              <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "2.25rem", color: "#F5E6D0", margin: "0 0 8px", letterSpacing: -0.5 }}>{BRAND.name}</h1>
              <p style={{ color: "#B5A898", fontSize: "1.0rem", margin: 0, lineHeight: 1.5 }}>Track your bourbon journey.</p>
              <p style={{ color: "#B5A898", fontSize: "0.8125rem", margin: "8px 0 0" }}>Every pour, every bottle, every discovery.</p>
            </div>
            {/* Social Sign-In */}
            <div style={{ display: "flex", gap: 10, marginBottom: 6 }}>
              {socialBtn("#1E1A14", "🌐", "Continue with Google", "google")}
            </div>
            {authDivider}
            {/* Email Buttons */}
            <button onClick={() => { setAuthScreen("login"); setAuthError(""); }} style={{ ...btnPrimary, marginBottom: 10, background: GRAD_PRIMARY }}>Sign In with Email</button>
            <button onClick={() => { setAuthScreen("signup"); setAuthError(""); }} style={{ ...btnPrimary, background: "transparent", border: "2px solid #5A4F3E", color: "#D4A053" }}>Create Account</button>
            {/* Biometric */}
            {biometricAvailable && biometricEnrolled && (<>
              <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "24px 0 0" }}>
                <div style={dividerLineStyle} /><span style={mutedText075Style}>quick access</span><div style={dividerLineStyle} />
              </div>
              <button onClick={handleBiometricLogin} disabled={authLoading}
                style={{ width: "100%", padding: "16px", background: "#1E1A14", border: "1px solid #5A4F3E", borderRadius: 14, color: "#D4A053", fontSize: "0.9375rem", fontWeight: 600, cursor: "pointer", marginTop: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, opacity: authLoading ? 0.5 : 1 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D4A053" strokeWidth="1.8" strokeLinecap="round"><path d="M12 10v4M7.5 8a6 6 0 0 1 9 0M5 6a9 9 0 0 1 14 0M2 4a13 13 0 0 1 20 0M12 18a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"/></svg>
                Sign in with Biometrics
              </button>
            </>)}
            {biometricAvailable && !biometricEnrolled && (
              <button onClick={() => { setBiometricEnrolled(true); handleBiometricLogin(); }}
                style={{ width: "100%", padding: "14px", background: "transparent", border: "1px solid #3D3428", borderRadius: 14, color: "#B5A898", fontSize: "0.8125rem", cursor: "pointer", marginTop: 20, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#A69880" strokeWidth="1.8" strokeLinecap="round"><path d="M12 10v4M7.5 8a6 6 0 0 1 9 0M5 6a9 9 0 0 1 14 0M12 18a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"/></svg>
                Enable Biometric Login
              </button>
            )}
            {authLoading && <div style={{ textAlign: "center", marginTop: 20, color: "#D4A053", fontSize: "0.8125rem" }}>Authenticating...</div>}
            {/* Footer */}
            <div style={{ textAlign: "center", marginTop: 40, color: "#A69880", fontSize: "0.6875rem" }}>
              <p style={{ margin: "0 0 6px" }}>By continuing, you agree to our <a href="/terms.html" target="_blank" style={mutedLinkStyle}>Terms of Service</a> and <a href="/privacy.html" target="_blank" style={mutedLinkStyle}>Privacy Policy</a>.</p>
              <p style={{ margin: "0 0 6px", color: "#A69880", fontSize: "0.625rem" }}>You must be 21 years of age or older to use BarrelRank.</p>
              <p style={{ margin: 0, color: "#A69880", fontSize: "0.625rem" }}>v{APP_VERSION} ({BUILD_DATE})</p>
            </div>
          </div>
        )}
        {/* ── LOGIN ── */}
        {authScreen === "login" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "40px 24px" }}>
            <button onClick={() => { setAuthScreen("landing"); setAuthError(""); }} style={{ background: "none", border: "none", color: "#D4A053", cursor: "pointer", padding: "0 0 24px", fontSize: "0.875rem", textAlign: "left" }}>{"\u2190"} Back</button>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.75rem", color: "#F5E6D0", margin: "0 0 6px" }}>Welcome Back</h2>
              <p style={mutedText0875Style}>Sign in to your {BRAND.name} account</p>
            </div>
            <div style={mb16Style}>
              <label style={sectionLabelMdStyle}>Email</label>
              <input value={authForm.email} onChange={e => setAuthForm(p => ({ ...p, email: e.target.value }))} type="email" aria-label="Email address" placeholder="you@example.com" autoComplete="email"
                style={authInputStyle} onFocus={e => { e.target.style.borderColor = "#D4A053"; }} onBlur={e => { e.target.style.borderColor = "#3D3428"; }} />
            </div>
            <div style={mb8Style}>
              <label style={sectionLabelMdStyle}>Password</label>
              <div style={posRelativeStyle}>
                <input value={authForm.password} onChange={e => setAuthForm(p => ({ ...p, password: e.target.value }))} type={showPassword ? "text" : "password"} aria-label="Password" placeholder={"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"} autoComplete="current-password"
                  style={{ ...authInputStyle, paddingRight: 48 }} onFocus={e => { e.target.style.borderColor = "#D4A053"; }} onBlur={e => { e.target.style.borderColor = "#3D3428"; }}
                  onKeyDown={e => { if (e.key === "Enter") handleLogin(authForm.email, authForm.password); }} />
                <button onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#B5A898", cursor: "pointer", fontSize: "0.8125rem", padding: 4 }}>{showPassword ? "👁" : "👁\u200D🗨"}</button>
              </div>
            </div>
            <button onClick={() => { setAuthScreen("forgot"); setAuthError(""); setForgotEmailSent(false); }} style={{ background: "none", border: "none", color: "#D4A053", cursor: "pointer", fontSize: "0.8125rem", textAlign: "right", padding: "4px 0 20px" }}>Forgot password?</button>
            {authError && <div style={{ padding: "10px 14px", background: "rgba(205,92,92,0.1)", border: "1px solid rgba(205,92,92,0.2)", borderRadius: 10, color: "#CD5C5C", fontSize: "0.8125rem", marginBottom: 16 }}>{authError}</div>}
            <button onClick={() => handleLogin(authForm.email, authForm.password)} disabled={authLoading}
              style={{ ...btnPrimary, marginBottom: 12 }}>{authLoading ? "Signing in..." : "Sign In"}</button>
            {biometricAvailable && biometricEnrolled && (
              <button onClick={handleBiometricLogin} disabled={authLoading}
                style={{ width: "100%", padding: "14px", background: "#1E1A14", border: "1px solid #5A4F3E", borderRadius: 14, color: "#D4A053", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D4A053" strokeWidth="1.8" strokeLinecap="round"><path d="M12 10v4M7.5 8a6 6 0 0 1 9 0M5 6a9 9 0 0 1 14 0M12 18a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"/></svg>
                Use Biometrics
              </button>
            )}
            {authDivider}
            <div style={flexGap10Style}>
              {socialBtn("#1E1A14", "🌐", "Continue with Google", "google")}
            </div>
            <div style={{ textAlign: "center", marginTop: 28, color: "#B5A898", fontSize: "0.875rem" }}>
              Don't have an account? <button onClick={() => { setAuthScreen("signup"); setAuthError(""); }} style={{ background: "none", border: "none", color: "#D4A053", cursor: "pointer", fontSize: "0.875rem", fontWeight: 600 }}>Sign Up</button>
            </div>
            <p style={{ textAlign: "center", color: "#A69880", fontSize: "0.625rem", marginTop: 20, lineHeight: 1.4 }}>By using BarrelRank, you confirm you are 21 years of age or older.</p>
          </div>
        )}
        {/* ── SIGNUP ── */}
        {authScreen === "signup" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "40px 24px" }}>
            <button onClick={() => { setAuthScreen("landing"); setAuthError(""); }} style={{ background: "none", border: "none", color: "#D4A053", cursor: "pointer", padding: "0 0 24px", fontSize: "0.875rem", textAlign: "left" }}>{"\u2190"} Back</button>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.75rem", color: "#F5E6D0", margin: "0 0 6px" }}>Join {BRAND.name}</h2>
              <p style={mutedText0875Style}>Start tracking your bourbon journey</p>
            </div>
            <div style={mb14Style}>
              <label style={sectionLabelMdStyle}>Name</label>
              <input value={authForm.name} onChange={e => setAuthForm(p => ({ ...p, name: e.target.value }))} aria-label="Full name" placeholder="Your name" autoComplete="name" style={authInputStyle} onFocus={e => { e.target.style.borderColor = "#D4A053"; }} onBlur={e => { e.target.style.borderColor = "#3D3428"; }} />
            </div>
            <div style={mb14Style}>
              <label style={sectionLabelMdStyle}>Email</label>
              <input value={authForm.email} onChange={e => setAuthForm(p => ({ ...p, email: e.target.value }))} type="email" aria-label="Email address" placeholder="you@example.com" autoComplete="email" style={authInputStyle} onFocus={e => { e.target.style.borderColor = "#D4A053"; }} onBlur={e => { e.target.style.borderColor = "#3D3428"; }} />
            </div>
            <div style={mb14Style}>
              <label style={sectionLabelMdStyle}>Password</label>
              <div style={posRelativeStyle}>
                <input value={authForm.password} onChange={e => setAuthForm(p => ({ ...p, password: e.target.value }))} type={showPassword ? "text" : "password"} aria-label="Password" placeholder="Min 8 chars, 1 uppercase, 1 number" autoComplete="new-password"
                  style={{ ...authInputStyle, paddingRight: 48 }} onFocus={e => { e.target.style.borderColor = "#D4A053"; }} onBlur={e => { e.target.style.borderColor = "#3D3428"; }} />
                <button onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#B5A898", cursor: "pointer", fontSize: "0.8125rem" }}>{showPassword ? "👁" : "👁\u200D🗨"}</button>
              </div>
              {authForm.password.length > 0 && (
                <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
                  {[
                    { test: authForm.password.length >= 8, label: "8+ chars" },
                    { test: /[A-Z]/.test(authForm.password), label: "Uppercase" },
                    { test: /[0-9]/.test(authForm.password), label: "Number" },
                  ].map(r => (
                    <span key={r.label} style={{ fontSize: "0.625rem", padding: "2px 8px", borderRadius: 4, color: r.test ? "#4CAF50" : "#A69880", background: r.test ? "rgba(76,175,80,0.1)" : "rgba(90,78,62,0.1)", border: "1px solid " + (r.test ? "rgba(76,175,80,0.2)" : "rgba(90,78,62,0.15)") }}>{r.test ? "\u2713" : "\u2022"} {r.label}</span>
                  ))}
                </div>
              )}
            </div>
            <div style={mb20Style}>
              <label style={sectionLabelMdStyle}>Confirm Password</label>
              <input value={authForm.confirmPassword} onChange={e => setAuthForm(p => ({ ...p, confirmPassword: e.target.value }))} type="password" aria-label="Confirm password" placeholder="Re-enter password" autoComplete="new-password"
                style={{ ...authInputStyle, borderColor: authForm.confirmPassword && authForm.confirmPassword !== authForm.password ? "#CD5C5C" : "#3D3428" }}
                onFocus={e => { e.target.style.borderColor = "#D4A053"; }} onBlur={e => { e.target.style.borderColor = authForm.confirmPassword && authForm.confirmPassword !== authForm.password ? "#CD5C5C" : "#3D3428"; }}
                onKeyDown={e => { if (e.key === "Enter") handleSignup(authForm.name, authForm.email, authForm.password, authForm.confirmPassword); }} />
              {authForm.confirmPassword && authForm.confirmPassword !== authForm.password && (
                <div style={{ color: "#CD5C5C", fontSize: "0.6875rem", marginTop: 4 }}>Passwords don't match</div>
              )}
            </div>
            {authError && <div style={{ padding: "10px 14px", background: "rgba(205,92,92,0.1)", border: "1px solid rgba(205,92,92,0.2)", borderRadius: 10, color: "#CD5C5C", fontSize: "0.8125rem", marginBottom: 16 }}>{authError}</div>}
            <label onClick={() => setAuthForm(p => ({ ...p, ageConfirmed: !p.ageConfirmed }))}
              style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 20, cursor: "pointer", padding: "12px 14px", background: authForm.ageConfirmed ? "rgba(212,160,83,0.06)" : "#150F0A", borderRadius: 10, border: `1px solid ${authForm.ageConfirmed ? "rgba(212,160,83,0.25)" : "#3D3428"}`, transition: "all 0.15s" }}>
              <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${authForm.ageConfirmed ? "#D4A053" : "#5A4F3E"}`, background: authForm.ageConfirmed ? "#D4A053" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1, transition: "all 0.15s" }}>
                {authForm.ageConfirmed && <span style={{ color: "#1A1510", fontSize: "0.8125rem", fontWeight: 700 }}>✓</span>}
              </div>
              <span style={{ color: "#F5E6D0", fontSize: "0.8125rem", lineHeight: 1.4 }}>I confirm I am 21 years of age or older, agree to the <a href="/terms.html" target="_blank" style={{ color: "#D4A053", textDecoration: "underline" }} onClick={e => e.stopPropagation()}>Terms of Use</a>, and will not post abusive, hateful, or objectionable content</span>
            </label>
            <button onClick={() => handleSignup(authForm.name, authForm.email, authForm.password, authForm.confirmPassword)} disabled={authLoading}
              style={btnPrimary}>{authLoading ? "Creating account..." : "Create Account"}</button>
            {authDivider}
            <div style={flexGap10Style}>
              {socialBtn("#1E1A14", "🌐", "Continue with Google", "google")}
            </div>
            <div style={{ textAlign: "center", marginTop: 28, color: "#B5A898", fontSize: "0.875rem" }}>
              Already have an account? <button onClick={() => { setAuthScreen("login"); setAuthError(""); }} style={{ background: "none", border: "none", color: "#D4A053", cursor: "pointer", fontSize: "0.875rem", fontWeight: 600 }}>Sign In</button>
            </div>
          </div>
        )}
        {/* ── EMAIL VERIFICATION ── */}
        {authScreen === "verify" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "40px 24px", textAlign: "center" }}>
            <div style={{ fontSize: "3.0rem", marginBottom: 16 }}>{"📧"}</div>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.5rem", color: "#F5E6D0", margin: "0 0 8px" }}>Check Your Email</h2>
            <p style={{ color: "#B5A898", fontSize: "0.875rem", margin: "0 0 4px" }}>We sent a verification link to:</p>
            <p style={{ color: "#D4A053", fontSize: "1.0rem", fontWeight: 600, margin: "0 0 16px" }}>{pendingVerification}</p>
            <p style={{ color: "#C4B8A8", fontSize: "0.875rem", lineHeight: 1.6, margin: "0 0 28px" }}>Click the link in the email to confirm your account, then come back and log in.</p>
            <button onClick={() => { setAuthScreen("login"); setAuthError(""); }}
              style={{ ...btnPrimary, marginBottom: 12 }}>Go to Login</button>
            <p style={{ color: "#B5A898", fontSize: "0.75rem", marginTop: 24 }}>Didn't receive it? Check your spam folder.</p>
          </div>
        )}
        {/* ── FORGOT PASSWORD ── */}
        {authScreen === "forgot" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "40px 24px" }}>
            <button onClick={() => { setAuthScreen("login"); setAuthError(""); }} style={{ background: "none", border: "none", color: "#D4A053", cursor: "pointer", padding: "0 0 24px", fontSize: "0.875rem", textAlign: "left" }}>{"\u2190"} Back</button>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <div style={sFonMar2Style}>{"🔒"}</div>
              <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.5rem", color: "#F5E6D0", margin: "0 0 6px" }}>Reset Password</h2>
              <p style={mutedText0875Style}>Enter your email and we'll send reset instructions</p>
            </div>
            {!forgotEmailSent ? (<>
              <div style={mb20Style}>
                <label style={sectionLabelMdStyle}>Email</label>
                <input value={authForm.email} onChange={e => setAuthForm(p => ({ ...p, email: e.target.value }))} type="email" aria-label="Email address" placeholder="you@example.com"
                  style={authInputStyle} onFocus={e => { e.target.style.borderColor = "#D4A053"; }} onBlur={e => { e.target.style.borderColor = "#3D3428"; }}
                  onKeyDown={e => { if (e.key === "Enter") setForgotEmailSent(true); }} />
              </div>
              <button onClick={async () => {
                if (!authForm.email.includes("@")) { setAuthError("Enter a valid email."); return; }

                const sb = getSupabase();
                if (sb) {

                  const { error } = await sb.auth.resetPasswordForEmail(authForm.email, { redirectTo: window.location.origin });

                  if (error) { setAuthError("Unable to send reset email. Please try again."); return; }

                }

                setForgotEmailSent(true);
              }} style={btnPrimary}>Send Reset Link</button>
              {authError && <div style={{ padding: "10px 14px", background: "rgba(205,92,92,0.1)", border: "1px solid rgba(205,92,92,0.2)", borderRadius: 10, color: "#CD5C5C", fontSize: "0.8125rem", marginTop: 12 }}>{authError}</div>}
            </>) : (
              <div style={textCenterStyle}>
                <div style={sFonMar2Style}>{"\u2709\uFE0F"}</div>
                <p style={{ color: "#F5E6D0", fontSize: "0.9375rem", margin: "0 0 6px" }}>Reset link sent!</p>
                <p style={{ color: "#B5A898", fontSize: "0.8125rem", margin: "0 0 20px" }}>Check your inbox for instructions to reset your password.</p>
                <button onClick={() => { setAuthScreen("login"); setForgotEmailSent(false); }} style={{ ...btnPrimary, background: "transparent", border: "2px solid #5A4F3E", color: "#D4A053" }}>Return to Sign In</button>
              </div>
            )}
          </div>
        )}
      </div>
    );

  }

  const currentPageLabel = {
    home: "Home", browse: "Browse", brands: "Brands", mybar: "My Bar", activity: "Journal", profile: "Profile", admin: "Admin",
    "bourbon-detail": getDisplayName(selectedBourbon) || "Bourbon", "distillery-detail": selectedBrand?.name || "Brand",
    "friend-profile": selectedFriend?.name || "Profile",
  }[view] || BRAND.name;

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: "#110D08", color: "#F5E6D0", minHeight: "100vh", maxWidth: 600, margin: "0 auto", position: "relative" }}>
      <style>{`
        .range-thumb::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 18px; height: 18px; border-radius: 50%; background: #D4A053; border: 2px solid #1A1510; cursor: pointer; pointer-events: auto; box-shadow: 0 1px 4px rgba(0,0,0,0.5); }
        .range-thumb::-moz-range-thumb { width: 14px; height: 14px; border-radius: 50%; background: #D4A053; border: 2px solid #1A1510; cursor: pointer; pointer-events: auto; box-shadow: 0 1px 4px rgba(0,0,0,0.5); }
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.7; transform: scale(0.95); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.7); } to { opacity: 1; transform: scale(1); } }
      `}</style>
      <div ref={pullIndicatorRef} style={pullIndicatorStyle} />
      {/* Fixed Top Header */}
      <div style={{ position: "sticky", top: 0, zIndex: 200, background: "linear-gradient(to bottom, #110D08 60%, rgba(17,13,8,0.95) 85%, rgba(17,13,8,0))", paddingBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 6px" }}>
          <div style={flexCenterGap10Style} onClick={() => navigateTo("home")} role="button" aria-label="Go to home" className="tap-feedback">
            <div style={{ color: "#D4A053", display: "flex", alignItems: "center", cursor: "pointer" }}><BarrelIcon /></div>
            <span style={{ fontFamily: FONT_DISPLAY, fontSize: "1.25rem", fontWeight: 700, color: "#F5E6D0", letterSpacing: -0.3, cursor: "pointer" }}>{BRAND.name}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => { setUniversalSearchOpen(true); setUniversalSearchQ(""); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: "#B5A898", display: "flex", alignItems: "center" }} aria-label="Search">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </button>
            <span style={{ color: "#B5A898", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 600 }}>{currentPageLabel}</span>
          </div>
        </div>
        <div style={{ height: 1, background: "linear-gradient(to right, transparent, #3D3428, transparent)", margin: "0 16px" }} />
      </div>
      {/* Content */}
      <div style={{ padding: "8px 16px 100px" }}>
        {renderContent()}
      </div>
      {/* Universal Search Overlay */}
      {universalSearchOpen && (() => {
        const q = normalizeSearch(universalSearchQ.toLowerCase().trim());
        const bourbonResults = q.length >= 2 ? bourbons.filter(b => {
          const idx = bourbonSearchIndex.get(b.id);
          if (!idx) return false;
          return idx.name.includes(q) || idx.brandName.includes(q) || idx.aliases.some(a => a.includes(q));
        }).filter(b => isBrowsableBourbon(b, true)).slice(0, 5) : [];
        const brandResults = q.length >= 2 ? brands.filter(b => isBrowsableBrand(b) && normalizeSearch((b.name || "").toLowerCase()).includes(q)).slice(0, 3) : [];
        const userResults = q.length >= 2 ? [...communityUserMap.values()].filter(u => u.id !== currentUser?.uid && (u.name || "").toLowerCase().includes(q.toLowerCase())).slice(0, 3) : [];
        return (
          <div style={universalSearchOverlayStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <div style={{ flex: 1, position: "relative" }}>
                <input value={universalSearchQ} onChange={e => setUniversalSearchQ(e.target.value)} placeholder="Search bourbons, brands, people..." style={universalSearchInputStyle} autoFocus aria-label="Universal search" />
                {universalSearchQ && (
                  <button onClick={() => setUniversalSearchQ("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#B5A898", cursor: "pointer", fontSize: "1.125rem", padding: 4 }} aria-label="Clear search">✕</button>
                )}
              </div>
              <button onClick={() => { setUniversalSearchOpen(false); setArchiveSearchResults([]); }} style={{ background: "none", border: "none", color: "#D4A053", fontSize: "0.875rem", cursor: "pointer", fontWeight: 600, padding: "8px 4px", flexShrink: 0 }}>Cancel</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {q.length < 2 && <div style={{ color: "#666", textAlign: "center", padding: "3rem 0", fontSize: "0.875rem" }}>Type at least 2 characters to search</div>}
              {bourbonResults.length > 0 && (<>
                <div style={universalSearchCategoryStyle}>Bourbons</div>
                {bourbonResults.map(b => (
                  <div key={b.id} className="tap-feedback" onClick={() => { setUniversalSearchOpen(false); setSelectedBourbon(b); navigateTo("bourbon-detail"); }}
                    style={{ padding: "10px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid rgba(61,52,40,0.5)" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: "#F5E6D0", fontSize: "0.875rem", fontWeight: 600 }}>{b.name}</div>
                      <div style={{ color: "#B5A898", fontSize: "0.75rem" }}>{getBrand(b.brandId)?.name}{b.proof ? ` · ${b.proof}°` : ""}</div>
                    </div>
                  </div>
                ))}
              </>)}
              {brandResults.length > 0 && (<>
                <div style={universalSearchCategoryStyle}>Brands</div>
                {brandResults.map(b => (
                  <div key={b.id} className="tap-feedback" onClick={() => { setUniversalSearchOpen(false); setSelectedBrand(b); navigateTo("distillery-detail"); }}
                    style={{ padding: "10px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid rgba(61,52,40,0.5)" }}>
                    <div style={{ color: "#F5E6D0", fontSize: "0.875rem", fontWeight: 600 }}>{b.name}</div>
                  </div>
                ))}
              </>)}
              {userResults.length > 0 && (<>
                <div style={universalSearchCategoryStyle}>People</div>
                {userResults.map(u => (
                  <div key={u.id} className="tap-feedback" onClick={() => { setUniversalSearchOpen(false); setSelectedFriend(u); navigateTo("friend-profile"); }}
                    style={{ padding: "10px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid rgba(61,52,40,0.5)" }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#3D3428", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem", flexShrink: 0 }}>{u.avatar || "👤"}</div>
                    <div style={{ color: "#F5E6D0", fontSize: "0.875rem", fontWeight: 600 }}>{u.name}</div>
                  </div>
                ))}
              </>)}
              {q.length >= 3 && bourbonResults.length === 0 && archiveSearchResults.length > 0 && (<>
                <div style={universalSearchCategoryStyle}>Extended Catalog</div>
                {archiveSearchResults.slice(0, 5).map(b => (
                  <div key={b.id} className="tap-feedback" onClick={async () => {
                    setUniversalSearchOpen(false);
                    const existing = bourbons.find(bb => bb.id === b.id);
                    if (existing) { setSelectedBourbon(existing); navigateTo("bourbon-detail"); return; }
                    try {
                      const sb = getSupabase();
                      if (sb) {
                        const { data: rows } = await sb.from("bourbons").select("*").eq("id", b.id).limit(1);
                        if (rows && rows[0]) {
                          const r = rows[0];
                          const full = { id: r.id, name: r.name, brandId: r.brand_id, facilityId: r.facility_id, proof: r.proof ? parseFloat(r.proof) : null, age: r.age || "", mashBill: r.mash_bill || "", msrp: r.msrp ? parseFloat(r.msrp) : null, type: r.type || "", finishType: r.finish_type || "", batch: r.batch || "", notes: r.notes || "", limitedRelease: r.limited_release || false, discontinued: r.discontinued || false, userAdded: r.user_added || false, addedBy: r.added_by || null, website: r.website || "", source: r.source || null, origin: r.origin || "", colaTtbId: r.cola_ttb_id || null, colaBrandName: r.cola_brand_name || null, colaClassName: r.cola_class_name || null, colaApprovalDate: r.cola_approval_date || null, imageUrl: r.image_url || null, submissionStatus: r.submission_status || "approved", submittedBy: r.submitted_by || null, submittedAt: r.submitted_at || null, reviewedBy: r.reviewed_by || null, reviewedAt: r.reviewed_at || null, rejectionReason: r.rejection_reason || "", mashCorn: r.mash_corn ?? null, mashWheat: r.mash_wheat ?? null, mashRye: r.mash_rye ?? null, mashBarley: r.mash_barley ?? null, mashOther: r.mash_other ?? null, proofMin: r.proof_min ? parseFloat(r.proof_min) : null, proofMax: r.proof_max ? parseFloat(r.proof_max) : null, tier: r.tier || "archive", popularityScore: r.popularity_score || 0, parentExpressionId: r.parent_expression_id || null, nameLocked: r.name_locked || false, colaProductName: r.cola_product_name || null, colaTastingFlavors: r.cola_tasting_flavors || null, colaDescription: r.cola_description || null, colaGrains: r.cola_grains || null, colaFinishingProcess: r.cola_finishing_process || null, colaBarcodeValue: r.cola_barcode_value || null, colaEnrichedAt: r.cola_enriched_at || null, expressionType: r.expression_type || null, mashbillSource: r.mashbill_source || null };
                          setBourbons(prev => [...prev, full]);
                          setSelectedBourbon(full);
                          navigateTo("bourbon-detail");
                        }
                      }
                    } catch { /* silent */ }
                  }}
                    style={{ padding: "10px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid rgba(61,52,40,0.5)" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: "#F5E6D0", fontSize: "0.875rem", fontWeight: 600 }}>{b.name}</div>
                      <div style={{ color: "#B5A898", fontSize: "0.75rem" }}>{getBrand(b.brandId)?.name}{b.proof ? ` \u00B7 ${b.proof}\u00B0` : ""}</div>
                    </div>
                    <div style={{ color: "#A69880", fontSize: "0.5625rem" }}>{b.tier}</div>
                  </div>
                ))}
              </>)}
              {q.length >= 2 && bourbonResults.length === 0 && brandResults.length === 0 && userResults.length === 0 && archiveSearchResults.length === 0 && (
                <div style={{ color: "#666", textAlign: "center", padding: "3rem 0", fontSize: "0.875rem" }}>No results for "{universalSearchQ}"</div>
              )}
            </div>
          </div>
        );
      })()}
      {/* Suggest Edit Modal */}
      {showSuggestEdit && selectedBourbon && (() => {
        const b = selectedBourbon;
        const d = suggestEditData;
        const upd = (key, val) => setSuggestEditData(prev => ({ ...prev, [key]: val }));
        const changes = {};
        if (d.name && d.name !== (b.name || "")) { const normBrand = getBrand(b.brandId); changes.name = normalizeColaName(d.name.trim(), normBrand?.name || ''); }
        if (d.proof !== "" && parseFloat(d.proof) !== b.proof) changes.proof = parseFloat(d.proof);
        if (d.age && d.age !== (b.age || "")) changes.age = d.age;
        if (d.msrp !== "" && parseFloat(d.msrp) !== b.msrp) changes.msrp = parseFloat(d.msrp);
        if (d.type && d.type !== (b.type || "")) changes.type = d.type;
        if (d.finishType && d.finishType !== (b.finishType || "")) changes.finishType = d.finishType;
        if (d.batch && d.batch !== (b.batch || "")) changes.batch = d.batch;
        const mc = parseInt(d.mashCorn); if (mc > 0 && mc !== (b.mashCorn || 0)) changes.mashCorn = mc;
        const mw = parseInt(d.mashWheat); if (mw > 0 && mw !== (b.mashWheat || 0)) changes.mashWheat = mw;
        const mr = parseInt(d.mashRye); if (mr > 0 && mr !== (b.mashRye || 0)) changes.mashRye = mr;
        const mba = parseInt(d.mashBarley); if (mba > 0 && mba !== (b.mashBarley || 0)) changes.mashBarley = mba;
        const mo = parseInt(d.mashOther); if (mo > 0 && mo !== (b.mashOther || 0)) changes.mashOther = mo;
        if (d.upc && d.upc.trim().length >= 12) changes.upc = d.upc.trim();
        const changeCount = Object.keys(changes).length;
        return (
          <div style={modalOverlayStyle} onClick={e => { if (e.target === e.currentTarget) setShowSuggestEdit(false); }}>
            <div style={{ background: "#1E1A14", borderRadius: 16, padding: 24, width: "100%", maxWidth: 420, maxHeight: "85vh", overflowY: "auto", border: "1px solid #3D3428", position: "relative" }}>
              <div style={flexBetweenCenterMb16Style}>
                <h3 style={{ fontFamily: FONT_DISPLAY, color: "#F5E6D0", fontSize: "1.125rem", margin: 0 }}>Suggest Edit</h3>
                <button onClick={() => setShowSuggestEdit(false)} style={{ background: "none", border: "none", color: "#B5A898", fontSize: "1.25rem", cursor: "pointer", padding: 8, minWidth: 44, minHeight: 44 }} aria-label="Close">{"✕"}</button>
              </div>
              <div style={{ fontSize: "0.75rem", color: "#B5A898", marginBottom: 16 }}>Suggest corrections for <span style={goldTextStyle}>{getDisplayName(b)}</span>. Changes will be reviewed by an admin.</div>
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>Name</label>
                <input value={d.name} onChange={e => upd("name", e.target.value)} aria-label="Expression name" style={formInputStyle} maxLength={100} />
                {d.name && d.name.trim().length > 2 && (() => {
                  const brand = getBrand(b.brandId) || {};
                  const normalized = normalizeColaName(d.name.trim(), brand.name || '');
                  return normalized !== d.name.trim() ? (
                    <div style={{ fontSize: '0.7rem', color: '#d4a04a', marginTop: 2, fontStyle: 'italic' }}>
                      Will display as: {normalized}
                    </div>
                  ) : null;
                })()}
                {d.name !== (b.name || "") && <div style={goldText05625Mt2Style}>Current: {b.name || "—"}</div>}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                <div>
                  <label style={formLabelStyle}>Proof</label>
                  <input value={d.proof} onChange={e => upd("proof", e.target.value)} aria-label="Proof" type="number" style={formInputStyle} />
                  {d.proof !== "" && parseFloat(d.proof) !== b.proof && <div style={goldText05625Mt2Style}>Current: {b.proof || "—"}</div>}
                </div>
                <div>
                  <label style={formLabelStyle}>Age</label>
                  <input value={d.age} onChange={e => upd("age", e.target.value)} aria-label="Age" style={formInputStyle} maxLength={20} />
                  {d.age !== (b.age || "") && <div style={goldText05625Mt2Style}>Current: {b.age || "—"}</div>}
                </div>
                <div>
                  <label style={formLabelStyle}>MSRP ($)</label>
                  <input value={d.msrp} onChange={e => upd("msrp", e.target.value)} aria-label="MSRP" type="number" style={formInputStyle} />
                  {d.msrp !== "" && parseFloat(d.msrp) !== b.msrp && <div style={goldText05625Mt2Style}>Current: {b.msrp || "—"}</div>}
                </div>
                <div>
                  <label style={formLabelStyle}>Finish Type</label>
                  <input value={d.finishType} onChange={e => upd("finishType", e.target.value)} aria-label="Finish type" style={formInputStyle} maxLength={100} />
                  {d.finishType !== (b.finishType || "") && <div style={goldText05625Mt2Style}>Current: {b.finishType || "—"}</div>}
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>Type / Category</label>
                <select value={d.type} onChange={e => upd("type", e.target.value)} aria-label="Type" style={selectInputStyle}>
                  <option value="">{"— Select Type —"}</option>
                  {["Bourbon, Straight", "Bourbon, Small Batch", "Bourbon, Single Barrel", "Bourbon, Barrel Proof", "Bourbon, Bottled in Bond", "Bourbon, Finished", "Rye, Straight", "Wheat, Straight", "Other"].map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                {d.type && d.type !== (b.type || "") && <div style={goldText05625Mt2Style}>Current: {b.type || "—"}</div>}
              </div>
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>Batch</label>
                <input value={d.batch} onChange={e => upd("batch", e.target.value)} aria-label="Batch" style={formInputStyle} maxLength={100} />
                {d.batch !== (b.batch || "") && <div style={goldText05625Mt2Style}>Current: {b.batch || "—"}</div>}
              </div>
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>UPC / Barcode</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input value={d.upc || ""} onChange={e => upd("upc", e.target.value.replace(/[^0-9]/g, ""))} aria-label="UPC / Barcode" style={{ ...formInputStyle, flex: 1 }} maxLength={13} placeholder="Enter 12 or 13 digit UPC" />
                  <button onClick={() => upd("scannerOpen", true)} style={{ ...scanButtonStyle, flexShrink: 0 }} aria-label="Scan barcode">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D4A574" strokeWidth="2" strokeLinecap="round"><path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="7" y1="8" x2="17" y2="8"/><line x1="7" y1="16" x2="17" y2="16"/></svg>
                  </button>
                </div>
                {d.upc && d.upc.trim().length > 0 && d.upc.trim().length < 12 && <div style={{ fontSize: "0.5625rem", color: "#CD5C5C", marginTop: 2 }}>UPC must be 12 or 13 digits</div>}
              </div>
              {d.scannerOpen && <BarcodeScanner autoResolve={false} onResult={(code) => { upd("upc", code); upd("scannerOpen", false); }} onClose={() => upd("scannerOpen", false)} />}
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>Mash Bill (%)</label>
                <div style={flexGap8Style}>
                  {[{ k: "mashCorn", l: "Corn" }, { k: "mashWheat", l: "Wheat" }, { k: "mashRye", l: "Rye" }, { k: "mashBarley", l: "Barley" }, { k: "mashOther", l: "Other" }].map(f => (
                    <div key={f.k} style={flexOneStyle}>
                      <div style={{ fontSize: "0.5625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{f.l}</div>
                      <input value={d[f.k] ?? ""} onChange={e => upd(f.k, e.target.value)} aria-label={f.l + " %"} type="number" min={0} max={100} placeholder="0" style={{ ...formInputStyle, padding: "8px 6px", textAlign: "center" }} />
                    </div>
                  ))}
                </div>
                {(() => { const t = [d.mashCorn, d.mashWheat, d.mashRye, d.mashBarley, d.mashOther].reduce((s, v) => s + (parseInt(v) || 0), 0); return t > 0 ? <div style={{ fontSize: "0.5625rem", marginTop: 4, color: t === 100 ? "#4CAF50" : "#CD5C5C" }}>Total: {t}%</div> : null; })()}
              </div>
              <div style={formGroupStyle}>
                <label style={formLabelStyle}>Reason for Edit</label>
                <textarea value={d.reason} onChange={e => upd("reason", e.target.value)} aria-label="Reason for edit" rows={3} placeholder="Explain what's incorrect and why..."
                  style={{ ...formInputStyle, resize: "vertical", minHeight: 60 }} maxLength={500} />
              </div>
              <div style={{ borderTop: "1px solid #3D3428", paddingTop: 12 }}>
                <div style={{ fontSize: "0.75rem", color: changeCount > 0 ? "#D4A053" : "#5A4F3E", marginBottom: 10 }}>{changeCount} field{changeCount !== 1 ? "s" : ""} changed</div>
                <button onClick={async () => {
                  if (changeCount === 0) { showToast("No changes to submit"); return; }
                  if (!d.reason.trim()) { showToast("Please provide a reason"); return; }
                  const result = await db.insertExpressionEdit(b.id, currentUser.uid, changes, sanitizeInput(d.reason.trim(), 500));
                  if (result) {
                    setExpressionEdits(prev => [{ id: result.id, bourbonId: b.id, userId: currentUser.uid, suggestedChanges: changes, reason: d.reason.trim(), status: "pending", reviewedBy: null, reviewedAt: null, createdAt: new Date().toISOString() }, ...prev]);
                    showToast("Edit suggestion submitted for review");
                    setShowSuggestEdit(false);
                  } else { showToast("Failed to submit — try again"); }
                }}
                  disabled={changeCount === 0 || !d.reason.trim()}
                  style={{ width: "100%", padding: "12px", background: changeCount > 0 && d.reason.trim() ? GRAD_PRIMARY : "#3D3428", border: "none", borderRadius: 8, color: changeCount > 0 && d.reason.trim() ? "#FFF" : "#5A4F3E", fontSize: "0.875rem", fontWeight: 700, cursor: changeCount > 0 && d.reason.trim() ? "pointer" : "default", minHeight: 44 }}>
                  Submit Suggestion ({changeCount} change{changeCount !== 1 ? "s" : ""})
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Check-in Modal */}
      {showCheckinModal && <CheckinModal
        editingCheckinId={editingCheckinId} checkins={checkins} checkinBourbon={checkinBourbon}
        checkinPrivacy={checkinPrivacy} myBar={myBar} friends={friends} bourbons={bourbons}
        setEditingCheckinId={setEditingCheckinId} setShowCheckinModal={setShowCheckinModal}
        setCheckinBourbon={setCheckinBourbon} setCheckins={setCheckins} setMyBar={setMyBar}
        setCelebrationData={setCelebrationData} setOverconsumptionNotice={setOverconsumptionNotice}
        setBarSharePrompt={setBarSharePrompt}
        getBourbon={getBourbon} getBrand={getBrand} getDisplayName={getDisplayName}
        checkProfanity={checkProfanity} showToast={showToast} db={db} currentUser={currentUser}
      />}
      {/* Celebration overlay */}
      {celebrationData && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.7)", animation: "fadeIn 0.3s ease" }}
          onClick={() => setCelebrationData(null)}>
          <div style={{ textAlign: "center", padding: 32, maxWidth: 340, animation: "scaleIn 0.4s cubic-bezier(0.34,1.56,0.64,1)" }}>
            <div style={{ fontSize: "3.5rem", marginBottom: 12 }}>{"🥃"}</div>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.5rem", color: "#D4A053", marginBottom: 8 }}>Cheers!</div>
            <div style={{ color: "#F5E6D0", fontSize: "1.0rem", fontWeight: 600, marginBottom: 4 }}>{celebrationData.bourbon}</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 12 }}>
              <StarRating rating={celebrationData.rating} size={32} />
              <span style={{ color: "#D4A053", fontWeight: 700, fontSize: "1.25rem", marginLeft: 4 }}>{fmtRating(celebrationData.rating)}</span>
            </div>
            <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginBottom: 16 }}>{celebrationData.statCallout}</div>
            <button onClick={(e) => {
              e.stopPropagation();
              const text = `${celebrationData.bourbon} \u2014 ${fmtRating(celebrationData.rating)}\u2605 on BarrelRank`;
              if (navigator.share) { navigator.share({ text }).catch(() => {}); }
              else { navigator.clipboard.writeText(text).then(() => showToast("Copied to clipboard!")); }
            }}
              style={{ padding: "8px 20px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 10, color: "#B5A898", fontSize: "0.75rem", cursor: "pointer" }}
              aria-label="Share check-in">
              {"📤"} Share
            </button>
          </div>
        </div>
      )}
      {/* Overconsumption awareness interstitial */}
      {overconsumptionNotice && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.75)", animation: "fadeIn 0.3s ease" }}>
          <div style={{ textAlign: "center", padding: 32, maxWidth: 340, background: "#1E1A14", borderRadius: 16, border: "1px solid #5A4F3E", animation: "scaleIn 0.4s cubic-bezier(0.34,1.56,0.64,1)" }}>
            <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>{"🥃"}</div>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", marginBottom: 10 }}>That{"'"}s {overconsumptionNotice.count} pours today</div>
            <p style={{ color: "#B5A898", fontSize: "0.8125rem", lineHeight: 1.5, margin: "0 0 20px" }}>Great tasting session. Remember, the best bourbon is the one you take your time with.</p>
            <button onClick={() => {
              const notice = overconsumptionNotice;
              setOverconsumptionNotice(null);
              setCelebrationData({ bourbon: notice.bourbon, rating: notice.rating, statCallout: notice.statCallout });
              setTimeout(() => setCelebrationData(null), 4000);
            }}
              style={{ padding: "10px 28px", background: GRAD_PRIMARY, border: "none", borderRadius: 10, color: "#1A1409", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer" }}
              aria-label="Continue">
              Got it
            </button>
          </div>
        </div>
      )}
      {/* Checkin read-only detail */}
      {viewCheckinDetail && (() => {
        const vcd = viewCheckinDetail;
        const vcdBourbon = getBourbon(vcd.bourbonId);
        return (
          <div style={modalOverlayStyle} onClick={() => setViewCheckinDetail(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: "#1E1A14", borderRadius: 16, padding: 24, border: "1px solid #5A4F3E", maxWidth: 440, width: "100%", maxHeight: "85vh", overflow: "auto" }}>
              <div style={flexBetweenCenterMb16Style}>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: 0 }}>Check-In Detail</h3>
                <button onClick={() => setViewCheckinDetail(null)} style={mutedText15Style} aria-label="Close">{"\u2715"}</button>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <StarRating rating={vcd.rating} size={20} />
                <span style={{ color: "#D4A053", fontWeight: 700, fontSize: "1.125rem" }}>{fmtRating(vcd.rating)}</span>
              </div>
              <div style={{ fontSize: "0.8125rem", color: "#B5A898", marginBottom: 12 }}>{"\uD83D\uDCCD"} {vcd.location} {"\u00B7"} {vcd.date} {"\u00B7"} {vcd.servingStyle} {"\u00B7"} {vcd.glassware}</div>
              {vcd.isBarrelPick && <div style={{ marginBottom: 12, padding: "4px 10px", background: "rgba(212,160,83,0.1)", border: "1px solid rgba(212,160,83,0.25)", borderRadius: 10, fontSize: "0.6875rem", fontWeight: 600, color: "#D4A053", display: "inline-block" }}>{"\uD83C\uDFF7\uFE0F"} Pick{vcd.pickSource ? ` \u00B7 ${vcd.pickSource}` : ""}{vcd.pickProof ? ` \u00B7 ${vcd.pickProof}\u00B0` : ""}</div>}
              {vcd.tastingNotes?.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                  {vcd.tastingNotes.map(n => <span key={n} style={{ padding: "3px 10px", borderRadius: 20, fontSize: "0.6875rem", background: "#3D3428", color: "#D4A053", border: "1px solid #5A4F3E" }}>{n}</span>)}
                </div>
              )}
              <NosePalateFinish checkin={vcd} />
              {vcd.comment && <p style={{ color: "#A69880", fontSize: "0.875rem", lineHeight: 1.6, margin: "12px 0 0", fontStyle: "italic" }}>"{sanitizeText(vcd.comment)}"</p>}
              <div style={{ display: "flex", gap: 10, marginTop: 20, borderTop: "1px solid #3D3428", paddingTop: 16 }}>
                <button onClick={() => { if (vcdBourbon) { setCheckinBourbon(vcdBourbon); setEditingCheckinId(vcd.id); setShowCheckinModal(true); setViewCheckinDetail(null); } }}
                  style={{ flex: 1, padding: "10px 0", background: GRAD_PRIMARY, border: "none", borderRadius: 10, color: "#1A1409", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer" }}>
                  Edit
                </button>
                <button onClick={() => setViewCheckinDetail(null)}
                  style={{ flex: 1, padding: "10px 0", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 10, color: "#B5A898", fontSize: "0.875rem", cursor: "pointer" }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Shelf Staple Picker Modal */}
      {shelfStaplePicker.open && (
        <div style={modalOverlayStyle} onClick={() => setShelfStaplePicker({ open: false, search: '', note: '' })}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#1a1a1a', borderRadius: '16px 16px 0 0', position: 'fixed', bottom: 0, left: 0, right: 0, maxHeight: '80vh', display: 'flex', flexDirection: 'column', padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#fff' }}>🥃 Set Shelf Staple</h3>
              <button onClick={() => setShelfStaplePicker({ open: false, search: '', note: '' })} aria-label="Close shelf staple picker"
                style={{ ...closeButtonStyle, position: 'static' }}>✕</button>
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <input type="text" placeholder="Why this bourbon? (optional, 140 chars)" value={shelfStaplePicker.note}
                onChange={e => setShelfStaplePicker(prev => ({ ...prev, note: e.target.value.slice(0, 140) }))}
                aria-label="Shelf staple note" style={{ ...formInputStyle, fontSize: '0.85rem' }} />
              {shelfStaplePicker.note && (
                <div style={{ textAlign: 'right', color: '#666', fontSize: '0.7rem', marginTop: 2 }}>{shelfStaplePicker.note.length}/140</div>
              )}
            </div>
            <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
              <span style={searchIconStyle}>🔍</span>
              <input type="text" placeholder="Search bourbons..." value={shelfStaplePicker.search}
                onChange={e => { const v = e.target.value; setShelfStaplePicker(prev => ({ ...prev, search: v })); clearTimeout(shelfSearchTimer.current); shelfSearchTimer.current = setTimeout(() => setShelfStapleQuery(v), 200); }}
                aria-label="Search bourbons for shelf staple" style={{ ...searchInputIconStyle, paddingRight: shelfStaplePicker.search ? 36 : undefined }} autoFocus />
              {shelfStaplePicker.search && <button onClick={() => { setShelfStaplePicker(prev => ({ ...prev, search: '' })); setShelfStapleQuery(''); }} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#B5A898", cursor: "pointer", fontSize: "1rem", padding: 4 }} aria-label="Clear search">✕</button>}
            </div>
            <div style={{ overflowY: 'auto', flex: 1, maxHeight: '50vh' }}>
              {(() => {
                const query = shelfStapleQuery.toLowerCase().trim();
                if (query.length < 2) return (
                  <div style={{ color: '#666', textAlign: 'center', padding: '2rem 0', fontSize: '0.85rem' }}>Type to search for a bourbon...</div>
                );
                const results = bourbons
                  .filter(b => isBrowsableBourbon(b) && (
                    b.name?.toLowerCase().includes(query) ||
                    getDisplayBrand(b.brandId)?.name?.toLowerCase().includes(query) ||
                    (b.aliases || []).some(a => a.alias?.toLowerCase().includes(query))
                  ))
                  .sort((a, b) => (b.popularity_score || 0) - (a.popularity_score || 0))
                  .slice(0, 20);
                if (!results.length) return (
                  <div style={{ color: '#666', textAlign: 'center', padding: '2rem 0', fontSize: '0.85rem' }}>No bourbons found</div>
                );
                return results.map(b => (
                  <div key={b.id} onClick={async () => {
                      const success = await db.updateShelfStaple(currentUser.uid, b.id, shelfStaplePicker.note || null);
                      if (success) {
                        setCurrentUser(prev => ({ ...prev, shelf_staple_id: b.id, shelf_staple_note: shelfStaplePicker.note || null }));
                        setShelfStaplePicker({ open: false, search: '', note: '' });
                      }
                    }}
                    style={{ padding: '0.75rem', borderBottom: '1px solid #2a2a2a', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={flexOneStyle}>
                      <div style={{ fontWeight: 600, color: '#fff', fontSize: '0.9rem' }}>{b.name}</div>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.15rem' }}>
                        <span style={{ color: '#aaa', fontSize: '0.8rem' }}>{getDisplayBrand(b.brandId)?.name}</span>
                        {b.proof && <span style={{ color: '#C4A469', fontSize: '0.75rem' }}>{b.proof}°</span>}
                      </div>
                    </div>
                    {currentUser?.shelf_staple_id === b.id && (
                      <span style={{ color: '#C4A469', fontSize: '0.85rem' }}>✓</span>
                    )}
                  </div>
                ));
              })()}
            </div>
            {currentUser?.shelf_staple_id && (
              <button onClick={async () => {
                  if (!window.confirm("Remove your Shelf Staple?")) return;
                  const success = await db.clearShelfStaple(currentUser.uid);
                  if (success) {
                    setCurrentUser(prev => ({ ...prev, shelf_staple_id: null, shelf_staple_note: null }));
                    setShelfStaplePicker({ open: false, search: '', note: '' });
                  }
                }}
                style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(255,100,100,0.1)', border: '1px solid rgba(255,100,100,0.3)', borderRadius: 8, color: '#ff6464', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                Remove Shelf Staple
              </button>
            )}
          </div>
        </div>
      )}
      {/* Bar share prompt */}
      {barSharePrompt && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.6)", animation: "fadeIn 0.2s ease" }}
          onClick={() => setBarSharePrompt(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#1E1A14", borderRadius: 14, padding: 24, border: "1px solid #5A4F3E", maxWidth: 320, textAlign: "center", animation: "scaleIn 0.3s cubic-bezier(0.34,1.56,0.64,1)" }}>
            {barSharePrompt.confirmAdd ? (() => {
              const bspBourbon = getBourbon(barSharePrompt.bourbonId);
              const isPickBase = bspBourbon?.expressionType === "pick_base";
              return (<>
                <div style={{ fontSize: "1.75rem", marginBottom: 8 }}>{"🍾"}</div>
                <div style={{ color: "#F5E6D0", fontSize: "0.9375rem", fontWeight: 600, marginBottom: 4 }}>{barSharePrompt.bourbonName}</div>
                <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginBottom: 16 }}>Add this bottle to your bar?</div>
                {isPickBase && (
                  <div style={{ textAlign: "left", marginBottom: 16 }}>
                    <button onClick={() => setBarSharePrompt(prev => ({ ...prev, isStorePick: !prev.isStorePick }))}
                      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: 0, background: "none", border: "none", cursor: "pointer", marginBottom: 8 }}>
                      <div style={{ width: 32, height: 18, borderRadius: 9, background: barSharePrompt.isStorePick ? "#6495ED" : "#3D3428", transition: "background 0.2s", position: "relative", flexShrink: 0 }}>
                        <div style={{ width: 14, height: 14, borderRadius: 7, background: "#F5E6D0", position: "absolute", top: 2, left: barSharePrompt.isStorePick ? 16 : 2, transition: "left 0.2s" }} />
                      </div>
                      <span style={{ fontSize: "0.75rem", color: barSharePrompt.isStorePick ? "#6495ED" : "#B5A898", fontWeight: barSharePrompt.isStorePick ? 600 : 400 }}>{"\uD83C\uDFF7\uFE0F"} This is a store pick</span>
                    </button>
                    {barSharePrompt.isStorePick && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <input value={barSharePrompt.bspStore || ""} onChange={e => setBarSharePrompt(prev => ({ ...prev, bspStore: e.target.value }))} aria-label="Store name" placeholder="Store name *" maxLength={100}
                          style={{ width: "100%", padding: "8px 10px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 8, color: "#F5E6D0", fontSize: "0.75rem", boxSizing: "border-box" }} />
                        <div style={grid2ColGap8Style}>
                          <input value={barSharePrompt.bspBarrel || ""} onChange={e => setBarSharePrompt(prev => ({ ...prev, bspBarrel: e.target.value }))} aria-label="Barrel number" placeholder="Barrel #" maxLength={50}
                            style={{ padding: "8px 10px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 8, color: "#F5E6D0", fontSize: "0.75rem", boxSizing: "border-box" }} />
                          <input type="number" value={barSharePrompt.bspProof || ""} onChange={e => setBarSharePrompt(prev => ({ ...prev, bspProof: e.target.value }))} aria-label="Pick proof" placeholder="Proof" min="80" max="160" step="0.1"
                            style={{ padding: "8px 10px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 8, color: "#F5E6D0", fontSize: "0.75rem", boxSizing: "border-box" }} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div style={{ background: "#1A1510", borderRadius: 10, padding: 16, border: "1px solid #2D231A", marginBottom: 12 }}>
                  <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginBottom: 6 }}>How full is this bottle?</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {[100, 75, 50, 25].map(lv => (
                      <button key={lv} className="tap-feedback" onClick={() => setAddFillLevel(lv)}
                        style={{ flex: 1, padding: "8px", borderRadius: 6, fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", background: addFillLevel === lv ? "#C4A469" : "transparent", color: addFillLevel === lv ? "#1A1409" : "#C4A469", border: `1px solid ${addFillLevel === lv ? "#C4A469" : "rgba(196,164,105,0.3)"}` }}>
                        {lv}%
                      </button>
                    ))}
                    <input type="number" min={1} max={100} placeholder="%" value={addFillLevel !== 100 && addFillLevel !== 75 && addFillLevel !== 50 && addFillLevel !== 25 ? addFillLevel : ""} onChange={e => { const v = Math.min(100, Math.max(1, parseInt(e.target.value) || 1)); setAddFillLevel(v); }} aria-label="Custom fill level" style={{ width: 56, padding: "6px 4px", borderRadius: 6, border: "1px solid #3A2E25", background: "#150F0A", color: "#F5E6D0", fontSize: "0.8125rem", textAlign: "center" }} />
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.6875rem", color: "#A69880", marginBottom: 4 }}>My bottle's proof</div>
                      <input type="number" step={0.1} min={60} max={160} defaultValue={bspBourbon?.proof || ""} onChange={e => setCustomProof(parseFloat(e.target.value) || null)} aria-label="Custom proof" style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #3A2E25", background: "#150F0A", color: "#F5E6D0", fontSize: "0.8125rem", textAlign: "center", boxSizing: "border-box" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.6875rem", color: "#A69880", marginBottom: 4 }}>Barrel # or pick info</div>
                      <input type="text" maxLength={50} placeholder="e.g., 097" value={barrelInfo} onChange={e => setBarrelInfo(e.target.value)} aria-label="Barrel or pick info" style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: isPickBase ? "1px solid rgba(212,160,83,0.4)" : "1px solid #3A2E25", borderLeft: isPickBase ? "3px solid #D4A053" : undefined, background: "#150F0A", color: "#F5E6D0", fontSize: "0.8125rem", boxSizing: "border-box" }} />
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                  <button onClick={() => {
                    const newItem = { bourbonId: barSharePrompt.bourbonId, pricePaid: Math.min(Math.max(bspBourbon?.msrp || 0, 0), 100000), fill: addFillLevel, dateAdded: new Date().toISOString().slice(0, 10) };
                    if (barSharePrompt.isStorePick) {
                      newItem.pickStore = sanitizeInput(barSharePrompt.bspStore || "", 100);
                      newItem.pickBarrel = sanitizeInput(barSharePrompt.bspBarrel || "", 50);
                      newItem.pickProof = barSharePrompt.bspProof ? parseFloat(barSharePrompt.bspProof) : null;
                    }
                    // Pack custom proof and barrel info into notes JSON
                    const notesMeta = {};
                    if (customProof && customProof !== bspBourbon?.proof) notesMeta.custom_proof = customProof;
                    if (barrelInfo.trim()) notesMeta.barrel_info = sanitizeInput(barrelInfo.trim(), 50);
                    if (Object.keys(notesMeta).length > 0) newItem.notes = JSON.stringify(notesMeta);
                    setMyBar(prev => [...prev, newItem]);
                    if (useSupabase() && currentUser?.uid) {
                      db.insertBarItem(currentUser.uid, newItem);
                      if (barSharePrompt.isStorePick && barSharePrompt.bspStore) {
                        db.addStorePick({ bourbonId: barSharePrompt.bourbonId, storeName: sanitizeInput(barSharePrompt.bspStore, 100), barrelNumber: sanitizeInput(barSharePrompt.bspBarrel || "", 50), proof: barSharePrompt.bspProof ? parseFloat(barSharePrompt.bspProof) : null, submittedBy: currentUser.uid });
                      }
                    }
                    showToast("Added to your bar!");
                    setAddFillLevel(100); setCustomProof(null); setBarrelInfo("");
                    setBarSharePrompt({ ...barSharePrompt, confirmAdd: false });
                  }}
                    style={{ padding: "10px 24px", background: GRAD_PRIMARY, border: "none", borderRadius: 10, color: "#1A1409", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer", minHeight: 44 }}
                    aria-label="Confirm add to bar">
                    Add to Bar
                  </button>
                  <button onClick={() => setBarSharePrompt(null)}
                    style={{ padding: "10px 24px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 10, color: "#B5A898", fontSize: "0.875rem", cursor: "pointer", minHeight: 44 }}
                    aria-label="Cancel">
                    Cancel
                  </button>
                </div>
              </>);
            })() : (<>
              <div style={{ fontSize: "1.75rem", marginBottom: 8 }}>{"🍾"}</div>
              <div style={{ color: "#F5E6D0", fontSize: "0.9375rem", fontWeight: 600, marginBottom: 4 }}>{barSharePrompt.bourbonName}</div>
              <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginBottom: 16 }}>Share to your community feed?</div>
              <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                <button onClick={() => {
                  const activity = {
                    id: "ba_" + Date.now(), type: "bar_add", userId: currentUser?.uid,
                    bourbonId: barSharePrompt.bourbonId, date: new Date().toISOString().slice(0, 10),
                    visibility: "public",
                  };
                  setCommunityCheckins(prev => [activity, ...prev]);
                  setBarSharePrompt(null);
                  showToast("Shared to feed!");
                }}
                  style={{ padding: "10px 24px", background: GRAD_PRIMARY, border: "none", borderRadius: 10, color: "#1A1409", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer" }}
                  aria-label="Share to feed">
                  Yes
                </button>
                <button onClick={() => setBarSharePrompt(null)}
                  style={{ padding: "10px 24px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 10, color: "#B5A898", fontSize: "0.875rem", cursor: "pointer" }}
                  aria-label="Skip sharing">
                  No thanks
                </button>
              </div>
            </>)}
          </div>
        </div>
      )}
      {/* Variant Picker Overlay */}
      {variantPickerData && (() => {
        const { parent, variants, source } = variantPickerData;
        const selectVariant = async (v) => {
          let expr = bourbons.find(bb => bb.id === v.id);
          if (!expr) {
            try {
              const sb = getSupabase();
              if (sb) {
                const { data: rows } = await sb.from("bourbons").select("*").eq("id", v.id).limit(1);
                if (rows && rows[0]) {
                  const r = rows[0];
                  expr = { id: r.id, name: r.name, brandId: r.brand_id, facilityId: r.facility_id, proof: r.proof ? parseFloat(r.proof) : null, age: r.age || "", mashBill: r.mash_bill || "", msrp: r.msrp ? parseFloat(r.msrp) : null, type: r.type || "", finishType: r.finish_type || "", batch: r.batch || "", notes: r.notes || "", limitedRelease: r.limited_release || false, discontinued: r.discontinued || false, userAdded: r.user_added || false, addedBy: r.added_by || null, website: r.website || "", source: r.source || null, origin: r.origin || "", colaTtbId: r.cola_ttb_id || null, colaBrandName: r.cola_brand_name || null, colaClassName: r.cola_class_name || null, colaApprovalDate: r.cola_approval_date || null, imageUrl: r.image_url || null, submissionStatus: r.submission_status || "approved", submittedBy: r.submitted_by || null, submittedAt: r.submitted_at || null, reviewedBy: r.reviewed_by || null, reviewedAt: r.reviewed_at || null, rejectionReason: r.rejection_reason || "", mashCorn: r.mash_corn ?? null, mashWheat: r.mash_wheat ?? null, mashRye: r.mash_rye ?? null, mashBarley: r.mash_barley ?? null, mashOther: r.mash_other ?? null, proofMin: r.proof_min ? parseFloat(r.proof_min) : null, proofMax: r.proof_max ? parseFloat(r.proof_max) : null, tier: r.tier || "archive", popularityScore: r.popularity_score || 0, parentExpressionId: r.parent_expression_id || null, nameLocked: r.name_locked || false, colaProductName: r.cola_product_name || null, colaTastingFlavors: r.cola_tasting_flavors || null, colaDescription: r.cola_description || null, colaGrains: r.cola_grains || null, colaFinishingProcess: r.cola_finishing_process || null, colaBarcodeValue: r.cola_barcode_value || null, colaEnrichedAt: r.cola_enriched_at || null, expressionType: r.expression_type || null, mashbillSource: r.mashbill_source || null };
                  setBourbons(prev => prev.some(b => b.id === expr.id) ? prev : [...prev, expr]);
                }
              }
            } catch { /* silent */ }
          }
          setVariantPickerData(null);
          if (expr) {
            if (source === "batch") {
              setBatchAddMode(prev => ({ ...prev, selected: expr, searchQ: expr.name }));
            } else {
              setSelectedBourbon(expr);
              navigateTo("bourbon-detail");
            }
          }
        };
        return (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.85)", zIndex: 1200, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div style={{ fontSize: "1rem", color: "#F5E6D0", fontWeight: 600, marginBottom: 8, fontFamily: FONT_DISPLAY }}>Scan matched: {parent.name}</div>
            <div style={{ fontSize: "0.8125rem", color: "#A69880", marginBottom: 16 }}>Which release?</div>
            <div style={{ maxHeight: 300, overflowY: "auto", width: "100%", maxWidth: 340 }}>
              {variants.map(v => (
                <div key={v.id} className="tap-feedback" onClick={() => selectVariant(v)}
                  style={{ padding: "12px 16px", background: "#2D231A", borderRadius: 8, marginBottom: 8, cursor: "pointer", border: "1px solid #3D3428" }}>
                  <div style={{ color: "#F5E6D0", fontSize: "0.875rem", fontWeight: 600 }}>{v.name}</div>
                  {v.proof && <div style={{ color: "#A69880", fontSize: "0.75rem" }}>{v.proof}°</div>}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
              <button className="tap-feedback" onClick={() => selectVariant(parent)} style={{ padding: "8px 16px", background: "#3A2E25", border: "none", borderRadius: 6, color: "#A69880", fontSize: "0.8125rem", cursor: "pointer" }}>Just the parent</button>
              <button className="tap-feedback" onClick={() => setVariantPickerData(null)} style={{ padding: "8px 16px", background: "#3A2E25", border: "none", borderRadius: 6, color: "#A69880", fontSize: "0.8125rem", cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        );
      })()}
      {(showAddBrandModal || showAddBourbonModal) && <AddMissingModal
        addModalFormRef={addModalFormRef} brands={brands} setBrands={setBrands}
        bourbons={bourbons} setBourbons={setBourbons} currentUser={currentUser}
        selectedBourbon={selectedBourbon} setSelectedBourbon={setSelectedBourbon}
        addBourbonPreselectedBrand={addBourbonPreselectedBrand}
        setShowAddBrandModal={setShowAddBrandModal} setShowAddBourbonModal={setShowAddBourbonModal}
        setAddBourbonPreselectedBrand={setAddBourbonPreselectedBrand}
        getBrand={getBrand} isBrowsableBrand={isBrowsableBrand} resolveUPC={resolveUPC}
        showToast={showToast} navigateTo={navigateTo} db={db}
      />}
      {/* Public Menu / Share Bar Modal (app level) */}
      {menuView && (() => {

        const isCommercial = accountType === "commercial";

        const showPours = isCommercial && ["bar", "tasting_room", "restaurant", "hotel"].includes(barProfile.venueType);

        const menuItems = myBar.filter(item => !item.emptyReason && item.fill > 0).map(item => {

          const b = getBourbon(item.bourbonId);

          const d = b ? getBrand(b.brandId) : null;

          const pp = pourPricing[item.bourbonId] || {};

          const dblPrice = pp.dbl ? parseFloat(pp.dbl) : (pp.single ? parseFloat(pp.single) * 2 : null);
          return { item, b, d, pp, dblPrice, hasPricing: !!pp.single };
        }).filter(m => m.b && m.pp.available !== false);
        menuItems.sort((a, z) => {
          if (a.item.featured && !z.item.featured) return -1;
          if (!a.item.featured && z.item.featured) return 1;
          let cmp = 0;
          if (barSort.key === "name") cmp = (getDisplayName(a.b) || "").localeCompare(getDisplayName(z.b) || "");
          else if (barSort.key === "proof") cmp = (a.b?.proof || 0) - (z.b?.proof || 0);
          else if (barSort.key === "fill") cmp = (a.item.fill || 0) - (z.item.fill || 0);
          else if (barSort.key === "price") cmp = (a.item.pricePaid || 0) - (z.item.pricePaid || 0);
          return barSort.dir === "desc" ? -cmp : cmp;

        });

        const pricedItems = menuItems.filter(m => m.hasPricing);

        const premiumItems = menuItems.filter(m => m.hasPricing && (parseFloat(m.pp.single) || 0) >= 12);

        const flightItems = menuItems.filter(m => m.pp.flight);

        const proofTiers = [
          { key: "all", label: "All (" + menuItems.length + ")" },
          ...(isCommercial ? [
            { key: "available", label: "Priced (" + pricedItems.length + ")" },
            { key: "premium", label: "Premium (" + premiumItems.length + ")" },
            { key: "flights", label: "Flights (" + flightItems.length + ")" },
          ] : [
            { key: "high", label: "100+\u00B0 (" + menuItems.filter(m => m.b.proof >= 100).length + ")" },
            { key: "aged", label: "Aged (" + menuItems.filter(m => m.b.age && !m.b.age.includes("NAS")).length + ")" },
          ]),
        ];

        const displayed = menuCategory === "premium" ? premiumItems : menuCategory === "flights" ? flightItems : menuCategory === "available" ? pricedItems : menuCategory === "high" ? menuItems.filter(m => m.b.proof >= 100) : menuCategory === "aged" ? menuItems.filter(m => m.b.age && !m.b.age.includes("NAS")) : menuItems;

        const sOz = barProfile.singlePourOz;

        const dOz = sOz * 2;

        return (
          <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: "#0D0A07", overflow: "auto" }}>
            <div style={{ maxWidth: 520, margin: "0 auto", padding: "0 16px 40px" }}>
              <div style={{ textAlign: "center", padding: "32px 0 24px", borderBottom: "1px solid #3D3428", marginBottom: 24, position: "relative" }}>
                <button onClick={() => setMenuView(false)}
                  style={{ position: "absolute", top: 0, right: 0, background: "none", border: "none", color: "#B5A898", fontSize: "1.5rem", cursor: "pointer" }} aria-label="Close">{"✕"}</button>
                {isCommercial ? (<>
                  <div style={{ fontSize: "0.75rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 3, marginBottom: 8 }}>{barProfile.vibe || "Bourbon Bar"}</div>
                  <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "2.0rem", color: "#F5E6D0", margin: "0 0 6px" }}>{barProfile.name}</h1>
                  <p style={{ color: "#A69880", fontSize: "0.875rem", margin: "0 0 8px", fontStyle: "italic" }}>{barProfile.tagline}</p>
                  <div style={mutedText075Style}>{"📍"} {barProfile.location}</div>
                  <div style={{ color: "#B5A898", fontSize: "0.75rem", marginTop: 4 }}>{"🕒"} {barProfile.hours}</div>
                  <button onClick={() => setShowBarQR(true)} style={{ marginTop: 12, padding: "8px 20px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 10, color: "#D4A053", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, transition: "all 0.2s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "#D4A053"; e.currentTarget.style.background = "rgba(212,160,83,0.08)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#5A4F3E"; e.currentTarget.style.background = "transparent"; }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D4A053" strokeWidth="2"><rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="2" width="8" height="8" rx="1"/><rect x="2" y="14" width="8" height="8" rx="1"/><rect x="14" y="14" width="4" height="4"/><line x1="22" y1="14" x2="22" y2="22"/><line x1="14" y1="22" x2="22" y2="22"/><rect x="5" y="5" width="2" height="2" fill="#D4A053"/><rect x="17" y="5" width="2" height="2" fill="#D4A053"/><rect x="5" y="17" width="2" height="2" fill="#D4A053"/></svg>
                    Share QR Code
                  </button>
                </>) : (<>
                  <div style={{ fontSize: "0.75rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 3, marginBottom: 8 }}>My Collection</div>
                  <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "2.0rem", color: "#F5E6D0", margin: "0 0 6px" }}>{username}'s Bar</h1>
                  <p style={{ color: "#A69880", fontSize: "0.875rem", margin: "0 0 4px" }}>{menuItems.length} bottle{menuItems.length !== 1 ? "s" : ""} {"\u00B7"} {[...new Set(menuItems.map(m => m.d?.name).filter(Boolean))].length} brands</p>
                  <button onClick={() => setShowBarQR(true)} style={{ marginTop: 12, padding: "8px 20px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 10, color: "#D4A053", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, transition: "all 0.2s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "#D4A053"; e.currentTarget.style.background = "rgba(212,160,83,0.08)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#5A4F3E"; e.currentTarget.style.background = "transparent"; }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D4A053" strokeWidth="2"><rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="2" width="8" height="8" rx="1"/><rect x="2" y="14" width="8" height="8" rx="1"/><rect x="14" y="14" width="4" height="4"/><line x1="22" y1="14" x2="22" y2="22"/><line x1="14" y1="22" x2="22" y2="22"/><rect x="5" y="5" width="2" height="2" fill="#D4A053"/><rect x="17" y="5" width="2" height="2" fill="#D4A053"/><rect x="5" y="17" width="2" height="2" fill="#D4A053"/></svg>
                    Share QR Code
                  </button>
                </>)}
              </div>
              <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap", justifyContent: "center" }}>
                {proofTiers.map(t => (
                  <button key={t.key} onClick={() => setMenuCategory(t.key)}
                    style={{ padding: "6px 14px", borderRadius: 16, fontSize: "0.75rem", cursor: "pointer", border: "1px solid", transition: "all 0.15s",
                      background: menuCategory === t.key ? "rgba(212,160,83,0.15)" : "transparent", color: menuCategory === t.key ? "#D4A053" : "#A69880", borderColor: menuCategory === t.key ? "#D4A053" : "#5A4F3E", fontWeight: menuCategory === t.key ? 700 : 400 }}>
                    {t.label}
                  </button>
                ))}
              </div>
              {/* Column Headers */}
              <div style={{ display: "flex", padding: "0 14px 8px", borderBottom: "1px solid #1A1510" }}>
                <div style={{ flex: 1, fontSize: "0.5625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1.5 }}>Expression</div>
                <div style={{ width: 56, fontSize: "0.5625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1, textAlign: "center" }}>Proof</div>
                {showPours && (<>
                  <div style={{ width: 58, fontSize: "0.5625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1, textAlign: "center" }}>{"Single"}<div style={{ fontSize: "0.5rem", color: "#B5A898", marginTop: 1 }}>{sOz} oz</div></div>
                  <div style={{ width: 58, fontSize: "0.5625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1, textAlign: "center" }}>{"Double"}<div style={{ fontSize: "0.5rem", color: "#B5A898", marginTop: 1 }}>{dOz} oz</div></div>
                </>)}
                <div style={{ width: 44, fontSize: "0.5625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1, textAlign: "center" }}>Fill</div>
              </div>
              {/* Menu Items */}
              {displayed.length > 0 ? displayed.map(({ item, b, d, pp, dblPrice }) => (
                <div key={item.bourbonId} style={{ display: "flex", alignItems: "center", padding: "12px 14px", borderBottom: "1px solid #1A1510", transition: "background 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(212,160,83,0.04)"; }} onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                  <div style={flexOneStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontFamily: FONT_DISPLAY, fontSize: "0.9375rem", color: "#F5E6D0" }}>{getDisplayName(b)}</span>
                      {isCommercial && pp.tag === "new" && (
                        <span style={{ fontSize: "0.5rem", fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: "rgba(76,175,80,0.15)", color: "#4CAF50", border: "1px solid rgba(76,175,80,0.3)", textTransform: "uppercase", letterSpacing: 0.5 }}>New</span>
                      )}
                      {isCommercial && pp.tag === "featured" && (
                        <span style={{ fontSize: "0.5rem", fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: "rgba(212,160,83,0.15)", color: "#D4A053", border: "1px solid rgba(212,160,83,0.3)", textTransform: "uppercase", letterSpacing: 0.5 }}>{"\u2B50"} Featured</span>
                      )}
                    </div>
                    <div style={{ color: "#A69880", fontSize: "0.6875rem", marginTop: 2 }}>{d?.name}{formatAge(b.age) !== "NAS" ? " \u00B7 " + formatAge(b.age) : ""}{b.type ? " \u00B7 " + b.type.split(" ").slice(0, 2).join(" ") : ""}</div>
                  </div>
                  <div style={{ width: 56, textAlign: "center", color: "#F5E6D0", fontSize: "0.8125rem", fontWeight: 600 }}>{b.proof}{"\u00B0"}</div>
                  {showPours && (<>
                    <div style={{ width: 58, textAlign: "center", color: pp.single ? "#D4A053" : "#5A4F3E", fontSize: "0.8125rem", fontWeight: 600 }}>{pp.single ? "$" + pp.single : "\u2014"}</div>
                    <div style={{ width: 58, textAlign: "center", color: dblPrice ? "#D4A053" : "#5A4F3E", fontSize: "0.8125rem", fontWeight: 600 }}>{dblPrice ? "$" + Math.round(dblPrice) : "\u2014"}</div>
                  </>)}
                  <div style={{ width: 44, textAlign: "center" }}>
                    <div style={{ width: 28, height: 8, borderRadius: 4, background: "#3D3428", margin: "0 auto", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: item.fill + "%", background: item.fill > 50 ? "#4CAF50" : item.fill > 20 ? "#D4A053" : "#CD5C5C", borderRadius: 4, transition: "width 0.3s" }} />
                    </div>
                    <div style={{ fontSize: "0.5625rem", color: "#B5A898", marginTop: 2 }}>{item.fill}%</div>
                  </div>
                </div>
              )) : (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#B5A898" }}>
                  <p style={{ margin: 0 }}>No bottles in this category.</p>
                </div>
              )}
              {/* Flight callout (commercial only) */}
              {showPours && flightItems.length >= 3 && menuCategory !== "premium" && (
                <div style={{ marginTop: 20, padding: 16, background: "#1E1A14", borderRadius: 12, border: "1px solid #3D3428", textAlign: "center" }}>
                  <div style={{ fontSize: "0.875rem", color: "#D4A053", fontWeight: 700, marginBottom: 4 }}>{"✈\uFE0F"} Build a Flight</div>
                  <div style={dimText075Style}>Choose any {Math.min(4, flightItems.length)} from {flightItems.length} available {"\u00B7"} 0.5 oz pours</div>
                </div>
              )}
              {/* Summary for personal */}
              {!isCommercial && (
                <div style={{ marginTop: 20, padding: 16, background: "#1E1A14", borderRadius: 12, border: "1px solid #3D3428" }}>
                  <div style={{ display: "flex", justifyContent: "space-around", textAlign: "center" }}>
                    <div>
                      <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#D4A053", fontFamily: FONT_DISPLAY }}>{menuItems.length}</div>
                      <div style={{ fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1 }}>Bottles</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#D4A053", fontFamily: FONT_DISPLAY }}>{[...new Set(menuItems.map(m => m.d?.name).filter(Boolean))].length}</div>
                      <div style={{ fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1 }}>Brands</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#D4A053", fontFamily: FONT_DISPLAY }}>{menuItems.length > 0 ? Math.round(menuItems.reduce((s, m) => s + m.b.proof, 0) / menuItems.length) : 0}{"\u00B0"}</div>
                      <div style={{ fontSize: "0.625rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 1 }}>Avg Proof</div>
                    </div>
                  </div>
                </div>
              )}
              {/* Referral QR Overlay */}
              {showReferralQR && (() => {

                const refUrl = `https://${BRAND.domain}/join?ref=` + referralCode;

                const qrGrid = generateQR(refUrl);

                const qrSize = qrGrid.length;

                const cellSize = Math.max(4, Math.floor(150 / qrSize));

                const svgSize = qrSize * cellSize;

                return (
                  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 20 }}
                    onClick={() => setShowReferralQR(false)}>
                    <div onClick={e => e.stopPropagation()} style={{ background: "#1E1A14", borderRadius: 16, padding: 32, maxWidth: 340, width: "100%", textAlign: "center", border: "1px solid #5A4F3E" }}>
                      <div style={{ fontSize: "0.75rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 3, marginBottom: 8 }}>Scan to Join</div>
                      <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.25rem", color: "#F5E6D0", margin: "0 0 6px" }}>{BRAND.name}</h3>
                      <div style={{ fontSize: "0.75rem", color: "#B5A898", marginBottom: 20 }}>Referred by {currentUser?.name || "a friend"}</div>
                      <div style={{ display: "inline-block", padding: 16, background: "#FFFFFF", borderRadius: 12 }}>
                        <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
                          {qrGrid.flatMap((row, y) => row.map((on, x) => on ? (
                            <rect key={y * qrSize + x} x={x * cellSize} y={y * cellSize}
                              width={cellSize} height={cellSize} fill="#1A1510" />
                          ) : null))}
                        </svg>
                      </div>
                      <div style={{ marginTop: 16, padding: "8px 14px", background: "#150F0A", borderRadius: 8, border: "1px solid #3D3428" }}>
                        <div style={{ fontSize: "0.625rem", color: "#A69880", textTransform: "uppercase", letterSpacing: 1 }}>Referral Code</div>
                        <div style={{ fontSize: "1.0rem", fontWeight: 700, color: "#D4A053", letterSpacing: 2, marginTop: 2 }}>{referralCode}</div>
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                        <button onClick={() => { copyToClipboard(refUrl); }}
                          style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "1px solid #5A4F3E", background: "#150F0A", color: "#D4A053", fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer" }}>
                          {"🔗"} Copy Link
                        </button>
                        <button onClick={() => setShowReferralQR(false)}
                          style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: GRAD_PRIMARY, color: "#FFF", fontSize: "0.8125rem", fontWeight: 700, cursor: "pointer" }}>
                          Done
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div style={{ marginTop: 32, textAlign: "center", padding: "20px 0", borderTop: "1px solid #1A1510" }}>
                <div style={{ fontSize: "0.6875rem", color: "#B5A898", textTransform: "uppercase", letterSpacing: 2 }}>Powered by</div>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#D4A053", marginTop: 4 }}>{BRAND.name}</div>
              </div>
            </div>
            {/* QR Code Overlay */}
            {showBarQR && (() => {

              const shareUrl = isCommercial
                ? `https://${BRAND.domain}/#/menu/${encodeURIComponent(barProfile.name.toLowerCase().replace(/\s+/g, "-"))}`
                : `https://${BRAND.domain}/#/bar/${encodeURIComponent(currentUser?.uid || "u1")}/${encodeURIComponent(username.toLowerCase().replace(/\s+/g, "-"))}`;

              const qrGrid = generateQR(shareUrl);

              const qrSize = qrGrid.length;

              const cellSize = Math.max(4, Math.floor(150 / qrSize));

              const svgSize = qrSize * cellSize;

              return (
                <div style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(0,0,0,0.9)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
                  onClick={() => setShowBarQR(false)}>
                  <div onClick={e => e.stopPropagation()} style={{ background: "#1E1A14", borderRadius: 20, padding: 32, maxWidth: 360, width: "100%", textAlign: "center", border: "1px solid #5A4F3E" }}>
                    <div style={{ fontSize: "0.75rem", color: "#D4A053", textTransform: "uppercase", letterSpacing: 3, marginBottom: 12 }}>Scan to View</div>
                    <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.375rem", color: "#F5E6D0", margin: "0 0 20px" }}>
                      {isCommercial ? barProfile.name : username + "'s Collection"}
                    </h3>
                    <div style={{ display: "inline-block", padding: 16, background: "#FFFFFF", borderRadius: 12 }}>
                      <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
                        {qrGrid.flatMap((row, y) => row.map((on, x) => on ? (
                          <rect key={y * qrSize + x} x={x * cellSize} y={y * cellSize}
                            width={cellSize} height={cellSize} fill="#1A1510" />
                        ) : null))}
                      </svg>
                    </div>
                    <p style={{ color: "#B5A898", fontSize: "0.6875rem", margin: "16px 0 4px", wordBreak: "break-all" }}>{shareUrl}</p>
                    <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "center" }}>
                      <button onClick={() => { copyToClipboard(shareUrl); }}
                        style={{ padding: "10px 20px", background: GRAD_PRIMARY, border: "none", borderRadius: 10, color: "#FFF", fontSize: "0.8125rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                        {"📋"} Copy Link
                      </button>
                      <button onClick={() => setShowBarQR(false)}
                        style={{ padding: "10px 20px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 10, color: "#B5A898", fontSize: "0.8125rem", cursor: "pointer" }}>
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}
      {/* Toast Notification */}
      {toastMsg && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, transform: `translateY(${toastVisible ? "0" : "-100%"})`,
          background: "#D4A053", padding: "12px 20px",
          color: "#1A1409", fontSize: "0.875rem", fontWeight: 700, zIndex: 9999, boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
          opacity: toastVisible ? 1 : 0, transition: "all 0.3s ease", pointerEvents: "none", textAlign: "center"
        }}>{toastMsg}</div>
      )}
      {/* Report / Block Modal — multi-step */}
      {reportModal && (() => {

        const step = reportModal.step || "choose"; // "choose" | "report-reason" | "report-confirm" | "block-confirm"

        const setStep = (s) => setReportModal(prev => prev ? { ...prev, step: s } : null);

        const setReason = (r) => setReportModal(prev => prev ? { ...prev, selectedReason: r, step: "report-confirm" } : null);

        return (
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setReportModal(null)}>
          <div style={{ background: "#1E1A14", border: "1px solid #5A4F3E", borderRadius: 16, width: "100%", maxWidth: 400, padding: 28 }}
            onClick={e => e.stopPropagation()}>
            {step === "choose" && (<>
              <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: "0 0 6px" }}>Report or Block</h3>
              <p style={{ color: "#B5A898", fontSize: "0.75rem", margin: "0 0 20px" }}>Take action on content by {reportModal.userName}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button onClick={() => setStep("report-reason")}
                  style={{ padding: "14px 16px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 10, color: "#F5E6D0", fontSize: "0.875rem", cursor: "pointer", textAlign: "left", transition: "border-color 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#D4A053"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
                  <div style={{ fontWeight: 600 }}>⚑ Report Content</div>
                  <div style={mutedText06875Mt3Style}>Flag this for review by the BarrelRank team</div>
                </button>
                {!isBlocked(reportModal.userId) ? (
                  <button onClick={() => setStep("block-confirm")}
                    style={{ padding: "14px 16px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 10, color: "#F5E6D0", fontSize: "0.875rem", cursor: "pointer", textAlign: "left", transition: "border-color 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "#CD5C5C"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
                    <div style={{ fontWeight: 600, color: "#CD5C5C" }}>🚫 Block User</div>
                    <div style={mutedText06875Mt3Style}>Hide all content from {reportModal.userName.split(" ")[0]}</div>
                  </button>
                ) : (
                  <button onClick={() => { unblockUser(reportModal.userId); setReportModal(null); }}
                    style={{ padding: "14px 16px", background: "rgba(76,175,80,0.06)", border: "1px solid rgba(76,175,80,0.25)", borderRadius: 10, color: "#4CAF50", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", textAlign: "left" }}>
                    ✓ Unblock {reportModal.userName.split(" ")[0]}
                  </button>
                )}
                <button onClick={() => setReportModal(null)}
                  style={{ padding: "10px 16px", background: "transparent", border: "1px solid #3D3428", borderRadius: 10, color: "#B5A898", fontSize: "0.8125rem", cursor: "pointer", marginTop: 4 }}>
                  Cancel
                </button>
              </div>
            </>)}
            {step === "report-reason" && (<>
              <button onClick={() => setStep("choose")} style={{ background: "none", border: "none", color: "#D4A053", cursor: "pointer", padding: "0 0 16px", fontSize: "0.8125rem" }}>← Back</button>
              <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: "0 0 6px" }}>Why are you reporting?</h3>
              <p style={{ color: "#B5A898", fontSize: "0.75rem", margin: "0 0 16px" }}>Select a reason</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {["Inappropriate content", "Spam", "Harassment", "Other"].map(reason => (
                  <button key={reason} onClick={() => setReason(reason)}
                    style={{ padding: "12px 16px", background: "#150F0A", border: "1px solid #3D3428", borderRadius: 10, color: "#F5E6D0", fontSize: "0.8125rem", cursor: "pointer", textAlign: "left", transition: "border-color 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "#D4A053"} onMouseLeave={e => e.currentTarget.style.borderColor = "#3D3428"}>
                    {reason}
                  </button>
                ))}
              </div>
            </>)}
            {step === "report-confirm" && (<>
              <button onClick={() => setStep("report-reason")} style={{ background: "none", border: "none", color: "#D4A053", cursor: "pointer", padding: "0 0 16px", fontSize: "0.8125rem" }}>← Back</button>
              <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: "0 0 6px" }}>Confirm Report</h3>
              <p style={{ color: "#B5A898", fontSize: "0.75rem", margin: "0 0 16px" }}>Reason: {reportModal.selectedReason}</p>
              {reportModal.contentText && (
                <div style={{ padding: "10px 12px", background: "#150F0A", borderRadius: 8, border: "1px solid #3D3428", marginBottom: 16, maxHeight: 80, overflow: "hidden" }}>
                  <p style={{ color: "#B5A898", fontSize: "0.75rem", margin: 0, lineHeight: 1.4 }}>"{reportModal.contentText.slice(0, 150)}{reportModal.contentText.length > 150 ? "..." : ""}"</p>
                </div>
              )}
              <div style={flexGap10Style}>
                <button onClick={() => setReportModal(null)}
                  style={{ flex: 1, padding: "12px", background: "transparent", border: "1px solid #3D3428", borderRadius: 10, color: "#B5A898", fontSize: "0.8125rem", cursor: "pointer" }}>
                  Cancel
                </button>
                <button onClick={async () => {

                  const newFlag = {
                    id: "f_" + Date.now(), type: reportModal.contentType || "user_report", userId: reportModal.userId,
                    userName: reportModal.userName, content: reportModal.contentText || "", flaggedWords: [reportModal.selectedReason],
                    status: "pending", flaggedAt: new Date().toISOString(), reviewedBy: null, reportedBy: currentUser?.uid
                  };
                  setFlaggedContent(prev => [...prev, newFlag]);

                  if (useSupabase()) {

                    const dbFlag = await db.insertFlag(newFlag);
                    if (dbFlag) setFlaggedContent(prev => prev.map(f => f.id === newFlag.id ? { ...f, id: dbFlag.id } : f));

                  }

                  setReportModal(null);
                  showToast("⚑ Report submitted — thank you");
                }}
                  style={{ flex: 1, padding: "12px", background: "rgba(212,160,83,0.12)", border: "1px solid #D4A053", borderRadius: 10, color: "#D4A053", fontSize: "0.8125rem", fontWeight: 700, cursor: "pointer" }}>
                  Submit Report
                </button>
              </div>
            </>)}
            {step === "block-confirm" && (<>
              <button onClick={() => setStep("choose")} style={{ background: "none", border: "none", color: "#D4A053", cursor: "pointer", padding: "0 0 16px", fontSize: "0.8125rem" }}>← Back</button>
              <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.125rem", color: "#F5E6D0", margin: "0 0 6px" }}>Block {reportModal.userName}?</h3>
              <p style={{ color: "#B5A898", fontSize: "0.75rem", margin: "0 0 20px", lineHeight: 1.5 }}>They won't be notified. Their content will be hidden from your feeds, and they won't appear in your friend search. You can unblock them later from your Profile settings.</p>
              <div style={flexGap10Style}>
                <button onClick={() => setStep("choose")}
                  style={{ flex: 1, padding: "12px", background: "transparent", border: "1px solid #3D3428", borderRadius: 10, color: "#B5A898", fontSize: "0.8125rem", cursor: "pointer" }}>
                  Cancel
                </button>
                <button onClick={() => { blockUser(reportModal.userId); setReportModal(null); }}
                  style={{ flex: 1, padding: "12px", background: "rgba(205,92,92,0.12)", border: "1px solid #CD5C5C", borderRadius: 10, color: "#CD5C5C", fontSize: "0.8125rem", fontWeight: 700, cursor: "pointer" }}>
                  Block User
                </button>
              </div>
            </>)}
          </div>
        </div>
        );
      })()}
      {/* Batch Add Overlay */}
      {batchAddMode && (() => {
        const bq = batchAddMode.searchQ.trim().toLowerCase();
        const batchResults = bq.length >= 2
          ? bourbons.filter(b => b.name.toLowerCase().includes(bq) || (b.batch && b.batch.toLowerCase().includes(bq)) || getBrand(b.brandId)?.name.toLowerCase().includes(bq)).slice(0, 8)
          : [];
        const handleBatchConfirm = (b) => {
          const newItem = { bourbonId: b.id, pricePaid: b.msrp || 0, fill: addFillLevel, dateAdded: new Date().toISOString().slice(0, 10) };
          const notesMeta = {};
          if (customProof && customProof !== b.proof) notesMeta.custom_proof = customProof;
          if (barrelInfo.trim()) notesMeta.barrel_info = sanitizeInput(barrelInfo.trim(), 50);
          if (Object.keys(notesMeta).length > 0) newItem.notes = JSON.stringify(notesMeta);
          setMyBar(prev => [...prev, newItem]);
          if (useSupabase() && currentUser?.uid) db.insertBarItem(currentUser.uid, newItem);
          setBatchAddResults(prev => [...prev, { bourbonId: b.id, name: getDisplayName(b), fillLevel: addFillLevel }]);
          setAddFillLevel(100); setCustomProof(null); setBarrelInfo("");
          setBatchAddMode(prev => ({ ...prev, selected: null, searchQ: "", flash: getDisplayName(b) }));
          setTimeout(() => setBatchAddMode(prev => prev ? { ...prev, flash: null } : null), 1200);
        };
        const handleBatchDone = () => {
          setBatchAddMode(null);
          if (batchAddResults.length === 0) return;
          // Summary shown via batchAddResults still being non-empty after mode closes
        };
        return (
          <div style={batchAddOverlayStyle}>
            <div style={batchAddHeaderStyle}>
              <div style={{ color: "#F5E6D0", fontFamily: FONT_DISPLAY, fontSize: "1.125rem", fontWeight: 700 }}>Adding Bottles {batchAddResults.length > 0 && <span style={{ color: "#C4A469", fontSize: "0.875rem", fontWeight: 600 }}>({batchAddResults.length} added)</span>}</div>
              <button onClick={handleBatchDone} style={{ padding: "8px 16px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.8125rem", fontWeight: 700, cursor: "pointer" }}>Done</button>
            </div>
            {batchAddMode.flash && (
              <div style={{ padding: "10px 14px", background: "rgba(76,175,80,0.12)", border: "1px solid rgba(76,175,80,0.3)", borderRadius: 8, marginBottom: 12, display: "flex", alignItems: "center", gap: 8, animation: "fadeIn 0.2s ease" }}>
                <span style={{ color: "#4CAF50", fontWeight: 600 }}>✓</span>
                <span style={{ color: "#F5E6D0", fontSize: "0.875rem" }}>{batchAddMode.flash} added</span>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <div style={{ flex: 1, position: "relative" }}>
                <input value={batchAddMode.searchQ} onChange={e => setBatchAddMode(prev => ({ ...prev, searchQ: e.target.value, selected: null }))} placeholder="Search bourbons..." style={{ ...formInputStyle, width: "100%", paddingRight: batchAddMode.searchQ ? 36 : undefined }} autoFocus aria-label="Batch add search" />
                {batchAddMode.searchQ && <button onClick={() => setBatchAddMode(prev => ({ ...prev, searchQ: "", selected: null }))} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#B5A898", cursor: "pointer", fontSize: "1rem", padding: 4 }} aria-label="Clear search">✕</button>}
              </div>
              <button onClick={() => setBatchAddMode(prev => ({ ...prev, scanning: true }))} style={{ padding: "8px 12px", background: "#3D3428", border: "1px solid #5A4F3E", borderRadius: 8, color: "#D4A053", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center" }} title="Scan Barcode">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2"/><line x1="7" y1="7" x2="7" y2="17"/><line x1="10" y1="7" x2="10" y2="17"/><line x1="13" y1="7" x2="13" y2="17"/><line x1="17" y1="7" x2="17" y2="17"/></svg>
              </button>
            </div>
            {batchAddMode.scanning && <BarcodeScanner autoResolve={true} onMatch={async (match) => { setBatchAddMode(prev => ({ ...prev, scanning: false })); setPendingScanUpc(null); logScan(match._scannedUpc, true, match.expression_id, "batch"); let b = bourbons.find(bb => bb.id === match.expression_id); if (!b) { try { const sb = getSupabase(); if (sb) { const { data: rows } = await sb.from("bourbons").select("*").eq("id", match.expression_id).limit(1); if (rows && rows[0]) { const r = rows[0]; b = { id: r.id, name: r.name, brandId: r.brand_id, facilityId: r.facility_id, proof: r.proof ? parseFloat(r.proof) : null, age: r.age || "", mashBill: r.mash_bill || "", msrp: r.msrp ? parseFloat(r.msrp) : null, type: r.type || "", finishType: r.finish_type || "", batch: r.batch || "", notes: r.notes || "", limitedRelease: r.limited_release || false, discontinued: r.discontinued || false, userAdded: r.user_added || false, addedBy: r.added_by || null, website: r.website || "", source: r.source || null, origin: r.origin || "", colaTtbId: r.cola_ttb_id || null, colaBrandName: r.cola_brand_name || null, colaClassName: r.cola_class_name || null, colaApprovalDate: r.cola_approval_date || null, imageUrl: r.image_url || null, submissionStatus: r.submission_status || "approved", submittedBy: r.submitted_by || null, submittedAt: r.submitted_at || null, reviewedBy: r.reviewed_by || null, reviewedAt: r.reviewed_at || null, rejectionReason: r.rejection_reason || "", mashCorn: r.mash_corn ?? null, mashWheat: r.mash_wheat ?? null, mashRye: r.mash_rye ?? null, mashBarley: r.mash_barley ?? null, mashOther: r.mash_other ?? null, proofMin: r.proof_min ? parseFloat(r.proof_min) : null, proofMax: r.proof_max ? parseFloat(r.proof_max) : null, tier: r.tier || "archive", popularityScore: r.popularity_score || 0, parentExpressionId: r.parent_expression_id || null, nameLocked: r.name_locked || false, colaProductName: r.cola_product_name || null, colaTastingFlavors: r.cola_tasting_flavors || null, colaDescription: r.cola_description || null, colaGrains: r.cola_grains || null, colaFinishingProcess: r.cola_finishing_process || null, colaBarcodeValue: r.cola_barcode_value || null, colaEnrichedAt: r.cola_enriched_at || null, expressionType: r.expression_type || null, mashbillSource: r.mashbill_source || null }; setBourbons(prev => [...prev, b]); } } } catch { /* silent */ } } if (b) { let ch = bourbons.filter(c => c.parentExpressionId === b.id); if (ch.length === 0) { try { const sb2 = getSupabase(); if (sb2) { const { data: cd } = await sb2.from("bourbons").select("id, name, proof, tier, parent_expression_id").eq("parent_expression_id", b.id).order("name", { ascending: false }).limit(20); if (cd && cd.length > 0) ch = cd.map(r => ({ id: r.id, name: r.name, proof: r.proof ? parseFloat(r.proof) : null, tier: r.tier || "archive", parentExpressionId: r.parent_expression_id })); } } catch { /* silent */ } } if (ch.length > 0) { setVariantPickerData({ parent: b, variants: ch, source: "batch" }); } else { setBatchAddMode(prev => ({ ...prev, selected: b, searchQ: b.name })); } } else showToast("Couldn't load this expression. Try searching instead."); }} onNoMatch={(upc) => { setBatchAddMode(prev => ({ ...prev, scanning: false })); logScan(upc, false, null, "batch"); const msg = SCAN_NO_MATCH_MESSAGES[Math.floor(Math.random() * SCAN_NO_MATCH_MESSAGES.length)]; setScanTransition(msg); setTimeout(() => setScanTransition(null), 2000); }} onClose={() => setBatchAddMode(prev => ({ ...prev, scanning: false }))} />}
            {batchAddMode.selected ? (
              <div style={{ background: "#150F0A", borderRadius: 10, padding: 14, border: "1px solid #D4A053" }}>
                <div style={{ color: "#F5E6D0", fontFamily: FONT_DISPLAY, fontSize: "0.9375rem", fontWeight: 600, marginBottom: 4 }}>{getDisplayName(batchAddMode.selected)}</div>
                <div style={{ color: "#B5A898", fontSize: "0.75rem", marginBottom: 10 }}>{getBrand(batchAddMode.selected.brandId)?.name}{batchAddMode.selected.proof ? ` · ${batchAddMode.selected.proof}°` : ""}</div>
                <div style={{ background: "#1A1510", borderRadius: 10, padding: 12, border: "1px solid #2D231A", marginBottom: 10 }}>
                  <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginBottom: 6 }}>How full is this bottle?</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                    {[100, 75, 50, 25].map(lv => (
                      <button key={lv} className="tap-feedback" onClick={() => setAddFillLevel(lv)}
                        style={{ flex: 1, padding: "8px", borderRadius: 6, fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", background: addFillLevel === lv ? "#C4A469" : "transparent", color: addFillLevel === lv ? "#1A1409" : "#C4A469", border: `1px solid ${addFillLevel === lv ? "#C4A469" : "rgba(196,164,105,0.3)"}` }}>
                        {lv}%
                      </button>
                    ))}
                    <input type="number" min={1} max={100} placeholder="%" value={addFillLevel !== 100 && addFillLevel !== 75 && addFillLevel !== 50 && addFillLevel !== 25 ? addFillLevel : ""} onChange={e => { const v = Math.min(100, Math.max(1, parseInt(e.target.value) || 1)); setAddFillLevel(v); }} aria-label="Custom fill level" style={{ width: 56, padding: "6px 4px", borderRadius: 6, border: "1px solid #3A2E25", background: "#150F0A", color: "#F5E6D0", fontSize: "0.8125rem", textAlign: "center" }} />
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.6875rem", color: "#A69880", marginBottom: 4 }}>My bottle's proof</div>
                      <input type="number" step={0.1} min={60} max={160} defaultValue={batchAddMode.selected?.proof || ""} onChange={e => setCustomProof(parseFloat(e.target.value) || null)} aria-label="Custom proof" style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #3A2E25", background: "#150F0A", color: "#F5E6D0", fontSize: "0.8125rem", textAlign: "center", boxSizing: "border-box" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.6875rem", color: "#A69880", marginBottom: 4 }}>Barrel # or pick info</div>
                      <input type="text" maxLength={50} placeholder="e.g., 097" value={barrelInfo} onChange={e => setBarrelInfo(e.target.value)} aria-label="Barrel or pick info" style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: batchAddMode.selected?.expressionType === "pick_base" ? "1px solid rgba(212,160,83,0.4)" : "1px solid #3A2E25", borderLeft: batchAddMode.selected?.expressionType === "pick_base" ? "3px solid #D4A053" : undefined, background: "#150F0A", color: "#F5E6D0", fontSize: "0.8125rem", boxSizing: "border-box" }} />
                    </div>
                  </div>
                </div>
                <button className="tap-feedback" onClick={() => handleBatchConfirm(batchAddMode.selected)}
                  style={{ width: "100%", padding: "10px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer" }}>
                  Add ✓
                </button>
              </div>
            ) : (
              <div style={{ flex: 1 }}>
                {bq.length < 2 && <div style={{ color: "#666", textAlign: "center", padding: "2rem 0", fontSize: "0.875rem" }}>Type at least 2 characters to search</div>}
                {batchResults.map(b => (
                  <div key={b.id} className="tap-feedback" onClick={() => { setBatchAddMode(prev => ({ ...prev, selected: b })); setAddFillLevel(100); }}
                    style={{ padding: "10px 12px", borderRadius: 8, cursor: "pointer", borderBottom: "1px solid rgba(61,52,40,0.5)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ color: "#F5E6D0", fontSize: "0.875rem", fontWeight: 600 }}>{getDisplayName(b)}</div>
                      <div style={{ color: "#B5A898", fontSize: "0.75rem" }}>{getBrand(b.brandId)?.name}{b.proof ? ` · ${b.proof}°` : ""}</div>
                    </div>
                    {batchAddResults.some(r => r.bourbonId === b.id) && <span style={{ color: "#4CAF50", fontSize: "0.75rem", fontWeight: 600 }}>✓ In bar</span>}
                  </div>
                ))}
                {bq.length >= 2 && batchResults.length === 0 && <div style={{ color: "#666", textAlign: "center", padding: "2rem 0", fontSize: "0.875rem" }}>No matching bourbons found</div>}
              </div>
            )}
          </div>
        );
      })()}
      {/* Batch Add Summary */}
      {!batchAddMode && batchAddResults.length > 0 && (() => {
        const display = batchAddResults.slice(0, 10);
        const overflow = batchAddResults.length - 10;
        return (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.6)", animation: "fadeIn 0.2s ease" }} onClick={() => setBatchAddResults([])}>
            <div onClick={e => e.stopPropagation()} style={{ background: "#1E1A14", borderRadius: 14, padding: 24, border: "1px solid #5A4F3E", maxWidth: 360, width: "90%", textAlign: "center", animation: "scaleIn 0.3s cubic-bezier(0.34,1.56,0.64,1)" }}>
              <div style={{ fontSize: "1.75rem", marginBottom: 8 }}>{"🍾"}</div>
              <div style={{ color: "#F5E6D0", fontFamily: FONT_DISPLAY, fontSize: "1.125rem", fontWeight: 700, marginBottom: 4 }}>Bar Updated!</div>
              <div style={{ color: "#B5A898", fontSize: "0.8125rem", marginBottom: 14 }}>Added {batchAddResults.length} bottle{batchAddResults.length !== 1 ? "s" : ""}:</div>
              <div style={{ maxHeight: "40vh", overflowY: "auto", textAlign: "left", marginBottom: 16 }}>
                {display.map((r, i) => (
                  <div key={i} style={{ padding: "6px 0", borderBottom: "1px solid rgba(61,52,40,0.4)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ color: "#F5E6D0", fontSize: "0.8125rem" }}>{r.name}</span>
                    <span style={{ color: "#C4A469", fontSize: "0.75rem", fontWeight: 600, flexShrink: 0, marginLeft: 8 }}>{r.fillLevel}%</span>
                  </div>
                ))}
                {overflow > 0 && <div style={{ color: "#B5A898", fontSize: "0.75rem", padding: "8px 0", fontStyle: "italic" }}>and {overflow} more</div>}
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => { showToast(`Shared: added ${batchAddResults.length} bottles to bar`); setBatchAddResults([]); }}
                  style={{ flex: 1, padding: "10px", background: GRAD_PRIMARY, border: "none", borderRadius: 8, color: "#FFF", fontSize: "0.8125rem", fontWeight: 700, cursor: "pointer" }}>
                  Share to Community
                </button>
                <button onClick={() => setBatchAddResults([])}
                  style={{ flex: 1, padding: "10px", background: "transparent", border: "1px solid #5A4F3E", borderRadius: 8, color: "#B5A898", fontSize: "0.8125rem", cursor: "pointer" }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Support FAB */}
      <div style={supportFabStyle} onClick={() => { window.location.href = "mailto:support@barrelrank.com"; }} role="button" aria-label="Contact support">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C4A469" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
          <polyline points="22,6 12,13 2,6"/>
        </svg>
      </div>
      {/* Bottom Navigation */}
      <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 600, background: "linear-gradient(to top, #110D08 70%, transparent)", paddingTop: 20, zIndex: 100 }}>
        <div style={{ background: "#1A1510", borderTop: "1px solid #3D3428", display: "flex", justifyContent: "space-around", padding: "10px 0 14px" }}>
          {[
            { key: "home", icon: "🏠", label: "Home" },
            { key: "browse", icon: "🔍", label: "Browse" },
            { key: "brands", icon: "🏛️", label: "Brands" },
            { key: "mybar", icon: "🗄️", label: "My Bar" },
            { key: "activity", icon: "📓", label: "Journal" },
            { key: "profile", icon: "👤", label: "Profile" },
            ...(userBrandRoles.length > 0 ? [{ key: "mybrand", icon: "🏢", label: "My Brand" }] : []),
            ...(currentUser?.role === "admin" ? [{ key: "admin", icon: "🛡️", label: "Admin" }] : []),
          ].map(tab => (
            <button key={tab.key} onClick={() => { if (view === tab.key) { window.scrollTo({ top: 0, behavior: "smooth" }); return; } setSelectedBourbon(null); setSelectedBrand(null); setSearchQuery(""); navigateTo(tab.key); }}
              style={{ background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "4px 8px", minWidth: 52, position: "relative" }}>
              <span style={{ fontSize: "1.25rem", filter: view === tab.key ? "none" : "grayscale(0.8)", opacity: view === tab.key ? 1 : 0.5 }}>{tab.icon}</span>
              {tab.key === "profile" && pendingRequests.incoming.length > 0 && (
                <span style={{ position: "absolute", top: 0, right: 4, width: 16, height: 16, borderRadius: "50%", background: "#D4A053", color: "#1A1409", fontSize: "0.625rem", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{pendingRequests.incoming.length}</span>
              )}
              {tab.key === "admin" && currentUser?.role === "admin" && (() => {

                const pc = adminPendingCount;
                return pc > 0 ? <span style={{ position: "absolute", top: 0, right: 4, minWidth: 16, height: 16, borderRadius: 8, padding: "0 4px", background: "#CD5C5C", color: "#FFF", fontSize: "0.625rem", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{pc}</span> : null;
              })()}
              <span style={{ fontSize: "0.625rem", color: view === tab.key ? "#D4A053" : "#A69880", fontWeight: view === tab.key ? 700 : 400, letterSpacing: 0.3 }}>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

}

// ─── PASSPHRASE GATEKEEPER ─────────────────────────────────
// Simple client-side lock for preview deployments.
// NOT real security — the hash lives in the bundle. This is a
// velvet rope, not a vault. Swap for real auth before launch.
//
// To change the passphrase, replace GATE_HASH with the SHA-256
// of your new code. Generate it:
//   echo -n "your-new-code" | shasum -a 256
//

const GATE_HASH = "eee87c253ade662e0e8a8f6f4fa67145b7ce1931a3aa5c9aad78a26e6bdf8db0";

const GATE_ENABLED = true; // flip to false to disable

function Gatekeeper({ children }) {

  const [unlocked, setUnlocked] = useState(false);

  const [input, setInput] = useState("");

  const [error, setError] = useState("");

  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!GATE_ENABLED) { setUnlocked(true); setChecking(false); return; }
    try {

      const stored = sessionStorage.getItem(`${BRAND.storagePrefix}_gate`);
      if (stored === GATE_HASH) { setUnlocked(true); }
    } catch {}
    setChecking(false);
  }, []);

  const handleSubmit = async () => {
    setError("");

    const trimmed = input.trim();
    if (!trimmed) { setError("Enter the access code."); return; }
    try {

      const encoded = new TextEncoder().encode(trimmed);

      const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);

      const hex = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, "0")).join("");
      if (hex === GATE_HASH) {
        try { sessionStorage.setItem(`${BRAND.storagePrefix}_gate`, GATE_HASH); } catch {}
        setUnlocked(true);
      } else {
        setError("Invalid access code.");
        setInput("");

      }

    } catch {
      setError("Something went wrong. Try again.");

    }

  };
  if (checking) return null;
  if (unlocked) return children;

  return (
    <div style={{ minHeight: "100vh", background: "#1A1409", display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <div style={{ background: "#2A1F14", borderRadius: 16, padding: 40, maxWidth: 380, width: "100%", textAlign: "center", border: "1px solid #5A4F3E" }}>
        <div style={sFonMar2Style}>🥃</div>
        <h1 style={{ color: "#F5E6D0", fontSize: "1.5rem", fontFamily: FONT_DISPLAY, margin: "0 0 4px", fontWeight: 700 }}>{BRAND.name}</h1>
        <p style={{ color: "#B5A898", fontSize: "0.8125rem", margin: "0 0 4px" }}>Preview Access</p>
        <p style={{ color: "#A69880", fontSize: "0.625rem", margin: "0 0 28px" }}>v{APP_VERSION} ({BUILD_DATE})</p>
        <input
          type="password"
          value={input}
          onChange={e => { setInput(e.target.value); setError(""); }}
          onKeyDown={e => { if (e.key === "Enter") handleSubmit(); }}
          placeholder="Access code"
          autoFocus
          style={{
            width: "100%", padding: "12px 16px", borderRadius: 8, border: "1px solid #5A4F3E",
            background: "#1A1409", color: "#F5E6D0", fontSize: "0.9375rem", outline: "none",
            marginBottom: 12, textAlign: "center", letterSpacing: 2,
          }}
        />
        {error && <p style={{ color: "#CD5C5C", fontSize: "0.8125rem", margin: "0 0 12px" }}>{error}</p>}
        <button
          onClick={handleSubmit}
          style={{
            width: "100%", padding: "12px 0", borderRadius: 8, border: "none",
            background: "#D4A053", color: "#1A1409", fontSize: "0.9375rem", fontWeight: 700,
            cursor: "pointer", letterSpacing: 0.5,
          }}
        >
          Enter
        </button>
        <p style={{ color: "#A69880", fontSize: "0.6875rem", marginTop: 20 }}>This is a preview build. Contact the developer for access.</p>
        <p style={{ color: "#A69880", fontSize: "0.6875rem", marginTop: 12 }}><a href="/privacy.html" style={mutedLinkStyle}>Privacy Policy</a> · <a href="/terms.html" style={mutedLinkStyle}>Terms of Service</a> · <a href="mailto:support@barrelrank.com" style={mutedLinkStyle}>Support</a></p>
      </div>
    </div>
  );

}

export default function BarrelRank() {

  return <Gatekeeper><ErrorBoundary><BarrelRankApp /></ErrorBoundary></Gatekeeper>;

}





